import { equipmentInstallDateLabel } from '../../../../shared/equipment-install-date';
import { OnCallRotationEditor, OnCallRotations } from "../components/OnCallRotations";
import { FileText, Clock3, CheckCircle2, Archive } from "lucide-react";
import { AccountingWidgetHeading } from "../components/AccountingWidgetHeading";
import { AccountingPayments } from "../components/AccountingPayments";
import type { AccountingSection } from "../lib/workspace-navigation";
import { readWorkspaceViewState, workspaceViewSearch, writeWorkspaceViewState } from "../lib/workspace-view-state";
import { CallRecordingPlayer } from "../components/CallRecordingPlayer";
import { InvoicePdfPane } from "../components/InvoicePdfPane";
import { membershipBookingJobType, type MembershipBookingPrefill } from '../lib/membership-booking-prefill';
import { commercialLinePresentation } from "../../../../shared/commercial-line-presentation";
import { MembershipRelatedInvoices, type MembershipRelatedInvoice } from '../components/MembershipRelatedInvoices';
import { pricebookPartReference } from "../lib/pricebook-parts";
import type { PricebookVendorCost } from "../lib/pricebook-vendor-costs";
import type { VendorCosts } from "../lib/vendor-costs";
import { MembershipPlanEditor } from '../components/MembershipPlanEditor';
import { isCurrentOrUpcomingMembership, locationMembershipStatus } from "../lib/membership-presentation";
import { InvoiceItemsTable } from "../components/InvoiceItemsTable";
import { MembershipServiceCoverage } from '../components/MembershipServiceCoverage';
import { MembershipVisitHistory } from '../components/MembershipVisitHistory';
import type { MembershipEvent, MembershipSeasonalVisit } from '../components/MembershipsWorkspace';
import { membershipAddress, type MembershipLocation } from '../lib/membership-presentation';
import { coveredMaintenanceServices, isHistoricalMembershipCoverage } from '../lib/membership-service-display';
import { DispatchNotesInput } from "../components/DispatchNotesInput";
import { LocationTagBadges } from "../components/LocationTagBadges";
import { Trash2 } from "lucide-react";
import { RecordCommunicationsContext, useRecordCommunications, type RecordContactTarget, type TechnicianTextTarget } from "../components/RecordCommunications";
import { createPortal } from "react-dom";
import { MembershipRenewalProposalDialog } from "../components/MembershipRenewalProposalDialog";
import { CashCheckPricingSettings } from "../components/CashCheckPricingSettings";
import { pricebookPartNumber, pricebookPartSearchText } from "../lib/pricebook-parts";
import type { DocumentTaxCalculation } from "../lib/document-tax";
import { PurchaseOrderVendorEmail } from "../components/PurchaseOrderVendorEmail";
import { loadPurchaseOrderSettings } from "../lib/purchase-order-settings";
import { CallHistory } from "../components/CallHistory";
import { CustomerMessages } from "../components/CustomerMessages";
import { moneyInputCents } from "../lib/money-input";
import { BookingContactCard } from "../components/BookingContactCard";
import { InstallScheduler } from "../components/InstallScheduler";
import { BookingMembershipServices } from '../components/BookingMembershipServices';
import { BookingMembershipRenewal } from '../components/BookingMembershipRenewal';
import { MembershipBookingDialog } from '../components/MembershipBookingDialog';
import { usePhotoImage } from "../lib/use-photo-image";
import { MembershipsWorkspace } from "../components/MembershipsWorkspace";
import { PayrollWorkspace } from "../components/PayrollWorkspace";
import { EstimatePresentation, type PresentedOption } from "../components/EstimatePresentation";
import { ContactNotifications } from "../components/ContactNotifications";
import { InventoryPurchaseOrderPanel, PurchaseOrderContextFields, type PurchaseOrderContext, type PurchaseOrderDocument } from "../components/InventoryPurchaseOrderPanel";
import { PurchaseOrderInvoiceItems } from "../components/PurchaseOrderInvoiceItems";
import { PurchaseOrderItemPicker } from "../components/PurchaseOrderItemPicker";
import { PurchaseOrderStatusChips, PurchaseOrderStatus, purchaseOrderStatus, purchaseOrderStatusLabel } from "../components/PurchaseOrderStatus";
import { PriceBookThumb } from "../components/PriceBookThumb";
import { matchingCaller, type BookingCaller } from "../components/communications-model";
import { TechnicianTextPanel } from "../components/TechnicianTextPanel";
import { TextsWorkspace } from "../components/TextsWorkspace";
import { CommunicationsBoard } from "../components/Communications";
import { estimateDescription } from "../lib/estimate-description";
import { EstimateDescription, EstimateOptionItems } from "../components/EstimateOptionItems";
import { EstimateItemPicker, type EstimateItemKind } from "../components/EstimateItemPicker";
import { AppointmentUnassign, JobTechnicianActions, type VisitTechnician } from "../components/JobTechnicianActions";
import { JobWaitingStatus, DispatchWaitingJobs, WAITING_REASONS, type WaitingReason, type WaitingFilter } from "../components/JobWaitingStatus";
import { JobAppointmentForm } from "../components/JobAppointmentForm";
import { DispatchReassignMenu } from "../components/DispatchReassignMenu";
import { useOfficeSchedulingSave } from "../components/OfficeSchedulingConflict";
import { JobCancellation } from "../components/JobCancellation";
import { ProjectsWorkspace } from "../components/ProjectsWorkspace";
import { JobPurchaseOrders, purchaseOrderLineSnapshot, type JobInventoryPermissions } from "../components/JobPurchaseOrders";
import { groupTimeByVisit } from "../components/job-record-model";
import { InvoiceInlineEditor, InvoiceAddLine } from "../components/InvoiceInlineEditor";
import { InvoiceItemActions, type InvoiceItemKind } from "../components/InvoiceItemActions";
import { groupInvoiceLines } from "../lib/invoice-line-groups";
import { isCustomerVisibleInvoiceLine } from "../lib/invoice-line-visibility";
import { equipmentDisplayName, equipmentDetails, equipmentFormDetails, PricebookEquipmentDetails, type PricebookEquipmentData } from "../components/PricebookEquipmentDetails";
import { PricebookTemplates } from "../components/PricebookTemplates";
import { EstimateTemplatePicker, type EstimateTemplateChoice } from "../components/EstimateTemplatePicker";
import { EstimatePackageReview, type TemplatePreview } from "../components/EstimatePackageReview";
import { Badge } from "../components/Badge";
import "./pricebook-item-widget.css";
import "./dispatch-board.css";
import { PriceBookPartFields, PriceBookCategoryPicker, PriceBookVendorCosts } from "../components/PriceBookItemFields";
import { DiscountFeeCatalog } from "../components/DiscountFeeCatalog";
import { consolidatedDiscountFeeCategoryIds, discountFeeItems } from "../lib/discount-fee-catalog";
import { EquipmentCatalog } from "../components/EquipmentCatalog";
import { DocumentTaxSummary } from "../components/DocumentTaxSummary";
import {
  Component,
  createContext,
  useContext,
  Fragment,
  FormEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ErrorInfo,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Bell, CalendarDays, FolderOpen, Images, ShieldCheck, Wrench, Wind, Droplets, FilePlus2, LayoutGrid, ArrowLeft, ArrowRight, Building2, Check, ChevronDown, Info, Mail, MapPin, MessageSquare, Phone, Plus, Search, Settings, UserRound, X } from "lucide-react";
import "./booking.css";
import "./location-workspace.css";
import "../components/estimate-builder.css";
import { CustomerMembershipTags, loadCustomerMemberships, shareMembershipLookups, type MembershipLoader } from "../components/CustomerMembershipTags";
import { PastAppointmentWarning } from "../components/PastAppointmentWarning";
import "./dispatch-flyout.css";
import { JobContacts, OwnedContacts } from "../components/JobContacts";
import { JobVisitTimeline, JobTimesheets } from "../components/JobTimeCards";
import { EstimatePdfPreview } from "../components/EstimatePdfPreview";
import { EstimateBatchEmail, JobEstimates } from "../components/JobEstimates";
import { API_URL, apiFetch, IS_PREVIEW_ENVIRONMENT } from "../lib/api-client";
import { authClient } from "../lib/auth-client";
import { estimatePresentation, invoicePresentation } from "../lib/commercial-status";
import {
  companyPatchBody,
  formFromData,
  type CompanyData,
  type SettingsForm,
} from "./MyCompanyPage";
import DispatchOperator, { ProposalCard } from "../components/DispatchOperator";
import BookingAgent, { type BookingRequest } from "../components/BookingAgent";
import { WorkspaceSearch } from "../components/WorkspaceSearch";
import AlertBell from "../components/AlertBell";
import { WorkspaceIconRail } from "../components/WorkspaceIconRail";
import { FoundryAnalysisWorkspace } from "../components/FoundryAnalysisWorkspace";
import {
  isWorkspaceNavActive,
  parentWorkspaceView,
  topLevelWorkspaceView,
  workspaceLandingAction,
  workspaceNav,
  type CommercialFilter,
  type WorkspaceNavId,
  type WorkspaceView,
} from "../lib/workspace-navigation";
import {
  companyLocalToIso,
  dispatchElapsedSegments,
  dispatchItemForTechnician,
  CREW_PROGRESS_LOADING_STATUS,
  formatDispatchDate,
  formatDispatchTime,
  isoToCompanyLocal,
  minutesToLocalTime,
  snapLaneMinutes,
  stackAppointments,
  technicianOpenWindows,
  rankTechniciansForSkills,
  softConflicts,
  isRunningLate,
  type DispatchItem,
  type DispatchProposal,
  type DispatchWeek,
  type OperatorNavigation,
  type ScheduleBlock,
  type Technician,
} from "../lib/dispatch";
import {
  Checkbox,
  ContactLine,
  Dialog,
  endTimeForRange,
  Flyout,
  phoneNumber,
  PrimaryButton,
  QuietButton,
  SectionLabel,
  type LabelTone,
  Select,
  TextArea,
  TextInput,
  TimeRange,
  readableTime,
  type Option,
} from "../components/controls";
import { AddressAlreadyOnFile, AddressFields, type ExistingAddressMatch } from "../components/AddressFields";
import { sameStreetAndZip, type StreetAddress } from "../lib/place-address";
import { TechnicianEventDialog } from "../components/TechnicianEventDialog";
import {
  eventLaneSpan,
  technicianEventLabel,
} from "../lib/technician-events";

const SUPPORT_MAILTO = "mailto:info@workviewpro.com";

type Customer = {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  email: string | null;
  address: string | null;
  balanceCents?: number | null;
  lastJobAt: string | null;
};
type WorkspaceTag = { id: string; name: string; color: string | null; active: boolean };
type CustomerDetail = {
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    displayName: string | null;
    email: string | null;
    billingAddressLine1: string | null;
    billingAddressLine2?: string | null;
    billingCity: string | null;
    billingState: string | null;
    billingPostalCode: string | null;
    taxExempt?: number | boolean;
    balanceCents?: number;
    createdAt: string;
  };
  contactMethods: Array<{ channel: string; value: string; isPrimary: number }>;
  contacts?: WorkspaceContact[];
  properties: Array<{
    id: string;
    name?: string | null;
    active?: number;
    addressLine1: string;
    addressLine2?: string | null;
    city: string;
    state: string;
    postalCode: string | null;
    propertyType: "residential" | "commercial";
    jobsCount?: number;
    tags?: WorkspaceTag[];
  }>;
  jobs: Array<{
    id: string;
    jobNumber: number;
    status: string;
    description: string | null;
    priority: string | null;
    createdAt: string;
    appointmentAt: string | null;
    jobTypeName?: string | null;
    propertyId?: string | null;
  }>;
  jobsPage?: number;
  jobsPageSize?: number;
  jobsTotal?: number;
  estimates: Array<{
    id: string;
    jobId?: string;
    estimateNumber: number;
    displayNumber?: string | null;
    status: string;
    totalCents: number;
    createdAt: string;
    propertyId?: string | null;
    title?: string | null;
    officeStatus?: string;
  }>;
  invoices: Array<{
    id: string;
    jobId?: string;
    invoiceNumber: number;
    displayNumber?: string | null;
    summary?: string | null;
    status: string;
    totalCents: number;
    balanceDueCents: number;
    dueDate: string | null;
    createdAt: string;
    propertyId?: string | null;
    jobTypeName?: string | null;
    paidCents?: number;
    hasInvoiceSignature?: boolean;
    hasSignedEstimateApproval?: boolean;
    fulfillment?: string | null;
    estimateDisplayNumber?: string | null;
    jobStatus?: string | null;
    historical?: boolean;
  }>;
  credits: Array<{ id: string; remainingCents: number; createdAt: string }>;
  equipment: Array<{
    id: string;
    propertyId?: string | null;
    name: string | null;
    manufacturer: string | null;
    modelNumber: string | null;
    serialNumber: string | null;
    status: string | null;
    installDate: string | null;
  }>;
  memberships: Array<{
    id: string;
    propertyId?: string | null;
    displayStatus?: string;
    renewedIntoId?: string | null;
    status: string;
    recurrence: string | null;
    priceCents: number | null;
    nextServiceDate: string | null;
    nextBillingDate: string | null;
    startDate?: string | null;
    endDate?: string | null;
    includedVisits?: number | null;
    membershipTypeName?: string | null;
    tags?: WorkspaceTag[];
  }>;
  tags?: WorkspaceTag[];
};
type JobOption = {
  id: string;
  jobNumber: number;
  description: string | null;
  status: string;
  jobId?: string | null;
  customerName: string;
  address: string;
};
type UntimedDispatchJob = {
  waitingOn?: WaitingReason | null;
  needsReturnVisit?: number;
  id: string;
  jobNumber: string;
  description: string | null;
  priority: string | null;
  status: string;
  customerName: string;
  address: string;
  createdAt: string;
  isSoldUnscheduled?: boolean;
  jobTypeName?: string | null;
  estimateDisplayNumber?: string | null;
  invoiceId?: string | null;
  invoiceDisplayNumber?: string | null;
  totalCents?: number | null;
  balanceDueCents?: number | null;
  paidCents?: number | null;
};
type BookingProperty = {
  id: string;
  name?: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string | null;
  propertyType: "residential" | "commercial";
};
type BookingMatchedProperty = Pick<
  BookingProperty,
  "id" | "addressLine1" | "addressLine2" | "city" | "state" | "postalCode"
>;
type BookingMatchedBilling = Pick<
  BookingProperty,
  "addressLine1" | "addressLine2" | "city" | "state" | "postalCode"
>;
type BookingCustomer = {
  membershipBooking?: MembershipBookingPrefill;
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  primaryContactName?: string | null;
  primaryPhone?: string | null;
  preferredPropertyId?: string | null;
  bookingNotes?: string | null;
  matchingProperty?: BookingMatchedProperty | null;
  matchingBilling?: BookingMatchedBilling | null;
};
type BookingJobType = { id: string; name: string; active: boolean; requiredSkillIds?: string[] };
type CommercialItem = {
  id: string;
  projectId?: string | null;
  projectName?: string | null;
  number: number;
  displayNumber: string | null;
  title?: string | null;
  status: string;
  totalCents: number;
  balanceDueCents?: number;
  dueDate?: string | null;
  createdAt: string;
  customerName: string;
  jobId: string;
  jobNumber: number;
  propertyId?: string | null;
  address?: string | null;
  jobTypeName?: string | null;
  paidCents?: number;
  hasInvoiceSignature?: boolean;
  fulfillment?: string | null;
  estimateDisplayNumber?: string | null;
  jobStatus?: string | null;
  officeStatus?: string;
  isFollowUp?: boolean;
  isLost?: boolean;
  isApprovedWork?: boolean;
  pendingApprovalCount?: number;
};
type CommercialSummary = {
  openEstimateCount: number;
  openEstimateValueCents: number;
  lostEstimateCount: number;
  lostEstimateValueCents: number;
  acceptedEstimateValueCents: number;
  unpaidInvoiceValueCents: number;
  overdueValueCents: number;
  importReviewCount: number;
};
type ReconciliationItem = {
  id: string;
  kind: "invoice" | "payment" | "fee";
  paymentId: string | null;
  invoiceId: string;
  jobId: string;
  occurredAt: string;
  customerName: string;
  invoiceDisplayNumber: string;
  grossCents: number;
  feeCents: number;
  netCents: number;
  stripeStatus: string;
  paymentSource: "WorkView" | "Stripe" | "Manual";
  paymentMethod: string | null;
  quickBooksStatus: string;
  overallStatus: string;
  stripeTransactionId: string | null;
  quickBooksPaymentId: string | null;
  quickBooksInvoiceId: string | null;
  undepositedFundsAccountId: string | null;
  lastError: string | null;
};
type QuickBooksComparisonResult = {
  invoiceId: string;
  invoiceDisplayNumber: string;
  quickBooksInvoiceId: string | null;
  quickBooksInvoiceUrl: string | null;
  status: "verified" | "needs_manual_fix" | "unable_to_compare";
  reasons: string[];
};
type PriceBookCategory = {
  id: string;
  name: string;
  kind:
    | "service"
    | "material"
    | "equipment"
    | "discount"
    | "fee"
    | "membership_plan";
  parentCategoryId: string | null;
  position: number;
  description: string | null;
  imageUrl: string | null;
  imageContentUrl?: string | null;
  imageSource?: "workview_upload" | "servicetitan_recovery" | "servicetitan_backup" | "external_url" | null;
  active: boolean;
};
type PriceBookItem = {
  id: string;
  categoryId: string;
  categoryIds: string[];
  name: string;
  description: string | null;
  priceCents: number;
	 effectivePriceCents?: number;
  unitType: "flat" | "hourly";
  costCents: number | null;
  sku: string | null;
  taxable: boolean;
  taxClass: "labor" | "material";
  itemType?: "service" | "material" | "equipment" | null;
  itemTypeSource?: "servicetitan" | "manual" | null;
  modifierType?: "discount" | "fee" | null;
  modifierAmountType?: "fixed" | "percentage" | null;
  modifierBasisPoints?: number | null;
  sourceIncomeAccountName?: string | null;
  sourceExpenseAccountName?: string | null;
  stripeTaxCode?: string | null;
  afterHoursPriceCents: number | null;
  memberPriceCents: number | null;
  addOnPriceCents: number | null;
  imageUrl: string | null;
  imageContentUrl?: string | null;
  imageSource?: "workview_upload" | "servicetitan_recovery" | "servicetitan_backup" | "external_url" | null;
  vendorName: string | null;
  vendorCosts?: PricebookVendorCost[];
  equipmentDetails?: PricebookEquipmentData | null;
  active: boolean;
};
type PriceBookReadiness = {
  activeItems: number;
  activeMaterialItems: number;
  materialsWithoutCost: number;
  zeroPriceItems: number;
  uncategorizedItems: number;
};
type DocumentLineItem = {
  catalogName?: string | null;
  catalogDescription?: string | null;
  itemName?: string | null;
  itemDescription?: string | null;
  id: string;
  optionId?: string | null;
  priceBookItemId: string | null;
  description: string;
  quantity: number;
  unitPriceCents: number;
  totalCents: number;
  sourceActive?: number | null;
  sourceDisplayInAmount?: number | null;
  sourceItemType?: string | null;
  sourceIsChargeable?: number | null;
  customerVisible?: number | boolean | null;
  hiddenReason?: string | null;
};
type InvoiceDocument = {
  historical?: boolean;
  historicalPaidCents?: number | null;
  id: string;
  displayNumber: string | null;
	adjustmentToInvoiceId?: string | null;
	adjustmentToInvoiceDisplayNumber?: string | null;
  summary: string | null;
  status: string;
  jobStatus?: string | null;
  fulfillment?: string | null;
  estimateDisplayNumber?: string | null;
  subtotalCents: number;
  discountCents: number;
  discountPresentation?: {
    amountCents: number;
    treatment: "subtract_from_subtotal" | "included_in_line_items";
  } | null;
  taxCents: number;
  taxCalculation?: DocumentTaxCalculation | null;
  totalCents: number;
  balanceDueCents: number;
  terms: string | null;
  dueDate: string | null;
  voidReason: string | null;
  voidedAt: string | null;
  accountingLockedAt: string | null;
  quickBooksSync?: { status: string; lastError: string | null; attempts: number } | null;
  adjustmentTotalCents: number;
  jobNumber?: string | null;
  jobTypeName?: string | null;
  companyName?: string | null;
  companyEmail?: string | null;
  companyPhone?: string | null;
  companyAddressLine1?: string | null;
  companyAddressLine2?: string | null;
  companyCity?: string | null;
  companyState?: string | null;
  companyPostalCode?: string | null;
  signerName?: string | null;
  signedAt?: string | null;
  signatureDataUrl?: string | null;
  adjustments: Array<{
    id: string;
    adjustmentNumber: number;
    reason: string;
    summary: string | null;
    totalCents: number;
    createdAt: string;
    syncStatus: string;
  }>;
  customerName: string;
  billingAddressLine1: string | null;
  billingAddressLine2?: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
  serviceAddressName?: string | null;
  serviceAddressLine1: string | null;
  serviceAddressLine2?: string | null;
  serviceCity: string | null;
  serviceState: string | null;
  servicePostalCode: string | null;
  lines: Array<DocumentLineItem & { groupName: string | null }>;
  payments: Array<{ kind?: "payment" | "refund"; method: string | null; amountCents: number; appliedAt: string | null; occurredAt?: string | null; reference?: string | null }>;
};
type JobTimesheet = {
  appointmentId?: string | null;
  id: string;
  userId?: string;
  technicianName: string;
  clockIn: string;
  clockOut: string | null;
  date?: string;
  minutes?: number;
  running?: boolean;
  jobNumber?: string | null;
};
type WorkspaceContact = {
  id: string;
  name: string;
  phone: string | null;
  phoneType: "mobile" | "home" | "work" | "other" | null;
  email: string | null;
  memo: string | null;
  doNotAlert: boolean;
  isPrimary: boolean;
};
type JobDetail = {
	/** The card shell is ready; commercial line-item detail is still loading. */
	detailPending?: boolean;
  jobTags?: WorkspaceTag[];
  locationTags?: WorkspaceTag[];
  job: {
    id: string;
    jobNumber: number;
    status: string;
    waitingOn?: WaitingReason | null;
    waitingNote?: string | null;
    description: string | null;
    priority: string | null;
    createdAt: string;
    completedAt?: string | null;
    customerId: string;
    customerName: string;
    customerEmail: string | null;
    propertyId: string;
    propertyType: "residential" | "commercial";
    addressLine1: string;
    addressLine2?: string | null;
    city: string;
    state: string;
    postalCode: string | null;
    afterHoursPricingMode: "flat_fee" | "labor_multiplier" | null;
    afterHoursLaborMultiplierBps: number | null;
  };
  propertyContacts: WorkspaceContact[];
  billingContacts: WorkspaceContact[];
  appointments: Array<{
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    status: string;
    technicianNames: string | null;
    technicians?: VisitTechnician[];
    dispatchedAt?: string | null;
    enRouteAt?: string | null;
    arrivedAt?: string | null;
    departedAt?: string | null;
  }>;
  jobTypes: Array<{
    id: string;
    name: string;
    afterHoursServiceMultiplierBps: number | null;
  }>;
  requiredSkills: Array<{ id: string; name: string }>;
  requiredSkillsSource: "job_type_default" | "manual_override" | null;
  availableSkills: Array<{ id: string; name: string }>;
  invoices: Array<{
    id: string;
    invoiceNumber: number;
    displayNumber?: string | null;
	adjustmentToInvoiceId?: string | null;
	adjustmentToInvoiceDisplayNumber?: string | null;
    summary?: string | null;
    status: string;
    renewalLocked?: boolean | number;
    accountingLockedAt?: string | null;
    paidCents?: number;
		refundedCents?: number;
    hasInvoiceSignature?: boolean;
    hasSignedEstimateApproval?: boolean;
    fulfillment?: string | null;
    estimateDisplayNumber?: string | null;
    jobStatus?: string | null;
    totalCents: number;
    balanceDueCents: number;
    dueDate?: string | null;
    createdAt: string;
    lineItems: DocumentLineItem[];
  }>;
  payments?: Array<{
    id: string;
    paymentId: string;
    invoiceId: string;
    invoiceDisplayNumber: string | null;
    amountCents: number;
		paymentType?: "payment" | "refund" | string;
		refundedPaymentId?: string | null;
    method: string | null;
    reference: string | null;
    appliedAt: string;
		quickBooksStatus?: string;
		quickBooksError?: string | null;
  }>;
  estimates: Array<{
    id: string;
    estimateNumber: number;
    optionsManaged?: number;
    displayNumber?: string | null;
    status: string;
    totalCents: number;
    createdAt: string;
    lineItems: DocumentLineItem[];
    officeStatus?: string;
    title?: string | null;
    summary?: string | null;
    optionLabels?: string | null;
    approvedOptionLabels?: string | null;
    approvedTotalCents?: number | null;
    options?: Array<{ id: string; label: string; status?: string; totalCents: number; taxCents?: number }>;
    pendingApprovalCount?: number;
    originatedFromSoldWork?: number;
    soldWork?: Array<{
      optionId: string;
      optionLabel: string;
      approvedAt: string;
      approvalMethod: "onsite" | "phone" | "remote";
      jobId: string;
      jobNumber: string;
      invoiceId: string;
      invoiceDisplayNumber: string;
      invoiceStatus: string;
      invoiceBalanceDueCents: number;
      fulfillment: "now" | "later";
    }>;
  }>;
  timesheets?: JobTimesheet[];
  notes?: Array<{ id: string; body: string; createdAt: string; source: string | null }>;
  historicalCalls?: Array<{
    id: string;
    serviceTitanCallId: string | null;
    serviceTitanLeadCallId: string;
    occurredAt: string | null;
    durationSeconds: number | null;
    direction: string | null;
    callType: string | null;
    fromPhone: string | null;
    toPhone: string | null;
    reason: string | null;
    recordingStatus: "available" | "unavailable" | "not_recorded";
    recordingUrl: string | null;
  }>;
  historicalMessages?: Array<{
    id: string;
    threadId: string | null;
    direction: "inbound" | "outbound" | "unknown";
    body: string;
    occurredAt: string | null;
    media: Array<{ id: string; contentType: string | null; width: number | null; height: number | null; status: "available" | "missing" | "unsupported"; contentUrl: string | null }>;
  }>;
};

// Dispatch needs enough information to route a visit, not the entire job
// record (commercial documents, line items, payment history, and so on).
// Keep this contract deliberately small so opening a board block stays fast.
type DispatchJobContext = {
  job: Pick<JobDetail["job"], "id" | "propertyId">;
  propertyContacts: WorkspaceContact[];
  billingContacts: WorkspaceContact[];
  jobTypes: JobDetail["jobTypes"];
	/** Loaded only after a dispatcher opens a visit; never block the board. */
	requiredSkills: Array<{ id: string; name: string }>;
	locationTags: WorkspaceTag[];
  invoices: Array<Pick<JobDetail["invoices"][number], "id" | "createdAt">>;
  memberships: CustomerDetail["memberships"];
};

/**
 * Keep imported adjustment invoices beside the invoice they correct. Source
 * systems commonly return the newer adjustment first, which makes the money
 * trail read backwards in the job record.
 */
function orderJobInvoices(invoices: JobDetail["invoices"]) {
  const byId = new Map(invoices.map((invoice) => [invoice.id, invoice]));
  const adjustmentsByInvoiceId = new Map<string, JobDetail["invoices"]>();

  for (const invoice of invoices) {
    const parentId = invoice.adjustmentToInvoiceId;
    if (!parentId || !byId.has(parentId)) continue;
    const adjustments = adjustmentsByInvoiceId.get(parentId) ?? [];
    adjustments.push(invoice);
    adjustmentsByInvoiceId.set(parentId, adjustments);
  }

  const ordered: JobDetail["invoices"] = [];
  const added = new Set<string>();
  const appendInvoice = (invoice: JobDetail["invoices"][number]) => {
    if (added.has(invoice.id)) return;
    added.add(invoice.id);
    ordered.push(invoice);
    adjustmentsByInvoiceId.get(invoice.id)?.forEach(appendInvoice);
  };

  // Keep unrelated invoices in the source order, while moving each adjustment
  // directly after its original invoice.
  invoices
    .filter((invoice) => !invoice.adjustmentToInvoiceId || !byId.has(invoice.adjustmentToInvoiceId))
    .forEach(appendInvoice);
  invoices.forEach(appendInvoice);
  return ordered;
}

type Photo = {
  id: string;
  fileName: string | null;
  contentType: string | null;
  createdAt: string;
  contentUrl: string | null;
  availability?: "available" | "not_copied";
  propertyId?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  jobId?: string | null;
  jobNumber?: number | null;
};
type AccessContext = {
  role: "owner" | "admin" | "dispatcher" | "technician";
  workView: "agent" | "home" | "dispatch" | "my-work";
  dataScope: "company" | "assigned";
  permissions: string[];
};

const money = (cents: number | null | undefined) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    (cents ?? 0) / 100,
  );
function streetAddress(parts: {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}) {
  return [
    parts.addressLine1,
    parts.addressLine2,
    parts.city,
    [parts.state, parts.postalCode].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
}
const date = (
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  },
) =>
  value
    ? new Intl.DateTimeFormat(undefined, options).format(new Date(value))
    : "—";
export function calendarDate(value: string | null | undefined) {
  if (!value) return "—";
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  if (match && (/^\d{4}-\d{2}-\d{2}$/.test(value) || /T00:00:00/.test(value))) {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${match[1]}T00:00:00Z`));
  }
  return date(value);
}
const time = (value: string) =>
  date(value, { hour: "numeric", minute: "2-digit" });
const callDuration = (seconds: number | null | undefined) => {
  if (seconds === null || seconds === undefined || seconds < 0) return "Duration unavailable";
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
};
const statusStyle = (value: string) => {
  const v = value.toLowerCase();
  const tone =
    v.includes("complete") || v.includes("paid") || v.includes("approved")
      ? "complete"
      : v.includes("cancel") || v.includes("no_show") || v.includes("void")
        ? "off"
        : v.includes("conflict") || v.includes("overdue")
          ? "conflict"
          : v.includes("progress") ||
              v.includes("route") ||
              v.includes("arrived")
            ? "working"
            : v.includes("dispatch") || v.includes("sent")
              ? "dispatched"
              : v.includes("confirm")
                ? "confirmed"
                : "scheduled";
  return TONE[tone].chip;
};
const plainTextDescription = (value: string | null | undefined) => {
  if (!value) return "";
  if (typeof document === "undefined") return value
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(?:div|p|li|tr|h[1-6])\s*>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n");

  const body = new DOMParser().parseFromString(value, "text/html").body;
  body.querySelectorAll("br").forEach((element) => element.replaceWith("\n"));
  body.querySelectorAll("div, p, li, tr, h1, h2, h3, h4, h5, h6").forEach((element) => {
    element.before("\n");
    element.after("\n");
  });
  return (body.textContent || "")
    .replace(/\u00a0/g, " ")
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n");
};

const cleanDescription = (
  value: string | null | undefined,
  fallback = "Service work",
  limit = 140,
) => {
  const normalized = plainTextDescription(value).replace(/\s+/g, " ").trim();
  if (!normalized) return fallback;
  return normalized.length > limit
    ? `${normalized.slice(0, limit - 1).trimEnd()}…`
    : normalized;
};

async function data<T>(path: string): Promise<T> {
  const response = await apiFetch(path);
  if (!response.ok) throw new Error("Unable to load workspace data");
  return ((await response.json()) as { data: T }).data;
}

function optionalCoords(form: FormData, latName = "lat", lngName = "lng") {
  const latRaw = String(form.get(latName) ?? "").trim();
  const lngRaw = String(form.get(lngName) ?? "").trim();
  if (!latRaw || !lngRaw) return {};
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return {};
  return { lat, lng };
}

// Remote search should feel like the local filters: type freely, then ask for
// the settled term once. This avoids slow broad searches racing narrower ones.
function useDebouncedValue<T>(value: T, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);
  return debounced;
}

async function workspaceAccess(): Promise<AccessContext | null> {
  const load = () =>
    data<{ applicationUser: AccessContext | null }>("/api/v1/me");
  const first = await load();
  if (first.applicationUser) return first.applicationUser;
  // A full page load can race a freshly restored auth session at the edge. Retry
  // once before treating a signed-in user as unassigned from their workspace.
  await new Promise((resolve) => window.setTimeout(resolve, 250));
  return (await load()).applicationUser;
}
async function mutate<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  body: unknown,
): Promise<T> {
  const response = await apiFetch(path, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = (await response.json()) as {
    data?: T;
    error?: { message?: string; code?: string };
  };
  if (!response.ok) {
    const error = new Error(
      payload.error?.message || "Unable to save dispatch changes",
    ) as Error & { code?: string };
    error.code = payload.error?.code;
    throw error;
  }
  window.dispatchEvent(new Event("workview:alerts-changed"));
  return payload.data as T;
}
export function linkedWorkspaceView(search: string): WorkspaceView | null {
  const params = new URLSearchParams(search);
  const requestedView = params.get("view");
  if (requestedView === "agent" || requestedView === "timesheets" || requestedView === "pricebook") return { type: requestedView };
  if (requestedView === "inventory") return { type: "inventory", ...(params.get("purchaseOrderId") ? { purchaseOrderId: params.get("purchaseOrderId")! } : {}), ...(params.get("forJobId") ? { forJobId: params.get("forJobId")! } : {}), ...(params.get("forJobNumber") ? { forJobNumber: params.get("forJobNumber")! } : {}) };
  if (requestedView === "customers") return { type: "customers", search: params.get("search") || "" };
  if (requestedView === "commercial") {
    const mode = params.get("mode") === "estimates" ? "estimates" : "invoices";
    const filter = params.get("filter");
    return { type: "commercial", mode, ...((["documents", "payments", "invoice_reconciliation", "purchase_orders", "purchase_order_reconciliation"] as const).find(value => value === params.get("section")) ? { section: params.get("section") as AccountingSection } : {}), filter: (["attention", "overdue", "paid", "review", "open", "approved", "history", "lost"] as const).find(value => value === filter) ?? (mode === "estimates" ? "open" : "attention") };
  }
  if (params.get("view") === "projects" || params.get("projectId")) return { type: "projects", projectId: params.get("projectId") || undefined };
  if (params.get("view") === "texts") return { type: "texts" };
  if (params.get("view") === "communications") return { type: "communications" };
  if (params.get("view") === "booking_agent") return { type: "booking_agent" };
  if (params.get("view") === "search") return { type: "search" };
  if (params.get("view") === "memberships") return { type: "memberships" };
  if (params.get("view") === "learning") return { type: "learning" };
  const invoiceId = params.get("invoice");
  if (invoiceId)
    return {
      type: "invoice",
      invoiceId,
      collectPayment: params.get("collectPayment") === "true" || undefined,
      jobId: params.get("job") || undefined,
      returnTo: (["customer", "job", "commercial", "memberships", "field"] as const).find(value => value === params.get("returnTo")) ?? "commercial",
      ...(params.get("customer") ? { customerId: params.get("customer")! } : {}),
      ...(params.get("technicianId") ? { fieldTechnicianId: params.get("technicianId")! } : {}),
    };
  const estimateId = params.get("estimate");
  if (estimateId)
    return {
      type: "estimate",
      estimateId,
      jobId: params.get("job") || undefined,
      returnTo: (["customer", "job", "commercial", "dispatch"] as const).find(value => value === params.get("returnTo")) ?? "commercial",
      ...(params.get("customer") ? { customerId: params.get("customer")! } : {}),
    };
  const customerId = params.get("customer");
  const jobId = params.get("job");
  const locationId = params.get("location");
  if (customerId && locationId)
    return { type: "location", customerId, propertyId: locationId };
  if (customerId)
    return { type: "customer", customerId, returnTo: "customers" };
  const fromCustomerId = params.get("from");
  const requestedDate = params.get("date");
  const date =
    requestedDate && /^\d{4}-\d{2}-\d{2}$/.test(requestedDate)
      ? requestedDate
      : new Date().toISOString().slice(0, 10);
  if (jobId)
    return {
      type: "job",
      jobId,
      returnTo: fromCustomerId ? "customer" : params.get("returnTo") === "commercial" ? "commercial" : "dispatch",
      customerId: fromCustomerId || undefined,
      date,
    };
  if (params.get("view") === "dispatch") return { type: "dispatch", date };
  return null;
}

export default function WorkspacePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId: jobRouteId, purchaseOrderId: purchaseOrderRouteId } = useParams();
  const { data: session, isPending } = authClient.useSession();
  const sessionUserId = session?.user?.id ?? null;
  const landingKey = `${location.pathname}${location.search}`;
  const initialLocationKey = useRef(location.key);
  const restoredView = useMemo(() => location.pathname === "/dashboard" && location.key === initialLocationKey.current ? readWorkspaceViewState(sessionUserId, landingKey) : null, [sessionUserId, landingKey, location.key]);
  const linkedView = useMemo(() => {
    if (jobRouteId) {
      const context = location.state?.workspaceJob as Extract<WorkspaceView, { type: "job" }> | undefined;
      if (context?.type === "job" && context.jobId === jobRouteId && /^\d{4}-\d{2}-\d{2}$/.test(context.date)
        && ["dispatch", "customer", "commercial"].includes(context.returnTo)) return context;
      const params = new URLSearchParams(location.search);
      return linkedWorkspaceView(`?${new URLSearchParams({job: jobRouteId, date: params.get("date") ?? "", from: params.get("from") ?? ""})}`);
    }
    return purchaseOrderRouteId ? { type: "inventory" as const, purchaseOrderId: purchaseOrderRouteId } : (linkedWorkspaceView(restoredView?.search ?? "") ?? linkedWorkspaceView(location.search));
  }, [jobRouteId, purchaseOrderRouteId, location.search, location.state, restoredView]);
  const appliedLandingSearch = useRef<string | null>(null);
  const [access, setAccess] = useState<AccessContext | null>(null);
  const [activeWidget, setActiveWidget] = useState<"texts" | "phone" | "alerts" | null>(null);
  const [textThreadId, setTextThreadId] = useState<string | null>(null);
  const [textRecipient, setTextRecipient] = useState<RecordContactTarget | null>(null);
  const [technicianTextTarget, setTechnicianTextTarget] = useState<TechnicianTextTarget | null>(null);
  const workspaceHeader = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const header = workspaceHeader.current;
    if (!header) return;
    const measure = () => {
      const top = `${Math.ceil(header.getBoundingClientRect().bottom) + 12}px`;
      if (document.documentElement.style.getPropertyValue("--workspace-panels-top") !== top) document.documentElement.style.setProperty("--workspace-panels-top", top);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(header);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); document.documentElement.style.removeProperty("--workspace-panels-top"); };
  }, [access?.role]);

  const [view, setViewState] = useState<WorkspaceView>({ type: "agent" });
  // A job is a dialog. Keep the source screen mounted so opening it from a
  // record or workboard never silently swaps that screen for Dispatch.
  const [jobBackgroundView, setJobBackgroundView] = useState<WorkspaceView | null>(null);
  const screenView = view.type === "job" ? jobBackgroundView ?? view : view;
  const [customerRecordOrigin, setCustomerRecordOrigin] = useState<Extract<WorkspaceView, { type: "customer" | "location" }> | null>(null);
  const customerRecordView = view.type === "customer" || view.type === "location" ? view
    : (view.type === "job" || view.type === "estimate") && customerRecordOrigin
      && (view.returnTo === "customer" || view.returnTo === "job")
      && view.customerId === customerRecordOrigin.customerId ? customerRecordOrigin : null;

  const chosenViewUser = useRef<string | null>(null);
  const setView = useCallback((next: WorkspaceView) => {
    chosenViewUser.current = sessionUserId;
    if (next.type !== "job") setJobBackgroundView(null);
    setActiveWidget(null);
    setTextRecipient(null);
    setTechnicianTextTarget(null);
    setTextThreadId(null);
    setViewState(next);
  }, [sessionUserId]);
  useEffect(() => { setActiveWidget(null); setTextThreadId(null); setTextRecipient(null); setTechnicianTextTarget(null); }, [location.key]);
  const [overview, setOverview] = useState<{
    counts: {
      customers: number;
      jobs: number;
      invoices: number;
      estimates: number;
    };
    appointments: { total: number; pending: number };
    outstandingBalanceCents: number;
  } | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const textsOpen = activeWidget === "texts";
  const openHistoricalTextThread = useCallback((threadId: string) => {
    setTextRecipient(null);
    setTextThreadId(threadId);
    setActiveWidget("texts");
  }, []);
  const [unreadTexts, setUnreadTexts] = useState(0);
  const [universalSearch, setUniversalSearch] = useState("");
  const [searchFocusRequest, setSearchFocusRequest] = useState(0);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerTags, setCustomerTags] = useState<WorkspaceTag[]>([]);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerDetail | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobDetail | null>(null);
  const jobRequestRef = useRef(0);
  useEffect(() => () => { jobRequestRef.current += 1; }, [location.key]);
  useEffect(() => {
    if (view.type !== "job" || !selectedJob) return;
    const controller = new AbortController();
    let pending = false;
    const refresh = async () => {
      if (document.visibilityState === "hidden" || pending) return;
      pending = true;
      try {
        const response = await apiFetch(`/api/v1/workspace/jobs/${encodeURIComponent(selectedJob.job.id)}`, { signal: controller.signal });
        if (!response.ok) return;
        const body = await response.json() as { data?: JobDetail };
        if (!controller.signal.aborted && body.data?.job.id === selectedJob.job.id) {
          setSelectedJob(current => current === selectedJob ? body.data! : current);
        }
      } catch { /* Keep the current card if a background refresh is unavailable. */ }
      finally { pending = false; }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { controller.abort(); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [view.type, selectedJob]);
  const [dispatch, setDispatch] = useState<DispatchItem[]>([]);
  const [untimedJobs, setUntimedJobs] = useState<UntimedDispatchJob[]>([]);
  const [appointmentActions, setAppointmentActions] = useState(false);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [blocks, setBlocks] = useState<ScheduleBlock[]>([]);
  const [dispatchDate, setDispatchDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [dispatchTimezone, setDispatchTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [defaultBookingDurationMinutes, setDefaultBookingDurationMinutes] = useState(120);
  const [dispatchWeek, setDispatchWeek] = useState<DispatchWeek | null>(null);
  const dispatchLoadGeneration = useRef(0);
  const [proposals, setProposals] = useState<DispatchProposal[]>([]);
  const [proposalBusy, setProposalBusy] = useState<string | null>(null);
  const [jobs, setJobs] = useState<JobOption[]>([]);
  const [bookingCaller, setBookingCaller] = useState<BookingCaller | null>(null);
  const [bookingCustomer, setBookingCustomer] =
    useState<BookingCustomer | null>(null);
  // An invoice opened from the accounting board lands open and scrolled to,
  // rather than collapsed at the bottom of the job it belongs to.
  const [focusDocumentId, setFocusDocumentId] = useState("");
  const [collectPaymentInvoiceId, setCollectPaymentInvoiceId] = useState("");
  // The pricing rules live on the company record. The pricebook loads it so
  // the rules can be set from the screen they govern.
  const [companySettings, setCompanySettings] = useState<{
    form: SettingsForm;
    jobTypes: CompanyData["jobTypes"];
  } | null>(null);
  /**
   * The job record reads the `contact` table; the customer card reads
   * `customer_contact_method`. Imported customers have the second and not the
   * first, so a job showed "no contact on file" for someone whose phone is on
   * screen one click away. Until the job payload carries the account's own
   * numbers (API-46), the job record fetches them when it has none.
   */
  const [jobCustomerContacts, setJobCustomerContacts] = useState<
    Array<{ channel: string; value: string; isPrimary: number }>
  >([]);
  const [commercial, setCommercial] = useState<CommercialItem[]>([]);
  const [commercialType, setCommercialType] = useState<
    "invoices" | "estimates"
  >("invoices");
  const [commercialSummary, setCommercialSummary] =
    useState<CommercialSummary | null>(null);
  const [commercialFilter, setCommercialFilter] =
    useState<CommercialFilter>("attention");
  const [commercialSearch, setCommercialSearch] = useState("");
  const [commercialPage, setCommercialPage] = useState(1);
  const [commercialTotal, setCommercialTotal] = useState<number | undefined>();
  const [commercialPageSize, setCommercialPageSize] = useState<number | undefined>();
  const commercialRequestRef = useRef(0);

  const [priceBook, setPriceBook] = useState<{
    categories: PriceBookCategory[];
    vendors?: Array<{ id: string; name: string }>;
    supportsItemDetails?: boolean;
    items: PriceBookItem[];
    readiness: PriceBookReadiness;
    accountingAccounts: Array<{ accountKind: "income" | "expense"; name: string; itemCount: number }>;
  }>({
    categories: [],
    items: [],
    accountingAccounts: [],
    readiness: {
      activeItems: 0,
      activeMaterialItems: 0,
      materialsWithoutCost: 0,
      zeroPriceItems: 0,
      uncategorizedItems: 0,
    },
  });
  const [priceBookArchived, setPriceBookArchived] = useState(false);
  const [priceBookLoading, setPriceBookLoading] = useState(false);
  const [priceBookLoadedFor, setPriceBookLoadedFor] = useState<boolean | null>(
    null,
  );
  const priceBookRequestRef = useRef(0);
  const customerRequestRef = useRef(0);
  const debouncedCustomerSearch = useDebouncedValue(
    view.type === "customers" ? view.search : "",
  );
  const debouncedCommercialSearch = useDebouncedValue(commercialSearch);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isPending && !session?.user) navigate("/auth", { replace: true });
  }, [isPending, navigate, session?.user]);
  useEffect(() => {
    // better-auth refetches the session on tab focus and hands back a new `user`
    // object even when the id is unchanged. Key this effect on the user id and
    // skip reapplying the role default once a landing has been chosen — that
    // reapply is what sent people back to Agent while they were working.
    if (!sessionUserId) {
      appliedLandingSearch.current = null;
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const applicationUser = await workspaceAccess();
        if (cancelled) return;
        if (!applicationUser) {
          setError(
            "Your account is not assigned to a WorkView tenant yet. Ask an owner to add you, then refresh this page.",
          );
          setLoading(false);
          return;
        }
        setAccess(current => current && JSON.stringify(current) === JSON.stringify(applicationUser) ? current : applicationUser);
        const landing = workspaceLandingAction({
          search: landingKey,
          appliedSearch: appliedLandingSearch.current,
          hasLinkedView: Boolean(linkedView),
        });
        // Remember every visited query, including one that preserves the screen,
        // so returning to an earlier deep link applies it again.
        appliedLandingSearch.current = landingKey;
        switch (landing) {
          case "keep-current":
            return;
          case "apply-url":
          case "apply-role-default":
            break;
          default: {
            const _exhaustive: never = landing;
            return _exhaustive;
          }
        }
        if (landing === "apply-url" && linkedView) {
          switch (linkedView.type) {
            case "customer":
              await openCustomer(linkedView.customerId);
              return;
            case "estimate":
              setView(linkedView);
              setLoading(false);
              return;
            case "invoice":
              await openInvoice(linkedView.invoiceId, linkedView);
              setLoading(false);
              return;
            case "search":
              setView({ type: "dispatch", date: dispatchDate });
              setSearchFocusRequest(request => request + 1);
              setLoading(false);
              return;
            case "texts":
              setView({ type: "dispatch", date: dispatchDate });
              setActiveWidget("texts");
              setLoading(false);
              return;
            case "projects":
            case "communications":
            case "learning":
              setView(linkedView);
              setLoading(false);
              return;
            case "job":
              // Keep old query-string links working, but make every job record
              // addressable with a stable URL people can bookmark or share.
              if (!jobRouteId) {
                navigate(`/jobs/${encodeURIComponent(linkedView.jobId)}`, {
                  replace: true, state: { workspaceJob: linkedView, focusDocumentId: restoredView?.focusDocumentId || "" },
                });
                return;
              }
              setDispatchDate(linkedView.date);
              setError("");
              setLoading(true);
              setSelectedJob(null);
              setJobCustomerContacts([]);
              setFocusDocumentId(typeof location.state?.focusDocumentId === "string" ? location.state.focusDocumentId : "");
              const request = ++jobRequestRef.current;
	              try {
	                const detail = await data<JobDetail>(`/api/v1/workspace/jobs/${encodeURIComponent(linkedView.jobId)}/summary`);
                if (cancelled || request !== jobRequestRef.current) return;
                setSelectedJob(detail);
                setView(linkedView);
                loadJobContactFallback(detail, request);
	                void data<JobDetail>(`/api/v1/workspace/jobs/${encodeURIComponent(linkedView.jobId)}`).then(fullDetail => {
	                  if (!cancelled && request === jobRequestRef.current) setSelectedJob(fullDetail);
                }).catch(() => undefined);
              } catch {
                if (!cancelled && request === jobRequestRef.current) setError("We couldn’t load that job.");
              } finally {
                if (!cancelled && request === jobRequestRef.current) setLoading(false);
              }
              return;
            case "dispatch":
              setDispatchDate(linkedView.date);
              setView(linkedView);
              setLoading(false);
              return;
            case "customers":
              setCustomerSearch(linkedView.search);
              setView(linkedView); setLoading(false); return;
            case "commercial":
              setCommercialType(linkedView.mode); setCommercialFilter(linkedView.filter);
              setView(linkedView); setLoading(false); return;
            case "agent":
            case "booking_agent":
            case "memberships":
            case "location":
            case "timesheets":
            case "pricebook":
            case "inventory":
            case "foundry_analysis":
              setView(linkedView);
              setLoading(false);
              return;
            default: {
              const _exhaustive: never = linkedView;
              return _exhaustive;
            }
          }
        }
        setView(
          applicationUser.workView === "dispatch"
            ? { type: "dispatch", date: dispatchDate }
            : { type: "agent" },
        );
        setLoading(false);
      } catch {
        if (!cancelled) {
          setError("We couldn’t load your workspace access.");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobRouteId, landingKey, linkedView, navigate, sessionUserId]);
  useEffect(() => {
    if (!sessionUserId || chosenViewUser.current !== sessionUserId || !access || loading || location.pathname !== "/dashboard" || appliedLandingSearch.current !== landingKey) return;
    writeWorkspaceViewState(sessionUserId, landingKey, view, focusDocumentId);
  }, [sessionUserId, access, loading, landingKey, view, focusDocumentId]);
  useEffect(() => {
    if (access?.permissions.includes("company:read")) void loadOverview();
  }, [access?.role]);
  useEffect(() => {
    if (!access?.permissions.includes("company:read")) return;
    const controller = new AbortController();
    // Records and Texts can open directly, without ever loading Dispatch.
    // Resolve their company timezone independently of the board request.
    void apiFetch("/api/v1/workspace/company", { signal: controller.signal }).then(async response => {
      if (!response.ok) return;
      const payload = await response.json() as { data?: { company?: { timezone?: string | null } } };
      const timezone = payload.data?.company?.timezone;
      if (timezone && !controller.signal.aborted) setDispatchTimezone(timezone);
    }).catch(() => { /* Keep the local timezone until company settings are available. */ });
    return () => controller.abort();
  }, [access]);
  useEffect(() => {
    if (
      access?.dataScope === "company" &&
      access.permissions.includes("scheduling:read")
    )
      void loadProposals();
  }, [access?.role]);
  useEffect(() => {
    if (access && view.type === "customers")
      void loadCustomers(debouncedCustomerSearch);
  }, [access, view.type, debouncedCustomerSearch]);
  useEffect(() => {
    if (access?.permissions.includes("customers:read"))
      void data<{ items: WorkspaceTag[] }>("/api/v1/workspace/customer-tags").then(({ items }) => setCustomerTags(items)).catch(() => setCustomerTags([]));
  }, [access?.role]);

  // A location is read off the customer it belongs to, so landing on one
  // directly — a reload, a restored tab — has to fetch that customer first.
  useEffect(() => {
    if (
      access &&
      view.type === "location" &&
      selectedCustomer?.customer.id !== view.customerId
    )
      void openCustomer(view.customerId, false, { propertyId: view.propertyId });
  }, [access, view.type, view.type === "location" ? view.customerId : null, view.type === "location" ? view.propertyId : null]);
  useEffect(() => {
    if (access && view.type === "dispatch")
      void loadDispatch(view.date, { reuseWeek: true });
  }, [access, view.type, view.type === "dispatch" ? view.date : null]);
  useEffect(() => {
    if (!access?.permissions.includes("commercial:read")) return;
    if (view.type === "job" || view.type === "invoice") { if (priceBookLoadedFor !== false) void loadPriceBook(false); }
    else if (view.type === "pricebook") {
      void loadPriceBook(priceBookArchived);
      void loadCompanySettings();
    } else if (view.type === "commercial")
      void loadCommercial(view.mode, view.filter, debouncedCommercialSearch, commercialPage);
  }, [
    access,
    view.type,
    view.type === "commercial" ? view.mode : null,
    view.type === "commercial" ? view.filter : null,
    debouncedCommercialSearch,
    commercialPage,
    priceBookArchived,
  ]);

  async function loadOverview() {
    try {
      setLoading(true);
      setOverview(await data("/api/v1/workspace/overview"));
    } catch {
      setError(
        "We couldn’t load the workview data. Please refresh and try again.",
      );
    } finally {
      setLoading(false);
    }
  }
  async function loadCustomers(query = customerSearch) {
    const requestId = ++customerRequestRef.current;
    try {
      setLoading(true);
      const result = await data<{ items: Customer[] }>(
        `/api/v1/workspace/customers?q=${encodeURIComponent(query)}`,
      );
      if (requestId !== customerRequestRef.current) return;
      setCustomers(result.items);
    } catch {
      if (requestId === customerRequestRef.current)
        setError("We couldn’t load customers.");
    } finally {
      if (requestId === customerRequestRef.current) setLoading(false);
    }
  }
  async function openCustomer(
    id: string,
    shouldNavigate = true,
    options: { jobsPage?: number; propertyId?: string } = {},
  ) {
    try {
      if (jobRouteId) navigate("/dashboard", { replace: true });
      setError("");
      setLoading(true);
      const params = new URLSearchParams();
      if (options.jobsPage) params.set("jobsPage", String(options.jobsPage));
      if (options.propertyId) params.set("propertyId", options.propertyId);
      const query = params.toString();
      setSelectedCustomer(
        await data<CustomerDetail>(
          `/api/v1/workspace/customers/${id}${query ? `?${query}` : ""}`,
        ),
      );
      if (shouldNavigate)
        setView({ type: "customer", customerId: id, returnTo: "customers" });
    } catch {
      setError("We couldn’t load that customer.");
    } finally {
      setLoading(false);
    }
  }
  async function refreshCustomer(id: string, propertyId?: string) {
    const params = propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : "";
    setSelectedCustomer(
      await data<CustomerDetail>(`/api/v1/workspace/customers/${id}${params}`),
    );
  }
  // One writer for everything the customer page can do, so every panel reloads the
  // same way and an error lands in the same place.
  async function customerAction(
    customerId: string,
    path: string,
    method: "POST" | "PATCH" | "DELETE",
    body: unknown,
  ) {
    try {
      setError("");
      await mutate(path, method, body);
      await refreshCustomer(
        customerId,
        view.type === "location" ? view.propertyId : undefined,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn’t save that.");
      throw err;
    }
  }
  function loadJobContactFallback(detail: JobDetail, request: number) {
    if ((detail.propertyContacts ?? []).length || (detail.billingContacts ?? []).length) return;
    void loadBookingContacts(detail.job.customerId).then(people => {
      if (request !== jobRequestRef.current) return;
      setJobCustomerContacts(people.flatMap(person => [
        ...(person.phone ? [{ channel: "phone", value: person.phone, isPrimary: person.isPrimary ? 1 : 0 }] : []),
        ...(person.email ? [{ channel: "email", value: person.email, isPrimary: person.isPrimary ? 1 : 0 }] : []),
      ]));
    }).catch(() => { if (request === jobRequestRef.current) setJobCustomerContacts([]); });
  }
  async function openJob(
    id: string,
    returnDate = dispatchDate,
    returnTo: "dispatch" | "customer" | "commercial" = "dispatch",
    focusDocumentId?: string,
    fromCustomerId?: string,
  ) {
    if (view.type !== "job" && !(view.type === "agent" && linkedView)) setJobBackgroundView(view);
    const customerContext = fromCustomerId || (view.type === "job" && view.returnTo === "customer" ? view.customerId : undefined);
    if (customerContext && (view.type === "customer" || view.type === "location")) setCustomerRecordOrigin(view);
    const next: Extract<WorkspaceView, { type: "job" }> = {type:"job",jobId:id,returnTo: customerContext ? "customer" : returnTo,customerId:customerContext,date:returnDate};
    const jobPath = `/jobs/${encodeURIComponent(id)}`;
    if (!customerContext && location.pathname !== jobPath) {
      // The route loader owns this request; carrying the board context avoids a
      // second fetch and keeps the selected schedule behind the job card.
      setError("");
      navigate(jobPath, { state: { workspaceJob: next, focusDocumentId: focusDocumentId || "" } });
      return;
    }
    const request = ++jobRequestRef.current;
    try {
      setError(""); setLoading(true);
      setFocusDocumentId(focusDocumentId || "");
      setJobCustomerContacts([]);
      const detail = await data<JobDetail>(`/api/v1/workspace/jobs/${encodeURIComponent(id)}/summary`);
      if (request !== jobRequestRef.current) return;
      setSelectedJob(detail); setView(next);
      loadJobContactFallback(detail, request);
	      void data<JobDetail>(`/api/v1/workspace/jobs/${encodeURIComponent(id)}`).then(fullDetail => {
	        if (request === jobRequestRef.current) setSelectedJob(fullDetail);
	      }).catch(() => undefined);
    } catch {
      if (request === jobRequestRef.current) setError("We couldn’t load that job.");
    } finally {
      if (request === jobRequestRef.current) setLoading(false);
    }
  }
  async function recordPayment(
    invoiceId: string,
    body: { amountCents: number; method: string; reference: string | null },
  ) {
    await mutate(
      `/api/v1/workspace/invoices/${invoiceId}/payments`,
      "POST",
      body,
    );
    // re-read the job so the balance and the payment show without a reload
    if (view.type === "job")
      setSelectedJob(
        await data<JobDetail>(`/api/v1/workspace/jobs/${view.jobId}`),
      );
  }
  async function createCommercialDocument(
    jobId: string,
    kind: "estimate" | "invoice",
    title: string,
    lineItems: Array<{ priceBookItemId?: string; description?: string; quantity: number; unitPriceCents?: number; taxable?: boolean; stripeTaxCode?: string | null }>,
    appointmentId: string | undefined,
    jobTypeId: string,
  ) {
    try {
      setError("");
      await mutate(`/api/v1/workspace/jobs/${jobId}/commercial`, "POST", {
        kind,
        title,
        lineItems,
        ...(appointmentId ? { appointmentId } : {}),
        jobTypeId,
      });
      await openJob(jobId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "We couldn’t create that commercial record.",
      );
      throw err;
    }
  }
  async function saveAfterHoursPricing(
    jobId: string,
    body: {
      mode: "flat_fee" | "labor_multiplier" | null;
      laborMultiplier: number | null;
    },
  ) {
    try {
      setError("");
      await mutate(
        `/api/v1/workspace/jobs/${jobId}/after-hours-pricing`,
        "PATCH",
        body,
      );
      await openJob(jobId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "We couldn’t save after-hours pricing.",
      );
      throw err;
    }
  }
  async function assignJobType(jobId: string, jobTypeId: string) {
    try {
      setError("");
      await mutate(`/api/v1/workspace/jobs/${jobId}/job-type`, "PATCH", {
        jobTypeId,
      });
      await openJob(jobId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "We couldn’t set the job type.",
      );
      throw err;
    }
  }
  async function saveJobRequiredSkills(jobId: string, skillIds: string[]) {
    try {
      setError("");
      await mutate(`/api/v1/workspace/jobs/${jobId}/required-skills`, "PUT", {
        skillIds,
      });
      await openJob(jobId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "We couldn’t save required skills.",
      );
      throw err;
    }
  }
  async function resetJobRequiredSkills(jobId: string) {
    try {
      setError("");
      await mutate(
        `/api/v1/workspace/jobs/${jobId}/required-skills/reset`,
        "POST",
        {},
      );
      await openJob(jobId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "We couldn’t reset required skills.",
      );
      throw err;
    }
  }
  async function saveJobContact(
    jobId: string,
    owner: "customer" | "property",
    ownerId: string,
    contact: Omit<WorkspaceContact, "id">,
    contactId?: string,
  ) {
    try {
      setError("");
      const path = `/api/v1/workspace/${owner === "customer" ? "customers" : "properties"}/${ownerId}/contacts${contactId ? `/${contactId}` : ""}`;
      await mutate(path, contactId ? "PATCH" : "POST", contact);
      const refreshed = await data<JobDetail>(`/api/v1/workspace/jobs/${jobId}`);
      setSelectedJob((current) => current?.job.id === jobId ? refreshed : current);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "We couldn’t save that contact.",
      );
      throw err;
    }
  }
  async function deleteJobContact(
    jobId: string,
    owner: "customer" | "property",
    ownerId: string,
    contactId: string,
  ) {
    try {
      setError("");
      await mutate(
        `/api/v1/workspace/${owner === "customer" ? "customers" : "properties"}/${ownerId}/contacts/${contactId}`,
        "DELETE",
        {},
      );
      const refreshed = await data<JobDetail>(`/api/v1/workspace/jobs/${jobId}`);
      setSelectedJob((current) => current?.job.id === jobId ? refreshed : current);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "We couldn’t remove that contact.",
      );
      throw err;
    }
  }
  async function saveJobNotes(jobId: string, description: string) {
    try {
      setError("");
      await mutate(`/api/v1/workspace/jobs/${jobId}`, "PATCH", { description });
      const refreshed = await data<JobDetail>(`/api/v1/workspace/jobs/${jobId}`);
      setSelectedJob((current) => current?.job.id === jobId ? refreshed : current);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "We couldn’t save the job notes.",
      );
      throw err;
    }
  }
  async function acceptEstimate(
    jobId: string,
    estimateId: string,
    signature: { fulfillment: "now" | "later"; jobTypeId?: string; signerName?: string; signatureDataUrl?: string },
  ) {
    const accepted = await mutate<{ invoiceId: string; jobId: string }>(
      `/api/v1/workspace/jobs/${jobId}/estimates/${estimateId}/accept`,
      "POST",
      signature ?? {},
    );
    await openJob(accepted.jobId || jobId);
    return accepted;
  }
  async function openInvoice(
    invoiceId: string,
    options: {
      jobId?: string;
      returnTo: "customer" | "job" | "commercial" | "memberships" | "field";
      customerId?: string;
      collectPayment?: boolean;
      fieldTechnicianId?: string;
    },
  ) {
    if (options.returnTo === "customer" && (view.type === "customer" || view.type === "location")) setCustomerRecordOrigin(view);
    const recordCustomerId = options.customerId || (view.type === "job" && view.returnTo === "customer" ? view.customerId : undefined);
    setError("");
    setLoading(true);
    try {
      const jobId = options.jobId || (await data<{ document: { jobId?: string | null } }>(
        `/api/v1/workspace/invoices/${invoiceId}/document`,
      )).document.jobId;
      if (jobId) {
        setCollectPaymentInvoiceId(options.collectPayment ? invoiceId : "");
        await openJob(jobId, dispatchDate, (options.returnTo === "customer" || (options.returnTo === "job" && recordCustomerId)) ? "customer" : options.returnTo === "commercial" ? "commercial" : "dispatch", invoiceId, recordCustomerId);
      } else {
        // Membership billing can have an invoice without a job record.
        setView({ type: "invoice", invoiceId, ...options, customerId: recordCustomerId, date: dispatchDate });
      }
    } catch {
      setError("We couldn’t open that invoice’s job.");
    } finally {
      setLoading(false);
    }
  }
  function openEstimate(
    estimateId: string,
    options: {
      jobId?: string;
      returnTo: "customer" | "job" | "commercial" | "dispatch";
      customerId?: string;
    },
  ) {
    if (options.returnTo === "customer" && (view.type === "customer" || view.type === "location")) setCustomerRecordOrigin(view);
    setError("");
    setView({
      type: "estimate",
      estimateId,
      jobId: options.jobId,
      returnTo: options.returnTo,
      customerId: options.customerId,
      date: dispatchDate,
    });
  }
  const openCustomerInNewTab = (id: string) =>
    `/dashboard?customer=${encodeURIComponent(id)}`;
  const openJobInNewTab = (id: string) =>
    `/jobs/${encodeURIComponent(id)}`;
  /**
   * The week strip used to wait on seven full daily boards, and the day board
   * waited with it. The day payload is enough to paint the lanes; the strip
   * can fill in after. Stepping within a week still reuses the strip, so a
   * date change is one board instead of a board plus another week rebuild.
   *
   * Anything that CHANGES the day — booking, moving, assigning — still asks
   * for the week again, because the counts in that strip have moved.
   */
  async function loadDispatch(
    dateOverride = dispatchDate,
    { reuseWeek = false, loadQueue = true, loadProposals: shouldLoadProposals = true }: { reuseWeek?: boolean; loadQueue?: boolean; loadProposals?: boolean } = {},
  ) {
    const generation = ++dispatchLoadGeneration.current;
    const startedAt = performance.now();
    let boardMs: number | null = null;
    let weekMs: number | null = null;
    const haveThisWeek =
      reuseWeek &&
      dispatchWeek &&
      dateOverride >= dispatchWeek.weekStart &&
      dateOverride <= dispatchWeek.weekEnd;
    const boardRequest = (async () => {
      const started = performance.now();
      try {
        return await data<{
          appointments: DispatchItem[];
          untimedJobs: UntimedDispatchJob[];
          technicians: Technician[];
          blocks: ScheduleBlock[];
          timezone: string;
          defaultBookingDurationMinutes?: number;
          appointmentActions?: boolean;
        }>(`/api/v1/workspace/dispatch/board?date=${dateOverride}`);
      } finally {
        boardMs = Math.round(performance.now() - started);
      }
    })();
    const weekRequest = haveThisWeek
      ? Promise.resolve(dispatchWeek)
      : (async () => {
          const started = performance.now();
          try {
            return await data<DispatchWeek>(
              `/api/v1/workspace/dispatch/week?date=${dateOverride}`,
            );
          } catch {
            // Handle failure immediately, even if the board is still pending or fails.
            return null;
          } finally {
            weekMs = Math.round(performance.now() - started);
          }
        })();
    const reportLoad = (outcome: "success" | "error") => {
      if (generation !== dispatchLoadGeneration.current) return;
      setLoading(false);
      window.requestAnimationFrame(() =>
        window.dispatchEvent(
          new CustomEvent("workview:dispatch-load", {
            detail: {
              outcome,
              totalMs: Math.round(performance.now() - startedAt),
              boardMs,
              weekMs,
              reusedWeek: haveThisWeek,
            },
          }),
        ),
      );
    };
    try {
      setLoading(true);
      const result = await boardRequest;
      if (generation !== dispatchLoadGeneration.current) return;
      setDispatch(result.appointments);
      if (loadQueue) setUntimedJobs([]);
      setAppointmentActions(result.appointmentActions === true);
      setTechnicians(result.technicians);
      setBlocks(result.blocks);
      setDispatchTimezone(result.timezone);
      setDefaultBookingDurationMinutes(result.defaultBookingDurationMinutes ?? 120);
      reportLoad("success");
		// Restore visual dispatch context only after the indexed calendar has
		// painted.  This request is capped to the IDs already on screen, so tags,
		// zones, and crew progress cannot turn a board refresh into a table scan.
		window.requestAnimationFrame(() => {
			const ids = result.appointments.map((appointment) => appointment.id);
			if (!ids.length) return;
			void data<{ items: Array<Pick<DispatchItem, "id" | "serviceZones" | "serviceZoneNames" | "locationTags" | "technicianProgress">> }>(
				`/api/v1/workspace/dispatch/board/presentation?appointmentIds=${encodeURIComponent(ids.join(","))}`,
			).then(({ items }) => {
				if (generation !== dispatchLoadGeneration.current) return;
				const byId = new Map(items.map((item) => [item.id, item]));
				setDispatch((current) => current.map((item) => ({ ...item, ...(byId.get(item.id) ?? {}) })));
			}).catch(() => {
				// The operational board remains usable if supplementary labels fail.
			});
		});
      // Keep the expensive unscheduled-work scan off the calendar's critical
      // path. It is a lower-board queue and can fill in after the lanes paint.
      if (loadQueue) {
        window.requestAnimationFrame(() => {
          void data<{ items: UntimedDispatchJob[] }>("/api/v1/workspace/dispatch/untimed-jobs")
            .then(({ items }) => {
              if (generation === dispatchLoadGeneration.current) setUntimedJobs(items);
            })
            .catch(() => {
              // The board remains useful when this supplemental queue fails.
            });
        });
      }
      const week = await weekRequest;
      if (week && generation === dispatchLoadGeneration.current) setDispatchWeek(week);
    } catch {
      if (generation !== dispatchLoadGeneration.current) return;
      setError("We couldn’t load the dispatch board.");
      reportLoad("error");
    }
    // The board is on screen by now; proposals do not need to be polled with
    // every calendar refresh.
    if (shouldLoadProposals) void loadProposals();
  }
  useEffect(() => {
    if (view.type !== "dispatch") return;
    let cancelled = false;
    let refresh: number | undefined;
    const scheduleRefresh = () => {
      refresh = window.setTimeout(async () => {
        // Schedule the next poll only after this request settles. setInterval
        // used to start identical board queries every 15 seconds even when the
        // prior one was still reading D1.
        await loadDispatch(view.date, { reuseWeek: true, loadQueue: false, loadProposals: false });
        if (!cancelled) scheduleRefresh();
      }, 60_000);
    };
    scheduleRefresh();
    return () => { cancelled = true; if (refresh !== undefined) window.clearTimeout(refresh); };
  }, [view.type, view.type === "dispatch" ? view.date : ""]);
  async function searchJobs(query: string) {
    try {
      setJobs(
        (
          await data<{ items: JobOption[] }>(
            `/api/v1/workspace/dispatch/jobs?q=${encodeURIComponent(query)}`,
          )
        ).items,
      );
    } catch {
      setError("We couldn’t find matching jobs.");
    }
  }
  const searchBookingCustomers = useCallback(async (query: string) => {
    return (
      await data<{ items: BookingCustomer[] }>(
        `/api/v1/workspace/dispatch/customers?q=${encodeURIComponent(query)}`,
      )
    ).items;
  }, []);
  const loadBookingProperties = useCallback(async (customerId: string) => {
    return (
      await data<{ items: BookingProperty[] }>(
        `/api/v1/workspace/dispatch/customers/${customerId}/properties`,
      )
    ).items;
  }, []);
  const loadBookingContacts = useCallback(async (
    customerId: string,
    propertyId?: string | null,
  ) => {
    const people = async (path: string, from: "customer" | "location") =>
      (await data<{ items: WorkspaceContact[] }>(path))
        .items.map((contact) => ({ ...contact, from }));
    const [onCustomer, atLocation] = await Promise.all([
      people(`/api/v1/workspace/customers/${customerId}/contacts`, "customer"),
      propertyId
        ? people(`/api/v1/workspace/properties/${propertyId}/contacts`, "location")
        : Promise.resolve([] as Array<WorkspaceContact & { from: "location" }>),
    ]);
    const merged: BookingContact[] = [...onCustomer, ...atLocation];
    // Preserve legacy account numbers when adding the first named contact.
    // Only omit a method when the same value is already on a customer contact.
    const { contactMethods, customer: account } = await data<{
      contactMethods: Array<{ channel: string; value: string; isPrimary: number }>;
      customer: { email: string | null };
    }>(`/api/v1/workspace/customers/${customerId}`);
    const accountMethods = account.email ? [...contactMethods, { channel: "email", value: account.email, isPrimary: 1 }] : contactMethods;
    const legacy = dedupeCustomerContactMethods(accountMethods).filter((method) =>
      !onCustomer.some((contact) => method.channel.toLowerCase() === "email"
        ? contact.email?.trim().toLowerCase() === method.value.trim().toLowerCase()
        : contact.phone?.replace(/\D/g, "") === method.value.replace(/\D/g, "")),
    );
    return [...merged, ...legacy.map((method, index) => methodAsBookingContact(method, index, ""))];
  }, []);
  const loadBookingJobTypes = useCallback(async () => {
    return (
      await data<{ items: BookingJobType[] }>(
        "/api/v1/workspace/dispatch/job-types",
      )
    ).items.filter((item) => item.active);
  }, []);
  async function saveDispatch(
    path: string,
    method: "POST" | "PATCH" | "PUT",
    body: unknown,
  ) {
    try {
      const result = await mutate<{ warning?: string | null }>(path, method, body);
      setError("");
      await loadDispatch();
      return result;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to save dispatch changes.",
      );
      throw err;
    }
  }
  const loadProposals = useCallback(async () => {
    try {
      setProposals(
        (
          await data<{ items: DispatchProposal[] }>(
            "/api/v1/workspace/dispatch/proposals",
          )
        ).items,
      );
    } catch {
      setError("We couldn’t load dispatch proposals.");
    }
  }, []);
  async function decideProposal(id: string, decision: "approve" | "reject") {
    try {
      setProposalBusy(id);
      await mutate(
        `/api/v1/workspace/dispatch/proposals/${id}/${decision}`,
        "POST",
        decision === "reject"
          ? { note: "Rejected from the dispatch board" }
          : {},
      );
      await Promise.all([
        loadProposals(),
        view.type === "dispatch" ? loadDispatch(view.date) : Promise.resolve(),
      ]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to review the proposal.",
      );
    } finally {
      setProposalBusy(null);
    }
  }
  async function loadCompanySettings() {
    try {
      const payload = await data<CompanyData>("/api/v1/workspace/company");
      setCompanySettings({
        form: formFromData(payload),
        jobTypes: payload.jobTypes ?? [],
      });
    } catch {
      setCompanySettings(null);
    }
  }
  async function savePricingRules(next: SettingsForm) {
    await mutate("/api/v1/workspace/company", "PATCH", companyPatchBody(next));
    await loadCompanySettings();
  }
  async function loadCommercial(
    typeOverride: "invoices" | "estimates" = commercialType,
    filterOverride: CommercialFilter = commercialFilter,
    searchOverride = commercialSearch,
    pageOverride = commercialPage,
  ) {
    const requestId = ++commercialRequestRef.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        type: typeOverride,
        filter: filterOverride,
        page: String(pageOverride),
        pageSize: "20",
      });
      if (searchOverride.trim()) params.set("q", searchOverride.trim());
      const result = await data<{
        items: CommercialItem[];
        summary: CommercialSummary;
        total?: number;
        page?: number;
        pageSize?: number;
      }>(`/api/v1/workspace/commercial?${params}`);
      if (requestId !== commercialRequestRef.current) return;
      setCommercial(result.items);
      setCommercialSummary(result.summary);
      setCommercialTotal(result.total);
      setCommercialPageSize(result.pageSize);
    } catch {
      if (requestId === commercialRequestRef.current)
        setError("We couldn’t load commercial records.");
    } finally {
      if (requestId === commercialRequestRef.current) setLoading(false);
    }
  }
  async function loadPriceBook(archived = priceBookArchived) {
    const requestId = ++priceBookRequestRef.current;
    try {
      setPriceBookLoading(true);
      const result = await data<{
        categories: PriceBookCategory[];
        vendors?: Array<{ id: string; name: string }>;
        supportsItemDetails?: boolean;
        items: PriceBookItem[];
        readiness: PriceBookReadiness;
        accountingAccounts?: Array<{ accountKind: "income" | "expense"; name: string; itemCount: number }>;
      }>(`/api/v1/workspace/pricebook?archived=${archived}`);
      if (requestId !== priceBookRequestRef.current) return;
      // The preview API may be one release behind this UI. Derive imported
      // accounts from the existing Pricebook response so the selectors remain
      // usable while the API migration rolls out.
      const fallbackAccounts = new Map<string, { accountKind: "income" | "expense"; name: string; itemCount: number }>();
      for (const item of result.items) {
        if (item.sourceIncomeAccountName) fallbackAccounts.set(`income:${item.sourceIncomeAccountName.toLocaleLowerCase()}`, { accountKind: "income", name: item.sourceIncomeAccountName, itemCount: 0 });
        if (item.sourceExpenseAccountName) fallbackAccounts.set(`expense:${item.sourceExpenseAccountName.toLocaleLowerCase()}`, { accountKind: "expense", name: item.sourceExpenseAccountName, itemCount: 0 });
      }
      const accountingAccounts = result.accountingAccounts ?? [...fallbackAccounts.values()];
      let vendorOptions = result.vendors;
      if (!vendorOptions && can("inventory:read")) {
        try {
          const vendors = await data<{ items: Array<{ id: string; name: string; active: boolean | number }> }>("/api/v1/workspace/inventory/vendors");
          vendorOptions = vendors.items.filter(vendor => vendor.active).map(({ id, name }) => ({ id, name }));
        } catch { /* Keep the existing catalog usable without inventory access. */ }
      }
      if (requestId !== priceBookRequestRef.current) return;
      setPriceBook({ ...result, vendors: vendorOptions, supportsItemDetails: result.supportsItemDetails === true, accountingAccounts });
      setPriceBookLoadedFor(archived);
    } catch {
      if (requestId === priceBookRequestRef.current)
        setError("We couldn’t load the pricebook.");
    } finally {
      if (requestId === priceBookRequestRef.current) setPriceBookLoading(false);
    }
  }
  async function signOut() {
    await authClient.signOut();
    navigate("/");
  }
  const navigateWorkspace = useCallback((next: WorkspaceView) => {
    if (next.type === "inventory" && next.purchaseOrderId) {
      navigate(`/purchase-orders/${encodeURIComponent(next.purchaseOrderId)}`);
      setError("");
      setView(next);
      return;
    }
    if (next.type === "dispatch") {
      // Dispatch's date is part of the user's working context. Put it in the
      // URL so a refresh, bookmark, or shared link restores the same board.
      const destination = `/dashboard${workspaceViewSearch(next)}`;
      if (`${location.pathname}${location.search}` !== destination) navigate(destination);
      setError("");
      setView(next);
      return;
    }
    if (jobRouteId || purchaseOrderRouteId) navigate("/dashboard", { replace: true });
    setError("");
    setView(next);
  }, [jobRouteId, location.pathname, location.search, purchaseOrderRouteId, navigate]);
  const handleOperatorNavigation = useCallback(
    (navigation: OperatorNavigation) => {
      if (navigation.view === "dispatch") {
        const nextDate = navigation.date || dispatchDate;
        setDispatchDate(nextDate);
        navigateWorkspace({ type: "dispatch", date: nextDate });
        return;
      }
      if (navigation.view === "customer" && navigation.customerId) {
        void openCustomer(navigation.customerId);
        return;
      }
      if (navigation.view === "job" && navigation.jobId) {
        void openJob(navigation.jobId);
        return;
      }
      navigateWorkspace({ type: "customers", search: customerSearch });
    },
    [customerSearch, dispatchDate, navigateWorkspace],
  );
  const setDispatchDateAndView = useCallback(
    (nextDate: string) => {
      setDispatchDate(nextDate);
      navigateWorkspace({ type: "dispatch", date: nextDate });
    },
    [navigateWorkspace],
  );
  const selectTopLevel = useCallback(
    (id: WorkspaceNavId) => {
      if (id === "search") { setSearchFocusRequest(request => request + 1); return; }
      if (id === "texts") { setActiveWidget(widget => widget === "texts" ? null : "texts"); return; }
      navigateWorkspace(
        topLevelWorkspaceView(id, {
          search: customerSearch,
          date: dispatchDate,
          mode: commercialType,
          filter: commercialFilter,
        }),
      );
    },
    [
      commercialFilter,
      commercialType,
      customerSearch,
      dispatchDate,
      navigateWorkspace,
    ],
  );
  const can = useCallback(
    (permission: string) => access?.permissions.includes(permission) ?? false,
    [access],
  );
  const availableNav = useMemo(
    () =>
      workspaceNav.filter((item) => {
        if (!access || access.dataScope !== "company") return false;
        if (item.id === "dispatch" || item.id === "timesheets" || item.id === "agent")
          return can("scheduling:read");
        if (item.id === "search") return can("customers:read") || can("jobs:read") || can("commercial:read");
        if (item.id === "learning") return can("learning:read");
        if (item.id === "texts") return can("scheduling:read") && can("customers:read");
        if (item.id === "communications") return can("scheduling:read") && can("customers:read") && can("jobs:read");
        if (item.id === "projects") return can("jobs:read");
        if (item.id === "customers") return can("customers:read");
        if (item.id === "pricebook") return can("commercial:read");
        if (item.id === "inventory") return can("inventory:read");
        if (item.id === "foundry_analysis") return access.role === "owner" || access.role === "admin";
        return can("commercial:read");
      }),
    [access, can],
  );
  const activeCustomerName = selectedCustomer
    ? selectedCustomer.customer.displayName ||
      `${selectedCustomer.customer.firstName} ${selectedCustomer.customer.lastName}`
    : "";

  if (isPending || !session?.user || (!access && loading))
    return (
      <main className="grid min-h-screen place-items-center bg-[var(--paper)] text-sm text-[var(--ink-soft)]">
        Loading WorkView…
      </main>
    );
  if (!access)
    return (
      <main className="grid min-h-screen place-items-center bg-[var(--paper)] px-6 text-center">
        <div>
          <p className="text-lg font-semibold text-[var(--ink)]">
            Tenant access required
          </p>
          <p className="mt-2 max-w-md text-sm text-[var(--ink-soft)]">
            {error || "Your account is not assigned to a WorkView tenant yet."}
          </p>
          <button
            type="button"
            onClick={() =>
              void authClient.signOut().then(() => navigate("/auth"))
            }
            className="mt-5 rounded-lg border border-[var(--line)] px-4 py-2 text-sm font-semibold text-[var(--ink-soft)]"
          >
            Sign out
          </button>
        </div>
      </main>
    );
  if (access.role === "technician")
    return (
      <TechnicianMyWork
        userName={session.user.name || session.user.email || "Technician"}
      />
    );
  const viewTitle =
    view.type === "customer"
      ? activeCustomerName
      : view.type === "job" && selectedJob
        ? `Job #${selectedJob.job.jobNumber}`
        : view.type === "invoice"
          ? "Invoice"
          : view.type === "estimate"
            ? "Estimate"
            : view.type === "booking_agent" ? "Communications · Call review" : availableNav.find((item) => item.id === view.type)?.label ||
            "Workspace";
  const isActive = (id: WorkspaceNavId) => id === "texts" ? textsOpen : isWorkspaceNavActive(screenView, id);
  const operatorProps = {
    authUserId: session.user.id,
    timezone: dispatchTimezone,
    proposals,
    proposalBusy,
    onNavigate: handleOperatorNavigation,
    onProposalCreated: loadProposals,
    onApprove: (id: string) => void decideProposal(id, "approve"),
    onReject: (id: string) => void decideProposal(id, "reject"),
  };
  return (
    <RecordCommunicationsContext.Provider value={{
      textsOpen,
      openTechnicianText: can("scheduling:write") && can("customers:read") ? target => { setTextThreadId(null); setTextRecipient(null); setTechnicianTextTarget(target); setActiveWidget("texts"); } : undefined,
      openText: can("scheduling:read") && can("customers:read") ? target => { setTechnicianTextTarget(null); setTextThreadId(null); setTextRecipient(target); setActiveWidget("texts"); } : undefined,
    }}>
    <main
      className={`wv workspace-shell min-h-screen workspace-with-icon-rail bg-[var(--paper)] text-[var(--ink)] ${textsOpen && view.type === "job" ? "workspace-texts-with-job" : ""}`}
    >

        {error && (
          <div
            className="fixed right-4 top-4 z-[80] flex w-[min(27rem,calc(100vw-2rem))] items-start gap-3 rounded-2xl border border-[var(--line)] bg-[var(--paper)] px-4 py-3 text-sm text-[var(--ink)] shadow-[0_18px_50px_rgba(15,23,42,0.18)]"
            role="alert"
          >
            <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--accent-wash)] text-[var(--accent)]">
              !
            </span>
            <span className="min-w-0 flex-1">{error}</span>
            <button
              type="button"
              onClick={() => setError("")}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--ink-soft)] transition hover:bg-[var(--wash)] hover:text-[var(--ink)]"
              aria-label="Dismiss error"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
                <path d="M2 2 10 10M10 2 2 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}
        <header ref={workspaceHeader} className="wv-header sticky top-0 z-[60] flex min-h-[60px] items-center justify-between gap-3 border-b border-[var(--line)] bg-[var(--paper)]/95 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <CompanyHeaderBrand />
            {view.type !== "job" && view.type !== "dispatch" && <span className="hidden truncate text-xs text-[var(--ink-soft)] sm:block">{viewTitle}</span>}
          </div>
          <WorkspaceSearch focusRequest={searchFocusRequest} query={universalSearch} onQueryChange={setUniversalSearch}
                canCustomers={can("customers:read")} canJobs={can("jobs:read")} canCommercial={can("commercial:read")}
                onOpen={(kind, id, jobId) => {
                  if (kind === "customers") void openCustomer(id);
                  else if (kind === "jobs") void openJob(id);
                  else if (kind === "invoices") openInvoice(id, { jobId, returnTo: "commercial" });
                  else openEstimate(id, { jobId, returnTo: "commercial" });
                }} />
          <div className="relative ml-auto flex items-center gap-2">
            {availableNav.some((item) => item.id === "agent") && view.type !== "agent" && view.type !== "dispatch" && (
              <button
                type="button"
                onClick={() => selectTopLevel("agent")}
                className="hidden min-h-10 items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--paper)] px-3 text-sm font-semibold text-[var(--ink)] shadow-[0_1px_0_rgba(15,23,42,0.02)] transition hover:border-[var(--accent)] hover:text-[var(--accent)] sm:inline-flex"
              >
                <span className="grid h-5 w-5 place-items-center rounded-full bg-[var(--accent-wash)] text-[var(--accent)]">
                  <WorkspaceNavGlyph id="agent" />
                </span>
                Ask AI
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsProfileMenuOpen((open) => !open)}
              aria-expanded={isProfileMenuOpen}
              aria-haspopup="menu"
              aria-label="Company settings"
              className="grid h-9 w-9 place-items-center rounded-full text-[var(--ink-soft)] transition hover:bg-[var(--wash)] hover:text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--line-strong)] focus:ring-offset-2"
            >
              <GearIcon />
            </button>
            {isProfileMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-12 z-50 min-w-56 overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)] py-1.5 shadow-lg"
              >
                <p className="truncate px-3.5 pb-1.5 pt-1 text-xs text-[var(--ink-soft)]">
                  {session.user.name || session.user.email}
                </p>
                <Link
                  to="/my-profile"
                  role="menuitem"
                  className="block px-3.5 py-2 text-sm font-medium text-[var(--ink)] transition hover:bg-[var(--wash)]"
                >
                  My profile
                </Link>
                {can("company:read") && (
                  <Link
                    to="/mycompany"
                    role="menuitem"
                    className="block px-3.5 py-2 text-sm font-medium text-[var(--ink)] transition hover:bg-[var(--wash)]"
                  >
                    Company settings
                  </Link>
                )}
                {can("company:read") && (
                  <Link to="/mycompany?settings=zones" role="menuitem" className="block px-3.5 py-2 text-sm font-medium text-[var(--ink)] transition hover:bg-[var(--wash)]">
                    Zones
                  </Link>
                )}
                <a
                  href={SUPPORT_MAILTO}
                  role="menuitem"
                  className="block px-3.5 py-2 text-sm font-medium text-[var(--ink)] transition hover:bg-[var(--wash)]"
                >
                  Help &amp; Support
                </a>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void signOut()}
                  className="w-full px-3.5 py-2 text-left text-sm font-medium text-[var(--ink-soft)] transition hover:bg-[var(--wash)]"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>
      {textsOpen && technicianTextTarget && can("scheduling:write") && can("customers:read") && <TechnicianTextPanel key={technicianTextTarget.id} technician={technicianTextTarget} timezone={dispatchTimezone} onClose={() => { setActiveWidget(null); setTechnicianTextTarget(null); }} />}
      {textsOpen && !technicianTextTarget && can("scheduling:read") && can("customers:read") && <TextsWorkspace canAttachPhotos={can("jobs:read") && can("jobs:write")} canReply={can("scheduling:write")} timezone={dispatchTimezone} initialThreadId={textThreadId} initialRecipient={textRecipient} withJob={view.type === "job"} onClose={() => { setActiveWidget(null); setTextThreadId(null); setTextRecipient(null); }} />}
      <WorkspaceNavigation unreadTexts={unreadTexts} items={availableNav} isActive={isActive} onSelect={selectTopLevel} />
      <section className="workspace-content">
        <div className={screenView.type === "dispatch" ? "w-full px-2 py-4 sm:px-3 lg:px-4" : "mx-auto max-w-[1880px] px-4 py-6 sm:px-6"}>
          <div
            className={
              screenView.type === "agent"
                ? "lg:mx-auto lg:w-full lg:max-w-6xl"
                : undefined
            }
          >
            {screenView.type === "agent" && (
              <AgentWorkspace
                operatorProps={operatorProps}
                userName={session.user.name || session.user.email || ""}
              />
            )}
            {screenView.type === "booking_agent" && can("scheduling:read") && can("customers:read") && can("jobs:read") && <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><div><h1 className="text-xl font-semibold">Call review assistant</h1><p className="text-sm text-[var(--ink-soft)]">Review existing calls and booking requests. AI phone answering is not connected yet.</p></div><button type="button" onClick={() => selectTopLevel("communications")} className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-medium hover:bg-[var(--wash)]">Back to Communications</button></div>
              <BookingAgent canReview={can("scheduling:write")} onBook={can("scheduling:write") && can("jobs:write") && can("customers:write") ? (request: BookingRequest) => {
              setBookingCustomer(null);
              setBookingCaller({ phone: request.phone ?? "", customerName: request.customerName, callId: request.id, bookingAgentLeadId: request.id.slice("lead:".length), bookingNotes: request.notes });
              setDispatchDateAndView(new Intl.DateTimeFormat("en-CA", { timeZone: dispatchTimezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()));
            } : undefined} />
            </>}
            {screenView.type === "communications" && can("scheduling:read") && can("customers:read") && can("jobs:read") && (
              <CommunicationsBoard canCancelJob={can("jobs:write")} canReview={can("scheduling:write")} timezone={dispatchTimezone} onOpenJob={id => void openJob(id)} onOpenCustomer={id => void openCustomer(id)} onOpenCallReview={() => navigateWorkspace({ type: "booking_agent" })} />
            )}
            {screenView.type === "learning" && (
              <LearningWorkspace canWrite={can("learning:write")} />
            )}
            {screenView.type === "foundry_analysis" && (access.role === "owner" || access.role === "admin") && <FoundryAnalysisWorkspace />}
            {screenView.type === "memberships" && can("commercial:read") && (
              <MembershipsWorkspace canWrite={can("commercial:write")} canSchedule={can("scheduling:write") && can("commercial:write") && can("jobs:write")}
                openInvoice={(invoiceId, collectPayment) => void openInvoice(invoiceId, { collectPayment, returnTo: "memberships" })}
                openCustomer={customerId => void openCustomer(customerId)} openJob={jobId => void openJob(jobId)}
                onBook={prefill => {
                  const date = new Date().toISOString().slice(0,10);
                  setDispatchDate(date); setBookingCaller(null);
                  setBookingCustomer({id:prefill.customerId,firstName:prefill.customerName,lastName:"",email:null,preferredPropertyId:prefill.propertyId,bookingNotes:prefill.notes,membershipBooking:prefill});
                  navigateWorkspace({type:"dispatch",date});
                }}
                schedule={(id, events, done, enabled) => <MembershipBookingDialog enabled={enabled} membershipId={id} events={events} onClose={done} onDone={done} />}
              />
            )}
            {screenView.type === "projects" && can("jobs:read") && <ProjectsWorkspace canWrite={can("jobs:write")} key={screenView.projectId || "projects"} projectId={screenView.projectId} openProject={id => navigateWorkspace({ type: "projects", projectId: id })} back={() => navigateWorkspace({ type: "projects" })} openCustomer={id => void openCustomer(id)} />}
            {screenView.type === "customers" && (
              <Customers
                canReadCommercial={can("commercial:read")}
                customers={customers}
                tags={customerTags}
                search={customerSearch}
                onSearch={(value) => {
                  setCustomerSearch(value);
                  navigateWorkspace({ type: "customers", search: value });
                }}
                open={openCustomer}
              />
            )}
            {customerRecordView?.type === "location" && selectedCustomer && (
              <LocationWorkspace
                openEstimate={(id) => openEstimate(id, { returnTo: "customer", customerId: selectedCustomer.customer.id })}
                openInvoice={(id) => openInvoice(id, { returnTo: "customer", customerId: selectedCustomer.customer.id })}
                priceBookItems={priceBook.items}
                priceBookCategories={priceBook.categories}
                canCreateEstimate={can("commercial:write")}
                canReadJobs={can("jobs:read")}
                canReadCommercial={can("commercial:read")}
                detail={selectedCustomer}
                propertyId={customerRecordView.propertyId}
                timezone={dispatchTimezone}
                openJob={id => `/dashboard?job=${encodeURIComponent(id)}&from=${encodeURIComponent(selectedCustomer.customer.id)}`}
                onOpenJob={id => void openJob(id, dispatchDate, "customer", undefined, selectedCustomer.customer.id)}
                canBookJobs={can("scheduling:write") && selectedCustomer.properties.find((property) => property.id === customerRecordView.propertyId)?.active !== 0}
                bookJob={(customer) => {
                  setDispatchDate(new Date().toISOString().slice(0, 10));
                  navigateWorkspace({
                    type: "dispatch",
                    date: new Date().toISOString().slice(0, 10),
                  });
                  setBookingCustomer(customer);
                }}
                canWrite={can("commercial:write") || can("customers:write")}
                canManageLocation={can("customers:write")}
                act={customerAction}
                tags={customerTags}
                back={() => {
                  void openCustomer(customerRecordView.customerId);
                }}
              />
            )}
            {customerRecordView?.type === "customer" && selectedCustomer && (
              <CustomerWorkspace
                onOpenInvoiceId={id => void openInvoice(id, { returnTo: "customer", customerId: selectedCustomer.customer.id })}
                canReadJobs={can("jobs:read")}
                timezone={dispatchTimezone}
                openLocation={(propertyId) => {
                  void openCustomer(customerRecordView.customerId, false, { propertyId }).then(
                    () =>
                      navigateWorkspace({
                        type: "location",
                        customerId: customerRecordView.customerId,
                        propertyId,
                      }),
                  );
                }}
                openInvoice={(invoice) =>
                  openInvoice(invoice.id, {
                    jobId: invoice.jobId,
                    returnTo: "customer",
                    customerId: selectedCustomer.customer.id,
                  })
                }
                openEstimate={(estimate) =>
                  openEstimate(estimate.id, {
                    jobId: estimate.jobId,
                    returnTo: "customer",
                    customerId: selectedCustomer.customer.id,
                  })
                }
                canReadCommercial={can("commercial:read")}
                canWriteCustomers={can("customers:write")}
                canWriteCommercial={can("commercial:write")}
                canBookJobs={can("scheduling:write")}
                act={customerAction}
                tags={customerTags}
                detail={selectedCustomer}
                openJob={id => `/dashboard?job=${encodeURIComponent(id)}&from=${encodeURIComponent(selectedCustomer.customer.id)}`}
                onOpenJob={id => void openJob(id, dispatchDate, "customer", undefined, selectedCustomer.customer.id)}
                bookJob={(customer) => {
                  setDispatchDate(new Date().toISOString().slice(0, 10));
                  navigateWorkspace({
                    type: "dispatch",
                    date: new Date().toISOString().slice(0, 10),
                  });
                  setBookingCustomer(customer);
                }}
                back={() =>
                  navigateWorkspace(
                    parentWorkspaceView(customerRecordView, { search: customerSearch }),
                  )
                }
              />
            )}
            {view.type === "job" && selectedJob && (
              <JobWorkspace
                key={selectedJob.job.id}
                detail={selectedJob}
                canSchedule={can("scheduling:write")}
                inventoryPermissions={{ read: can("inventory:read"), write: can("inventory:write"), approve: can("inventory:approve"), adjust: can("inventory:adjust") }}
                reloadJob={() =>
                  openJob(
                    view.jobId,
                    view.date,
                    view.returnTo,
                    focusDocumentId,
                    view.customerId,
                  )
                }
                priceBookItems={priceBook.items}
                priceBookCategories={priceBook.categories}
                timezone={dispatchTimezone}
                openHistoricalTextThread={openHistoricalTextThread}
                focusDocumentId={focusDocumentId}
                collectPaymentInvoiceId={collectPaymentInvoiceId}
                customerContacts={jobCustomerContacts}
                backLabel={
                  jobBackgroundView ? "Back"
                  : view.returnTo === "customer"
                    ? "Back to customer"
                    : view.returnTo === "commercial"
                      ? "Back to accounting"
                      : "Back to dispatch"
                }
                back={() => {
                  if (jobBackgroundView) {
                    navigateWorkspace(jobBackgroundView);
                    return;
                  }
                  if (view.returnTo === "customer" && view.customerId)
                    customerRecordOrigin?.customerId === view.customerId ? setView(customerRecordOrigin) : void openCustomer(view.customerId);
                  else if (view.returnTo === "commercial")
                    navigateWorkspace({
                      type: "commercial",
                      mode: commercialType,
                      filter: commercialFilter,
                    });
                  else
                    navigateWorkspace(
                      parentWorkspaceView(view, { search: customerSearch }),
                    );
                }}
                openCustomer={openCustomer}
                openInvoice={(invoiceId) =>
                  openInvoice(invoiceId, { jobId: view.jobId, returnTo: "job", customerId: view.customerId })
                }
                openRelatedJob={(jobId, focus) => void openJob(jobId, view.date, view.returnTo, focus, view.customerId)}
                openRelatedInvoice={(invoiceId, jobId) =>
                  openInvoice(invoiceId, { jobId, returnTo: "job", customerId: view.customerId })
                }
                openEstimate={(estimateId) =>
                  openEstimate(estimateId, { jobId: view.jobId, returnTo: "job", customerId: view.customerId })
                }
                canCreateCommercial={can("jobs:write")}
                canEditCommercial={can("commercial:write")}
                canEditContacts={can("customers:write")}
                saveContact={saveJobContact}
                deleteContact={deleteJobContact}
                saveJobNotes={saveJobNotes}
                saveAfterHoursPricing={saveAfterHoursPricing}
                assignJobType={assignJobType}
                saveJobRequiredSkills={saveJobRequiredSkills}
                resetJobRequiredSkills={resetJobRequiredSkills}
                createCommercial={createCommercialDocument}
                acceptEstimate={acceptEstimate}
                recordPayment={recordPayment}
              />
            )}
            {screenView.type === "invoice" && (
              <InvoiceWorkspace
                invoiceId={screenView.invoiceId}
                customerEmail={selectedCustomer && selectedCustomer.customer.id === screenView.customerId ? selectedCustomer.customer.email || selectedCustomer.contacts?.find(contact => contact.email)?.email || selectedCustomer.contactMethods.find(method => method.channel === "email")?.value || "" : ""}
                startCollectPayment={screenView.collectPayment}
                priceBookItems={priceBook.items}
                priceBookCategories={priceBook.categories}
                canWrite={can("commercial:write")}
                recordPayment={recordPayment}
                backLabel={screenView.returnTo === "field" ? "Return to field job" : screenView.returnTo === "job" ? "Return to job" : screenView.returnTo === "customer" ? "Return to customer" : screenView.returnTo === "memberships" ? "Return to memberships" : "Return to invoices"}
                back={() => {
                  if (screenView.returnTo === "memberships") navigateWorkspace({ type: "memberships" });
                  else if (screenView.returnTo === "field" && screenView.jobId) {
                    const params = new URLSearchParams({ jobId: screenView.jobId });
                    if (screenView.fieldTechnicianId) params.set("technicianId", screenView.fieldTechnicianId);
                    window.location.assign(`${window.location.hostname.includes("preview") ? "https://field-preview.workviewpro.com" : "https://field.workviewpro.com"}/technician?${params}`);
                  }
                  else if (screenView.returnTo === "customer" && screenView.customerId)
                    customerRecordOrigin?.customerId === screenView.customerId ? setView(customerRecordOrigin) : void openCustomer(screenView.customerId);
                  else if (screenView.returnTo === "job" && screenView.jobId)
                    void openJob(screenView.jobId, screenView.date, screenView.customerId && customerRecordOrigin?.customerId === screenView.customerId ? "customer" : "dispatch", undefined, screenView.customerId);
                  else
                    navigateWorkspace({
                      type: "commercial",
                      mode: "invoices",
                      filter: commercialFilter,
                    });
                }}
              />
            )}
            {screenView.type === "estimate" && (
              <EstimateWorkspace
                key={screenView.estimateId}
                estimateId={screenView.estimateId}
                canWrite={can("commercial:write")}
                openJob={(jobId, step) => void openJob(jobId, screenView.date ?? dispatchDate, screenView.returnTo === "customer" ? "customer" : screenView.returnTo === "commercial" ? "commercial" : "dispatch", step ? `job-step:${step}` : screenView.estimateId, screenView.customerId)}
                back={() => {
                  if (screenView.returnTo === "customer" && screenView.customerId)
                    customerRecordOrigin?.customerId === screenView.customerId ? setView(customerRecordOrigin) : void openCustomer(screenView.customerId);
                  else if (screenView.returnTo === "job" && screenView.jobId)
                    void openJob(screenView.jobId, screenView.date, screenView.customerId && customerRecordOrigin?.customerId === screenView.customerId ? "customer" : "dispatch", undefined, screenView.customerId);
                  else if (screenView.returnTo === "dispatch")
                    navigateWorkspace({ type: "dispatch", date: screenView.date ?? dispatchDate });
                  else
                    navigateWorkspace({
                      type: "commercial",
                      mode: "estimates",
                      filter: commercialFilter,
                    });
                }}
              />
            )}
            {(screenView.type === "dispatch" || (screenView.type === "job" && screenView.returnTo === "dispatch")) && (
              <DispatchControlRoom
                onScheduleComplete={() => { void loadDispatch(); }}
                onOpenEstimate={can("commercial:read") ? (id) => openEstimate(id, { returnTo: "dispatch" }) : undefined}
                loadDispatchJobContext={(jobId) =>
                  data<DispatchJobContext>(`/api/v1/workspace/dispatch/jobs/${encodeURIComponent(jobId)}/context`)
                }
                items={dispatch}
                untimedJobs={untimedJobs}
                technicians={technicians}
                blocks={blocks}
                week={dispatchWeek}
                proposals={proposals}
                timezone={dispatchTimezone}
                defaultBookingDurationMinutes={defaultBookingDurationMinutes}
                proposalBusy={proposalBusy}
                date={dispatchDate}
                setDate={setDispatchDateAndView}
                jobs={jobs}
                searchJobs={searchJobs}
                searchCustomers={searchBookingCustomers}
                loadProperties={loadBookingProperties}
                loadContacts={loadBookingContacts}
                loadMemberships={can("customers:read") && can("commercial:read") ? loadCustomerMemberships : undefined}
                loadJobTypes={loadBookingJobTypes}
                appointmentActions={appointmentActions}
                canConfirmAppointments={can("scheduling:write")}
                save={saveDispatch}
                open={openCustomerInNewTab}
                openJob={openJobInNewTab}
                onOpenJob={(id, focus) => void openJob(id, dispatchDate, "dispatch", focus)}
                onOpenCustomer={(id) => void openCustomer(id)}
                onOpenInvoice={(invoiceId, jobId, customerId) =>
                  openInvoice(invoiceId, { jobId, returnTo: "job", customerId })
                }
                bookingCustomer={bookingCustomer}
                bookingCaller={bookingCaller}
                clearBookingCustomer={() => { setBookingCustomer(null); setBookingCaller(null); }}
                approve={(id) => void decideProposal(id, "approve")}
                reject={(id) => void decideProposal(id, "reject")}
              />
            )}
            {screenView.type === "timesheets" && (
              <PayrollWorkspace canEdit={can("scheduling:write")} openJob={(id) => void openJob(id, dispatchDate)} />
            )}
            {screenView.type === "pricebook" && (
              <Pricebook
                settings={companySettings}
                saveSettings={savePricingRules}
                onOpenEstimate={(id) => openEstimate(id, { returnTo: "commercial" })}
                categories={priceBook.categories}
                vendorOptions={priceBook.vendors}
                supportsItemDetails={priceBook.supportsItemDetails === true}
                items={priceBook.items}
                accountingAccounts={priceBook.accountingAccounts}
                readiness={priceBook.readiness}
                loading={
                  priceBookLoading || priceBookLoadedFor !== priceBookArchived
                }
                archived={priceBookArchived}
                canWrite={can("commercial:write")}
                setArchived={setPriceBookArchived}
                save={async (path, method, body) => {
                  try {
                    await mutate(path, method, body);
                    await loadPriceBook(priceBookArchived);
                  } catch (err) {
                    setError(
                      err instanceof Error
                        ? err.message
                        : "We couldn’t save the Pricebook change.",
                    );
                    throw err;
                  }
                }}
                uploadImage={async (path, image) => {
                  const form = new FormData();
                  form.set("image", image);
                  const response = await apiFetch(path, { method: "POST", body: form });
                  const payload = (await response.json()) as { error?: { message?: string } };
                  if (!response.ok)
                    throw new Error(payload.error?.message || "We couldn’t upload the photo.");
                  await loadPriceBook(priceBookArchived);
                }}
              />
            )}
            {screenView.type === "inventory" && (
              <InventoryWorkspace
                  canInvoice={Boolean(access?.permissions.includes("commercial:write"))}
                canWrite={can("inventory:write")}
                canApprove={can("inventory:approve")}
                canAdjust={can("inventory:adjust")}
                priceBookItems={priceBook.items}
                forJobId={screenView.forJobId}
                forJobNumber={screenView.forJobNumber}
                purchaseOrderId={screenView.purchaseOrderId}
                onPurchaseOrderChange={id => {
                  if (!id && purchaseOrderRouteId) navigateWorkspace({ type: "inventory" });
                  else setView({ ...screenView, type: "inventory", purchaseOrderId: id || undefined });
                }}
              />
            )}
            {screenView.type === "commercial" && (
              <Commercial
                activeSection={screenView.section}
                onSectionChange={section => setView({ ...screenView, type: "commercial", section })}
                items={commercial}
                summary={commercialSummary}
                type={screenView.mode}
                filter={screenView.filter}
                total={commercialTotal}
                page={commercialPage}
                pageSize={commercialPageSize}
                search={commercialSearch}
                setSearch={(next) => {
                  setCommercialSearch(next);
                  setCommercialPage(1);
                }}
                setPage={setCommercialPage}
                setType={(next) => {
                  setCommercialType(next);
                }}
                setFilter={(next) => {
                  const nextType = ["open", "approved", "lost", "history"].includes(next) ? "estimates" : "invoices";
                  setCommercialType(nextType);
                  setCommercialFilter(next);
                  setCommercialPage(1);
                  setCommercialSearch("");
                  navigateWorkspace({
                    type: "commercial",
                    mode: nextType,
                    filter: next,
                  });
                }}
                openJob={(id, documentId) =>
                  void openJob(id, dispatchDate, "commercial", documentId)
                }
                openInvoice={(invoiceId, jobId) =>
                  openInvoice(invoiceId, { jobId, returnTo: "commercial" })
                }
                openEstimate={(estimateId, jobId) =>
                  openEstimate(estimateId, { jobId, returnTo: "commercial" })
                }
                canRetryQuickBooks={access.role === "owner" || access.role === "admin"}
                openPurchaseOrder={(purchaseOrderId) => navigateWorkspace({ type: "inventory", purchaseOrderId })}
              />
            )}
          </div>
        </div>
      </section>
      <AlertBell
        onUnreadTextsChange={setUnreadTexts}
        clearComposer={view.type === "booking_agent"}
        activeWidget={activeWidget}
        onWidgetChange={widget => { setTechnicianTextTarget(null); setActiveWidget(widget); }}
        openCommunications={() => selectTopLevel("communications")}
        bookCaller={can("scheduling:write") ? caller => {
          setBookingCustomer(null);
          setBookingCaller(caller);
          setDispatchDateAndView(new Intl.DateTimeFormat("en-CA", { timeZone: dispatchTimezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()));
        } : undefined}
        role={access.role}
        permissions={access.permissions}
        proposals={proposals}
        timezone={dispatchTimezone}
        pageBoard={
          dispatchDate === new Date().toISOString().slice(0, 10)
            ? {
                date: dispatchDate,
                payload: {
                  appointments: dispatch,
                  untimedJobs,
                  technicians,
                  timezone: dispatchTimezone,
                },
              }
            : null
        }
        pageReadiness={priceBookLoadedFor === null ? null : priceBook.readiness}
        actions={{
          openBoard: () =>
            setDispatchDateAndView(new Date().toISOString().slice(0, 10)),
          openJob: (jobId) => void openJob(jobId, dispatchDate),
          openAccounting: (mode) => {
            setCommercialType(mode);
            navigateWorkspace({
              type: "commercial",
              mode,
              filter: mode === "invoices" ? "attention" : "open",
            });
          },
          openPricebook: () => navigateWorkspace({ type: "pricebook" }),
        }}
      />
    </main>
    </RecordCommunicationsContext.Provider>
  );
}

type MyWorkItem = {
  appointmentId: string;
  scheduledStart: string;
  scheduledEnd: string;
  appointmentStatus: string;
  jobId: string;
  jobNumber: number;
  jobStatus: string;
  description: string | null;
  priority: string | null;
  customerName: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string | null;
};

type InventoryVendor = { id: string; name: string; contactEmail: string | null; active: number };
type InventoryLocation = { id: string; name: string; type: "warehouse" | "truck"; active: number; assignedTechnicianName: string | null; assignedTechnicianId?: string | null };
type InventoryPurchaseOrder = { createdAt?: string; createdBy?: string | null; orderType?: string | null; id: string; displayNumber: string | null; status: string; sourceStatus?: string | null; sourceImported?: number; amountCents: number; vendorName: string; requiredByDate: string | null; jobId?: string | null; jobNumber?: string | null; customerName?: string | null };
type InventoryStock = { id: string; quantityOnHand: number; reservedQuantity: number; itemName: string; sku: string | null; locationName: string; locationType: string; reorderMin: number | null };
type InventoryMaterial = { id: string; name: string; sku: string | null; costCents: number | null };
type InventoryTechnician = { id: string; name: string };
type InventoryJobOption = { id: string; jobNumber: string; customerName: string; description: string | null; status: string; address?: string | null; technicianId?: string | null };
type InventoryPurchaseOrderDetail = PurchaseOrderDocument;

function formatMoney(cents: number) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100); }


/**
 * What the office wants at a glance: how many orders are sitting at each stage
 * and what they are worth. Sent covers partially received, because a part
 * delivery is still an order you are waiting on.
 */
const PO_STAGES = [
  { key: "pending", label: "Pending", tone: "var(--st-scheduled-solid)", holds: (status: string) => ["draft", "pending", "submitted", "approved"].includes(status) },
  { key: "sent", label: "Sent", tone: "var(--st-confirmed-solid)", holds: (status: string) => status === "sent" },
  { key: "partially_received", label: "Partially received", tone: "var(--st-dispatched-solid)", holds: (status: string) => status === "partially_received" },
  { key: "received", label: "Received", tone: "var(--st-working-solid)", holds: (status: string) => status === "received" },
  { key: "cancelled", label: "Cancelled", tone: "var(--st-off-solid)", holds: (status: string) => status === "cancelled" },
];
/**
 * A purchase order raised against a job is numbered for that job — 21263-001 is
 * the first PO on job 21263. The office reads it that way and so does the old
 * admin page, so the card should not say "no job" when the number says one.
 */
function purchaseOrderJobNumber(order: { jobNumber?: string | null; displayNumber: string | null }) {
  if (order.jobNumber) return order.jobNumber;
  const match = /^(\d+)-\d+$/.exec((order.displayNumber ?? "").trim());
  return match ? match[1] : null;
}
export function InventoryWorkspace({ canInvoice = false, canWrite, canApprove, canAdjust, forJobId, forJobNumber, purchaseOrderId, onPurchaseOrderChange }: { canInvoice?: boolean; canWrite: boolean; canApprove: boolean; canAdjust: boolean; priceBookItems: PriceBookItem[]; forJobId?: string; forJobNumber?: string; purchaseOrderId?: string; onPurchaseOrderChange?: (id: string | null) => void }) {
  const [section, setSection] = useState<"purchases" | "inventory">("purchases");
  const tabId = useId();
  const [vendors, setVendors] = useState<InventoryVendor[]>([]);
  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [technicians, setTechnicians] = useState<InventoryTechnician[]>([]);
  const [orders, setOrders] = useState<InventoryPurchaseOrder[]>([]);
  const [stock, setStock] = useState<InventoryStock[]>([]);
  const [materialItems, setMaterialItems] = useState<InventoryMaterial[]>([]);
  const [jobs, setJobs] = useState<InventoryJobOption[]>([]);
  const [selected, setSelected] = useState<InventoryPurchaseOrderDetail | null>(null);
  const [receiptLocationId, setReceiptLocationId] = useState("");
  const [workflowAvailable, setWorkflowAvailable] = useState(false);
  const [workflowStatus, setWorkflowStatus] = useState<"loading" | "ready" | "error">("loading");
  const [poContext, setPoContext] = useState<PurchaseOrderContext>({ shipTo: "shop" });
  const [inventorySettings, setInventorySettings] = useState<{orderTypes: string[]; shopAddress: string}>({ orderTypes: [], shopAddress: "" });
  const [stockItemId, setStockItemId] = useState("");
  const [adjustmentLocationId, setAdjustmentLocationId] = useState("");
  const [adjustmentQuantity, setAdjustmentQuantity] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [fromLocationId, setFromLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [transferQuantity, setTransferQuantity] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [vendorEmail, setVendorEmail] = useState("");
  const [locationName, setLocationName] = useState("");
  const [locationType, setLocationType] = useState<"warehouse" | "truck">("warehouse");
  const [locationTechnicianId, setLocationTechnicianId] = useState("");
  const [poVendorId, setPoVendorId] = useState("");
  const [poItemId, setPoItemId] = useState("");
  const [poQuantity, setPoQuantity] = useState("1");
  const [poCost, setPoCost] = useState("");
  const [poJobId, setPoJobId] = useState("");
  const [poStage, setPoStage] = useState<string | null>(null);
  const [poQuery, setPoQuery] = useState("");
  const [poFormOpen, setPoFormOpen] = useState(Boolean(forJobId));
  /**
   * A purchase order is a document with lines on it, and the API has taken up
   * to 200 of them all along — the form was only ever sending one.
   */
  const [poLines, setPoLines] = useState<Array<VendorCosts & { key: string; priceBookItemId: string; name: string; description: string; quantity: string; unitCostCents: string; vendorPartNumber: string }>>([]);
  const [poPickerLine, setPoPickerLine] = useState<string | null>(null);
  const [poSaving, setPoSaving] = useState(false);
  const poSaveLock = useRef(false);
  const [poRequiredBy, setPoRequiredBy] = useState("");
  const [tool, setTool] = useState<null | "vendor" | "location" | "adjust" | "snapshot" | "transfer">(null);
  const [poTax, setPoTax] = useState("0");
  const [poFreight, setPoFreight] = useState("0");
  const [poMemo, setPoMemo] = useState("");
  const poLineTotal = poLines.reduce((total, line) => total + (Number(line.quantity) || 0) * Math.round((Number(line.unitCostCents) || 0) * 100), 0);
  const addPoLine = () => setPoPickerLine("new");
  const setPoLine = (key: string, patch: Partial<{ priceBookItemId: string; name: string; description: string; quantity: string; unitCostCents: string; vendorPartNumber: string }>) =>
    setPoLines((lines) => lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  // Arriving from a job card: the order is already for that job, so the form
  // opens with it attached rather than asking the office to find it again in a
  // list of every job in the company.
  useEffect(() => {
    if (!forJobId) return;
    setSection("purchases");
    setPoJobId(forJobId);
    setPoContext(current => ({ ...current, shipTo: "job" }));
    setPoFormOpen(true);
  }, [forJobId]);
  const [snapshotFile, setSnapshotFile] = useState<File | null>(null);
  const [snapshotDate, setSnapshotDate] = useState(new Date().toISOString().slice(0, 10));
  const [snapshotReview, setSnapshotReview] = useState<{ id: string; approved: number; quarantined: number; rows: Array<{ rowNumber: number; sourceSkuId: string | null; sourceSkuCode: string | null; sourceLocationId: string | null; sourceLocationName: string | null; quantityOnHand: number | null; issueCode: string | null; issueMessage: string | null }> } | null>(null);
  const [snapshotReason, setSnapshotReason] = useState("ServiceTitan Inventory On Hand opening balance");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [stockState, setStockState] = useState<"loading" | "ready" | "error">("loading");
  const inventoryLoadSequence = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++inventoryLoadSequence.current;
    setInventoryLoading(true);
    setError("");
    try {
      // Purchase orders are the primary screen content. Fetch them first so an
      // overloaded auxiliary endpoint cannot hide them behind a generic error.
      const orderResult = await data<{ items: InventoryPurchaseOrder[] }>("/api/v1/workspace/inventory/purchase-orders");
      if (sequence !== inventoryLoadSequence.current) return;
      setOrders(orderResult.items);
      setInventoryLoading(false);
    } catch {
      if (sequence === inventoryLoadSequence.current) {
        setError("We couldn’t load purchase orders. Please try again.");
        setInventoryLoading(false);
      }
      return;
    }

    // D1 has a per-window resource limit. These were previously eight
    // simultaneous reads (and state defaults retriggered the whole batch).
    // Load supporting data one at a time and keep successful data visible.
    let supportingDataFailed = false;
    const loadSupportingData = async <T,>(request: () => Promise<T>, apply: (result: T) => void) => {
      try {
        const result = await request();
        if (sequence === inventoryLoadSequence.current) apply(result);
      } catch {
        supportingDataFailed = true;
      }
    };
    await loadSupportingData(() => data<{ items: InventoryVendor[] }>("/api/v1/workspace/inventory/vendors"), (result) => {
      setVendors(result.items);
      if (result.items[0]) setPoVendorId((current) => current || result.items[0].id);
    });
    await loadSupportingData(() => data<{ items: InventoryLocation[] }>("/api/v1/workspace/inventory/locations"), (result) => {
      setLocations(result.items);
      if (result.items[0]) {
        setAdjustmentLocationId((current) => current || result.items[0].id);
        setFromLocationId((current) => current || result.items[0].id);
      }
      if (result.items[1]) setToLocationId((current) => current || result.items[1].id);
    });
    await loadSupportingData(() => data<{ items: InventoryMaterial[] }>("/api/v1/workspace/inventory/materials"), (result) => {
      setMaterialItems(result.items);
      if (result.items[0]) {
        setPoItemId((current) => current || result.items[0].id);
        setStockItemId((current) => current || result.items[0].id);
      }
    });
    try {
      const result = await loadPurchaseOrderSettings();
      if (sequence === inventoryLoadSequence.current) {
        setWorkflowAvailable(Boolean(result));
        setWorkflowStatus("ready");
        if (result) setInventorySettings(result);
      }
    } catch {
      supportingDataFailed = true;
      if (sequence === inventoryLoadSequence.current) setWorkflowStatus("error");
    }
    await loadSupportingData(() => data<{ items: InventoryTechnician[] }>("/api/v1/workspace/inventory/technicians"), (result) => setTechnicians(result.items));
    try {
      const result = await data<{ items: InventoryStock[] }>("/api/v1/workspace/inventory/stock");
      if (sequence === inventoryLoadSequence.current) { setStock(result.items); setStockState("ready"); }
    } catch {
      supportingDataFailed = true;
      if (sequence === inventoryLoadSequence.current) setStockState("error");
    }
    if (sequence === inventoryLoadSequence.current && supportingDataFailed) {
      setError("Purchase orders loaded. Some inventory details are temporarily unavailable; please try again.");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!poFormOpen) return;
    let active = true;
    void data<{ items: InventoryJobOption[] }>("/api/v1/workspace/inventory/jobs").then(result => { if (active) setJobs(result.items); }).catch(() => { if (active) setError("Jobs could not be loaded. Close and reopen the form to retry."); });
    return () => { active = false; };
  }, [poFormOpen]);
  async function open(id: string) { setSection("purchases"); try { setReceiptLocationId(current => current || locations.find(location => location.active)?.id || ""); setSelected((await data<{ item: InventoryPurchaseOrderDetail }>(`/api/v1/workspace/inventory/purchase-orders/${id}`)).item); if (id !== purchaseOrderId) onPurchaseOrderChange?.(id); } catch { setError("We couldn’t load that purchase order."); } }
  useEffect(() => { if (purchaseOrderId && purchaseOrderId !== selected?.id) void open(purchaseOrderId); }, [purchaseOrderId]);

  async function addVendor(event: FormEvent) { event.preventDefault(); try { await mutate("/api/v1/workspace/inventory/vendors", "POST", { name: vendorName, contactEmail: vendorEmail || null }); setVendorName(""); setVendorEmail(""); setNotice("Vendor added."); await load(); } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to add vendor."); } }
  async function addLocation(event: FormEvent) { event.preventDefault(); try { await mutate("/api/v1/workspace/inventory/locations", "POST", { name: locationName, type: locationType, assignedTechnicianId: locationType === "truck" ? locationTechnicianId : null }); setLocationName(""); setLocationTechnicianId(""); setNotice("Inventory location added."); await load(); } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to add location."); } }
  async function createPurchaseOrder(event: FormEvent) {
    event.preventDefault();
    if (poSaveLock.current) return;
    const lines = poLines.map(line => ({ priceBookItemId: line.priceBookItemId || null, description: purchaseOrderLineSnapshot(line.name, line.description), quantity: Number(line.quantity), unitCostCents: Math.round(Number(line.unitCostCents) * 100), ...(workflowAvailable ? { vendorPartNumber: line.vendorPartNumber.trim() || null } : {}) }));
    if (!lines.length || lines.some((line, index) => !line.priceBookItemId || !poLines[index].name.trim() || line.description.length > 1000 || !Number.isFinite(line.quantity) || line.quantity <= 0 || !poLines[index].unitCostCents.trim() || !Number.isFinite(line.unitCostCents) || line.unitCostCents < 0)) return setError("Enter a part, quantity, and unit cost for every order line. Keep the part and description within 1,000 characters.");
    if (!poVendorId) return setError("Choose a vendor.");
    if (workflowAvailable && !locations.some(location => location.active && location.id === poContext.inventoryLocationId)) return setError("Choose an active warehouse or truck for the inventory location.");
    const taxCents = Math.round(Number(poTax) * 100), freightCents = Math.round(Number(poFreight) * 100);
    if (![taxCents, freightCents].every(value => Number.isSafeInteger(value) && value >= 0 && value <= 100000000)) return setError("Enter valid tax and freight amounts.");
    poSaveLock.current = true; setPoSaving(true); setError("");
    try {
      const created = await mutate<{ id: string; displayNumber: string }>("/api/v1/workspace/inventory/purchase-orders", "POST", { vendorId: poVendorId, ...(taxCents ? { taxCents } : {}), freightCents, jobId: poJobId || null, requiredByDate: poRequiredBy || null, vendorMemo: poMemo.trim() || null, ...(workflowAvailable ? { orderType: poContext.orderType || null, shipTo: poContext.shipTo || (poJobId ? "job" : "shop"), inventoryLocationId: poContext.inventoryLocationId || null, technicianId: (poContext.technicianId === undefined ? jobs.find(job => job.id === poJobId)?.technicianId : poContext.technicianId) ?? null } : {}), lines });
      setPoFormOpen(false); setPoLines([]); setPoRequiredBy(""); setPoMemo(""); setPoTax("0"); setPoFreight("0");
      setNotice(`Purchase order ${created.displayNumber} created.`); await load(); await open(created.id);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to create purchase order."); }
    finally { poSaveLock.current = false; setPoSaving(false); }
  }
  async function adjustStock(event: FormEvent) { event.preventDefault(); try { await mutate("/api/v1/workspace/inventory/adjustments", "POST", { priceBookItemId: stockItemId, locationId: adjustmentLocationId, quantityDelta: Number(adjustmentQuantity), reason: adjustmentReason }); setAdjustmentQuantity(""); setAdjustmentReason(""); setNotice("Inventory adjustment recorded."); await load(); } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to adjust stock."); } }
  async function recordOpeningBalance() { if (!stockItemId || !adjustmentLocationId || Number(adjustmentQuantity) <= 0 || !adjustmentReason.trim()) return setError("Choose a material and location, then provide a positive quantity and reason."); try { await mutate("/api/v1/workspace/inventory/opening-balances", "POST", { priceBookItemId: stockItemId, locationId: adjustmentLocationId, quantity: Number(adjustmentQuantity), reason: adjustmentReason }); setAdjustmentQuantity(""); setAdjustmentReason(""); setNotice("Opening balance recorded."); await load(); } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to record opening balance."); } }
  async function transferStock(event: FormEvent) { event.preventDefault(); try { await mutate("/api/v1/workspace/inventory/transfers", "POST", { priceBookItemId: stockItemId, fromLocationId, toLocationId, quantity: Number(transferQuantity), note: "Office stock transfer" }); setTransferQuantity(""); setNotice("Inventory transfer recorded."); await load(); } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to transfer stock."); } }

  async function uploadSnapshot(event: FormEvent) { event.preventDefault(); if (!snapshotFile) return setError("Choose the ServiceTitan inventory report."); try { setError(""); const form = new FormData(); form.set("file", snapshotFile); form.set("asOfDate", snapshotDate); const response = await fetch(`${API_URL}/api/v1/workspace/inventory/snapshots`, { method: "POST", credentials: "include", body: form }); const body = await response.json() as { data?: { id: string; summary: { approved: number; quarantined: number } }; error?: { message?: string } }; if (!response.ok || !body.data) throw new Error(body.error?.message ?? "Unable to review inventory snapshot."); const reviewed = await data<{ rows: Array<{ rowNumber: number; sourceSkuId: string | null; sourceSkuCode: string | null; sourceLocationId: string | null; sourceLocationName: string | null; quantityOnHand: number | null; issueCode: string | null; issueMessage: string | null }> }>(`/api/v1/workspace/inventory/snapshots/${body.data.id}`); setSnapshotReview({ id: body.data.id, approved: body.data.summary.approved, quarantined: body.data.summary.quarantined, rows: reviewed.rows }); setNotice(`Snapshot reviewed: ${body.data.summary.approved} approved, ${body.data.summary.quarantined} need review.`); } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to review inventory snapshot."); } }
  async function applySnapshot() { if (!snapshotReview || snapshotReview.quarantined || !snapshotReason.trim()) return; try { const result = await mutate<{ rows: number }>(`/api/v1/workspace/inventory/snapshots/${snapshotReview.id}/apply`, "POST", { reason: snapshotReason }); setNotice(`Opening balances applied for ${result.rows} material/location rows.`); setSnapshotReview(null); await load(); } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to apply inventory snapshot."); } }


function groupedPurchaseOrderLines(lines: InventoryPurchaseOrderDetail["lines"]) { return [...lines.reduce((groups, line) => { const key = `${line.priceBookItemId ?? line.description}|${line.unitCostCents}`; const current = groups.get(key) ?? { ...line, sourceLines: 0 }; current.quantity += current.sourceLines ? line.quantity : 0; current.receivedQuantity += current.sourceLines ? line.receivedQuantity : 0; current.sourceLines++; groups.set(key, current); return groups; }, new Map<string, InventoryPurchaseOrderDetail["lines"][number] & { sourceLines: number }>()).values()]; }
  async function action(path: string, body: unknown = {}) { try { setError(""); const result = await mutate<{ status?: string }>(path, "POST", body); setNotice(result.status ? `Purchase order is ${result.status.replaceAll("_", " ")}.` : "Saved."); await load(); if (selected) await open(selected.id); } catch (reason) { setError(reason instanceof Error ? reason.message : "We couldn’t save that change."); } }
  async function receiveAll() { if (!selected) return; const destination = locations.find((location) => location.id === receiptLocationId && location.active); if (!destination) return setError("Choose an active warehouse or truck before receiving stock."); const outstanding = selected.lines.filter((line) => line.priceBookItemId && line.quantity > line.receivedQuantity); if (!outstanding.length) return; await action(`/api/v1/workspace/inventory/purchase-orders/${selected.id}/receipts`, { lines: outstanding.map((line) => ({ purchaseOrderLineItemId: line.id, inventoryLocationId: destination.id, quantity: line.quantity - line.receivedQuantity, unitCostCents: line.unitCostCents })) }); }

  const poNeedle = poQuery.trim().toLowerCase();
  const visibleOrders = orders.filter((order) => {
    if (poStage && !PO_STAGES.find((stage) => stage.key === poStage)?.holds(order.status)) return false;
    if (!poNeedle) return true;
    return [order.displayNumber, order.vendorName, purchaseOrderJobNumber(order), order.customerName, purchaseOrderStatusLabel(order.status)]
      .filter(Boolean).join(" ").toLowerCase().includes(poNeedle);
  });
  return <section aria-label="Inventory workspace">

    {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}{notice && <p role="status" className="mt-4 rounded-lg bg-[var(--accent-wash)] px-3 py-2 text-sm text-[var(--accent)]">{notice}</p>}
    <div role="tablist" aria-label="Inventory sections" className="mt-5 flex gap-1 border-b border-[var(--line)]">
      {([{ key: "purchases", label: "Purchases" }, { key: "inventory", label: "Inventory" }] as const).map(tab => <button key={tab.key} type="button" role="tab" id={`${tabId}-${tab.key}-tab`} aria-controls={`${tabId}-${tab.key}-panel`} aria-selected={section === tab.key} tabIndex={section === tab.key ? 0 : -1} onClick={() => { setSection(tab.key); setTool(null); }} onKeyDown={event => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === "Home" ? "purchases" : event.key === "End" ? "inventory" : tab.key === "purchases" ? "inventory" : "purchases";
        setSection(next); setTool(null);
        event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[id="${tabId}-${next}-tab"]`)?.focus();
      }} className={`min-h-11 rounded-t-lg border border-b-0 px-5 py-2.5 text-sm font-semibold ${section === tab.key ? "border-[var(--line)] bg-[var(--wash)] text-[var(--ink)]" : "border-transparent text-[var(--ink-soft)] hover:bg-[var(--wash)] hover:text-[var(--ink)]"}`}>{tab.label}</button>)}
    </div>
    <div role="tabpanel" id={`${tabId}-purchases-panel`} aria-labelledby={`${tabId}-purchases-tab`} hidden={section !== "purchases"} tabIndex={0}>
    <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {inventoryLoading ? PO_STAGES.map((stage) => <div key={stage.key} className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]" aria-label="Loading purchase orders"><span className="block h-1.5" style={{ background: stage.tone }} /><span className="block px-4 py-3"><span className="block text-sm font-medium text-[var(--ink-soft)]">Loading…</span><span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-[var(--ink-soft)]">{stage.label}</span></span></div>) : PO_STAGES.map((stage) => {
        const held = orders.filter((order) => stage.holds(order.status));
        const on = poStage === stage.key;
        return <button key={stage.key} type="button" onClick={() => setPoStage(on ? null : stage.key)}
          title={on ? "Close this list" : `Open the ${stage.label.toLowerCase()} orders`}
          className={`overflow-hidden rounded-xl border text-left transition ${on ? "border-[var(--ink)] bg-[var(--wash)]" : "border-[var(--line)] bg-[var(--paper)] hover:border-[var(--line-strong)]"}`}>
          <span className="block h-1.5" style={{ background: stage.tone }} />
          <span className="block px-4 py-3">
            <span className="block text-2xl font-semibold tabular-nums text-[var(--ink)]">{held.length}</span>
            <span className="mt-0.5 block text-xs font-semibold uppercase tracking-wide text-[var(--ink-soft)]">{stage.label}</span>
            <span className="mt-0.5 block text-xs text-[var(--ink-faint)]">{formatMoney(held.reduce((total, order) => total + order.amountCents, 0))}</span>
          </span>
        </button>;
      })}
    </div>

    <div className="mt-4 flex flex-wrap items-center gap-2 xl:flex-nowrap">
      <div className="flex min-w-0 flex-1 items-center gap-2 xl:max-w-[24rem]"><input value={poQuery} onChange={(event) => setPoQuery(event.target.value)} placeholder="Search PO number, vendor, job, or status" aria-label="Search purchase orders" className="min-w-0 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm outline-none focus:border-[var(--line-strong)]" />
      {poStage && <button type="button" onClick={() => setPoStage(null)} className="rounded-full border border-[var(--line)] px-4 py-2 text-sm font-semibold text-[var(--ink-soft)]">Clear</button>}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 xl:flex-nowrap">
        {canWrite && <button type="button" onClick={() => setTool("vendor")} className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-4 py-2 text-sm font-semibold text-[var(--ink)] transition hover:border-[var(--line-strong)]">New vendor</button>}
{canWrite && <button type="button" onClick={() => setPoFormOpen(true)} className="shrink-0 rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-white">+ New purchase order</button>}
      </div>
    </div>

    {canWrite && poFormOpen && <section className="mt-5 rounded-xl border border-[var(--line-strong)] bg-[var(--paper)] p-4 sm:p-5">
          <form aria-label="New purchase order" onSubmit={createPurchaseOrder} className="po-document-form space-y-5">
            <header className="flex items-center justify-between gap-3"><h2 className="text-base font-semibold">New purchase order</h2><QuietButton disabled={poSaving} type="button" onClick={() => { setPoFormOpen(false); if (!forJobId) setPoJobId(""); }}>Cancel</QuietButton></header>
            <p className="text-xs text-[var(--ink-soft)]">PO number assigned when created</p>
            <PurchaseOrderStatusChips status="pending" />
            <fieldset disabled={poSaving} className="min-w-0 space-y-5">
            <div className="grid gap-3">
              <label>Vendor<select required value={poVendorId} onChange={event => { const next = event.target.value; setPoVendorId(next); setPoLines(current => current.map(line => line.vendorCosts?.length ? { ...line, vendorPartNumber: line.vendorCosts.find(cost => cost.vendorId === next && cost.active !== false)?.vendorPartNumber ?? "", unitCostCents: line.vendorCosts.find(cost => cost.vendorId === next && cost.active !== false)?.costCents == null ? "" : (line.vendorCosts.find(cost => cost.vendorId === next && cost.active !== false)!.costCents / 100).toFixed(2) } : line)); }}><option value="">Choose vendor</option>{vendors.filter(vendor => vendor.active).map(vendor => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}</select></label>
            </div>
            {workflowAvailable && <PurchaseOrderContextFields value={{ ...poContext, technicianId: poContext.technicianId === undefined ? jobs.find(job => job.id === poJobId)?.technicianId : poContext.technicianId }} onChange={patch => setPoContext(current => ({ ...current, ...patch }))} orderTypes={inventorySettings.orderTypes} locations={locations} technicians={technicians} hasJob={Boolean(poJobId)} jobAddress={jobs.find(job => job.id === poJobId)?.address} shopAddress={inventorySettings.shopAddress} />}
              <label>For job<select value={poJobId} onChange={event => { const jobId = event.target.value; setPoJobId(jobId); setPoContext(current => ({ ...current, shipTo: jobId ? "job" : "shop", technicianId: jobs.find(job => job.id === jobId)?.technicianId })); }}><option value="">Warehouse / shop stock</option>{forJobId && !jobs.some(job => job.id === forJobId) && <option value={forJobId}>Job #{forJobNumber}</option>}{jobs.map(job => <option key={job.id} value={job.id}>#{job.jobNumber} · {job.customerName}</option>)}</select></label>
            <div className="po-document-facts">
              <label>Created date<input readOnly value={new Date().toLocaleDateString()} /></label>
              <label>Delivery date<input type="date" value={poRequiredBy} onChange={event => setPoRequiredBy(event.target.value)} /></label>
            </div>
            <label className="grid gap-1 text-sm font-semibold">Memo<textarea maxLength={5000} rows={2} value={poMemo} onChange={event => setPoMemo(event.target.value)} placeholder="Optional note to the supplier" /></label>

            <div className="job-table-scroll"><table className="po-document-items"><thead><tr><th>Item</th><th>Ordered</th><th>Unit cost</th><th>Total</th><th><span className="sr-only">Actions</span></th></tr></thead><tbody>{poLines.map(line => <tr key={line.key}>
              <td><label className="block text-xs text-[var(--ink-soft)]">Part<input aria-label="Part" required maxLength={1000} value={line.name} onChange={event => setPoLine(line.key, { name: event.target.value })} /></label><button type="button" className="job-po-catalog-link" onClick={() => setPoPickerLine(line.key)}>Change item</button><label className="mt-2 block text-xs text-[var(--ink-soft)]">Description<textarea aria-label="Description" rows={2} maxLength={1000} value={line.description} onChange={event => setPoLine(line.key, { description: event.target.value })} placeholder="Part details or ordering instructions" /></label>{workflowAvailable && <label className="mt-2 block text-xs text-[var(--ink-soft)]">Vendor part number<input aria-label="Vendor part number" value={line.vendorPartNumber} onChange={event => setPoLine(line.key, { vendorPartNumber: event.target.value })} placeholder="Vendor part #" /></label>}</td>
              <td><input required type="number" min="0.01" max="100000" step="0.01" aria-label="Quantity" value={line.quantity} onChange={event => setPoLine(line.key, { quantity: event.target.value })} /></td>
              <td><input required type="number" min="0" max="1000000" step="0.01" aria-label="Unit cost" value={line.unitCostCents} onChange={event => setPoLine(line.key, { unitCostCents: event.target.value })} /></td>
              <td>{formatMoney(Math.round((Number(line.quantity) || 0) * (Number(line.unitCostCents) || 0) * 100))}</td>
              <td><button type="button" aria-label="Remove line" className="job-icon-button" onClick={() => setPoLines(current => current.filter(item => item.key !== line.key))}>×</button></td>
            </tr>)}</tbody></table>{!poLines.length && <p className="py-4 text-sm text-[var(--ink-soft)]">Add materials from Pricebook to begin.</p>}</div>
            <QuietButton type="button" disabled={poLines.length >= 200} onClick={addPoLine}>Add materials</QuietButton>
            <div className="flex justify-end"><div className="po-document-totals">
              <p><span>Subtotal</span><strong>{formatMoney(poLineTotal)}</strong></p>
              <label>Freight<input aria-label="Freight" type="number" min="0" max="1000000" step="0.01" value={poFreight} onChange={event => setPoFreight(event.target.value)} /></label>
              <label>Sales tax<input aria-label="Sales tax" type="number" min="0" max="1000000" step="0.01" value={poTax} onChange={event => setPoTax(event.target.value)} /></label>
              <p><span>Order total</span><strong>{formatMoney(poLineTotal + Math.round((Number(poFreight) || 0) * 100) + Math.round((Number(poTax) || 0) * 100))}</strong></p>
            </div></div>
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <button className="ml-auto block rounded bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-white" disabled={poSaving || workflowStatus !== "ready"}>{poSaving ? "Creating…" : "Create purchase order"}</button>
            </fieldset>
          </form>
    </section>}
    <div className="mt-5 grid gap-5">
      <div className="space-y-5">
        {(poStage || poNeedle) && <section className="rounded-xl border border-[var(--line)] bg-[var(--paper)]"><div className="flex items-center justify-between gap-3 border-b border-[var(--line)] px-5 py-4"><h2 className="font-semibold">{poStage ? PO_STAGES.find((stage) => stage.key === poStage)?.label : "Search results"}</h2><span className="text-sm text-[var(--ink-soft)]">{visibleOrders.length}</span></div><div className="divide-y divide-[var(--line)]">{visibleOrders.map((order) => <button key={order.id} type="button" onClick={() => void open(order.id)} className="flex w-full items-center justify-between gap-4 px-5 py-3 text-left hover:bg-[var(--wash)]"><span><strong className="block text-sm">{order.displayNumber ?? "PO number unavailable"}</strong><span className="text-sm text-[var(--ink-soft)]">{order.vendorName}{purchaseOrderJobNumber(order) ? ` · job #${purchaseOrderJobNumber(order)}` : ""}{order.orderType ? ` · ${order.orderType}` : ""}{order.requiredByDate ? ` · due ${order.requiredByDate}` : ""}</span></span><span className="text-right"><strong className="block text-sm">{formatMoney(order.amountCents)}</strong><span className="text-xs text-[var(--ink-soft)]">{order.sourceImported ? `Imported · ${order.sourceStatus ?? order.status}` : `${order.status === "submitted" ? "Field request" : "Office"} · ${purchaseOrderStatusLabel(order.status)}`}</span></span></button>)}{!visibleOrders.length && <p className="px-5 py-10 text-center text-sm text-[var(--ink-soft)]">{orders.length ? "No purchase order matches that." : "No purchase orders yet."}</p>}</div></section>}
      </div>

    </div>
    </div>
    <div role="tabpanel" id={`${tabId}-inventory-panel`} aria-labelledby={`${tabId}-inventory-tab`} hidden={section !== "inventory"} tabIndex={0} className="space-y-5 pt-5">
      {canAdjust && <div className="flex flex-wrap items-center gap-2" aria-label="Inventory tools">
        {canAdjust && <button type="button" onClick={() => setTool("location")} className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-4 py-2 text-sm font-semibold text-[var(--ink)] transition hover:border-[var(--line-strong)]">New location</button>}
        {canAdjust && <button type="button" onClick={() => setTool("adjust")} className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-4 py-2 text-sm font-semibold text-[var(--ink)] transition hover:border-[var(--line-strong)]">Adjust stock</button>}
        {canAdjust && <button type="button" onClick={() => setTool("snapshot")} className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-4 py-2 text-sm font-semibold text-[var(--ink)] transition hover:border-[var(--line-strong)]">Stock snapshot</button>}
        {canAdjust && <button type="button" onClick={() => setTool("transfer")} className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-4 py-2 text-sm font-semibold text-[var(--ink)] transition hover:border-[var(--line-strong)]">Transfer stock</button>}
      </div>}
        <section className="rounded-xl border border-[var(--line)] bg-[var(--paper)]"><div className="border-b border-[var(--line)] px-5 py-4"><h2 className="font-semibold">Stock by location</h2></div><div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="text-xs uppercase tracking-wide text-[var(--ink-faint)]"><tr><th className="px-5 py-3">Material</th><th className="px-3 py-3">Location</th><th className="px-3 py-3 text-right">On hand</th><th className="px-5 py-3 text-right">Available</th></tr></thead><tbody>{stock.map((row) => { const available = row.quantityOnHand - row.reservedQuantity; const low = row.reorderMin != null && available <= row.reorderMin; return <tr key={row.id} className="border-t border-[var(--line)]"><td className="px-5 py-3 font-medium">{row.itemName}{low && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Low stock</span>}</td><td className="px-3 py-3 text-[var(--ink-soft)]">{row.locationName}</td><td className="px-3 py-3 text-right">{row.quantityOnHand}</td><td className="px-5 py-3 text-right">{available}{row.reorderMin != null && <span className="ml-1 text-xs text-[var(--ink-faint)]">/ min {row.reorderMin}</span>}</td></tr>; })}{!stock.length && <tr><td colSpan={4} className="px-5 py-10 text-center text-[var(--ink-soft)]">{stockState === "loading" ? "Loading stock…" : stockState === "error" ? "Stock could not be loaded. Please try again." : "Stock appears after an opening adjustment or PO receipt."}</td></tr>}</tbody></table></div></section>
    </div>
    {poPickerLine && <PurchaseOrderItemPicker vendorId={poVendorId} onClose={() => setPoPickerLine(null)} onAdd={item => { const patch = { vendorCosts: item.vendorCosts, vendorPartNumber: item.vendorCosts?.find(cost => cost.vendorId === poVendorId)?.vendorPartNumber ?? "", priceBookItemId: item.id, name: item.name, description: "", unitCostCents: item.costCents == null ? "" : (item.costCents / 100).toFixed(2) }; if (poPickerLine === "new") setPoLines(current => current.length < 200 ? [...current, { key: crypto.randomUUID(), quantity: "1", ...patch }] : current); else { setPoLine(poPickerLine, patch); setPoPickerLine(null); } }} />}
    {selected && workflowStatus !== "ready" && <Dialog title="Purchase order" onClose={() => { setSelected(null); onPurchaseOrderChange?.(null); }}><p role="status">{workflowStatus === "loading" ? "Loading purchase order settings…" : "Purchase order settings could not be loaded. Please retry."}</p>{workflowStatus === "error" && <button type="button" onClick={() => { setWorkflowStatus("loading"); void load(); }}>Retry</button>}</Dialog>}
    {selected && workflowStatus === "ready" && !workflowAvailable && <Dialog title="Purchase order" onClose={() => { setSelected(null); onPurchaseOrderChange?.(null); }}><section aria-label={`Purchase order ${selected.displayNumber ?? "number unavailable"}`} className="space-y-4 rounded-xl border border-[var(--line-strong)] bg-[var(--paper)] p-4 sm:p-5"><header className="space-y-3"><h2 className="text-lg font-semibold">{selected.displayNumber ?? "PO number unavailable"}</h2><PurchaseOrderStatus order={selected} canWrite={canWrite} canSend={canApprove} canDeliver={selected.status === "approved"} showMetadata={false} onSaved={async () => { await open(selected.id); await load(); }} /></header><p className="text-sm text-[var(--ink-soft)]">Enhanced purchase order fields and receiving will be available after the purchase order update is released.</p><p className="text-sm text-[var(--ink-soft)]">{selected.vendorName}</p>{purchaseOrderJobNumber(selected) ? <p className="text-sm text-[var(--ink)]"><span className="text-[var(--ink-soft)]">For job</span> <span className="font-semibold">#{purchaseOrderJobNumber(selected)}</span>{selected.customerName ? ` · ${selected.customerName}` : ""}</p> : <p className="text-sm text-[var(--ink-faint)]">No job assigned</p>}{canInvoice && selected.jobId && selected.status !== "cancelled" && <PurchaseOrderInvoiceItems jobId={selected.jobId} orderId={selected.id} />}<div className="overflow-hidden rounded-lg border border-[var(--line)] text-sm"><div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 border-b border-[var(--line)] bg-[var(--wash)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]"><span>Item</span><span>Ordered</span><span>Received</span><span>Unit cost</span></div>{groupedPurchaseOrderLines(selected.lines).map((line) => <div key={`${line.id}-${line.sourceLines}`} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 border-b border-[var(--line)] px-3 py-2"><span>{line.description}{line.sourceLines > 1 && <small className="ml-2 text-[var(--ink-soft)]">{line.sourceLines} imported lines</small>}</span><span>{line.quantity}</span><span>{line.receivedQuantity}</span><span>{formatMoney(line.unitCostCents)}</span></div>)}</div>{selected.sourceImported === 1 && <details className="text-sm"><summary className="cursor-pointer font-semibold">Show imported source lines</summary><div className="mt-2 divide-y rounded border border-[var(--line)]">{selected.lines.map((line) => <div key={line.id} className="flex justify-between gap-3 px-3 py-2"><span>{line.description}</span><span>{line.receivedQuantity} received of {line.quantity} ordered · {formatMoney(line.unitCostCents)}</span></div>)}</div></details>}<a className="inline-flex rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold" href={`${API_URL}/api/v1/workspace/inventory/purchase-orders/${selected.id}/pdf`} target="_blank" rel="noreferrer">Download PDF</a>{["pending", "sent", "partially_received", "received"].includes(selected.status) && canAdjust && <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">Receive into<select value={receiptLocationId} onChange={(event) => setReceiptLocationId(event.target.value)} className="mt-1.5 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm"><option value="">Choose destination</option>{locations.filter((location) => location.active).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select></label>}<div className="flex flex-wrap gap-2">{["pending", "sent", "partially_received", "received"].includes(selected.status) && canAdjust && <button onClick={() => void receiveAll()} className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-white">Receive outstanding stock</button>}</div></section></Dialog>}
    {selected && workflowAvailable && <Dialog title="Purchase order" onClose={() => { setSelected(null); onPurchaseOrderChange?.(null); }} width="max-w-4xl"><InventoryPurchaseOrderPanel key={selected.id} order={selected} locations={locations} technicians={technicians} orderTypes={inventorySettings.orderTypes} shopAddress={inventorySettings.shopAddress} canWrite={canWrite} canApprove={canApprove} canAdjust={canAdjust} canInvoice={canInvoice} onSaved={async () => { await open(selected.id); await load(); }} onDeleted={async () => { setSelected(null); onPurchaseOrderChange?.(null); setNotice("Draft purchase order deleted."); await load(); }} /></Dialog>}
    {tool === "vendor" && <Dialog title="New vendor" onClose={() => setTool(null)} width="max-w-lg"><form onSubmit={addVendor} className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5"><h2 className="font-semibold">Add vendor</h2><input required value={vendorName} onChange={(event) => setVendorName(event.target.value)} placeholder="Vendor name" className="mt-3 w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm" /><input value={vendorEmail} onChange={(event) => setVendorEmail(event.target.value)} type="email" placeholder="orders@vendor.com" className="mt-2 w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm" /><button className="mt-3 rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold">Add vendor</button></form></Dialog>}
    {tool === "location" && <Dialog title="New location" onClose={() => setTool(null)} width="max-w-lg"><form onSubmit={addLocation} className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5"><h2 className="font-semibold">Add stock location</h2><input required value={locationName} onChange={(event) => setLocationName(event.target.value)} placeholder="Main warehouse" className="mt-3 w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm" /><select value={locationType} onChange={(event) => setLocationType(event.target.value as "warehouse" | "truck")} className="mt-2 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm"><option value="warehouse">Warehouse</option><option value="truck">Truck</option></select>{locationType === "truck" && <select required value={locationTechnicianId} onChange={(event) => setLocationTechnicianId(event.target.value)} className="mt-2 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm"><option value="">Assign technician</option>{technicians.map((technician) => <option key={technician.id} value={technician.id}>{technician.name}</option>)}</select>}<button className="mt-3 rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold">Add location</button></form></Dialog>}
    {tool === "adjust" && <Dialog title="Adjust stock" onClose={() => setTool(null)} width="max-w-lg"><form onSubmit={adjustStock} className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5"><h2 className="font-semibold">Opening balance or adjustment</h2><select required value={stockItemId} onChange={(event) => setStockItemId(event.target.value)} className="mt-3 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm"><option value="">Choose material</option>{materialItems.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><select required value={adjustmentLocationId} onChange={(event) => setAdjustmentLocationId(event.target.value)} className="mt-2 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm"><option value="">Choose location</option>{locations.filter((location) => location.active).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select><input required type="number" step="0.01" value={adjustmentQuantity} onChange={(event) => setAdjustmentQuantity(event.target.value)} placeholder="+ or - quantity" className="mt-2 w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm" /><input required value={adjustmentReason} onChange={(event) => setAdjustmentReason(event.target.value)} placeholder="Reason" className="mt-2 w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm" /><div className="mt-3 flex flex-wrap gap-2"><button type="button" onClick={() => void recordOpeningBalance()} className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-white">Record opening balance</button><button className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold">Record adjustment</button></div></form></Dialog>}
    {tool === "snapshot" && <Dialog title="Stock snapshot" onClose={() => setTool(null)} width="max-w-lg"><form onSubmit={uploadSnapshot} className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5"><h2 className="font-semibold">Import ServiceTitan stock snapshot</h2><p className="mt-1 text-sm text-[var(--ink-soft)]">Upload Inventory On Hand by Location (CSV/XLSX). It is reviewed before any stock is created.</p><input required type="file" accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" onChange={(event) => setSnapshotFile(event.target.files?.[0] ?? null)} className="mt-3 block w-full text-sm" /><input required type="date" value={snapshotDate} onChange={(event) => setSnapshotDate(event.target.value)} className="mt-2 w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm" /><button className="mt-3 rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold">Review snapshot</button>{snapshotReview && <div className="mt-3 rounded-lg bg-[var(--wash)] p-3 text-sm"><strong>{snapshotReview.approved} rows approved</strong>{snapshotReview.quarantined ? <><p className="mt-1 text-red-700">{snapshotReview.quarantined} rows need mapping before this snapshot can be applied.</p><div className="mt-2 max-h-52 overflow-auto rounded border border-red-200 bg-white"><table className="w-full text-left text-xs"><thead><tr className="border-b"><th className="p-2">Row</th><th className="p-2">Item</th><th className="p-2">Location</th><th className="p-2">Reason</th></tr></thead><tbody>{snapshotReview.rows.filter((row) => row.issueCode).map((row) => <tr key={row.rowNumber} className="border-b align-top"><td className="p-2">{row.rowNumber}</td><td className="p-2">{row.sourceSkuCode ?? row.sourceSkuId ?? "—"}</td><td className="p-2">{row.sourceLocationName ?? row.sourceLocationId ?? "—"}</td><td className="p-2">{row.issueMessage ?? row.issueCode}</td></tr>)}</tbody></table></div></> : <><input value={snapshotReason} onChange={(event) => setSnapshotReason(event.target.value)} className="mt-2 w-full rounded border border-[var(--line)] px-2 py-1.5" /><button type="button" onClick={() => void applySnapshot()} className="mt-2 rounded bg-[var(--ink)] px-3 py-2 font-semibold text-white">Apply approved opening balances</button></>}</div>}</form></Dialog>}
    {tool === "transfer" && <Dialog title="Transfer stock" onClose={() => setTool(null)} width="max-w-lg"><form onSubmit={transferStock} className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5"><h2 className="font-semibold">Transfer stock</h2><select required value={stockItemId} onChange={(event) => setStockItemId(event.target.value)} className="mt-3 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm"><option value="">Choose material</option>{materialItems.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><select required value={fromLocationId} onChange={(event) => setFromLocationId(event.target.value)} className="mt-2 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm"><option value="">From location</option>{locations.filter((location) => location.active).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select><select required value={toLocationId} onChange={(event) => setToLocationId(event.target.value)} className="mt-2 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm"><option value="">To location</option>{locations.filter((location) => location.active).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select><input required min="0.01" type="number" step="0.01" value={transferQuantity} onChange={(event) => setTransferQuantity(event.target.value)} placeholder="Quantity" className="mt-2 w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm" /><button className="mt-3 rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold">Transfer</button></form></Dialog>}
  </section>;
}

function TechnicianMyWork({ userName }: { userName: string }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<MyWorkItem[]>([]);
  const [timezone, setTimezone] = useState("UTC");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    void data<{ timezone: string; items: MyWorkItem[] }>(
      "/api/v1/workspace/my-work",
    )
      .then((result) => {
        if (cancelled) return;
        setItems(result.items);
        setTimezone(result.timezone);
      })
      .catch(() => {
        if (!cancelled) setError("We couldn’t load your assigned work.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <header className="sticky top-0 z-10 border-b border-[var(--line)] bg-[var(--ink)] px-5 py-4 text-[var(--paper)]">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[.16em] text-[var(--ink-soft)]">
              Technician workspace
            </p>
            <h1 className="mt-1 text-xl font-semibold">My Work</h1>
          </div>
          <button
            onClick={() => void authClient.signOut().then(() => navigate("/"))}
            className="rounded-lg border border-[var(--paper)]/30 px-3 py-2 text-xs font-semibold text-[var(--paper)]"
          >
            Sign out
          </button>
        </div>
      </header>
      <section className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <p className="text-sm text-[var(--ink-soft)]">
          Welcome, {userName}. Only work assigned to you appears here.
        </p>
        {error && (
          <div className="mt-5 rounded-lg border border-[var(--accent)] bg-[var(--accent-wash)] px-4 py-3 text-sm text-[var(--accent)]">
            {error}
          </div>
        )}
        {loading ? (
          <p className="py-16 text-center text-sm text-[var(--ink-soft)]">
            Loading assigned work…
          </p>
        ) : (
          <div className="mt-5 space-y-4">
            {items.map((item) => (
              <article
                key={item.appointmentId}
                className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ink)]">
                      Job #{item.jobNumber}
                    </p>
                    <h2 className="mt-1 text-lg font-semibold text-[var(--ink)]">
                      {item.customerName}
                    </h2>
                  </div>
                  <span
                    className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${statusStyle(item.appointmentStatus)}`}
                  >
                    {item.appointmentStatus}
                  </span>
                </div>
                <p className="mt-4 text-sm font-semibold text-[var(--ink)]">
                  {formatDispatchDate(item.scheduledStart, timezone, {
                    weekday: "long",
                    year: "numeric",
                  })}
                </p>
                <p className="mt-1 text-sm text-[var(--ink-soft)]">
                  {formatDispatchTime(item.scheduledStart, timezone)}–
                  {formatDispatchTime(item.scheduledEnd, timezone)}
                </p>
                <p className="mt-3 text-sm leading-6 text-[var(--ink-soft)]">
                  {item.addressLine1}
                  {item.addressLine2 ? `, ${item.addressLine2}` : ""}
                  <br />
                  {item.city}, {item.state} {item.postalCode}
                </p>
                <p className="mt-3 border-t border-[var(--line)] pt-3 text-sm text-[var(--ink-soft)]">
                  {cleanDescription(item.description, "Service work", 240)}
                </p>
              </article>
            ))}
            {!items.length && !error && (
              <div className="rounded-lg border border-dashed border-[var(--line)] bg-[var(--paper)] px-5 py-14 text-center">
                <p className="font-semibold text-[var(--ink)]">
                  No upcoming assignments
                </p>
                <p className="mt-2 text-sm text-[var(--ink-soft)]">
                  New work will appear here after dispatch assigns it to you.
                </p>
              </div>
            )}
          </div>
        )}
      </section>
    </main>
  );
}

/**
 * THE OPENING LINE. The office's own name and the time of day, centred over the
 * operator — nothing else. The gradient banner it replaces was decoration, and
 * a heading that announces the screen you are already looking at is furniture.
 * Matches the welcome on workview.mgpmechanical.com (Melania, 8/26/2026).
 */
export function WorkspaceNavigation({ items, isActive, onSelect, unreadTexts }: {
  unreadTexts?: number;
  items: Array<{ id: WorkspaceNavId; label: string }>;
  isActive: (id: WorkspaceNavId) => boolean;
  onSelect: (id: WorkspaceNavId) => void;
}) {
  return <WorkspaceIconRail unreadTexts={unreadTexts} items={items} isActive={isActive} onSelect={onSelect} />;
}

function WorkspaceNavGlyph({ id }: { id: WorkspaceNavId }) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (id === "projects") return <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path {...common} d="M3 7V5h7l2 2h9v13H3zM7 11h10M7 15h6" /></svg>;
  if (id === "agent")
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path {...common} d="M12 3v3M12 18v3M4.6 7.5l2.6 1.5M16.8 15l2.6 1.5M19.4 7.5 16.8 9M7.2 15l-2.6 1.5" />
        <circle {...common} cx="12" cy="12" r="4" />
      </svg>
    );
  if (id === "learning")
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path {...common} d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H6.5A2.5 2.5 0 0 0 4 21.5z" />
        <path {...common} d="M4 5.5v16M8 7h8M8 11h7" />
      </svg>
    );
  if (id === "dispatch")
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <rect {...common} x="4" y="5" width="16" height="15" rx="3" />
        <path {...common} d="M8 3v4M16 3v4M4 10h16M8 14h2M14 14h2" />
      </svg>
    );
  if (id === "timesheets")
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <circle {...common} cx="12" cy="12" r="8" />
        <path {...common} d="M12 7v5l3 2" />
      </svg>
    );
  if (id === "customers")
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path {...common} d="M16 19a4 4 0 0 0-8 0" />
        <circle {...common} cx="12" cy="9" r="3" />
        <path {...common} d="M4 18a3.5 3.5 0 0 1 4-3M20 18a3.5 3.5 0 0 0-4-3" />
      </svg>
    );
  if (id === "pricebook")
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path {...common} d="M6 4h12v16H6zM9 8h6M9 12h6M9 16h3" />
      </svg>
    );
  if (id === "inventory")
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path {...common} d="M4 8.5 12 4l8 4.5-8 4.5z" />
        <path {...common} d="M4 8.5v7L12 20l8-4.5v-7M12 13v7" />
      </svg>
    );
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect {...common} x="4" y="5" width="16" height="14" rx="2" />
      <path {...common} d="M4 10h16M8 14h3M14 14h2" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="19"
      height="19"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function AgentWorkspace({
  operatorProps,
  userName,
}: {
  operatorProps: Parameters<typeof DispatchOperator>[0];
  userName: string;
}) {
  // The greeting lives inside the assistant now, so an empty screen centres
  // as one block instead of holding a greeting at the top and a composer at
  // the bottom with a window of white between them.
  return (
    <div className="mx-auto w-full max-w-4xl px-1">
      <AgentErrorBoundary>
        <DispatchOperator {...operatorProps} userName={userName} />
      </AgentErrorBoundary>
    </div>
  );
}

class AgentErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Dispatch agent failed to load", { error, info });
  }
  render() {
    if (this.state.failed)
      return (
        <div className="grid min-h-80 place-items-center p-6 text-center">
          <div>
            <p className="text-lg font-semibold text-[var(--ink)]">
              The dispatch agent is unavailable in this local preview.
            </p>
            <p className="mt-2 max-w-md text-sm text-[var(--ink-soft)]">
              Other workspace areas, including Learning, are still available.
            </p>
          </div>
        </div>
      );
    return this.props.children;
  }
}

type LearningLesson = {
  id: string;
  title: string;
  topic: string;
  issueSummary: string;
  keyTakeaway: string;
  equipmentTags: string[];
  status: "draft" | "published";
  publishedAt: string | null;
  createdAt: string;
  steps?: string[];
  ambiguityNote?: string | null;
};
/**
 * THE CALL, PLAYABLE WHERE IT IS LISTED. Reviewing a call meant opening the
 * lesson to reach a play button; the point of the library is that somebody
 * listens, so the card plays it. A scrubber, because a supervisor skips to the
 * part he cares about rather than sitting through eight minutes, and 1.25×,
 * because most of them do.
 */
function clock(seconds: number) {
  if (!Number.isFinite(seconds)) return "0:00";
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

function CallPlayer({
  lessonId,
  dark = false,
}: {
  lessonId: string;
  dark?: boolean;
}) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [length, setLength] = useState(0);
  const [rate, setRate] = useState(1);
  const [failed, setFailed] = useState(false);
  const toggle = async () => {
    const player = audio.current;
    if (!player) return;
    try {
      if (player.paused) await player.play();
      else player.pause();
    } catch {
      setFailed(true);
    }
  };
  const seek = (seconds: number) => {
    const player = audio.current;
    if (!player) return;
    player.currentTime = seconds;
    setAt(seconds);
  };
  const toggleRate = () => {
    const player = audio.current;
    if (!player) return;
    const next = rate === 1 ? 1.25 : 1;
    player.playbackRate = next;
    setRate(next);
  };
  const ink = dark ? "text-[var(--paper)]" : "text-[var(--ink)]";
  const line = dark ? "border-[var(--paper)]/30" : "border-[var(--line)]";
  if (failed)
    return (
      <p
        className={`text-xs ${dark ? "text-[var(--ink-faint)]" : "text-[var(--ink-soft)]"}`}
      >
        No recording on this lesson.
      </p>
    );
  return (
    <div
      className={`flex items-center gap-2.5 rounded-lg border ${line} px-2.5 py-2`}
      onClick={(event) => event.stopPropagation()}
    >
      <audio
        ref={audio}
        src={`${API_URL}/api/v1/learning/${lessonId}/source-audio`}
        crossOrigin="use-credentials"
        preload="metadata"
        onLoadedMetadata={(event) => setLength(event.currentTarget.duration)}
        onTimeUpdate={(event) => setAt(event.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setAt(0);
        }}
        onError={() => setFailed(true)}
      />
      <button
        type="button"
        onClick={() => void toggle()}
        aria-label={playing ? "Pause the call" : "Play the call"}
        className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--accent)] text-white transition hover:brightness-110"
      >
        {playing ? (
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="currentColor"
            aria-hidden="true"
          >
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 5.5v13a.8.8 0 0 0 1.2.7l10-6.5a.8.8 0 0 0 0-1.4l-10-6.5A.8.8 0 0 0 8 5.5z" />
          </svg>
        )}
      </button>
      <input
        type="range"
        aria-label="Position in the call"
        min={0}
        max={Math.max(1, length)}
        step={1}
        value={at}
        onChange={(event) => seek(Number(event.target.value))}
        className="wv-scrub min-w-0 flex-1"
      />
      <span
        className={`shrink-0 text-[11px] font-medium tabular-nums ${dark ? "text-[var(--ink-faint)]" : "text-[var(--ink-soft)]"}`}
      >
        {clock(at)} / {clock(length)}
      </span>
      <button
        type="button"
        onClick={toggleRate}
        aria-label={`Play at ${rate === 1 ? "1.25" : "1"} times speed`}
        className={`shrink-0 rounded-md border ${line} px-1.5 py-0.5 text-[11px] font-medium ${ink}`}
      >
        {rate === 1.25 ? "1.25\u00d7" : "1\u00d7"}
      </button>
    </div>
  );
}

function SourceCallQuestions({ lessonId }: { lessonId: string }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState("");
  const ask = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!question.trim()) return;
    setAsking(true);
    setError("");
    try {
      const result = await mutate<{ answer: string }>(
        `/api/v1/learning/${lessonId}/source-questions`,
        "POST",
        { question },
      );
      setAnswer(result.answer);
    } catch {
      setError("I couldn’t search this call right now.");
    } finally {
      setAsking(false);
    }
  };
  return (
    <div className="mt-4 rounded-xl border border-[var(--line)]/35 bg-[var(--ink)] p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-soft)]">
        Ask this call
      </p>
      <p className="mt-1 text-xs leading-5 text-[var(--ink-faint)]">
        Answers use only the reviewed call transcript.
      </p>
      <form onSubmit={ask} className="mt-3 flex gap-2">
        <input
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="What did Kevin recommend?"
          className="min-w-0 flex-1 rounded-lg border border-[var(--paper)]/30 bg-[var(--paper)] px-3 py-2 text-sm text-[var(--ink)] placeholder:text-[var(--ink-faint)]"
        />
        <button
          disabled={asking}
          className="rounded-lg bg-[var(--wash)] px-3 py-2 text-sm font-semibold text-[var(--ink)] disabled:opacity-60"
        >
          {asking ? "Asking…" : "Ask"}
        </button>
      </form>
      {error && <p className="mt-3 text-xs text-[var(--accent)]">{error}</p>}
      {answer && (
        <p className="mt-3 rounded-lg bg-[var(--paper)]/10 p-3 text-sm leading-6 text-[var(--paper)]">
          {answer}
        </p>
      )}
    </div>
  );
}
function LearningWorkspace({ canWrite }: { canWrite: boolean }) {
  const [items, setItems] = useState<LearningLesson[]>([]);
  const [selected, setSelected] = useState<LearningLesson | null>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(
    async (q = query) => {
      try {
        setLoading(true);
        const result = await data<{ items: LearningLesson[] }>(
          `/api/v1/learning?q=${encodeURIComponent(q)}`,
        );
        setItems(result.items);
      } catch {
        setError("We couldn’t load the company knowledge library.");
      } finally {
        setLoading(false);
      }
    },
    [query],
  );
  useEffect(() => {
    void load("");
  }, []);
  const open = async (item: LearningLesson) => {
    try {
      setSelected(await data<LearningLesson>(`/api/v1/learning/${item.id}`));
    } catch {
      setError("We couldn’t open that lesson.");
    }
  };
  const publish = async () => {
    if (!selected?.steps) return;
    try {
      const saved = await mutate<LearningLesson>(
        `/api/v1/learning/${selected.id}`,
        "PATCH",
        {
          title: selected.title,
          topic: selected.topic,
          issueSummary: selected.issueSummary,
          steps: selected.steps,
          keyTakeaway: selected.keyTakeaway,
          equipmentTags: selected.equipmentTags,
          status: "published",
        },
      );
      setSelected(saved);
      await load();
    } catch {
      setError("We couldn’t publish this lesson.");
    }
  };
  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setUploading(true);
    setError("");
    try {
      const response = await apiFetch("/api/v1/learning", {
        method: "POST",
        body: form,
      });
      const payload = (await response.json()) as {
        data?: LearningLesson;
        error?: { message?: string };
      };
      if (!response.ok || !payload.data)
        throw new Error(payload.error?.message);
      setSelected(payload.data);
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn’t create a lesson draft.",
      );
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="min-h-[calc(100vh-150px)]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[.18em] text-[var(--ink)]">
            Company knowledge library
          </p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-[var(--ink)]">
            Lessons built from your team.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--ink-soft)]">
            Turn the answers your senior technicians give today into clear
            guidance for the next call.
          </p>
        </div>
        {canWrite && (
          <button
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-[var(--ink)] px-4 py-3 text-sm font-semibold text-[var(--paper)]"
          >
            Create lesson from call
          </button>
        )}
      </div>
      {error && (
        <p className="mt-4 rounded-lg bg-[var(--accent-wash)] px-4 py-3 text-sm text-[var(--accent)]">
          {error}
        </p>
      )}
      {showCreate && (
        <Dialog
          onClose={() => setShowCreate(false)}
          width="max-w-2xl"
          title="Create a lesson from a call"
        >
          <form onSubmit={create}>
            <p className="mt-1 text-sm text-[var(--ink-soft)]">
              The recording remains private. The reviewed transcript is used to
              draft the technician lesson.
            </p>
            <textarea
              required
              name="transcript"
              rows={7}
              placeholder="Paste the reviewed call transcript"
              className="mt-4 w-full rounded-xl border border-[var(--line)] bg-[var(--paper)] p-3 text-sm"
            />
            <input
              name="audio"
              type="file"
              accept="audio/*"
              className="mt-3 block text-sm"
            />
            <div className="mt-4 flex gap-3">
              <button
                disabled={uploading}
                className="rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-60"
              >
                {uploading ? "Creating draft…" : "Generate lesson draft"}
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="text-sm font-semibold text-[var(--ink-soft)]"
              >
                Cancel
              </button>
            </div>
          </form>
        </Dialog>
      )}
      <div className={`mt-7 ${selected ? "hidden" : ""}`}>
        <section>
          <div className="mb-4 flex gap-2">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void load();
              }}
              placeholder="Search lessons or topics"
              className="w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-4 py-3 text-sm"
            />
            <button
              onClick={() => void load()}
              className="rounded-lg border border-[var(--line)] px-4 text-sm font-semibold"
            >
              Search
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex flex-col rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5 transition hover:border-[var(--accent)]"
              >
                <button
                  type="button"
                  onClick={() => void open(item)}
                  className="flex-1 text-left"
                >
                  <SectionLabel>{item.topic}</SectionLabel>
                  <h2 className="mt-2 text-lg font-semibold text-[var(--ink)]">
                    {item.title}
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-[var(--ink-soft)]">
                    {item.issueSummary}
                  </p>
                  {item.status === "draft" && (
                    <span className="mt-3 inline-block rounded-md bg-[var(--warn-wash)] px-2 py-0.5 text-[11px] font-medium text-[var(--warn)]">
                      Draft
                    </span>
                  )}
                </button>
                {canWrite && (
                  <div className="mt-4">
                    <CallPlayer lessonId={item.id} />
                  </div>
                )}
              </div>
            ))}
            {!loading && !items.length && (
              <div className="col-span-full rounded-xl border border-dashed border-[var(--line)] bg-[var(--paper)] p-10 text-center text-sm text-[var(--ink-soft)]">
                No published lessons yet.{" "}
                {canWrite
                  ? "Create the first one from a reviewed call."
                  : "Check back after your team publishes guidance."}
              </div>
            )}
          </div>
        </section>
      </div>
      <aside
        className={`rounded-xl bg-[var(--ink)] p-6 text-[var(--paper)] ${selected ? "" : "hidden"}`}
      >
        {selected ? (
          <>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="mb-5 text-sm font-semibold text-[var(--paper)]"
            >
              ← Back to the library
            </button>
            <p className="text-xs font-semibold uppercase tracking-[.16em] text-[var(--ink-soft)]">
              {selected.topic}
            </p>
            <h2 className="mt-3 text-2xl font-semibold">{selected.title}</h2>
            <div className="mt-6 border-t border-[var(--paper)]/30 pt-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-soft)]">
                What was the issue?
              </p>
              <p className="mt-2 text-sm leading-6 text-[var(--ink-faint)]">
                {selected.issueSummary}
              </p>
            </div>
            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-soft)]">
                Steps we took
              </p>
              <ol className="mt-3 space-y-3">
                {selected.steps?.map((step, index) => (
                  <li
                    key={`${index}-${step}`}
                    className="flex gap-3 text-sm leading-5"
                  >
                    <span className="grid size-6 shrink-0 place-items-center rounded-lg bg-[var(--ink)] text-xs font-semibold">
                      {index + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
            <div className="mt-6 rounded-lg bg-[var(--wash)] p-4 text-[var(--ink)]">
              <p className="text-xs font-semibold uppercase tracking-wider">
                Key takeaway
              </p>
              <p className="mt-1 text-sm font-medium">{selected.keyTakeaway}</p>
            </div>
            {canWrite && (
              <>
                <div className="mt-4">
                  <SectionLabel>Private source call</SectionLabel>
                  <div className="mt-2">
                    <CallPlayer lessonId={selected.id} dark />
                  </div>
                </div>
                <SourceCallQuestions lessonId={selected.id} />
              </>
            )}
            {selected.ambiguityNote && (
              <p className="mt-4 text-xs text-[var(--ink)]">
                Reviewer note: {selected.ambiguityNote}
              </p>
            )}
            {canWrite && selected.status === "draft" && (
              <button
                onClick={() => void publish()}
                className="mt-6 rounded-lg bg-[var(--wash)] px-4 py-2 text-sm font-semibold text-[var(--ink)]"
              >
                Publish lesson
              </button>
            )}
          </>
        ) : null}
      </aside>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-2xl font-semibold text-[var(--ink)]">{value}</p>
      <p className="mt-1 text-xs font-medium uppercase tracking-wide text-[var(--ink-soft)]">
        {label}
      </p>
    </div>
  );
}
export function Customers({
  canReadCommercial,
  customers,
  tags = [],
  search,
  onSearch,
  open,
}: {
  canReadCommercial: boolean;
  customers: Customer[];
  tags?: WorkspaceTag[];
  search: string;
  onSearch: (value: string) => void;
  open: (id: string) => void;
}) {
  return (
    <>
      <h1 className="text-3xl font-semibold tracking-tight text-[var(--ink)]">
        Customers
      </h1>
      <div className="mt-6 rounded-xl border border-[var(--line)] bg-[var(--paper)]">
        <div className="border-b border-[var(--line)] p-4">
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search by name, email, service address, or phone"
            className="w-full rounded-lg border border-[var(--line)] bg-[var(--wash)] px-4 py-3 text-sm outline-none focus:border-[var(--line)] focus:bg-[var(--paper)]"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-[var(--wash)] text-xs uppercase tracking-wide text-[var(--ink-soft)]">
              <tr>
                <th className="px-5 py-3 font-semibold">Customer</th>
                <th className="px-5 py-3 font-semibold">Billing address</th>
                <th className="px-5 py-3 font-semibold">Last job</th>
                {canReadCommercial && (
                  <th className="px-5 py-3 text-right font-semibold">Owes</th>
                )}
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr
                  key={customer.id}
                  onClick={() => void open(customer.id)}
                  className="cursor-pointer border-t border-[var(--line)] hover:bg-[var(--wash)]"
                >
                  <td className="px-5 py-4">
                    <p className="font-semibold text-[var(--ink)]">
                      {customer.displayName ||
                        `${customer.firstName} ${customer.lastName}`}
                    </p>
                    {customer.email && (
                      <p className="mt-1 text-xs text-[var(--ink-soft)]">
                        {customer.email}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-[var(--ink-soft)]">
                    {customer.address || "No billing address"}
                  </td>
                  <td className="px-5 py-4 text-[var(--ink-soft)]">
                    {customer.lastJobAt ? date(customer.lastJobAt) : "Never"}
                  </td>
                  {canReadCommercial && (
                    <td
                      className={`px-5 py-4 text-right font-semibold ${customer.balanceCents ? "text-[var(--accent)]" : "text-[var(--ink-faint)]"}`}
                    >
                      {customer.balanceCents
                        ? money(customer.balanceCents)
                        : "—"}
                    </td>
                  )}
                </tr>
              ))}
              {!customers.length && (
                <tr>
                  <td
                    colSpan={canReadCommercial ? 4 : 3}
                    className="px-5 py-14 text-center text-[var(--ink-soft)]"
                  >
                    No customers match that search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
type CustomerAction = (
  customerId: string,
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body: unknown,
) => Promise<void>;
function TagEditor({ entityPath, assigned, catalog, canWrite, act, customerId }: { entityPath: string; assigned: WorkspaceTag[]; catalog: WorkspaceTag[]; canWrite: boolean; act: CustomerAction; customerId: string }) {
  /*
   * Only the tags this customer HAS are chips. The rest of the catalog used to
   * be drawn in the same row as add-buttons, so an account with none looked
   * like it had every tag the company ever made (Melania, 2/9/2026). Adding
   * one is a deliberate act now — open the picker, choose, done.
   */
  const [picking, setPicking] = useState(false);
  const [needle, setNeedle] = useState("");
  const available = catalog
    .filter((tag) => tag.active !== false && !assigned.some((item) => item.id === tag.id))
    .filter((tag) => !needle.trim() || tag.name.toLowerCase().includes(needle.trim().toLowerCase()));
  if (!assigned.length && !canWrite) return null;
  return <div className="mt-3">
    <div className="flex flex-wrap items-center gap-2">
      {assigned.map((tag) => <button key={tag.id} type="button" disabled={!canWrite} title={canWrite ? `Remove ${tag.name}` : tag.name} onClick={() => void act(customerId, `${entityPath}/${tag.id}`, "DELETE", {})} className="rounded-full border px-2.5 py-1 text-xs font-semibold disabled:cursor-default" style={{ borderColor: tag.color || "var(--line)", color: tag.color || "var(--ink)" }}>{tag.name}{canWrite ? " ×" : ""}</button>)}
      {canWrite && !picking && <button type="button" onClick={() => setPicking(true)} className="rounded-full border border-dashed border-[var(--line-strong)] px-2.5 py-1 text-xs font-semibold text-[var(--accent)]">+ Tag</button>}
    </div>
    {canWrite && picking && <div className="mt-2 max-w-sm rounded-lg border border-[var(--line)] bg-[var(--paper)] p-2">
      <div className="flex items-center gap-2">
        <input autoFocus value={needle} onChange={(event) => setNeedle(event.target.value)} placeholder="Find a tag" aria-label="Find a tag" className="w-full rounded-lg border border-[var(--line)] px-2.5 py-1.5 text-sm outline-none focus:border-[var(--line-strong)]" />
        <button type="button" onClick={() => { setPicking(false); setNeedle(""); }} className="shrink-0 text-xs font-semibold text-[var(--ink-soft)]">Done</button>
      </div>
      <div className="mt-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
        {available.slice(0, 40).map((tag) => <button key={tag.id} type="button" onClick={() => void act(customerId, entityPath, "POST", { tagId: tag.id })} className="rounded-full border border-[var(--line)] px-2.5 py-1 text-xs font-semibold text-[var(--ink)] hover:bg-[var(--wash)]">{tag.name}</button>)}
        {!available.length && <span className="px-1 text-xs text-[var(--ink-faint)]">{needle.trim() ? "No tag matches that." : "Every tag is already on this customer."}</span>}
      </div>
    </div>}
  </div>;
}

function contactAsBookingContact(contact: WorkspaceContact, from: BookingContact["from"]): BookingContact {
  return { ...contact, from };
}

function methodAsBookingContact(
  method: CustomerDetail["contactMethods"][number],
  index: number,
  fallbackName: string,
): BookingContact {
  const value = method.value.trim();
  const isEmail = value.includes("@");
  return {
    id: `method-${index}`,
    name: fallbackName,
    phone: isEmail ? null : value,
    phoneType: isEmail ? null : method.channel.toLowerCase() === "mobile" ? "mobile" : null,
    email: isEmail ? value : null,
    memo: null,
    isPrimary: Boolean(method.isPrimary),
    from: "customer",
  };
}

function normalizedContactEmail(value: string | null | undefined) {
  return (value ?? "").trim().toLowerCase();
}

function contactMethodKey(method: CustomerDetail["contactMethods"][number]) {
  const value = method.value.trim();
  if (!value) return "";
  const channel = method.channel.trim().toLowerCase();
  if (channel === "email" || value.includes("@")) {
    return `email:${normalizedContactEmail(value)}`;
  }
  const digits = value.replace(/\D/g, "");
  return `phone:${digits || value.toLowerCase()}`;
}

function dedupeCustomerContactMethods(methods: CustomerDetail["contactMethods"]) {
  const seen = new Set<string>();
  return methods.filter((method) => {
    const key = contactMethodKey(method);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function textableContactChannel(channel: string | null | undefined) {
  return Boolean(channel && ["mobile", "cell", "sms", "text"].includes(channel.toLowerCase()));
}

function CustomerContactPerson({
  person,
  fallbackName,
  customerId,
}: {
  customerId?: string;
  person: BookingPerson;
  fallbackName: string;
}) {
  const communication = useRecordCommunications();
  const ways = bookingContactWays(person);
  const displayName = person.name || fallbackName;
  const shownWays = ways;

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex min-w-0 items-start justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-semibold text-[var(--ink)]">
          {displayName}
        </p>
        {person.isPrimary && (
          <span className="shrink-0 rounded-md border border-[var(--line)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-soft)]">
            Primary
          </span>
        )}
      </div>
      {shownWays.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {shownWays.map((way) => {
            const isEmail = way.value.includes("@");
            const rawPhone = isEmail ? "" : way.contact.phone ?? way.value;
            const href = isEmail
              ? `mailto:${way.value}`
              : "";
            const ContactAction = isEmail ? "a" : "button";
            return (
              <ContactAction
                key={way.key}
                href={isEmail ? href : undefined}
                type={isEmail ? undefined : "button"}
                disabled={isEmail ? undefined : !customerId || !communication.openCalls}
                title={isEmail ? undefined : "Calling inside WorkView is not available yet"}
                onClick={isEmail ? undefined : () => customerId && communication.openCalls?.({ customerId, phone: rawPhone, name: displayName })}
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md bg-[var(--wash)] px-2 py-1 text-[12px] font-semibold text-[var(--ink)] transition hover:bg-[var(--accent-wash)]"
              >
                <span className="min-w-0 truncate">{way.value}</span>
                <span className="contact-method-memo">{way.contact.memo}</span>
                <ContactNotifications off={way.contact.doNotAlert} />
                {way.note && (
                  <span className="shrink-0 text-[11px] font-medium text-[var(--ink-soft)]">
                    {way.note}
                  </span>
                )}
              </ContactAction>
            );
          })}

        </div>
      )}
      {textableContactChannel(person.phones[0]?.type) && person.phones[0] && (
        <button type="button" data-workspace-widget-trigger="texts"
          disabled={!customerId || !communication.openText}
          onClick={() => customerId && communication.openText?.({ customerId, phone: person.phones[0].value, name: displayName })}
          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--accent)]"
        >
          <MessageIcon />
          Text
        </button>
      )}

    </div>
  );
}

function CustomerContactOverview({
  customerId,
  customerName,
  billingPeople,
  label = "Billing contact",
  countLabel = "billing contact",
  emptyText = "No billing contact on file.",
  compact = false,
  showAll = false,
}: {
  customerName: string;
  billingPeople: BookingPerson[];
  customerId?: string;
  label?: string;
  countLabel?: string;
  emptyText?: string;
  compact?: boolean;
  showAll?: boolean;
}) {
  const shownBillingPeople = showAll ? billingPeople : billingPeople.slice(0, 3);
  const hiddenBillingPeople = billingPeople.length - shownBillingPeople.length;
  return (
    <div className={compact ? "" : "mt-4 border-t border-[var(--line)] pt-4"}>
      {!compact && <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel>{label}</SectionLabel>
        <span className="text-[12px] font-semibold text-[var(--ink-soft)]">
          {billingPeople.length} {countLabel}
          {billingPeople.length === 1 ? "" : "s"}
        </span>
      </div>}
      <div className={`${compact ? "" : "mt-4 "}divide-y divide-[var(--line)]`}>
        {shownBillingPeople.map((person) => (
          <CustomerContactPerson
            key={person.key}
            person={person}
            fallbackName={customerName}
            customerId={customerId}
          />
        ))}
        {!billingPeople.length && (
          <p className="py-3 text-sm text-[var(--ink-faint)]">
            {emptyText}
          </p>
        )}
      </div>
      {hiddenBillingPeople > 0 && (
        <p className="mt-2 text-[12px] font-semibold text-[var(--ink-soft)]">
          +{hiddenBillingPeople} more {countLabel}
          {hiddenBillingPeople === 1 ? "" : "s"}
        </p>
      )}
    </div>
  );
}

const WidgetFocusContext = createContext<{ frontline: string | null; activate: (title: string) => void } | null>(null);

function RecordWidgetGrid({ children, ariaLabel, as = "div" }: { children: ReactNode; ariaLabel: string; as?: "div" | "aside" }) {
  const [frontline, activate] = useState<string | null>(null);
  const Container = as;
  return <WidgetFocusContext.Provider value={{ frontline, activate }}><Container className="location-side-widgets" aria-label={ariaLabel}>{children}</Container></WidgetFocusContext.Provider>;
}

const CustomerJobContext = createContext<((id: string) => void) | undefined>(undefined);
const WidgetDisclosureContext = createContext<(() => void) | undefined>(undefined);

function WidgetPreviewButton({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  const expand = useContext(WidgetDisclosureContext);
  return <button type="button" className="widget-record-action" onClick={onClick || expand}>{children}</button>;
}

function CustomerJobLink({ jobId, href, children, className }: { jobId: string; href: string; children: ReactNode; className?: string }) {
  const open = useContext(CustomerJobContext);
  return <a href={href} className={className} onClick={event => {
    if (!open || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); open(jobId);
  }}>{children}</a>;
}

function CustomerBillingAddress({ customer, canEdit, act }: { customer: CustomerDetail["customer"]; canEdit: boolean; act: CustomerAction }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const address = streetAddress({
    addressLine1: customer.billingAddressLine1,
    addressLine2: customer.billingAddressLine2,
    city: customer.billingCity,
    state: customer.billingState,
    postalCode: customer.billingPostalCode,
  });
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await act(customer.id, `/api/v1/customers/${customer.id}`, "PATCH", {
        billingAddressLine1: String(form.get("billingAddressLine1") || "").trim(),
        billingAddressLine2: String(form.get("billingAddressLine2") || "").trim() || null,
        billingCity: String(form.get("billingCity") || "").trim(),
        billingState: String(form.get("billingState") || "").trim(),
        billingPostalCode: String(form.get("billingPostalCode") || "").trim(),
        // The old coordinates no longer describe an edited address.
        billingLat: null,
        billingLng: null,
      });
      setEditing(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to save billing address. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return <div className="mt-3 space-y-3">
    <address className="customer-billing-address">{address || "No billing address on file"}</address>
    {canEdit && (editing ? <form aria-label="Edit billing address" onSubmit={event => void save(event)} className="space-y-3">
      <p className="text-sm text-[var(--ink-soft)]">Update the billing address for this customer. Service locations are managed separately.</p>
      <fieldset disabled={busy} className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2"><TextInput autoFocus required label="Street address" name="billingAddressLine1" defaultValue={customer.billingAddressLine1 ?? ""} autoComplete="billing address-line1" pattern={".*\\S.*"} /></div>
        <div className="sm:col-span-2"><TextInput label="Unit / suite" name="billingAddressLine2" defaultValue={customer.billingAddressLine2 ?? ""} autoComplete="billing address-line2" /></div>
        <TextInput required label="City" name="billingCity" defaultValue={customer.billingCity ?? ""} autoComplete="billing address-level2" pattern={".*\\S.*"} />
        <TextInput required label="State" name="billingState" defaultValue={customer.billingState ?? ""} autoComplete="billing address-level1" pattern={".*\\S.*"} />
        <TextInput required label="ZIP / postal code" name="billingPostalCode" defaultValue={customer.billingPostalCode ?? ""} autoComplete="billing postal-code" pattern={".*\\S.*"} />
      </fieldset>
      {error && <p role="alert" className="text-sm text-[var(--ink)]">{error}</p>}
      <div className="flex flex-wrap gap-3">
        <QuietButton type="submit" disabled={busy}>{busy ? "Saving…" : "Save billing address"}</QuietButton>
        <QuietButton type="button" disabled={busy} onClick={() => { setEditing(false); setError(""); }}>Cancel</QuietButton>
      </div>
    </form> : <button type="button" className="text-sm font-semibold text-[var(--accent)]" onClick={() => setEditing(true)}>Edit billing address</button>)}
  </div>;
}

export function CustomerWorkspace({
  timezone = "UTC",
  canReadJobs = false,
  canReadCommercial,
  canWriteCustomers,
  canWriteCommercial,
  canBookJobs,
  act,
  tags,
  detail,
  openJob,
  onOpenJob,
  openInvoice,
  onOpenInvoiceId,
  openEstimate,
  bookJob,
  back,
  openLocation,
}: {
  timezone?: string;
  canReadJobs?: boolean;
  canReadCommercial: boolean;
  canWriteCustomers: boolean;
  canWriteCommercial: boolean;
  canBookJobs: boolean;
  act: CustomerAction;
  tags: WorkspaceTag[];
  detail: CustomerDetail;
  onOpenJob?: (id: string) => void;
  openJob: (id: string, fromCustomerId?: string) => string;
  openInvoice: (invoice: CustomerDetail["invoices"][number]) => void;
  onOpenInvoiceId?: (id: string) => void;
  openEstimate?: (estimate: CustomerDetail["estimates"][number]) => void;
  bookJob: (customer: BookingCustomer) => void;
  back: () => void;
  openLocation: (propertyId: string) => void;
}) {
  const { customer } = detail;
  // customer.balance_cents is never written by the import, so it reads zero on every
  // imported account. What the customer owes is the sum of the unpaid invoices.
  const openInvoices = detail.invoices.filter(
    (invoice) => invoice.balanceDueCents > 0,
  );
  const owedCents = openInvoices.reduce(
    (total, invoice) => total + invoice.balanceDueCents,
    0,
  );
  const [payingOpen, setPayingOpen] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState<"outstanding" | "paid" | "all">("outstanding");
  const name =
    customer.displayName || `${customer.firstName} ${customer.lastName}`;
  const accountContactMethods = dedupeCustomerContactMethods(detail.contactMethods);
  if (
    customer.email &&
    !accountContactMethods.some(
      (method) =>
        normalizedContactEmail(method.value) === normalizedContactEmail(customer.email),
    )
  )
    accountContactMethods.unshift({
      channel: "email",
      value: customer.email,
      isPrimary: 0,
    });
  const billingPeople = groupBookingContacts(
    (detail.contacts ?? []).length
      ? (detail.contacts ?? []).map((contact) =>
          contactAsBookingContact(contact, "customer"),
        )
      : accountContactMethods.map((method, index) =>
          methodAsBookingContact(method, index, name),
        ),
  );
  const contactContent = <div className="customer-contact-widget-body">
{canWriteCustomers ? (
                  <OwnedContacts
                    customerName={name}
                    canEdit
                    sections={[{
                      owner: "customer",
                      ownerId: customer.id,
                      title: "Contact information",
                      records: detail.contacts ?? [],
                    }]}
                    saveContact={async (_owner, ownerId, contact, contactId) => {
                      await act(
                        customer.id,
                        `/api/v1/workspace/customers/${ownerId}/contacts${contactId ? `/${contactId}` : ""}`,
                        contactId ? "PATCH" : "POST",
                        contact,
                      );
                    }}
                    deleteContact={async (_owner, ownerId, contactId) => {
                      await act(
                        customer.id,
                        `/api/v1/workspace/customers/${ownerId}/contacts/${contactId}`,
                        "DELETE",
                        {},
                      );
                    }}
                  />
                ) : (
                  <CustomerContactOverview
                    customerId={customer.id}
                    customerName={name}
                    billingPeople={billingPeople}
                    compact
                    showAll
                  />
                )}
  </div>;
  const billingContent = (
      <div className="customer-billing-content">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-semibold tracking-tight text-[var(--ink)]">
                {name}
              </h1>
              {Boolean(customer.taxExempt) && <span className="rounded-md bg-[var(--wash)] px-2 py-0.5 text-[11px] font-medium text-[var(--ink-soft)]">Tax exempt</span>}
            </div>
            {canWriteCustomers && (
              <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-sm text-[var(--ink-soft)]" title="Estimates and invoices calculate no sales tax">
                <input
                  type="checkbox"
                  checked={Boolean(customer.taxExempt)}
                  onChange={(event) => void act(customer.id, `/api/v1/customers/${customer.id}`, "PATCH", { taxExempt: event.currentTarget.checked })}
                />
                Tax exempt
              </label>
            )}
            <TagEditor entityPath={`/api/v1/workspace/customers/${customer.id}/tags`} assigned={detail.tags ?? []} catalog={tags} canWrite={canWriteCustomers} act={act} customerId={customer.id} />

          </div>
          {canReadCommercial && (
            <button
              type="button"
              onClick={() => setPayingOpen(true)}
              disabled={!owedCents || !canWriteCommercial}
              className="rounded-lg bg-[var(--wash)] px-4 py-3 text-right enabled:hover:bg-[var(--line)] disabled:cursor-default"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-soft)]">
                Owes
              </p>
              <p
                className={`mt-1 text-xl font-semibold ${owedCents ? "text-[var(--accent)]" : "text-[var(--ink)]"}`}
              >
                {money(owedCents)}
              </p>
              {owedCents > 0 && canWriteCommercial && (
                <p className="mt-1 text-xs font-semibold text-[var(--accent)]">
                  Take a payment →
                </p>
              )}
              {owedCents > 0 && (
                <p className="mt-1 text-xs text-[var(--ink-soft)]">
                  {openInvoices.length} unpaid invoice
                  {openInvoices.length === 1 ? "" : "s"}
                </p>
              )}
            </button>
          )}
        </div>
      <CustomerBillingAddress key={customer.id} customer={customer} canEdit={canWriteCustomers} act={act} />
        {contactContent}
      </div>
  );
  const locationsContent = (
          <ServiceLocations
            compact
            customerId={customer.id}
            memberships={canReadCommercial ? detail.memberships : undefined}
            today={new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date())}
            properties={detail.properties}
            jobs={detail.jobs}
            jobsTotal={detail.jobsTotal}
            equipment={detail.equipment}
            openLocation={openLocation}
            canWrite={canWriteCustomers}
            act={act}
          />
  );
  return (
    <CustomerJobContext.Provider value={onOpenJob}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={back}
          className="text-sm font-semibold text-[var(--ink)]"
        >
          ← Back to customers
        </button>
        {canBookJobs && (
          <button
            type="button"
            onClick={() =>
              bookJob({
                id: customer.id,
                firstName: customer.firstName,
                lastName: customer.lastName,
                email: customer.email,
              })
            }
            className="rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)]"
          >
            Book Job
          </button>
        )}
      </div>

      <RecordWidgetGrid key={customer.id} ariaLabel="Customer widgets">
        <LocationSideWidget title="Billing" preview={billingContent}>{billingContent}</LocationSideWidget>
        <LocationSideWidget title="Service locations" count={detail.properties.length} countLabel="service locations" preview={locationsContent}>{locationsContent}</LocationSideWidget>
        {canReadCommercial && <CustomerMembershipWidget detail={detail} today={new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date())} canWrite={canWriteCommercial} canBookJobs={canBookJobs} bookJob={bookJob} act={act} tags={tags} openJob={openJob} />}
        <EquipmentWidget equipment={detail.equipment} />
        {canReadCommercial && <LocationSideWidget title="Invoices" count={invoiceFilter === "all" ? detail.invoices.length : invoiceFilter === "outstanding" ? openInvoices.length : detail.invoices.filter(item => item.status === "paid").length} countLabel={invoiceFilter === "all" ? "recent invoices" : `${invoiceFilter} invoices`} summary={<div className="record-filter-bar" aria-label="Invoice filters">{(["outstanding", "paid", "all"] as const).map(kind => <WidgetFilterButton key={kind} selected={invoiceFilter === kind} onSelect={() => setInvoiceFilter(kind)}>{kind === "all" ? "All" : kind === "paid" ? "Paid" : "Outstanding"} ({kind === "all" ? detail.invoices.length : kind === "outstanding" ? openInvoices.length : detail.invoices.filter(item => item.status === "paid").length})</WidgetFilterButton>)}</div>} subtitle={owedCents ? `${money(owedCents)} outstanding · ${openInvoices.length} unpaid` : "No balance due on recent invoices"} attention={owedCents > 0} expanded={payingOpen} onCollapse={() => setPayingOpen(false)} preview={<div className="location-record-preview">{(invoiceFilter === "all" ? detail.invoices : invoiceFilter === "outstanding" ? openInvoices : detail.invoices.filter(item => item.status === "paid")).slice(0, 5).map(item => <WidgetPreviewButton key={item.id} onClick={() => openInvoice(item)}>{customerDocumentLabel("Invoice", item)}<span>{item.status === "paid" ? "Paid" : `${money(item.balanceDueCents)} due`}</span></WidgetPreviewButton>)}</div>}>
              <InvoicesPanel
                invoiceFilter={invoiceFilter}
                onInvoiceFilterChange={setInvoiceFilter}
                customerId={customer.id}
                openInvoice={openInvoice}
                openJob={openJob}
                invoices={detail.invoices}
                properties={detail.properties}
                credits={detail.credits}
                owedCents={owedCents}
                canWrite={canWriteCommercial}
                act={act}
                open={payingOpen}
                setOpen={setPayingOpen}
              />
        </LocationSideWidget>}
        {canReadCommercial && <EstimateRecordWidget items={detail.estimates} detail={detail} canReadJobs={canReadJobs} openJob={openJob} openEstimate={openEstimate ? id => { const estimate = detail.estimates.find(item => item.id === id); if (estimate) openEstimate(estimate); } : undefined} openInvoice={id => { if (onOpenInvoiceId) return onOpenInvoiceId(id); const invoice = detail.invoices.find(item => item.id === id); if (invoice) openInvoice(invoice); }} />}
        <LocationJobs detail={detail} timezone={timezone} openJob={openJob} canReadJobs={canReadJobs} />
        <CustomerPhotosWidget customerId={customer.id} openJob={openJob} />
        <DocumentsPanel title="Documents" path={`/api/v1/workspace/customers/${customer.id}/attachments`} canWrite={canWriteCustomers} groupByLocation locationWidget />
      </RecordWidgetGrid>
    </CustomerJobContext.Provider>
  );
}
function PhoneIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}
function MessageIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}
function Panel({
  title,
  tone = "standard",
  children,
}: {
  title: string;
  tone?: LabelTone;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5">
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-[var(--ink)]">
        <span
          aria-hidden="true"
          className={`h-3.5 w-1 rounded-full ${toneBar[tone]}`}
        />
        {title}
      </h2>
      {children}
    </section>
  );
}
const toneBar: Record<LabelTone, string> = {
  standard: "bg-[var(--accent)]",
  important: "bg-[var(--warn)]",
  urgent: "bg-[var(--st-conflict)]",
  good: "bg-[var(--good)]",
  renewal: "bg-[var(--band-purple)]",
};
const toneChip: Record<LabelTone, string> = {
  standard: "bg-[var(--accent-wash)] text-[var(--accent)]",
  important: "bg-[var(--warn-wash)] text-[var(--warn)]",
  urgent: "bg-[var(--st-conflict-wash)] text-[var(--st-conflict)]",
  good: "bg-[var(--good-wash)] text-[var(--good)]",
  renewal: "bg-[var(--st-dispatched-wash)] text-[var(--band-purple)]",
};

/**
 * WHAT A MEMBERSHIP'S COLOUR MEANS, in Melania's words:
 *   red     expired
 *   purple  up for renewal
 *   green   up to date
 * Read in that order — expired beats renewal. A plan that is lapsed AND ready
 * to renew is still lapsed; that is the thing to ring about.
 */
const MEMBERSHIP_WORST: LabelTone[] = [
  "urgent",
  "renewal",
  "good",
];
function membershipPanelTone(
  memberships: CustomerDetail["memberships"],
): LabelTone {
  if (!memberships.length) return "standard";
  const tones = memberships.map(
    (membership) => membershipState(membership).tone,
  );
  return MEMBERSHIP_WORST.find((tone) => tones.includes(tone)) ?? "standard";
}

function MembershipStatusChip({ label, tone, ariaLabel }: { label: string; tone: LabelTone; ariaLabel?: string }) {
  return <span aria-label={ariaLabel} className={`membership-status-chip rounded-md px-2 py-0.5 text-[11px] font-medium ${toneChip[tone]}`}>{label}</span>;
}
function locationMembershipTone(state: ReturnType<typeof locationMembershipStatus>["state"]): LabelTone {
  return state === "active" ? "good" : state === "upcoming" ? "renewal" : state === "expired" ? "urgent" : "standard";
}

function membershipState(membership: CustomerDetail["memberships"][number], today = new Date().toISOString().slice(0, 10)): {
  tone: LabelTone;
  label: string;
} {
  const status = (membership.displayStatus || membership.status).toLowerCase();
  if (membership.renewedIntoId || status === "renewed") return { tone: "standard", label: "Prior term · renewed" };
  const within = (value: string | null, days: number) => {
    if (!value) return false;
    const limit = new Date();
    limit.setDate(limit.getDate() + days);
    return value.slice(0, 10) <= limit.toISOString().slice(0, 10);
  };
  if (["cancel", "delete", "inactive", "pause", "suspend"].some(value => status.includes(value)))
    return { tone: "standard", label: "Inactive membership" };
  if (status.includes("expire") || status.includes("lapse") || (membership.endDate && membership.endDate.slice(0, 10) < today))
    return { tone: "urgent", label: "Expired" };
  if (status.includes("renew") || within(membership.nextBillingDate, 30))
    return { tone: "renewal", label: "Ready to renew" };
  if (status.includes("active")) return { tone: "good", label: "Active member" };
  return { tone: "standard", label: `Membership ${membership.status}` };
}
function Empty({ text }: { text: string }) {
  return <p className="py-3 text-sm text-[var(--ink-faint)]">{text}</p>;
}
/**
 * One thumbnail, which fetches its own image.
 *
 * The gallery used to pull every blob for every photo the moment it mounted.
 * On a customer with hundreds of job photos that is hundreds of authenticated
 * requests before anything is on screen. A thumbnail now loads only when it is
 * actually rendered, so a page of twelve costs twelve.
 */
function PhotoThumb({
  photo,
  onOpen,
  openJob,
}: {
  photo: Photo;
  onOpen: (photo: Photo & { imageUrl: string }) => void;
  openJob?: (id: string) => string;
}) {
  const { imageUrl, error: photoError, retry, onImageError } = usePhotoImage(photo.contentUrl);
  return (
    <div className="min-w-0">
      <button
        type="button"
        disabled={!imageUrl || !photo.contentUrl}
        onClick={() => imageUrl && onOpen({ ...photo, imageUrl })}
        className="block w-full overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--wash)] text-left transition hover:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            onError={onImageError}
            alt={photo.fileName || `Photo from ${date(photo.createdAt)}`}
            className="aspect-[4/3] w-full object-cover"
          />
        ) : (
          <span className="grid aspect-[4/3] w-full place-items-center text-xs text-[var(--ink-faint)]">
            {photoError || (photo.contentUrl ? "Loading…" : "Not copied to preview")}
          </span>
        )}
        <span className="block truncate px-2 py-1.5 text-[11px] font-medium text-[var(--ink-soft)]">
          {date(photo.createdAt, { month: "short", day: "numeric" })}
        </span>
      </button>
      {photoError && <button type="button" onClick={retry} className="mt-1 text-xs font-semibold text-[var(--accent)]">Retry photo</button>}
      {photo.jobId && openJob && (
        <CustomerJobLink
          jobId={photo.jobId}
          href={openJob(photo.jobId)}
          className="mt-1 block truncate text-xs font-semibold text-[var(--accent)] hover:underline"
        >
          Job #{photo.jobNumber ?? "record"}
        </CustomerJobLink>
      )}
    </div>
  );
}

const PHOTO_PAGE = 12;

/** A grid of thumbnails, a page at a time, with the full photo behind a click. */
function PhotoGrid({
  photos,
  openJob,
}: {
  photos: Photo[];
  openJob?: (id: string) => string;
}) {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<
    (Photo & { imageUrl: string }) | null
  >(null);
  const pages = Math.ceil(photos.length / PHOTO_PAGE);
  const current = photos.slice(
    page * PHOTO_PAGE,
    page * PHOTO_PAGE + PHOTO_PAGE,
  );
  return (
    <div>
      <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 lg:grid-cols-6">
        {current.map((photo) => (
          <PhotoThumb
            key={photo.id}
            photo={photo}
            onOpen={setSelected}
            openJob={openJob}
          />
        ))}
      </div>
      {pages > 1 && (
        <div className="mt-3 flex items-center gap-2">
          <QuietButton
            type="button"
            disabled={page === 0}
            onClick={() => setPage((n) => Math.max(0, n - 1))}
          >
            Back
          </QuietButton>
          <span className="text-xs font-semibold text-[var(--ink-soft)]">
            {page * PHOTO_PAGE + 1}–
            {Math.min(photos.length, (page + 1) * PHOTO_PAGE)} of{" "}
            {photos.length}
          </span>
          <QuietButton
            type="button"
            disabled={page + 1 >= pages}
            onClick={() => setPage((n) => Math.min(pages - 1, n + 1))}
          >
            More
          </QuietButton>
        </div>
      )}
      {selected && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Photo preview"
          className="fixed inset-0 z-[70] grid place-items-center bg-[var(--ink)]/80 p-5"
          onClick={() => setSelected(null)}
        >
          <div
            className="absolute right-5 top-5 flex gap-2"
            onClick={(event) => event.stopPropagation()}
          >
            {/* The image is already a blob in the page, so saving it costs nothing
            and does not send the office back through an authenticated URL. */}
            <a
              href={selected.imageUrl}
              download={selected.fileName || `photo-${selected.id}.jpg`}
              className="rounded-lg bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--accent)]"
            >
              Download
            </a>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="rounded-lg bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)]"
            >
              Close
            </button>
          </div>
          <img
            src={selected.imageUrl}
            alt={selected.fileName || "Photo preview"}
            onClick={(event) => event.stopPropagation()}
            className="max-h-[85vh] max-w-full object-contain"
          />
        </div>
      )}
    </div>
  );
}

/** Photo metadata for one owner. The images themselves load per thumbnail. */
function usePhotos(path: string, revision = 0) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    void data<{ items: Photo[] }>(path)
      .then(({ items }) => {
        if (!cancelled) setPhotos(items);
      })
      .catch(() => {
        if (!cancelled) setError("We couldn’t load photos.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, revision]);
  return { photos, loading, error };
}

type FileAttachment = { id: string; fileName: string | null; contentType: string | null; createdAt: string; contentUrl: string | null; availability?: "available" | "not_copied"; propertyId?: string | null; addressLine1?: string | null; city?: string | null; state?: string | null };
function DocumentsPanel({ title, path, canWrite, groupByLocation = false, locationWidget = false }: { title: string; path: string; canWrite: boolean; groupByLocation?: boolean; locationWidget?: boolean }) {
  const [items, setItems] = useState<FileAttachment[]>([]), [error, setError] = useState(""), [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const loadSequence = useRef(0);
  const load = async () => { const sequence = ++loadSequence.current; try { const nextItems = (await data<{ items: FileAttachment[] }>(path)).items; if (sequence === loadSequence.current) { setItems(nextItems); setLoaded(true); setError(""); } } catch { if (sequence === loadSequence.current) setError("We couldn’t load documents."); } };
  useEffect(() => { void load(); return () => { loadSequence.current += 1; }; }, [path]);
  const upload = async (file: File | undefined) => { if (!file) return; setBusy(true); setError(""); try { const body = new FormData(); body.set("file", file); const response = await apiFetch(path, { method: "POST", body }); const payload = await response.json(); if (!response.ok) throw new Error(payload.error?.message || "Upload failed."); await load(); } catch (cause) { setError(cause instanceof Error ? cause.message : "Upload failed."); } finally { setBusy(false); } };
  const remove = async (item: FileAttachment) => { if (!window.confirm(`Delete ${item.fileName || "this document"}?`)) return; setBusy(true); setError(""); try { const response = await apiFetch(`/api/v1/workspace/attachments/${item.id}`, { method: "DELETE" }); if (!response.ok) { const payload = await response.json(); throw new Error(payload.error?.message || "Delete failed."); } await load(); } catch (cause) { setError(cause instanceof Error ? cause.message : "Delete failed."); } finally { setBusy(false); } };
  const groups = groupByLocation ? items.reduce<Array<{ key: string; label: string; items: FileAttachment[] }>>((all, item) => { const key = item.propertyId || "customer"; const label = item.propertyId ? [item.addressLine1, item.city, item.state].filter(Boolean).join(", ") || "Service location" : "Customer documents"; const group = all.find((entry) => entry.key === key); if (group) group.items.push(item); else all.push({ key, label, items: [item] }); return all; }, []) : [{ key: "all", label: "", items }];
  const content = <Panel title={`${title} (${items.length})`}><div className="space-y-3">{canWrite && <label className="inline-flex cursor-pointer rounded-lg border border-[var(--line)] px-3 py-2 text-xs font-semibold text-[var(--ink-soft)]">{busy ? "Working…" : "Upload document"}<input aria-label={`Upload ${title}`} className="sr-only" type="file" accept="application/pdf,text/plain,.doc,.docx,.xls,.xlsx,.csv,text/csv" disabled={busy} onChange={(event) => void upload(event.target.files?.[0])} /></label>}{error && <p className="text-xs text-[var(--accent)]">{error}</p>}{groups.map((group) => <div key={group.key} className="space-y-2">{groupByLocation && <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-soft)]">{group.label}</p>}{group.items.map((item) => <div key={item.id} className="flex items-center justify-between gap-2 border-b border-[var(--line)] pb-2 last:border-0">{item.contentUrl ? <a className="min-w-0 truncate text-sm font-semibold text-[var(--accent)]" href={`${API_URL}${item.contentUrl}`} target="_blank" rel="noreferrer">{item.fileName || "Document"}</a> : <span className="min-w-0 truncate text-sm font-semibold text-[var(--ink-faint)]">{item.fileName || "Document"} <span className="font-normal">· Not copied to preview</span></span>}{canWrite && <button type="button" disabled={busy} onClick={() => void remove(item)} className="text-xs font-semibold text-[var(--accent)]">Delete</button>}</div>)}</div>)}{!items.length && <Empty text="No documents on file." />}</div></Panel>;
  if (locationWidget) return <LocationSideWidget title="Documents" count={loaded ? items.length : null} countLabel="documents on file" subtitle={error || (loaded && !items.length ? "Keep manuals, warranties, and other files here." : "Files saved to this service location")} attention={Boolean(error)} preview={<div className="location-record-preview">{items.slice(0, 2).map(item => <div key={item.id}>{item.contentUrl ? <a className="widget-record-action" href={`${API_URL}${item.contentUrl}`} target="_blank" rel="noreferrer">{item.fileName || "Document"}</a> : <p>{item.fileName || "Document"}<span>Not copied to preview</span></p>}</div>)}</div>}>{content}</LocationSideWidget>;
  return content;
}

/**
 * SERVICE LOCATIONS — the spine of the customer page.
 *
 * A customer is an account; the work happens at addresses. So the locations
 * are the list, and each one holds the photos taken there rather than the page
 * carrying one undifferentiated pile of "all photos". Five at a time, with a
 * search box once there are more than five, because accounts with dozens of
 * properties are the ones you actually need to find an address in.
 */
function LocationJobs({ detail, propertyId, timezone, openJob, canReadJobs }: {
  detail: CustomerDetail; propertyId?: string; timezone: string; openJob: (id: string, customerId?: string) => string; canReadJobs: boolean;
}) {
  const [jobs, setJobs] = useState(() => detail.jobs.filter(job => !propertyId || job.propertyId === propertyId));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  useEffect(() => {
    let active = true;
    const initial = detail.jobs.filter(job => !propertyId || job.propertyId === propertyId);
    const total = propertyId ? detail.properties.find(property => property.id === propertyId)?.jobsCount : detail.jobsTotal;
    setJobs(initial); setError(""); setPage(0);
    if (total !== undefined && initial.length >= total) { setLoading(false); return; }
    setLoading(true);
    void (async () => {
      try {
        const all: CustomerDetail["jobs"] = [];
        for (let nextPage = 1; active; nextPage++) {
          const params = new URLSearchParams({ ...(propertyId ? { propertyId } : {}), jobsPage: String(nextPage), jobsPageSize: "50" });
          const result = await data<CustomerDetail>(`/api/v1/workspace/customers/${detail.customer.id}?${params}`);
          if (!active) return;
          all.push(...result.jobs.filter(job => !propertyId || job.propertyId === propertyId));
          if (nextPage * (result.jobsPageSize ?? 50) >= (result.jobsTotal ?? all.length) || !result.jobs.length) break;
        }
        if (active) setJobs([...new Map(all.map(job => [job.id, job])).values()]);
      } catch { if (active) setError("We couldn’t load the full job history. Retry to search all jobs."); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [detail, propertyId, retry]);
  const timestamp = (job: CustomerDetail["jobs"][number]) => Date.parse(job.appointmentAt || job.createdAt) || Date.parse(job.createdAt) || 0;
  const search = query.trim().toLowerCase().replace(/^#/, "");
  const matched = jobs.filter(job => `${job.jobNumber} ${job.description ?? ""} ${statusChip(job.status).label} ${job.appointmentAt ?? job.createdAt}`.toLowerCase().includes(search))
    .sort((a, b) => timestamp(b) - timestamp(a) || a.id.localeCompare(b.id));
  const pages = Math.max(1, Math.ceil(matched.length / 5));
  const current = Math.min(page, pages - 1);
  const shown = matched.slice(current * 5, current * 5 + 5);
  const [jobTypeNames, setJobTypeNames] = useState<Record<string, string>>({});
  const [jobTypeRetry, setJobTypeRetry] = useState(0);
  const jobTypeIdsKey = JSON.stringify(shown.filter(job => !job.jobTypeName).map(job => job.id).sort());
  useEffect(() => {
    setJobTypeNames({});
    if (!canReadJobs) return;
    const controller = new AbortController();
    void Promise.all((JSON.parse(jobTypeIdsKey) as string[]).map(async jobId => {
      try {
        const response = await apiFetch(`/api/v1/workspace/jobs/${encodeURIComponent(jobId)}`, { signal: controller.signal });
        if (!response.ok) throw new Error("Unable to load job type");
        const payload = await response.json() as { data: JobDetail };
        const names = [...new Set((payload.data.jobTypes ?? []).map(type => type.name).filter(Boolean))].join(" · ");
        if (!controller.signal.aborted) setJobTypeNames(current => ({ ...current, [jobId]: names || "Job type not on file" }));
      } catch {
        if (!controller.signal.aborted) setJobTypeNames(current => ({ ...current, [jobId]: "Job type unavailable" }));
      }
    }));
    return () => controller.abort();
  }, [jobTypeIdsKey, canReadJobs, jobTypeRetry]);
  const content = <Panel title={`${propertyId ? "Jobs at this address" : "Customer jobs"} (${jobs.length})`}>
    <label className="block text-xs font-semibold text-[var(--ink-soft)]">Search jobs
      <input type="search" value={query} onChange={event => { setQuery(event.target.value); setPage(0); }} placeholder="Job number, description, or status" className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm" />
    </label>
    <p className="mt-2 text-xs text-[var(--ink-soft)]">Most recent appointment first</p>
    {Object.values(jobTypeNames).includes("Job type unavailable") && <button type="button" className="min-h-11 text-xs underline" onClick={() => setJobTypeRetry(value => value + 1)}>Retry job types</button>}
    {loading && <p role="status" className="mt-2 text-sm">Loading full job history…</p>}
    {error && <p role="alert" className="mt-2 text-sm text-[var(--accent)]">{error} <button type="button" className="underline" onClick={() => setRetry(value => value + 1)}>Retry jobs</button></p>}
    <div role="region" aria-label="Location job results" tabIndex={0} className="mt-3 max-h-[26rem] overflow-y-auto divide-y divide-[var(--line)]">
      {shown.map(job => <CustomerJobLink key={job.id} jobId={job.id} href={openJob(job.id, detail.customer.id)} className="location-job-row">
        <span className="location-job-number">#{job.jobNumber}</span>
        <span className="location-job-type">{job.jobTypeName || jobTypeNames[job.id] || (canReadJobs ? "Loading job type…" : "Job type not on file")}</span>
        <span className="location-job-description" title={job.description || undefined}>{cleanDescription(job.description, "No dispatch notes", 140)}</span>
        <span className={`location-job-status shrink-0 rounded-md px-2 py-0.5 text-[11px] font-medium ${statusChip(job.status).className}`}>{statusChip(job.status).label}</span>
        <span className="location-job-date">{date(job.appointmentAt || job.createdAt, { month: "short", day: "numeric", year: "numeric", timeZone: timezone })}</span>
      </CustomerJobLink>)}
      {!shown.length && !loading && !error && <Empty text={query.trim() ? "No jobs match your search." : "No job has been done at this address yet."} />}
    </div>
    <nav aria-label="Job history pages" className="mt-3 flex flex-wrap items-center justify-between gap-2">
      <span aria-live="polite" className="text-xs text-[var(--ink-soft)]">{matched.length ? `${current * 5 + 1}–${current * 5 + shown.length} of ${matched.length}` : "0 jobs"}</span>
      <div className="flex items-center gap-2"><QuietButton type="button" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous jobs</QuietButton><span className="text-xs">Page {current + 1} of {pages}</span><QuietButton type="button" disabled={current + 1 >= pages} onClick={() => setPage(current + 1)}>Next jobs</QuietButton></div>
    </nav>
  </Panel>;
  return <LocationSideWidget title="Jobs" count={loading || error ? (propertyId ? detail.properties.find(item => item.id === propertyId)?.jobsCount : detail.jobsTotal) ?? null : jobs.length} countLabel={propertyId ? "jobs at this address" : "jobs on this account"} subtitle={error || (loading ? "Loading full job history…" : "Service history at this address")} attention={Boolean(error)} preview={<div className="location-record-preview">{[...jobs].sort((a, b) => timestamp(b) - timestamp(a)).slice(0, 2).map(job => <CustomerJobLink key={job.id} jobId={job.id} href={openJob(job.id, detail.customer.id)} className="widget-record-action">#{job.jobNumber} · {job.jobTypeName || cleanDescription(job.description, "Service job", 60)}<span>{statusChip(job.status).label} · {date(job.appointmentAt || job.createdAt, { month: "short", day: "numeric", year: "numeric", timeZone: timezone })}</span></CustomerJobLink>)}{!loading && !jobs.length && <p>No jobs recorded yet.</p>}</div>}>{content}</LocationSideWidget>;
}

type EstimateDeliveryEntry = { id: string; channel?: "email" | "sms" | "link"; recipientEmail?: string | null; recipientPhone?: string | null; sentAt: string; sentByName?: string | null; deliveryError?: string | null };
function EstimateDeliverySummary({ estimateId, revision = 0, onlyText = false }: { estimateId: string; revision?: number; onlyText?: boolean }) {
  const [history, setHistory] = useState<{ email?: EstimateDeliveryEntry[] | null; text?: EstimateDeliveryEntry[] | null }>({});
  useEffect(() => {
    let canceled = false; setHistory({});
    for (const [key, endpoint] of [["email", "deliveries"], ["text", "remote-approval"]] as const) {
      if (onlyText && key === "email") continue;
      void data<{ items: EstimateDeliveryEntry[] }>(`/api/v1/workspace/estimates/${encodeURIComponent(estimateId)}/${endpoint}`)
        .then(result => { if (!Array.isArray(result.items)) throw new Error("History unavailable"); if (!canceled) setHistory(current => ({ ...current, [key]: result.items })); })
        .catch(() => { if (!canceled) setHistory(current => ({ ...current, [key]: null })); });
    }
    return () => { canceled = true; };
  }, [estimateId, revision, onlyText]);
  const latest = (items: EstimateDeliveryEntry[] | null | undefined) => items ? [...items].sort((a, b) => (Date.parse(b.sentAt) || 0) - (Date.parse(a.sentAt) || 0))[0] : undefined;
  const email = latest(history.email), text = latest(history.text?.filter(item => item.channel === "sms"));
  const label = (entry: EstimateDeliveryEntry | undefined, kind: "Email" | "Text", loaded: boolean, failed: boolean) => !entry ? failed ? `${kind} history unavailable` : loaded ? `Not ${kind === "Email" ? "emailed" : "texted"}` : `Loading ${kind.toLowerCase()} history…` : `${entry.deliveryError ? `${kind} failed` : kind === "Email" ? "Emailed" : "Texted"} ${date(entry.sentAt, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · ${entry.recipientPhone || entry.recipientEmail || "Recipient not recorded"} · ${entry.sentByName || "Sender not recorded"}`;
  return <div className="estimate-delivery-summary" aria-label="Estimate delivery status">{!onlyText && <p>{label(email, "Email", Boolean(history.email), history.email === null)}</p>}<p>{label(text, "Text", Boolean(history.text), history.text === null)}</p></div>;
}

type EstimateWorkFacts = { jobs: Record<string, JobDetail>; approvals: Record<string, EstimateApproval[]>; failedJobs: string[]; failedApprovals: string[]; retry: () => void; loading: boolean };
function useEstimateWorkFacts(items: CustomerDetail["estimates"], canReadJobs: boolean): EstimateWorkFacts {
  const [jobs, setJobs] = useState<Record<string, JobDetail>>({});
  const [approvals, setApprovals] = useState<Record<string, EstimateApproval[]>>({});
  const [failedJobs, setFailedJobs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [failedApprovals, setFailedApprovals] = useState<string[]>([]);
  const [revision, setRevision] = useState(0);
  const key = JSON.stringify(items.map(item => ({ id: item.id, jobId: item.jobId })));
  useEffect(() => {
    let canceled = false; let next = 0;
    setJobs({}); setApprovals({}); setFailedJobs([]); setFailedApprovals([]); setLoading(true);
    const records = JSON.parse(key) as Array<{ id: string; jobId?: string }>;
    const cache = new Map<string, Promise<JobDetail | undefined>>();
    const job = (id: string) => {
      if (!cache.has(id)) cache.set(id, data<JobDetail>(`/api/v1/workspace/jobs/${encodeURIComponent(id)}`).then(value => {
        if (!value.job) throw new Error("Job unavailable");
        if (!canceled) setJobs(current => ({ ...current, [id]: value })); return value;
      }).catch(() => { if (!canceled) setFailedJobs(current => [...current, id]); return undefined; }));
      return cache.get(id)!;
    };
    void Promise.all(Array.from({ length: Math.min(3, records.length) }, async () => {
      while (!canceled && next < records.length) {
        const item = records[next++];
        const [source, approvalRows] = await Promise.all([
          canReadJobs && item.jobId ? job(item.jobId) : Promise.resolve(undefined),
          data<{ items: EstimateApproval[] }>(`/api/v1/workspace/estimates/${encodeURIComponent(item.id)}/approvals`).then(result => { if (!Array.isArray(result.items)) throw new Error("Approval history unavailable"); return result.items; }).catch(() => { if (!canceled) setFailedApprovals(current => [...current, item.id]); return []; }),
        ]);
        if (canceled) return;
        setApprovals(current => ({ ...current, [item.id]: approvalRows }));
        const links = new Set([...(source?.estimates?.find(estimate => estimate.id === item.id)?.soldWork ?? []).map(work => work.jobId), ...approvalRows.flatMap(row => row.jobId ? [row.jobId] : [])]);
        if (canReadJobs) for (const id of links) { if (canceled) return; await job(id); }
      }
    })).finally(() => { if (!canceled) setLoading(false); });
    return () => { canceled = true; };
  }, [key, canReadJobs, revision]);
  return { jobs, approvals, failedJobs, failedApprovals, loading, retry: () => setRevision(value => value + 1) };
}

function estimateWorkState(item: CustomerDetail["estimates"][number], facts: EstimateWorkFacts) {
  const source = item.jobId ? facts.jobs[item.jobId] : undefined;
  const work = source?.estimates?.find(estimate => estimate.id === item.id)?.soldWork ?? [];
  const approvals = facts.approvals[item.id] ?? [];
  const sold = item.status === "accepted" || item.officeStatus === "sold" || work.length > 0 || approvals.length > 0;
  const linked = new Set([...work.map(row => row.jobId), ...approvals.flatMap(row => row.jobId ? [row.jobId] : [])]);
  const unscheduled = sold && (approvals.some(row => !row.jobId || (row.fulfillment === "later" && row.jobStatus === "new")) || [...linked].some(id => { const detail = facts.jobs[id]; return detail && !["completed", "canceled", "cancelled"].includes(detail.job.status) && !(detail.appointments ?? []).some(visit => !["canceled", "cancelled"].includes(visit.status)); }));
  return { sold, unscheduled, open: !sold && !["declined", "void", "cancelled", "canceled", "expired"].includes(item.status) };
}

function WidgetFilterButton({ children, selected, onSelect }: { children: ReactNode; selected: boolean; onSelect: () => void }) {
  const reveal = useContext(WidgetDisclosureContext);
  return <button type="button" aria-pressed={selected} className="record-filter-button" onClick={() => { onSelect(); reveal?.(); }}>{children}</button>;
}

function EstimateWorkStatusSummary({ item, facts, onOpenJob }: { item: CustomerDetail["estimates"][number]; facts: EstimateWorkFacts; onOpenJob?: (id: string) => void }) {
  const customerJob = useContext(CustomerJobContext);
  const source = item.jobId ? facts.jobs[item.jobId] : undefined;
  const work = source?.estimates?.find(estimate => estimate.id === item.id)?.soldWork ?? [];
  const approvals = facts.approvals[item.id] ?? [];
  const state = estimateWorkState(item, facts);
  if (!state.sold) return null;
  const links = new Map<string, { invoiceId?: string | null; jobStatus?: string | null; balance?: number | null; paid?: number }>();
  for (const row of work) links.set(row.jobId, { invoiceId: row.invoiceId });
  for (const row of approvals) if (row.jobId) links.set(row.jobId, { invoiceId: row.invoiceId, jobStatus: row.jobStatus, balance: row.balanceDueCents, paid: row.paidCents });
  if (!links.size && item.jobId) links.set(item.jobId, {});
  return <div className="estimate-work-status" aria-label="Sold work status">
    {state.unscheduled && <p>Sold · Unscheduled</p>}
    {work.length > 0 && <p>{[...new Set(work.map(row => row.optionLabel).filter(Boolean))].join(" · ")}</p>}
    {[...links].map(([id, link]) => {
      const detail = facts.jobs[id];
      const invoice = detail?.invoices?.find(invoice => invoice.id === link.invoiceId) ?? (detail?.invoices?.filter(invoice => invoice.estimateDisplayNumber === item.displayNumber && item.displayNumber).length === 1 ? detail.invoices.find(invoice => invoice.estimateDisplayNumber === item.displayNumber) : undefined);
      const status = detail?.job.status ?? link.jobStatus;
      const paid = invoice?.status === "paid" || (link.balance === 0 && (link.paid ?? 0) > 0);
      return <div key={id}><button type="button" className="estimate-job-link" onClick={() => customerJob ? customerJob(id) : onOpenJob?.(id)} disabled={!onOpenJob && !customerJob}>{detail?.job.jobNumber ? `Job #${detail.job.jobNumber}` : "Job number unavailable"}</button><span>{status === "completed" ? "Completed" : status ? status.replaceAll("_", " ") : "Job status unavailable"}</span><span>{paid ? "Paid" : invoice?.balanceDueCents !== undefined ? `${money(invoice.balanceDueCents)} due` : link.balance != null ? `${money(link.balance)} due` : "Payment status unavailable"}</span></div>;
    })}
  </div>;
}

function EstimateRecordWidget({ items, detail, canReadJobs, openJob, openEstimate, openInvoice, createEstimate, count, error }: { items: CustomerDetail["estimates"]; detail: CustomerDetail; canReadJobs: boolean; openJob: (id: string) => string; openEstimate?: (id: string) => void; openInvoice?: (id: string) => void; createEstimate?: () => void; count?: number | null; error?: string }) {
  const facts = useEstimateWorkFacts(items, canReadJobs);
  const [filter, setFilter] = useState<"all" | "open" | "sold" | "unscheduled">("all");
  const [query, setQuery] = useState(""); const [page, setPage] = useState(0);
  const counts = { all: items.length, open: 0, sold: 0, unscheduled: 0 };
  for (const item of items) { const state = estimateWorkState(item, facts); for (const kind of ["open", "sold", "unscheduled"] as const) if (state[kind]) counts[kind]++; }
  const matched = items.filter(item => (filter === "all" || estimateWorkState(item, facts)[filter]) && `${item.displayNumber ?? item.estimateNumber} ${item.title ?? ""}`.toLowerCase().includes(query.trim().toLowerCase()));
  const current = Math.min(page, Math.max(0, Math.ceil(matched.length / 5) - 1));
  const shown = matched.slice(current * 5, current * 5 + 5);
  const records = <LocationEstimateRecords items={shown} customerInvoices={detail.invoices} canReadJobs={canReadJobs} openJob={openJob} openEstimate={openEstimate} openInvoice={openInvoice} facts={facts} />;
  return <LocationSideWidget title="Estimates" attention={Boolean(error)} summary={<div className="record-filter-bar" aria-label="Estimate filters">{(["all", "open", "sold", "unscheduled"] as const).map(kind => <WidgetFilterButton key={kind} selected={filter === kind} onSelect={() => { setFilter(kind); setPage(0); }}>{kind === "all" ? "All" : kind[0].toUpperCase() + kind.slice(1)} ({kind === "unscheduled" && (facts.loading || facts.failedApprovals.length || facts.failedJobs.length || !canReadJobs) ? "…" : kind === "all" && count === null ? "…" : counts[kind]})</WidgetFilterButton>)}</div>} subtitle={error || (facts.loading ? "Checking linked work…" : undefined)}>
    {createEstimate && <PrimaryButton type="button" onClick={createEstimate}>New estimate</PrimaryButton>}
    <label className="block text-xs font-semibold">Search estimates<input type="search" aria-label="Search customer estimates" value={query} onChange={event => { setQuery(event.target.value); setPage(0); }} className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm" /></label>
    {(facts.failedApprovals.length > 0 || facts.failedJobs.length > 0) && <p role="status" className="text-xs text-[var(--ink-soft)]">Some linked work statuses are unavailable. <button type="button" className="underline" onClick={facts.retry}>Retry statuses</button></p>}
    {records}
    {!shown.length && <Empty text="No estimates in this view." />}
    {matched.length > 5 && <div className="mt-3 flex items-center justify-between gap-3 text-xs"><span>{current * 5 + 1}–{Math.min(current * 5 + 5, matched.length)} of {matched.length}</span><QuietButton type="button" disabled={!current} onClick={() => setPage(current - 1)}>Previous estimates</QuietButton><QuietButton type="button" disabled={(current + 1) * 5 >= matched.length} onClick={() => setPage(current + 1)}>Next estimates</QuietButton></div>}
  </LocationSideWidget>;
}

function LocationEstimateRecords({ items, customerInvoices, canReadJobs, openEstimate, openInvoice, openJob, facts }: {
  facts?: EstimateWorkFacts;
  items: CustomerDetail["estimates"];
  customerInvoices: CustomerDetail["invoices"];
  openJob: (id: string) => string;
  canReadJobs: boolean;
  openEstimate?: (id: string) => void;
  openInvoice?: (id: string) => void;
}) {
  const [loadedJobs, setJobs] = useState<Record<string, JobDetail>>({});
  const jobs = facts?.jobs ?? loadedJobs;
  const [failedJobs, setFailedJobs] = useState<string[]>([]);
  const [retry, setRetry] = useState(0);
  // Match the six rows shown by RecordList and share one request for estimates on the same job.
  const jobIdsKey = JSON.stringify([...new Set(items.slice(0, 6).flatMap(item => item.jobId ? [item.jobId] : []))].sort());
  useEffect(() => {
    setJobs({});
    setFailedJobs([]);
    if (facts || !canReadJobs) return;
    const controller = new AbortController();
    const jobIds = JSON.parse(jobIdsKey) as string[];
    void Promise.all(jobIds.map(async jobId => {
      try {
        const response = await apiFetch(`/api/v1/workspace/jobs/${encodeURIComponent(jobId)}`, { signal: controller.signal });
        if (!response.ok) throw new Error("Unable to read invoice connection");
        const payload = await response.json() as { data: JobDetail };
        if (!controller.signal.aborted) setJobs(current => ({ ...current, [jobId]: payload.data }));
      } catch {
        if (!controller.signal.aborted) setFailedJobs(current => [...current, jobId]);
      }
    }));
    return () => controller.abort();
  }, [jobIdsKey, canReadJobs, retry, Boolean(facts)]);
  return <RecordList items={items} label="Estimate" onOpen={openEstimate} inlineDetails renderDetails={id => {
    const estimate = items.find(item => item.id === id)!;
    const detail = estimate.jobId ? jobs[estimate.jobId] : undefined;
    const soldWork = detail?.estimates.find(item => item.id === id)?.soldWork ?? [];
    const invoiceLinks = new Map<string, { id: string; displayNumber: string; status: string; completed: boolean; direct: boolean; balanceDueCents?: number; jobId?: string }>(soldWork.map(work => [work.invoiceId, {
      id: work.invoiceId, displayNumber: work.invoiceDisplayNumber, status: work.invoiceStatus,
      completed: jobs[work.jobId]?.job.status === "completed", direct: true,
      balanceDueCents: work.invoiceBalanceDueCents, jobId: work.jobId,
    }]));
    for (const invoice of customerInvoices) {
      if (!estimate.displayNumber || invoice.estimateDisplayNumber !== estimate.displayNumber
        || items.filter(item => item.displayNumber === estimate.displayNumber).length !== 1) continue;
      invoiceLinks.set(invoice.id, { id: invoice.id, displayNumber: invoice.displayNumber ?? "", status: invoice.status,
        completed: invoice.jobStatus === "completed", direct: true, balanceDueCents: invoice.balanceDueCents, jobId: invoice.jobId });
    }
    for (const approval of facts?.approvals[id] ?? []) {
      if (!approval.invoiceId || invoiceLinks.has(approval.invoiceId)) continue;
      const invoice = customerInvoices.find(item => item.id === approval.invoiceId) ?? (approval.jobId ? jobs[approval.jobId]?.invoices?.find(item => item.id === approval.invoiceId) : undefined);
      invoiceLinks.set(approval.invoiceId, { id: approval.invoiceId, displayNumber: invoice?.displayNumber ?? "", status: invoice?.status ?? (approval.invoiceTotalCents && (approval.paidCents ?? 0) >= approval.invoiceTotalCents ? "paid" : "open"), completed: approval.jobStatus === "completed", direct: true, balanceDueCents: invoice?.balanceDueCents ?? approval.balanceDueCents ?? undefined, jobId: approval.jobId ?? undefined });
    }
    const hasDirectInvoice = invoiceLinks.size > 0;
    for (const invoice of detail?.invoices ?? []) {
      if (invoiceLinks.has(invoice.id)) continue;
      const direct = Boolean(estimate.displayNumber && invoice.estimateDisplayNumber === estimate.displayNumber
        && detail?.estimates.filter(item => item.displayNumber === estimate.displayNumber).length === 1);
      if (hasDirectInvoice && !direct) continue;
      // An invoice explicitly assigned to another estimate is not this estimate's sale.
      if (invoice.estimateDisplayNumber && !direct) continue;
      invoiceLinks.set(invoice.id, { id: invoice.id, displayNumber: invoice.displayNumber ?? "", status: invoice.status,
        completed: detail?.job.status === "completed", direct, balanceDueCents: invoice.balanceDueCents, jobId: detail?.job.id });
    }
    const sold = facts ? estimateWorkState(estimate, facts).sold : estimate.status === "accepted" || estimate.officeStatus === "sold" || soldWork.length > 0;
    const failed = Boolean(estimate.jobId && (facts?.failedJobs ?? failedJobs).includes(estimate.jobId));
    return <div className="location-estimate-connections" aria-label={`Connections for ${customerDocumentLabel("Estimate", estimate)}`}>
      {facts?.jobs[estimate.jobId ?? ""]?.estimates?.find(item => item.id === id)?.approvedOptionLabels && <span className="estimate-sold-description">{facts.jobs[estimate.jobId!].estimates.find(item => item.id === id)?.approvedOptionLabels}</span>}
      {facts && estimateWorkState(estimate, facts).unscheduled && <span className="font-semibold text-amber-800">Unscheduled</span>}
      {sold && <span className="mr-2 font-semibold text-[var(--good)]">Sold</span>}
      {canReadJobs && estimate.jobId && !detail && !failed && !invoiceLinks.size && <span role="status">Loading invoice connection…</span>}
      {failed && <span>Invoice connection unavailable. <button type="button" className="underline" onClick={() => facts ? facts.retry() : setRetry(value => value + 1)}>Retry connection</button></span>}
      {[...invoiceLinks.values()].map(invoice => <span key={invoice.id} className="location-invoice-connection">
        {invoice.jobId && !invoice.completed && jobs[invoice.jobId]?.job.status && <span className="capitalize">Job {jobs[invoice.jobId].job.status.replaceAll("_", " ")} ·</span>}
        {invoice.completed && <span className="font-semibold text-[var(--good)]">Job completed ·</span>}
        <span>{invoice.direct ? "Invoice" : "Job invoice"}</span>
        {openInvoice ? <button type="button" className="min-h-11 font-semibold text-[var(--accent)] underline underline-offset-2" onClick={() => openInvoice(invoice.id)} aria-label={`Open invoice ${invoice.displayNumber || "document"}`}>{invoice.displayNumber ? `#${invoice.displayNumber}` : "Open invoice"}</button> : <span>{invoice.displayNumber ? `#${invoice.displayNumber}` : "Number unavailable"}</span>}
        <span className="location-payment-state" data-paid={invoice.status === "paid"}>{invoice.status === "paid" ? "Paid" : invoice.status.replaceAll("_", " ")}</span>
        {(invoice.balanceDueCents !== undefined || invoice.status === "paid") && <span className="location-invoice-balance">Balance {money(invoice.balanceDueCents ?? 0)}</span>}
        {invoice.jobId && <CustomerJobLink jobId={invoice.jobId} href={openJob(invoice.jobId)} className="location-job-details-link">{jobs[invoice.jobId]?.job.jobNumber ? `Job #${jobs[invoice.jobId].job.jobNumber}` : "Job details"}</CustomerJobLink>}
      </span>)}
      {facts && !invoiceLinks.size && <EstimateWorkStatusSummary item={estimate} facts={facts} onOpenJob={jobId => { window.location.assign(openJob(jobId)); }} />}
      <EstimateDeliverySummary estimateId={id} />
    </div>;
  }} />;
}

function EquipmentWidget({ equipment }: { equipment: CustomerDetail["equipment"] }) {
  return (
        <LocationSideWidget title="Equipment" count={equipment.length} countLabel="equipment records" preview={<div className="location-equipment-preview">{equipment.length ? equipment.slice(0, 2).map(unit => <WidgetPreviewButton key={unit.id}><strong>{unit.name || unit.manufacturer || "Equipment"}</strong>{unit.manufacturer && <span>{unit.manufacturer}</span>}<span>Model: {unit.modelNumber || "Not recorded"}</span><span>Serial number: {unit.serialNumber || "Not recorded"}</span></WidgetPreviewButton>) : <p>No equipment on file</p>}</div>}>
            <div className="space-y-3">
              {equipment.map((unit) => (
                <div
                  key={unit.id}
                  className="border-b border-[var(--line)] pb-3 last:border-0 last:pb-0"
                >
                  <p className="text-sm font-semibold text-[var(--ink)]">
                    {unit.name || unit.manufacturer || "Equipment"}
                  </p>
                  <p className="mt-1 text-xs text-[var(--ink-soft)]">
                    {unit.manufacturer || "Manufacturer not recorded"}
                  </p>
                  <p className="mt-1 text-xs text-[var(--ink-soft)]">Model: {unit.modelNumber || "Not recorded"}</p>
                  <p className="mt-1 text-xs text-[var(--ink-soft)]">Serial number: {unit.serialNumber || "Not recorded"}</p>
                  {unit.installDate && (
                    <p className="mt-1 text-xs text-[var(--ink-faint)]">
                      Installed {equipmentInstallDateLabel(unit.installDate)}
                    </p>
                  )}
                </div>
              ))}
              {!equipment.length && (
                <Empty text="Nothing on file at this address." />
              )}
            </div>
        </LocationSideWidget>
  );
}

function CustomerPhotosWidget({ customerId, openJob }: { customerId: string; openJob: (id: string) => string }) {
  const { photos, loading, error } = usePhotos(`/api/v1/workspace/customers/${customerId}/photos`);
  return <LocationSideWidget title="Photos" count={loading || error ? null : photos.length} countLabel="photos across service locations" subtitle={error || "Photos from work on this account"} attention={Boolean(error)} preview={!loading && !error && photos.length ? <div className="location-photo-preview"><PhotoGrid photos={photos.slice(0, 4)} openJob={openJob} /></div> : undefined}>
    {loading ? <p role="status">Loading photos…</p> : error ? <p role="alert">{error}</p> : photos.length ? <PhotoGrid photos={photos} openJob={openJob} /> : <Empty text="No photos on this account yet." />}
  </LocationSideWidget>;
}

function CustomerMembershipWidget({ detail, today, canWrite, canBookJobs, bookJob, act, tags, openJob }: { detail: CustomerDetail; today: string; canWrite: boolean; canBookJobs: boolean; bookJob: (customer: BookingCustomer) => void; act: CustomerAction; tags: WorkspaceTag[]; openJob: (id: string) => string }) {
  const current = detail.memberships.filter(item => isCurrentOrUpcomingMembership(item, today));
  const past = detail.memberships.filter(item => !isCurrentOrUpcomingMembership(item, today));
  const primary = current[0] ?? past[0];
  const single = current.length <= 1;
  const renewalCount = useMembershipRenewalCount(single ? detail.memberships : [], primary?.id);
  const property = detail.properties.find(item => item.id === primary?.propertyId) ?? (detail.properties.length === 1 ? detail.properties[0] : undefined);
  const customer = { id: detail.customer.id, firstName: detail.customer.firstName, lastName: detail.customer.lastName, email: detail.customer.email };
  const row = (membership: CustomerDetail["memberships"][number], compact = false) => {
    const location = detail.properties.find(item => item.id === membership.propertyId) ?? (detail.properties.length === 1 ? detail.properties[0] : undefined);
    return <MembershipRow key={membership.id} customerId={customer.id} membership={membership} today={today} showEquipment compact={compact} upcoming={Boolean(membership.startDate && membership.startDate.slice(0, 10) > today)} propertyId={location?.id} renewalLocation={location ? { id: location.id, address: streetAddress(location), customerName: detail.customer.displayName || `${customer.firstName} ${customer.lastName}` } : undefined} bookingCustomer={customer} canBookJobs={canBookJobs && location?.active !== 0} bookJob={bookJob} canWrite={canWrite} act={act} tags={tags} openJob={openJob} />;
  };
  return <LocationSideWidget title="Memberships" heading={single && primary ? primary.membershipTypeName || primary.recurrence || "Memberships" : "Memberships"} count={single && primary ? undefined : current.length} countLabel="current plans" renewalCount={single && primary ? renewalCount : undefined} subtitle={single && primary ? `${primary.startDate ? membershipTermDate(primary.startDate) : "Start not recorded"} through ${primary.endDate ? membershipTermDate(primary.endDate) : "End not recorded"}` : "Memberships across service locations"} badge={single && primary ? <MembershipPrice membership={primary} /> : undefined} preview={!single ? <div className="location-record-preview">{current.slice(0, 2).map(item => <WidgetPreviewButton key={item.id}>{item.membershipTypeName || item.recurrence || "Membership"}<span>{item.priceCents == null ? "Price not recorded" : money(item.priceCents)}</span></WidgetPreviewButton>)}</div> : undefined} action={single && primary && property ? <LocationMembershipBooking membership={primary} customer={customer} propertyId={property.id} address={streetAddress(property)} canWrite={canWrite} canBookJobs={canBookJobs && property.active !== 0 && current.includes(primary) && (!primary.startDate || primary.startDate.slice(0, 10) <= today)} bookJob={bookJob} /> : undefined}>
    <div className="space-y-4">{current.map(item => row(item, single))}{!current.length && <p className="text-sm text-[var(--ink-soft)]">No current membership.</p>}{past.length > 0 && <MembershipHistoryGroup count={past.length}>{past.map(item => <MembershipHistoryDisclosure key={item.id} membership={item} today={today}>{row(item)}</MembershipHistoryDisclosure>)}</MembershipHistoryGroup>}</div>
  </LocationSideWidget>;
}

const LOCATION_WIDGET_ICONS = { Billing: UserRound, "Service location": MapPin, "Service locations": MapPin, Invoices: FilePlus2, Memberships: ShieldCheck, Equipment: Wrench, Estimates: FilePlus2, Jobs: CalendarDays, Photos: Images, Documents: FolderOpen };

function LocationSideWidget({ title, heading = title, renewalCount, expanded, onCollapse, count, countLabel = "records", subtitle, preview, action, badge, summary, attention = false, children }: {
  title: keyof typeof LOCATION_WIDGET_ICONS; heading?: string; renewalCount?: number | null; expanded?: boolean; onCollapse?: () => void; count?: number | null; countLabel?: string;
  subtitle?: string; preview?: ReactNode; action?: ReactNode; badge?: ReactNode; summary?: ReactNode;
  attention?: boolean; children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const focus = useContext(WidgetFocusContext);
  const widgetRef = useRef<HTMLElement>(null);
  const activate = focus?.activate;
  const frontline = open && focus?.frontline === title;
  useEffect(() => { if (open) activate?.(title); }, [open, title, activate]);
  useLayoutEffect(() => { if (frontline) widgetRef.current?.scrollIntoView?.({ block: "start", behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" }); }, [frontline]);
  useEffect(() => { if (expanded) setOpen(true); }, [expanded]);
  const contentId = useId();
  const Icon = LOCATION_WIDGET_ICONS[title];
  const reveal = () => { activate?.(title); setOpen(true); };
  const toggle = () => { if (open) { onCollapse?.(); setOpen(false); } else reveal(); };
  return <WidgetDisclosureContext.Provider value={reveal}><section ref={widgetRef} className="location-side-widget" data-frontline={Boolean(frontline)} data-open={open} data-kind={title.toLowerCase()} aria-label={`${title} widget`}>
    <button type="button" className="location-widget-toggle" aria-label={`${open ? "Collapse" : "Expand"} ${title.toLowerCase()}`} aria-expanded={open} aria-controls={contentId} onClick={toggle}>
      <span className="location-widget-icon"><Icon size={21} strokeWidth={1.7} aria-hidden="true" /></span>
      <strong>{heading}</strong>{renewalCount !== undefined && <span className="membership-renewal-count" title="Recorded renewals" aria-label={renewalCount === null ? "Renewal count unavailable" : `${renewalCount} recorded renewals`}>{renewalCount ?? "—"}</span>}<ChevronDown size={18} aria-hidden="true" />
    </button>
    <div className="location-widget-summary">
      {count !== undefined && <button type="button" className="location-widget-count" aria-label={`Open ${title.toLowerCase()}`} aria-expanded={open} aria-controls={contentId} onClick={reveal}><strong>{count === null ? "—" : count}</strong><span>{count === null ? (attention ? "Count unavailable" : "Loading records…") : countLabel}</span></button>}
      {badge}
      {summary}
      {subtitle && <p className="location-widget-dates" data-attention={attention}>{subtitle}</p>}
      {!open && preview}
      {!open && action}
    </div>
    <button type="button" className="location-widget-details" aria-label={`${open ? "Hide details" : "View details"} for ${title.toLowerCase()}`} aria-expanded={open} aria-controls={contentId} onClick={toggle}>{open ? "Hide details" : "View details"}<ArrowRight size={15} aria-hidden="true" /><span className="sr-only"> for {title.toLowerCase()}</span></button>
    <div id={contentId} hidden={!open} className="location-widget-content">{open && children}</div>
  </section></WidgetDisclosureContext.Provider>;
}

function membershipTermDate(value: string) {
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${value.slice(0, 10)}T00:00:00Z`));
}

function MembershipPrice({ membership }: { membership: CustomerDetail["memberships"][number] }) {
  return <p className="membership-widget-price">{membership.priceCents == null ? "Price not recorded" : money(membership.priceCents)}{membership.priceCents != null && membership.recurrence && <span> / {membership.recurrence}</span>}</p>;
}

function LocationMembershipBooking({ membership, customer, propertyId, bookJob, canBookJobs, canWrite, address }: { membership: CustomerDetail["memberships"][number]; customer: BookingCustomer; propertyId: string; bookJob: (customer: BookingCustomer) => void; canBookJobs: boolean; canWrite: boolean; address: string }) {
  const [loaded, setLoaded] = useState<MembershipDetail | null>(null);
  const [renewing, setRenewing] = useState(false);
  useEffect(() => {
    let canceled = false;
    void loadMembershipDetail(membership.id).then(value => { if (!canceled) setLoaded(value); }).catch(() => {});
    return () => { canceled = true; };
  }, [membership.id]);
  return <div className="membership-widget-actions">
    {canBookJobs && loaded && membershipHasBookableService(loaded) && <button type="button" className="location-widget-book" onClick={() => bookJob({ ...customer, preferredPropertyId: propertyId, bookingNotes: membershipBookingNotes(membership, loaded) })}>Book remaining service</button>}
    {canWrite && !membership.renewedIntoId && <button type="button" className="location-widget-book membership-renew-button" onClick={() => setRenewing(true)}>Renew</button>}
    {renewing && createPortal(<MembershipRenewalProposalDialog location={{ id: propertyId, address, customerName: `${customer.firstName} ${customer.lastName}` }} membershipId={membership.id} currentEndDate={membership.endDate ?? null} onClose={() => setRenewing(false)} openInvoice={id => window.location.assign(`/dashboard?invoice=${encodeURIComponent(id)}&collectPayment=true`)} />, document.body)}
  </div>;
}

// Older customer payloads omit lifecycle links. Resolve them through existing
// read-only membership details, with at most three requests in flight.
function useMembershipRenewalCount(records: CustomerDetail["memberships"], currentId?: string) {
  const key = JSON.stringify(records.map(item => ({ id: item.id, renewedIntoId: item.renewedIntoId })));
  const [result, setResult] = useState<{ key: string; currentId?: string; count: number | null } | null>(null);
  useEffect(() => {
    let canceled = false;
    const links = JSON.parse(key) as Array<{ id: string; renewedIntoId?: string | null }>;
    let next = 0;
    void (async () => {
      try {
        await Promise.all(Array.from({ length: Math.min(3, links.length) }, async () => {
          while (!canceled && next < links.length) {
            const item = links[next++];
            if (item.renewedIntoId !== undefined) continue;
            const loaded = await loadMembershipDetail(item.id);
            if (!loaded) throw new Error("Membership lifecycle unavailable");
            item.renewedIntoId = loaded.renewedIntoId ?? null;
          }
        }));
        if (canceled) return;
        const seen = new Set<string>();
        let id = currentId;
        let count = 0;
        while (id && !seen.has(id)) {
          seen.add(id);
          const predecessors = links.filter(item => item.renewedIntoId === id && !seen.has(item.id));
          if (predecessors.length !== 1) break;
          count++; id = predecessors[0].id;
        }
        setResult({ key, currentId, count });
      } catch { if (!canceled) setResult({ key, currentId, count: null }); }
    })();
    return () => { canceled = true; };
  }, [key, currentId]);
  return result?.key === key && result.currentId === currentId ? result.count : null;
}

export function LocationWorkspace({
  detail,
  propertyId,
  openEstimate,
  openInvoice,
  priceBookItems = [],
  priceBookCategories = [],
  canCreateEstimate = false,
  canReadJobs = false,
  canReadCommercial = true,
  timezone,
  openJob,
  onOpenJob,
  canBookJobs,
  bookJob,
  back,
  canWrite,
  canManageLocation,
  act,
  tags,
}: {
  detail: CustomerDetail;
  propertyId: string;
  openEstimate?: (id: string) => void;
  openInvoice?: (id: string) => void;
  priceBookItems?: PriceBookItem[];
  priceBookCategories?: PriceBookCategory[];
  canCreateEstimate?: boolean;
  canReadJobs?: boolean;
  canReadCommercial?: boolean;
  timezone: string;
  onOpenJob?: (id: string) => void;
  openJob: (id: string) => string;
  canBookJobs: boolean;
  bookJob: (customer: BookingCustomer) => void;
  back: () => void;
  canWrite: boolean;
  canManageLocation: boolean;
  act: CustomerAction;
  tags: WorkspaceTag[];
}) {
  const [locationSettingsOpen, setLocationSettingsOpen] = useState(false);
  const [locationStatusBusy, setLocationStatusBusy] = useState(false);
  const [locationStatusError, setLocationStatusError] = useState("");
  useEffect(() => { setLocationSettingsOpen(false); setLocationStatusError(""); }, [propertyId]);
  const [locationEstimates, setLocationEstimates] = useState<CustomerDetail["estimates"] | null>(null);
  const [estimateError, setEstimateError] = useState("");
  const [creatingLocationEstimate, setCreatingLocationEstimate] = useState(false);
  const reloadLocationEstimates = useCallback(async () => {
    const result = await data<{ estimates: CustomerDetail["estimates"] }>(`/api/v1/workspace/properties/${propertyId}`);
    if (!Array.isArray(result.estimates)) throw new Error("Missing location estimates");
    setLocationEstimates(result.estimates);
  }, [propertyId]);
  useEffect(() => {
    let cancelled = false;
    setLocationEstimates(null); setEstimateError(""); setCreatingLocationEstimate(false);
    if (canReadCommercial) void data<{ estimates: CustomerDetail["estimates"] }>(`/api/v1/workspace/properties/${propertyId}`)
      .then((result) => { if (!Array.isArray(result.estimates)) throw new Error("Missing location estimates"); if (!cancelled) setLocationEstimates(result.estimates); })
      .catch(() => { if (!cancelled) setEstimateError("Unable to load all estimates at this address."); });
    return () => { cancelled = true; };
  }, [propertyId, canReadCommercial]);
  const property = detail.properties.find((entry) => entry.id === propertyId);
  const { photos, loading, error } = usePhotos(
    `/api/v1/workspace/customers/${detail.customer.id}/photos`,
  );
  const [locationContacts, setLocationContacts] = useState<WorkspaceContact[]>(
    [],
  );
  useEffect(() => {
    let cancelled = false;
    void data<{ items: WorkspaceContact[] }>(
      `/api/v1/workspace/properties/${propertyId}/contacts`,
    )
      .then(({ items }) => {
        if (!cancelled) setLocationContacts(items);
      })
      .catch(() => {
        if (!cancelled) setLocationContacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, detail.customer.id]);
  const scopedMemberships = detail.memberships.filter(item => item.propertyId === propertyId || (!item.propertyId && detail.properties.length === 1));
  const currentTerm = scopedMemberships.find(item => isCurrentOrUpcomingMembership(item, new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()))) ?? scopedMemberships[0];
  const renewalCount = useMembershipRenewalCount(canReadCommercial ? scopedMemberships : [], currentTerm?.id);
  const customerName =
    detail.customer.displayName ||
    `${detail.customer.firstName} ${detail.customer.lastName}`;
  if (!property)
    return (
      <>
        <button
          onClick={back}
          className="text-sm font-semibold text-[var(--ink)]"
        >
          ← Back to {customerName}
        </button>
        <Empty text="That address is no longer on this customer." />
      </>
    );
  const address = streetAddress(property);
  const estimates = locationEstimates ?? detail.estimates.filter(item => item.propertyId === propertyId);
  const taken = photos.filter((photo) => photo.propertyId === propertyId);
  const hereEquipment = detail.equipment.filter(
    (item) =>
      item.propertyId === propertyId ||
      (!item.propertyId && detail.properties.length === 1),
  );
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const locationMemberships = detail.memberships.filter(item => item.propertyId === propertyId || (!item.propertyId && detail.properties.length === 1));
  const hereMemberships = locationMemberships.filter(item => isCurrentOrUpcomingMembership(item, today));
  const pastMemberships = locationMemberships.filter(item => !isCurrentOrUpcomingMembership(item, today));
  const membershipStatus = locationMembershipStatus(locationMemberships, today);
  const locationPeople = groupBookingContacts(
    locationContacts.map((contact) => contactAsBookingContact(contact, "location")),
  );
  const locationIdentity = (
      <header className="location-widget-identity" role="region" aria-label="Service location">
        <div className="location-identity-heading">
          <h1>{property.name?.trim() || address}</h1>
          {canManageLocation && typeof property.active === "number" && <div className="relative">
            <button type="button" aria-label="Location settings" aria-expanded={locationSettingsOpen} aria-controls="location-settings" onClick={() => { setLocationSettingsOpen(value => !value); setLocationStatusError(""); }} className="grid min-h-11 min-w-11 place-items-center rounded-lg border border-[var(--line)] text-[var(--ink-soft)]"><Settings size={18} aria-hidden="true" /></button>
            {locationSettingsOpen && <div id="location-settings" className="absolute right-0 z-10 mt-2 w-64 rounded-xl border border-[var(--line)] bg-[var(--paper)] p-3 shadow-lg" onKeyDown={event => { if (event.key === "Escape") { setLocationSettingsOpen(false); event.stopPropagation(); } }}>
              <p className="text-sm text-[var(--ink-soft)]">{property.active === 0 ? "Inactive location" : "Active location"}</p>
              <button type="button" disabled={locationStatusBusy} className="mt-2 min-h-11 text-sm font-semibold text-[var(--accent)]" onClick={async () => { setLocationStatusBusy(true); setLocationStatusError(""); try { await act(detail.customer.id, `/api/v1/workspace/properties/${property.id}/status`, "PATCH", { active: property.active === 0 }); setLocationSettingsOpen(false); } catch { setLocationStatusError("Location status could not be saved. Please try again."); } finally { setLocationStatusBusy(false); } }}>{locationStatusBusy ? "Saving…" : property.active === 0 ? "Reactivate location" : "Mark inactive"}</button>
              {locationStatusError && <p role="alert" className="mt-2 text-sm text-[var(--st-conflict)]">{locationStatusError}</p>}
            </div>}
          </div>}
        </div>
        {property.name?.trim() && <p className="location-street">{address}</p>}
        {canReadCommercial && <div className="mt-2"><MembershipStatusChip label={membershipStatus.label} tone={locationMembershipTone(membershipStatus.state)} ariaLabel="Membership status" /></div>}
        {(property.tags?.length || canWrite) ? <section aria-label="Location tags" className="location-record-tags">
          <p className="text-xs font-semibold text-[var(--ink-soft)]">Location tags</p>
          <TagEditor entityPath={`/api/v1/workspace/properties/${property.id}/tags`} assigned={property.tags ?? []} catalog={tags} canWrite={canWrite} act={act} customerId={detail.customer.id} />
        </section> : null}
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex min-h-11 items-center text-sm font-semibold text-[var(--accent)]"
          >
            Map it
          </a>
          <div className="customer-contact-widget-body location-widget-contact-details" role="region" aria-label="Location contacts">
        {canWrite ? (
          <OwnedContacts
            customerName="Location contact"
            canEdit
            sections={[{
              owner: "property",
              ownerId: propertyId,
              title: "Location contact",
              records: locationContacts,
            }]}
            saveContact={async (_owner, ownerId, contact, contactId) => {
              await act(
                detail.customer.id,
                `/api/v1/workspace/properties/${ownerId}/contacts${contactId ? `/${contactId}` : ""}`,
                contactId ? "PATCH" : "POST",
                contact,
              );
              const next = await data<{ items: WorkspaceContact[] }>(
                `/api/v1/workspace/properties/${propertyId}/contacts`,
              );
              setLocationContacts(next.items);
            }}
            deleteContact={async (_owner, ownerId, contactId) => {
              await act(
                detail.customer.id,
                `/api/v1/workspace/properties/${ownerId}/contacts/${contactId}`,
                "DELETE",
                {},
              );
              const next = await data<{ items: WorkspaceContact[] }>(
                `/api/v1/workspace/properties/${propertyId}/contacts`,
              );
              setLocationContacts(next.items);
            }}
          />
        ) : (
          <CustomerContactOverview
            compact
            showAll
            customerId={detail.customer.id}
            customerName="Location contact"
            billingPeople={locationPeople}
            label="Location contact"
            countLabel="location contact"
            emptyText="No contact on file at this address."
          />
        )}
          </div>
      </header>
  );
  return (
    <CustomerJobContext.Provider value={onOpenJob}>
      <button type="button" onClick={back} className="location-account-link location-account-back">
        Customer account · {customerName}<ArrowLeft size={16} aria-hidden="true" />
      </button>
      <RecordWidgetGrid key={propertyId} as="aside" ariaLabel="Location widgets">
        <LocationSideWidget title="Service location" preview={locationIdentity}>{locationIdentity}</LocationSideWidget>
        {canReadCommercial && <LocationSideWidget title="Memberships" heading={currentTerm?.membershipTypeName || currentTerm?.recurrence || "Memberships"} renewalCount={currentTerm ? renewalCount : undefined} subtitle={currentTerm ? `${currentTerm.startDate ? membershipTermDate(currentTerm.startDate) : "Start not recorded"} through ${currentTerm.endDate ? membershipTermDate(currentTerm.endDate) : "End not recorded"}` : undefined} badge={currentTerm ? <MembershipPrice membership={currentTerm} /> : <MembershipStatusChip label={membershipStatus.label} tone={locationMembershipTone(membershipStatus.state)} />} action={currentTerm && <LocationMembershipBooking key={currentTerm.id} membership={currentTerm} customer={{ id: detail.customer.id, firstName: detail.customer.firstName, lastName: detail.customer.lastName, email: detail.customer.email }} propertyId={propertyId} bookJob={bookJob} canBookJobs={canBookJobs && hereMemberships.includes(currentTerm) && (!currentTerm.startDate || currentTerm.startDate.slice(0, 10) <= today)} canWrite={canWrite} address={address} />}>
          {hereMemberships.length > 0 && (
            <section className="location-current-memberships">
              <div className="space-y-3">
                {hereMemberships.map((membership) => (
                  <MembershipRow
                    key={membership.id}
                    customerId={detail.customer.id}
                    membership={membership}
                    today={today}
                    upcoming={Boolean(membership.startDate && membership.startDate.slice(0, 10) > today)}
                    showEquipment
                    compact={hereMemberships.length === 1}
                    propertyId={propertyId}
                    renewalLocation={{ id: propertyId, address, customerName }}
                    bookingCustomer={{
                      id: detail.customer.id,
                      firstName: detail.customer.firstName,
                      lastName: detail.customer.lastName,
                      email: detail.customer.email,
                    }}
                    canBookJobs={canBookJobs}
                    bookJob={bookJob}
                    canWrite={canWrite}
                    act={act}
                    tags={tags}
                    openJob={openJob}
                  />
                ))}
              </div>
            </section>
          )}
        {hereMemberships.length === 0 && <p className="text-sm text-[var(--ink-soft)]">{membershipStatus.label}</p>}
        {pastMemberships.length > 0 && <section className="location-membership-history" aria-label="Past memberships">
          <MembershipHistoryGroup count={pastMemberships.length}>
          {pastMemberships.map(membership => <MembershipHistoryDisclosure key={membership.id} membership={membership} today={today}>
                  <MembershipRow
                    customerId={detail.customer.id}
                    membership={membership}
                    today={today}
                    upcoming={false}
                    showEquipment
                    propertyId={propertyId}
                    renewalLocation={{ id: propertyId, address, customerName }}
                    bookingCustomer={{
                      id: detail.customer.id,
                      firstName: detail.customer.firstName,
                      lastName: detail.customer.lastName,
                      email: detail.customer.email,
                    }}
                    canBookJobs={canBookJobs}
                    bookJob={bookJob}
                    canWrite={canWrite}
                    act={act}
                    tags={tags}
                    openJob={openJob}
                  />
          </MembershipHistoryDisclosure>)}
          </MembershipHistoryGroup>
        </section>}

        </LocationSideWidget>}
        <EquipmentWidget equipment={hereEquipment} />
        {canReadCommercial && <EstimateRecordWidget items={estimates} detail={detail} canReadJobs={canReadJobs} openJob={openJob} openEstimate={openEstimate} openInvoice={openInvoice} createEstimate={canCreateEstimate ? () => setCreatingLocationEstimate(true) : undefined} count={locationEstimates === null ? null : estimates.length} error={estimateError} />}
        {creatingLocationEstimate && canCreateEstimate && <OfficeEstimateComposer owner={{ customerId: detail.customer.id, propertyId }} priceBookItems={priceBookItems} priceBookCategories={priceBookCategories} onClose={() => setCreatingLocationEstimate(false)} onSaved={() => { setCreatingLocationEstimate(false); void reloadLocationEstimates().catch(() => setEstimateError("Estimate saved, but the location list could not be refreshed.")); }} />}
        <LocationJobs key={propertyId} detail={detail} propertyId={propertyId} timezone={timezone} openJob={openJob} canReadJobs={canReadJobs} />
        <LocationSideWidget title="Photos" count={loading || error ? null : taken.length} countLabel="photos at this address" subtitle={error || (taken.length ? "Photos from work at this location" : "Build a visual history of this location.")} attention={Boolean(error)} preview={!loading && !error && taken.length ? <div className="location-photo-preview"><PhotoGrid photos={taken.slice(0, 4)} openJob={openJob} /></div> : undefined}>
          <Panel title={`Photos (${taken.length})`}>{loading ? <p role="status">Loading photos…</p> : error ? <p role="alert">{error}</p> : taken.length ? <PhotoGrid photos={taken} openJob={openJob} /> : <Empty text="Nothing has been photographed here yet." />}</Panel>
        </LocationSideWidget>
        <DocumentsPanel key={`documents-${propertyId}`} title="Documents" path={`/api/v1/workspace/properties/${propertyId}/attachments`} canWrite={canWrite} locationWidget />
      </RecordWidgetGrid>
    </CustomerJobContext.Provider>
  );
}

function ServiceLocations({
  compact = false,
  customerId,
  memberships,
  today,
  properties,
  jobs,
  jobsTotal,
  equipment,
  openLocation,
  canWrite,
  act,
}: {
  compact?: boolean;
  customerId: string;
  memberships?: CustomerDetail["memberships"];
  today: string;
  properties: CustomerDetail["properties"];
  jobs: CustomerDetail["jobs"];
  jobsTotal?: number;
  equipment: CustomerDetail["equipment"];
  openLocation: (propertyId: string) => void;
  canWrite: boolean;
  act: CustomerAction;
}) {
  /**
   * Without a jobsTotal the API used to return the 25 most recent jobs for the
   * whole customer, so on an account with many addresses most locations had none
   * of those 25. Saying "No jobs" there was false. Prefer the per-location count
   * when the payload includes it; otherwise stay quiet once the page is full.
   */
  const jobsAreCapped =
    typeof jobsTotal === "number"
      ? jobs.length < jobsTotal
      : jobs.length >= 25;
  const { photos, loading, error } = usePhotos(
    `/api/v1/workspace/customers/${customerId}/photos`,
  );
  const [query, setQuery] = useState("");
  const [locationPage, setLocationPage] = useState(0);
  const LOCATION_PAGE_SIZE = 5;
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [alreadyOnFile, setAlreadyOnFile] = useState<BookingCustomer[]>([]);
  const addLocation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    try {
      await act(
        customerId,
        `/api/v1/workspace/customers/${customerId}/properties`,
        "POST",
        {
          addressLine1: String(form.get("addressLine1")).trim(),
          addressLine2: String(form.get("addressLine2") || "").trim() || null,
          city: String(form.get("city")).trim(),
          state: String(form.get("state")).trim(),
          postalCode: String(form.get("postalCode")).trim(),
          propertyType: String(form.get("propertyType")),
          ...optionalCoords(form),
        },
      );
      setAdding(false);
    } catch {
      /* act put the message on the page */
    } finally {
      setBusy(false);
    }
  };

  const matched = query.trim()
    ? properties.filter((property) =>
        `${property.name ?? ""} ${property.addressLine1} ${property.addressLine2 ?? ""} ${property.city} ${property.state} ${property.postalCode ?? ""}`
          .toLowerCase()
          .includes(query.trim().toLowerCase()),
      )
    : properties;
  const locationPageCount = Math.max(
    1,
    Math.ceil(matched.length / LOCATION_PAGE_SIZE),
  );
  const locationPageIndex = Math.min(locationPage, locationPageCount - 1);
  const locationStart = locationPageIndex * LOCATION_PAGE_SIZE;
  const shown = matched.slice(
    locationStart,
    locationStart + LOCATION_PAGE_SIZE,
  );
  const photosFor = (propertyId: string) =>
    photos.filter((photo) => photo.propertyId === propertyId);
  const jobsFor = (propertyId: string) =>
    jobs.filter((job) => job.propertyId === propertyId);

  return (
    <section className={compact ? "customer-location-list" : "rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {!compact && <h2 className="text-sm font-semibold text-[var(--ink)]">
          Service locations ({properties.length})
        </h2>}
        <div className="flex items-center gap-3">
          {properties.length > 5 && (
            <input
              type="search"
              aria-label="Search service locations"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setLocationPage(0);
              }}
              placeholder="Street or ZIP"
              className="w-56 rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-1.5 text-sm outline-none focus:border-[var(--line-strong)]"
            />
          )}
          {canWrite && (
            <button
              type="button"
              onClick={() => {
                setAdding(!adding);
                setAlreadyOnFile([]);
              }}
              className="text-sm font-semibold text-[var(--accent)]"
            >
              {adding ? "Cancel" : "+ Location"}
            </button>
          )}
        </div>
      </div>

      {adding && (
        <form
          onSubmit={(event) => void addLocation(event)}
          className="mt-4 grid gap-2 border border-[var(--line)] bg-[var(--wash)] p-4 sm:grid-cols-2"
        >
          <div className="sm:col-span-2 space-y-2">
            <AddressFields
              enableLookup
              searchExisting={(query) =>
                data<{ items: BookingCustomer[] }>(
                  `/api/v1/workspace/dispatch/customers?q=${encodeURIComponent(query)}`,
                ).then((payload) => payload.items.filter((row) => row.id !== customerId))
              }
              onExistingSelect={(row) => setAlreadyOnFile([bookingCustomerFromMatch(row)])}
              onResolved={(address) => {
                void data<{ items: BookingCustomer[] }>(
                  `/api/v1/workspace/dispatch/customers?q=${encodeURIComponent(address.addressLine1)}`,
                )
                  .then((payload) =>
                    setAlreadyOnFile(
                      payload.items
                        .filter(
                          (row) =>
                            row.id !== customerId &&
                            ((row.matchingProperty && sameStreetAndZip(row.matchingProperty, address)) ||
                              (row.matchingBilling && sameStreetAndZip(row.matchingBilling, address))),
                        )
                        .slice(0, 4),
                    ),
                  )
                  .catch(() => setAlreadyOnFile([]));
              }}
            />
            <AddressAlreadyOnFile
              rows={alreadyOnFile}
              onChoose={() => setAdding(false)}
            />
          </div>
          <div className="flex gap-2 sm:col-span-2">
            <select
              name="propertyType"
              defaultValue="residential"
              aria-label="Property type"
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
            >
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
            </select>
            <button
              disabled={busy}
              className="rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-50"
            >
              {busy ? "Adding…" : "Add location"}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="text-sm font-semibold text-[var(--ink-soft)]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-4 divide-y divide-[var(--line)]">
        {shown.map((property) => {
          const at = photosFor(property.id);
          const here = jobsFor(property.id);
          const jobsCount =
            typeof property.jobsCount === "number" ? property.jobsCount : here.length;
          const details = [
            jobsCount
              ? `${jobsCount} job${jobsCount === 1 ? "" : "s"}`
              : typeof property.jobsCount === "number" || !jobsAreCapped
                ? "No jobs"
                : "",
            at.length
              ? `${at.length} photo${at.length === 1 ? "" : "s"}`
              : "",
          ].filter(Boolean);
          const membershipStatus = memberships ? locationMembershipStatus(memberships.filter(item => item.propertyId === property.id || (!item.propertyId && properties.length === 1)), today) : null;
          return (
            <div key={property.id} className="py-3 first:pt-0">
              <button
                type="button"
                onClick={() => openLocation(property.id)}
                className="flex w-full items-center justify-between gap-3 rounded-lg px-1 py-1 text-left transition hover:bg-[var(--wash)]"
              >
                <span className="min-w-0">
                  {property.name && <span className="mb-1 block text-sm font-semibold text-[var(--ink)]">{property.name}</span>}
                  <span className="location-address-line">
                    <span className="text-sm font-semibold text-[var(--ink)]">{property.addressLine1}{property.addressLine2 ? `, ${property.addressLine2}` : ""}</span>
                    {membershipStatus && <MembershipStatusChip label={membershipStatus.label} tone={locationMembershipTone(membershipStatus.state)} />}
                  </span>
                </span>
                <span className="shrink-0 text-xs font-semibold text-[var(--ink-soft)]">
                  <span className="text-[var(--ink-soft)]">
                    {property.active === 0 ? "Inactive · historical · " : ""}{details.join(" · ")}
                  </span>{" "}
                  <span className="text-[var(--accent)]">Open →</span>
                </span>
              </button>
            </div>
          );
        })}
        {!shown.length && (
          <p className="py-3 text-sm text-[var(--ink-faint)]">
            {properties.length
              ? "No address matches that."
              : "No service location on file."}
          </p>
        )}
      </div>

      {matched.length > LOCATION_PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-[var(--ink-soft)]">
            {locationStart + 1}&ndash;{locationStart + shown.length} of{" "}
            {matched.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous locations"
              disabled={locationPageIndex === 0}
              onClick={() => setLocationPage(locationPageIndex - 1)}
              className="rounded-lg border border-[var(--line)] px-2.5 py-1 text-sm font-semibold text-[var(--ink)] disabled:opacity-40"
            >
              &larr;
            </button>
            <button
              type="button"
              aria-label="Next locations"
              disabled={locationPageIndex >= locationPageCount - 1}
              onClick={() => setLocationPage(locationPageIndex + 1)}
              className="rounded-lg border border-[var(--line)] px-2.5 py-1 text-sm font-semibold text-[var(--ink)] disabled:opacity-40"
            >
              &rarr;
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The purchase orders raised against one job. The inventory list already
 * carries the job — by id when it was linked, and by the PO number when it was
 * raised in ServiceTitan, where 21263-003 is the third PO on job 21263.
 */
/** 95 minutes reads as 1h 35m — the way the office says it. */
function formatMinutes(total: number) {
  if (!total) return "0m";
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (!hours) return `${minutes}m`;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}
function PhotoGallery({
  title,
  path,
  openJob,
  canWrite = false,
}: {
  canWrite?: boolean;
  title: string;
  path: string;
  openJob?: (id: string) => string;
}) {
  const [revision, setRevision] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const uploadLock = useRef(false);
  const { photos, loading, error } = usePhotos(path, revision);
  const upload = async (file: File) => {
    if (uploadLock.current) return;
    uploadLock.current = true; setUploading(true); setUploadError("");
    try {
      const body = new FormData(); body.set("photo", file);
      const response = await apiFetch(path, { method: "POST", body });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error?.message || "Photo upload failed.");
      setRevision(value => value + 1);
    } catch (cause) { setUploadError(cause instanceof Error ? cause.message : "Photo upload failed."); }
    finally { uploadLock.current = false; setUploading(false); }
  };
  return (
    <section className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5">
      <h2 className="text-sm font-semibold text-[var(--ink)]">
        {title}
        {photos.length ? ` (${photos.length})` : ""}
      </h2>
      {canWrite && <label className="mt-3 inline-flex cursor-pointer rounded-lg border border-[var(--line)] px-3 py-2 text-xs font-semibold text-[var(--ink-soft)]">{uploading ? "Uploading…" : "Add photo"}<input aria-label="Add job photo" className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={event => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (file) void upload(file); }} /></label>}
      {uploadError && <p role="alert" className="mt-2 text-sm text-[var(--accent)]">{uploadError}</p>}
      <div className="mt-4">
        {loading ? (
          <p className="text-sm text-[var(--ink-faint)]">Loading photos…</p>
        ) : error ? (
          <p className="text-sm text-[var(--accent)]">{error}</p>
        ) : !photos.length ? (
          <Empty text="No photos on file." />
        ) : (
          <PhotoGrid photos={photos} openJob={openJob} />
        )}
      </div>
    </section>
  );
}
/**
 * The office takes a payment against a customer, not against a job. So the amount
 * is entered once and split across selected unpaid invoices newest first. The
 * complete split is sent in one request so the payment cannot be partially recorded.
 */
export function InvoicesPanel({
  invoiceFilter,
  onInvoiceFilterChange,
  customerId,
  openInvoice,
  openJob,
  invoices,
  properties = [],
  credits = [],
  owedCents,
  canWrite,
  act,
  open,
  setOpen,
}: {
  invoiceFilter?: "outstanding" | "paid" | "all";
  onInvoiceFilterChange?: (filter: "outstanding" | "paid" | "all") => void;
  customerId: string;
  openInvoice?: (invoice: CustomerDetail["invoices"][number]) => void;
  openJob?: (jobId: string) => void;
  invoices: CustomerDetail["invoices"];
  properties?: CustomerDetail["properties"];
  credits?: CustomerDetail["credits"];
  owedCents: number;
  canWrite: boolean;
  act: CustomerAction;
  open: boolean;
  setOpen: (value: boolean) => void;
}) {
  const unpaid = [...invoices]
    .filter((invoice) => invoice.balanceDueCents > 0 && !invoice.historical && invoice.status !== "void" && invoice.status !== "paid")
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const [selected, setSelected] = useState<string[] | null>(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("check");
  const [paymentError, setPaymentError] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [importedPaymentInvoice, setImportedPaymentInvoice] = useState<CustomerDetail["invoices"][number] | null>(null);
  const [importedPaymentAmount, setImportedPaymentAmount] = useState("");
  const [importedPaymentMethod, setImportedPaymentMethod] = useState("check");
  const [importedPaymentError, setImportedPaymentError] = useState("");
  const [importedPaymentBusy, setImportedPaymentBusy] = useState(false);
  const isCard = paymentMethod === "credit_card" || paymentMethod === "debit_card";
  useEffect(() => { if (!open) { setCheckoutUrl(""); setPaymentError(""); } }, [open]);
  /**
   * A real account carries hundreds of invoices, so the list is ordered the way
   * the office reads it — what is still owed first, newest first inside that —
   * then searched and paged five at a time. Sorting outstanding to the top
   * matters more than the date: the office is here to chase money.
   */
  const [localFilter, setLocalFilter] = useState<"outstanding" | "paid" | "all">("outstanding");
  const activeFilter = invoiceFilter ?? localFilter;
  const changeFilter = (value: "outstanding" | "paid" | "all") => { setLocalFilter(value); onInvoiceFilterChange?.(value); setInvoicePage(0); };
  const [invoiceQuery, setInvoiceQuery] = useState("");
  const [invoicePage, setInvoicePage] = useState(0);
  const INVOICE_PAGE_SIZE = 5;
  const orderedInvoices = invoices.filter(item => activeFilter === "all" || (activeFilter === "paid" ? item.status === "paid" : item.balanceDueCents > 0)).sort((a, b) => {
    const owed = Number(b.balanceDueCents > 0) - Number(a.balanceDueCents > 0);
    return owed !== 0 ? owed : b.createdAt.localeCompare(a.createdAt);
  });
  const invoiceNeedle = invoiceQuery.trim().toLowerCase();
  const matchedInvoices = invoiceNeedle
    ? orderedInvoices.filter((invoice) =>
        [
          customerDocumentLabel("Invoice", invoice),
          invoice.status.replaceAll("_", " "),
          invoice.summary ?? "",
          money(invoice.totalCents),
          money(invoice.balanceDueCents),
          date(invoice.createdAt),
        ]
          .join(" ")
          .toLowerCase()
          .includes(invoiceNeedle),
      )
    : orderedInvoices;
  const invoicePageCount = Math.max(
    1,
    Math.ceil(matchedInvoices.length / INVOICE_PAGE_SIZE),
  );
  const invoicePageIndex = Math.min(invoicePage, invoicePageCount - 1);
  const invoiceStart = invoicePageIndex * INVOICE_PAGE_SIZE;
  const pagedInvoices = matchedInvoices.slice(
    invoiceStart,
    invoiceStart + INVOICE_PAGE_SIZE,
  );
  /**
   * The office takes a payment against the invoice in front of them, so the
   * form opens under that row with the invoice chosen and the balance filled
   * in. The header button still opens it at the top, for a payment that
   * spreads across several.
   */
  const [payRow, setPayRow] = useState<string | null>(null);
  const panel = useRef<HTMLElement>(null);
  const wasOpen = useRef(open);
  const fromRow = useRef(false);
  const collectOn = (invoiceId: string, balanceCents: number) => {
    setCheckoutUrl(""); setPaymentError("");
    fromRow.current = true;
    setPayRow(invoiceId);
    setSelected([invoiceId]);
    setAmount(inputFromCents(balanceCents));
    setOpen(true);
  };
  useEffect(() => {
    if (open && !wasOpen.current && !fromRow.current) {
      // Opened from somewhere else on the page — clear any row that was being
      // collected on, and bring the form to where the eye is.
      setPayRow(null);
      panel.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }
    if (open && !wasOpen.current) fromRow.current = false;
    wasOpen.current = open;
  }, [open]);
  const [creditInvoiceId, setCreditInvoiceId] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [creditBusy, setCreditBusy] = useState(false);
  /**
   * Nothing is chosen until the office chooses it. Selecting every unpaid
   * invoice by default meant a payment could be spread across the whole
   * account without anyone picking a row or typing an amount.
   */
  const chosen = selected ?? [];
  const selectedCents = unpaid
    .filter((invoice) => chosen.includes(invoice.id))
    .reduce((total, invoice) => total + invoice.balanceDueCents, 0);
  const enteredCents = amount.trim()
    ? moneyInputCents(amount)
    : selectedCents;

  let left = Number.isFinite(enteredCents) ? Math.max(0, enteredCents) : 0;
  const allocation = new Map<string, number>();
  for (const invoice of unpaid) {
    if (!chosen.includes(invoice.id) || left <= 0) continue;
    const applied = Math.min(left, invoice.balanceDueCents);
    allocation.set(invoice.id, applied);
    left -= applied;
  }
  const applying = [...allocation.values()].reduce(
    (total, value) => total + value,
    0,
  );

  const toggle = (id: string) => {
    setCheckoutUrl("");
    setSelected(
      chosen.includes(id)
        ? chosen.filter((value) => value !== id)
        : [...chosen, id],
    );
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!Number.isFinite(enteredCents) || enteredCents <= 0) return;
    const form = new FormData(event.currentTarget);
    const method = String(form.get("method"));
    const reference = String(form.get("reference") || "") || null;
    setPaymentError("");
    if (method === "credit_card" || method === "debit_card") {
      const invoice = unpaid.find(item => item.id === chosen[0]);
      if (!chosen.length || !invoice) { setPaymentError("Choose invoices for card checkout."); return; }
      if (enteredCents > selectedCents) { setPaymentError("Card payment cannot exceed the selected invoice balances."); return; }
      setBusy(true);
      // Open during the user's click so browsers do not block the later redirect.
      const checkoutTab = window.open("about:blank", "_blank");
      if (checkoutTab) checkoutTab.opener = null;
      try {
        const result = await mutate<{ checkout?: { url?: string | null } }>(
          chosen.length === 1 ? `/api/v1/workspace/invoices/${encodeURIComponent(invoice.id)}/checkout` : `/api/v1/workspace/customers/${encodeURIComponent(customerId)}/checkout`,
          "POST",
          chosen.length === 1 ? { amountCents: enteredCents } : {
            amountCents: enteredCents,
            applications: [...allocation].map(([invoiceId, amountCents]) => ({ invoiceId, amountCents })),
          },
        );
        if (!result.checkout?.url) throw new Error("Secure checkout is unavailable. Please try again.");
        setCheckoutUrl(result.checkout.url);
        if (checkoutTab) checkoutTab.location.href = result.checkout.url;
      } catch (cause) {
        checkoutTab?.close();
        setPaymentError(cause instanceof Error ? cause.message : "We couldn’t open secure checkout.");
      } finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      await act(
        customerId,
        `/api/v1/workspace/customers/${customerId}/payments`,
        "POST",
        {
          amountCents: enteredCents,
          method,
          reference,
          applications: [...allocation].map(([invoiceId, amountCents]) => ({
            invoiceId,
            amountCents,
          })),
        },
      );
      setOpen(false);
      setAmount("");
      setSelected(null);
    } catch {
      /* act put the message on the page */
    } finally {
      setBusy(false);
    }
  };
  const submitImportedPayment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!importedPaymentInvoice) return;
    const amountCents = importedPaymentAmount.trim()
      ? moneyInputCents(importedPaymentAmount)
      : importedPaymentInvoice.balanceDueCents;
    if (!Number.isFinite(amountCents) || amountCents <= 0 || amountCents > importedPaymentInvoice.balanceDueCents) {
      setImportedPaymentError("Enter an amount up to the remaining balance.");
      return;
    }
    const form = new FormData(event.currentTarget);
    const reference = String(form.get("reference") || "").trim() || null;
    if (importedPaymentMethod === "check" && !reference) {
      setImportedPaymentError("A check number is required.");
      return;
    }
    setImportedPaymentBusy(true);
    setImportedPaymentError("");
    try {
      await act(
        customerId,
        `/api/v1/workspace/imported-invoices/${encodeURIComponent(importedPaymentInvoice.id)}/payments`,
        "POST",
        { amountCents, method: importedPaymentMethod, reference },
      );
      setImportedPaymentInvoice(null);
      setImportedPaymentAmount("");
    } catch {
      // CustomerAction puts the server response in the customer workspace.
    } finally {
      setImportedPaymentBusy(false);
    }
  };
  const collectImportedCard = async (invoice: CustomerDetail["invoices"][number]) => {
    setImportedPaymentBusy(true);
    setImportedPaymentError("");
    const checkoutTab = window.open("about:blank", "_blank");
    if (checkoutTab) checkoutTab.opener = null;
    try {
      const result = await mutate<{ checkout?: { url?: string | null } }>(
        `/api/v1/workspace/imported-invoices/${encodeURIComponent(invoice.id)}/checkout`,
        "POST",
        { amountCents: invoice.balanceDueCents },
      );
      if (!result.checkout?.url) throw new Error("Secure checkout is unavailable. Please try again.");
      if (checkoutTab) checkoutTab.location.href = result.checkout.url;
      else window.open(result.checkout.url, "_blank", "noopener,noreferrer");
    } catch (cause) {
      checkoutTab?.close();
      setImportedPaymentInvoice(invoice);
      setImportedPaymentError(cause instanceof Error ? cause.message : "We couldn’t open secure checkout.");
    } finally {
      setImportedPaymentBusy(false);
    }
  };
  const applyCredit = async (credit: CustomerDetail["credits"][number]) => {
    const invoice = unpaid.find((item) => item.id === creditInvoiceId);
    const amountCents = creditAmount.trim()
      ? moneyInputCents(creditAmount)
      : Math.min(credit.remainingCents, invoice?.balanceDueCents ?? 0);
    if (!invoice || !Number.isFinite(amountCents) || amountCents <= 0) return;
    setCreditBusy(true);
    try {
      await act(
        customerId,
        `/api/v1/workspace/customers/${customerId}/credits/${credit.id}/apply`,
        "POST",
        { invoiceId: invoice.id, amountCents },
      );
      setCreditAmount("");
      setCreditInvoiceId("");
    } catch {
      /* act put the message on the page */
    } finally {
      setCreditBusy(false);
    }
  };

  return (
    <section
      ref={panel}
      className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--ink)]">
          <span
            aria-hidden="true"
            className={`h-3.5 w-1 rounded-full ${toneBar[owedCents > 0 ? "urgent" : "standard"]}`}
          />
          Invoices ({invoices.length})
          {owedCents > 0 && (
            <span className="rounded-full bg-[var(--st-conflict-wash)] px-2 py-0.5 text-[11px] font-semibold text-[var(--st-conflict)]">
              {money(owedCents)} owed
            </span>
          )}
        </h2>
        {canWrite && owedCents > 0 && (
          <button
            type="button"
            onClick={() => {
              setPayRow(null);
              setSelected(null);
              setAmount("");
              setOpen(!open);
            }}
            className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)]"
          >
            {open ? "Cancel" : "Take a payment"}
          </button>
        )}
      </div>

      {!onInvoiceFilterChange && <div className="record-filter-bar mt-3" aria-label="Invoice filters">{(["outstanding", "paid", "all"] as const).map(kind => <button type="button" key={kind} className="record-filter-button" aria-pressed={activeFilter === kind} onClick={() => changeFilter(kind)}>{kind === "outstanding" ? "Outstanding" : kind === "paid" ? "Paid" : "All"} ({invoices.filter(item => kind === "all" || (kind === "paid" ? item.status === "paid" : item.balanceDueCents > 0)).length})</button>)}</div>}
      {/*
        * Taking a payment is its own decision, so it gets its own surface
        * instead of unfolding grey blocks halfway down the invoice list.
        */}
      {open && payRow === null && (
        <Dialog title="Take a payment" onClose={() => setOpen(false)} width="max-w-2xl">
        <form
          onSubmit={(event) => void submit(event)}
          className="p-1"
        >
          <div className="grid gap-2 sm:grid-cols-[160px_170px_1fr]">
            <input
              value={amount}
              onChange={(event) => { setAmount(event.target.value); setCheckoutUrl(""); }}
              type="text" inputMode="decimal"
              placeholder={inputFromCents(selectedCents)}
              disabled={busy}
              aria-label="Payment amount"
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
            />
            <select
              name="method"
              value={paymentMethod}
              onChange={event => { setPaymentMethod(event.target.value); setPaymentError(""); setCheckoutUrl(""); }}
              disabled={busy}
              aria-label="Payment method"
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
            >
              {PAYMENT_METHODS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              name="reference"
              placeholder="Check number or reference (optional)"
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
            />
          </div>
          {isCard && <p className="mt-3 text-xs text-[var(--ink-soft)]">Select one or more open invoices for a single card payment. Review the split below, then continue to Stripe.</p>}
          {paymentError && <p role="alert" className="mt-3 text-sm text-[var(--st-conflict)]">{paymentError}</p>}
          {checkoutUrl && <p role="status" className="mt-3 text-sm"><a href={checkoutUrl} target="_blank" rel="noreferrer" className="font-semibold underline">Open Stripe checkout</a>. Payment will be recorded after confirmation.</p>}
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
            Applies to
          </p>
          <button type="button" disabled={busy} className="mt-2 text-sm font-semibold text-[var(--accent)]" onClick={() => {
            setSelected(chosen.length === unpaid.length ? [] : unpaid.map(invoice => invoice.id));
            setAmount(""); setCheckoutUrl(""); setPaymentError("");
          }}>{chosen.length === unpaid.length ? "Clear invoice selection" : "Select all open invoices"}</button>
          <div className="mt-2 divide-y divide-[var(--line)]">
            {unpaid.map((invoice) => {
              const applied = allocation.get(invoice.id) ?? 0;
              return (
                <label
                  key={invoice.id}
                  className="flex items-center justify-between gap-3 py-2 text-sm"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <input
                      type="checkbox"
                      disabled={busy}
                      checked={chosen.includes(invoice.id)}
                      onChange={() => toggle(invoice.id)}
                    />
                    <span className="truncate font-semibold text-[var(--ink)]">
                      {customerDocumentLabel("Invoice", invoice)}
                    </span>
                    <span className="shrink-0 text-xs text-[var(--ink-soft)]">
                      {date(invoice.createdAt)} ·{" "}
                      {money(invoice.balanceDueCents)} due
                    </span>
                  </span>
                  <span
                    className={`shrink-0 font-semibold ${applied ? "text-[var(--ink)]" : "text-[var(--ink-faint)]"}`}
                  >
                    {applied ? money(applied) : "—"}
                  </span>
                </label>
              );
            })}
          </div>
          {!chosen.length && (
            <p className="mt-2 text-xs text-[var(--ink-soft)]">
              Choose the invoices this payment is for.
            </p>
          )}
          {left > 0 && !isCard && (
            <p className="mt-2 text-xs font-semibold text-[var(--accent)]">
              {money(left)} will be saved as customer credit for a later
              invoice.
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              disabled={busy || !enteredCents || !chosen.length || (isCard && left > 0)}
              className="rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-50"
            >
              {busy ? isCard ? "Opening checkout…" : "Recording…" : isCard ? `Pay ${money(enteredCents)} by card` : `Record ${money(enteredCents)}`}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setAmount("");
                setSelected(null);
                setPayRow(null);
              }}
              className="text-sm font-semibold text-[var(--ink-soft)]"
            >
              Cancel
            </button>
          </div>
        </form>
        </Dialog>
      )}

      {importedPaymentInvoice && (
        <Dialog title={`Record payment — ${customerDocumentLabel("Invoice", importedPaymentInvoice)}`} onClose={() => !importedPaymentBusy && setImportedPaymentInvoice(null)} width="max-w-xl">
          <form onSubmit={(event) => void submitImportedPayment(event)} className="p-1">
            <p className="text-sm text-[var(--ink-soft)]">This is an imported membership renewal without a job. Record a non-card payment already received; use “Take card payment” on the invoice row for secure checkout.</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-[160px_170px_1fr]">
              <input value={importedPaymentAmount} onChange={(event) => { setImportedPaymentAmount(event.target.value); setImportedPaymentError(""); }} type="text" inputMode="decimal" placeholder={inputFromCents(importedPaymentInvoice.balanceDueCents)} disabled={importedPaymentBusy} aria-label="Imported invoice payment amount" className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm" />
              <select value={importedPaymentMethod} onChange={(event) => { setImportedPaymentMethod(event.target.value); setImportedPaymentError(""); }} disabled={importedPaymentBusy} aria-label="Imported invoice payment method" className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm">
                {PAYMENT_METHODS.filter((option) => option.value !== "credit_card" && option.value !== "debit_card").map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <input name="reference" placeholder={importedPaymentMethod === "check" ? "Check number (required)" : "Reference (optional)"} disabled={importedPaymentBusy} className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm" />
            </div>
            <p className="mt-3 text-xs text-[var(--ink-soft)]">Up to {money(importedPaymentInvoice.balanceDueCents)} will be applied to this imported invoice.</p>
            {importedPaymentError && <p role="alert" className="mt-3 text-sm text-[var(--st-conflict)]">{importedPaymentError}</p>}
            <div className="mt-4 flex flex-wrap gap-3"><button disabled={importedPaymentBusy} className="rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-50">{importedPaymentBusy ? "Recording…" : "Record payment"}</button><button type="button" disabled={importedPaymentBusy} onClick={() => setImportedPaymentInvoice(null)} className="text-sm font-semibold text-[var(--ink-soft)]">Cancel</button></div>
          </form>
        </Dialog>
      )}

      {!!credits.length && (
        <div className="mt-4 border border-[var(--line)] bg-[var(--wash)] p-4">
          <p className="text-sm font-semibold text-[var(--ink)]">
            Available customer credit:{" "}
            {money(
              credits.reduce(
                (total, credit) => total + credit.remainingCents,
                0,
              ),
            )}
          </p>
          <p className="mt-1 text-xs text-[var(--ink-soft)]">
            Apply credit to an outstanding invoice when the customer is ready to
            use it.
          </p>
          {canWrite && unpaid.length > 0 && (
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <select
                value={creditInvoiceId}
                onChange={(event) => setCreditInvoiceId(event.target.value)}
                aria-label="Invoice for customer credit"
                className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
              >
                <option value="">Choose an invoice</option>
                {unpaid.map((invoice) => (
                  <option key={invoice.id} value={invoice.id}>
                    {customerDocumentLabel("Invoice", invoice)} —{" "}
                    {money(invoice.balanceDueCents)} due
                  </option>
                ))}
              </select>
              <input
                value={creditAmount}
                onChange={(event) => setCreditAmount(event.target.value)}
                type="text" inputMode="decimal"
                placeholder="Full available amount"
                aria-label="Credit amount"
                className="w-44 rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
              />
              {credits.map((credit) => (
                <button
                  key={credit.id}
                  type="button"
                  disabled={creditBusy || !creditInvoiceId}
                  onClick={() => void applyCredit(credit)}
                  className="rounded-lg border border-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--ink)] disabled:opacity-50"
                >
                  {creditBusy
                    ? "Applying…"
                    : `Apply up to ${money(credit.remainingCents)}`}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {invoices.length > INVOICE_PAGE_SIZE && (
        <input
          value={invoiceQuery}
          onChange={(event) => {
            setInvoiceQuery(event.target.value);
            setInvoicePage(0);
          }}
          placeholder="Invoice number, status, or amount"
          aria-label="Search invoices"
          className="mt-4 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-1.5 text-sm outline-none focus:border-[var(--line-strong)]"
        />
      )}

      <div className="mt-4 divide-y divide-[var(--line)]">
        {pagedInvoices.map((invoice) => {
          const canOpen = Boolean(openInvoice);
          return <Fragment key={invoice.id}>
            <div
              role={canOpen ? "button" : undefined}
              tabIndex={canOpen ? 0 : undefined}
              onClick={() => canOpen && openInvoice?.(invoice)}
              onKeyDown={(event) => {
                if (
                  canOpen &&
                  (event.key === "Enter" || event.key === " ")
                ) {
                  event.preventDefault();
                  openInvoice?.(invoice);
                }
              }}
              className={`group relative flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 ${canOpen ? "cursor-pointer rounded-lg px-2 -mx-2 transition hover:bg-[var(--wash)]" : ""}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[var(--ink)]">
                  {customerDocumentLabel("Invoice", invoice)}{" "}
                  <span
                    className={`ml-2 rounded-md px-2 py-0.5 text-[11px] font-medium ${statusStyle(invoicePresentation(invoice).label)}`}
                  >
                    {invoicePresentation(invoice).label}
                  </span>
                  {invoice.historical && <span className="ml-2 text-[11px] font-medium text-[var(--ink-soft)]">Historical import</span>}
                </p>
                <p className="mt-1 text-xs text-[var(--ink-soft)]">
                  {date(invoice.createdAt)}
                  {invoice.dueDate ? ` · due ${date(invoice.dueDate)}` : ""}
                  {properties.length > 1 && invoice.propertyId
                    ? ` · ${properties.find((entry) => entry.id === invoice.propertyId)?.addressLine1 ?? ""}`
                    : ""}
                </p>
                {invoicePresentation(invoice).estimateDisplayNumber && (
                  <p className="mt-1 text-xs text-[var(--ink-soft)]">
                    Includes approved work from Estimate #{invoicePresentation(invoice).estimateDisplayNumber}
                    {invoicePresentation(invoice).soldLater ? " · Not scheduled yet." : ""}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-[var(--ink)]">
                  {money(invoice.totalCents)}
                </p>
                <p
                  className={`mt-1 text-xs font-semibold ${invoice.balanceDueCents > 0 ? "text-[var(--accent)]" : "text-[var(--ink-faint)]"}`}
                >
                  {invoice.balanceDueCents > 0
                    ? `${money(invoice.balanceDueCents)} due`
                    : "Paid"}
                </p>
              </div>

              {/*
                * Deciding what to chase means knowing what the invoice was for
                * and what is still on it, without leaving the list. Hover only —
                * the row itself already opens the invoice.
                */}
              <div className="pointer-events-none absolute left-2 top-full z-20 hidden w-[26rem] max-w-[calc(100%-1rem)] group-hover:block">
                <div className="pointer-events-auto mt-1 rounded-xl border border-[var(--line)] bg-[var(--paper)] p-3 shadow-lg">
                  <p className="text-xs font-semibold text-[var(--ink)]">
                    {customerDocumentLabel("Invoice", invoice)}
                  </p>
                  {invoice.summary && (
                    <p className="mt-1 line-clamp-3 text-xs text-[var(--ink-soft)]">
                      {invoice.summary}
                    </p>
                  )}
                  {invoice.jobTypeName?.trim() && (
                    <p className="mt-1 text-xs text-[var(--ink)]">
                      {invoice.jobTypeName}
                    </p>
                  )}
                  <dl className="mt-2 grid grid-cols-2 gap-x-5 gap-y-1 text-xs">
                    <div className="flex justify-between gap-3">
                      <dt className="text-[var(--ink-soft)]">Total</dt>
                      <dd className="font-semibold text-[var(--ink)]">
                        {money(invoice.totalCents)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-[var(--ink-soft)]">Balance</dt>
                      <dd
                        className={`font-semibold ${invoice.balanceDueCents > 0 ? "text-[var(--accent)]" : "text-[var(--ink-faint)]"}`}
                      >
                        {invoice.balanceDueCents > 0
                          ? money(invoice.balanceDueCents)
                          : "Paid"}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-[var(--ink-soft)]">Status</dt>
                      <dd className="capitalize text-[var(--ink)]">
                        {invoice.status.replaceAll("_", " ")}
                      </dd>
                    </div>
                    {invoice.dueDate && (
                      <div className="flex justify-between gap-3">
                        <dt className="text-[var(--ink-soft)]">Due</dt>
                        <dd className="text-[var(--ink)]">
                          {date(invoice.dueDate)}
                        </dd>
                      </div>
                    )}
                  </dl>
                  <div className="mt-2 flex flex-wrap items-center gap-4">
                    {canOpen && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openInvoice?.(invoice);
                        }}
                        className="text-xs font-semibold text-[var(--accent)]"
                      >
                        Open invoice &rarr;
                      </button>
                    )}
                    {openJob && invoice.jobId && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openJob(invoice.jobId!);
                        }}
                        className="text-xs font-semibold text-[var(--accent)]"
                      >
                        Open job &rarr;
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {canWrite && !invoice.historical && invoice.balanceDueCents > 0 && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    collectOn(invoice.id, invoice.balanceDueCents);
                  }}
                  className="shrink-0 rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-1.5 text-xs font-semibold text-[var(--ink)] transition hover:bg-[var(--wash)]"
                >
                  Collect payment
                </button>
              )}
              {canWrite && invoice.historical && invoice.balanceDueCents > 0 && (
                <div className="flex shrink-0 gap-2">{IMPORTED_CARD_INVOICE_IDS.has(invoice.id) && <button type="button" disabled={importedPaymentBusy} onClick={(event) => { event.stopPropagation(); void collectImportedCard(invoice); }} className="rounded-lg bg-[var(--ink)] px-2.5 py-1.5 text-xs font-semibold text-[var(--paper)] transition hover:opacity-90 disabled:opacity-50">{importedPaymentBusy ? "Opening…" : "Take card payment"}</button>}<button type="button" onClick={(event) => { event.stopPropagation(); setImportedPaymentInvoice(invoice); setImportedPaymentAmount(""); setImportedPaymentMethod("check"); setImportedPaymentError(""); }} className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-1.5 text-xs font-semibold text-[var(--ink)] transition hover:bg-[var(--wash)]">Record payment</button></div>
              )}
            </div>
            {open && payRow === invoice.id && (
              <form
                onSubmit={(event) => void submit(event)}
                className="mt-4 border border-[var(--line)] bg-[var(--wash)] p-4"
              >
                <div className="grid gap-2 sm:grid-cols-[160px_170px_1fr]">
                  <input
                    value={amount}
                    onChange={(event) => { setAmount(event.target.value); setCheckoutUrl(""); }}
                    type="text" inputMode="decimal"
                    placeholder={inputFromCents(selectedCents)}
                    disabled={busy}
                    aria-label="Payment amount"
                    className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
                  />
                  <select
                    name="method"
                    value={paymentMethod}
                    onChange={event => { setPaymentMethod(event.target.value); setPaymentError(""); setCheckoutUrl(""); }}
                    disabled={busy}
                    aria-label="Payment method"
                    className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
                  >
                    {PAYMENT_METHODS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <input
                    name="reference"
                    placeholder="Check number or reference (optional)"
                    className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
                  />
                </div>
                {isCard && <p className="mt-3 text-xs text-[var(--ink-soft)]">Select one or more open invoices for a single card payment. Review the split below, then continue to Stripe.</p>}
                {paymentError && <p role="alert" className="mt-3 text-sm text-[var(--st-conflict)]">{paymentError}</p>}
                {checkoutUrl && <p role="status" className="mt-3 text-sm"><a href={checkoutUrl} target="_blank" rel="noreferrer" className="font-semibold underline">Open Stripe checkout</a>. Payment will be recorded after confirmation.</p>}
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                  Applies to
                </p>
                <button type="button" disabled={busy} className="mt-2 text-sm font-semibold text-[var(--accent)]" onClick={() => {
                  setSelected(chosen.length === unpaid.length ? [] : unpaid.map(invoice => invoice.id));
                  setAmount(""); setCheckoutUrl(""); setPaymentError("");
                }}>{chosen.length === unpaid.length ? "Clear invoice selection" : "Select all open invoices"}</button>
                <div className="mt-2 divide-y divide-[var(--line)]">
                  {unpaid.map((invoice) => {
                    const applied = allocation.get(invoice.id) ?? 0;
                    return (
                      <label
                        key={invoice.id}
                        className="flex items-center justify-between gap-3 py-2 text-sm"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <input
                            type="checkbox"
                            disabled={busy}
                            checked={chosen.includes(invoice.id)}
                            onChange={() => toggle(invoice.id)}
                          />
                          <span className="truncate font-semibold text-[var(--ink)]">
                            {customerDocumentLabel("Invoice", invoice)}
                          </span>
                          <span className="shrink-0 text-xs text-[var(--ink-soft)]">
                            {date(invoice.createdAt)} ·{" "}
                            {money(invoice.balanceDueCents)} due
                          </span>
                        </span>
                        <span
                          className={`shrink-0 font-semibold ${applied ? "text-[var(--ink)]" : "text-[var(--ink-faint)]"}`}
                        >
                          {applied ? money(applied) : "—"}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {left > 0 && !isCard && (
                  <p className="mt-2 text-xs font-semibold text-[var(--accent)]">
                    {money(left)} will be saved as customer credit for a later
                    invoice.
                  </p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button
                    disabled={busy || !enteredCents || !chosen.length || (isCard && left > 0)}
                    className="rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-50"
                  >
                    {busy ? isCard ? "Opening checkout…" : "Recording…" : isCard ? `Pay ${money(enteredCents)} by card` : `Record ${money(enteredCents)}`}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      setAmount("");
                      setSelected(null);
                    }}
                    className="text-sm font-semibold text-[var(--ink-soft)]"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </Fragment>;
        })}
        {!invoices.length && <Empty text="No invoices yet." />}
        {invoices.length > 0 && !matchedInvoices.length && (
          <Empty text="No invoice matches that." />
        )}
      </div>

      {matchedInvoices.length > INVOICE_PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-[var(--ink-soft)]">
            {invoiceStart + 1}&ndash;
            {invoiceStart + pagedInvoices.length} of {matchedInvoices.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous invoices"
              disabled={invoicePageIndex === 0}
              onClick={() => setInvoicePage(invoicePageIndex - 1)}
              className="rounded-lg border border-[var(--line)] px-2.5 py-1 text-sm font-semibold text-[var(--ink)] disabled:opacity-40"
            >
              &larr;
            </button>
            <button
              type="button"
              aria-label="Next invoices"
              disabled={invoicePageIndex >= invoicePageCount - 1}
              onClick={() => setInvoicePage(invoicePageIndex + 1)}
              className="rounded-lg border border-[var(--line)] px-2.5 py-1 text-sm font-semibold text-[var(--ink)] disabled:opacity-40"
            >
              &rarr;
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function membershipVisitUsageLabel(includedVisits: number | null, visitsUsed: number) {
  return includedVisits === null
    ? "Visit allowance not specified"
    : `${includedVisits} included · ${visitsUsed} used`;
}

export function membershipEquipmentLabel(equipment: { name: string | null; manufacturer: string | null; modelNumber: string | null; serialNumber: string | null }) {
  return [equipment.name, [equipment.manufacturer, equipment.modelNumber].filter(Boolean).join(" "), equipment.serialNumber ? `S/N ${equipment.serialNumber}` : ""]
    .filter(Boolean)
    .join(" · ");
}

type MembershipEquipment = {
  id: string;
  name: string | null;
  manufacturer: string | null;
  modelNumber: string | null;
  serialNumber: string | null;
};

type MembershipVisit = MembershipEvent & {
  id: string;
  name?: string | null;
  status: string;
  dueAt: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  jobId?: string | null;
  jobNumber?: string | null;
  jobStatus?: string | null;
  jobScheduledAt?: string | null;
  jobCompletedAt?: string | null;
};

type MembershipDetail = MembershipLocation & {
  renewedIntoId?: string | null;
  relatedInvoices?: MembershipRelatedInvoice[];
  memo?: string | null;
  id?: string;
  startDate?: string | null;
  endDate?: string | null;
  seasonalVisits?: MembershipSeasonalVisit[];
  coveredEquipment?: MembershipEquipment[];
  includedVisits: number | null;
  visitsUsed: number;
  equipment: MembershipEquipment | null;
  tags: WorkspaceTag[];
  history: Array<{ newStatus: string; reason: string | null; changedAt: string }>;
  services: Array<{
    name: string;
    cadence: string | null;
    active: boolean;
    equipmentId: string | null;
    equipmentName: string | null;
    equipmentManufacturer: string | null;
    equipmentModelNumber: string | null;
    equipmentSerialNumber: string | null;
    nextDueAt?: string | null;
  }>;
  events: MembershipVisit[];
};

async function loadMembershipDetail(membershipId: string): Promise<MembershipDetail | null> {
  const response = await apiFetch(
    `/api/v1/workspace/memberships/${membershipId}`,
  );
  if (response.status === 404) return null;
  if (!response.ok) throw new Error("We couldn’t load that membership.");
  const payload = (await response.json()) as {
    data?: {
      membership?: {
        addressLine1?: string | null;
        addressLine2?: string | null;
        city?: string | null;
        state?: string | null;
        postalCode?: string | null;
        startDate?: string | null;
        endDate?: string | null;
        seasonalVisits?: MembershipSeasonalVisit[];
        coveredEquipment?: MembershipEquipment[];
        memo?: string | null;
        relatedInvoices?: MembershipRelatedInvoice[];
        includedVisits?: number | null;
        visitsUsed?: number;
        equipment?: MembershipEquipment | null;
        tags?: WorkspaceTag[];
        history?: MembershipDetail["history"];
        services?: MembershipDetail["services"];
        events?: MembershipDetail["events"];
      };
    };
  };
  const membership = payload.data?.membership;
  if (!membership || !Array.isArray(membership.history)) return null;
  return {
    ...membership,
    id: membershipId,
    includedVisits: membership.includedVisits ?? null,
    visitsUsed: membership.visitsUsed ?? 0,
    equipment: membership.equipment ?? null,
    tags: membership.tags ?? [],
    history: membership.history,
    services: membership.services ?? [],
    events: (membership.events ?? []).map((event, index) => ({
      ...event,
      id: event.id || `${membershipId}-event-${index}`,
    })),
  };
}

function membershipRemainingVisits(detail: MembershipDetail) {
  return detail.includedVisits === null
    ? null
    : Math.max(0, detail.includedVisits - detail.visitsUsed);
}

function closedMembershipEvent(status: string) {
  const value = status.toLowerCase();
  return (
    value.includes("complete") ||
    value.includes("cancel") ||
    value.includes("remove") ||
    value.includes("skip")
  );
}

function nextMembershipEvent(detail: MembershipDetail) {
  const timeOf = (event: MembershipDetail["events"][number]) =>
    Date.parse(event.scheduledAt ?? event.dueAt ?? "") || Number.MAX_SAFE_INTEGER;
  return detail.events
    .filter((event) => !closedMembershipEvent(event.status) && !event.completedAt && event.jobStatus!=="completed" && event.appointmentStatus!=="completed" && !event.jobCompletedAt)
    .sort((left, right) => timeOf(left) - timeOf(right))[0];
}

function membershipHasBookableService(detail: MembershipDetail) {
  const bookable = detail.events.some(event => Boolean(event.canBook)
    && !event.completedAt && !event.jobCompletedAt && event.jobStatus !== "completed"
    && event.appointmentStatus !== "completed" && event.status !== "completed");
  // Imported allowances count source service definitions, not appointments.
  // Use the API's service eligibility instead of subtracting completed visits.
  if (detail.id?.startsWith("st-membership-") || detail.events.some(event => event.canBook !== undefined)) return bookable;
  // Legacy native plans can have an allowance before individual events exist.
  const remaining = membershipRemainingVisits(detail), next = nextMembershipEvent(detail);
  return !next?.scheduledAt && (remaining !== null ? remaining > 0 : Boolean(next && !next.jobId && !next.appointmentId));
}

function membershipVisitStatus(
  membership: CustomerDetail["memberships"][number],
  detail: MembershipDetail,
) {
  const remaining = membershipRemainingVisits(detail);
  const upcoming = nextMembershipEvent(detail);
  return [
    detail.id?.startsWith("st-membership-") ? `${detail.visitsUsed} completed ${detail.visitsUsed === 1 ? "visit" : "visits"} · ${coveredMaintenanceServices(detail.services, isHistoricalMembershipCoverage(detail)).length} covered services` : membershipVisitUsageLabel(detail.includedVisits, detail.visitsUsed),
    detail.id?.startsWith("st-membership-") || remaining === null
      ? ""
      : `${remaining} remaining`,
    upcoming?.scheduledAt
      ? `scheduled ${calendarDate(upcoming.scheduledAt)}`
      : upcoming?.dueAt
        ? `due ${calendarDate(upcoming.dueAt)}`
        : membership.nextServiceDate
          ? `next service ${calendarDate(membership.nextServiceDate)}`
          : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

function membershipCoveredEquipmentLabels(detail: MembershipDetail) {
  const labels = new Set<string>();
  for (const equipment of detail.coveredEquipment ?? []) labels.add(membershipEquipmentLabel(equipment));
  if (detail.equipment) labels.add(membershipEquipmentLabel(detail.equipment));
  for (const service of detail.services) {
    if (!service.equipmentName) continue;
    labels.add(
      membershipEquipmentLabel({
        name: service.equipmentName,
        manufacturer: service.equipmentManufacturer,
        modelNumber: service.equipmentModelNumber,
        serialNumber: service.equipmentSerialNumber,
      }),
    );
  }
  return [...labels].filter(Boolean);
}

function membershipBookingNotes(
  membership: CustomerDetail["memberships"][number],
  detail: MembershipDetail,
) {
  const service = detail.services.find((item) => item.active)?.name;
  const equipment = membershipCoveredEquipmentLabels(detail)[0];
  return [
    "Membership remaining service",
    membership.recurrence,
    service,
    equipment ? `Covered equipment: ${equipment}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

function MembershipDetailsButton({
  membershipId,
  showEquipment = false,
  tags = [],
  openJob,
}: {
  membershipId: string;
  showEquipment?: boolean;
  tags?: WorkspaceTag[];
  openJob?: (id: string) => string;
}) {
  const openCustomerJob = useContext(CustomerJobContext);
  const [open, setOpen] = useState(false);
  const [booking, setBooking] = useState(false);
  const [available, setAvailable] = useState(true);
  const [detail, setDetail] = useState<MembershipDetail | null>(null);
  const [error, setError] = useState("");
  const load = async () => {
    setError("");
    try {
      const loaded = await loadMembershipDetail(membershipId);
      if (!loaded) {
        setAvailable(false);
        return;
      }
      setDetail(loaded);
      setOpen(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "We couldn’t load that membership.");
    }
  };
  if (!available) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => void load()}
        className="text-xs font-semibold text-[var(--accent)]"
      >
        Details
      </button>
      {error && (
        <p className="mt-1 text-xs text-[var(--accent)]">{error}</p>
      )}
      {open && detail && (
        <Dialog title="Membership details" onClose={() => setOpen(false)}>
          <div className="space-y-3 text-sm">
            <p className="font-medium">{membershipAddress(detail) || 'Service address not recorded'}</p>
            <p className="text-xs text-[var(--ink-soft)]">Coverage {calendarDate(detail.startDate ?? null)} – {calendarDate(detail.endDate ?? null)}</p>
            <MembershipServiceCoverage services={detail.services} historical={isHistoricalMembershipCoverage(detail)}/>
            {detail.memo&&<section><h3 className="font-semibold">Plan notes</h3><p className="whitespace-pre-wrap text-sm">{detail.memo}</p></section>}
            {showEquipment && <section><h3 className="font-semibold">Covered equipment</h3>{membershipCoveredEquipmentLabels(detail).length ? membershipCoveredEquipmentLabels(detail).map(label=><p key={label}>{label}</p>) : <p className="text-[var(--ink-soft)]">No equipment coverage recorded on this membership.</p>}</section>}
            <MembershipVisitHistory events={detail.events} seasonalVisits={detail.seasonalVisits} canSchedule={false} onSchedule={()=>{}} openJob={id=>{if(openCustomerJob){setOpen(false);openCustomerJob(id);}else if(openJob)window.location.assign(openJob(id));}}/>
            {detail.history.map((row, index) => (
              <p key={`${row.changedAt}-${index}`} className="text-[var(--ink-soft)]">
                {row.newStatus}
                {row.reason ? ` · ${row.reason}` : ""}
              </p>
            ))}
            {!detail.history.length && !detail.services.length && !detail.events.length && (
              <p className="text-[var(--ink-soft)]">No history on file.</p>
            )}
            <PrimaryButton type="button" onClick={() => setBooking(true)}>Schedule appointment</PrimaryButton>
            <MembershipRelatedInvoices invoices={detail.relatedInvoices}/>
          </div>
        </Dialog>
      )}
      {booking && <OwnerAppointmentDialog owner={{ type: "membership", id: membershipId }} events={detail?.events ?? []} onClose={() => setBooking(false)} onDone={() => { setBooking(false); setOpen(false); }} />}
    </>
  );
}

function MembershipHistoryGroup({ count, children }: { count: number; children: import("react").ReactNode }) {
  const [open, setOpen] = useState(false);
  return <details className="membership-history-group" onToggle={event => setOpen(event.currentTarget.open)}>
    <summary><ChevronDown size={14} aria-hidden="true" />Past memberships ({count})</summary>
    {open && children}
  </details>;
}

function MembershipHistoryDisclosure({ membership, children, today }: { membership: CustomerDetail["memberships"][number]; children: import("react").ReactNode; today: string }) {
  const [open, setOpen] = useState(false);
  const state = membershipState(membership, today);
  return <details className="membership-history-disclosure" onToggle={event => setOpen(event.currentTarget.open)}>
    <summary><ChevronDown size={14} aria-hidden="true" /><span className="membership-history-plan">{membership.membershipTypeName || membership.recurrence || "Membership"}</span><span className="membership-history-dates">{calendarDate(membership.startDate ?? null)} – {calendarDate(membership.endDate ?? null)}</span><MembershipStatusChip label={state.label} tone={state.tone} /></summary>
    {open && <div className="membership-history-content">{children}</div>}
  </details>;
}

function MembershipRow({
  customerId,
  renewalLocation,
  membership,
  today,
  showEquipment = false,
  compact = false,
  upcoming = false,
  propertyId,
  bookingCustomer,
  canBookJobs = false,
  bookJob,
  canWrite,
  act,
  tags = [],
  openJob,
}: {
  customerId: string;
  renewalLocation?: { id: string; address: string; customerName: string };
  membership: CustomerDetail["memberships"][number];
  today?: string;
  showEquipment?: boolean;
  compact?: boolean;
  upcoming?: boolean;
  propertyId?: string;
  bookingCustomer?: BookingCustomer;
  canBookJobs?: boolean;
  bookJob?: (customer: BookingCustomer) => void;
  canWrite: boolean;
  act: CustomerAction;
  tags?: WorkspaceTag[];
  openJob?: (id: string) => string;
}) {
  const openCustomerJob = useContext(CustomerJobContext);
  const [renewing, setRenewing] = useState(false);
  const [editingPlan,setEditingPlan]=useState(false);
  const [planRevision,setPlanRevision]=useState(0);
  const [detail, setDetail] = useState<MembershipDetail | null>(null);
  const [detailError, setDetailError] = useState("");
  const state: { tone: LabelTone; label: string } = upcoming ? { tone: "standard", label: "Upcoming term" } : membershipState(membership, today);
  useEffect(() => {
    if (!showEquipment) return;
    let cancelled = false;
    setDetail(null);
    setDetailError("");
    void loadMembershipDetail(membership.id)
      .then((loaded) => {
        if (!cancelled) setDetail(loaded);
      })
      .catch((cause) => {
        if (!cancelled) {
          setDetailError(
            cause instanceof Error
              ? cause.message
              : "We couldn’t load membership visits.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [membership.id, showEquipment, planRevision]);
  const remainingVisits = detail ? membershipRemainingVisits(detail) : null;
  const coveredEquipment = detail ? membershipCoveredEquipmentLabels(detail) : [];
  const canBookRemainingService = Boolean(
    showEquipment &&
      detail &&
      canBookJobs &&
      bookJob &&
      bookingCustomer &&
      propertyId &&
      isCurrentOrUpcomingMembership(membership, today ?? new Date().toISOString().slice(0, 10)) &&
      membershipHasBookableService(detail),
  );
  const bookRemainingService = () => {
    if (!detail || !bookJob || !bookingCustomer || !propertyId) return;
    bookJob({
      ...bookingCustomer,
      preferredPropertyId: propertyId,
      bookingNotes: membershipBookingNotes(membership, detail),
    });
  };
  const planEditor = editingPlan && createPortal(<MembershipPlanEditor membershipId={membership.id} onClose={()=>setEditingPlan(false)} onSaved={()=>{setEditingPlan(false);setPlanRevision(value=>value+1);}}/>,document.body);
  const renewalDialog = renewing && createPortal(<MembershipRenewalProposalDialog location={renewalLocation} membershipId={membership.id} currentEndDate={membership.endDate ?? null} onClose={() => setRenewing(false)} openInvoice={(invoiceId) => { window.location.assign(`/dashboard?invoice=${encodeURIComponent(invoiceId)}&collectPayment=true`); }} />, document.body);
  return (
    <div className="text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!compact && <MembershipStatusChip label={state.label} tone={state.tone} />}
        {canWrite && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setRenewing(!renewing)}
              className="text-xs font-semibold text-[var(--accent)]"
            >
              {renewing ? "Cancel" : "Renew"}
            </button>
            <button type="button" className="text-xs font-semibold text-[var(--accent)]" onClick={()=>setEditingPlan(true)}>Edit plan</button>
            <MembershipDetailsButton membershipId={membership.id} showEquipment={showEquipment} tags={membership.tags ?? []} openJob={openJob} />
          </div>
        )}
        {!canWrite && <MembershipDetailsButton membershipId={membership.id} showEquipment={showEquipment} tags={membership.tags ?? []} openJob={openJob} />}
      </div>
      {!compact && <>
      <p className="mt-2 font-semibold text-[var(--ink)]">
        {membership.membershipTypeName || membership.recurrence || "Membership"}
        {membership.recurrence && membership.membershipTypeName ? ` · ${membership.recurrence}` : ""}
        {membership.priceCents != null ? ` · ${money(membership.priceCents)}` : ""}
      </p>
      <p className="mt-1 text-xs text-[var(--ink-soft)]">{membership.startDate ? calendarDate(membership.startDate) : "Start not recorded"} – {membership.endDate ? calendarDate(membership.endDate) : "End not recorded"}</p>
      <p className="mt-1 text-xs text-[var(--ink-soft)]">
        {[
          membership.nextServiceDate
            ? `Next service ${calendarDate(membership.nextServiceDate)}`
            : "No visit scheduled",
          membership.nextBillingDate
            ? `bills ${calendarDate(membership.nextBillingDate)}`
            : "",
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      </>}
      {showEquipment && (
        <div className="mt-3 rounded-lg border border-[var(--line)] bg-[var(--wash)] p-3">
          {detailError ? (
            <p className="text-xs font-semibold text-[var(--st-conflict)]">
              {detailError}
            </p>
          ) : detail ? (
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                  Visits
                </p>
                <p className="mt-1 text-[13px] text-[var(--ink-soft)]">
                  {membershipVisitStatus(membership, detail)}
                </p>
                {canBookRemainingService ? (
                  <button
                    type="button"
                    onClick={bookRemainingService}
                    className="mt-2 rounded-lg bg-[var(--ink)] px-3 py-2 text-xs font-semibold text-[var(--paper)]"
                  >
                    Book remaining service
                  </button>
                ) : !membership.id.startsWith("st-membership-") && remainingVisits === 0 ? (
                  <p className="mt-1 text-xs font-semibold text-[var(--ink-faint)]">
                    No remaining included service.
                  </p>
                ) : null}
              </div>
              <div className="space-y-3 pt-3">
              <MembershipServiceCoverage services={detail.services} historical={isHistoricalMembershipCoverage(detail)}/>
            {detail.memo&&<section><h3 className="font-semibold">Plan notes</h3><p className="whitespace-pre-wrap text-sm">{detail.memo}</p></section>}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                  Covered equipment
                </p>
                {coveredEquipment.length ? (
                  <div className="mt-1 space-y-1">
                    {coveredEquipment.map((label) => (
                      <p key={label} className="text-[13px] text-[var(--ink-soft)]">
                        {label}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-[13px] text-[var(--ink-faint)]">
                    No equipment coverage recorded on this membership.
                  </p>
                )}
              </div>
              <MembershipVisitHistory events={detail.events} seasonalVisits={detail.seasonalVisits} canSchedule={false} onSchedule={() => {}} openJob={id => { if (openCustomerJob) openCustomerJob(id); else if (openJob) window.location.assign(openJob(id)); }} jobHref={openCustomerJob ? undefined : openJob} />
              <MembershipRelatedInvoices invoices={detail.relatedInvoices}/>
              </div>
            </div>
          ) : (
            <p className="text-xs text-[var(--ink-faint)]">
              Loading membership visits and covered equipment…
            </p>
          )}
        </div>
      )}

      {planEditor}
      {renewalDialog}
    </div>
  );
}

type CustomerDocumentNumber = { displayNumber?: string | null; status?: string };

export function customerDocumentNumber(item: CustomerDocumentNumber) {
  return item.displayNumber?.trim() || null;
}

export function customerDocumentLabel(
  label: "Invoice" | "Estimate",
  item: CustomerDocumentNumber,
) {
  const number = customerDocumentNumber(item);
  if (number) return `${label} #${number}`;
  return label === "Invoice" && item.status === "draft" ? "Draft" : `${label} number unavailable`;
}

export function RecordList({
  items,
  label,
  onOpen,
  renderDetails,
  inlineDetails = false,
}: {
  items: Array<{
    id: string;
    status: string;
    totalCents: number;
    displayNumber?: string | null;
    balanceDueCents?: number;
    createdAt: string;
  }>;
  label: "Invoice" | "Estimate";
  onOpen?: (id: string) => void;
  renderDetails?: (id: string) => import("react").ReactNode;
  inlineDetails?: boolean;
}) {
  return (
    <div className="space-y-3">
      {items.slice(0, 6).map((item) => {
        const content = <>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-[var(--ink)]">
              {customerDocumentLabel(label, item)}
            </span>
            <span className="mt-1 block text-xs text-[var(--ink-soft)]">
              {date(item.createdAt)}{!inlineDetails && <> · <span className="capitalize">{item.status}</span></>}
            </span>
          </span>
          <span className="shrink-0 text-right">
            <span className="block text-sm font-semibold">{money(item.totalCents)}</span>
            {item.balanceDueCents !== undefined && (
              <span className="mt-1 block text-xs text-[var(--ink-soft)]">
                Due {money(item.balanceDueCents)}
              </span>
            )}
          </span>
        </>;
        return <div key={item.id} className={inlineDetails ? "location-estimate-row" : undefined} role={inlineDetails ? "group" : undefined} aria-label={inlineDetails ? customerDocumentLabel(label, item) : undefined}>{onOpen ? (
          <button type="button" onClick={() => onOpen(item.id)}
            aria-label={customerDocumentLabel(label, item)}
            className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-[var(--wash)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]">
            {content}
          </button>
        ) : <div className="flex items-center justify-between gap-3">{content}</div>}
          {renderDetails?.(item.id)}
        </div>;
      })}
      {items.length > 6 && (
        <p className="pt-1 text-xs text-[var(--ink-soft)]">
          {items.length - 6} older {label.toLowerCase()}s not shown
        </p>
      )}
      {!items.length && <Empty text={`No ${label.toLowerCase()}s yet.`} />}
    </div>
  );
}

function GroupedRecordList({
  items,
  properties,
  label,
  onOpen,
}: {
  items: Array<{
    id: string;
    status: string;
    totalCents: number;
    displayNumber?: string | null;
    balanceDueCents?: number;
    createdAt: string;
    propertyId?: string | null;
  }>;
  properties: CustomerDetail["properties"];
  label: "Invoice" | "Estimate";
  onOpen?: (id: string) => void;
}) {
  if (properties.length < 2 || !items.some((item) => item.propertyId)) {
    return <RecordList items={items} label={label} onOpen={onOpen} />;
  }
  const groups = properties
    .map((property) => ({
      property,
      items: items.filter((item) => item.propertyId === property.id),
    }))
    .filter((group) => group.items.length);
  const unknown = items.filter(
    (item) =>
      !item.propertyId ||
      !properties.some((property) => property.id === item.propertyId),
  );
  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.property.id}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
            {group.property.addressLine1}
          </p>
          <RecordList items={group.items} label={label} onOpen={onOpen} />
        </div>
      ))}
      {unknown.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
            Unassigned
          </p>
          <RecordList items={unknown} label={label} onOpen={onOpen} />
        </div>
      )}
    </div>
  );
}
type DispatchBoardProps = {
  loadMemberships?: MembershipLoader;
  loadDispatchJobContext?: (jobId: string) => Promise<DispatchJobContext>;
  loadTeams?: () => Promise<BoardTeam[]>;
  onScheduleComplete?: () => void;
  items: DispatchItem[];
  untimedJobs?: UntimedDispatchJob[];
  technicians: Technician[];
  blocks: ScheduleBlock[];
  week: DispatchWeek | null;
  proposals: DispatchProposal[];
  timezone: string;
  defaultBookingDurationMinutes?: number;
  proposalBusy: string | null;
  date: string;
  setDate: (value: string) => void;
  jobs: JobOption[];
  searchJobs: (query: string) => Promise<void>;
  searchCustomers: (query: string) => Promise<BookingCustomer[]>;
  loadProperties: (customerId: string) => Promise<BookingProperty[]>;
  loadContacts: (
    customerId: string,
    propertyId?: string | null,
  ) => Promise<BookingContact[]>;
  loadJobTypes: () => Promise<BookingJobType[]>;
  appointmentActions?: boolean;
  canConfirmAppointments?: boolean;
  save: (
    path: string,
    method: "POST" | "PATCH" | "PUT",
    body: unknown,
  ) => Promise<{ warning?: string | null }>;
  open: (id: string) => string;
  openJob: (id: string) => string;
  onOpenJob?: (id: string, focus?: string) => void;
  onOpenEstimate?: (id: string) => void;
  onOpenCustomer?: (id: string) => void;
  onOpenInvoice?: (invoiceId: string, jobId: string, customerId: string) => void;
  bookingCustomer: BookingCustomer | null;
  bookingCaller?: BookingCaller | null;
  clearBookingCustomer: () => void;
  approve: (id: string) => void;
  reject: (id: string) => void;
};

type LanePrefill = { technicianId: string; startsAt: string; endsAt: string };

/*
 * The office's initial time window sets the board zoom and starting scroll
 * position. The full day stays reachable on either side of that window.
 */
export type BoardHours = { from: number; to: number };
const HOURS_KEY = "wv:board-hours";

function readHours(): BoardHours {
  try {
    const raw = JSON.parse(
      window.localStorage.getItem(HOURS_KEY) || "null",
    ) as BoardHours | null;
    if (
      raw &&
      Number.isInteger(raw.from) &&
      Number.isInteger(raw.to) &&
      raw.from >= 0 &&
      raw.to <= 24 &&
      raw.to > raw.from
    )
      return raw;
  } catch {
    /* private mode */
  }
  return { from: 8, to: 18 };
}

function writeHours(hours: BoardHours) {
  try {
    window.localStorage.setItem(HOURS_KEY, JSON.stringify(hours));
  } catch {
    /* private mode */
  }
}

const hourLabel = (hour: number) =>
  hour === 0
    ? "12a"
    : hour === 12
      ? "12p"
      : hour === 24
        ? "12a"
        : hour > 12
          ? `${hour - 12}p`
          : `${hour}a`;

export type BoardTeam = { id: string; name: string; technicianIds: string[]; jobTypeIds: string[] };
const loadDispatchTeams = async () => (await data<{ items: BoardTeam[] }>("/api/v1/workspace/dispatch/teams")).items;

function DispatchTechnicianSearch({ onSearchChange }: { onSearchChange: (value: string) => void }) {
  const [value, setValue] = useState("");

  // Rendering a populated schedule involves every lane and appointment. Keep
  // the controlled input independent so keystrokes do not wait for that work;
  // the board catches up shortly after the dispatcher pauses typing.
  useEffect(() => {
    const timer = window.setTimeout(() => onSearchChange(value), 150);
    return () => window.clearTimeout(timer);
  }, [onSearchChange, value]);

  return (
    <input
      type="search"
      aria-label="Search technicians"
      placeholder="Search…"
      value={value}
      onChange={(event) => setValue(event.target.value)}
      className="dispatch-technician-search"
    />
  );
}

export function DispatchControlRoom({
  loadMemberships,
  loadDispatchJobContext,
  loadTeams = loadDispatchTeams,
  onScheduleComplete,
  items,
  untimedJobs = [],
  technicians,
  blocks,
  proposals,
  timezone,
  defaultBookingDurationMinutes = 120,
  proposalBusy,
  date: boardDate,
  setDate,
  jobs,
  searchJobs,
  searchCustomers,
  loadProperties,
  loadContacts,
  loadJobTypes,
  appointmentActions = false,
  canConfirmAppointments = false,
  save: persist,
  open,
  openJob,
  onOpenJob,
  onOpenCustomer,
  onOpenEstimate,
  onOpenInvoice,
  bookingCustomer,
  bookingCaller,
  clearBookingCustomer,
  approve,
  reject,
}: DispatchBoardProps) {
  const { openTechnicianText } = useRecordCommunications();
  const [onCallTechnicianId, setOnCallTechnicianId] = useState<string | null>(null);
  const [boardNow, setBoardNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setBoardNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const historyToday = isoToCompanyLocal(new Date(boardNow).toISOString(), timezone).slice(0, 10);
  // Closed visits remain visible through the end of their scheduled day as
  // read-only history. Dispatchers need to see today's completed work without
  // losing the protection against moving or resizing it.
  const visibleItems = useMemo(
    () => items.filter(item => boardDate <= historyToday || !['completed', 'cancelled', 'no_show'].includes(item.status)),
    [items, boardDate, historyToday],
  );
  const membershipLookup = useMemo(() => loadMemberships ? shareMembershipLookups(loadMemberships) : undefined, [loadMemberships, boardDate]);
  const [overlap, setOverlap] = useState<{
    message: string;
    path: string;
    method: "POST" | "PATCH" | "PUT";
    body: unknown;
    resolve: (value: any) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const [overlapBusy, setOverlapBusy] = useState(false);
  const [overlapError, setOverlapError] = useState("");
  const cancelOverlap = () => {
    overlap?.reject(Object.assign(new Error("Booking cancelled. Choose another time or technician."), { shown: true }));
    setOverlap(null);
  };
  const [bookingWarning, setBookingWarning] = useState<string | null>(null);
  const save: DispatchBoardProps["save"] = async (path, method, body) => {
    try {
      const result = await persist(path, method, body);
      setOverlap(null);
      // a saver that answers with nothing (the tests' does) is not a warning
      if (result?.warning) setBookingWarning(result.warning);
      return result;
    } catch (error) {
      if (
        appointmentActions &&
        error instanceof Error &&
        (error as Error & { code?: string }).code === "SCHEDULING_CONFLICT"
      ) {
        setOverlapError("");
        return await new Promise((resolve, reject) => {
          setOverlap({ message: error.message, path, method, body, resolve, reject });
        });
      }
      throw error;
    }
  };
  const [panel, setPanel] = useState<
    "none" | "roster" | "block" | "appointment" | "technician"
  >("none");
  const bookingDuration = defaultBookingDurationMinutes;
  const [selected, setSelected] = useState<DispatchItem | null>(null);
  const [jobPreview, setJobPreview] = useState<{ id: string; technicianId: string; trigger: HTMLButtonElement } | null>(null);
  const [reassign, setReassign] = useState<{ id: string; x: number; y: number; trigger: HTMLElement } | null>(null);
  const [reassignBusy, setReassignBusy] = useState(false);
  const [unbookableSlot, setUnbookableSlot] = useState<LanePrefill | null>(null);
  const [estimateToSchedule, setEstimateToSchedule] = useState<string | null>(null);
  const [startEstimateBooking, setStartEstimateBooking] = useState(false);
  const [queueRevision, setQueueRevision] = useState(0);
  const [soldWorkToSchedule, setSoldWorkToSchedule] = useState<UntimedDispatchJob | null>(null);
  const [appointmentPrefill, setAppointmentPrefill] =
    useState<LanePrefill | null>(null);
  const [blockTechnicianId, setBlockTechnicianId] = useState("");
  const [selectedTechnician, setSelectedTechnician] = useState<Technician | null>(null);
  const [eventDraft, setEventDraft] = useState<{ event: ScheduleBlock | null; technicianId: string } | null>(null);
  const [dropHint, setDropHint] = useState<{
    technicianId: string;
    minutes: number;
    durationMinutes: number;
  } | null>(null);
  const [slotHint, setSlotHint] = useState<{
    technicianId: string;
    minutes: number;
  } | null>(null);
  const [resizing, setResizing] = useState<{
    id: string;
    endMinutes: number;
  } | null>(null);
  const justResized = useRef(false);
  const resizeCleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => resizeCleanup.current?.(), []);
  const dragged = useRef<DispatchItem | null>(null);
  const draggedOffsetMinutes = useRef(0);
  const draggedTechnician = useRef<string | null>(null);
  const draggedFromTeam = useRef<string | null>(null);
  const boardScrollRef = useRef<HTMLElement>(null);
  const unassignedRowRef = useRef<HTMLDivElement>(null);
  const [waitingFiltersTarget, setWaitingFiltersTarget] = useState<HTMLDivElement | null>(null);
  const [schedulingFilter, setSchedulingFilter] = useState<WaitingFilter>("all");
  const [pendingSoldEstimateCount, setPendingSoldEstimateCount] = useState(0);
  const schedulingQueueRef = useRef<HTMLDivElement>(null);
  const schedulingQueueId = useId();
  const dragScrollFrame = useRef<number | null>(null);
  const dragPointerY = useRef<number | null>(null);
  const stopDragScroll = () => {
    if (dragScrollFrame.current !== null) cancelAnimationFrame(dragScrollFrame.current);
    dragScrollFrame.current = null;
    dragPointerY.current = null;
  };
  useEffect(() => {
    const over = (event: globalThis.DragEvent) => {
      if (!draggedTechnician.current && !dragged.current) return;
      const board = boardScrollRef.current;
      if (!board) return;
      const rect = board.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top - 64 || event.clientY > rect.bottom + 64) { stopDragScroll(); return; }
      event.preventDefault();
      dragPointerY.current = event.clientY;
      if (dragScrollFrame.current !== null) return;
      let previousFrame = performance.now();
      const tick = (now: number) => {
        const elapsed = Math.min(100, Math.max(1, now - previousFrame));
        previousFrame = now;
        if ((!draggedTechnician.current && !dragged.current) || dragPointerY.current === null || !boardScrollRef.current) { stopDragScroll(); return; }
        const area = boardScrollRef.current;
        const bounds = area.getBoundingClientRect();
        const y = dragPointerY.current;
        const top = Math.max(0, bounds.top) + 76;
        const bottom = Math.min(window.innerHeight, bounds.bottom) - 64;
        const velocity = y < top ? -Math.min(24, Math.max(3, (top - y) / 3)) : y > bottom ? Math.min(24, Math.max(3, (y - bottom) / 3)) : 0;
        area.scrollTop += velocity * elapsed / (1000 / 60);
        dragScrollFrame.current = requestAnimationFrame(tick);
      };
      dragScrollFrame.current = requestAnimationFrame(tick);
    };
    document.addEventListener("dragover", over);
    document.addEventListener("drop", stopDragScroll);
    document.addEventListener("dragend", stopDragScroll);
    return () => { document.removeEventListener("dragover", over); document.removeEventListener("drop", stopDragScroll); document.removeEventListener("dragend", stopDragScroll); stopDragScroll(); };
  }, []);
  const [teams, setTeams] = useState<BoardTeam[]>([]);
  const [teamsReady, setTeamsReady] = useState(false);
  const [teamError, setTeamError] = useState("");
  const [teamNotice, setTeamNotice] = useState("");
  const membershipBusyRef = useRef(false);
  const [teamAttempt, setTeamAttempt] = useState(0);
  const [editingTeam, setEditingTeam] = useState<BoardTeam | null>(null);
  const [technicianSearch, setTechnicianSearch] = useState("");
  const updateTechnicianSearch = useCallback((value: string) => {
    setTechnicianSearch(value);
  }, []);
  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(() => new Set());
  const toggleTeam = (id: string) => setCollapsedTeams((current) => {
    const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  useEffect(() => {
    let live = true;
    void loadTeams().then((items) => { if (live) { setTeams(items); setTeamsReady(true); setTeamError(""); } })
      .catch(() => { if (live) setTeamError("Unable to load company teams."); });
    return () => { live = false; };
  }, [loadTeams, teamAttempt]);
  const saveTeam = async (team: BoardTeam, active = true) => {
    const { id, ...fields } = team;
    const result = await persist(id ? `/api/v1/workspace/dispatch/teams/${id}` : "/api/v1/workspace/dispatch/teams", id ? "PUT" : "POST", { ...fields, active }) as { item: BoardTeam };
    if (!result?.item) throw new Error("Unable to save team");
    setTeams((current) => !active ? current.filter((item) => item.id !== id) : current.some((item) => item.id === result.item.id) ? current.map((item) => item.id === result.item.id ? result.item : item) : [...current, result.item]);
  };
  const [hours, setHours] = useState<BoardHours>(readHours);
  const [dropTeam, setDropTeam] = useState<string | null>(null);
  // Read refreshed data while the clicked appointment is open.
  const selectedItem = selected ? items.find(item => item.id === selected.id) : undefined;
  const reassignItem = reassign ? items.find(item => item.id === reassign.id) : undefined;
  const previewAppointment = jobPreview ? items.find(item => item.id === jobPreview.id) : undefined;
  const previewItem = previewAppointment ? dispatchItemForTechnician(previewAppointment, jobPreview?.technicianId) : undefined;
  useEffect(() => {
    writeHours(hours);
  }, [hours]);
  const addTeam = () => setEditingTeam({ id: "", name: "", technicianIds: [], jobTypeIds: [] });
  const saveMembership = async (technicianId: string, teamIds: string[]) => {
    if (membershipBusyRef.current) throw new Error("Wait for the current team change to finish.");
    membershipBusyRef.current = true;
    setTeamError("");
    try {
      const result = await persist(`/api/v1/workspace/dispatch/teams/technicians/${technicianId}`, "PUT", { teamIds }) as { items: BoardTeam[] };
      if (!result?.items) throw new Error("Unable to save team assignment");
      setTeams((current) => {
        const byId = new Map(result.items.map((team) => [team.id, team]));
        return [...current.flatMap((team) => byId.has(team.id) ? [byId.get(team.id)!] : []), ...result.items.filter((team) => !current.some((existing) => existing.id === team.id))];
      });
      setTeamNotice(`${technicians.find((item) => item.id === technicianId)?.name || "Technician"}’s teams updated.`);
    } finally { membershipBusyRef.current = false; }
  };
  const moveTechnician = (technicianId: string | null, teamId: string | null) => {
    if (!technicianId || teamId === draggedFromTeam.current) return;
    const memberships = teams.filter((team) => team.technicianIds.includes(technicianId) && team.id !== draggedFromTeam.current).map((team) => team.id);
    if (teamId && !memberships.includes(teamId)) memberships.push(teamId);
    void saveMembership(technicianId, memberships).catch((error) => setTeamError(error instanceof Error ? error.message : "Unable to move technician"));
  };
  const companyToday = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(boardNow));
  const unassigned = visibleItems.filter((item) => !item.technicianIds.length);
  const unassignedCount = unassigned.filter(item => !["completed", "cancelled", "no_show"].includes(item.status) && isoToCompanyLocal(item.scheduledStart, timezone).slice(0, 10) === boardDate).length;
  const pendingProposals = proposals.filter(
    (proposal) => proposal.status === "pending",
  );
  const hourOf = (value: string) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(value));
    const record = Object.fromEntries(
      parts.map((part) => [part.type, part.value]),
    );
    return Number(record.hour) + Number(record.minute) / 60;
  };
  // Keep the full day reachable. The saved hours set the initial view and
  // zoom, rather than cutting earlier and later hours out of the timeline.
  const startHour = 0;
  const endHour = 24;
  const span = endHour - startHour;
  const [boardViewportWidth, setBoardViewportWidth] = useState(920);
  const hourWidth = Math.max(128, (boardViewportWidth - 200) / (hours.to - hours.from));
  useEffect(() => {
    const board = boardScrollRef.current;
    if (!board) return;
    const measure = () => setBoardViewportWidth(board.clientWidth || 920);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(board);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const board = boardScrollRef.current;
    if (board) board.scrollLeft = hours.from * hourWidth;
  }, [hours.from, hourWidth]);
  const position = (value: string) =>
    Math.max(0, Math.min(100, ((hourOf(value) - startHour) / span) * 100));
  const shiftDay = (offset: number) => {
    const value = new Date(`${boardDate}T12:00:00`);
    value.setDate(value.getDate() + offset);
    setDate(value.toISOString().slice(0, 10));
  };
  const submit = async (
    event: FormEvent<HTMLFormElement>,
    action: () => Promise<void>,
  ) => {
    event.preventDefault();
    try {
      await action();
      setPanel("none");
      setAppointmentPrefill(null);
      clearBookingCustomer();
    } catch {}
  };
  const closePanel = () => {
    if (overlap) return;
    setPanel("none");
    setAppointmentPrefill(null);
    clearBookingCustomer();
  };
  useEffect(() => {
    if (panel === "none") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !overlap) closePanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panel, overlap]);
  const openSchedulePanel = (prefill: LanePrefill | null) => {
    setAppointmentPrefill(prefill);
    setPanel("appointment");
    void searchJobs("");
  };
  useEffect(() => {
    if (bookingCustomer || bookingCaller) openSchedulePanel(null);
  }, [bookingCustomer, bookingCaller]);
  /**
   * The office books two-hour windows, so the lane is two-hour slots. The
   * cursor lights the slot it is over and a click books that slot — the old
   * lane was one long blank strip that snapped to the nearest half hour, and
   * nobody could tell what they were about to schedule.
   */
  const laneSlotStart = (event: {
    clientX: number;
    currentTarget: HTMLElement;
  }) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = rect.width
      ? Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
      : 0;
    return Math.min(
      24 * 60 - bookingDuration,
      Math.max(
        0,
        Math.floor(((startHour + ratio * span) * 60) / bookingDuration) *
          bookingDuration,
      ),
    );
  };
  /**
   * Is this two-hour window actually free for him — inside his hours, and no
   * job already on it? The lane offers nothing it cannot keep.
   */
  // This calculation walks every appointment and schedule block. Keep it
  // stable while the live clock, hover state, or drag state rerenders the
  // board; it only needs to change with the actual schedule data.
  const openWindowsByTechnician = useMemo(
    () =>
      new Map(
        technicians.map((technician) => [
          technician.id,
          technicianOpenWindows(blocks, items, technician.id),
        ]),
      ),
    [technicians, blocks, items],
  );
  // Paint the same complete appointment windows that a lane click can book.
  // A partial hour at either end of a shift stays unavailable.
  const bookableSlotsByTechnician = useMemo(() => {
    const slots = new Map<string, Array<{ minutes: number; startsAt: string; endsAt: string; kind: "working" | "on_call" }>>();
    const candidates = [];
    if (!Number.isFinite(bookingDuration) || bookingDuration <= 0) return slots;
    for (let minutes = 0; minutes + bookingDuration <= 24 * 60; minutes += bookingDuration) {
      const startsAt = companyLocalToIso(`${boardDate}T${minutesToLocalTime(minutes)}`, timezone);
      const from = Date.parse(startsAt);
      const to = from + bookingDuration * 60_000;
      candidates.push({ minutes, startsAt, endsAt: new Date(to).toISOString(), from, to });
    }
    for (const [technicianId, windows] of openWindowsByTechnician) {
      const hours = blocks.filter(block => block.technicianId === technicianId && (block.kind === "working" || block.kind === "on_call"));
      const available = [];
      for (const { minutes, startsAt, endsAt, from, to } of candidates) {
        if (!windows.some(window => Date.parse(window.startsAt) <= from && Date.parse(window.endsAt) >= to)) continue;
        const covering = hours.filter(block => Date.parse(block.startsAt) <= from && Date.parse(block.endsAt) >= to);
        const kind = covering.some(block => block.kind === "working") ? "working" as const : "on_call" as const;
        available.push({ minutes, startsAt, endsAt, kind });
      }
      slots.set(technicianId, available);
    }
    return slots;
  }, [openWindowsByTechnician, blocks, boardDate, timezone, bookingDuration]);
  const slotIsOpen = (technicianId: string, minutes: number) =>
    (bookableSlotsByTechnician.get(technicianId) ?? []).some(slot => slot.minutes === minutes);
  const slotLeft = (minutes: number) =>
    ((minutes / 60 - startHour) / span) * 100;
  const slotWidth = (bookingDuration / 60 / span) * 100;
  const laneClick = (
    technicianId: string,
    event: MouseEvent<HTMLDivElement>,
  ) => {
    if (event.target !== event.currentTarget) return;
    if (justResized.current) {
      justResized.current = false;
      return;
    }
    const minutes = laneSlotStart(event);
    const prefill = {
      technicianId,
      startsAt: `${boardDate}T${minutesToLocalTime(minutes)}`,
      endsAt: `${boardDate}T${minutesToLocalTime(Math.min(24 * 60 - 15, minutes + bookingDuration))}`,
    };
    if (!slotIsOpen(technicianId, minutes)) {
      setUnbookableSlot(prefill);
      return;
    }
    openSchedulePanel(prefill);
  };
  const laneDropMinutes = (event: DragEvent<HTMLDivElement>) => {
    const laneRect = event.currentTarget.getBoundingClientRect();
    return snapLaneMinutes(
      laneRect.width ? (event.clientX - laneRect.left) / laneRect.width : 0,
      startHour - draggedOffsetMinutes.current / 60,
      span,
      15,
    );
  };
  const laneDrop = async (
    technicianId: string,
    event: DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    setDropHint(null);
    setSlotHint(null);
    const item = dragged.current;
    dragged.current = null;
    if (!item) return;
    const dropMinutes = laneDropMinutes(event);
    const startsAt = companyLocalToIso(
      `${boardDate}T${minutesToLocalTime(dropMinutes)}`,
      timezone,
    );
    const endsAt = new Date(
      Date.parse(startsAt) +
        Math.max(
          900_000,
          Date.parse(item.scheduledEnd) - Date.parse(item.scheduledStart),
        ),
    ).toISOString();
    const movedTime =
      Math.abs(Date.parse(startsAt) - Date.parse(item.scheduledStart)) >=
      60_000;
    const movedTechnician = item.technicianIds[0] !== technicianId;
    try {
      if (movedTime)
        await save(
          `/api/v1/workspace/dispatch/appointments/${item.id}/time`,
          "PATCH",
          { startsAt, endsAt },
        );
      if (movedTechnician)
        await save(
          `/api/v1/workspace/dispatch/appointments/${item.id}/assignments`,
          "PUT",
          { technicianIds: [technicianId] },
        );
    } catch {}
  };
  /**
   * DRAG THE RIGHT EDGE TO CHANGE HOW LONG A JOB IS. Dragging the block moves
   * it and keeps its length; the only way to say "this one is going to take
   * four hours" was to open the flyout and pick a duration from a list. The
   * handle writes the same PATCH the flyout does — the start does not move,
   * the duration changes in 15-minute steps as the edge moves.
   */
  const beginResize = (
    item: DispatchItem,
    event: PointerEvent<HTMLSpanElement>,
  ) => {
    if (event.button !== 0 || resizing || resizeCleanup.current) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const lane = handle.closest(
      "[data-lane]",
    ) as HTMLElement | null;
    if (!lane) return;
    const rect = lane.getBoundingClientRect();
    if (!rect.width) return;
    setSlotHint(null);
    handle.setPointerCapture(event.pointerId);
    const startMinutes = Math.round(hourOf(item.scheduledStart) * 60);
    const originalEnd = Math.round(hourOf(item.scheduledEnd) * 60) || 24 * 60;
    const initialX = event.clientX;
    const endAt = (clientX: number) => {
      const delta = Math.round(((clientX - initialX) / rect.width * span * 60) / 15) * 15;
      return Math.max(startMinutes + 15, Math.min(24 * 60 - 15, originalEnd + delta));
    };
    const move = (moveEvent: globalThis.PointerEvent) => {
      if (moveEvent.pointerId !== event.pointerId) return;
      setResizing({ id: item.id, endMinutes: endAt(moveEvent.clientX) });
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", drop);
      window.removeEventListener("pointercancel", cancel);
      handle.removeEventListener("lostpointercapture", cancel);
      if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
      resizeCleanup.current = null;
    };
    const cancel = (cancelEvent: globalThis.PointerEvent) => {
      if (cancelEvent.pointerId !== event.pointerId) return;
      cleanup();
      setResizing(null);
    };
    const drop = (upEvent: globalThis.PointerEvent) => {
      if (upEvent.pointerId !== event.pointerId) return;
      cleanup();
      justResized.current = true;
      window.setTimeout(() => { justResized.current = false; }, 0);
      const endMinutes = endAt(upEvent.clientX);
      if (upEvent.clientX === initialX || endMinutes === originalEnd) {
        setResizing(null);
        return;
      }
      setResizing({ id: item.id, endMinutes });
      void save(
        `/api/v1/workspace/dispatch/appointments/${item.id}/time`,
        "PATCH",
        {
          startsAt: item.scheduledStart,
          endsAt: companyLocalToIso(
            `${boardDate}T${minutesToLocalTime(endMinutes)}`,
            timezone,
          ),
        },
      )
        .catch(() => undefined)
        .finally(() => setResizing(null));
    };
    resizeCleanup.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", drop);
    window.addEventListener("pointercancel", cancel);
    handle.addEventListener("lostpointercapture", cancel);
  };
  const jobBlock = (item: DispatchItem, row: number, technicianId: string) => {
    const rowItem = dispatchItemForTechnician(item, technicianId);
    const closed = ["completed", "cancelled", "canceled", "no_show"].includes(item.status);
    const left = position(item.scheduledStart);
    const stretching = resizing?.id === item.id;
    const right = stretching && resizing
      ? slotLeft(resizing.endMinutes)
      : position(item.scheduledEnd);
    const blockHeight = 40;
    const elapsed = technicianId ? dispatchElapsedSegments(item, technicianId, boardDate, timezone, startHour, endHour, boardNow) : [];
    return (
      <div key={item.id} className="contents">
      <div
        className={`dispatch-job-block group/block absolute z-20 overflow-hidden rounded-md ${appointmentTone(rowItem)} ${appointmentAlert(rowItem, timezone) ? "ring-2 ring-inset ring-[var(--st-alert)]" : ""} ${stretching ? "ring-2 ring-inset ring-[var(--accent)]" : ""}`}
        onContextMenu={event => {
          if (!appointmentActions || ["completed", "cancelled", "canceled", "no_show"].includes(item.status)) return;
          event.preventDefault(); event.stopPropagation();
          if (reassignBusy) return;
          setJobPreview(null);
          const trigger = event.currentTarget.querySelector<HTMLButtonElement>("button") || event.currentTarget;
          const rect = trigger.getBoundingClientRect();
          setReassign({ id: item.id, x: event.clientX || rect.left, y: event.clientY || rect.bottom, trigger });
        }}
        style={{
          left: `${left}%`,
          width: `${Math.max(0.5, right - left)}%`,
          top: `${4 + row * 44}px`,
          height: `${blockHeight}px`,
        }}
      >
        <button
          type="button"
          draggable={!closed}
          onDragStart={(event) => {
            if (closed || reassignBusy) { event.preventDefault(); return; }
            setReassign(null);
            dragged.current = item;
            setJobPreview(null);
            setSlotHint(null);
            const lane = event.currentTarget.closest("[data-lane]");
            const rect = lane?.getBoundingClientRect();
            draggedOffsetMinutes.current = rect?.width
              ? (startHour + ((event.clientX - rect.left) / rect.width) * span) * 60 - hourOf(item.scheduledStart) * 60
              : 0;
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", item.id);
          }}
          onDragEnd={() => {
            stopDragScroll();
            dragged.current = null;
            setDropHint(null);
            setSlotHint(null);
          }}
          onClick={() => {
            setJobPreview(null);
            // The schedule is an entry point to the job card. Keep the full
            // record as the primary action; its route can paint the card while
            // the heavier sections finish loading.
            if (item.jobId && onOpenJob) onOpenJob(item.jobId);
            else setSelected(item);
          }}
          aria-label={`${item.jobId ? `#${item.jobNumber}` : `${item.ownerType} appointment`} ${item.customerName} ${appointmentAlert(rowItem, timezone) || appointmentLabel(rowItem)} ${item.jobTypeName?.trim() || ""}`}
          className={`focus-visible:ring-2 focus-visible:ring-[var(--accent)] block h-full w-full overflow-hidden px-2.5 py-0.5 text-left text-[12px] font-semibold leading-tight ${closed ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"}`}
        >
          <span className="dispatch-job-heading">
            <span className="dispatch-job-name truncate">{item.jobId ? <span className="invisible" aria-hidden="true">#{item.jobNumber}</span> : `${item.ownerType} appointment`} {item.customerName}</span>
          <span className="dispatch-job-badges">
            {Boolean(item.serviceZoneNames?.length) && <span className="dispatch-zone" title={`Zone: ${item.serviceZoneNames!.join(', ')}`}>{item.serviceZones?.map((zone) => <span key={zone.id} className="dispatch-zone-dot" style={{ backgroundColor: zone.color }} title={zone.name} aria-label={`${zone.name} zone`} />)}{item.serviceZoneNames!.join(', ')}</span>}
            {membershipLookup && dispatchMembershipLabel(item.membershipStatus) && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-emerald-900">{dispatchMembershipLabel(item.membershipStatus)}</span>}
            <LocationTagBadges tags={item.locationTags} compact />
          </span>
          </span>
          <span className="dispatch-job-status mt-0.5 flex items-center gap-1 text-[11px] font-medium">
            <span className="shrink-0 font-semibold">{appointmentAlert(rowItem, timezone) || appointmentLabel(rowItem)}</span>
            <span aria-hidden="true">·</span>
            <span className="min-w-0 truncate">{stretching && resizing
              ? `ends ${readableTime(minutesToLocalTime(Math.min(24 * 60 - 15, resizing.endMinutes)))}`
              : item.jobTypeName?.trim() || formatDispatchTime(item.scheduledStart, timezone)}</span>
          </span>

          {elapsed.length > 0 && <span className="dispatch-job-progress">
            {elapsed.map(segment => <span key={segment.kind} className={`dispatch-elapsed dispatch-elapsed-${segment.kind}`} role="img" aria-label={`${item.customerName}: ${segment.label}`} title={segment.label}
              style={{ flexGrow: segment.width }} />)}
          </span>}
        </button>
        {item.jobId && <button
          type="button"
          className="dispatch-job-preview-trigger"
          aria-label={`Preview job #${item.jobNumber}`}
          aria-haspopup="dialog"
          aria-expanded={jobPreview?.id === item.id && jobPreview.technicianId === technicianId}
          onClick={event => {
            event.stopPropagation();
            const trigger = event.currentTarget;
            setJobPreview(current => current?.id === item.id && current.technicianId === technicianId ? null : { id: item.id, technicianId, trigger });
          }}
        >#{item.jobNumber}</button>}
        {!closed && <span
          role="separator"
          aria-label={`Change how long job ${item.jobNumber} takes`}
          onPointerDown={(event) => beginResize(item, event)}
          onClick={(event) => event.stopPropagation()}
          title="Drag to change how long this takes"
          className="absolute inset-y-0 right-0 w-2.5 touch-none select-none cursor-ew-resize"
        />}
      </div>
      </div>
    );
  };
  const laneHeight = (laneItems: DispatchItem[]) =>
    4 + 44 * Math.max(1, ...stackAppointments(laneItems).map(entry => entry.row + 1));
  const blocksByTechnician = useMemo(() => {
    const grouped = new Map<string, ScheduleBlock[]>();
    for (const block of blocks) {
      const list = grouped.get(block.technicianId) ?? [];
      list.push(block); grouped.set(block.technicianId, list);
    }
    return grouped;
  }, [blocks]);
  const itemsByTechnician = useMemo(() => {
    const grouped = new Map<string, DispatchItem[]>();
    for (const item of visibleItems) for (const id of item.technicianIds) {
      const list = grouped.get(id) ?? [];
      list.push(item); grouped.set(id, list);
    }
    return grouped;
  }, [visibleItems]);
  const technicianRow = (technician: Technician, sourceTeamId: string | null = null) => {
    const technicianBlocks = blocksByTechnician.get(technician.id) ?? [];
    const technicianItems = itemsByTechnician.get(technician.id) ?? [];
    const hours = technicianBlocks.filter(
      (block) => block.kind === "working" || block.kind === "on_call",
    ).sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
    // No hours on the board is no availability. The lane stops taking
    // clicks rather than booking someone who is not working that day.
    const bookable = hours.length > 0;
    return (
      <div
        key={technician.id}
        className={`grid grid-cols-[200px_1fr] border-b border-[var(--line)] last:border-0 ${bookable ? "" : "bg-[var(--wash)]/60"}`}
      >
        <div
          draggable
          onDragStart={(event) => {
            draggedTechnician.current = technician.id;
            draggedFromTeam.current = sourceTeamId;
            event.dataTransfer.setData("text/plain", technician.id);
            event.dataTransfer.effectAllowed = "move";
          }}
          onDragEnd={() => {
            draggedTechnician.current = null;
            draggedFromTeam.current = null;
            stopDragScroll();
            setDropTeam(null);
          }}
          title="Drag onto a team"
          className="dispatch-technician-label sticky left-0 z-[21] flex cursor-grab select-none items-center justify-between gap-2 rounded-lg px-3 py-1 transition-colors active:cursor-grabbing hover:bg-[var(--wash)]"
        >
          <button
            type="button"
            aria-label={technician.name}
            onClick={() => {
              setSelectedTechnician(technician);
              setPanel("technician");
            }}
            className="min-w-0 text-left text-[13px] font-semibold text-[var(--ink)] hover:text-[var(--accent)]"
          >
            <span className="block truncate">{technician.name}</span>
          </button>
          <span className="flex shrink-0 items-center gap-1">
            {technician.phone && (
              <>
                <a
                  href={`tel:${technician.phone.replace(/[^\d+]/g, "")}`}
                  onClick={(event) => event.stopPropagation()}
                  aria-label={`Call ${technician.name}`}
                  title={`Call ${phoneNumber(technician.phone)}`}
                  className="grid size-6 place-items-center rounded-md text-[var(--ink-soft)] transition hover:bg-[var(--accent-wash)] hover:text-[var(--accent)]"
                >
                  <PhoneIcon />
                </a>
                <button
                  type="button"
                  disabled={!openTechnicianText}
                  onClick={(event) => { event.stopPropagation(); openTechnicianText?.({ id: technician.id, name: technician.name, phone: technician.phone! }); }}
                  aria-label={`Text ${technician.name}`}
                  title="Text"
                  className="grid size-6 place-items-center rounded-md text-[var(--ink-soft)] transition hover:bg-[var(--accent-wash)] hover:text-[var(--accent)]"
                >
                  <MessageIcon />
                </button>
              </>
            )}
            {!bookable && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                Off
              </span>
            )}
          </span>
        </div>
        <div
          onClick={(event) => laneClick(technician.id, event)}
          onMouseMove={
            bookable
              ? (event) => {
                  if (dragged.current) return;
                  const minutes = laneSlotStart(event);
                  setSlotHint((current) => {
                    if (current?.technicianId === technician.id && current.minutes === minutes) return current;
                    return slotIsOpen(technician.id, minutes) ? { technicianId: technician.id, minutes } : null;
                  });
                }
              : undefined
          }
          onMouseLeave={() =>
            setSlotHint((current) =>
              current?.technicianId === technician.id ? null : current,
            )
          }
          onDragOver={
            bookable
              ? (event) => {
                  const item = dragged.current;
                  if (!item) return;
                  event.preventDefault();
                  const minutes = laneDropMinutes(event);
                  const durationMinutes = Math.max(15, (Date.parse(item.scheduledEnd) - Date.parse(item.scheduledStart)) / 60_000);
                  setDropHint(current =>
                    current?.technicianId === technician.id && current.minutes === minutes && current.durationMinutes === durationMinutes
                      ? current
                      : { technicianId: technician.id, minutes, durationMinutes },
                  );
                }
              : undefined
          }
          onDragLeave={
            bookable
              ? (event) => {
                  if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
                  setDropHint(current => current?.technicianId === technician.id ? null : current);
                }
              : undefined
          }
          onDrop={
            bookable
              ? (event) => void laneDrop(technician.id, event)
              : undefined
          }
          data-lane={technician.id}
          className={`dispatch-availability--unavailable relative ${bookable ? "cursor-copy" : "cursor-pointer"} ${dropHint?.technicianId === technician.id ? "ring-2 ring-inset ring-[var(--line-strong)]" : ""}`}
          style={{ height: `${laneHeight(technicianItems)}px` }}
        >
          {(bookableSlotsByTechnician.get(technician.id) ?? []).map(slot => (
            <div
              key={slot.minutes}
              role="img"
              aria-label={`${slot.kind === "working" ? "Available" : "After hours"} ${formatDispatchTime(slot.startsAt, timezone)} – ${formatDispatchTime(slot.endsAt, timezone)}`}
              className={`pointer-events-none absolute inset-y-0 ${slot.kind === "working" ? "dispatch-availability--available" : "dispatch-availability--on-call"}`}
              style={{ left: `${slotLeft(slot.minutes)}%`, width: `${slotWidth}%` }}
            />
          ))}
          {technicianBlocks
            .filter((block) => block.kind !== "working" && block.kind !== "on_call")
            .map((block) => {
              const spanOnLane = eventLaneSpan(block, boardDate, timezone, startHour, endHour);
              if (!spanOnLane) return null;
              return (
                <button
                  key={block.id}
                  type="button"
                  onClick={(click) => {
                    click.stopPropagation();
                    setEventDraft({ event: block, technicianId: technician.id });
                  }}
                  className={`absolute inset-y-1.5 z-[12] overflow-hidden rounded-md px-2 text-left text-[11px] font-semibold leading-tight ${
                    block.kind === "meeting"
                      ? "bg-[var(--accent-wash)] text-[var(--accent)]"
                      : "bg-[var(--warn-wash)] text-[var(--warn)]"
                  }`}
                  style={{ left: `${spanOnLane.left}%`, width: `${Math.max(3, spanOnLane.width)}%` }}
                  title={`${block.title} · ${technicianEventLabel(block.kind)}${block.paid ? " · paid" : " · non-paid"}`}
                >
                  <span className="block truncate">{block.title || technicianEventLabel(block.kind)}</span>
                </button>
              );
            })}
          <div
            className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(to_right,rgba(17,17,17,.04)_1px,transparent_1px)]"
            style={{
              backgroundSize: `${(bookingDuration / 60 / span) * 100}% 100%`,
            }}
          />
          {slotHint?.technicianId === technician.id && (
            <div
              className="pointer-events-none absolute inset-y-1.5 z-[5] rounded-lg bg-[var(--accent-wash)] ring-1 ring-inset ring-[var(--accent)]/45"
              style={{
                left: `${slotLeft(slotHint.minutes)}%`,
                width: `${slotWidth}%`,
              }}
            />
          )}
          {dropHint?.technicianId === technician.id && (
            <div
              role="img"
              aria-label={`Drop appointment at ${minutesToLocalTime(dropHint.minutes)}`}
              className="pointer-events-none absolute inset-y-1.5 z-30 rounded-lg bg-[var(--accent-wash)] ring-1 ring-inset ring-[var(--accent)]/45"
              style={{
                left: `${slotLeft(dropHint.minutes)}%`,
                width: `${(dropHint.durationMinutes / 60 / span) * 100}%`,
              }}
            />
          )}
          {stackAppointments(technicianItems).map((entry) =>
            jobBlock(entry.item, entry.row, technician.id),
          )}
          {!technicianItems.length && !bookable && (
            <p className="pointer-events-none absolute inset-0 grid place-items-center text-xs text-[var(--ink-faint)]">
              Not working this day
            </p>
          )}
        </div>
      </div>
    );
  };
  /**
   * A team is a drop target and a divider, nothing else. The office moves
   * people between them mid-morning, so the header takes a dragged
   * technician rather than opening a form to do it.
   */
  const teamHeader = (team: BoardTeam | null) => {
    const key = team ? team.id : "none";
    return (
      <div
        key={`head-${key}`}
        onDragOver={(event) => {
          if (!draggedTechnician.current) return;
          event.preventDefault();
          setDropTeam(key);
        }}
        onDragLeave={() =>
          setDropTeam((current) => (current === key ? null : current))
        }
        onDrop={(event) => {
          event.preventDefault();
          moveTechnician(draggedTechnician.current, team ? team.id : null);
          draggedTechnician.current = null;
          draggedFromTeam.current = null;
          stopDragScroll();
          setDropTeam(null);
        }}
        className={`dispatch-team-header ${team ? "" : "dispatch-team-header--unassigned"} ${dropTeam === key ? "dispatch-team-header--drop" : ""}`}
      >
        <button type="button" aria-expanded={!collapsedTeams.has(key)} aria-label={`${collapsedTeams.has(key) ? "Expand" : "Collapse"} ${team?.name ?? "No team"}`} onClick={() => toggleTeam(key)} className="dispatch-team-toggle flex min-w-0 items-center gap-2 text-[13px] font-semibold">
          <svg aria-hidden="true" viewBox="0 0 16 16" className={`size-4 shrink-0 transition-transform ${collapsedTeams.has(key) ? "-rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m4 6 4 4 4-4" /></svg>
          <span className="truncate">{team?.name ?? "No team"}</span>
        </button>
        {team && <button type="button" aria-label={`${team.name} · Edit team`} onClick={() => setEditingTeam(team)} className="dispatch-team-edit shrink-0 text-xs font-medium">Edit team</button>}
      </div>
    );
  };
  const teamOf = new Map<string, string>();
  teams.forEach((team) =>
    team.technicianIds.forEach((id) => teamOf.set(id, team.id)),
  );
  // Teams are retained for future staffing, but a blank team is noise on a
  // live board. Show it again automatically as soon as a technician is added.
  const staffedTeams = teams.filter((team) => team.technicianIds.length > 0);
  const search = technicianSearch.trim().toLowerCase();
  const boardTechnicians = technicians.filter((technician) =>
    `${technician.name} ${technician.email ?? ""}`.toLowerCase().includes(search),
  );
  const unteamed = boardTechnicians.filter(
    (technician) => !teamOf.has(technician.id),
  );
  const allTeamsCollapsed = staffedTeams.every((team) => collapsedTeams.has(team.id)) && (!unteamed.length || collapsedTeams.has("none"));
  const soldUnscheduled = untimedJobs.filter((job) => !job.waitingOn && job.status === "new" && (job.isSoldUnscheduled || job.needsReturnVisit));
  return (
    <>
      <div className="dispatch-calendar">
      <header className="dispatch-calendar-toolbar">
        <div className="dispatch-toolbar-main">
          <div className="dispatch-toolbar-date flex flex-wrap items-center gap-2">
          <button
            onClick={() => shiftDay(-1)}
            aria-label="Previous day"
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold"
          >
            <ArrowLeft size={16} />
          </button>
          <label className="dispatch-date-control">
            <span className="dispatch-date-icon" aria-hidden="true"><CalendarDays size={16} /></span>
            <span className="dispatch-date-label" aria-label="Selected dispatch date">{new Intl.DateTimeFormat(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${boardDate}T12:00:00Z`))}</span>
            <input
              aria-label="Dispatch date"
              type="date"
              value={boardDate}
              onChange={(event) => { if (event.target.value) setDate(event.target.value); }}
            />
          </label>
          <button
            onClick={() => setDate(companyToday)}
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold"
          >
            Today
          </button>
          <button
            onClick={() => shiftDay(1)}
            aria-label="Next day"
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold"
          >
            <ArrowRight size={16} />
          </button>
          {unassignedCount > 0 && <button
            type="button"
            className="dispatch-unassigned-alert"
            aria-label={`Show ${unassignedCount} unassigned ${unassignedCount === 1 ? "job" : "jobs"} for ${boardDate}`}
            title={`${unassignedCount} unassigned ${unassignedCount === 1 ? "job" : "jobs"} on this day`}
            onClick={() => {
              if (boardScrollRef.current) boardScrollRef.current.scrollTop = 0;
              unassignedRowRef.current?.focus({ preventScroll: true });
            }}
          ><Bell size={16} aria-hidden="true" /><span aria-hidden="true">{unassignedCount}</span></button>}
          </div>
          <div className="dispatch-waiting-filters-slot" ref={setWaitingFiltersTarget} />
          <div className="dispatch-toolbar-actions">
          <button
            type="button"
            onClick={() => setEventDraft({ event: null, technicianId: "" })}
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-4 py-2 text-sm font-semibold"
          >
            Block
          </button>
          <button
            onClick={() => openSchedulePanel(null)}
            className="rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white"
          >
            Book Job
          </button>
          </div>
        </div>
      </header>
      {onCallTechnicianId && <Dialog title="Technician on-call schedule" width="max-w-2xl" onClose={() => setOnCallTechnicianId(null)}><OnCallRotations technicianId={onCallTechnicianId} technicians={technicians} date={boardDate} timezone={timezone} save={save} /></Dialog>}
      {estimateToSchedule && <JobEstimateAllocationAction startBooking={startEstimateBooking} estimateId={estimateToSchedule} onClose={() => setEstimateToSchedule(null)} onDone={(jobId, step) => { setQueueRevision((value) => value + 1); onScheduleComplete?.(); if (jobId) onOpenJob?.(jobId, step ? `job-step:${step}` : undefined); }} />}
      {panel !== "none" && panel !== "appointment" && (
        <Dialog
          onClose={closePanel}
          width="max-w-lg"
          title={
            panel === "roster"
              ? "Technicians"
              : panel === "block"
                ? "Hours and time off"
                : panel === "technician"
                  ? "Technician details"
                  : "Book Job"
          }
        >
          {panel === "roster" && (
            <RosterForm technicians={technicians} save={save} submit={submit} />
          )}
          {panel === "block" && (
            <BlockForm
              technicians={technicians}
              technicianId={blockTechnicianId}
              date={boardDate}
              timezone={timezone}
              defaultKind={selectedTechnician ? "on_call" : "working"}
              onDone={closePanel}
              save={save}
              submit={submit}
            />
          )}
          {panel === "technician" && selectedTechnician && (
            <>
            <TechnicianTeamAssignment key={`${selectedTechnician.id}-${teamsReady}`} technician={selectedTechnician} teams={teams} ready={teamsReady} save={(ids) => saveMembership(selectedTechnician.id, ids)} />
            <TechnicianDispatchProfile technician={selectedTechnician} blocks={blocks.filter((block) => block.technicianId === selectedTechnician.id)} appointments={items.filter((item) => item.technicianIds.includes(selectedTechnician.id))} timezone={timezone} date={boardDate} addOnCall={() => { setBlockTechnicianId(selectedTechnician.id); setPanel("block"); }} manageOnCall={() => { closePanel(); setOnCallTechnicianId(selectedTechnician.id); }} cancelOnCall={(blockId) => { if (blockId.startsWith("rotation:")) { closePanel(); setOnCallTechnicianId(selectedTechnician.id); } else void save(`/api/v1/workspace/dispatch/blocks/${blockId}`, "PATCH", { status: "cancelled" }); }} addEvent={() => { setEventDraft({ event: null, technicianId: selectedTechnician.id }); setPanel("none"); }} openEvent={(block) => { setEventDraft({ event: block, technicianId: selectedTechnician.id }); setPanel("none"); }} />
            </>
          )}
        </Dialog>
      )}
      {editingTeam && <DispatchTeamEditor team={editingTeam} technicians={technicians} loadJobTypes={loadJobTypes} save={saveTeam} close={() => setEditingTeam(null)} />}
      {unbookableSlot && <Dialog title="Not bookable timeslot" onClose={() => setUnbookableSlot(null)} footer={<div className="flex gap-2">
        <QuietButton type="button" onClick={() => setUnbookableSlot(null)}>Choose another time</QuietButton>
        <PrimaryButton type="button" onClick={() => { const prefill = unbookableSlot; setUnbookableSlot(null); openSchedulePanel(prefill); }}>Book anyway</PrimaryButton>
      </div>}>
        <p className="text-sm">{technicians.find((technician) => technician.id === unbookableSlot.technicianId)?.name} is unavailable for this window. It falls outside available hours or overlaps existing work.</p>
        <p className="mt-2 text-sm font-semibold">{readableTime(unbookableSlot.startsAt.slice(11))}–{readableTime(unbookableSlot.endsAt.slice(11))}</p>
      </Dialog>}
      {panel === "appointment" && (
            <BookingForm
              technicians={technicians}
              teams={teams}
              teamsReady={teamsReady}
              blocks={blocks}
              appointments={items}
              date={boardDate}
              timezone={timezone}
              defaultBookingDurationMinutes={bookingDuration}
              prefill={appointmentPrefill}
              bookingCustomer={bookingCustomer}
              bookingCaller={bookingCaller}
              loadMemberships={membershipLookup}
              searchCustomers={searchCustomers}
              loadProperties={loadProperties}
              loadContacts={loadContacts}
              loadJobTypes={loadJobTypes}
              save={save}
              submit={submit}
              onClose={closePanel}
            />
      )}
      {eventDraft && (
        <TechnicianEventDialog
          technicians={technicians}
          timezone={timezone}
          date={boardDate}
          event={eventDraft.event}
          defaultTechnicianId={eventDraft.technicianId}
          save={save}
          onClose={() => setEventDraft(null)}
        />
      )}
      {soldWorkToSchedule && (
        <SoldWorkScheduleDialog
          job={soldWorkToSchedule}
          technicians={technicians}
          date={boardDate}
          timezone={timezone}
          defaultDurationMinutes={bookingDuration}
          save={save}
          onClose={() => setSoldWorkToSchedule(null)}
          onScheduled={() => setSoldWorkToSchedule(null)}
        />
      )}
      {/*
       * The board scrolls inside itself so the clock stays put. Sticky only
       * holds against a scroll container, and a page-height board has none —
       * bounding it here is what lets the hours sit above a long roster.
       */}
      <section ref={boardScrollRef} aria-label="Dispatch board" className="dispatch-calendar-scroll max-h-[calc(100dvh-200px)] overflow-auto bg-[var(--paper)]">
        <div className="relative" style={{ width: `${200 + span * hourWidth}px`, minWidth: "100%" }}>
          <div className="sticky top-0 z-[25] grid grid-cols-[200px_1fr] border-b border-[var(--line)] bg-[var(--wash)] text-[var(--ink-soft)]">
            <div className="sticky left-0 z-[30] bg-[var(--wash)] px-4 py-3">
              <div className="dispatch-roster-heading"><p className="text-xs font-medium text-[var(--ink-soft)]">Technicians</p><DispatchTechnicianSearch onSearchChange={updateTechnicianSearch} /></div>
            </div>
            <div
              role="presentation"
              aria-label="Hours"
              className="relative h-10"
            >
              {Array.from({ length: span + 1 }, (_, index) => {
                const last = index === span;
                return (
                  <span
                    key={index}
                    className={`absolute top-3 text-xs font-medium text-[var(--ink-soft)] ${last ? "-translate-x-full" : index === 0 ? "" : "-translate-x-1/2"}`}
                    style={{ left: `${(index / span) * 100}%` }}
                  >
                    {hourLabel(startHour + index)}
                  </span>
                );
              })}
              {boardDate === companyToday && <span className="dispatch-now-label" style={{ left: `${position(new Date(boardNow).toISOString())}%` }}>Now</span>}
            </div>
          </div>
          {unassigned.length > 0 && (
            <div ref={unassignedRowRef} tabIndex={-1} role="region" aria-label="Unassigned appointments" className="grid grid-cols-[200px_1fr] border-b border-[var(--line)] bg-[var(--wash)]">
              <div className="sticky left-0 z-[21] border-r border-[var(--line)] bg-[var(--wash)] px-4 py-4">
                <p className="font-semibold text-[var(--ink)]">Unassigned</p>
              </div>
              <div
                data-lane="unassigned"
                className="relative"
                style={{ height: `${laneHeight(unassigned)}px` }}
              >
                {stackAppointments(unassigned).map((entry) =>
                  jobBlock(entry.item, entry.row, ""),
                )}
              </div>
            </div>
          )}
          {staffedTeams.map((team) => {
            const members = boardTechnicians.filter(
              (technician) => team.technicianIds.includes(technician.id),
            );
            return (
              <div key={team.id} data-team-id={team.id}>
                {teamHeader(team)}
                {!collapsedTeams.has(team.id) && members.map((technician) => technicianRow(technician, team.id))}
                {!collapsedTeams.has(team.id) && !members.length && !search && (
                  <div className="border-b border-[var(--line)] px-4 py-4 text-xs text-[var(--ink-faint)]">
                    Use Edit team to assign technicians, or drag a name here.
                  </div>
                )}
              </div>
            );
          })}
          {staffedTeams.length > 0 && unteamed.length > 0 && teamHeader(null)}
          {!collapsedTeams.has("none") && unteamed.map((technician) => technicianRow(technician))}
          {!boardTechnicians.length && (
            <div className="p-12 text-center text-[var(--ink-soft)]">
              {search ? "No technicians match your search." : "Add technicians in Settings to start dispatching."}
            </div>
          )}
          {boardDate === companyToday && <div
            className="dispatch-now-line"
            aria-label={`Current time ${formatDispatchTime(new Date(boardNow).toISOString(), timezone)}`}
            style={{ left: `${200 + hourOf(new Date(boardNow).toISOString()) * hourWidth}px` }}
          />}
        </div>
      </section>
      <div className="dispatch-calendar-tools flex flex-wrap items-center gap-2">
        <div className="dispatch-board-legend" aria-label="Dispatch color code"><BoardLegend /></div>
        <button type="button" disabled={!teamsReady} onClick={addTeam} className="dispatch-board-chip">Add team</button>
        <button type="button" onClick={() => setCollapsedTeams(allTeamsCollapsed ? new Set() : new Set([...staffedTeams.map((team) => team.id), "none"]))} className="dispatch-board-chip">{allTeamsCollapsed ? "Expand all" : "Collapse all"}</button>
      </div>
      {teamNotice && <p role="status" className="mt-3 text-sm">{teamNotice}</p>}
      {teamError && <p role="alert">{teamError} <button type="button" onClick={() => setTeamAttempt((value) => value + 1)}>Retry teams</button></p>}
      </div>
      <div ref={schedulingQueueRef} id={schedulingQueueId} tabIndex={-1} className="dispatch-scheduling-queues" role="region" aria-label="Scheduling work">
      <DispatchWaitingJobs filtersTarget={waitingFiltersTarget} openJob={onOpenJob} jobHref={openJob} soldCount={soldUnscheduled.length + pendingSoldEstimateCount} onFilterChange={setSchedulingFilter} scrollTarget={schedulingQueueRef} />
      <div className="dispatch-work-queues" hidden={schedulingFilter !== "all" && schedulingFilter !== "sold"}>
        <PendingEstimateWork openEstimate={onOpenEstimate ? id => { setStartEstimateBooking(false); setEstimateToSchedule(id); } : undefined} onSchedule={onOpenEstimate ? id => { setStartEstimateBooking(true); setEstimateToSchedule(id); } : undefined} soldCount={soldUnscheduled.length} revision={queueRevision} onCountChange={setPendingSoldEstimateCount} showEmpty={schedulingFilter === "sold"}>
              {soldUnscheduled.map((job) => (
                <article key={job.id} className="dispatch-work-card cursor-pointer" onClick={() => onOpenJob?.(job.id)}>
                  <button type="button" className="dispatch-work-number">{job.estimateDisplayNumber ? `Estimate #${job.estimateDisplayNumber}` : `Job #${job.jobNumber}`}</button>
                  <h3 title={job.customerName}>{job.customerName}</h3>
                  <p className="dispatch-work-description" title={job.address}>{job.address}</p>
                  <p className="dispatch-work-type">{job.jobTypeName || "Job type not set"}</p>
                  <div className="dispatch-work-meta">
                    <span>Job #{job.jobNumber}</span>
                    {job.totalCents != null && <span>{money(job.totalCents)}</span>}
                  </div>
                  <button className="dispatch-work-schedule" type="button" onClick={(event) => { event.stopPropagation(); setSoldWorkToSchedule(job); }}>Book appointment</button>
                  {job.paidCents != null && <p className="dispatch-work-type">{job.paidCents > 0 ? `${money(job.paidCents)} paid toward invoice` : "No payment recorded"}</p>}
                </article>
              ))}
        </PendingEstimateWork>
      </div>
      </div>
      {pendingProposals.length > 0 && (
        <section
          className="mt-5 space-y-3"
          aria-label="Waiting on your approval"
        >
          <SectionLabel>Waiting on your approval</SectionLabel>
          {pendingProposals.map((proposal) => (
            <ProposalCard
              key={proposal.id}
              proposal={proposal}
              timezone={timezone}
              busy={proposalBusy === proposal.id}
              onApprove={approve}
              onReject={reject}
            />
          ))}
        </section>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
            Initial view
          </span>
          <select
            aria-label="Board starts at"
            value={hours.from}
            onChange={(event) =>
              setHours((current) => {
                const from = Number(event.target.value);
                return { from, to: Math.max(current.to, from + 1) };
              })
            }
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2 py-1 text-xs font-semibold text-[var(--ink)]"
          >
            {Array.from({ length: 24 }, (_, hour) => (
              <option key={hour} value={hour}>
                {hourLabel(hour)}
              </option>
            ))}
          </select>
          <span className="text-xs text-[var(--ink-faint)]">to</span>
          <select
            aria-label="Board ends at"
            value={hours.to}
            onChange={(event) =>
              setHours((current) => {
                const to = Number(event.target.value);
                return { from: Math.min(current.from, to - 1), to };
              })
            }
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2 py-1 text-xs font-semibold text-[var(--ink)]"
          >
            {Array.from({ length: 24 }, (_, index) => index + 1).map((hour) => (
              <option key={hour} value={hour}>
                {hourLabel(hour)}
              </option>
            ))}
          </select>

        </div>
      </div>
      {reassign && reassignItem && <DispatchReassignMenu key={reassign.id} item={reassignItem} technicians={technicians}
        x={reassign.x} y={reassign.y} trigger={reassign.trigger} save={save} onClose={() => setReassign(null)} onBusyChange={setReassignBusy} />}
      {jobPreview && previewItem && <DispatchJobPreview
        item={previewItem}
        timezone={timezone}
        trigger={jobPreview.trigger}
        save={save}
        canConfirmAppointments={canConfirmAppointments}
        onClose={() => setJobPreview(null)}
        onOpen={() => { setJobPreview(null); if (previewItem.jobId && onOpenJob) onOpenJob(previewItem.jobId); else setSelected(previewItem); }}
      >
        <LocationTagBadges tags={previewItem.locationTags} />
        <CustomerMembershipTags customerId={previewItem.customerId} loadMemberships={membershipLookup} />
      </DispatchJobPreview>}
      {selectedItem && (
        <DispatchJobDrawer
          defaultBookingDurationMinutes={bookingDuration}
          item={selectedItem}
          appointments={items}
          availabilityDate={boardDate}
          teams={teams}
          teamsReady={teamsReady}
          technicians={technicians}
          blocks={blocks}
          timezone={timezone}
          save={save}
          appointmentActions={appointmentActions}
          canConfirmAppointments={canConfirmAppointments}
          loadDispatchJobContext={loadDispatchJobContext}
          close={() => setSelected(null)}
          open={open}
          openJob={openJob}
          onOpenJob={onOpenJob ? (id) => { setSelected(null); onOpenJob(id); } : undefined}
          onOpenCustomer={onOpenCustomer}
          onOpenInvoice={onOpenInvoice}
        />
      )}
      {overlap && (
        <Dialog title="This overlaps another visit" onClose={() => { if (!overlapBusy) cancelOverlap(); }}>
          <div className="space-y-4 p-4">
            <p className="text-sm text-[var(--ink)]">{overlap.message}</p>
            {overlapError && <p role="alert">{overlapError}</p>}
            <div className="flex gap-2">
              <PrimaryButton
                type="button"
                disabled={overlapBusy}
                onClick={async () => {
                  if (overlapBusy) return;
                  setOverlapBusy(true);
                  setOverlapError("");
                  try {
                    const result = await persist(overlap.path, overlap.method, {
                      ...(overlap.body as Record<string, unknown>),
                      allowConflict: true,
                    });
                    if (result?.warning) setBookingWarning(result.warning);
                    overlap.resolve(result);
                    setOverlap(null);
                  } catch (error) {
                    setOverlapError(error instanceof Error ? error.message : "Unable to book. Try again.");
                  } finally {
                    setOverlapBusy(false);
                  }
                }}
              >
                Book Anyway
              </PrimaryButton>
            </div>
          </div>
        </Dialog>
      )}
      {bookingWarning && (
        <div
          role="status"
          className="fixed bottom-5 left-1/2 z-50 w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-950 shadow-lg"
        >
          <div className="flex items-start justify-between gap-3">
            <span>{bookingWarning}</span>
            <button
              type="button"
              aria-label="Dismiss booking warning"
              onClick={() => setBookingWarning(null)}
              className="shrink-0 text-amber-900 underline underline-offset-2"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function SoldWorkScheduleDialog({
  job,
  date: boardDate,
  defaultDurationMinutes,
  save,
  onClose,
  onScheduled,
}: {
  job: UntimedDispatchJob;
  technicians: Technician[];
  date: string;
  timezone: string;
  defaultDurationMinutes: number;
  save: DispatchBoardProps["save"];
  onClose: () => void;
  onScheduled: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [jobTypes, setJobTypes] = useState<Array<{ id: string; name: string; active: boolean }>>([]);
  const [jobTypeId, setJobTypeId] = useState("");
  const [typeSaved, setTypeSaved] = useState(false);
  const [typeError, setTypeError] = useState("");
  useEffect(() => {
    let live = true;
    void data<{ items: typeof jobTypes }>("/api/v1/workspace/dispatch/job-types").then(({ items }) => {
      if (!live) return;
      setJobTypes(items.filter((type) => type.active));
      setJobTypeId(items.find((type) => type.name === job.jobTypeName)?.id ?? "");
    }).catch(() => { if (live) setTypeError("Unable to load job types. Close and reopen to retry."); });
    return () => { live = false; };
  }, [job.id, job.jobTypeName]);
  const prepareJob = async () => {
    if (!jobTypeId) throw new Error("Choose a job type.");
    if (jobTypes.find((type) => type.id === jobTypeId)?.name !== job.jobTypeName) await save(`/api/v1/workspace/jobs/${job.id}/job-type`, "PATCH", { jobTypeId });
    setTypeSaved(true);
    return job.id;
  };
  return <Dialog title={`Schedule Job #${job.jobNumber}`} width="max-w-3xl" onClose={() => { if (!busy) onClose(); }}>
    <label className="block text-sm">Job type<select aria-label="Job type" disabled={busy || typeSaved} value={jobTypeId} onChange={(event) => setJobTypeId(event.target.value)} className="mt-1 w-full rounded-lg border p-2"><option value="">Choose job type</option>{jobTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>
    {typeError && <p role="alert">{typeError}</p>}
    <InstallScheduler allowDoubleBooking jobTypeId={jobTypeId} initialDate={boardDate} initialMinutes={defaultDurationMinutes} prepareJob={prepareJob} saveVisit={(visit) => save("/api/v1/workspace/dispatch/appointments", "POST", visit)} onBusyChange={setBusy} onDone={onScheduled} />
  </Dialog>;
}

/**
 * WHAT A BLOCK'S COLOUR MEANS.
 *
 * The board is the one screen where colour is data: the dispatcher reads the
 * row before they read a word of it. Solid blue, deep blue, purple and green
 * distinguish scheduled, confirmed, travelling and on-site work. Completed
 * work is slate. Each fill uses a contrasting text colour.
 *
 * Missed visits are red. A red outline says another job is running late or
 * double booked, drawn over whatever colour the state already is, so an alert
 * never hides the state it happened to.
 *
 * The class strings are spelled out in full and never built from a variable —
 * Tailwind only compiles classes it can see literally in the source, so a
 * template string here would silently produce an unstyled block.
 *
 * "Confirmed" is the customer having said yes. The board colours a pending
 * visit that way when customerConfirmedAt is on the appointment.
 */
const TONE = {
  loading: {
    fill: "border border-[#cbd5e1] bg-[#f1f5f9] text-[#475569]",
    chip: "bg-[#f1f5f9] text-[#475569]",
  },
  scheduled: {
    fill: "border border-[#9fc9eb] bg-[#BFE3FF] text-[#102a43]",
    chip: "bg-[#BFE3FF] text-[#102a43]",
  },
  confirmed: {
    fill: "border border-[#387bc4] bg-[#4A90E2] text-[#102a43]",
    chip: "bg-[#4A90E2] text-[#102a43]",
  },
  dispatched: {
    fill: "border border-[#bf97dc] bg-[#dbbaf2] text-[#102a43]",
    chip: "bg-[#dbbaf2] text-[#102a43]",
  },
  working: {
    fill: "border border-[#82c677] bg-[#A6E89B] text-[#102a43]",
    chip: "bg-[#A6E89B] text-[#102a43]",
  },
  complete: {
    fill: "border border-[#c5c5c5] bg-[#e5e5e5] text-[#333333]",
    chip: "bg-[#e5e5e5] text-[#333333]",
  },
  conflict: {
    fill: "border border-[#902e46] bg-[#ad3652] text-white",
    chip: "bg-[var(--st-conflict)] text-white",
  },
  off: {
    fill: "border border-[#94a3b8] bg-[#cbd5e1] text-[#334155]",
    chip: "bg-[var(--st-off)] text-white",
  },
} as const;

type ToneKey = keyof typeof TONE;

export function statusChip(status: string) {
  const state = APPOINTMENT_STATES.find(
    (entry) => entry.key === status.toLowerCase(),
  );
  if (state)
    return {
      label: state.label.toUpperCase(),
      className: `${TONE[state.tone].chip} uppercase tracking-wide`,
    };
  // Job and paperwork states the board has no block for keep the same palette.
  return { label: status.replace(/_/g, " "), className: statusStyle(status) };
}

export const APPOINTMENT_STATES: Array<{
  key: string;
  label: string;
  tone: ToneKey;
}> = [
  { key: CREW_PROGRESS_LOADING_STATUS, label: "Loading crew status", tone: "loading" },
  { key: "pending", label: "Scheduled", tone: "scheduled" },
  { key: "confirmed", label: "Confirmed", tone: "confirmed" },
  // Dispatched and en route are one thing to the office: he has left. Arrived
  // and in progress are one thing too: he is there. Six words for four states
  // made the board read like a system log.
  { key: "dispatched", label: "On the way", tone: "dispatched" },
  { key: "en_route", label: "On the way", tone: "dispatched" },
  { key: "arrived", label: "On site", tone: "working" },
  { key: "in_progress", label: "On site", tone: "working" },
  { key: "completed", label: "Completed", tone: "complete" },
  { key: "cancelled", label: "Cancelled", tone: "off" },
  { key: "no_show", label: "Missed", tone: "conflict" },
];

function appointmentAlert(item: DispatchItem, timezone: string) {
  return isRunningLate(item, timezone) ? "Running late" : "";
}

const SLOT_MINUTES = 120;

function appointmentState(item: DispatchItem) {
  const status = item.status.toLowerCase();
  if (
    item.customerConfirmedAt &&
    (status === "pending" || status === "scheduled")
  ) {
    return (
      APPOINTMENT_STATES.find((state) => state.key === "confirmed") ??
      APPOINTMENT_STATES[0]
    );
  }
  return (
    APPOINTMENT_STATES.find((state) => state.key === status) ??
    APPOINTMENT_STATES[0]
  );
}
function appointmentLabel(item: DispatchItem) {
  return appointmentState(item).label;
}
function dispatchMembershipLabel(status: string | null | undefined) {
  const normalized = status?.trim().toLowerCase() ?? "";
  if (normalized === "active") return "Member";
  if (normalized.includes("renew")) return "Renewal";
  if (normalized.includes("expire") || normalized.includes("lapse")) return "Expired";
  return normalized ? "Member" : null;
}
function appointmentTone(item: DispatchItem) {
  return TONE[appointmentState(item).tone].fill;
}

function DispatchJobPreview({ item, timezone, trigger, save, canConfirmAppointments, onClose, onOpen, children }: {
  item: DispatchItem; timezone: string; trigger: HTMLButtonElement;
  save: DispatchBoardProps["save"]; canConfirmAppointments: boolean;
  onClose: () => void; onOpen: () => void; children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 8, left: 8 });
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const canConfirm = canConfirmAppointments && !item.customerConfirmedAt && ["pending", "scheduled"].includes(item.status.toLowerCase());
  const confirmAppointment = async () => {
    setConfirming(true);
    setConfirmError("");
    try {
      await save(`/api/v1/workspace/dispatch/appointments/${item.id}/status`, "POST", { action: "confirm" });
      onClose();
    } catch (error) {
      setConfirmError(error instanceof Error ? error.message : "Could not confirm appointment. Please try again.");
    } finally {
      setConfirming(false);
    }
  };
  useLayoutEffect(() => {
    const anchor = trigger.getBoundingClientRect();
    const rect = panel.current?.getBoundingClientRect();
    const width = rect?.width || Math.min(440, window.innerWidth - 16);
    const height = rect?.height || 300;
    setPosition({
      left: Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8)),
      top: anchor.bottom + height + 16 <= window.innerHeight ? anchor.bottom + 8 : Math.max(8, anchor.top - height - 8),
    });
    closeButton.current?.focus();
  }, [trigger]);
  useEffect(() => {
    const outside = (event: globalThis.PointerEvent) => {
      if (event.target instanceof Node && !panel.current?.contains(event.target) && !trigger.contains(event.target)) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      trigger.focus();
      onClose();
    };
    const scroll = (event: Event) => {
      if (event.target instanceof Node && panel.current?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape, true);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape, true);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [trigger, onClose]);
  return createPortal(<div ref={panel} role="dialog" aria-label={`Job #${item.jobNumber} preview`} className="dispatch-job-popup fixed z-50 overflow-y-auto rounded-xl border border-[var(--line)] bg-[var(--paper)] p-3.5 shadow-lg" style={{ top: position.top, left: position.left, maxHeight: `calc(100dvh - ${position.top + 8}px)` }}>
    <div className="flex items-start justify-between gap-3">
      <div><p className="text-sm font-semibold">{item.customerName}</p><p className="text-xs text-[var(--ink-soft)]">Job #{item.jobNumber}{item.jobTypeName?.trim() ? ` · ${item.jobTypeName}` : ""}</p></div>
      <button ref={closeButton} type="button" aria-label="Close job preview" className="grid size-7 shrink-0 place-items-center rounded-md hover:bg-[var(--wash)]" onClick={() => { trigger.focus(); onClose(); }}><X size={16} aria-hidden /></button>
    </div>
    <p className="mt-2 text-xs font-semibold">{appointmentAlert(item, timezone) || appointmentLabel(item)}</p>
    <p className="mt-1 text-xs">{formatDispatchDate(item.scheduledStart, timezone, { weekday: "short", month: "short", day: "numeric" })} · {formatDispatchTime(item.scheduledStart, timezone)}–{formatDispatchTime(item.scheduledEnd, timezone)}</p>
    {children}
    <p className="mt-2 text-xs text-[var(--ink-soft)]">{item.address}{item.postalCode ? ` ${item.postalCode}` : ""}</p>
    {Boolean(item.serviceZoneNames?.length) && <p className="mt-1 text-xs text-[var(--ink-soft)]">Zone: {item.serviceZoneNames!.join(", ")}</p>}
    {item.description && <p className="mt-2 whitespace-pre-wrap break-words text-xs">{plainTextDescription(item.description)}</p>}
    <p className="mt-2 text-xs font-semibold">{item.technicianNames || "Nobody assigned"}</p>
    {confirmError && <p role="alert" className="mt-2 text-xs text-[var(--st-conflict)]">{confirmError}</p>}
    <div className="mt-3 flex flex-wrap gap-2">
      {canConfirm && <button type="button" disabled={confirming} className="rounded-md bg-[var(--accent)] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" onClick={() => void confirmAppointment()}>{confirming ? "Confirming…" : "Confirm appointment"}</button>}
      <button type="button" disabled={confirming} className="rounded-md border border-[var(--line)] px-3 py-1.5 text-xs font-semibold hover:bg-[var(--wash)]" onClick={onOpen}>Open job</button>
    </div>
  </div>, document.body);
}

/** The board says what its colours mean, or they are just colours. */
function BoardLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {APPOINTMENT_STATES.filter(
        (state, index) =>
          state.tone !== "off" &&
          APPOINTMENT_STATES.findIndex(
            (other) => other.label === state.label,
          ) === index,
      ).map((state) => (
        <span
          key={state.key}
          className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--ink-soft)]"
        >
          <span className={`h-3 w-3 rounded-[3px] ${TONE[state.tone].fill}`} />
          {state.label}
        </span>
      ))}
      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--ink-soft)]">
        <span className="h-3 w-3 rounded-[3px] bg-[#BFE3FF] ring-2 ring-inset ring-[var(--st-alert)]" />
        Running late
      </span>
      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--ink-soft)]">
        <span className="dispatch-availability--available h-3 w-3 rounded-[3px] border border-[var(--line)]" />
        Available
      </span>
      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--ink-soft)]">
        <span className="dispatch-availability--on-call h-3 w-3 rounded-[3px] border border-[#ff8a00]/20" />
        After hours
      </span>
      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--ink-soft)]">
        <span className="dispatch-availability--unavailable h-3 w-3 rounded-[3px] border border-[#c4cbd5]" />
        Unavailable
      </span>
    </div>
  );
}
/**
 * The board's flyout — the job card condensed to what dispatch decides:
 * who is going, when, and whether anything is wrong. Money and history live
 * on the full job card behind "Open job".
 */
function DispatchJobDrawer({
  defaultBookingDurationMinutes,
  item,
  appointments: initialAppointments,
  availabilityDate,
  teams,
  teamsReady,
  technicians: initialTechnicians,
  blocks: initialBlocks,
  timezone,
  save,
  appointmentActions = false,
  canConfirmAppointments = false,
  loadDispatchJobContext,
  close,
  open,
  openJob,
  onOpenJob,
  onOpenCustomer,
  onOpenInvoice,
}: {
  defaultBookingDurationMinutes: number;
  item: DispatchItem;
  appointments: DispatchItem[];
  availabilityDate: string;
  teams: BoardTeam[];
  teamsReady: boolean;
  technicians: Technician[];
  blocks: ScheduleBlock[];
  timezone: string;
  save: DispatchBoardProps["save"];
  appointmentActions?: boolean;
  canConfirmAppointments?: boolean;
  loadDispatchJobContext?: (jobId: string) => Promise<DispatchJobContext>;
  close: () => void;
  open: (id: string) => string;
  openJob: (id: string) => string;
  onOpenJob?: (id: string) => void;
  onOpenCustomer?: (id: string) => void;
  onOpenInvoice?: (invoiceId: string, jobId: string, customerId: string) => void;
}) {
  const { openTechnicianText } = useRecordCommunications();
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState(() => plainTextDescription(item.description));
  const [noteDraft, setNoteDraft] = useState(() => plainTextDescription(item.description));
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState("");
  const saveNotes = async () => {
    if (!item.jobId) return;
    setNotesSaving(true);
    setNotesError("");
    try {
      await save(`/api/v1/workspace/jobs/${item.jobId}`, "PATCH", { description: noteDraft });
      setNotes(noteDraft);
      setEditingNotes(false);
    } catch (error) {
      setNotesError(error instanceof Error ? error.message : "Could not save job notes.");
    } finally { setNotesSaving(false); }
  };
  const [saving, setSaving] = useState(false);
  const [cancelKind, setCancelKind] = useState<
    null | "cancel_dispatch" | "cancel_visit"
  >(null);
  /**
   * The board payload is what a block needs to be drawn: a customer, a time,
   * an address. Deciding anything about the job needs the job — what kind of
   * work it is, who to ring at the property, what the office wrote down — so
   * the flyout fetches it when it opens.
   */
  const [detail, setDetail] = useState<DispatchJobContext | null>(null);
  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setMembershipError(false);
    if (loadDispatchJobContext && item.jobId)
      void loadDispatchJobContext(item.jobId)
        .then((result) => {
          if (!cancelled) setDetail(result);
        })
        .catch(() => { if (!cancelled) setMembershipError(true); });
    return () => {
      cancelled = true;
    };
  }, [item.jobId, loadDispatchJobContext]);
  /*
   * The technician is going to ONE address, so the people at that address come
   * first and the account's billing contacts only stand in when the address has
   * nobody. The same person can sit in both lists and the same details can
   * arrive more than once, so identical entries collapse.
   */
  const dedupeContacts = (list: WorkspaceContact[]) => {
    const seen = new Set<string>();
    return list.filter((contact) => {
      const key = [
        (contact.name || "").trim().toLowerCase(),
        (contact.phone || "").replace(/\D/g, ""),
        normalizedContactEmail(contact.email),
      ].join("|");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const atThisAddress = dedupeContacts(detail?.propertyContacts ?? []);
  const onTheAccount = dedupeContacts(detail?.billingContacts ?? []);
  const people = atThisAddress.length ? atThisAddress : onTheAccount;
  const standingInForTheAddress =
    !atThisAddress.length && onTheAccount.length > 0;
  const contactGroups = Array.from(people.reduce((groups, contact) => {
    const key = `${contact.name.trim().toLowerCase()}:${Boolean(contact.doNotAlert)}`;
    const group = groups.get(key) ?? { key, name: contact.name, doNotAlert: contact.doNotAlert, contacts: [] as WorkspaceContact[] };
    group.contacts.push(contact);
    groups.set(key, group);
    return groups;
  }, new Map<string, { key: string; name: string; doNotAlert: boolean; contacts: WorkspaceContact[] }>()).values());
  const [membershipError, setMembershipError] = useState(false);
  const memberships = detail
    ? detail.memberships.filter((membership) => !membership.propertyId || membership.propertyId === detail.job.propertyId)
    : null;
  // Current coverage takes precedence over historical expired plans.
  const membershipStates = (memberships ?? []).map(membership => membershipState(membership));
  const membershipTag = membershipStates.find((state) => state.tone === "renewal")
    ?? membershipStates.find((state) => state.tone === "good")
    ?? membershipStates.find((state) => state.tone === "standard")
    ?? membershipStates.find((state) => state.tone === "urgent");
  const membershipTagLabel = membershipTag?.tone === "good" ? "Active"
    : membershipTag?.tone === "renewal" ? "Up for renewal"
    : membershipTag?.tone === "urgent" ? "Expired" : membershipTag?.label;
  const [start, setStart] = useState(
    isoToCompanyLocal(item.scheduledStart, timezone).slice(11, 16),
  );
  const originalMinutes = (() => {
    const from = isoToCompanyLocal(item.scheduledStart, timezone).slice(11, 16);
    const to = isoToCompanyLocal(item.scheduledEnd, timezone).slice(11, 16);
    const span =
      Number(to.slice(0, 2)) * 60 +
      Number(to.slice(3, 5)) -
      (Number(from.slice(0, 2)) * 60 + Number(from.slice(3, 5)));
    return span > 0 ? span : SLOT_MINUTES;
  })();
  const [technicianIds, setTechnicianIds] = useState<string[]>(() => [...item.technicianIds]);
  const [boardDate, setBoardDate] = useState(
    isoToCompanyLocal(item.scheduledStart, timezone).slice(0, 10),
  );

  const [additionalVisit, setAdditionalVisit] = useState<{ date: string; time: string; technicianIds: string[] } | null>(null);
  const [visitEnd, setVisitEnd] = useState("");
  const [visitTimeSelected, setVisitTimeSelected] = useState(false);
  const [visitError, setVisitError] = useState("");
  const selectedMinutes = visitTimeSelected && visitEnd ? Number(visitEnd.slice(0, 2)) * 60 + Number(visitEnd.slice(3)) - Number(start.slice(0, 2)) * 60 - Number(start.slice(3)) : 0;
  const minutes = additionalVisit ? selectedMinutes : originalMinutes;
  const beginAdditionalVisit = () => {
    setAdditionalVisit({ date: boardDate, time: start, technicianIds: [...technicianIds] });
    const nextDay = new Date(`${boardDate}T12:00:00Z`);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    setBoardDate(nextDay.toISOString().slice(0, 10));
    setVisitTimeSelected(false);
    setVisitEnd("");
    setTechnicianIds([]);
    setVisitError("");
  };
  const cancelAdditionalVisit = () => {
    if (!additionalVisit) return;
    setBoardDate(additionalVisit.date);
    setStart(additionalVisit.time);
    setTechnicianIds(additionalVisit.technicianIds);
    setAdditionalVisit(null);
    setVisitError("");
  };

  const [otherDay, setOtherDay] = useState<{ date: string; blocks: ScheduleBlock[]; appointments: DispatchItem[]; technicians: Technician[] } | null>(null);
  const [dayError, setDayError] = useState("");
  const [dayAttempt, setDayAttempt] = useState(0);
  const dayReady = boardDate === availabilityDate || otherDay?.date === boardDate;
  const blocks = boardDate === availabilityDate ? initialBlocks : otherDay?.date === boardDate ? otherDay.blocks : [];
  const appointments = boardDate === availabilityDate ? initialAppointments : otherDay?.date === boardDate ? otherDay.appointments : [];
  const technicians = boardDate === availabilityDate ? initialTechnicians : otherDay?.date === boardDate ? otherDay.technicians : initialTechnicians;
  useEffect(() => {
    if (boardDate === availabilityDate || !boardDate) return;
    let current = true;
    setDayError("");
    void data<{ blocks: ScheduleBlock[]; appointments: DispatchItem[]; technicians: Technician[] }>(`/api/v1/workspace/dispatch/board?date=${boardDate}`)
      .then((result) => { if (current) setOtherDay({ ...result, date: boardDate }); })
      .catch(() => { if (current) setDayError("Could not load availability for this date."); });
    return () => { current = false; };
  }, [boardDate, availabilityDate, dayAttempt]);
  const serviceTypeIds = new Set((detail?.jobTypes ?? []).map((type) => type.id));
  const serviceMemberIds = new Set(teams.filter((team) => team.jobTypeIds.some((id) => serviceTypeIds.has(id))).flatMap((team) => team.technicianIds));
  const openByTechnician = new Map(technicians.map((technician) => [technician.id,
    technicianOpenWindows(blocks, appointments.filter((visit) => (additionalVisit || visit.id !== item.id) && !["cancelled", "completed", "no_show"].includes(visit.status.toLowerCase())), technician.id, 0),
  ]));
  const availableAt = (id: string, time: string) => {
    const from = Date.parse(companyLocalToIso(`${boardDate}T${time}`, timezone));
    return minutes > 0 && dayReady && (openByTechnician.get(id) ?? []).some((window) => Date.parse(window.startsAt) <= from && Date.parse(window.endsAt) >= from + minutes * 60_000);
  };
  const windows: Array<{ time: string; available: string[] }> = [];
  if (!additionalVisit && dayReady && teamsReady && serviceTypeIds.size) {
    for (let minute = 0; minute + minutes <= 1440; minute += 30) {
      const time = minutesToLocalTime(minute);
      const available = technicians.filter((tech) => serviceMemberIds.has(tech.id) && availableAt(tech.id, time)).map((tech) => tech.id);
      if (available.length) windows.push({ time, available });
    }
  }

  /*
   * A JOB THAT NEEDS A TRADE ONLY OFFERS THAT TRADE. The list used to rank
   * everybody and still show everybody, so a plumbing call listed the install
   * crew under the plumbers and the dispatcher had to read notes to tell them
   * apart. Now the ones who cannot do the work are off the list — and one
   * click puts them back, because the day it matters is the day somebody is
   * out and it has to be covered anyway.
   */
  const [showEveryone, setShowEveryone] = useState(false);
  const [addingTechnician, setAddingTechnician] = useState(false);
  const [technicianSearch, setTechnicianSearch] = useState("");
  // Skills are only needed when the dispatcher opens this appointment. Keeping
  // them out of the day-board response avoids repeating job-skill lookups for
  // every lane on every refresh.
  const requirementSkillIds = detail?.requiredSkills?.map((skill) => skill.id) ?? item.requiredSkillIds ?? [];
  const requirementSkills = detail?.requiredSkills?.map((skill) => skill.name) ?? item.requiredSkills ?? [];
  const requirementsLoading = Boolean(item.jobId && loadDispatchJobContext && !detail && !membershipError);
  const rankedTechnicians = rankTechniciansForSkills(technicians, requirementSkillIds);
  const needsSkills = requirementSkillIds.length > 0;
  const matching = rankedTechnicians.filter((entry) => entry.rank === 0);
  const hidden =
    needsSkills && !showEveryone
      ? rankedTechnicians.length - matching.length
      : 0;
  const offered =
    needsSkills && !showEveryone
      ? rankedTechnicians.filter(
          (entry) => entry.rank === 0 || technicianIds.includes(entry.technician.id),
        )
      : rankedTechnicians;
  const windowStart = Date.parse(companyLocalToIso(`${boardDate}T${start}`, timezone));
  const windowEnd = windowStart + minutes * 60_000;
  const technicianOptions = offered
    .filter(({ technician }) => !technicianIds.includes(technician.id))
    .filter(({ technician }) => `${technician.name} ${(technician.skills || []).join(" ")}`.toLowerCase().includes(technicianSearch.trim().toLowerCase()))
    .map(({ technician }) => {
      const known = dayReady;
      const hours = technicianHasHours(blocks, technician.id);
      const free = known && technicianOpenWindows(blocks, appointments.filter((visit) => (additionalVisit || visit.id !== item.id) && !["cancelled", "completed", "no_show"].includes(visit.status.toLowerCase())), technician.id, 0)
        .some((window) => Date.parse(window.startsAt) <= windowStart && Date.parse(window.endsAt) >= windowEnd);
      return { technician, free, disabled: known && !hours,
        availability: !known ? "Availability not loaded for this date" : free ? "Available for this visit" : !hours ? "No hours this day" : "Schedule conflict" };
    }).sort((left, right) => Number(right.free) - Number(left.free));

  const rescheduleChanged = `${boardDate}T${start}` !== isoToCompanyLocal(item.scheduledStart, timezone).slice(0, 16);
  const rescheduleBlocked = rescheduleChanged && (!dayReady || !windows.some((window) => window.time === start));
  const availableVisitTechnicians = rankedTechnicians.filter(({ technician }) => serviceMemberIds.has(technician.id) && availableAt(technician.id, start));
  const bookableVisitBlocks = blocks.filter((block) => block.kind === "working" || block.kind === "on_call");
  const localVisitMinutes = (value: string) => {
    const local = isoToCompanyLocal(value, timezone);
    return Number(local.slice(11, 13)) * 60 + Number(local.slice(14, 16));
  };
  const visitDayOpens = bookableVisitBlocks.length ? Math.min(...bookableVisitBlocks.map((block) => localVisitMinutes(block.startsAt))) : 8 * 60;
  const visitDayCloses = bookableVisitBlocks.length ? Math.max(...bookableVisitBlocks.map((block) => localVisitMinutes(block.endsAt))) : 17 * 60;
  const visitArrivalWindows: Array<{ time: string; end: string; count: number }> = [];
  if (additionalVisit && dayReady && teamsReady && detail) {
    for (let at = Math.ceil(visitDayOpens / defaultBookingDurationMinutes) * defaultBookingDurationMinutes;
      at + defaultBookingDurationMinutes <= Math.floor(visitDayCloses / defaultBookingDurationMinutes) * defaultBookingDurationMinutes;
      at += defaultBookingDurationMinutes) {
      const time = minutesToLocalTime(at);
      const end = endTimeForRange(time, defaultBookingDurationMinutes);
      if (!end) continue;
      const from = Date.parse(companyLocalToIso(`${boardDate}T${time}`, timezone));
      const count = technicians.filter((technician) => serviceMemberIds.has(technician.id) && (openByTechnician.get(technician.id) ?? []).some((window) => Date.parse(window.startsAt) <= from && Date.parse(window.endsAt) >= from + defaultBookingDurationMinutes * 60_000)).length;
      visitArrivalWindows.push({ time, end, count });
    }
  }
  const chooseVisitStart = (time: string) => {
    setStart(time);
    setVisitEnd(endTimeForRange(time, defaultBookingDurationMinutes) || "");
    setVisitTimeSelected(true);
    setTechnicianIds([]);
    setVisitError("");
  };
  const visitTimeOptions = Array.from({ length: 96 }, (_, index) => {
    const value = minutesToLocalTime(index * 15);
    return { value, label: readableTime(value) };
  });
  const additionalVisitBlocked = !visitTimeSelected || minutes <= 0 || !dayReady || !rescheduleChanged || !technicianIds.length || technicianIds.some((id) => !availableVisitTechnicians.some(({ technician }) => technician.id === id));
  const addVisit = async () => {
    if (!additionalVisit || additionalVisitBlocked) return;
    if (!item.jobId) return;
    setSaving(true);
    setVisitError("");
    try {
      const end = endTimeForRange(start, minutes);
      if (!end) throw new Error("Choose a duration that ends before midnight.");
      await save("/api/v1/workspace/dispatch/appointments", "POST", {
        jobId: item.jobId,
        startsAt: companyLocalToIso(`${boardDate}T${start}`, timezone),
        endsAt: companyLocalToIso(`${boardDate}T${end}`, timezone),
        technicianIds: technicianIds,
      });
      close();
    } catch (error) {
      setVisitError(error instanceof Error ? error.message : "Could not add this visit. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  const submit = async () => {
    if (rescheduleBlocked) return;
    setSaving(true);
    try {
      const end = endTimeForRange(start, minutes);
      if (!end) throw new Error("Choose a duration that ends before midnight.");
      await save(
        `/api/v1/workspace/dispatch/appointments/${item.id}/time`,
        "PATCH",
        {
          startsAt: companyLocalToIso(`${boardDate}T${start}`, timezone),
          endsAt: companyLocalToIso(`${boardDate}T${end}`, timezone),
        },
      );
      await save(
        `/api/v1/workspace/dispatch/appointments/${item.id}/assignments`,
        "PUT",
        { technicianIds: technicianIds },
      );
      close();
    } catch {
      /* overlap dialog or page error already set */
    } finally {
      setSaving(false);
    }
  };
  const visitStatus = item.status.toLowerCase();
  const visitClosed = ["completed", "cancelled", "no_show"].includes(visitStatus);
  const onSite = ["arrived", "in_progress"].includes(visitStatus);
  const onTheWay = ["dispatched", "en_route"].includes(visitStatus);
  // The newest invoice on the job is the one the office wants to reach.
  const latestInvoice = (detail?.invoices ?? [])
    .slice()
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
  const runAction = async (
    action: "confirm" | "dispatch" | "arrive" | "complete" | "cancel_dispatch" | "cancel_visit",
    reason?: "duplicate" | "customer_cancelled" | "rescheduled" | "no_longer_needed",
  ) => {
    setSaving(true);
    try {
      await save(
        `/api/v1/workspace/dispatch/appointments/${item.id}/status`,
        "POST",
        { action, ...(reason ? { reason } : {}) },
      );
      setCancelKind(null);
      close();
    } catch {
      /* page error already set */
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Flyout
      centered
      className="dispatch-job-flyout"
      onClose={close}
      title={
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
          {item.jobId && <a
            href={openJob(item.jobId)}
            onClick={(event) => {
              if (
                onOpenJob && item.jobId &&
                event.button === 0 &&
                !event.metaKey &&
                !event.ctrlKey &&
                !event.shiftKey &&
                !event.altKey
              ) {
                event.preventDefault();
                onOpenJob(item.jobId);
              }
            }}
            className="shrink-0 text-xl font-bold text-[var(--ink)] underline decoration-[var(--line)] underline-offset-4 hover:text-[var(--accent)]"
          >
            Job #{item.jobNumber}
          </a>}
          {!item.jobId && <p className="text-xl font-bold">{item.ownerType} appointment</p>}
          <p className="rounded-lg bg-[var(--accent-wash)] px-3 py-2 text-xl font-bold text-[var(--accent)]">{detail?.jobTypes.length ? detail.jobTypes.map((type) => type.name).join(" · ") : item.jobTypeName || "Service visit"}</p>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-md px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${TONE[appointmentState(item).tone].chip}`}
          >
            {appointmentLabel(item)}
          </span>
          {item.priority &&
            !["normal", "standard", "low"].includes(
              item.priority.toLowerCase(),
            ) && (
              <span className="rounded-lg bg-[var(--accent-wash)] px-2.5 py-1 text-[11px] font-semibold uppercase text-[var(--accent)]">
                {item.priority}
              </span>
            )}

        </div>

          </div>

        </div>
      }
      footer={
        <div className="flex items-center gap-2">
          <PrimaryButton
            type="button"
            disabled={saving || (additionalVisit ? additionalVisitBlocked : rescheduleBlocked)}
            onClick={() => void (additionalVisit ? addVisit() : submit())}
          >
            {saving ? "Saving…" : additionalVisit ? "Schedule additional visit" : "Save changes"}
          </PrimaryButton>
          {additionalVisit && <QuietButton type="button" disabled={saving} onClick={cancelAdditionalVisit}>Cancel additional visit</QuietButton>}
          {onOpenInvoice && latestInvoice && item.jobId && (
            <button
              type="button"
              onClick={() =>
                item.jobId && onOpenInvoice(latestInvoice.id, item.jobId, item.customerId)
              }
              className="shrink-0 rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] transition hover:bg-[var(--wash)]"
            >
              Invoice →
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
          {!detail && !membershipError && <div role="status" aria-live="polite" className="flex items-center gap-2 rounded-lg border border-[var(--line)] bg-[var(--wash)] px-3 py-2 text-sm text-[var(--ink-soft)]">
            <span className="flex gap-1" aria-hidden="true"><span className="size-1.5 animate-bounce rounded-full bg-[var(--accent)] [animation-delay:-0.2s]" /><span className="size-1.5 animate-bounce rounded-full bg-[var(--accent)] [animation-delay:-0.1s]" /><span className="size-1.5 animate-bounce rounded-full bg-[var(--accent)]" /></span>
            Loading dispatch details…
          </div>}
          <section aria-label="Location contact details" className="border-b border-[var(--line)] pb-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
        <a
          href={open(item.customerId)}
          onClick={(event) => {
            if (onOpenCustomer && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
              event.preventDefault();
              onOpenCustomer(item.customerId);
            }
          }}
          className="text-base font-semibold text-[var(--ink)] underline decoration-[var(--line)] underline-offset-4 hover:text-[var(--accent)]"
        >
          {item.customerName}
        </a>
        {item.jobId && <div aria-label="Membership status">
          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${membershipTag ? toneChip[membershipTag.tone] : "bg-[var(--wash)] text-[var(--ink-soft)]"}`}>
            {memberships === null ? (membershipError ? "Membership unavailable" : "Loading membership…") : membershipTagLabel ? `Membership · ${membershipTagLabel}` : "No membership"}
          </span>
        </div>}
            </div>
            <h3 className="mb-3 text-xs font-medium text-[var(--ink-soft)]">Location contact details</h3>
            {standingInForTheAddress && <p className="mb-2 text-xs text-[var(--ink-soft)]">No location contacts on file. Showing account contacts.</p>}
            <div className="divide-y divide-[var(--line)]">
              {contactGroups.map((group) => <div key={group.key} className="space-y-2 py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {group.name.trim().toLowerCase() !== item.customerName.trim().toLowerCase() && <p className="text-sm font-semibold text-[var(--ink)]">{group.name}</p>}
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-[var(--ink-soft)]"><span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${group.doNotAlert ? "bg-[var(--ink-faint)]" : "bg-[var(--good)]"}`} />Notifications {group.doNotAlert ? "off" : "on"}</span>
                </div>
                {group.contacts.map((contact) => <div key={contact.id} className="space-y-1.5">
                  {contact.phone && <div className="flex flex-wrap items-center gap-3 text-sm">
                    <a href={`tel:${contact.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-2 text-[var(--ink-soft)] hover:text-[var(--accent)]"><Phone size={14} aria-hidden="true" />{phoneNumber(contact.phone)}</a>
                    <a href={`sms:${contact.phone.replace(/[^\d+]/g, "")}`} aria-label={`Text ${group.name}`} title={`Text ${group.name}`} className="p-1 text-[var(--ink-soft)] hover:text-[var(--accent)]"><MessageSquare size={14} /></a>
                    {contact.memo && <span className="text-xs text-[var(--ink-soft)]">{contact.memo}</span>}
                  </div>}
                  {contact.email && <a href={`mailto:${contact.email}`} className="flex min-w-0 items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--accent)]"><Mail size={14} className="shrink-0" aria-hidden="true" /><span className="break-all">{contact.email}</span>{contact.memo && <span className="text-xs text-[var(--ink-soft)]">{contact.memo}</span>}</a>}
                </div>)}
              </div>)}
            </div>
          </section>
        <LocationTagBadges tags={detail?.locationTags ?? item.locationTags} />
        <p className="text-sm text-[var(--ink-soft)]">{item.address}{item.postalCode ? ` ${item.postalCode}` : ""}</p>
        <p className="text-sm font-semibold text-[var(--ink)]">
          {formatDispatchDate(item.scheduledStart, timezone, {
            weekday: "long",
            month: "short",
            day: "numeric",
          })}{" "}
          · {formatDispatchTime(item.scheduledStart, timezone)}–
          {formatDispatchTime(item.scheduledEnd, timezone)}
        </p>
          {!additionalVisit && <div>
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold">Assigned technicians</legend>
              {appointmentActions && !visitClosed && <details className="relative technician-action-menu">
                <summary className="w-fit cursor-pointer text-xs font-semibold text-[var(--accent)]">Technician actions</summary>
                <div className="mt-2 space-y-2 rounded-lg border border-[var(--line)] bg-[var(--paper)] p-3">
                  <p className="text-xs text-[var(--ink-soft)]">Updates this visit for all assigned technicians.</p>
                  <div className="flex flex-wrap gap-2">
              {!onSite && !onTheWay && (
                <QuietButton
                  type="button"
                  disabled={saving}
                  onClick={() => void runAction("dispatch")}
                >
                  Dispatch
                </QuietButton>
              )}
              {!onSite && (
                <QuietButton
                  type="button"
                  disabled={saving}
                  onClick={() => void runAction("arrive")}
                >
                  Arrive
                </QuietButton>
              )}
              <QuietButton
                type="button"
                disabled={saving}
                onClick={() => void runAction("complete")}
              >
                Complete
              </QuietButton>              {!onSite && !onTheWay && <button
                type="button"
                disabled={saving}
                onClick={() => setCancelKind("cancel_dispatch")}
                className="text-sm font-semibold text-[var(--ink-soft)] underline decoration-[var(--line)] underline-offset-4 hover:text-[var(--ink)]"
              >
                Cancel appointment
              </button>}
                  </div>
                </div>
              </details>}

              {technicianIds.length === 0 ? <p className="text-sm text-[var(--ink-soft)]">Unassigned</p> : (
                <div className="flex flex-wrap gap-2">
                  {technicianIds.map((id) => {
                    const technician = technicians.find((technician) => technician.id === id);
                    const name = technician?.name || "Technician";
                    const phone = technician?.phone?.replace(/[^\d+]/g, "");
                    return <span key={id} className="inline-flex items-center gap-2 rounded-md bg-[var(--accent-wash)] px-3 py-2 text-sm font-medium">
                      {name}{phone && <>
                        <a href={`tel:${phone}`} aria-label={`Call ${name}`} title={`Call ${name}`} className="p-1"><Phone size={15} /></a>
                        <button type="button" disabled={!openTechnicianText} onClick={() => { close(); openTechnicianText?.({ id, name, phone }); }} aria-label={`Text ${name}`} title={`Text ${name}`} className="p-1"><MessageSquare size={15} /></button>
                      </>}<button type="button" aria-label={`Remove ${name}`} className="text-[var(--ink-soft)]" onClick={() => setTechnicianIds((current) => current.filter((selectedId) => selectedId !== id))}>×</button>
                    </span>;
                  })}
                </div>
              )}
              <button type="button" disabled={requirementsLoading} aria-expanded={addingTechnician} className="text-sm font-semibold text-[var(--accent)] disabled:cursor-wait disabled:opacity-60" onClick={() => { setAddingTechnician((current) => !current); setTechnicianSearch(""); }}>{requirementsLoading ? "Loading visit requirements…" : addingTechnician ? "Done adding" : "+ Add technician"}</button>
              {addingTechnician && <div className="space-y-2 rounded-lg border border-[var(--line)] p-3">
                <TextInput label="Find technician" placeholder="Search name or trade" value={technicianSearch} onChange={(event) => setTechnicianSearch(event.target.value)} />
                <div className="max-h-48 space-y-1 overflow-y-auto">
                  {technicianOptions.map(({ technician, availability, free, disabled }) => (
                    <button key={technician.id} type="button" disabled={disabled} aria-label={`Add ${technician.name}`} className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left hover:bg-[var(--wash)] disabled:opacity-50"
                      onClick={() => setTechnicianIds((current) => [...current, technician.id])}>
                      <span className="text-sm font-medium">{technician.name}<small className="block font-normal text-[var(--ink-soft)]">{tradeLabel(technician.skills)}</small></span>
                      <span className={`text-xs ${free ? "text-[var(--good)]" : "text-[var(--ink-soft)]"}`}>{availability}</span>
                    </button>
                  ))}
                  {technicianOptions.length === 0 && <p className="py-2 text-sm text-[var(--ink-soft)]">No other technicians match.</p>}
                </div>
              </div>}
            </fieldset>
            {addingTechnician && needsSkills && (
              <p className="mt-1.5 text-[11.5px] text-[var(--ink-soft)]">
                {showEveryone ? (
                  <>
                    Everyone on the roster.{" "}
                    <button
                      type="button"
                      onClick={() => setShowEveryone(false)}
                      className="font-semibold text-[var(--accent)]"
                    >
                      Only{" "}
                    {requirementSkills.join(", ") || "the right trade"}
                    </button>
                  </>
                ) : (
                  <>
                    {requirementSkills.join(", ") || "The job's trade"} only
                    {hidden > 0
                      ? ` — ${hidden} other${hidden === 1 ? "" : "s"} hidden`
                      : ""}
                    .{" "}
                    <button
                      type="button"
                      onClick={() => setShowEveryone(true)}
                      className="font-semibold text-[var(--accent)]"
                    >
                      Show everyone
                    </button>
                  </>
                )}
              </p>
            )}
          </div>}
        <section className="rounded-lg bg-[var(--wash)] p-3" aria-label="Job notes">
          <div className="flex items-center justify-between gap-2">
            <SectionLabel>Job notes</SectionLabel>
            {item.jobId && !editingNotes && <button type="button" className="text-xs font-semibold text-[var(--accent)]" onClick={() => { setNoteDraft(notes); setNotesError(""); setEditingNotes(true); }}>Edit job notes</button>}
          </div>
          {editingNotes ? <form className="mt-2 space-y-2" onSubmit={(event) => { event.preventDefault(); void saveNotes(); }}>
            <textarea aria-label="Job notes" value={noteDraft} disabled={notesSaving} onChange={(event) => setNoteDraft(event.target.value)} rows={4} className="w-full rounded-md border border-[var(--line)] bg-[var(--paper)] p-2 text-sm" />
            {notesError && <p role="alert" className="text-sm text-[var(--st-conflict)]">{notesError}</p>}
            <div className="flex gap-2"><PrimaryButton type="submit" disabled={notesSaving}>{notesSaving ? "Saving notes…" : "Save notes"}</PrimaryButton><QuietButton type="button" disabled={notesSaving} onClick={() => setEditingNotes(false)}>Cancel</QuietButton></div>
          </form> : <p className="mt-1.5 whitespace-pre-wrap text-sm text-[var(--ink)]">{notes || "No job notes on file."}</p>}
        </section>
        {softConflicts(item).map((conflict) => (
          <p
            key={conflict}
            className="rounded-lg bg-[var(--accent-wash)] px-3 py-2 text-[13px] font-semibold text-[var(--accent)]"
          >
            {conflict}
          </p>
        ))}
        <section className="visit-actions-panel" aria-label="Visit actions">
          <div className="visit-actions-heading">
            <SectionLabel>Visit</SectionLabel>
          </div>
          <div className="visit-actions-row">
            {!additionalVisit && canConfirmAppointments && !visitClosed && !item.customerConfirmedAt && (visitStatus === "pending" || visitStatus === "scheduled") && (
              <button type="button" className="visit-action" disabled={saving} onClick={() => void runAction("confirm")}>
                Customer confirmed
              </button>
            )}
            {item.jobId && !additionalVisit && <button
              type="button"
              disabled={saving}
              onClick={beginAdditionalVisit}
              className="visit-action"
            >
              Add visit
            </button>}
          </div>
          <div className="reschedule-section">
            <SectionLabel>{additionalVisit ? "Additional visit" : "Reschedule"}</SectionLabel>
            {additionalVisit && <p className="mt-2 text-sm text-[var(--ink-soft)]">Choose a date and time for another visit under Job #{item.jobNumber}. The existing visit stays in place.</p>}
            {visitError && <p role="alert" className="mt-2 text-sm text-[var(--st-conflict)]">{visitError}</p>}
            <input
              type="date"
              aria-label={additionalVisit ? "Additional visit date" : "Day"}
              disabled={saving}
              value={boardDate}
              onChange={(event) => { if (event.target.value) { setDayError(""); setOtherDay(null); setBoardDate(event.target.value); if (additionalVisit) setTechnicianIds([]); setVisitError(""); } }}
              className="mt-2 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
            />
            {additionalVisit ? <div className="mt-3 space-y-3">
              <div>
                <SectionLabel>Arrival windows</SectionLabel>
                <p className="mt-1 text-xs text-[var(--ink-soft)]">Choose a company booking window, then adjust the end time if needed.</p>
                {(!dayReady || !teamsReady || !detail) && !dayError && <p role="status" className="mt-2 text-sm">Loading arrival windows…</p>}
                {dayError && <p role="alert" className="mt-2 text-sm">{dayError} <button type="button" onClick={() => setDayAttempt((value) => value + 1)}>Retry availability</button></p>}
                <div className="visit-arrival-windows" role="group" aria-label="Additional visit arrival windows">
                  {visitArrivalWindows.map((window) => <button key={window.time} type="button" disabled={saving || !window.count} aria-pressed={visitTimeSelected && start === window.time} onClick={() => chooseVisitStart(window.time)}>
                    <span>{readableTime(window.time)}–{readableTime(window.end)}</span>
                    <small>{window.count} available</small>
                  </button>)}
                </div>
                {dayReady && !visitArrivalWindows.length && <p className="mt-2 text-sm text-[var(--ink-soft)]">No booking windows available for this date. Try another date.</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select label="Start time" placeholder="Choose time" value={visitTimeSelected ? start : ""} disabled={saving} options={visitArrivalWindows.map((window) => ({ value: window.time, label: readableTime(window.time), disabled: !window.count }))} onChange={chooseVisitStart} />
                <Select label="End time" placeholder="Choose time" value={visitEnd} disabled={saving || !visitTimeSelected} options={visitTimeOptions.filter((option) => option.value > start)} onChange={(value) => { setVisitEnd(value); setTechnicianIds([]); setVisitError(""); }} />
              </div>
              {!visitTimeSelected || !visitEnd ? <p className="text-sm text-[var(--ink-soft)]">Choose start and end times to see available technicians.</p>
                : minutes <= 0 ? <p role="alert" className="text-sm">End time must be after start time on this date.</p>
                : dayError ? <p role="alert">{dayError} <button type="button" onClick={() => setDayAttempt((value) => value + 1)}>Retry availability</button></p>
                : !dayReady ? <p role="status">Loading availability…</p>
                : <fieldset className="space-y-2"><legend className="text-sm font-semibold">Available technicians</legend>
                  <p className="text-xs text-[var(--ink-soft)]">{minutes}-minute visit · Select who will attend</p>
                  {availableVisitTechnicians.map(({ technician }) => <label key={technician.id} className="flex items-center gap-3 rounded-lg border border-[var(--line)] p-3 text-sm">
                    <input type="checkbox" disabled={saving} checked={technicianIds.includes(technician.id)} onChange={(event) => setTechnicianIds((ids) => event.target.checked ? [...ids, technician.id] : ids.filter((id) => id !== technician.id))} />
                    {technician.name}
                  </label>)}
                  {!availableVisitTechnicians.length && <p role="status" className="text-sm">No matching technicians are available for these times. Try another time or date.</p>}
                </fieldset>}
            </div> : <>
            <p className="mt-2 text-xs text-[var(--ink-soft)]">Open times for this service · {minutes}-minute visit</p>
            {dayError ? <p role="alert" className="text-sm">{dayError} <button type="button" onClick={() => setDayAttempt((value) => value + 1)}>Retry availability</button></p>
              : !dayReady ? <p role="status">Loading availability…</p>
              : !teamsReady || !detail ? <p role="status">Loading service teams…</p>
              : !serviceMemberIds.size ? <p role="status">No team is linked to this service type.</p>
              : !windows.length ? <p role="status">No open times for this service on this date.</p> : null}
            {dayReady && technicianIds.length > 0 && <div className="mt-2 space-y-1" aria-label="Assigned technician availability">
              <p className="text-xs font-semibold">At {readableTime(start)}</p>
              {technicianIds.map((id) => <p key={id} className={`text-xs ${availableAt(id, start) ? "text-[var(--good)]" : "text-[var(--st-conflict)]"}`}>{technicians.find((tech) => tech.id === id)?.name || "Technician"} · {availableAt(id, start) ? "Available" : "Unavailable"}</p>)}
            </div>}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {windows.map((window) => {
                const chosen = window.time === start && (!additionalVisit || visitTimeSelected);
                return (
                  <button
                    key={window.time}
                    type="button"
                    disabled={saving}
                    onClick={() => { setStart(window.time); setVisitTimeSelected(true); setVisitError(""); }}
                    className={`rounded-md border px-2 py-1 text-[11.5px] font-medium transition ${chosen ? "border-[var(--accent)] bg-[var(--accent)] text-white" : "border-[var(--line)] text-[var(--ink)] hover:bg-[var(--accent-wash)]"}`}
                  >
                    {readableTime(window.time)}
                    <span className="block text-[10px] font-normal">{window.available.length} available{technicianIds.length ? ` · ${technicianIds.filter((id) => availableAt(id, window.time)).length}/${technicianIds.length} assigned free` : ""}</span>
                  </button>
                );
              })}
            </div>
            </>}
          </div>
        </section>
        {appointmentActions && !visitClosed && (
          <button type="button" disabled={saving} onClick={() => setCancelKind("cancel_visit")} className="text-sm font-semibold text-[var(--st-conflict)]">
            Cancel the job
          </button>
        )}
      </div>
    </Flyout>
    {cancelKind && (
      <Dialog
        title={cancelKind === "cancel_visit" ? "Cancel the job" : "Cancel appointment"}
        onClose={() => setCancelKind(null)}
      >
        <div className="space-y-3 p-4">
          <p className="text-sm text-[var(--ink)]">
            {cancelKind === "cancel_visit"
              ? "This closes the job. The visit will not be rebooked."
              : "This removes only this unused appointment from the board. The job and its other visits stay unchanged. The cancelled appointment remains in history."}
          </p>
          <div className="grid gap-2">
            {(
              [
                ["duplicate", "Duplicate"],
                ["customer_cancelled", "Customer cancelled"],
                ["rescheduled", "Rescheduled"],
                ["no_longer_needed", "No longer needed"],
              ] as const
            ).map(([reason, label]) => (
              <QuietButton
                key={reason}
                type="button"
                disabled={saving}
                onClick={() => void runAction(cancelKind, reason)}
              >
                {label}
              </QuietButton>
            ))}
          </div>
        </div>
      </Dialog>
    )}
    </>
  );
}

/**
 * ONE TRADE, NOT A LIST OF TAGS. A technician's skill list is everything he has
 * ever been signed off on, and printing all of it beside his name told the
 * dispatcher nothing — she is asking one question, which is what he does.
 *
 * So the tags are read down to the shape the office actually says out loud:
 * the trade, the kind of work, and commercial if he does it. "HVAC service",
 * "Plumbing install", "HVAC service · commercial". The full list stays on the
 * row as a tooltip for the times somebody does want it.
 */
const TRADES = [
  ["hvac", "HVAC"],
  ["plumb", "Plumbing"],
  ["electric", "Electrical"],
  ["refrig", "Refrigeration"],
  ["boiler", "Boiler"],
] as const;
const WORK = [
  ["install", "install"],
  ["service", "service"],
  ["maintenance", "maintenance"],
  ["repair", "service"],
] as const;

export function tradeLabel(skills?: string[]) {
  if (!skills?.length) return "No trade tagged";
  const all = skills.join(" ").toLowerCase();
  const trade = TRADES.find(([key]) => all.includes(key))?.[1];
  const work = WORK.find(([key]) => all.includes(key))?.[1];
  const commercial = all.includes("commercial");
  const core = [trade, work].filter(Boolean).join(" ");
  if (!core) return skills[0];
  return commercial ? `${core} · commercial` : core;
}
function RosterForm({
  technicians,
  save,
  submit,
}: {
  technicians: Technician[];
  save: any;
  submit: any;
}) {
  const [formError, setFormError] = useState("");
  const run = async (action: () => Promise<void>) => {
    setFormError("");
    try {
      await action();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "That didn’t save.");
      throw err;
    }
  };
  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) =>
          submit(e, () =>
            run(async () => {
              const f = new FormData(e.currentTarget);
              await save("/api/v1/workspace/dispatch/technicians", "POST", {
                name: f.get("name"),
                email: f.get("email"),
                phone: f.get("phone") || null,
              });
            }),
          )
        }
        className="space-y-3"
      >
        <SectionLabel>Add a technician</SectionLabel>
        <TextInput required label="Name" name="name" />
        <div className="grid gap-2 sm:grid-cols-2">
          <TextInput required label="Work email" name="email" type="email" />
          <TextInput label="Phone" name="phone" />
        </div>
        <PrimaryButton>Add technician</PrimaryButton>
      </form>
      <div className="space-y-2 border-t border-[var(--line)] pt-4">
        <SectionLabel>On the board</SectionLabel>
        <div className="space-y-1.5">
          {technicians.map((tech) => (
            <div
              key={tech.id}
              className="flex items-center justify-between gap-2 rounded-lg bg-[var(--wash)] px-2.5 py-2"
            >
              <span className="text-[13px] font-semibold text-[var(--ink)]">
                {tech.name}
              </span>
              <button
                type="button"
                onClick={() =>
                  void run(() =>
                    save(
                      `/api/v1/workspace/dispatch/technicians/${tech.id}`,
                      "PATCH",
                      { active: !(tech.active ?? true) },
                    ),
                  ).catch(() => {})
                }
                className="text-[11px] font-semibold text-[var(--accent)]"
              >
                {tech.active === false ? "Bring back" : "Take off"}
              </button>
            </div>
          ))}
          {!technicians.length && (
            <p className="text-[13px] text-[var(--ink-faint)]">
              Nobody on the board yet.
            </p>
          )}
        </div>
      </div>
      {formError && (
        <p
          role="alert"
          className="text-[13px] font-semibold text-[var(--accent)]"
        >
          {formError}
        </p>
      )}
    </div>
  );
}

const BLOCK_KINDS: Option[] = [
  { value: "working", label: "Working hours" },
  { value: "on_call", label: "On call" },
  { value: "time_off", label: "Time off" },
  { value: "training", label: "Training" },
];

function TechnicianDispatchProfile({ technician, blocks, appointments, timezone, date, addOnCall, manageOnCall, cancelOnCall, addEvent, openEvent }: { technician: Technician; blocks: ScheduleBlock[]; appointments: DispatchItem[]; timezone: string; date: string; addOnCall: () => void; manageOnCall: () => void; cancelOnCall: (blockId: string) => void; addEvent: () => void; openEvent: (block: ScheduleBlock) => void }) {
  const initials = technician.name.split(/\s+/).filter(Boolean).slice(0, 2).map((name) => name[0]).join("").toUpperCase();
  const onCall = blocks.filter((block) => block.kind === "on_call");
  const events = blocks.filter((block) => block.kind !== "working" && block.kind !== "on_call");
  return <div className="space-y-5"><div className="flex items-center gap-3"><>{technician.photoAttachmentId ? <img src={`${API_URL}/api/v1/workspace/attachments/${technician.photoAttachmentId}/content`} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <span className="grid h-12 w-12 place-items-center rounded-lg bg-[var(--wash)] font-semibold text-[var(--ink-soft)]">{initials || "?"}</span>}</><div><p className="font-semibold text-[var(--ink)]">{technician.name}</p><p className="text-sm text-[var(--ink-soft)]">{technician.email}{technician.phone ? ` · ${phoneNumber(technician.phone)}` : ""}</p></div></div><div><SectionLabel>Skills</SectionLabel><p className="mt-1 text-sm text-[var(--ink-soft)]">{technician.skills?.length ? technician.skills.join(" · ") : "No skills assigned"}</p></div><div className="flex items-center justify-between gap-3"><div><SectionLabel>On-call shifts</SectionLabel><p className="mt-1 text-sm text-[var(--ink-soft)]">{onCall.length ? `${onCall.length} shift${onCall.length === 1 ? "" : "s"} on ${date}` : "No on-call shift for this day."}</p></div><div className="flex flex-wrap gap-2"><QuietButton onClick={addOnCall}>Add on-call shift</QuietButton><QuietButton onClick={manageOnCall}>Recurring on-call schedule</QuietButton></div></div>{onCall.map((block) => <div key={block.id} className="flex items-center justify-between gap-2 rounded-lg border border-[var(--line)] px-3 py-2 text-sm text-[var(--ink-soft)]"><span>{block.title} · {formatDispatchTime(block.startsAt, timezone)}–{formatDispatchTime(block.endsAt, timezone)}</span><button type="button" onClick={() => cancelOnCall(block.id)} className="text-xs font-semibold text-[var(--accent)]">{block.id.startsWith("rotation:") ? "Manage schedule" : "Cancel"}</button></div>)}<div className="flex items-center justify-between gap-3"><div><SectionLabel>Blocks</SectionLabel><p className="mt-1 text-sm text-[var(--ink-soft)]">{events.length ? `${events.length} on this day` : "No blocks on this day."}</p></div><QuietButton onClick={addEvent}>Add block</QuietButton></div>{events.map((block) => <button key={block.id} type="button" onClick={() => openEvent(block)} className="flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--line)] px-3 py-2 text-left text-sm text-[var(--ink-soft)]"><span>{technicianEventLabel(block.kind)} · {block.paid ? "Paid" : "Non-paid"} · {block.title}</span><span className="text-xs font-semibold text-[var(--accent)]">Edit</span></button>)}<div><SectionLabel>Today’s jobs</SectionLabel><p className="mt-1 text-sm text-[var(--ink-soft)]">{appointments.length ? appointments.map((item) => `#${item.jobNumber} ${item.customerName}`).join(" · ") : "No jobs assigned."}</p></div></div>;
}

export function BlockForm({
  technicians,
  technicianId,
  date: boardDate,
  timezone,
  defaultKind = "working",
  save,
  submit,
  onDone,
}: {
  technicians: Technician[];
  technicianId: string;
  date: string;
  timezone: string;
  defaultKind?: string;
  save: any;
  submit: any;
  onDone?: () => void;
}) {
  const [formError, setFormError] = useState("");
  const [who, setWho] = useState(technicianId);
  const [kind, setKind] = useState(defaultKind);
  const [title, setTitle] = useState(defaultKind === "on_call" ? "On call" : "Working hours");
  const [start, setStart] = useState("08:00");
  const [minutes, setMinutes] = useState(480);
  if (kind === "on_call") return <div className="space-y-4">
    <Select label="Kind" value={kind} onChange={setKind} options={BLOCK_KINDS} />
    <OnCallRotationEditor technicians={technicians} technicianId={who} date={boardDate} timezone={timezone} save={save} done={onDone} />
  </div>;
  return (
    <form
      onSubmit={(e) =>
        submit(e, async () => {
          const f = new FormData(e.currentTarget);
          setFormError("");
          if (!who) {
            setFormError("Which technician?");
            throw new Error("no technician");
          }
          const end = endTimeForRange(start, minutes);
          if (!end) {
            setFormError("Choose a duration that ends before midnight.");
            throw new Error("overnight appointment");
          }
          try {
            await save("/api/v1/workspace/dispatch/blocks", "POST", {
              technicianId: who,
              kind,
              title: f.get("title"),
              startsAt: companyLocalToIso(`${boardDate}T${start}`, timezone),
              endsAt: companyLocalToIso(`${boardDate}T${end}`, timezone),
            });
          } catch (err) {
            setFormError(
              err instanceof Error ? err.message : "Those hours didn’t save.",
            );
            throw err;
          }
        })
      }
      className="space-y-4"
    >
      <Select
        required
        label="Technician"
        value={who}
        onChange={setWho}
        placeholder="Choose technician"
        options={technicians.map((t) => ({
          value: t.id,
          label: t.name,
          note: t.skills?.join(", "),
        }))}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="Kind"
          value={kind}
          onChange={(nextKind) => {
            setKind(nextKind);
            setTitle(BLOCK_KINDS.find((option) => option.value === nextKind)?.label ?? nextKind);
          }}
          options={BLOCK_KINDS}
        />
        <TextInput
          required
          label="Label"
          name="title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>
      <TimeRange
        label="Starts"
        start={start}
        minutes={minutes}
        onChange={(nextStart, nextMinutes) => {
          setStart(nextStart);
          setMinutes(nextMinutes);
        }}
        startHour={5}
        endHour={22}
      />
      {formError && (
        <p
          role="alert"
          className="text-[13px] font-semibold text-[var(--accent)]"
        >
          {formError}
        </p>
      )}
      <PrimaryButton>Save hours</PrimaryButton>
    </form>
  );
}

/**
 * A contact is a person — the office needs the name to ask for and the memo
 * ("gate code 4471", "call the son, not her") before it books anything. Billing
 * people stay with the customer; site people stay with the selected location.
 */
type BookingContact = {
  id: string;
  name: string;
  phone: string | null;
  phoneType: "mobile" | "home" | "work" | "other" | null;
  email: string | null;
  memo: string | null;
  isPrimary: boolean;
  from: "customer" | "location";
  doNotAlert?: boolean;
};

/**
 * The import wrote one contact row per phone and per email, all under the
 * same name, so the card was six copies of the customer. A person is the
 * unit: one name, every way to reach them under it. The name edits the
 * record that carries it; each number or address edits its own.
 */
type BookingPerson = {
  key: string;
  name: string;
  isPrimary: boolean;
  lead: BookingContact;
  phones: Array<{ contact: BookingContact; value: string; type: BookingContact["phoneType"] }>;
  emails: Array<{ contact: BookingContact; value: string }>;
  memos: string[];
};
export function groupBookingContacts(list: BookingContact[]): BookingPerson[] {
  const people = new Map<string, BookingPerson>();
  const seenEmails = new Set<string>();
  for (const contact of list) {
    const name = contact.name.trim().replace(/\s+/g, " ");
    const key = `${contact.from}:${name.toLowerCase()}`;
    let person = people.get(key);
    if (!person) {
      person = { key, name, isPrimary: false, lead: contact, phones: [], emails: [], memos: [] };
      people.set(key, person);
    }
    if (contact.isPrimary && !person.isPrimary) {
      person.isPrimary = true;
      person.lead = contact;
    }
    const digits = (contact.phone ?? "").replace(/\D/g, "");
    if (contact.phone && !person.phones.some((phone) => phone.value.replace(/\D/g, "") === digits && phone.contact.memo === contact.memo && Boolean(phone.contact.doNotAlert) === Boolean(contact.doNotAlert)))
      person.phones.push({ contact, value: contact.phone, type: contact.phoneType });
    const address = normalizedContactEmail(contact.email);
    const addressKey = `${key}:${address}:${contact.memo ?? ""}:${Boolean(contact.doNotAlert)}`;
    if (contact.email && address && !seenEmails.has(addressKey)) {
      person.emails.push({ contact, value: contact.email });
      seenEmails.add(addressKey);
    }
    const memo = (contact.memo ?? "").trim();
    if (memo && !person.memos.some((current) => current.toLowerCase() === memo.toLowerCase()))
      person.memos.push(memo);
  }
  return [...people.values()].sort(
    (a, b) =>
      Number(b.isPrimary) - Number(a.isPrimary) ||
      Number(b.phones.length > 0) - Number(a.phones.length > 0),
  );
}

function bookingContactWays(person: BookingPerson) {
  return [
    ...person.phones.map((phone) => ({
      key: `phone:${phone.contact.id}`,
      contact: phone.contact,
      value: phoneNumber(phone.value),
      note: phone.type,
    })),
    ...person.emails.map((email) => ({
      key: `email:${email.contact.id}`,
      contact: email.contact,
      value: email.value,
      note: null,
    })),
  ];
}

/**
 * The search API answers "contains", so "me" brings back James and Ahmed
 * before it brings back Melania. The office types a name from its first
 * letter, so a row whose name or email STARTS with what was typed comes
 * first, and the contains-only rows are dropped once any of those exist.
 * A matching service or billing address stays in the list so a street search
 * is not hidden by a name that happens to share those letters.
 */
export function rankCustomerMatch(
  row: Pick<BookingCustomer, "firstName" | "lastName" | "email" | "primaryContactName" | "matchingProperty" | "matchingBilling">,
  query: string,
) {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const starts = (value: string | null | undefined) =>
    Boolean(value && value.trim().toLowerCase().startsWith(q));
  if (starts(row.firstName) || starts(row.lastName)) return 0;
  if (`${row.firstName} ${row.lastName}`.toLowerCase().split(/\s+/).some((word) => word.startsWith(q))) return 1;
  if (starts(row.primaryContactName)) return 2;
  if (starts(row.email)) return 3;
  if (row.matchingProperty || row.matchingBilling) return 4;
  return 5;
}

function bookingCustomerFromMatch(row: ExistingAddressMatch): BookingCustomer {
  return {
    id: row.id,
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email ?? null,
    primaryPhone: row.primaryPhone,
    preferredPropertyId: row.preferredPropertyId,
    bookingNotes: row.bookingNotes,
    matchingProperty: row.matchingProperty
      ? {
          id: row.matchingProperty.id,
          addressLine1: row.matchingProperty.addressLine1,
          addressLine2: row.matchingProperty.addressLine2 ?? null,
          city: row.matchingProperty.city,
          state: row.matchingProperty.state ?? "",
          postalCode: row.matchingProperty.postalCode ?? null,
        }
      : null,
    matchingBilling: row.matchingBilling
      ? {
          addressLine1: row.matchingBilling.addressLine1,
          addressLine2: row.matchingBilling.addressLine2 ?? null,
          city: row.matchingBilling.city,
          state: row.matchingBilling.state ?? "",
          postalCode: row.matchingBilling.postalCode ?? null,
        }
      : null,
  };
}

/** Hours on the board are what makes a technician bookable at all. */
function technicianHasHours(blocks: ScheduleBlock[], technicianId: string) {
  return blocks.some(
    (block) =>
      block.technicianId === technicianId &&
      (block.kind === "working" || block.kind === "on_call"),
  );
}

function TechnicianTeamAssignment({ technician, teams, ready, save }: {
  technician: Technician; teams: BoardTeam[]; ready: boolean; save: (ids: string[]) => Promise<void>;
}) {
  const [selected, setSelected] = useState(() => teams.filter((team) => team.technicianIds.includes(technician.id)).map((team) => team.id));
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  return <section className="mb-5 space-y-3 border-b border-[var(--line)] pb-5" aria-label="Team assignment">
    <h3 className="font-semibold">Teams for {technician.name}</h3>
    {!ready ? <p>Loading teams…</p> : <>
      <TextInput label="Find a team" value={query} onChange={(event) => setQuery(event.target.value)} />
      <fieldset disabled={busy} className="max-h-48 space-y-2 overflow-y-auto">
        {teams.filter((team) => team.name.toLowerCase().includes(query.trim().toLowerCase())).map((team) => <label key={team.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selected.includes(team.id)} onChange={(event) => { setSaved(false); setSelected((ids) => event.target.checked ? [...ids, team.id] : ids.filter((id) => id !== team.id)); }} />{team.name}</label>)}
        {!teams.length && <p className="text-sm">Add a team on the board first.</p>}
      </fieldset>
      <p className="text-xs text-[var(--ink-soft)]">Select one or more teams. Clear all teams to leave this technician unassigned.</p>
      {error && <p role="alert">{error}</p>}
      {saved && <p role="status">Team assignment saved.</p>}
      <PrimaryButton type="button" disabled={busy} onClick={async () => {
        if (busy) return;
        setBusy(true); setError(""); setSaved(false);
        try { await save(selected); setSaved(true); }
        catch (error) { setError(error instanceof Error ? error.message : "Unable to save teams"); }
        finally { setBusy(false); }
      }}>{busy ? "Saving…" : "Save teams"}</PrimaryButton>
    </>}
  </section>;
}

function DispatchTeamEditor({ team, technicians, loadJobTypes, save, close }: {
  team: BoardTeam;
  technicians: Technician[];
  loadJobTypes: () => Promise<BookingJobType[]>;
  save: (team: BoardTeam, active?: boolean) => Promise<void>;
  close: () => void;
}) {
  const formId = useId();
  const [draft, setDraft] = useState(team);
  const [technicianQuery, setTechnicianQuery] = useState("");
  const [showSelected, setShowSelected] = useState(false);
  const filteredTechnicians = technicians.filter((technician) => (!showSelected || draft.technicianIds.includes(technician.id)) && `${technician.name} ${technician.email} ${(technician.skills ?? []).join(" ")}`.toLowerCase().includes(technicianQuery.trim().toLowerCase()));
  const [types, setTypes] = useState<BookingJobType[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [addingType, setAddingType] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    void loadJobTypes().then((items) => { if (live) { setTypes(items); setLoaded(true); } })
      .catch(() => { if (live) setError("Unable to load job types. Close and reopen the team editor to retry."); });
    return () => { live = false; };
  }, [loadJobTypes]);
  const addJobType = async () => {
    if (!newTypeName.trim() || addingType) return;
    setAddingType(true);
    setError("");
    try {
      const { item } = await mutate<{ item: BookingJobType }>("/api/v1/workspace/job-types", "POST", { name: newTypeName.trim(), afterHoursServiceMultiplier: null });
      setTypes((current) => [...current, item]);
      setDraft((current) => ({ ...current, jobTypeIds: [...current.jobTypeIds, item.id] }));
      setNewTypeName("");
    } catch (error) { setError(error instanceof Error ? error.message : "Unable to add job type"); }
    finally { setAddingType(false); }
  };
  const toggle = (field: "technicianIds" | "jobTypeIds", id: string) => setDraft((current) => ({
    ...current, [field]: current[field].includes(id) ? current[field].filter((value) => value !== id) : [...current[field], id],
  }));
  const submit = async (active = true) => {
    if (busy || addingType || !draft.name.trim() || !loaded) return;
    setBusy(true);
    setError("");
    try { await save({ ...draft, name: draft.name.trim() }, active); close(); }
    catch (error) { setError(error instanceof Error ? error.message : "Unable to save team"); }
    finally { setBusy(false); }
  };
  return <Dialog title={team.id ? "Edit team" : "Add team"} onClose={() => { if (!busy && !addingType) close(); }} footer={<div className="flex gap-2"><PrimaryButton type="submit" form={formId} disabled={busy || addingType || !loaded}>{busy ? "Saving…" : "Save team"}</PrimaryButton>
      {team.id && <QuietButton type="button" disabled={busy || addingType || !loaded} onClick={() => void submit(false)}>Archive team</QuietButton>}</div>}>
    <form id={formId} className="space-y-4" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <TextInput label="Team name" required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="HVAC Service" />
      <fieldset disabled={busy || addingType}><legend className="font-semibold">Job types</legend>
        <p className="mb-2 text-sm">Book Job shows available members of teams linked to the selected job type.</p>
        {!loaded && <p>Loading job types…</p>}
        {types.map((type) => <label key={type.id} className="flex items-center gap-2 py-1 text-sm"><input type="checkbox" checked={draft.jobTypeIds.includes(type.id)} onChange={() => toggle("jobTypeIds", type.id)} />{type.name}</label>)}
        <div className="mt-3 flex items-end gap-2"><TextInput label="New job type" value={newTypeName} onChange={(event) => setNewTypeName(event.target.value)} placeholder="HVAC Install" /><QuietButton type="button" disabled={!loaded || !newTypeName.trim()} onClick={() => void addJobType()}>Add job type</QuietButton></div>
      </fieldset>
      <fieldset disabled={busy} className="space-y-3"><legend className="font-semibold">Technicians · {draft.technicianIds.length} selected</legend>
        <TextInput label="Find technicians" value={technicianQuery} onChange={(event) => setTechnicianQuery(event.target.value)} placeholder="Search name, email, or skill" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={showSelected} onChange={(event) => setShowSelected(event.target.checked)} />Only selected technicians</label>
        <div className="flex flex-wrap gap-3 text-sm">
          <button type="button" disabled={!filteredTechnicians.length} className="font-semibold text-[var(--accent)] disabled:opacity-50" onClick={() => setDraft((current) => ({ ...current, technicianIds: [...new Set([...current.technicianIds, ...filteredTechnicians.map((technician) => technician.id)])] }))}>Select all shown ({filteredTechnicians.length})</button>
          <button type="button" disabled={!filteredTechnicians.length} className="text-[var(--ink-soft)] disabled:opacity-50" onClick={() => { const shownIds = new Set(filteredTechnicians.map((technician) => technician.id)); setDraft((current) => ({ ...current, technicianIds: current.technicianIds.filter((id) => !shownIds.has(id)) })); }}>Clear shown</button>
        </div>
        <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-[var(--line)] p-3" aria-label="Technician selection">
          {filteredTechnicians.map((technician) => <label key={technician.id} className="flex items-center gap-2 py-1 text-sm"><input type="checkbox" checked={draft.technicianIds.includes(technician.id)} onChange={() => toggle("technicianIds", technician.id)} />{technician.name}</label>)}
          {!filteredTechnicians.length && <p className="text-sm">No technicians match this filter.</p>}
        </div>
      </fieldset>
      <p className="text-sm">Technicians can belong to multiple teams. Skills remain booking guidance.</p>
      {error && <p role="alert">{error}</p>}
    </form>
  </Dialog>;
}

function capitalizeNameInitial(value: string) {
  const name = value.trim();
  return name.charAt(0).toLocaleUpperCase() + name.slice(1);
}

export function formatBookingPhone(value: string) {
  const raw = value.replace(/\D/g, "");
  const digits = raw.length === 11 && raw.startsWith("1") ? raw.slice(1) : raw;
  if (digits.length > 10) return digits;
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)})${digits.slice(3)}`;
  return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function BookingForm({
  technicians: boardTechnicians,
  teams,
  teamsReady,
  blocks: boardBlocks,
  appointments: boardAppointments,
  date: initialDate,
  timezone,
  defaultBookingDurationMinutes,
  prefill,
  bookingCustomer,
  bookingCaller,
  searchCustomers,
  loadProperties,
  loadContacts,
  loadJobTypes,
  loadMemberships,
  save,
  submit,
  onClose,
}: {
  technicians: Technician[];
  teams: BoardTeam[];
  teamsReady: boolean;
  blocks: ScheduleBlock[];
  appointments: DispatchItem[];
  date: string;
  timezone: string;
  defaultBookingDurationMinutes: number;
  prefill: LanePrefill | null;
  bookingCustomer: BookingCustomer | null;
  bookingCaller?: BookingCaller | null;
  searchCustomers: (query: string) => Promise<BookingCustomer[]>;
  loadProperties: (customerId: string) => Promise<BookingProperty[]>;
  loadContacts: (customerId: string, propertyId?: string | null) => Promise<BookingContact[]>;
  loadJobTypes: () => Promise<BookingJobType[]>;
  loadMemberships?: MembershipLoader;
  save: any;
  submit: any;
  onClose: () => void;
}) {
  const [membershipSelection,setMembershipSelection]=useState<{scope:string;ids:string[]}>({scope:"",ids:[]});
  const [boardDate, setBookingDate] = useState(initialDate);
  const [otherDay, setOtherDay] = useState<{
    date: string; technicians: Technician[]; blocks: ScheduleBlock[]; appointments: DispatchItem[];
  } | null>(null);
  const [dayError, setDayError] = useState("");
  const [dayAttempt, setDayAttempt] = useState(0);
  const dayReady = boardDate === initialDate || otherDay?.date === boardDate;
  const technicians = boardDate === initialDate ? boardTechnicians : otherDay?.date === boardDate ? otherDay.technicians : [];
  const blocks = boardDate === initialDate ? boardBlocks : otherDay?.date === boardDate ? otherDay.blocks : [];
  const appointments = boardDate === initialDate ? boardAppointments : otherDay?.date === boardDate ? otherDay.appointments : [];
  useEffect(() => {
    if (boardDate === initialDate || !boardDate) return;
    let current = true;
    setDayError("");
    void data<{ technicians: Technician[]; blocks: ScheduleBlock[]; appointments: DispatchItem[] }>(
      `/api/v1/workspace/dispatch/board?date=${boardDate}`,
    ).then((result) => { if (current) setOtherDay({ ...result, date: boardDate }); })
      .catch(() => { if (current) setDayError("Unable to load availability for this day."); });
    return () => { current = false; };
  }, [boardDate, initialDate, dayAttempt]);
  const [customerMode, setCustomerMode] = useState<"existing" | "new">(
    "existing",
  );
  const [customer, setCustomer] = useState<BookingCustomer | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const formId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const stepRef = useRef<HTMLDivElement>(null);
  const [reviewFields, setReviewFields] = useState<Record<string, string>>({});
  const [bookingBusy, setBookingBusy] = useState(false);
  const [customerConfirmed, setCustomerConfirmed] = useState(false);
  const bookingBusyRef = useRef(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [customerLoadError, setCustomerLoadError] = useState(false);
  const customerRequest = useRef(0);
  useEffect(() => {
    if (step === 1) return;
    stepRef.current?.focus();
    stepRef.current?.closest('[role="dialog"]')?.querySelector(".dialog-body")?.scrollTo?.({ top: 0 });
  }, [step]);
  const [query, setQuery] = useState(bookingCaller?.phone ?? "");
  const callerLookupPending = useRef(Boolean(bookingCaller));
  const [results, setResults] = useState<BookingCustomer[]>([]);
  const bookingSearchRequestRef = useRef(0);
  const [contacts, setContacts] = useState<BookingContact[]>([]);
  const [contactEditField, setContactEditField] = useState<"name" | "phone" | "email" | "memo" | null>(null);
  const [moreLocationPeople, setMoreLocationPeople] = useState(false);
  const [loadingLocationPeople, setLoadingLocationPeople] = useState(false);
  const [locationContactsError, setLocationContactsError] = useState(false);
  const [locationContactAttempt, setLocationContactAttempt] = useState(0);
  // A customer with one building is booked at it; with several, the list is
  // open until one is picked and then folds to the pick.
  const [pickingLocation, setPickingLocation] = useState(true);
  const [locationPage, setLocationPage] = useState(0);
  const [editingContact, setEditingContact] = useState<BookingContact | null>(null);
  const [contactDraft, setContactDraft] = useState({
    name: "",
    phone: "",
    phoneType: "mobile" as NonNullable<BookingContact["phoneType"]>,
    email: "",
    memo: "",
    isPrimary: false,
    doNotAlert: false,
  });
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState("");

  useEffect(() => {
    const requestId = ++bookingSearchRequestRef.current;
    const term = query.trim();
    if (!term) {
      setResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void searchCustomers(term)
        .then((found) => {
          if (requestId !== bookingSearchRequestRef.current) return;
          setResults(found);
          if (callerLookupPending.current && bookingCaller && term === bookingCaller.phone) {
            callerLookupPending.current = false;
            const match = matchingCaller(found, bookingCaller.phone);
            if (match) void chooseCustomer(match);
          }
        })
        .catch(() => {
          if (requestId === bookingSearchRequestRef.current) setResults([]);
        });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [query, searchCustomers]);

  const beginContactEdit = (contact: BookingContact, field: "name" | "phone" | "email" | "memo" | null = null) => {
    setContactEditField(field);
    setEditingContact(contact);
    setContactDraft({
      name: contact.name,
      phone: formatBookingPhone(contact.phone ?? ""),
      phoneType: contact.phoneType ?? "mobile",
      email: contact.email ?? "",
      memo: contact.memo ?? "",
      isPrimary: contact.isPrimary,
      doNotAlert: contact.doNotAlert ?? false,
    });
    setContactError("");
  };
  /**
   * A bare number off the old customer record has no card behind it. Saving
   * one from here makes the card, so the next booking sees a person.
   */
  async function saveContact() {
    if (!customer || !editingContact) return;
    const phone = contactDraft.phone.replace(/\D/g, "") || null;
    if ((phone && phone.length !== 10) || (contactEditField === "phone" && !phone)) return setContactError("Enter a 10-digit phone number, including the area code.");
    const email = contactDraft.email.trim() || null;
    const name = contactDraft.name.trim();
    if (!name) return setContactError("Give them a name.");
    if (!phone && !email) return setContactError("A phone or an email.");
    const fresh = editingContact.id.startsWith("method-");
    const owner =
      editingContact.from === "location" && propertyId
        ? `/api/v1/workspace/properties/${propertyId}/contacts`
        : `/api/v1/workspace/customers/${customer.id}/contacts`;
    setContactSaving(true);
    try {
      await save(fresh ? owner : `${owner}/${editingContact.id}`, fresh ? "POST" : "PATCH", {
        name,
        phone,
        phoneType: phone ? contactDraft.phoneType : null,
        email,
        memo: contactDraft.memo.trim() || null,
        isPrimary: contactDraft.isPrimary,
        doNotAlert: contactDraft.doNotAlert,
      });
      setContacts(await loadContacts(customer.id, propertyId || undefined));
      setEditingContact(null);
    } catch (err) {
      setContactError(
        err instanceof Error ? err.message : "We couldn’t save that contact.",
      );
    } finally {
      setContactSaving(false);
    }
  }
  const [contactDeleteError, setContactDeleteError] = useState("");
  async function deleteBookingPerson(person: BookingPerson) {
    if (!customer || contactSaving) return;
    const records = [...new Map([person.lead, ...bookingContactWays(person).map(way => way.contact)].filter(contact => !contact.id.startsWith("method-")).map(contact => [contact.id, contact])).values()];
    if (!records.length || !window.confirm(`Delete ${person.name || "this contact"} and their saved phone numbers, emails and memos?`)) return;
    setContactSaving(true); setContactDeleteError("");
    try {
      for (const contact of records) {
        if (contact.from === "location" && !propertyId) throw new Error("Select the service location before deleting its contact.");
        const owner = contact.from === "location" ? `properties/${propertyId}` : `customers/${customer.id}`;
        await save(`/api/v1/workspace/${owner}/contacts/${contact.id}`, "DELETE", {});
        setContacts(current => current.filter(item => item.id !== contact.id));
        setEditingContact(current => current?.id === contact.id ? null : current);
      }
      setContacts(await loadContacts(customer.id, propertyId || undefined));
    } catch (error) {
      setContactDeleteError(error instanceof Error ? error.message : "Could not delete the contact. Please try again.");
    } finally { setContactSaving(false); }
  }
  async function removeContact() {
    if (!customer || !editingContact || editingContact.id.startsWith("method-")) return;
    if (!window.confirm(`Remove ${editingContact.name || "this contact"}?`)) return;
    const owner =
      editingContact.from === "location" && propertyId
        ? `/api/v1/workspace/properties/${propertyId}/contacts/${editingContact.id}`
        : `/api/v1/workspace/customers/${customer.id}/contacts/${editingContact.id}`;
    setContactSaving(true);
    try {
      await save(owner, "DELETE", {});
      setContacts(await loadContacts(customer.id, propertyId || undefined));
      setEditingContact(null);
    } catch (err) {
      setContactError(
        err instanceof Error ? err.message : "We couldn’t remove that contact.",
      );
    } finally {
      setContactSaving(false);
    }
  }
  const [properties, setProperties] = useState<BookingProperty[]>([]);
  const [propertyId, setPropertyId] = useState("");
  const [propertyType, setPropertyType] = useState<
    "residential" | "commercial"
  >("residential");
  const [propertyQuery, setPropertyQuery] = useState("");
  const [sameBilling, setSameBilling] = useState(true);
  const [jobTypes, setJobTypes] = useState<BookingJobType[]>([]);
  const [jobTypeId, setJobTypeId] = useState("");
  const membershipTypeApplied = useRef<string | null>(null);
  useEffect(() => {
    const context = customer?.membershipBooking;
    if (!context) { membershipTypeApplied.current = null; return; }
    const key = `${context.membershipId}:${context.eventIds.join(',')}`;
    if (!jobTypes.length || membershipTypeApplied.current === key) return;
    membershipTypeApplied.current = key;
    const type = membershipBookingJobType(jobTypes, context.season);
    setJobTypeId(type?.id ?? "");
    setJobTypeQuery(type ? "" : context.season ? `PM ${context.season} Visit` : "");
  }, [customer, jobTypes]);
  const [bookingTeamId, setBookingTeamId] = useState("");
  // BOOK-07: the office knows what it wants and types it — "hv" should offer
  // HVAC Service and HVAC Install, not a list to scroll past.
  const [jobTypeQuery, setJobTypeQuery] = useState("");
  const [jobTypeOpen, setJobTypeOpen] = useState(false);
  const [notes, setNotes] = useState(bookingCaller?.bookingNotes ?? "");
  /*
   * BOOK-11: a duplicate customer is only ever noticed later, when the money
   * and the history have already split across two records. The same search the
   * office just used is asked again as the new name is typed.
   */
  const [maybeExisting, setMaybeExisting] = useState<BookingCustomer[]>([]);
  const [addressAlreadyOnFile, setAddressAlreadyOnFile] = useState<BookingCustomer[]>([]);
  const [newName, setNewName] = useState({ first: "", last: "" });
  const [noEmailProvided, setNoEmailProvided] = useState(false);
  const callerPhoneDigits = (bookingCaller?.phone ?? "").replace(/\D/g, "");
  const callerPhone = formatBookingPhone(callerPhoneDigits.length === 11 && callerPhoneDigits.startsWith("1") ? callerPhoneDigits.slice(1) : callerPhoneDigits);
  const [additionalContacts, setAdditionalContacts] = useState<Array<{
    id: string; channel: "phone" | "email"; value: string; memo: string; phoneType: "mobile" | "home" | "work" | "other"; doNotAlert: boolean;
  }>>([]);
  const [billingContactRows, setBillingContactRows] = useState<typeof additionalContacts>([]);
  const [newPhone, setNewPhone] = useState(callerPhone);
  const [newEmail, setNewEmail] = useState("");
  const [phoneAlertsOff, setPhoneAlertsOff] = useState(false);
  const [emailAlertsOff, setEmailAlertsOff] = useState(false);
  const [excludedBillingContacts, setExcludedBillingContacts] = useState<string[]>([]);
  const serviceContactChoices = [
    ...(newPhone ? [{ id: "phone", value: newPhone }] : []),
    ...(!noEmailProvided && newEmail ? [{ id: "email", value: newEmail }] : []),
    ...additionalContacts.filter(row => row.value.trim()).map(row => ({ id: row.id, value: row.value })),
  ];
  const [customTime, setCustomTime] = useState(false);
  const [bookingTimeChosen, setBookingTimeChosen] = useState(Boolean(prefill?.startsAt));
  const [availabilityNow, setAvailabilityNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setAvailabilityNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  function addBookingContact(channel: "phone" | "email", billing = false) {
    const setRows = billing ? setBillingContactRows : setAdditionalContacts;
    setRows((rows) => [...rows, { id: crypto.randomUUID(), channel, value: "", memo: "", phoneType: "mobile", doNotAlert: false }]);
  }

  useEffect(() => {
    const typed = `${newName.first} ${newName.last}`.trim();
    if (customerMode !== "new" || typed.length < 3) {
      setMaybeExisting([]);
      return;
    }
    let live = true;
    const timer = window.setTimeout(async () => {
      try {
        const found = await searchCustomers(typed);
        if (live) setMaybeExisting(found.slice(0, 4));
      } catch {
        if (live) setMaybeExisting([]);
      }
    }, 350);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [customerMode, newName.first, newName.last, searchCustomers]);
  const [start, setStart] = useState(
    prefill?.startsAt.slice(11, 16) ?? "09:00",
  );
  const [minutes, setMinutes] = useState(defaultBookingDurationMinutes);
  const [technicianIds, setTechnicianIds] = useState<string[]>(() => prefill?.technicianId ? [prefill.technicianId] : []);
  const [showOtherTechnicians, setShowOtherTechnicians] = useState(false);
  /*
   * A booking gets a window. "Set time later" was a way to forget — a job with
   * no time is a job nobody is going to (Melania, 1/9/2026). The flag stays in
   * the payload as false so the endpoint keeps its contract.
   */
  const scheduleLater = false;
  const [formError, setFormError] = useState("");

  useEffect(() => {
    void loadJobTypes()
      .then(setJobTypes)
      .catch(() => setFormError("We couldn’t load job types."));
  }, [loadJobTypes]);
  useEffect(() => {
    if (!bookingCustomer) return;
    void chooseCustomer(bookingCustomer);
    return () => { customerRequest.current += 1; };
  }, [bookingCustomer, loadContacts, loadProperties]);

  // A new customer has nothing on file, so there is no saved location to pick
  // and no old selection that may still be sitting in state. Clearing here is
  // what stops the form booking the previous customer's address.
  function chooseMode(mode: "existing" | "new") {
    callerLookupPending.current = false;
    if (mode === "new") {
      const [first = "", ...last] = query.trim().split(/\s+/);
      setNewName({ first: capitalizeNameInitial(first), last: capitalizeNameInitial(last.join(" ")) });
      setAdditionalContacts([]);
      setBillingContactRows([]);
      setNoEmailProvided(false);
      setNewPhone(callerPhone);
      setNewEmail("");
      setPhoneAlertsOff(false);
      setEmailAlertsOff(false);
      setExcludedBillingContacts([]);
      setSameBilling(true);
    }
    customerRequest.current += 1;
    setStep(1);
    setLoadingCustomer(false);
    setCustomerLoadError(false);
    setEditingContact(null);
    setCustomerMode(mode);
    setCustomer(null);
    setContacts([]);

    setMoreLocationPeople(false);
    setLoadingLocationPeople(false);
    setPickingLocation(true);
    setLocationPage(0);
    setProperties([]);
    setPropertyId("");
    setPropertyType("residential");
    setQuery("");
    setResults([]);
    setPropertyQuery("");
    setNotes("");
    setFormError("");
    setAddressAlreadyOnFile([]);
  }

  // The service location has its own people — the tenant, the site manager —
  // and they only make sense once the office has said which building.
  useEffect(() => {
    setLocationContactsError(false);
    setEditingContact(null);
    if (!customer || !propertyId) {
      setContacts((current) => current.filter((contact) => contact.from !== "location"));
      setLoadingLocationPeople(false);
      return;
    }
    let stale = false;
    setMoreLocationPeople(false);
    setLoadingLocationPeople(true);
    setContacts((current) => current.filter((contact) => contact.from !== "location"));
    loadContacts(customer.id, propertyId)
      .then((people) => {
        if (!stale)
          setContacts((current) => {
            const nextBilling = people.filter((contact) => contact.from === "customer");
            const nextLocation = people.filter((contact) => contact.from === "location");
            return [
              ...(nextBilling.length
                ? nextBilling
                : current.filter((contact) => contact.from === "customer")),
              ...nextLocation,
            ];
          });
      })
      .catch(() => {
        if (!stale) {
          setLocationContactsError(true);
          setContacts((current) => current.filter((contact) => contact.from !== "location"));
        }
      })
      .finally(() => {
        if (!stale) setLoadingLocationPeople(false);
      });
    return () => {
      stale = true;
    };
  }, [customer, propertyId, loadContacts, locationContactAttempt]);

  async function chooseCustomer(chosen: BookingCustomer) {
    const request = ++customerRequest.current;
    setCustomerMode("existing");
    setStep(1);
    setCustomer(chosen);
    setResults([]);
    setQuery("");
    setPropertyId("");
    setProperties([]);
    setContacts([]);
    setPropertyQuery("");
    setPickingLocation(true);
    setLocationPage(0);

    setMoreLocationPeople(false);
    setNotes(bookingCaller?.bookingNotes ?? chosen.bookingNotes ?? "");
    setFormError("");
    setCustomerLoadError(false);
    setLoadingCustomer(true);
    setAddressAlreadyOnFile([]);
    try {
      const [locations, methods] = await Promise.all([
        loadProperties(chosen.id),
        loadContacts(chosen.id),
      ]);
      if (request !== customerRequest.current) return;
      setProperties(locations);
      setContacts(methods);
      const matched = chosen.matchingProperty
        ? locations.find((location) => location.id === chosen.matchingProperty?.id)
        : undefined;
      const preferred = locations.find((location) => location.id === chosen.preferredPropertyId);
      if (matched) {
        setPropertyId(matched.id);
        setPickingLocation(false);
      } else if (preferred || locations.length === 1) {
        setPropertyId((preferred ?? locations[0]).id);
        setPickingLocation(false);
      }
    } catch {
      if (request === customerRequest.current) setCustomerLoadError(true);
    } finally {
      if (request === customerRequest.current) setLoadingCustomer(false);
    }
  }

  async function checkAddressOnFile(address: StreetAddress) {
    const needle = address.addressLine1.trim();
    if (needle.length < 3) {
      setAddressAlreadyOnFile([]);
      return;
    }
    try {
      const found = await searchCustomers(needle);
      setAddressAlreadyOnFile(
        found
          .filter(
            (row) =>
              row.id !== customer?.id &&
              ((row.matchingProperty && sameStreetAndZip(row.matchingProperty, address)) ||
                (row.matchingBilling && sameStreetAndZip(row.matchingBilling, address))),
          )
          .slice(0, 4),
      );
    } catch {
      setAddressAlreadyOnFile([]);
    }
  }

  /**
   * WHICH TIMES ARE ACTUALLY OPEN.
   *
   * Each chip is a two-hour slot with a count of the technicians whose working
   * hours cover it and who have nothing booked across it. Only members of
   * teams linked to the job type count.
   */
  const chosenJobType = jobTypes.find((jobType) => jobType.id === jobTypeId);
  const matchingTeams = teams.filter((team) => team.jobTypeIds.includes(jobTypeId));
  const afterHoursBooking = /after[\s-]*hours/i.test(chosenJobType?.name ?? "");
  const bookingTeam = matchingTeams.length === 1 ? matchingTeams[0] : matchingTeams.find((team) => team.id === bookingTeamId);
  const eligibleTeams = afterHoursBooking ? bookingTeam ? [bookingTeam] : [] : matchingTeams;
  const teamMemberIds = new Set(eligibleTeams.flatMap((team) => team.technicianIds));
  const bookingBlocks = useMemo(() => afterHoursBooking ? blocks.filter((block) => block.kind !== "working") : blocks, [afterHoursBooking, blocks]);
  const availabilityPool = technicians.filter((technician) => teamMemberIds.has(technician.id));
  const openWindowsByTechnician = useMemo(
    () =>
      new Map(
        technicians.map((technician) => [
          technician.id,
          technicianOpenWindows(bookingBlocks, appointments, technician.id),
        ]),
      ),
    [technicians, bookingBlocks, appointments],
  );
  const bookableBlocks = bookingBlocks.filter(
    (block) => teamMemberIds.has(block.technicianId) && (block.kind === "working" || block.kind === "on_call"),
  );
  const localMinutes = (value: string) => {
    const at = isoToCompanyLocal(value, timezone);
    return Number(at.slice(11, 13)) * 60 + Number(at.slice(14, 16));
  };
  const dayOpens = bookableBlocks.length
    ? Math.min(...bookableBlocks.map((block) => localMinutes(block.startsAt)))
    : 8 * 60;
  const dayCloses = bookableBlocks.length
    ? Math.max(...bookableBlocks.map((block) => localMinutes(block.endsAt)))
    : 17 * 60;
  const slots: Array<{ minutes: number; open: number }> = [];
  for (
    let at = Math.ceil(dayOpens / defaultBookingDurationMinutes) * defaultBookingDurationMinutes;
    at + defaultBookingDurationMinutes <= Math.floor(dayCloses / defaultBookingDurationMinutes) * defaultBookingDurationMinutes;
    at += defaultBookingDurationMinutes
  ) {
    const from = Date.parse(
      companyLocalToIso(`${boardDate}T${minutesToLocalTime(at)}`, timezone),
    );
    const to = from + defaultBookingDurationMinutes * 60_000;
    slots.push({
      minutes: at,
      open: availabilityPool.filter((technician) =>
        (openWindowsByTechnician.get(technician.id) || []).some(
          (window) =>
            Date.parse(window.startsAt) <= from &&
            Date.parse(window.endsAt) >= to,
        ),
      ).length,
    });
  }

  const availableSlots = slots.filter((slot) => {
    const from = Date.parse(companyLocalToIso(`${boardDate}T${minutesToLocalTime(slot.minutes)}`, timezone));
    return dayReady && slot.open > 0 && from + defaultBookingDurationMinutes * 60_000 > availabilityNow;
  });
  const availableSlotKey = availableSlots.map((slot) => slot.minutes).join(",");
  useEffect(() => {
    if (customTime || step === 3 || !dayReady || !jobTypeId || !teamsReady) return;
    const choices = availableSlotKey ? availableSlotKey.split(",").map(Number) : [];
    if (!choices.some((at) => minutesToLocalTime(at) === start)) {
      setStart(choices.length ? minutesToLocalTime(choices[0]) : "");
      setMinutes(defaultBookingDurationMinutes);
    }
  }, [availableSlotKey, customTime, dayReady, start, jobTypeId, teamsReady, step]);

  const needsNewAddress =
    customerMode === "new" || (Boolean(customer) && !properties.length);
  const filtered = propertyQuery.trim()
    ? properties.filter((property) =>
        `${property.name ?? ""} ${property.addressLine1} ${property.city} ${property.postalCode ?? ""}`
          .toLowerCase()
          .includes(propertyQuery.trim().toLowerCase()),
      )
    : properties;

  /*
   * Team membership determines the shortlist. Availability and skill details
   * help rank the choices, without treating missing skills as a booking block.
   */
  const windowFrom = (() => {
    try {
      return Date.parse(companyLocalToIso(`${boardDate}T${start}`, timezone));
    } catch {
      return Number.NaN;
    }
  })();
  const windowTo = windowFrom + minutes * 60_000;
  const windowLabel = start ? `${readableTime(start)}–${readableTime(endTimeForRange(start, minutes) || start)}` : "Choose an arrival window";
  const isPastWindow = bookingTimeChosen && Number.isFinite(windowTo) && windowTo <= availabilityNow;
  const technicianChoices = rankTechniciansForSkills(
    technicians,
    Array.isArray(chosenJobType?.requiredSkillIds)
      ? chosenJobType.requiredSkillIds
      : [],
  )
    .map(({ technician, rank }) => {
      const hours = technicianHasHours(blocks, technician.id);
      const free =
        hours &&
        (openWindowsByTechnician.get(technician.id) || []).some(
          (window) =>
            Date.parse(window.startsAt) <= windowFrom &&
            Date.parse(window.endsAt) >= windowTo,
        );
      const wrongTrade =
        Array.isArray(chosenJobType?.requiredSkillIds) &&
        chosenJobType.requiredSkillIds.length > 0 &&
        rank !== 0;
      return {
        technician,
        disabled: !hours,
        wrongTrade,
        free,
        order: (hours ? 0 : 4) + (wrongTrade ? 2 : 0) + (free ? 0 : 1),
        note: !hours
          ? "Outside working hours — approval required"
          : free
            ? `Free ${windowLabel}`
            : "Unavailable — approval required",
        trade: technician.skills?.length ? tradeLabel(technician.skills) : "",
      };
    })
    .sort(
      (left, right) =>
        left.order - right.order ||
        left.technician.name.localeCompare(right.technician.name),
    );
  // Keep an explicitly selected board technician visible even when outside
  // the job type’s normal teams or available window.
  const chosenChoices = technicianChoices.filter(
    (choice) => technicianIds.includes(choice.technician.id),
  );
  const choiceClass = (checked: boolean) =>
    `flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-[13px] transition disabled:cursor-not-allowed disabled:opacity-60 ${checked ? "border-[var(--accent)] bg-[var(--accent-wash)]" : "border-[var(--line)] hover:bg-[var(--wash)]"}`;

  const ranked = results
    .map((row) => ({ row, rank: rankCustomerMatch(row, query) }))
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        a.row.lastName.localeCompare(b.row.lastName) ||
        a.row.firstName.localeCompare(b.row.firstName),
    );
  const hits = (ranked.some((entry) => entry.rank < 4)
    ? ranked.filter((entry) => entry.rank < 4 || entry.row.matchingProperty || entry.row.matchingBilling)
    : ranked
  ).map((entry) => entry.row);
  const billingPeople = groupBookingContacts(
    contacts.filter((contact) => contact.from === "customer"),
  );
  const locationPeople = groupBookingContacts(
    contacts.filter((contact) => contact.from === "location"),
  );
  const shownLocationPeople = locationPeople;
  const locationHasMore =
    !moreLocationPeople &&
    (locationPeople.length > shownLocationPeople.length ||
      shownLocationPeople.some(
        (person) => bookingContactWays(person).length > 2 || person.memos.length > 1,
      ));
  const LOCATION_PAGE = 4;
  const locationPages = Math.max(1, Math.ceil(filtered.length / LOCATION_PAGE));
  const pageOfLocations = filtered.slice(
    Math.min(locationPage, locationPages - 1) * LOCATION_PAGE,
    Math.min(locationPage, locationPages - 1) * LOCATION_PAGE + LOCATION_PAGE,
  );
  useEffect(()=>{
    const context = customer?.membershipBooking;
    setMembershipSelection(context && customerMode === "existing" && !needsNewAddress && propertyId === context.propertyId
      ? {scope:`${customer!.id}:${propertyId}`,ids:context.eventIds} : {scope:"",ids:[]});
  },[customer?.id,customer?.membershipBooking,propertyId,customerMode,needsNewAddress,scheduleLater]);
  const chosenProperty = properties.find((property) => property.id === propertyId);
  const bookingDateLabel = formatDispatchDate(`${boardDate}T12:00:00Z`, timezone, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const customerName = customer
    ? `${customer.firstName} ${customer.lastName}`.trim()
    : "";
  const memoLabel = (contact: BookingContact) => contact.phone && contact.email ? "Phone & email memo" : contact.phone ? "Phone memo" : contact.email ? "Email memo" : "Contact memo";
  const addContactMethod = (person: BookingPerson, field: "phone" | "email") => beginContactEdit({ ...person.lead, id: "method-new", phone: null, email: null, memo: null, isPrimary: false, doNotAlert: false }, field);
  const contactDisplayWays = (person: BookingPerson) => {
    const groups = new Map<string, ReturnType<typeof bookingContactWays>>();
    for (const way of bookingContactWays(person)) {
      const key = way.key.startsWith("phone:") ? `phone:${way.value.replace(/\D/g, "")}` : `email:${way.value.trim().toLowerCase()}`;
      groups.set(key, [...(groups.get(key) ?? []), way]);
    }
    return [...groups.values()];
  };
  const personCard = (person: BookingPerson, options: { expanded?: boolean; fallbackName?: string; location?: boolean } = {}) => <BookingContactCard key={person.key} name={person.name || options.fallbackName || "Contact"} records={[...new Map([person.lead, ...bookingContactWays(person).map(way => way.contact)].map(contact => [contact.id, contact])).values()]} editLabel={options.location && shownLocationPeople.length === 1 ? "Edit service location contact" : `Edit ${options.location ? "service location" : "billing"} contact ${person.name}`} saveRecord={async record => {
    const owner = record.from === "location" ? `properties/${propertyId}` : `customers/${customer!.id}`;
    const fresh = record.id.startsWith("method-");
    await save(`/api/v1/workspace/${owner}/contacts${fresh ? "" : `/${record.id}`}`, fresh ? "POST" : "PATCH", { name: record.name, phone: record.phone, phoneType: record.phone ? record.phoneType || "mobile" : null, email: record.email, memo: record.memo, isPrimary: record.isPrimary, doNotAlert: record.doNotAlert ?? false });
  }} onSaved={async () => setContacts(await loadContacts(customer!.id, propertyId || undefined))} onDelete={() => deleteBookingPerson(person)}>
    <div className="booking-contact-methods">{contactDisplayWays(person).map(ways => {
      const way = ways.find(item => item.contact.isPrimary) ?? ways[0];
      const invalidPhone = way.key.startsWith("phone:") && way.value.replace(/\D/g, "").length !== 10;
      const notes = ways.filter((item, index) => item.contact.memo && ways.findIndex(other => other.contact.memo === item.contact.memo) === index);
      return <div key={way.key} className="booking-contact-method">
        <button type="button" className="booking-contact-value" title={`Edit ${way.value}`} >{way.value}{invalidPhone && <span className="booking-invalid-phone">Invalid phone · correct number</span>}{ways.some(item => item.contact.doNotAlert) && <ContactNotifications off />}</button>
        <div className="booking-contact-row-memos">{notes.map(note => <button key={note.key} type="button" className="booking-primary-memo booking-memo-action" aria-label={`Edit memo for ${way.value}: ${note.contact.memo}`} ><span className="sr-only">{memoLabel(note.contact)}:</span> {note.contact.memo}</button>)}</div>
      </div>;
    })}</div>
  </BookingContactCard>;
  const contactEditor = editingContact && (contactEditField ? <div className="booking-inline-contact-editor">
    {editingContact.id.startsWith("method-") && <TextInput label="Contact name" value={contactDraft.name} onChange={event => setContactDraft(draft => ({ ...draft, name: event.target.value }))} />}
    {contactEditField !== "memo" && <TextInput autoFocus label={`Contact ${contactEditField}`} type={contactEditField === "email" ? "email" : contactEditField === "phone" ? "tel" : "text"} value={contactDraft[contactEditField]} onChange={event => setContactDraft(draft => ({ ...draft, [contactEditField]: contactEditField === "phone" ? formatBookingPhone(event.target.value) : event.target.value }))} />}
    {contactEditField !== "phone" && contactDraft.phone && contactDraft.phone.replace(/\D/g, "").length !== 10 && <TextInput label="Contact phone" type="tel" value={contactDraft.phone} onChange={event => setContactDraft(draft => ({ ...draft, phone: formatBookingPhone(event.target.value) }))} />}
    <p className="booking-contact-caption">{memoLabel(editingContact)}{editingContact.phone || editingContact.email ? ` · ${[editingContact.phone ? phoneNumber(editingContact.phone) : null, editingContact.email].filter(Boolean).join(" · ")}` : ""}</p>
    <TextInput label="Contact memo" value={contactDraft.memo} onChange={event => setContactDraft(draft => ({ ...draft, memo: event.target.value }))} />
    <label><input type="checkbox" checked={contactDraft.isPrimary} onChange={event => setContactDraft(draft => ({ ...draft, isPrimary: event.target.checked }))} />Primary contact</label>
    <div className="booking-inline-contact-actions"><ContactNotifications off={contactDraft.doNotAlert} disabled={contactSaving} onChange={off => setContactDraft(draft => ({ ...draft, doNotAlert: off }))} /><button type="button" disabled={contactSaving} className="booking-text-action" onClick={() => void saveContact()}>{contactSaving ? "Saving…" : "Save contact"}</button><button type="button" disabled={contactSaving} className="booking-text-action" onClick={() => setEditingContact(null)}>Cancel</button></div>
    {contactError && <p role="alert" className="booking-error">{contactError}</p>}
  </div> : (
    <div className="mt-3 grid gap-2 rounded-lg border border-[var(--line)] bg-[var(--wash)] p-3 sm:grid-cols-2">
      <input
        aria-label="Contact name"
        placeholder="Name"
        value={contactDraft.name}
        onChange={(event) =>
          setContactDraft((draft) => ({ ...draft, name: event.target.value }))
        }
        className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm sm:col-span-2"
      />
      <input
        aria-label="Contact phone"
        placeholder="Phone"
        value={contactDraft.phone}
        onChange={(event) =>
          setContactDraft((draft) => ({ ...draft, phone: formatBookingPhone(event.target.value) }))
        }
        className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
      />
      <select
        aria-label="Contact phone type"
        value={contactDraft.phoneType}
        onChange={(event) =>
          setContactDraft((draft) => ({
            ...draft,
            phoneType: event.target.value as typeof draft.phoneType,
          }))
        }
        className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
      >
        <option value="mobile">Mobile / cell</option>
        <option value="work">Work</option>
        <option value="home">Home</option>
        <option value="other">Other</option>
      </select>
      <input
        aria-label="Contact email"
        type="email"
        placeholder="Email"
        value={contactDraft.email}
        onChange={(event) =>
          setContactDraft((draft) => ({ ...draft, email: event.target.value }))
        }
        className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm sm:col-span-2"
      />
      <input
        aria-label="Contact memo"
        placeholder="Memo"
        value={contactDraft.memo}
        onChange={(event) =>
          setContactDraft((draft) => ({ ...draft, memo: event.target.value }))
        }
        className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm sm:col-span-2"
      />
      <label className="flex items-center gap-2 text-sm text-[var(--ink-soft)] sm:col-span-2">
        <input
          type="checkbox"
          checked={contactDraft.isPrimary}
          onChange={(event) =>
            setContactDraft((draft) => ({ ...draft, isPrimary: event.target.checked }))
          }
        />
        Primary contact
      </label>
      <div className="flex items-center gap-2 sm:col-span-2">
        <ContactNotifications off={contactDraft.doNotAlert} disabled={contactSaving} onChange={(off) => setContactDraft((draft) => ({ ...draft, doNotAlert: off }))} />
        <span>{contactDraft.doNotAlert ? "Notifications off" : "Notifications on"}</span>
      </div>
      <p className="text-xs text-[var(--ink-soft)] sm:col-span-2">Saved on this contact for all jobs. Other contacts keep their own preferences.</p>
      {contactError && (
        <p role="alert" className="text-[13px] font-semibold text-[var(--st-alert)] sm:col-span-2">
          {contactError}
        </p>
      )}
      <div className="flex gap-2 sm:col-span-2">
        <button
          type="button"
          disabled={contactSaving}
          onClick={() => void saveContact()}
          className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-60"
        >
          {contactSaving ? "Saving…" : "Save contact"}
        </button>
        <button
          type="button"
          onClick={() => setEditingContact(null)}
          className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)]"
        >
          Cancel
        </button>
        {!editingContact.id.startsWith("method-") && (
          <button
            type="button"
            disabled={contactSaving}
            onClick={() => void removeContact()}
            className="rounded-lg border border-[var(--accent)] px-3 py-2 text-sm font-semibold text-[var(--accent)] disabled:opacity-60"
          >
            Remove contact
          </button>
        )}
      </div>
    </div>
  ));
  const addContactButton = (channel: "phone" | "email", billing = false) => <button type="button" aria-label={billing ? `Add billing ${channel}` : `Add another ${channel}`} title={billing ? `Add billing ${channel}` : `Add another ${channel}`} disabled={billing ? billingContactRows.length + serviceContactChoices.filter(row => !excludedBillingContacts.includes(row.id)).length >= 20 : additionalContacts.length >= 18} onClick={() => addBookingContact(channel, billing)} className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[var(--accent)] bg-[var(--accent-wash)] text-[var(--accent)] disabled:opacity-40"><Plus size={15} aria-hidden="true" /></button>;
  const extraContactRow = (row: typeof additionalContacts[number], index: number, billing = false) => {
    const setRows = billing ? setBillingContactRows : setAdditionalContacts;
    const prefix = billing ? "Billing " : "";
    return <div key={row.id} role="group" aria-label={`${prefix}${row.channel} contact ${index + 1}`} className={`grid items-start gap-2 sm:col-span-2 ${row.channel === "phone" ? "sm:grid-cols-[1fr_1fr_100px_auto]" : "sm:grid-cols-[1fr_1fr_auto]"}`}>
    <TextInput required label={row.channel === "phone" ? "Phone" : "Email"} aria-label={billing ? `Billing ${row.channel} ${index + 1}` : `Additional ${row.channel} ${index + 1}`} type={row.channel === "email" ? "email" : "tel"} pattern={row.channel === "phone" ? "[()]?[0-9]{3}[)]?\\s?[0-9]{3}-[0-9]{4}" : undefined} maxLength={row.channel === "phone" ? 40 : 254} value={row.value} onChange={event => setRows(rows => rows.map(item => item.id === row.id ? { ...item, value: row.channel === "phone" ? formatBookingPhone(event.target.value) : event.target.value } : item))} />
    <TextInput label="Memo" aria-label={`${prefix}Memo ${index + 1}`} maxLength={2000} value={row.memo} onChange={event => setRows(rows => rows.map(item => item.id === row.id ? { ...item, memo: event.target.value } : item))} />
    {row.channel === "phone" && <select aria-label={`${prefix}Phone type ${index + 1}`} value={row.phoneType} onChange={event => setRows(rows => rows.map(item => item.id === row.id ? { ...item, phoneType: event.target.value as typeof row.phoneType } : item))} className="min-h-11 self-end rounded-xl border border-[var(--line)] bg-[var(--paper)] px-2 text-sm"><option value="mobile">Mobile</option><option value="home">Home</option><option value="work">Work</option><option value="other">Other</option></select>}
    <div className="flex min-h-11 items-center gap-2 self-end"><ContactNotifications off={row.doNotAlert} onChange={off => setRows(rows => rows.map(item => item.id === row.id ? { ...item, doNotAlert: off } : item))} /><button type="button" className="booking-text-action" onClick={() => setRows(rows => rows.filter(item => item.id !== row.id))}>Cancel</button></div>
  </div>;
  };
  const serviceLocationContactCard = chosenProperty && (
    <section className="booking-location-contact" aria-label="Service location contact" aria-busy={loadingLocationPeople}>
      <div className="booking-location-heading"><h3>Service location contact</h3></div>
      {loadingLocationPeople ? (
        <p role="status" className="booking-empty">Loading service-location contact...</p>
      ) : locationContactsError ? (
        <div role="alert" className="booking-empty">
          <p>Could not load contacts for this address.</p>
          <button type="button" className="booking-text-action" onClick={() => setLocationContactAttempt((attempt) => attempt + 1)}>Retry contact loading</button>
        </div>
      ) : locationPeople.length ? (
        shownLocationPeople.map((person) => personCard(person, { expanded: moreLocationPeople, location: true }))
      ) : (
        <p className="booking-empty">No service-location contact on file for this address.</p>
      )}
      {(locationHasMore || moreLocationPeople) && (
        <button type="button" className="booking-text-action booking-show-more" onClick={() => setMoreLocationPeople((value) => !value)}>
          {moreLocationPeople ? "Show less" : "Show all service-location contacts"}
        </button>
      )}
      <div className="booking-contact-chips"><button type="button" className="booking-contact-chip" onClick={() => beginContactEdit({ id: "method-new", from: "location", name: "", phone: null, phoneType: null, email: null, memo: null, isPrimary: false, doNotAlert: false })}>Add contact</button></div>
      {editingContact?.from === "location" && contactEditor}
    </section>
  );

  const stepNames = ["Customer & Location", "Service Details", "Confirm"];
  function validateStep(checkStep: 1 | 2) {
    setFormError("");
    const fail = (message: string) => { setStep(checkStep); setFormError(message); return false; };
    if (checkStep === 1) {
      if (customerMode === "new" && serviceContactChoices.filter(row => !excludedBillingContacts.includes(row.id)).length + billingContactRows.length > 20) return fail("Keep up to 20 billing contact details. Deselect a copied detail or cancel a billing row.");
      if (customerMode === "existing" && !customer) return fail("Choose a customer to continue.");
      if (loadingCustomer) return fail("Wait for the customer details to load.");
      if (customerLoadError) return fail("Reload the customer details before continuing.");
      if (!needsNewAddress && !propertyId) return fail("Pick the service location before booking.");
    } else {
      if (!dayReady) return fail("Wait for availability to load before booking.");
      if (!teamsReady) return fail("Load company teams before booking.");
      if (!jobTypeId) return fail("Choose a job type to continue.");
      if (afterHoursBooking && !bookingTeam) return fail("Choose the service team for this after-hours appointment.");
		if (afterHoursBooking && !technicianIds.length) return fail("Choose at least one on-call technician for this appointment.");
		if (afterHoursBooking && technicianIds.some((id) => !teamMemberIds.has(id) || !technicianChoices.find((choice) => choice.technician.id === id)?.free)) return fail("Choose available on-call technicians from the selected service team.");
      if (!notes.trim()) return fail("Describe what is happening at the property.");
      if (!customTime && !availableSlots.some((slot) => minutesToLocalTime(slot.minutes) === start)) return fail("Choose an available arrival window, or enter a custom time.");
      if (!start || !Number.isFinite(minutes) || minutes <= 0 || !endTimeForRange(start, minutes)) return fail("Choose a duration that ends before midnight.");
    }
    const fields = formRef.current?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      `[data-booking-step="${checkStep}"] input, [data-booking-step="${checkStep}"] textarea, [data-booking-step="${checkStep}"] select`,
    );
    for (const field of fields ?? []) {
      if (!field.checkValidity()) {
        setStep(checkStep);
        setFormError("Complete the required fields to continue.");
        window.requestAnimationFrame(() => field.reportValidity());
        return false;
      }
    }
    return true;
  }
  function nextStep() {
    if (!validateStep(step === 1 ? 1 : 2)) return;
    setEditingContact(null);
    if (step === 2 && formRef.current) {
      setReviewFields(Object.fromEntries([...new FormData(formRef.current).entries()].map(([key, value]) => [key, key === "firstName" || key === "lastName" ? capitalizeNameInitial(String(value)) : String(value)])));
    }
    setStep(step === 1 ? 2 : 3);
  }
  async function submitBooking(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step !== 3) { nextStep(); return; }
    if (bookingBusyRef.current || !validateStep(1) || !validateStep(2)) return;
    bookingBusyRef.current = true;
    setBookingBusy(true);
    try {
      await submit(event, async () => {
          const form = new FormData(event.currentTarget);
          setFormError("");
          if (!needsNewAddress && !propertyId) {
            setFormError("Pick the service location before booking.");
            throw new Error("no service location");
          }
          const address = {
            addressLine1: String(form.get("addressLine1") || ""),
            addressLine2: String(form.get("addressLine2") || "") || null,
            city: String(form.get("city") || ""),
            state: String(form.get("state") || ""),
            postalCode: String(form.get("postalCode") || ""),
            ...optionalCoords(form),
          };
          const billing =
            customerMode === "new" && !sameBilling
              ? {
                  addressLine1: String(form.get("billingAddressLine1") || ""),
                  addressLine2: null,
                  city: String(form.get("billingCity") || ""),
                  state: String(form.get("billingState") || ""),
                  postalCode: String(form.get("billingPostalCode") || ""),
                  ...optionalCoords(form, "billingLat", "billingLng"),
                }
              : undefined;
          const contactName = `${capitalizeNameInitial(String(form.get("firstName") || ""))} ${capitalizeNameInitial(String(form.get("lastName") || ""))}`.trim();
          const serviceContactRows = [
            { key: "phone", name: contactName, phone: String(form.get("phone") || "").replace(/\D/g, ""), phoneType: String(form.get("phoneType") || "mobile"), email: null, memo: form.get("phoneMemo") || null, isPrimary: true, doNotAlert: phoneAlertsOff },
            ...(form.get("email") ? [{ key: "email", name: contactName, phone: null, phoneType: null, email: form.get("email"), memo: form.get("emailMemo") || null, isPrimary: false, doNotAlert: emailAlertsOff }] : []),
            ...additionalContacts.map(row => ({ key: row.id, name: contactName, phone: row.channel === "phone" ? row.value.replace(/\D/g, "") : null, phoneType: row.channel === "phone" ? row.phoneType : null, email: row.channel === "email" ? row.value.trim() : null, memo: row.memo.trim() || null, isPrimary: false, doNotAlert: row.doNotAlert })),
          ];
          const serviceContacts = serviceContactRows.map(({ key: _key, ...contact }) => contact);
          const billingContacts = [...serviceContactRows.filter(row => !excludedBillingContacts.includes(row.key)).map(({ key: _key, ...contact }) => contact), ...billingContactRows.map(row => ({ name: contactName, phone: row.channel === "phone" ? row.value.replace(/\D/g, "") : null, phoneType: row.channel === "phone" ? row.phoneType : null, email: row.channel === "email" ? row.value.trim() : null, memo: row.memo.trim() || null, isPrimary: false, doNotAlert: row.doNotAlert }))].map((contact, index) => ({ ...contact, isPrimary: index === 0 }));
          const billingPhone = billingContacts.find(contact => contact.phone);
          const billingEmail = billingContacts.find(contact => contact.email);
          const end = scheduleLater ? null : endTimeForRange(start, minutes);
          if (!scheduleLater && !end) {
            setFormError("Choose a duration that ends before midnight.");
            throw new Error("overnight appointment");
          }
          try {
            await save("/api/v1/workspace/dispatch/bookings", "POST", {
              customer:
                customerMode === "existing"
                  ? { kind: "existing", customerId: customer?.id }
                  : {
                      kind: "new",
                      firstName: capitalizeNameInitial(String(form.get("firstName") || "")),
                      lastName: capitalizeNameInitial(String(form.get("lastName") || "")),
                      email: billingEmail?.email || null,
                      phone: billingPhone?.phone || null,
                      phoneType: billingPhone?.phoneType || null,
                      contacts: billingContacts,
                      ...(billing ? { billingAddress: billing } : {}),
                    },
              property: needsNewAddress
                ? { kind: "new", address, propertyType, ...(customerMode === "new" ? { contacts: serviceContacts } : {}) }
                : { kind: "existing", propertyId },
              jobType: { kind: "existing", jobTypeId },
              ...(afterHoursBooking && bookingTeam ? { teamId: bookingTeam.id } : {}),
              ...(customerMode==="existing"&&!needsNewAddress&&!scheduleLater&&membershipSelection.scope===`${customer?.id}:${propertyId}`&&membershipSelection.ids.length?{membershipServiceEventIds:membershipSelection.ids}:{}),
              notes: form.get("notes"),
              ...(bookingCaller?.bookingAgentLeadId ? { bookingAgentLeadId: bookingCaller.bookingAgentLeadId } : bookingCaller?.callId ? { sourceCallId: bookingCaller.callId } : {}),
              scheduleLater,
              ...(!scheduleLater && customerConfirmed ? { customerConfirmed: true } : {}),
              ...(scheduleLater
                ? {}
                : {
                    startsAt: companyLocalToIso(
                      `${boardDate}T${start}`,
                      timezone,
                    ),
                    endsAt: companyLocalToIso(`${boardDate}T${end!}`, timezone),
                    ...(technicianIds.length > 1
                      ? { technicianIds }
                      : { technicianId: technicianIds[0] || null }),
                  }),
            });
            if (bookingCaller?.callId && !bookingCaller.bookingAgentLeadId) window.dispatchEvent(new Event("workview:calls-changed"));
          } catch (err) {
            if (!(err as { shown?: boolean }).shown)
              setFormError(
                err instanceof Error ? err.message : "We couldn’t book that job.",
              );
            throw err;
          }
      });
    } finally {
      bookingBusyRef.current = false;
      setBookingBusy(false);
    }
  }
  const reviewName = customer ? customerName : `${reviewFields.firstName ?? ""} ${reviewFields.lastName ?? ""}`.trim();
  const reviewAddress = chosenProperty
    ? [chosenProperty.name?.trim(), chosenProperty.addressLine1, chosenProperty.addressLine2, `${chosenProperty.city}, ${chosenProperty.state} ${chosenProperty.postalCode ?? ""}`].filter(Boolean).join(", ")
    : [reviewFields.addressLine1, reviewFields.addressLine2, `${reviewFields.city ?? ""}, ${reviewFields.state ?? ""} ${reviewFields.postalCode ?? ""}`].filter(Boolean).join(", ");

  return (
    <Dialog
      title="Book Job"
      width="booking-dialog"
      subtitle={<div ref={stepRef} tabIndex={-1} className="booking-progress" aria-label={`Step ${step}: ${stepNames[step - 1]}`}>
        <ol>
          {stepNames.map((name, index) => (
            <li key={name} data-active={step === index + 1} data-complete={step > index + 1}>
              <button type="button" disabled={index + 1 > step || bookingBusy} aria-current={step === index + 1 ? "step" : undefined} onClick={() => { setFormError(""); setStep((index + 1) as 1 | 2 | 3); }}>
                <span className="booking-step-number">{step > index + 1 ? <Check size={16} aria-hidden /> : index + 1}</span>
                <span>{name}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>}
      onClose={onClose}
      footer={
        <>
          {isPastWindow && <PastAppointmentWarning date={boardDate} start={start} timezone={timezone} />}
          {formError && <p role="alert" className="booking-error">{formError}</p>}
          {contactDeleteError && <p role="alert" className="booking-error">{contactDeleteError}</p>}
          <div className="booking-footer-actions">
            <button type="button" className="booking-secondary" disabled={bookingBusy} onClick={step === 1 ? onClose : () => { setFormError(""); setStep(step === 3 ? 2 : 1); }}>
              {step > 1 && <ArrowLeft size={17} aria-hidden />}{step === 1 ? "Cancel" : "Back"}
            </button>
            {step < 3 ? (
              <button key="next-step" type="button" className="booking-primary" disabled={loadingCustomer || contactSaving} onClick={(event) => { event.preventDefault(); nextStep(); }}>
                {step === 1 ? "Next: Service Details" : "Next: Confirm"}<ArrowRight size={18} aria-hidden />
              </button>
            ) : (
              <button key="confirm-booking" type="submit" form={formId} className="booking-primary" disabled={bookingBusy}>
                {bookingBusy ? "Booking..." : "Book Job"}<Check size={18} aria-hidden />
              </button>
            )}
          </div>
        </>
      }
    >
      {bookingCaller && <div className="booking-caller-context"><Phone size={16} aria-hidden="true" /><span>{bookingCaller.bookingAgentLeadId ? "Booking from online request" : "Booking from call"} <strong>{phoneNumber(bookingCaller.phone)}</strong>{bookingCaller.customerName ? ` · ${bookingCaller.customerName}` : ""}</span></div>}


      <form ref={formRef} id={formId} className="booking-form" noValidate onSubmit={submitBooking}>
        <fieldset hidden={step !== 1} data-booking-step="1" className="booking-step-content">
          {customerMode === "existing" && (
            <div className="booking-search">
              <div className="booking-search-heading">
                <label htmlFor={`${formId}-customer`}>Search customer</label>
              </div>
              <div className="booking-search-control">
                <Search size={20} aria-hidden />
                <input id={`${formId}-customer`} autoComplete="off" placeholder="Name, email, service address, or phone number" readOnly={Boolean(customer)} value={customer ? customerName : query} onChange={(event) => setQuery(event.target.value)} />
                {customer && <button type="button" aria-label="Change customer" title="Back to customer search" onClick={() => { chooseMode("existing"); document.getElementById(`${formId}-customer`)?.focus(); }}><X size={18} aria-hidden /></button>}
                {customer && <button type="button" aria-label="Search customers" title="Search customers" onClick={() => { chooseMode("existing"); document.getElementById(`${formId}-customer`)?.focus(); }}><ChevronDown size={18} aria-hidden /></button>}
              </div>
              {!customer && hits.length > 0 && (
                <ul className="booking-search-results">
                  {hits.map((row) => (
                    <li key={row.id}>
                      <button type="button" onClick={() => void chooseCustomer(row)}>
                        <span>
                          <strong>{row.firstName} {row.lastName}</strong>
                          <small>
                            {row.matchingProperty
                              ? `${row.matchingProperty.addressLine1}, ${row.matchingProperty.city} ${row.matchingProperty.postalCode ?? ""}`
                              : row.matchingBilling
                                ? `${row.matchingBilling.addressLine1}, ${row.matchingBilling.city} ${row.matchingBilling.postalCode ?? ""}`
                                : (row.primaryContactName || row.email)}
                          </small>
                        </span>
                        {row.primaryPhone && <small>{phoneNumber(row.primaryPhone)}</small>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!customer && query.trim() && !hits.length && (
                <div className="booking-search-empty">
                  <p>No matching customers.</p>
                  <button type="button" className="booking-create-customer" onClick={() => chooseMode("new")}>Create customer</button>
                </div>
              )}
            </div>
          )}
          {loadingCustomer && <p role="status" className="booking-empty">Loading customer details...</p>}
          {customerLoadError && customer && <div role="alert" className="booking-empty"><p>Could not load customer details.</p><button type="button" className="booking-text-action" onClick={() => void chooseCustomer(customer)}>Reload customer details</button></div>}

        {customerMode === "new" && (
          <div className="grid gap-2 sm:grid-cols-2">
            {/* the toggle is gone, so this is the way back to searching */}
            <div className="flex items-center justify-between gap-3 sm:col-span-2">
              <p className="text-[13px] font-semibold text-[var(--ink)]">
                New customer
              </p>
              <button
                type="button"
                onClick={() => chooseMode("existing")}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--accent)]"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
                  <path d="M8.5 3 4.5 7l4 4M5 7h6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Search again
              </button>
            </div>
            <TextInput
              required
              label="First name"
              name="firstName"
              value={newName.first}
              onBlur={(event) => {
                const first = capitalizeNameInitial(event.currentTarget.value);
                setNewName((name) => ({ ...name, first }));
              }}
              onChange={(event) =>
                setNewName((name) => ({ ...name, first: event.target.value }))
              }
            />
            <TextInput
              required
              label="Last name"
              name="lastName"
              value={newName.last}
              onBlur={(event) => {
                const last = capitalizeNameInitial(event.currentTarget.value);
                setNewName((name) => ({ ...name, last }));
              }}
              onChange={(event) =>
                setNewName((name) => ({ ...name, last: event.target.value }))
              }
            />
            {maybeExisting.length > 0 && (
              <div className="sm:col-span-2 rounded-lg border border-[var(--st-alert)] bg-[var(--accent-wash)] p-3">
                <p className="text-[13px] font-semibold text-[var(--ink)]">
                  {maybeExisting.length === 1
                    ? "Somebody with that name is already on file."
                    : "People with that name are already on file."}
                </p>
                <p className="mt-0.5 text-[12px] text-[var(--ink-soft)]">
                  Booking a second one splits their history and what they owe.
                </p>
                <div className="mt-2 space-y-1">
                  {maybeExisting.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => void chooseCustomer(row)}
                      className="block w-full rounded-lg bg-[var(--paper)] px-3 py-2 text-left hover:bg-[var(--wash)]"
                    >
                      <span className="block text-sm font-semibold text-[var(--ink)]">
                        {row.firstName} {row.lastName}
                      </span>
                      <span className="mt-0.5 block truncate text-[12px] text-[var(--ink-soft)]">
                        {row.primaryPhone
                          ? phoneNumber(row.primaryPhone)
                          : row.email || "Use this customer instead"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid items-start gap-2 sm:col-span-2 sm:grid-cols-[1fr_1fr_100px_auto]">
              <TextInput required label="Phone" name="phone" type="tel" pattern={"[()]?[0-9]{3}[)]?\\s?[0-9]{3}-[0-9]{4}"} value={newPhone} onChange={event => setNewPhone(formatBookingPhone(event.target.value))} />
              <TextInput label="Memo" aria-label="Phone memo" name="phoneMemo" maxLength={2000} />
              <select required name="phoneType" aria-label="Phone type" defaultValue="mobile" className="min-h-11 self-end rounded-xl border border-[var(--line)] bg-[var(--paper)] px-2 text-sm"><option value="mobile">Mobile</option><option value="home">Home</option><option value="work">Work</option><option value="other">Other</option></select>
              <div className="flex min-h-11 items-center gap-1 self-end"><ContactNotifications off={phoneAlertsOff} onChange={setPhoneAlertsOff} />{addContactButton("phone")}</div>
            </div>
            {additionalContacts.map((row, index) => row.channel === "phone" ? extraContactRow(row, index) : null)}
            <div className="grid items-start gap-2 sm:col-span-2 sm:grid-cols-[1fr_1fr_auto]">
              <TextInput label="Email" name="email" type="email" value={newEmail} onChange={event => setNewEmail(event.target.value)} required={!noEmailProvided} disabled={noEmailProvided} />
              <TextInput label="Memo" aria-label="Email memo" name="emailMemo" maxLength={2000} disabled={noEmailProvided} />
              <div className="flex min-h-11 items-center gap-1 self-end"><ContactNotifications off={emailAlertsOff} disabled={noEmailProvided} onChange={setEmailAlertsOff} />{addContactButton("email")}</div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[var(--ink-soft)] sm:col-span-2"><input type="checkbox" name="noEmailProvided" checked={noEmailProvided} onChange={event => setNoEmailProvided(event.target.checked)} className="h-4 w-4" />No email provided</label>
            {additionalContacts.map((row, index) => row.channel === "email" ? extraContactRow(row, index) : null)}
          </div>
        )}
          {(customerMode === "new" || (customer && !loadingCustomer && !customerLoadError)) && (
            <section className="booking-address-section" aria-label="Service location">
              <h3>Service location</h3>
              {!needsNewAddress && (
                <div className="booking-address-picker">
                  <button type="button" className="booking-address-control" aria-label={chosenProperty ? "Change service location" : "Select service address"} aria-expanded={pickingLocation} onClick={() => setPickingLocation((value) => !value)}>
                    <MapPin size={24} aria-hidden />
                    <span>{chosenProperty ? <>{chosenProperty.name?.trim() && <strong>{chosenProperty.name.trim()}</strong>}<strong>{chosenProperty.addressLine1}{chosenProperty.addressLine2 ? `, ${chosenProperty.addressLine2}` : ""}</strong><small>{chosenProperty.city}, {chosenProperty.state} {chosenProperty.postalCode}</small></> : <strong>Select service address</strong>}</span>
                    <ChevronDown size={18} aria-hidden />
                  </button>
                  {pickingLocation && (
                    <div className="booking-address-options">
                      {properties.length > LOCATION_PAGE && <TextInput label={`Find one of ${properties.length} locations`} placeholder="Location name, street or ZIP" autoComplete="off" value={propertyQuery} onChange={(event) => { setPropertyQuery(event.target.value); setLocationPage(0); }} />}
                      <ul>
                        {pageOfLocations.map((property) => (
                          <li key={property.id}><button type="button" onClick={() => { setPropertyId(property.id); setPickingLocation(false); setMoreLocationPeople(false); }}>
                            <span>{property.name?.trim() && <strong>{property.name.trim()}</strong>}<strong>{property.addressLine1}{property.addressLine2 ? `, ${property.addressLine2}` : ""}</strong><small>{property.city}, {property.state} {property.postalCode}</small></span>
                            {propertyId === property.id && <Check size={18} aria-hidden />}
                          </button></li>
                        ))}
                      </ul>
                      {!filtered.length && <p className="booking-empty">No location matches that.</p>}
                      {locationPages > 1 && <div className="booking-location-pages">
                        <button type="button" aria-label="Previous locations" disabled={locationPage === 0} onClick={() => setLocationPage((page) => Math.max(0, page - 1))}><ArrowLeft size={18} aria-hidden /></button>
                        <span>{locationPage + 1} of {locationPages}</span>
                        <button type="button" aria-label="Next locations" disabled={locationPage >= locationPages - 1} onClick={() => setLocationPage((page) => Math.min(locationPages - 1, page + 1))}><ArrowRight size={18} aria-hidden /></button>
                      </div>}
                    </div>
                  )}
                </div>
              )}
              {needsNewAddress && <>
                <AddressFields
                  enableLookup
                  searchExisting={searchCustomers}
                  onExistingSelect={(row) => void chooseCustomer(bookingCustomerFromMatch(row))}
                  onResolved={(address) => void checkAddressOnFile(address)}
                />
                <AddressAlreadyOnFile
                  rows={addressAlreadyOnFile}
                  onChoose={(id) => {
                    const row = addressAlreadyOnFile.find((entry) => entry.id === id);
                    if (row) void chooseCustomer(row);
                  }}
                />
                <Select required label="Property type" value={propertyType} onChange={(value) => setPropertyType(value as "residential" | "commercial")} options={[{value:"residential",label:"Residential"},{value:"commercial",label:"Commercial"}]} />
              </>}
              {customer && chosenProperty && loadMemberships && <div className="my-3 flex flex-wrap items-center gap-2" aria-label="Location memberships"><span className="text-xs font-medium text-[var(--ink-soft)]">Maintenance plan</span><CustomerMembershipTags customerId={customer.id} propertyId={chosenProperty.id} loadMemberships={loadMemberships} /></div>}
              {customer && chosenProperty && loadMemberships && <BookingMembershipRenewal key={`${customer.id}:${chosenProperty.id}`} customerId={customer.id} propertyId={chosenProperty.id} loadMemberships={loadMemberships} />}
              {serviceLocationContactCard}
          {customer && !loadingCustomer && !customerLoadError && (
            <details key={customer.id} className="booking-primary-contact booking-customer-contacts">
              <summary>Customer contacts ({billingPeople.length})</summary>
              <section aria-label="Customer contacts">

              {billingPeople.map(person => personCard(person, { fallbackName: customerName }))}
              {!billingPeople.length && <p className="booking-empty">No customer contacts on file.</p>}
              {!billingPeople.length && <div className="booking-contact-chips">{(["phone", "email"] as const).map(field => <button key={field} type="button" className="booking-contact-chip" onClick={() => beginContactEdit({ id: "method-new", from: "customer", name: customerName, phone: null, phoneType: null, email: null, memo: null, isPrimary: true, doNotAlert: false }, field)}>Add {field}</button>)}</div>}
              {editingContact?.from === "customer" && contactEditor}
              </section>
            </details>
          )}
              {customerMode === "new" && <div className="space-y-3">
                <fieldset className="space-y-2"><legend className="text-sm font-medium">Is this the same as your billing address?</legend><div className="flex gap-5">{[true, false].map(value => <label key={String(value)} className="flex items-center gap-2 text-sm"><input type="radio" name="sameBillingAddress" value={String(value)} checked={sameBilling === value} onChange={() => setSameBilling(value)} />{value ? "Yes" : "No"}</label>)}</div></fieldset>
                {!sameBilling && <fieldset aria-label="Billing address"><AddressFields prefix="billing" legend="Billing address" enableLookup searchExisting={searchCustomers} /></fieldset>}
                <section aria-label="Billing contacts" className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3"><h3>Billing contacts</h3><div className="flex items-center gap-4"><span className="flex items-center gap-2 text-sm">Phone {addContactButton("phone", true)}</span><span className="flex items-center gap-2 text-sm">Email {addContactButton("email", true)}</span></div></div>
                  <fieldset className="space-y-2"><legend className="text-sm font-medium">Copy service location contact details</legend>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={serviceContactChoices.length > 0 && serviceContactChoices.every(row => !excludedBillingContacts.includes(row.id))} onChange={event => setExcludedBillingContacts(event.target.checked ? [] : serviceContactChoices.map(row => row.id))} />Select all</label>
                    {serviceContactChoices.map(row => <label key={row.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!excludedBillingContacts.includes(row.id)} onChange={event => setExcludedBillingContacts(ids => event.target.checked ? ids.filter(id => id !== row.id) : [...ids, row.id])} />{row.id === "phone" || additionalContacts.find(item => item.id === row.id)?.channel === "phone" ? <Phone size={14} aria-hidden="true" /> : <Mail size={14} aria-hidden="true" />}{row.value}</label>)}
                    {!serviceContactChoices.length && <p className="text-sm text-[var(--ink-soft)]">Enter service contact details above to copy them.</p>}
                  </fieldset>
                  {billingContactRows.map((row, index) => extraContactRow(row, index, true))}
                </section>
              </div>}
            </section>
          )}
        </fieldset>
        <fieldset hidden={step !== 2} data-booking-step="2" className="booking-step-content">
          <p className="booking-service-date">{bookingDateLabel}</p>
      {/*
       * The card asks one question at a time. Until somebody is on it there is
       * no location to pick, no work to describe and no window to book, so
       * none of that is on screen telling the office to do something first.
       */}
      {(customerMode === "new" || customer) && (
        <section className="space-y-3">
          <SectionLabel>Work</SectionLabel>
          {/* Job types are company records linked to dispatch teams. */}
          {jobTypeId ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--line)] px-3 py-2">
              <span className="truncate text-sm font-semibold text-[var(--ink)]">
                {jobTypes.find((type) => type.id === jobTypeId)?.name}
              </span>
              <button
                type="button"
                aria-label="Change job type"
                onClick={() => {
                  setJobTypeId("");
                  setJobTypeQuery("");
                }}
                className="shrink-0 text-xs font-semibold text-[var(--accent)]"
              >
                Change
              </button>
            </div>
          ) : (
            <div>
              <TextInput
                label="Job type"
                placeholder="Type to find it — hv, drain, install"
                autoComplete="off"
                value={jobTypeQuery}
                onFocus={() => setJobTypeOpen(true)}
                onBlur={() => window.setTimeout(() => setJobTypeOpen(false), 150)}
                onChange={(event) => setJobTypeQuery(event.target.value)}
              />
              {(jobTypeOpen || jobTypeQuery.trim()) && (
                <div className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-[var(--line)]">
                  {jobTypes
                    .filter((type) =>
                      type.name
                        .toLowerCase()
                        .includes(jobTypeQuery.trim().toLowerCase()),
                    )
                    .map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setJobTypeId(type.id);
                          setBookingTeamId("");
                          setJobTypeQuery("");
                          setJobTypeOpen(false);
                        }}
                        className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-[var(--wash)]"
                      >
                        {type.name}
                      </button>
                    ))}
                  {!jobTypes.some((type) =>
                    type.name
                      .toLowerCase()
                      .includes(jobTypeQuery.trim().toLowerCase()),
                  ) && (
                    <p className="px-3 py-3 text-sm text-[var(--ink-soft)]">
                      No job type matches that. Add it in My Company → Operations, then link it to a team on the board.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {afterHoursBooking && matchingTeams.length > 1 && <label className="flex flex-col items-start gap-2">Service team
            <select aria-label="Service team" required value={bookingTeamId} onChange={(event) => { setBookingTeamId(event.target.value); setTechnicianIds([]); }} className="w-full rounded-md border border-[var(--line)] bg-white p-2">
              <option value="">Choose HVAC or plumbing team</option>
              {matchingTeams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
            </select>
          </label>}
          <TextArea
            required
            label="What is happening at the property?"
            name="notes"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </section>
      )}

      {customerMode==="existing"&&customer&&loadMemberships&&!needsNewAddress&&propertyId&&<BookingMembershipServices key={`${customer.id}:${propertyId}`} customerId={customer.id} propertyId={propertyId} jobTypeName={chosenJobType?.name} selected={membershipSelection.scope===`${customer.id}:${propertyId}`?membershipSelection.ids:[]} onChange={ids=>setMembershipSelection({scope:`${customer.id}:${propertyId}`,ids})} disabled={bookingBusy} scheduleLater={scheduleLater}/>}

      {(customerMode === "new" || customer) && jobTypeId && (
          <section className="space-y-3">
            <SectionLabel>When and who</SectionLabel>
            <TextInput label="Job date" type="date" required value={boardDate} onChange={(event) => { if (!event.target.value) return; setDayError(""); setOtherDay(null); setBookingDate(event.target.value); setBookingTimeChosen(true); setTechnicianIds([]); }} />
            {!dayReady && !dayError && <p role="status">Loading technician availability…</p>}
            {dayError && <p role="alert">{dayError} <button type="button" onClick={() => setDayAttempt((value) => value + 1)}>Retry</button></p>}
            {/*
             * Both halves belong here: a job the office cannot place yet is booked
             * without a time at all, and a job it can place is placed on a slot
             * somebody is actually free for.
             */}
            {(
              <>
                {jobTypeId && !customTime && availableSlots.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {availableSlots.map((slot) => {
                      const chosen =
                        start === minutesToLocalTime(slot.minutes) &&
                        minutes === defaultBookingDurationMinutes;
                      return (
                        <button
                          key={slot.minutes}
                          type="button"
                          onClick={() => {
                            setStart(minutesToLocalTime(slot.minutes));
                            setBookingTimeChosen(true);
                            setMinutes(defaultBookingDurationMinutes);
                          }}
                          className={`rounded-lg border px-2.5 py-1.5 text-left text-[12px] font-semibold transition ${chosen ? "border-[var(--line-strong)] bg-[var(--ink)] text-[var(--paper)]" : slot.open ? "border-[var(--line)] text-[var(--ink)] hover:bg-[var(--wash)]" : "border-[var(--line)] text-[var(--ink-faint)] hover:bg-[var(--wash)]"}`}
                        >
                          {readableTime(minutesToLocalTime(slot.minutes))}–{readableTime(endTimeForRange(minutesToLocalTime(slot.minutes), defaultBookingDurationMinutes) || minutesToLocalTime(slot.minutes))}
                          <span
                            className={`mt-0.5 block text-[10px] font-semibold ${chosen ? "text-[var(--paper)]" : "text-[var(--ink-soft)]"}`}
                          >
                            {slot.open ? `${slot.open} free` : "nobody free"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {!customTime && dayReady && !availableSlots.length && <p role="status">No available arrival windows remain on this date. Choose another date or enter a custom time.</p>}
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={customTime} onChange={(event) => { setCustomTime(event.target.checked); setBookingTimeChosen(event.target.checked); if (!start) setStart("09:00"); }} />Choose a custom time</label>
                {customTime && <div className="grid gap-2 sm:grid-cols-2">
                  <TextInput label="Arrival window start" type="time" required value={start} onChange={(event) => { setStart(event.target.value); setBookingTimeChosen(true); }} />
                  <TextInput label="Arrival window end" type="time" required value={endTimeForRange(start, minutes) || ""} onChange={(event) => { const [hours, mins] = event.target.value.split(":").map(Number); const [startHours, startMins] = start.split(":").map(Number); setMinutes(hours * 60 + mins - startHours * 60 - startMins); }} />
                </div>}
                <p className="text-[13px] font-semibold text-[var(--ink)]">{windowLabel}</p>
                {chosenChoices.filter((choice) => !choice.free).map((choice) => <p key={choice.technician.id} role="alert">{choice.technician.name} is unavailable for this window. Booking will ask you to approve the conflict.</p>)}
                {!teamsReady && <p role="status">Company teams are not loaded yet.</p>}
                {teamsReady && matchingTeams.length === 0 && <p role="status">No team is linked to this job type. {afterHoursBooking ? "Link the service team on the board before booking after-hours service." : "Edit a team on the board to link it, or leave this job unassigned."}</p>}
                {matchingTeams.length > 0 && <p className="text-sm">Teams: {matchingTeams.map((team) => team.name).join(", ")}</p>}
                {matchingTeams.length > 0 && technicianIds.some((id) => !teamMemberIds.has(id)) && <p role="alert">A selected technician is outside this job type’s teams.</p>}
                <div
                  role="group"
                  aria-label="Technicians"
                  className="space-y-1.5"
                >
                  {!afterHoursBooking && <button
                    type="button"
                    role="checkbox"
                    aria-checked={technicianIds.length === 0}
                    onClick={() => setTechnicianIds([])}
                    className={choiceClass(technicianIds.length === 0)}
                  >
                    <span className="font-semibold text-[var(--ink)]">
                      Leave unassigned
                    </span>
                    <span className="text-[11px] text-[var(--ink-soft)]">
                      Someone claims it later
                    </span>
                  </button>}
                  {dayReady && teamsReady && !dayError && matchingTeams.length > 0 && (
                    !teamMemberIds.size ? <p role="status">This job type’s teams have no technicians assigned. Check their membership in Edit team on Dispatch.</p>
                      : !availabilityPool.length ? <p role="status">None of this job type’s team members appear in the loaded technician roster. Check the team membership and technician settings.</p>
                        : !technicianChoices.some((choice) => teamMemberIds.has(choice.technician.id) && choice.free) && <p role="status">No team members are available for this window.</p>
                  )}
                  {technicianChoices.filter((choice) => technicianIds.includes(choice.technician.id) || (teamMemberIds.has(choice.technician.id) && (choice.free || (!afterHoursBooking && showOtherTechnicians)))).map((choice) => (
                    <button
                      key={choice.technician.id}
                      type="button"
                      role="checkbox"
                      aria-checked={technicianIds.includes(choice.technician.id)}
					  onClick={() => setTechnicianIds((current) => current.includes(choice.technician.id) ? current.filter((id) => id !== choice.technician.id) : [...current, choice.technician.id])}
                      className={choiceClass(technicianIds.includes(choice.technician.id))}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-current">{technicianIds.includes(choice.technician.id) ? "✓" : ""}</span>
                        <span className="truncate font-semibold text-[var(--ink)]">
                          {choice.technician.name}
                        </span>
                        {choice.trade && (
                          <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-[var(--ink-faint)]">
                            {choice.trade}
                          </span>
                        )}
                      </span>
                      <span
                        className={`shrink-0 text-[11px] font-semibold ${choice.free ? "text-[var(--good)]" : choice.disabled ? "text-[var(--ink-faint)]" : "text-[var(--ink-soft)]"}`}
                      >
                        {choice.note}
                      </span>
                    </button>
                  ))}
                  {!afterHoursBooking && dayReady && teamsReady && availabilityPool.length > 0 && <button type="button" onClick={() => setShowOtherTechnicians((value) => !value)} className="booking-text-action">{showOtherTechnicians ? "Hide unavailable technicians" : "Show unavailable technicians"}</button>}
                </div>
              </>
            )}
          </section>
      )}
        </fieldset>
        {step === 3 && <section className="booking-review" aria-label="Confirm service call">
          <div className="booking-review-heading"><h3>Customer & Location</h3><button type="button" className="booking-text-action" onClick={() => setStep(1)}>Edit customer & location</button></div>
          <strong>{reviewName}</strong>
          <p>{reviewAddress}</p>
          {locationPeople.length > 0 && <div className="booking-review-contact">{shownLocationPeople.map((person) => <div key={person.key}><strong>{person.name}</strong><p>{bookingContactWays(person).slice(0, 2).map((way) => way.value).join(" | ")}</p>{person.memos.map((memo) => <p key={memo} className="booking-billing-memo">Memo: {memo}</p>)}</div>)}</div>}
          {customerMode === "new" && <div>
            {reviewFields.noEmailProvided && <p>No email provided</p>}
            <p>{[reviewFields.phone, reviewFields.email].filter(Boolean).join(" | ")}</p>
            {reviewFields.phoneMemo && <p>Phone memo: {reviewFields.phoneMemo}</p>}
            {reviewFields.emailMemo && <p>Email memo: {reviewFields.emailMemo}</p>}
            {billingContactRows.length > 0 && <div><h4>Added billing contacts</h4>{billingContactRows.map(row => <div key={row.id}><p>{row.value}</p>{row.memo && <p>Memo: {row.memo}</p>}<ContactNotifications off={row.doNotAlert} /></div>)}</div>}
            {additionalContacts.map((row) => <div key={row.id}><p>{reviewName}: {row.value}</p>{row.memo && <p>Memo: {row.memo}</p>}</div>)}
          </div>}
          <div className="booking-review-heading"><h3>Service Details</h3><button type="button" className="booking-text-action" onClick={() => setStep(2)}>Edit service details</button></div>
          <dl><div><dt>Job type</dt><dd>{chosenJobType?.name}</dd></div>{afterHoursBooking && bookingTeam && <div><dt>Service team</dt><dd>{bookingTeam.name}</dd></div>}<div><dt>Date</dt><dd>{bookingDateLabel}</dd></div><div><dt>Arrival window</dt><dd>{windowLabel}</dd></div><div><dt>Technicians</dt><dd>{chosenChoices.map((choice) => choice.technician.name).join(", ") || "Unassigned"}</dd></div></dl>
          <h3>What is happening at the property?</h3><p className="booking-review-notes">{notes}</p>
          {!scheduleLater && <label><input type="checkbox" checked={customerConfirmed} disabled={bookingBusy} onChange={(event) => setCustomerConfirmed(event.target.checked)} />Customer confirmed this appointment</label>}
        </section>}
      </form>
    </Dialog>
  );
}
function JobRecordWidget({ id, label, regionLabel, open, onToggle, children }: {
  id: string; label: string; regionLabel: string; count?: number; open: boolean; onToggle: () => void; children: ReactNode;
}) {
  return <section className="job-side-widget" hidden={!open}>
    <header><h2>{label}</h2><button type="button" aria-label={`Close ${label}`} onClick={onToggle}><X size={18} aria-hidden /></button></header>
    <div id={id} role="group" aria-label={regionLabel} className="job-widget-content">{children}</div>
  </section>;
}

function JobSection({
  title,
  hideTitle = false,
  collapsible = false,
  initiallyOpen = false,
  right,
  children,
}: {
  title: string;
  hideTitle?: boolean;
  collapsible?: boolean;
  initiallyOpen?: boolean;
  right?: ReactNode;
  children: ReactNode;
}) {
  if (collapsible) return <details open={initiallyOpen} className="job-widget-disclosure"><summary><h2>{title}</h2><span className="job-disclosure-summary">{right}<ChevronDown size={16} /></span></summary><div className="pt-3">{children}</div></details>;
  return (
    <section className="job-widget-section">
      <div className="flex items-center justify-between gap-2">
        {!hideTitle && <h2 className="text-sm font-semibold text-[var(--ink)]">
          {title}
        </h2>}
        {right}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

async function openPrintablePdf(path: string, label: string, existingTab?: Window) {
  // Open synchronously so browsers retain the user gesture, then replace the
  // temporary tab with an authenticated, in-memory PDF. Direct /api navigation
  // is blocked in the embedded review browser and would expose a session route.
  const printTab = existingTab ?? window.open("", "_blank");
  if (!printTab) {
    throw new Error("Allow pop-ups to open the printable document, then try again.");
  }
  printTab.document.title = `Preparing ${label}…`;
  printTab.document.body.textContent = `Preparing ${label}…`;

  try {
    const response = await apiFetch(path);
    if (!response.ok) {
      throw new Error(
        response.status === 404
          ? `${label} not found.`
          : `We couldn’t open the printable ${label.toLowerCase()}.`,
      );
    }
    const pdfUrl = URL.createObjectURL(await response.blob());
    printTab.location.replace(pdfUrl);
    window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 5 * 60_000);
  } catch (cause) {
    printTab.close();
    throw cause;
  }
}

function openEstimatePdf(estimate: JobDetail["estimates"][number], optionId?: string) {
  return openPrintablePdf(
    `/api/v1/workspace/estimates/${encodeURIComponent(estimate.id)}/pdf${optionId ? `?optionId=${encodeURIComponent(optionId)}` : ""}`,
    "Estimate",
  );
}

function opaqueSignatureJpeg(canvas: HTMLCanvasElement) {
  const copy = document.createElement("canvas");
  copy.width = canvas.width; copy.height = canvas.height;
  const context = copy.getContext("2d");
  if (!context) return canvas.toDataURL("image/jpeg", 0.92);
  context.fillStyle = "#fff"; context.fillRect(0, 0, copy.width, copy.height);
  context.drawImage(canvas, 0, 0);
  return copy.toDataURL("image/jpeg", 0.92);
}

export function EstimateAcceptDialog({
  estimate,
  busy,
  onClose,
  onAccept,
}: {
  estimate: JobDetail["estimates"][number];
  busy: boolean;
  onClose: () => void;
  onAccept: (
    signature: { fulfillment: "now" | "later"; jobTypeId?: string; signerName?: string; signatureDataUrl?: string },
  ) => Promise<void>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [signerName, setSignerName] = useState("");
  const [drawn, setDrawn] = useState(false);
  const [fulfillment, setFulfillment] = useState<"now" | "later">("later");
  const [jobTypeId, setJobTypeId] = useState("");
  const [jobTypes, setJobTypes] = useState<Array<{id:string;name:string;active:boolean}>>([]);
  useEffect(() => { void data<{items:typeof jobTypes}>("/api/v1/workspace/dispatch/job-types").then(({items}) => setJobTypes(items.filter(type => type.active))).catch(() => setError("Unable to load job types.")); }, []);
  const [error, setError] = useState("");
  const point = (event: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
    };
  };
  const accept = async (withSignature: boolean) => {
    setError("");
    if (fulfillment === "later" && !jobTypeId) { setError("Choose a job type for the new job."); return; }
    const allocation = { fulfillment, ...(fulfillment === "later" ? { jobTypeId } : {}) };
    if (withSignature) {
		const image = canvasRef.current ? opaqueSignatureJpeg(canvasRef.current) : undefined;
      if (!signerName.trim() || !drawn || !image) {
        setError("Capture the signer name and signature, or accept without one.");
        return;
      }
      await onAccept({ ...allocation, signerName: signerName.trim(), signatureDataUrl: image });
      return;
    }
    await onAccept(allocation);
  };
  return (
    <Dialog title="Accept this estimate" onClose={onClose}>
      <p className="text-sm text-[var(--ink)]">
        {customerDocumentLabel("Estimate", estimate)} · {money(estimate.totalCents)}
      </p>
      <label className="mt-4 block text-sm">Sold work<select aria-label="Sold work" disabled={busy} value={fulfillment} onChange={event => setFulfillment(event.target.value as "now" | "later")} className="mt-1 w-full rounded border p-2"><option value="later">Book into a new job</option><option value="now">Add to this job</option></select></label>
      <p className="mt-2 text-sm">{fulfillment === "later" ? "Creates a separate job and invoice. The estimate and deposit remain accessible from the original visit." : "Adds the sold work to this job’s invoice for completion on this visit."}</p>
      {fulfillment === "later" && <label className="mt-3 block text-sm">New job type<select aria-label="New job type" disabled={busy} value={jobTypeId} onChange={event => setJobTypeId(event.target.value)} className="mt-1 w-full rounded border p-2"><option value="">Choose job type</option>{jobTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>}
      <label className="mt-4 block text-sm font-semibold text-[var(--ink)]">
        Signer name
        <input
          value={signerName}
          onChange={(event) => setSignerName(event.target.value)}
          className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-normal"
        />
      </label>
      <canvas
        ref={canvasRef}
        width={600}
        height={160}
        aria-label="Estimate signature pad"
        className="mt-3 h-32 w-full rounded-lg border border-dashed border-[var(--line)] bg-[var(--wash)]"
        style={{ touchAction: "none" }}
        onPointerDown={(event) => {
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext("2d");
          if (!canvas || !ctx) return;
          drawing.current = true;
          canvas.setPointerCapture(event.pointerId);
          const start = point(event);
          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
        }}
        onPointerMove={(event) => {
          if (!drawing.current) return;
          const ctx = canvasRef.current?.getContext("2d");
          if (!ctx) return;
          const next = point(event);
          ctx.lineTo(next.x, next.y);
          ctx.strokeStyle = "#111";
          ctx.lineWidth = 3;
          ctx.lineCap = "round";
          ctx.stroke();
          setDrawn(true);
        }}
        onPointerUp={() => {
          drawing.current = false;
        }}
        onPointerCancel={() => {
          drawing.current = false;
        }}
      />
      {error && (
        <p role="alert" className="mt-2 text-sm text-[var(--accent)]">
          {error}
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <QuietButton
          type="button"
          disabled={busy}
          onClick={() => void accept(false)}
        >
          Accept without signature
        </QuietButton>
        <PrimaryButton
          type="button"
          disabled={busy || !signerName.trim() || !drawn}
          onClick={() => void accept(true)}
        >
          Accept with signature
        </PrimaryButton>
      </div>
    </Dialog>
  );
}


const IMPORTED_CARD_INVOICE_IDS = new Set([
  "st-invoice-53638356", "st-invoice-65640744", "st-invoice-66923446", "st-invoice-66923462",
  "st-invoice-67777194", "st-invoice-67918887", "st-invoice-70834602", "st-invoice-71534394",
]);
const PAYMENT_METHODS: Option[] = [
  { value: "check", label: "Check" },
  { value: "cash", label: "Cash" },
  { value: "credit_card", label: "Credit card" },
  { value: "debit_card", label: "Debit card" },
  { value: "ach", label: "ACH / bank transfer" },
  { value: "greensky", label: "GreenSky" },
  { value: "synchrony", label: "Synchrony" },
  { value: "other", label: "Other" },
];

/**
 * An invoice you can actually read. The row is the summary; opening it shows
 * the lines that make up the number, which is the only way anyone can check a
 * total against what was done. Invoice links open this card in the job record.
 */
function InvoiceCard({
  invoice: sourceInvoice,
  onSaved = () => {},
  priceBookItems = [],
  priceBookCategories = [],
  canWrite,
  recordPayment,
  startOpen = false,
  startCollectPayment = false,
  customerEmail = "",
  showPaymentActions = true,
  openInvoice,
  payments = [],
  previewPdf,
}: {
  previewPdf?: (invoiceId: string, label: string) => void;
  onSaved?: () => void;
  priceBookItems?: PriceBookItem[];
  priceBookCategories?: PriceBookCategory[];
  invoice: JobDetail["invoices"][number];
  canWrite: boolean;
  recordPayment: (
    invoiceId: string,
    body: { amountCents: number; method: string; reference: string | null },
  ) => Promise<void>;
  openInvoice?: (invoiceId: string) => void;
  startOpen?: boolean;
  startCollectPayment?: boolean;
  customerEmail?: string;
  showPaymentActions?: boolean;
  payments?: NonNullable<JobDetail["payments"]>;
}) {
  const [updatedInvoice, setUpdatedInvoice] = useState<JobDetail["invoices"][number] | null>(null);
  const invoice = updatedInvoice ?? sourceInvoice;
  const currentInvoice = useRef(invoice);
  currentInvoice.current = invoice;
  useEffect(() => { setUpdatedInvoice(null); }, [sourceInvoice]);
  const [editing, setEditing] = useState(false);
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const flushInvoiceEdits = useRef<(() => Promise<boolean>) | null>(null);
  const withInvoiceSaved = async (action: () => unknown) => {
    setError("");
    if (!flushInvoiceEdits.current) {
      try { await action(); return true; } catch (cause) { setError(cause instanceof Error ? cause.message : "Invoice action could not finish."); return false; }
    }
    setBusy(true);
    try { if (!await flushInvoiceEdits.current()) return false; await action(); return true; }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Invoice action could not finish."); return false; }
    finally { setBusy(false); }
  };
  const [invoiceEditorDirty, setInvoiceEditorDirty] = useState(false);
  const [open, setOpen] = useState(true);
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (startOpen) { setOpen(true); card.current?.scrollIntoView?.({ block: "center" }); }
  }, [startOpen]);
  const [paying, setPaying] = useState(startCollectPayment);
  useEffect(() => { if (startCollectPayment) { setOpen(true); setPaying(true); } }, [startCollectPayment]);
  const [cardCheckoutOpen, setCardCheckoutOpen] = useState(false);
  const [cardAmount, setCardAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState(String(invoice.balanceDueCents / 100));
  const [method, setMethod] = useState("check");
  const [paymentAuthorization, setPaymentAuthorization] = useState<{ signerName: string; signatureDataUrl: string } | null>(null);
  const [error, setError] = useState("");
  const [delivery, setDelivery] = useState<"invoice" | "payment" | null>(null);
  const [recipient, setRecipient] = useState(customerEmail);
	const [emailSubject, setEmailSubject] = useState("");
	const [emailMessage, setEmailMessage] = useState("");
	const loadInvoiceEmailTemplate = () => void apiFetch("/api/v1/workspace/company/email-templates").then(async response => { const body = await response.json(); const template = body?.data?.templates?.find((item: { type?: string }) => item.type === "invoice_pdf"); if (response.ok && template) { setEmailSubject(template.subject); setEmailMessage(template.body); } }).catch(() => {});
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [includePaymentLink, setIncludePaymentLink] = useState(invoice.balanceDueCents > 0);
  const [requestedAmount, setRequestedAmount] = useState((invoice.balanceDueCents / 100).toFixed(2));
  const [notice, setNotice] = useState("");
  const beginEmail = () => {
    setEditing(false); setError(""); setNotice(""); setRecipient(customerEmail); setChannel("email");
    setIncludePaymentLink(currentInvoice.current.balanceDueCents > 0);
    setRequestedAmount((currentInvoice.current.balanceDueCents / 100).toFixed(2));
    setEmailSubject(`${presentation.isPaidReceipt ? "Receipt" : "Invoice"}${currentInvoice.current.displayNumber ? ` #${currentInvoice.current.displayNumber}` : ""}`);
    setEmailMessage(`Please find ${presentation.isPaidReceipt ? "your receipt" : "your invoice"} attached.`);
    if (!presentation.isPaidReceipt) loadInvoiceEmailTemplate();
    setDelivery("invoice");
  };
  const sendDocument = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !delivery) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const amountCents = moneyInputCents(requestedAmount);
		const result = await mutate<{ checkout?: { url?: string | null }; status?: string }>(`/api/v1/workspace/invoices/${invoice.id}/deliver`, "POST", { channel, recipient: recipient.trim(), includePaymentLink: includePaymentLink && invoice.balanceDueCents > 0, ...(includePaymentLink ? { amountCents } : {}), ...(channel === "email" ? { subject: emailSubject.trim(), message: emailMessage.trim() } : {}) });
      setNotice(result.status === "copy_link" && result.checkout?.url ? `Text is unavailable in Preview. Copy this payment link: ${result.checkout.url}` : "Invoice sent.");
      setDelivery(null);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "The message could not be sent."); }
    finally { setBusy(false); }
  };
  const lines = invoice.lineItems ?? [];
  const hiddenLineCount = lines.filter((line) => line.sourceActive !== 0 && !isCustomerVisibleInvoiceLine(line)).length;
  const invoiceLines = lines.filter((line) => line.sourceActive !== 0 && (showHiddenItems || isCustomerVisibleInvoiceLine(line)));
  const presentation = invoicePresentation(invoice);
  const isEmptyDraft = invoice.status === "draft" && lines.length === 0 && invoice.invoiceNumber === 0 && !invoice.displayNumber;
	const canEditInvoice = !["void", "paid"].includes(invoice.status) && !invoice.renewalLocked;
  const requiresManualAuthorization = ["cash", "check"].includes(method) && !invoice.hasInvoiceSignature && !invoice.hasSignedEstimateApproval;
  const closeInvoiceEditor = () => {
    if (invoiceEditorDirty && !window.confirm("Discard unsaved invoice edits? Saved changes will be kept.")) return;
    setEditing(false);
    setInvoiceEditorDirty(false);
    onSaved();
  };
  const balanceLabel = invoice.balanceDueCents > 0
    ? `${money(invoice.balanceDueCents)} due`
    : invoice.status === "draft" ? "" : presentation.label;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cents = moneyInputCents(amount);
    setError("");
    if (!Number.isFinite(cents) || cents < 1)
      return setError("How much came in?");
    if (cents > invoice.balanceDueCents)
      return setError(
        `That is more than the ${money(invoice.balanceDueCents)} still owed.`,
      );
    setBusy(true);
    try {
      const form = new FormData(event.currentTarget);
      if (requiresManualAuthorization) {
        if (!paymentAuthorization) throw new Error("Capture the customer's authorization before recording a cash or check payment.");
        const signatureResponse = await apiFetch(`/api/v1/workspace/invoices/${invoice.id}/signature`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(paymentAuthorization) });
        const signaturePayload = await signatureResponse.json().catch(() => null) as { error?: { message?: string } } | null;
        if (!signatureResponse.ok) throw new Error(signaturePayload?.error?.message || "Unable to save the customer authorization.");
      }
      await recordPayment(invoice.id, {
        amountCents: cents,
        method,
        reference: String(form.get("reference") || "") || null,
      });
      setPaymentAuthorization(null);
      setPaying(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "That payment didn’t save.",
      );
    } finally {
      setBusy(false);
    }
  };
  const startCardCheckout = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amountCents = moneyInputCents(cardAmount);
    setError("");
    if (
      !Number.isFinite(amountCents) ||
      amountCents < 1 ||
      amountCents > invoice.balanceDueCents
    ) {
      setError("Enter an amount from $0.01 through the remaining balance.");
      return;
    }
    setBusy(true);
    try {
      const { checkout } = await mutate<{ checkout: { url: string | null } }>(
        `/api/v1/workspace/invoices/${invoice.id}/checkout`,
        "POST",
        { amountCents },
      );
      if (!checkout.url) {
        throw new Error(
          "A Checkout session is already pending. Refresh its status or wait for it to expire.",
        );
      }
      window.open(checkout.url, "_blank", "noopener,noreferrer");
      setCardCheckoutOpen(false);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "We couldn’t open card payment.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        ref={card}
      className={`job-invoice-card ${startOpen ? "job-invoice-focused" : ""}`}
    >
      <div className="job-invoice-heading flex items-start gap-2 pr-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={editing || busy}
        aria-expanded={open}
        className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left"
      >
        <span>
          <span className="block text-sm font-semibold text-[var(--ink)]">
            {invoice.displayNumber ? `Invoice #${invoice.displayNumber}` : "Invoice"} ·{" "}
            {money(invoice.totalCents)}
          </span>
          <span className="mt-1 block text-xs capitalize text-[var(--ink-soft)]">
            {invoice.dueDate ? `Due ${calendarDate(invoice.dueDate)}` : date(invoice.createdAt)}
          </span>
          {invoice.summary && <span className={`mt-1 whitespace-pre-wrap text-sm text-[var(--ink-soft)] ${open ? "block" : "line-clamp-2"}`}>{invoice.summary}</span>}
						{invoice.adjustmentToInvoiceId && <span className="mt-1 block text-xs font-semibold text-[var(--accent)]">Adjustment of Invoice {invoice.adjustmentToInvoiceDisplayNumber ? `#${invoice.adjustmentToInvoiceDisplayNumber}` : "the original invoice"}</span>}
          {presentation.estimateDisplayNumber && (
            <span className="mt-1 block max-w-xl truncate text-xs text-[var(--ink-soft)]">
              Includes approved work from Estimate #{presentation.estimateDisplayNumber}
              {presentation.soldLater ? " · Not scheduled yet." : ""}
            </span>
          )}
        </span>
        <span className="flex items-center gap-2">
          <span
            data-balance={invoice.balanceDueCents > 0 ? "due" : isEmptyDraft ? "draft" : presentation.isNoCharge ? "no-charge" : "paid"}
            className={`text-sm font-semibold ${invoice.balanceDueCents ? "text-[var(--st-conflict)]" : isEmptyDraft || presentation.isNoCharge ? "text-[var(--ink-faint)]" : "text-[var(--good)]"}`}
          >
            {balanceLabel}
          </span>
          <span className="text-xs text-[var(--ink-faint)]">
            {open ? "▾" : "▸"}
          </span>
        </span>
      </button>
      <div className="shrink-0 py-2.5">{canWrite && canEditInvoice && <QuietButton type="button" disabled={busy || editing} onClick={() => { setError(""); setDelivery(null); setEditing(true); setOpen(true); }}>Edit invoice</QuietButton>}</div>
      </div>

      {delivery && <form className="job-invoice-delivery" aria-label="Send invoice to customer" onSubmit={(event) => void sendDocument(event)}>
        <fieldset disabled={busy}>
          <h3>{presentation.isPaidReceipt ? "Send receipt" : "Send invoice"}</h3>
          <label>Send by<select value={channel} onChange={(event) => { const next = event.target.value as "email" | "sms"; setChannel(next); setRecipient(""); if (next === "sms") setIncludePaymentLink(true); }}><option value="email">Email</option>{invoice.balanceDueCents > 0 && <option value="sms">Text message</option>}</select></label>
          <TextInput required type={channel === "email" ? "email" : "tel"} label={channel === "email" ? "Recipient email" : "Recipient mobile"} value={recipient} onChange={(event) => setRecipient(event.target.value)} />
		  {channel === "email" && <><TextInput required label="Email subject" value={emailSubject} maxLength={200} onChange={(event) => setEmailSubject(event.target.value)} /><TextArea required label="Message" value={emailMessage} maxLength={5000} rows={5} onChange={(event) => setEmailMessage(event.target.value)} /></>}
          {invoice.balanceDueCents > 0 && !presentation.isPaidReceipt && <><label><input type="checkbox" checked={includePaymentLink} disabled={channel === "sms"} onChange={(event) => setIncludePaymentLink(event.target.checked)} /> Include secure payment link</label>{includePaymentLink && <TextInput label="Payment request amount" type="text" inputMode="decimal" value={requestedAmount} onChange={(event) => setRequestedAmount(event.target.value)} hint={`Up to ${money(invoice.balanceDueCents)}`} />}</>}
          <div className="job-actions"><PrimaryButton type="submit">{busy ? "Sending…" : "Send now"}</PrimaryButton><QuietButton type="button" onClick={() => setDelivery(null)}>Cancel</QuietButton></div>
        </fieldset>
      </form>}
      {notice && <p role="status" className="job-invoice-feedback">{notice}</p>}
      {error && !paying && !cardCheckoutOpen && <p role="alert" className="job-error job-invoice-feedback">{error}</p>}

      {open && editing && <div className="border-t border-[var(--line)] px-3 py-3">
        <InvoiceInlineEditor
          invoiceId={invoice.id}
          categories={priceBookCategories}
          priceBookItems={priceBookItems}
          onUpdated={(document) => { const updated = {
            ...invoice,
            summary: document.summary,
            status: document.status,
            totalCents: document.totalCents,
            balanceDueCents: document.balanceDueCents,
            paidCents: document.payments.reduce((sum, payment) => sum + payment.amountCents, 0),
            lineItems: document.lines.map((line) => ({
              ...line,
              priceBookItemId: line.priceBookItemId ?? lines.find((original) => original.id === line.id)?.priceBookItemId ?? null,
            })),
          }; currentInvoice.current = updated; setUpdatedInvoice(updated); }}
          onFlushReady={flush => { flushInvoiceEdits.current = flush; }}
          onDirtyChange={setInvoiceEditorDirty}
          onClose={() => { setEditing(false); setInvoiceEditorDirty(false); onSaved(); }}
        />
      </div>}
      {open && !editing && (
        <div className="border-t border-[var(--line)] px-3 py-3">
          {hiddenLineCount > 0 && <div className="mb-3"><label className="flex items-center gap-2 text-xs font-semibold text-[var(--ink-soft)]"><input type="checkbox" checked={showHiddenItems} onChange={(event) => setShowHiddenItems(event.target.checked)} /> Show hidden items ({hiddenLineCount})</label></div>}
          <InvoiceItemsTable lines={invoiceLines} items={priceBookItems} categories={priceBookCategories} />
          <DocumentTaxSummary kind="invoices" id={invoice.id} totalCents={invoice.totalCents} />
          <dl className="mt-3 space-y-1 text-sm">
            <div className="flex justify-between gap-3"><dt>Invoice total</dt><dd>{money(invoice.totalCents)}</dd></div>
            {invoice.refundedCents ? <><div className="flex justify-between gap-3"><dt>Payment collected</dt><dd>{money((invoice.paidCents ?? 0) + invoice.refundedCents)}</dd></div><div className="flex justify-between gap-3"><dt>Refund issued</dt><dd>-{money(invoice.refundedCents)}</dd></div></> : null}
            {invoice.paidCents !== undefined && <div className="flex justify-between gap-3"><dt>Payments applied</dt><dd>{money(invoice.paidCents)}</dd></div>}
            <div className="flex justify-between gap-3 font-semibold"><dt>Balance due</dt><dd>{money(invoice.balanceDueCents)}</dd></div>
          </dl>
          <JobPayments invoices={[invoice]} payments={payments} onSaved={onSaved} canWrite={canWrite} />
          {showPaymentActions && canWrite && invoice.balanceDueCents > 0 && (
            <div className="mt-3 border-t border-[var(--line)] pt-3">
              {paying ? (
                <form
                  onSubmit={(event) => void submit(event)}
                  className="space-y-3"
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    <TextInput
                      required
                      label="Amount"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value)}
                      inputMode="decimal"
                      hint={`${money(invoice.balanceDueCents)} owed`}
                    />
                    <Select
                      label="How it came in"
                      value={method}
                      onChange={setMethod}
                      options={PAYMENT_METHODS}
                    />
                  </div>
                  <TextInput
                    label="Check number or reference"
                    name="reference"
                    hint="optional"
                  />
                  {requiresManualAuthorization && <InvoiceAuthorizationCapture disabled={busy} onCapture={setPaymentAuthorization} />}
                  {error && (
                    <p
                      role="alert"
                      className="text-[13px] font-semibold text-[var(--st-conflict)]"
                    >
                      {error}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <PrimaryButton disabled={busy}>
                      {busy ? "Saving…" : "Record payment"}
                    </PrimaryButton>
                    <QuietButton
                      type="button"
                      onClick={() => {
                        setPaying(false);
                        setError("");
                      }}
                      className="shrink-0"
                    >
                      Cancel
                    </QuietButton>
                  </div>
                </form>
              ) : cardCheckoutOpen ? (
                <form onSubmit={(event) => void startCardCheckout(event)} className="max-w-sm space-y-3">
                  <TextInput
                    required
                    autoFocus
                    label="Card amount"
                    value={cardAmount}
                    onChange={(event) => setCardAmount(event.target.value)}
                    inputMode="decimal"
                    hint={`Up to ${money(invoice.balanceDueCents)} remaining`}
                  />
                  {error && (
                    <p role="alert" className="text-[13px] font-semibold text-[var(--st-conflict)]">
                      {error}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <PrimaryButton disabled={busy}>
                      {busy ? "Opening…" : IS_PREVIEW_ENVIRONMENT ? "Continue to Stripe Sandbox" : "Continue to card payment"}
                    </PrimaryButton>
                    <QuietButton
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setCardCheckoutOpen(false);
                        setError("");
                      }}
                    >
                      Cancel
                    </QuietButton>
                  </div>
                </form>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <PrimaryButton type="button" disabled={busy} onClick={() => {
                    setCardAmount(presentation.soldLater ? "" : (invoice.balanceDueCents / 100).toFixed(2));
                    setError("");
                    setCardCheckoutOpen(true);
                  }}>
                    {IS_PREVIEW_ENVIRONMENT ? "Pay by card (Stripe Sandbox)" : "Pay by card"}
                  </PrimaryButton>
                  <QuietButton type="button" onClick={() => { setAmount(presentation.soldLater ? "" : (invoice.balanceDueCents / 100).toFixed(2)); setPaying(true); }}>
									Record payment
                  </QuietButton>
                </div>
              )}

            </div>
          )}
        </div>
      )}
      <div className="job-invoice-toolbar" aria-label="Invoice actions">
		{canWrite && !canEditInvoice && invoice.status !== "paid" && <span className="text-xs font-semibold text-[var(--ink-soft)]">Accounting record — locked</span>}


        <QuietButton type="button" disabled={busy} onClick={() => {
          if (previewPdf) { void withInvoiceSaved(() => previewPdf(invoice.id, presentation.isPaidReceipt ? "Receipt" : "Invoice")); return; }
          const printTab = window.open("", "_blank");
          if (!printTab) { setError("Allow pop-ups to open the printable document, then try again."); return; }
          printTab.document.body.textContent = "Preparing invoice…";
          void withInvoiceSaved(() => openPrintablePdf(`/api/v1/workspace/invoices/${encodeURIComponent(invoice.id)}/pdf`, presentation.isPaidReceipt ? "Receipt" : "Invoice", printTab)).then(ok => { if (!ok) printTab.close(); });
        }}>View PDF</QuietButton>
		{canWrite && <QuietButton type="button" disabled={busy} onClick={() => void withInvoiceSaved(beginEmail)}>Email</QuietButton>}
		{canWrite && presentation.isPaidReceipt && <QuietButton type="button" disabled={busy} onClick={() => void withInvoiceSaved(beginEmail)}>Send receipt</QuietButton>}
      </div>
    </div>

    </>
  );
}

function InvoiceAuthorizationCapture({ disabled, onCapture }: { disabled: boolean; onCapture: (authorization: { signerName: string; signatureDataUrl: string }) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [signerName, setSignerName] = useState("");
  const [drawn, setDrawn] = useState(false);
  const point = (event: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const bounds = canvas.getBoundingClientRect();
    return { x: (event.clientX - bounds.left) * canvas.width / bounds.width, y: (event.clientY - bounds.top) * canvas.height / bounds.height };
  };
  const clear = () => { const canvas = canvasRef.current; canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height); drawing.current = false; setDrawn(false); };
  return <div className="rounded-lg border border-[var(--line)] bg-[var(--wash)] p-3">
    <p className="text-sm font-semibold text-[var(--ink)]">Customer authorization</p>
    <p className="mt-1 text-xs text-[var(--ink-soft)]">A one-time signature is required before recording a cash or check payment.</p>
    <TextInput label="Customer name" value={signerName} onChange={(event) => setSignerName(event.target.value)} />
    <canvas ref={canvasRef} width={600} height={160} aria-label="Invoice authorization signature pad" className="mt-2 h-28 w-full rounded-lg border border-dashed border-[var(--line)] bg-[var(--paper)]" style={{ touchAction: "none" }} onPointerDown={(event) => { const canvas = canvasRef.current; const context = canvas?.getContext("2d"); if (!canvas || !context || disabled) return; drawing.current = true; canvas.setPointerCapture(event.pointerId); const start = point(event); context.beginPath(); context.moveTo(start.x, start.y); }} onPointerMove={(event) => { if (!drawing.current || disabled) return; const context = canvasRef.current?.getContext("2d"); if (!context) return; const next = point(event); context.lineTo(next.x, next.y); context.strokeStyle = "#111"; context.lineWidth = 3; context.lineCap = "round"; context.stroke(); setDrawn(true); }} onPointerUp={() => { drawing.current = false; }} onPointerCancel={() => { drawing.current = false; }} />
    <div className="mt-2 flex gap-2"><QuietButton type="button" disabled={disabled} onClick={clear}>Clear</QuietButton><QuietButton type="button" disabled={disabled || !signerName.trim() || !drawn} onClick={() => { const canvas = canvasRef.current; if (canvas) onCapture({ signerName: signerName.trim(), signatureDataUrl: opaqueSignatureJpeg(canvas) }); }}>Use authorization</QuietButton></div>
  </div>;
}

function JobPayments({
  invoices,
  payments = [],
	 onSaved = () => {},
  canWrite = false,
}: {
  invoices: JobDetail["invoices"];
  payments?: NonNullable<JobDetail["payments"]>;
	 onSaved?: () => void | Promise<void>;
  canWrite?: boolean;
}) {
	const [reversing, setReversing] = useState<string | null>(null);
	const [reverseError, setReverseError] = useState("");
	const [reversePaymentId, setReversePaymentId] = useState<string | null>(null);
	const [reverseReason, setReverseReason] = useState("");
	const reverse = async (payment: NonNullable<JobDetail["payments"]>[number]) => {
		if (["synced", "processing", "historical"].includes(payment.quickBooksStatus ?? "")) return;
		if (!reverseReason.trim()) { setReverseError("Enter a reason for reversing this payment."); return; }
		setReversing(payment.paymentId); setReverseError("");
		try { const response = await apiFetch(`/api/v1/workspace/payments/${payment.paymentId}/reverse`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason: reverseReason.trim() }) }); if (!response.ok) { const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null; throw new Error(payload?.error?.message || "Unable to reverse payment."); } setReversePaymentId(null); setReverseReason(""); await onSaved(); }
		catch (cause) { setReverseError(cause instanceof Error ? cause.message : "Unable to reverse payment."); }
		finally { setReversing(null); }
	};
  const importedPaidInvoices = invoices.filter((invoice) =>
    invoice.status === "paid" &&
    invoice.totalCents > 0 &&
    invoice.balanceDueCents <= 0 &&
    !payments.some((payment) => payment.invoiceId === invoice.id),
  );
  const selectedReversePayment = payments.find((payment) => payment.paymentId === reversePaymentId) ?? null;
  return <>
  <section aria-label="Invoice payment details" className="mt-3">
	{reverseError && <p role="alert" className="mb-2 text-sm text-[var(--st-conflict)]">{reverseError}</p>}{(payments.length || importedPaidInvoices.length) ? <div className="divide-y divide-[var(--line)] rounded-lg border border-[var(--line)]">{payments.map((payment) => { const isRefund = payment.paymentType === "refund"; return <div key={payment.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm"><div><p className="font-semibold">{isRefund ? `-${money(Math.abs(payment.amountCents))} · Refund${payment.method ? ` · ${payment.method.replaceAll("_", " ")}` : ""}` : `${money(payment.amountCents)} · ${payment.method?.replaceAll("_", " ") || "Payment"}`}</p><p className="text-xs text-[var(--ink-soft)]">{customerDocumentLabel("Invoice", { displayNumber: payment.invoiceDisplayNumber } as JobDetail["invoices"][number])} · {date(payment.appliedAt)}{payment.reference ? ` · ${payment.reference}` : ""}</p>{payment.quickBooksError && <p className="mt-1 text-xs text-[var(--accent)]">{payment.quickBooksError}</p>}</div><div className="flex items-center gap-2"><span className={`font-semibold ${isRefund ? "text-[var(--ink-soft)]" : "text-[var(--good)]"}`}>{isRefund ? "Refund issued" : "Payment confirmed"}</span>{!isRefund && canWrite && payment.method && !["synced", "processing", "historical"].includes(payment.quickBooksStatus ?? "") && <QuietButton type="button" disabled={reversing === payment.paymentId} onClick={() => { setReverseError(""); setReverseReason(""); setReversePaymentId(payment.paymentId); }}>{reversing === payment.paymentId ? "Reversing…" : "Reverse"}</QuietButton>}</div></div>; })}{importedPaidInvoices.map((invoice) => <div key={`imported-paid-${invoice.id}`} className="flex flex-wrap items-center justify-between gap-2 bg-[var(--wash)] px-3 py-2.5 text-sm"><div><p className="font-semibold">{money(invoice.totalCents)} · Paid — imported history</p><p className="text-xs text-[var(--ink-soft)]">{customerDocumentLabel("Invoice", invoice)} · Payment method and date were not included in the imported history.</p></div><span className="font-semibold text-[var(--ink-soft)]">Historical</span></div>)}</div> : null}
  </section>
  {selectedReversePayment && <Dialog title="Reverse manual payment" onClose={() => { if (!reversing) { setReversePaymentId(null); setReverseReason(""); setReverseError(""); } }}>
    <p className="text-sm text-[var(--ink)]">{money(selectedReversePayment.amountCents)} · {selectedReversePayment.method?.replaceAll("_", " ") || "Payment"}</p>
    <p className="mt-1 text-sm text-[var(--ink-soft)]">Invoice {selectedReversePayment.invoiceDisplayNumber ?? "number unavailable"}. This restores the invoice balance and cancels pending QuickBooks work for this receipt.</p>
    <TextArea required label="Reason for reversal" value={reverseReason} onChange={(event) => setReverseReason(event.target.value)} />
    {reverseError && <p role="alert" className="mt-2 text-sm text-[var(--st-conflict)]">{reverseError}</p>}
    <div className="mt-4 flex gap-2"><PrimaryButton type="button" disabled={Boolean(reversing) || !reverseReason.trim()} onClick={() => void reverse(selectedReversePayment)}>{reversing ? "Reversing…" : "Reverse payment"}</PrimaryButton><QuietButton type="button" disabled={Boolean(reversing)} onClick={() => { setReversePaymentId(null); setReverseReason(""); setReverseError(""); }}>Cancel</QuietButton></div>
  </Dialog>}
  </>;
}

function address(parts: Array<string | null | undefined>) {
  return parts.filter(Boolean).join(", ") || "Not on file";
}

function serviceUnit(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed && /^\d+[A-Za-z]?$/.test(trimmed) ? `#${trimmed}` : trimmed;
}

export function CompanyHeaderBrand({ showName = false }: { showName?: boolean }) {
  const [company, setCompany] = useState<CompanyData["company"]>(null);
  const [failedLogo, setFailedLogo] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    void apiFetch("/api/v1/workspace/company", { signal: controller.signal }).then(async (response) => {
      if (!response.ok) return;
      const payload = await response.json();
      if (!controller.signal.aborted) setCompany(payload.data?.company ?? null);
    }).catch(() => {});
    return () => controller.abort();
  }, []);
  if (!company) return null;
  return company.logoAttachmentId && company.logoAvailability !== "not_copied" && !failedLogo
    ? <span className="flex min-w-0 items-center gap-2"><img src={`${API_URL}/api/v1/workspace/attachments/${encodeURIComponent(company.logoAttachmentId)}/content`} alt={company.name} onError={() => setFailedLogo(true)} className={`h-9 object-contain object-left ${showName ? "max-w-24" : "max-w-48"}`} />{showName && <span className="truncate text-sm font-semibold text-[var(--ink)]">{company.name}</span>}</span>
    : <span className="truncate text-base font-semibold text-[var(--ink)]">{company.name}</span>;
}

export function CompanyLetterhead({
  document,
}: {
  document: {
    companyName?: string | null;
    companyEmail?: string | null;
    companyPhone?: string | null;
    companyAddressLine1?: string | null;
    companyAddressLine2?: string | null;
    companyCity?: string | null;
    companyState?: string | null;
    companyPostalCode?: string | null;
  };
}) {
  const [logo, setLogo] = useState<string | null>(null);
  const [failedLogo, setFailedLogo] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void apiFetch("/api/v1/workspace/company", { signal: controller.signal }).then(async (response) => {
      if (!response.ok) return;
      const payload = await response.json();
      const company = payload.data?.company;
      if (!controller.signal.aborted) setLogo(company?.logoAttachmentId && company.logoAvailability !== "not_copied"
        ? `${API_URL}/api/v1/workspace/attachments/${encodeURIComponent(company.logoAttachmentId)}/content` : null);
    }).catch(() => {});
    return () => controller.abort();
  }, []);
  if (!document.companyName && !logo) return null;
  return (
    <div className="w-full sm:w-auto">
      {logo && failedLogo !== logo && <img
        src={logo}
        alt={document.companyName || "Company logo"}
        onError={() => setFailedLogo(logo)}
        className="mb-2 max-h-20 max-w-64 object-contain object-left"
      />}
      <address className="space-y-1 text-sm not-italic text-[var(--ink-soft)]">
        {document.companyName && <p className="font-semibold text-[var(--ink)]">{document.companyName}</p>}
        {document.companyAddressLine1 && <p>{document.companyAddressLine1}</p>}
        {document.companyAddressLine2 && <p>{document.companyAddressLine2}</p>}
        <p>{[document.companyCity, [document.companyState, document.companyPostalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</p>
        {document.companyEmail && <p>{document.companyEmail}</p>}
        {document.companyPhone && <p>{document.companyPhone}</p>}
      </address>
    </div>
  );
}

/** The document endpoint is the source of truth for both this screen and the PDF. */
export function InvoiceWorkspace({
  invoiceId,
  customerEmail = "",
  startCollectPayment = false,
  priceBookCategories = [],
  priceBookItems = [],
  canWrite,
  recordPayment,
  back,
  backLabel = "Back",
}: {
  invoiceId: string;
  customerEmail?: string;
  startCollectPayment?: boolean;
  priceBookCategories?: PriceBookCategory[];
  priceBookItems?: PriceBookItem[];
  canWrite: boolean;
  recordPayment: (
    invoiceId: string,
    body: { amountCents: number; method: string; reference: string | null },
  ) => Promise<void>;
  back: () => void;
  backLabel?: string;
}) {
  const [document, setDocument] = useState<InvoiceDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const paymentPanel = useRef<HTMLElement>(null);
  useEffect(() => { if(startCollectPayment && !loading) paymentPanel.current?.scrollIntoView?.({block:'center'}); },[startCollectPayment, loading]);
  const [error, setError] = useState("");
  const [editingDocument, setEditingDocument] = useState(false);
  const [showHiddenItems, setShowHiddenItems] = useState(false);
	const [emailing, setEmailing] = useState(false);
	const [emailRecipient, setEmailRecipient] = useState("");
	const [emailSubject, setEmailSubject] = useState("");
	const [emailMessage, setEmailMessage] = useState("");
	const loadInvoiceEmailTemplate = () => void apiFetch("/api/v1/workspace/company/email-templates").then(async response => { const body = await response.json(); const template = body?.data?.templates?.find((item: { type?: string }) => item.type === "invoice_pdf"); if (response.ok && template) { setEmailSubject(template.subject); setEmailMessage(template.body); } }).catch(() => {});
  const [adjusting, setAdjusting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [sends, setSends] = useState<Array<{ id: string; recipientEmail: string; sentAt: string; sentByName: string | null; deliveryError: string | null }>>([]);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setDocument(
        (
          await data<{ document: InvoiceDocument }>(
            `/api/v1/workspace/invoices/${invoiceId}/document`,
          )
        ).document,
      );
      const history = await data<{ items: Array<{ id: string; recipientEmail: string; sentAt: string; sentByName: string | null; deliveryError: string | null }> }>(
        `/api/v1/workspace/invoices/${invoiceId}/sends`,
      ).catch(() => ({ items: [] }));
      setSends(history.items);
    } catch {
      setError("We couldn’t load that invoice.");
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);
  useEffect(() => {
    void load();
  }, [load]);
  const createAdjustment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const reason = String(form.get("reason") || "").trim();
    const description = String(form.get("description") || "").trim();
    const originalInvoiceLineId = String(form.get("originalInvoiceLineId") || "").trim();
    const amountCents = moneyInputCents(form.get("amount"));
    if (
      reason.length < 3 ||
      !description || !originalInvoiceLineId ||
      !Number.isFinite(amountCents) ||
      amountCents <= 0
    ) {
      setError("Enter a reason, description, and positive correction amount.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await mutate(
        `/api/v1/workspace/invoices/${invoiceId}/adjustments`,
        "POST",
        {
          reason,
          summary: String(form.get("summary") || "").trim() || null,
          lineItems: [
            { description, originalInvoiceLineId, quantity: 1, unitPriceCents: -amountCents },
          ],
        },
      );
      setAdjusting(false);
      setNotice(
        "Negative adjustment created. Sync it to QuickBooks from accounting.",
      );
      await load();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "We couldn’t create the adjustment.",
      );
    } finally {
      setBusy(false);
    }
  };
  const printPdf = () => {
    setError("");
    void openPrintablePdf(
      `/api/v1/workspace/invoices/${encodeURIComponent(invoiceId)}/pdf`,
      "Invoice",
    ).catch((cause) =>
      setError(cause instanceof Error ? cause.message : "We couldn’t open the printable invoice."),
    );
  };
  const openSecureCheckout = async () => {
    if (!document || document.balanceDueCents <= 0) return;
    setBusy(true);
    setError("");
    try {
      const result = await mutate<{ checkout?: { url?: string | null } }>(
        `/api/v1/workspace/invoices/${invoiceId}/checkout`,
        "POST",
        { amountCents: document.balanceDueCents },
      );
      if (!result.checkout?.url) throw new Error("Secure checkout is unavailable. Try again in a moment.");
      window.open(result.checkout.url, "_blank", "noopener,noreferrer");
      setNotice("Secure checkout opened.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "We couldn’t open secure checkout.");
    } finally {
      setBusy(false);
    }
  };
  if (loading)
    return <p className="text-sm text-[var(--ink-soft)]">Loading invoice…</p>;
  if (!document)
    return (
      <div>
        <button
          onClick={back}
          className="text-sm font-semibold text-[var(--ink)]"
        >
          ← {backLabel}
        </button>
        <p className="mt-4 text-sm text-[var(--accent)]">
          {error || "Invoice not found."}
        </p>
      </div>
    );
  const isVoid = document.status === "void";
  const canEdit = canWrite && !document.historical;
  const paidCents = document.historicalPaidCents ?? document.payments.reduce((sum, payment) => sum + payment.amountCents, 0);
  const locked =
    document.historical ||
    isVoid ||
    document.status === "paid";
  const presentation = invoicePresentation({
    ...document,
    paidCents,
  });
  const discountPresentation = document.discountPresentation ??
    (document.discountCents === 0
      ? null
      : {
          amountCents: Math.abs(document.discountCents),
          treatment: document.discountCents < 0
            ? "included_in_line_items" as const
            : "subtract_from_subtotal" as const,
        });
  /*
   * INV-03: where it stands is stamped at the bottom, under the balance, the
   * way a paid invoice is stamped on paper — not tucked into the header.
   */
  const stamp = isVoid
    ? { label: "Void", className: "bg-[var(--st-conflict-wash)] text-[var(--st-conflict)]" }
    : presentation.isNoCharge
      ? { label: "No charge", className: "bg-[var(--wash)] text-[var(--ink-soft)]" }
    : presentation.isPaid
      ? { label: "Paid", className: "bg-[var(--good-wash)] text-[var(--good)]" }
      : document.balanceDueCents < document.totalCents
        ? { label: presentation.label, className: "bg-[var(--warn-wash)] text-[var(--warn)]" }
        : { label: presentation.label, className: "bg-[var(--wash)] text-[var(--ink-soft)]" };
  const hiddenLineCount = document.lines.filter((line) => line.sourceActive !== 0 && !isCustomerVisibleInvoiceLine(line)).length;
  const invoiceLines = document.lines.filter((line) => line.sourceActive !== 0 && (showHiddenItems || isCustomerVisibleInvoiceLine(line)));
  return (
    <article className="mx-auto max-w-5xl">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={back}
          className="text-sm font-semibold text-[var(--ink)]"
        >
          ← Back
        </button>
        <div className="flex gap-2">
          <QuietButton
            type="button"
            disabled={busy}
            onClick={printPdf}
          >
            Print invoice
          </QuietButton>
          {canWrite && !isVoid && (
            <QuietButton
              type="button"
              disabled={busy}
              onClick={() => {
                setEmailRecipient(customerEmail.trim());
                setEmailSubject(`Invoice${document.displayNumber ? ` #${document.displayNumber}` : ""}`);
                setEmailMessage("Please find your invoice attached.");
				loadInvoiceEmailTemplate();
                setEmailing(true);
              }}
            >
              {busy ? "Sending…" : "Email invoice"}
            </QuietButton>
          )}
        </div>
      </div>
      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg bg-[var(--accent-wash)] px-4 py-3 text-sm text-[var(--accent)]"
        >
          {error}
        </p>
      )}
      {notice && (
        <p
          role="status"
          className="mb-4 rounded-lg bg-[var(--wash)] px-4 py-3 text-sm text-[var(--ink)]"
        >
          {notice}
        </p>
      )}
      {document.historical && <p className="mb-4 text-sm text-[var(--ink-soft)]">Historical invoice · Read-only.{document.payments.length === 0 && " Payment method and date were not included in the imported history."}</p>}
      <section className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5 sm:p-8">
        <header className="flex flex-wrap items-start justify-between gap-6">
          <CompanyLetterhead document={document} />
          <div className="sm:text-right">
            <p className="text-xs font-semibold uppercase tracking-[.16em] text-[var(--ink-faint)]">
              Invoice
            </p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-[var(--ink)]">
              {document.displayNumber
                ? `#${document.displayNumber}`
                : "Invoice number unavailable"}
            </h1>
            <p className="mt-2 text-sm text-[var(--ink-soft)]">
              {[
                document.jobNumber ? `Job #${document.jobNumber}` : "",
                document.jobTypeName?.trim() || "",
                document.dueDate ? `Due ${date(document.dueDate)}` : "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
			{document.adjustmentToInvoiceId && <p className="mt-2 text-sm font-semibold text-[var(--accent)]">Adjustment of Invoice {document.adjustmentToInvoiceDisplayNumber ? `#${document.adjustmentToInvoiceDisplayNumber}` : "the original invoice"}</p>}
          </div>
        </header>
        <div className="mt-6 grid gap-4 border-b border-[var(--line)] pb-6 text-sm sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
              Bill to
            </p>
            <p className="mt-1 font-semibold text-[var(--ink)]">
              {document.customerName}
            </p>
            <p className="mt-1 text-[var(--ink-soft)]">
              {address([
                document.billingAddressLine1,
                document.billingAddressLine2,
                document.billingCity,
                [document.billingState, document.billingPostalCode]
                  .filter(Boolean)
                  .join(" "),
              ])}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
              Service address
            </p>
            <p className="mt-1 text-[var(--ink-soft)]">
              {address([
                document.serviceAddressName,
                document.serviceAddressLine1,
                serviceUnit(document.serviceAddressLine2),
                document.serviceCity,
                [document.serviceState, document.servicePostalCode]
                  .filter(Boolean)
                  .join(" "),
              ])}
            </p>
          </div>
        </div>
        {sends.length > 0 && (
          <div className="mt-5 rounded-lg border border-[var(--line)] bg-[var(--wash)] p-4 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">Email history</p>
            {sends.slice(0, 5).map((item) => (
              <p key={item.id} className="mt-2 text-[var(--ink)]">
                {date(item.sentAt)} · {item.recipientEmail}
                {item.sentByName ? ` · ${item.sentByName}` : ""}
                {item.deliveryError ? ` · ${item.deliveryError}` : ""}
              </p>
            ))}
          </div>
        )}
        {isVoid && (
          <div className="mt-5 rounded-lg border border-[var(--accent)] bg-[var(--accent-wash)] p-4">
            <p className="font-semibold text-[var(--accent)]">
              This invoice is void.
            </p>
            <p className="mt-1 text-sm text-[var(--accent)]">
              {document.voidReason || "No reason recorded"}
              {document.voidedAt ? ` · ${date(document.voidedAt)}` : ""}
            </p>
          </div>
        )}
        <section className="mt-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-[var(--ink)]">
              Invoice summary
            </h2>
            {canEdit && !isVoid && (
              <button
                type="button"
                disabled={busy || editingDocument}
                onClick={() => setEditingDocument(true)}
                className="text-xs font-semibold text-[var(--accent)]"
              >
                Edit invoice details
              </button>
            )}
          </div>
          {!editingDocument && <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[var(--ink-soft)]">{document.summary || "No summary added."}</p>}
          {presentation.estimateDisplayNumber && (
            <p className="mt-2 text-sm text-[var(--ink-soft)]">
              Includes approved work from Estimate #{presentation.estimateDisplayNumber}.
              {presentation.soldLater ? " Not scheduled yet." : ""}
            </p>
          )}
          {presentation.paidCents > 0 && document.balanceDueCents > 0 && (
            <p className="mt-2 text-sm text-[var(--ink-soft)]">
              {money(presentation.paidCents)} paid · {money(document.balanceDueCents)} remaining.
            </p>
          )}

        </section>
        {editingDocument && <InvoiceInlineEditor invoiceId={invoiceId} categories={priceBookCategories} priceBookItems={priceBookItems} onUpdated={updated => setDocument(current => current ? { ...current, summary: updated.summary, status: updated.status, subtotalCents: updated.subtotalCents, discountCents: updated.discountCents, taxCents: updated.taxCents, totalCents: updated.totalCents, balanceDueCents: updated.balanceDueCents } : current)} onClose={() => { setEditingDocument(false); void load(); }} />}
        {!editingDocument && <section className="mt-5">
          {hiddenLineCount > 0 && <label className="mb-3 flex items-center gap-2 text-xs font-semibold text-[var(--ink-soft)]"><input type="checkbox" checked={showHiddenItems} onChange={(event) => setShowHiddenItems(event.target.checked)} /> Show hidden items ({hiddenLineCount})</label>}
          {canEdit && locked && !isVoid && <p className="mb-3 text-xs text-[var(--ink-soft)]">Paid invoices cannot have their line items changed.</p>}
          <InvoiceItemsTable lines={invoiceLines} items={priceBookItems} categories={priceBookCategories} onEdit={canEdit && !locked ? () => setEditingDocument(true) : undefined} />
        </section>}
        {!editingDocument && <section aria-label="Invoice totals" className="mt-3 border-t border-[var(--line-strong)] pt-3 text-sm">
          <DocumentTaxSummary kind="invoices" id={document.id} totalCents={document.totalCents} totalsOverride={{ ...document, discountPresentation }} />
          <div className="flex justify-between border-t border-[var(--line)] py-3 text-base font-semibold">
            <span>Invoice total</span>
            <span>{money(document.totalCents)}</span>
          </div>
          <div className="flex justify-between py-1 text-sm">
            <span>Payments applied</span>
            <span>{money(paidCents)}</span>
          </div>
          <div className="flex justify-between text-base font-semibold">
            <span>Balance due</span>
            <span>{money(document.balanceDueCents)}</span>
          </div>
          <div className="mt-3 flex justify-end">
            <span
              role="status"
              className={`rounded-md px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[.14em] ${stamp.className}`}
            >
              {stamp.label}
            </span>
          </div>
        </section>}
        {document.adjustments.length > 0 && (
          <section className="mt-7 border-t border-[var(--line)] pt-5">
            <h2 className="text-sm font-semibold text-[var(--ink)]">
              Adjustments
            </h2>
            <div className="mt-3 space-y-2">
              {document.adjustments.map((adjustment) => (
                <div
                  key={adjustment.id}
                  className="flex justify-between gap-4 text-sm"
                >
                  <span className="text-[var(--ink-soft)]">
                    #{adjustment.adjustmentNumber} · {adjustment.reason} ·{" "}
                    {adjustment.syncStatus.replaceAll("_", " ")}
                  </span>
                  <span className="font-semibold">
                    {money(adjustment.totalCents)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
        {document.payments.length > 0 && (
          <section className="mt-7 border-t border-[var(--line)] pt-5">
            <h2 className="text-sm font-semibold text-[var(--ink)]">
              Payments
            </h2>
            <div className="mt-3 space-y-2">
              {document.payments.map((payment, index) => (
                <div
                  key={`${payment.appliedAt}-${index}`}
                  className="flex justify-between text-sm"
                >
                  <div className="text-[var(--ink-soft)]">
                    <p className="capitalize">{payment.kind === "refund" ? "Refund" : "Payment"} · {payment.method?.replaceAll("_", " ") || "Method unavailable"} · {payment.occurredAt || payment.appliedAt ? date(payment.occurredAt || payment.appliedAt) : "Date unavailable"}</p>
                    {payment.occurredAt && payment.appliedAt && payment.occurredAt.slice(0, 10) !== payment.appliedAt.slice(0, 10) && <p className="mt-1 text-xs">Applied to this invoice {date(payment.appliedAt)}</p>}
                    {payment.reference && <p className="mt-1 text-xs">Reference: {payment.reference}</p>}
                  </div>
                  <span className="font-semibold">
                    {money(payment.amountCents)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
        {document.signatureDataUrl && (
          <section className="mt-7 border-t border-[var(--line)] pt-5">
            <h2 className="text-sm font-semibold text-[var(--ink)]">
              Customer signature
            </h2>
            {document.signerName && (
              <p className="mt-2 text-sm text-[var(--ink-soft)]">
                Signed by {document.signerName}
                {document.signedAt ? ` · ${date(document.signedAt)}` : ""}
              </p>
            )}
            <img
              src={document.signatureDataUrl}
              alt={
                document.signerName
                  ? `Signature of ${document.signerName}`
                  : "Customer signature"
              }
              className="mt-3 max-h-32 rounded-lg border border-[var(--line)] bg-[var(--paper)]"
            />
          </section>
        )}
        {canEdit && !isVoid && document.balanceDueCents > 0 && (
          <section ref={paymentPanel} aria-label="Collect payment" className="mt-7 border-t border-[var(--line)] pt-5">
            <h2 className="text-sm font-semibold text-[var(--ink)]">
              Collect payment
            </h2>
            {paying ? (
              <PaymentForm
                invoice={document}
                busy={busy}
                record={recordPayment}
                done={async () => {
                  setPaying(false);
                  setNotice("Payment recorded.");
                  await load();
                }}
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                <QuietButton type="button" disabled={busy} onClick={() => void openSecureCheckout()}>
                  Pay by card / wallet
                </QuietButton>
                <QuietButton type="button" onClick={() => setPaying(true)}>
                  Record a payment
                </QuietButton>
              </div>
            )}
          </section>
        )}
      </section>
	  {emailing && <Dialog title="Email invoice" onClose={() => { if (!busy) setEmailing(false); }}>
		  <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); setBusy(true); setError(""); setNotice(""); void mutate(`/api/v1/workspace/invoices/${invoiceId}/send`, "POST", { recipient: emailRecipient.trim(), subject: emailSubject.trim(), message: emailMessage.trim() }).then(async () => { setNotice("Invoice email recorded."); setEmailing(false); await load(); }).catch((cause) => setError(cause instanceof Error ? cause.message : "We couldn’t send the invoice.")).finally(() => setBusy(false)); }}>
			  <TextInput label="Recipient email" type="email" required value={emailRecipient} disabled={busy} onChange={(event) => setEmailRecipient(event.target.value)} />
			  <TextInput label="Email subject" required value={emailSubject} maxLength={200} disabled={busy} onChange={(event) => setEmailSubject(event.target.value)} />
			  <TextArea label="Message" required value={emailMessage} maxLength={5000} rows={5} disabled={busy} onChange={(event) => setEmailMessage(event.target.value)} />
			  <div className="flex gap-2"><PrimaryButton type="submit" disabled={busy}>{busy ? "Sending…" : "Send invoice"}</PrimaryButton><QuietButton type="button" disabled={busy || !emailRecipient.trim() || !emailSubject.trim() || !emailMessage.trim()} onClick={() => { setBusy(true); setError(""); setNotice(""); void mutate(`/api/v1/workspace/invoices/${invoiceId}/send-test`, "POST", { recipient: emailRecipient.trim(), subject: emailSubject.trim(), message: emailMessage.trim() }).then(() => { setNotice("Test email sent."); setEmailing(false); }).catch((cause) => setError(cause instanceof Error ? cause.message : "We couldn’t send the test email.")).finally(() => setBusy(false)); }}>Test email</QuietButton><QuietButton type="button" disabled={busy} onClick={() => setEmailing(false)}>Cancel</QuietButton></div>
		  </form>
	  </Dialog>}
      {adjusting && (
        <Dialog
          title="Create invoice adjustment"
          onClose={() => setAdjusting(false)}
        >
          <form
            onSubmit={(event) => void createAdjustment(event)}
            className="space-y-3"
          >
            <p className="text-sm text-[var(--ink-soft)]">
              The original synced invoice stays unchanged. This creates an
              immutable negative correction for QuickBooks.
            </p>
            <TextArea required label="Reason" name="reason" />
            <TextArea label="Customer-facing summary" name="summary" />
            <label className="block text-sm font-semibold">Original invoice line
              <select required name="originalInvoiceLineId" className="mt-1 block w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm">
                <option value="">Select the charge being corrected</option>
                {document.lines.filter((line) => line.unitPriceCents > 0).map((line) => <option key={line.id} value={line.id}>{line.description} · {money(line.totalCents)}</option>)}
              </select>
            </label>
            <TextInput
              required
              label="Correction description"
              name="description"
            />
            <TextInput
              required
              label="Amount to reduce"
              name="amount"
              type="number"
              min="0.01"
              step="0.01"
            />
            <div className="flex gap-2">
              <PrimaryButton disabled={busy}>
                {busy ? "Creating…" : "Create adjustment"}
              </PrimaryButton>
              <QuietButton type="button" onClick={() => setAdjusting(false)}>
                Cancel
              </QuietButton>
            </div>
          </form>
        </Dialog>
      )}
    </article>
  );
}

function PaymentForm({
  invoice,
  busy,
  record,
  done,
}: {
  invoice: InvoiceDocument;
  busy: boolean;
  record: (
    invoiceId: string,
    body: { amountCents: number; method: string; reference: string | null },
  ) => Promise<void>;
  done: () => Promise<void>;
}) {
  const [amount, setAmount] = useState(String(invoice.balanceDueCents / 100));
  const [method, setMethod] = useState("check");
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cents = moneyInputCents(amount);
    if (
      !Number.isFinite(cents) ||
      cents < 1 ||
      cents > invoice.balanceDueCents
    ) {
      setError(`Enter an amount up to ${money(invoice.balanceDueCents)}.`);
      return;
    }
    try {
      const form = new FormData(event.currentTarget);
      await record(invoice.id, {
        amountCents: cents,
        method,
        reference: String(form.get("reference") || "") || null,
      });
      await done();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "We couldn’t record that payment.",
      );
    }
  };
  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="mt-3 grid gap-3 sm:grid-cols-3"
    >
      <TextInput
        required
        label="Amount"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        inputMode="decimal"
      />
      <Select
        label="Method"
        value={method}
        onChange={setMethod}
        options={PAYMENT_METHODS}
      />
      <TextInput label="Reference" name="reference" />
      <div className="sm:col-span-3">
        {error && <p className="mb-2 text-sm text-[var(--accent)]">{error}</p>}
        <PrimaryButton disabled={busy}>
          {busy ? "Saving…" : "Record payment"}
        </PrimaryButton>
      </div>
    </form>
  );
}

type EstimateDocument = {
  id: string;
  customerId?: string;
  customerEmail?: string | null;
  customerPhone?: string | null;
  createdAt?: string;
  jobId?: string | null;
  appointments?: Array<{ id: string; scheduledStart: string; scheduledEnd: string; status: string }>;
  revision?: number;
  optionsManaged?: number;
  options?: PresentedOption[];
  selectionMode?: "single" | "multi";
  proposed?: { kind: "range"; minCents: number; maxCents: number } | { kind: "combined"; totalCents: number };
  terms?: string | null;
  displayNumber: string | null;
  optionLabel?: string | null;
  title?: string | null;
  summary?: string | null;
  status: string;
  officeStatus?: string;
  convertedInvoiceId?: string | null;
  subtotalCents?: number;
  taxCents: number;
  totalCents: number;
  jobNumber?: string | null;
  jobTypeName?: string | null;
  companyName?: string | null;
  companyEmail?: string | null;
  companyPhone?: string | null;
  companyAddressLine1?: string | null;
  companyAddressLine2?: string | null;
  companyCity?: string | null;
  companyState?: string | null;
  companyPostalCode?: string | null;
  customerName: string;
  billingAddressLine1: string | null;
  billingAddressLine2?: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
  serviceAddressLine1: string | null;
  serviceAddressLine2?: string | null;
  serviceCity: string | null;
  serviceState: string | null;
  servicePostalCode: string | null;
  signerName?: string | null;
  signedAt?: string | null;
  signatureDataUrl?: string | null;
  lines: Array<DocumentLineItem & { groupName: string | null }>;
};
type EstimateApproval = { jobNumber?: string | number | null; invoiceDisplayNumber?: string | null; jobStatus?: string | null; paidCents?: number; invoiceTotalCents?: number | null; balanceDueCents?: number | null; id: string; soldByName?: string | null; createdAt?: string; optionId: string; customerName: string; method: "onsite" | "phone" | "remote"; approvedAmountCents: number; jobId?: string | null; invoiceId?: string | null; fulfillment?: "now" | "later" | null };

type OfficeEstimateLine = { id?: string; priceBookItemId?: string | null; itemName?: string; description: string; quantity: number; unitPriceCents: number };
type OfficeEstimateOption = { id?: string; label: string; description: string; isRecommended: boolean; taxCents: number; lines: OfficeEstimateLine[] };
const estimateItemChips: { kind: EstimateItemKind; label: string }[] = [
  { kind: "Service", label: "Service" }, { kind: "Equipment", label: "Equipment" },
  { kind: "Parts", label: "Materials" }, { kind: "Discounts", label: "Discounts" },
  { kind: "Rebates", label: "Rebates" }, { kind: "Fees", label: "Fees" },
];
const blankOfficeOption = (label = "Recommended work"): OfficeEstimateOption => ({ label, description: "", isRecommended: false, taxCents: 0, lines: [] });

function OfficeEstimateComposer({ owner, priceBookItems, priceBookCategories, estimateId: initialEstimateId, focusOptionId, presentation = false, onClose, onSaved }: { presentation?: boolean; owner: Pick<JobDetail["job"], "customerId" | "propertyId"> & { jobId?: string }; priceBookItems: PriceBookItem[]; priceBookCategories: PriceBookCategory[]; estimateId?: string; focusOptionId?: string; onClose: () => void; onSaved: (id: string) => void }) {
  const [estimateId, setEstimateId] = useState(initialEstimateId);
  const [editableFocusOptionId, setEditableFocusOptionId] = useState(focusOptionId);
  const [duplicatePickerOpen, setDuplicatePickerOpen] = useState(false);
  const [title, setTitle] = useState(""); const [terms, setTerms] = useState(""); const [selectionMode, setSelectionMode] = useState<"single" | "multi">("multi"); const [buildMode, setBuildMode] = useState<"scratch" | null>(null); const [options, setOptions] = useState<OfficeEstimateOption[]>([blankOfficeOption()]); const [itemKind, setItemKind] = useState<EstimateItemKind | null>(null); const [itemTarget, setItemTarget] = useState(0); const [busy, setBusy] = useState(false); const [error, setError] = useState(""); const [taxNotice, setTaxNotice] = useState(""); const [calculatingTax, setCalculatingTax] = useState(false); const [templatePicker, setTemplatePicker] = useState(false); const [templatePreview, setTemplatePreview] = useState<TemplatePreview | null>(null); const [revision, setRevision] = useState<number | null>(null);
  useEffect(() => { if (!estimateId) return; setBusy(true); void apiFetch(`/api/v1/workspace/estimates/${encodeURIComponent(estimateId)}`).then(async (response) => { const payload = await response.json(); if (!response.ok) throw new Error(payload.error?.message || "Unable to load estimate"); const document = payload.data.document; setTitle(document.title); setTerms(document.terms || ""); setSelectionMode(document.selectionMode); setRevision(document.revision); setOptions(document.options.map((option: OfficeEstimateOption) => ({ id: option.id, label: option.label, description: estimateDescription(option.description), isRecommended: option.isRecommended, taxCents: option.taxCents || 0, lines: option.lines.map((line) => ({ id: line.id, priceBookItemId: line.priceBookItemId, description: estimateDescription(line.description), quantity: line.quantity, unitPriceCents: line.unitPriceCents })) }))); setBuildMode("scratch"); }).catch((cause) => setError(cause instanceof Error ? cause.message : "Unable to load estimate")).finally(() => setBusy(false)); }, [estimateId]);
  const updateOption = (index: number, change: Partial<OfficeEstimateOption>) => setOptions((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, ...change } : item));
  const updateLine = (optionIndex: number, lineIndex: number, change: Partial<OfficeEstimateLine>) => setOptions((items) => items.map((option, currentOption) => currentOption !== optionIndex ? option : { ...option, lines: option.lines.map((line, currentLine) => currentLine === lineIndex ? { ...line, ...change } : line) }));
  const addPricebookLine = (optionIndex: number, item: { id: string; name: string; description: string | null; priceCents: number }) => updateOption(optionIndex, { lines: [...options[optionIndex].lines.filter((line) => line.description.trim()), { priceBookItemId: item.id, itemName: item.name, description: estimateDescription(item.description) || estimateDescription(item.name), quantity: 1, unitPriceCents: item.priceCents }] });
  const duplicateOption = (index: number) => {
    setOptions(current => {
      const source = current[index];
      if (!source || current.length >= 20) return current;
      const label = current.length === 1 ? title.trim() || source.label : source.label;
      return [...current.map(option => current.length === 1 ? { ...option, label } : option), {
        label: `${label} (copy)`, description: source.description, isRecommended: false, taxCents: 0,
        lines: source.lines.map(({ id: _id, ...line }) => ({ ...line })),
      }];
    });
    setEditableFocusOptionId(undefined);
    setDuplicatePickerOpen(false);
  };
  const duplicateAction = estimateId && revision ? <QuietButton type="button" disabled={busy || options.length === 0 || options.length >= 20} onClick={() => {
    if (editableFocusOptionId) duplicateOption(options.findIndex(option => option.id === editableFocusOptionId));
    else if (options.length === 1) duplicateOption(0);
    else setDuplicatePickerOpen(true);
  }}>Duplicate</QuietButton> : null;
  const createOptionAction = estimateId && revision && !editableFocusOptionId && <QuietButton type="button" disabled={busy || options.length >= 20} onClick={() => setOptions(current => current.length >= 20 ? current : [...current.map(option => current.length === 1 ? { ...option, label: title.trim() || option.label } : option), blankOfficeOption(`Option ${current.length + 1}`)])}>Add another option</QuietButton>;
  const duplicatePicker = duplicatePickerOpen && <Dialog title="Duplicate option" onClose={() => setDuplicatePickerOpen(false)}>
    <p className="mb-3 text-sm text-[var(--ink-soft)]">Choose the option to copy.</p>
    <div className="grid gap-2">{options.map((option, index) => <QuietButton key={option.id ?? index} type="button" onClick={() => duplicateOption(index)}>{option.label || `Option ${index + 1}`}</QuietButton>)}</div>
  </Dialog>;
  const deductionBaseCents = (optionIndex: number) => options[optionIndex].lines.reduce((sum, line) => sum + Math.max(0, Math.round(line.quantity * line.unitPriceCents)), 0);
  const valid = title.trim() && options.length > 0 && options.every((option) => option.label.trim() && option.lines.length > 0 && option.lines.every((line) => line.description.trim() && line.quantity > 0 && Number.isInteger(line.unitPriceCents)) && option.lines.reduce((sum, line) => sum + Math.round(line.quantity * line.unitPriceCents), 0) >= 0);
  const taxPreviewKey = JSON.stringify(options.map((option) => option.lines.map((line) => [line.priceBookItemId ?? null, line.description, line.quantity, line.unitPriceCents])));
  useEffect(() => {
    if (!buildMode || !valid) { setCalculatingTax(false); return; }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setCalculatingTax(true); setTaxNotice("");
      void apiFetch("/api/v1/workspace/estimates/tax-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          propertyId: owner.propertyId,
          options: options.map((option) => ({
            lines: option.lines.map((line) => ({
              ...(line.priceBookItemId ? { priceBookItemId: line.priceBookItemId } : {}),
              description: estimateDescription(line.description),
              quantity: line.quantity,
              unitPriceCents: line.unitPriceCents,
            })),
          })),
        }),
      }).then(async (response) => {
        const payload = await response.json(); if (!response.ok) throw new Error(payload.error?.message || "Tax preview could not be calculated."); return payload.data as { status: string; message?: string; taxCents: number[] };
      }).then((preview) => { if (!cancelled) { setOptions((current) => current.map((option, index) => ({ ...option, taxCents: preview.taxCents[index] ?? 0 }))); setTaxNotice(preview.status === "attention" ? preview.message || "Tax needs attention before saving." : ""); } }).catch((cause) => { if (!cancelled) setTaxNotice(cause instanceof Error ? cause.message : "Tax preview could not be calculated."); }).finally(() => { if (!cancelled) setCalculatingTax(false); });
    }, 350);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [buildMode, owner.propertyId, taxPreviewKey, valid]);
  const save = async () => { if (!valid || (estimateId && !revision)) return setError("Add an estimate name and valid items."); setBusy(true); setError(""); try { const content = { title: title.trim(), terms: terms.trim() || null, selectionMode, options: options.map((option) => ({ ...(option.id ? { id: option.id } : {}), label: options.length === 1 && !presentation ? title.trim() : option.label.trim(), description: estimateDescription(option.description) || null, isRecommended: option.isRecommended, taxCents: 0, lines: option.lines.map((line) => ({ ...(line.id ? { id: line.id } : {}), ...(line.priceBookItemId ? { priceBookItemId: line.priceBookItemId } : {}), description: estimateDescription(line.description), quantity: line.quantity, unitPriceCents: line.unitPriceCents })) })) }; const response = await apiFetch(estimateId ? `/api/v1/workspace/estimates/${encodeURIComponent(estimateId)}` : "/api/v1/workspace/estimates", { method: estimateId ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(estimateId ? { ...content, revision } : { ...content, customerId: owner.customerId, propertyId: owner.propertyId, ...(owner.jobId ? { jobId: owner.jobId } : {}) }) }); const payload = await response.json(); if (!response.ok) throw new Error(payload.error?.message || "Unable to save estimate"); const saved = payload.data.document; setOptions(saved.options.map((option: OfficeEstimateOption) => ({ id: option.id, label: option.label, description: estimateDescription(option.description), isRecommended: option.isRecommended, taxCents: option.taxCents || 0, lines: option.lines.map((line) => ({ id: line.id, priceBookItemId: line.priceBookItemId, description: estimateDescription(line.description), quantity: line.quantity, unitPriceCents: line.unitPriceCents })) }))); setRevision(saved.revision); setEstimateId(saved.id); onSaved(saved.id); } catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to save estimate"); } finally { setBusy(false); } };
  const applyPreview = (preview: TemplatePreview) => { const document = preview.document; setTitle(document.title); setTerms(document.terms || ""); setSelectionMode(document.selectionMode); setOptions(document.options.map((option) => ({ label: option.label, description: estimateDescription(option.description), isRecommended: Boolean(option.isRecommended), taxCents: option.taxCents || 0, lines: option.lines.map((line) => ({ priceBookItemId: line.priceBookItemId, description: estimateDescription(line.description), quantity: line.quantity, unitPriceCents: line.unitPriceCents })) }))); setBuildMode("scratch"); setTemplatePicker(false); setTemplatePreview(null); };
  const useTemplate = async (template: EstimateTemplateChoice) => { setBusy(true); setError(""); try { const response = await apiFetch(`/api/v1/workspace/estimate-templates/${encodeURIComponent(template.id)}/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ customerId: owner.customerId, propertyId: owner.propertyId, ...(owner.jobId ? { jobId: owner.jobId } : {}), revision: template.revision }) }); const payload = await response.json(); if (!response.ok) throw new Error(response.status >= 500 ? "This template could not be loaded. Try again, choose another template, or build from scratch." : payload.error?.message || "Unable to preview template"); const preview = payload.data as TemplatePreview; if (template.sourceKind === "package") { setTemplatePreview(preview); setTemplatePicker(false); } else applyPreview(preview); } catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to preview template"); } finally { setBusy(false); } };

  if (presentation) {
    const singleEstimateName = options.length === 1 && options[0].label === title;
    const displayedOptions = options.map((option, index) => ({
      ...option, id: option.id ?? `new-option-${index}`, description: option.description || null,
      lines: option.lines.map((line, lineIndex) => ({ ...line, id: line.id ?? `new-line-${index}-${lineIndex}` })),
      totalCents: option.lines.reduce((sum, line) => sum + Math.round(line.quantity * line.unitPriceCents), 0) + option.taxCents,
    }));
    const optionIndex = (id: string) => displayedOptions.findIndex(option => option.id === id);
    const lineIndex = (id: string, lineId: string) => displayedOptions[optionIndex(id)].lines.findIndex(line => line.id === lineId);
    return <section aria-label="Edit estimate in job" className="mt-3">
      <div className="flex items-start justify-end gap-3">
        {!singleEstimateName && <label className="min-w-0 flex-1 text-sm font-semibold">Estimate name<input aria-label="Estimate name" disabled={busy} value={title} onChange={event => setTitle(event.target.value)} className="mt-1 w-full rounded-md border border-[var(--line)] bg-[var(--paper)] px-2 py-1.5 text-lg font-semibold" /></label>}
        <button type="button" aria-label="Close without saving" title="Close without saving" disabled={busy} onClick={onClose} className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-[var(--ink-soft)] hover:bg-[var(--paper)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"><X size={18} aria-hidden="true" /></button>
      </div>
      {busy && !revision ? <p role="status" className="mt-3 text-sm">Loading estimate…</p> : revision && <EstimatePresentation office options={displayedOptions} selected={[]} onSelect={() => undefined}
        catalog={priceBookItems.length ? { items: priceBookItems, categories: priceBookCategories } : undefined}
        officeEditor={{ disabled: busy, optionId: editableFocusOptionId, singleEstimateName,
          onOptionChange: (id, change) => { if (singleEstimateName && change.label !== undefined) setTitle(change.label); updateOption(optionIndex(id), change); },
          onLineChange: (id, lineId, change) => updateLine(optionIndex(id), lineIndex(id, lineId), change),
          onRemoveLine: (id, lineId) => updateOption(optionIndex(id), { lines: options[optionIndex(id)].lines.filter((_, index) => index !== lineIndex(id, lineId)) }),
          renderActions: id => <div className="flex flex-wrap gap-2">{estimateItemChips.map(({ kind, label }) => <QuietButton key={kind} type="button" disabled={busy} onClick={() => { setItemTarget(optionIndex(id)); setItemKind(kind); }}>{label}</QuietButton>)}</div>,
        }} />}
      {taxNotice && <p role="status" className="mt-3 text-sm text-amber-800">{taxNotice}</p>}
      {error && <p role="alert" className="mt-3 text-sm text-[var(--accent)]">{error}</p>}
      <div className="mt-4 flex flex-wrap gap-2">{createOptionAction}{duplicateAction}</div>
      {duplicatePicker}
      <div className="mt-4 flex justify-end border-t border-[var(--line)] pt-4"><PrimaryButton type="button" disabled={busy || calculatingTax || !revision} onClick={() => void save()}>{busy ? "Saving…" : calculatingTax ? "Calculating tax…" : "Save estimate"}</PrimaryButton></div>
      {itemKind && <EstimateItemPicker key={itemKind} kind={itemKind} deductionBaseCents={deductionBaseCents(itemTarget)} onClose={() => setItemKind(null)} onAdd={item => addPricebookLine(itemTarget, item)} />}
    </section>;
  }
  return <section className="mt-3 rounded-xl border border-[var(--line)] bg-[var(--wash)] p-4" aria-label={estimateId ? "Edit estimate" : "New estimate"}>
    <div className="flex items-center justify-between gap-3">
      <h3 className="font-semibold">{estimateId ? "Edit estimate" : "New estimate"}</h3>
      <QuietButton type="button" disabled={busy} onClick={onClose}>Cancel</QuietButton>
    </div>
    {!estimateId && <div className="estimate-build-choices">
      <button type="button" className="estimate-choice-tile" aria-label="Build From Scratch" disabled={busy} aria-pressed={buildMode === "scratch"} onClick={() => { setBuildMode("scratch"); setError(""); }}>
        <span className="estimate-tile-icon"><FilePlus2 size={22} aria-hidden="true" /></span>
        <strong>Build From Scratch</strong><small>Name your estimate and add the items you need.</small>
        <span className="estimate-tile-action">Start building <ArrowRight size={16} aria-hidden="true" /></span>
      </button>
      <button type="button" className="estimate-choice-tile" aria-label="Build from Templates" disabled={busy} onClick={() => { setTemplatePicker(true); setError(""); }}>
        <span className="estimate-tile-icon"><LayoutGrid size={22} aria-hidden="true" /></span>
        <strong>Build from Templates</strong><small>Browse saved packages or search for a template.</small>
        <span className="estimate-tile-action">Browse templates <ArrowRight size={16} aria-hidden="true" /></span>
      </button>
    </div>}
    {buildMode === "scratch" && <fieldset disabled={busy} className="mt-4 space-y-4">
      <label className="block text-sm font-semibold">Estimate name<input aria-label="Estimate name" value={title} onChange={(event) => setTitle(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 font-normal" /></label>
      {options.length > 1 && <label className="block text-sm font-semibold">Customer approval<select aria-label="Customer approval" value={selectionMode} onChange={(event) => setSelectionMode(event.target.value as "single" | "multi")} className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 font-normal"><option value="single">Customer chooses one</option><option value="multi">Customer can approve multiple</option></select></label>}
      {editableFocusOptionId && <p className="rounded-lg bg-[var(--accent-wash)] px-3 py-2 text-sm text-[var(--ink-soft)]">Editing the selected option. The other options are retained unchanged.</p>}
      {options.map((option, optionIndex) => ({ option, optionIndex })).filter(({ option }) => !editableFocusOptionId || option.id === editableFocusOptionId).map(({ option, optionIndex }) => <div key={option.id ?? optionIndex} className="rounded-lg border border-[var(--line)] bg-[var(--paper)] p-3">
        {options.length > 1 && <label className="block text-sm font-semibold">Option name<input aria-label={`Office option ${optionIndex + 1} name`} value={option.label} onChange={(event) => updateOption(optionIndex, { label: event.target.value })} className="mt-1 w-full rounded border border-[var(--line)] px-2 py-1.5 font-normal" /></label>}
        <label className="mt-3 block text-sm font-semibold">{options.length === 1 ? "Estimate description" : "Option description"}<EstimateDescription aria-label={`Office option ${optionIndex + 1} description`} value={option.description} onChange={(event) => updateOption(optionIndex, { description: event.target.value })} className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 font-normal" /></label>
<EstimateOptionItems lines={option.lines} items={priceBookItems} categories={priceBookCategories} optionNumber={optionIndex + 1} onChange={(lineIndex, change) => updateLine(optionIndex, lineIndex, change)} onRemove={(lineIndex) => updateOption(optionIndex, { lines: option.lines.filter((_, index) => index !== lineIndex) })} />{option.lines.some((line) => line.priceBookItemId && line.unitPriceCents === 0) && <p className="mt-2 text-xs font-semibold text-amber-800">$0.00 — review price before saving</p>}{option.lines.length > 0 && <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-[var(--wash)] px-3 py-2 text-xs"><span>Subtotal <b className="block text-sm text-[var(--ink)]">{money(option.lines.reduce((sum, line) => sum + Math.round(line.quantity * line.unitPriceCents), 0))}</b></span><span>Tax <b className="block text-sm text-[var(--ink)]">{calculatingTax ? "Calculating…" : money(option.taxCents)}</b></span><span className="text-right">Total <b className="block text-sm text-[var(--ink)]">{calculatingTax ? "Calculating…" : money(option.lines.reduce((sum, line) => sum + Math.round(line.quantity * line.unitPriceCents), 0) + option.taxCents)}</b></span></div>}<div className="mt-3 flex flex-wrap gap-2" aria-label={`Add items to option ${optionIndex + 1}`}>{estimateItemChips.map(({ kind, label }) => <QuietButton key={kind} type="button" onClick={() => { setItemTarget(optionIndex); setItemKind(kind); }}>{label}</QuietButton>)}</div>
      </div>)}
      {estimateId && revision && <div className="flex flex-wrap gap-2">{createOptionAction}{duplicateAction}</div>}
      {duplicatePicker}
      {taxNotice && <p role="status" className="text-sm text-amber-800">{taxNotice}</p>}<PrimaryButton type="button" disabled={busy || calculatingTax} onClick={() => void save()}>{busy ? "Saving…" : calculatingTax ? "Calculating tax…" : "Save estimate"}</PrimaryButton>
    </fieldset>}
    {error && <p role="alert" className="mt-3 text-sm text-[var(--accent)]">{error}</p>}
    {itemKind && <EstimateItemPicker key={itemKind} kind={itemKind} deductionBaseCents={deductionBaseCents(itemTarget)} onClose={() => setItemKind(null)} onAdd={(item) => addPricebookLine(itemTarget, item)} />}
    {templatePicker && <EstimateTemplatePicker endpoint="/api/v1/workspace/estimate-templates" busy={busy} error={error} onBuildFromScratch={() => { setTemplatePicker(false); setTemplatePreview(null); setBuildMode("scratch"); setError(""); }} onClose={() => { if (!busy) setTemplatePicker(false); }} onSelect={(template) => { if (!busy) void useTemplate(template); }} />}
    {templatePreview && <EstimatePackageReview preview={templatePreview} busy={busy} onBack={() => setTemplatePreview(null)} onContinue={() => applyPreview(templatePreview)} />}
  </section>;
}

export function EstimateWorkspace({
  estimateId,
  back,
  openJob,
  openInJob = false,
  canWrite = false,
}: {
  openInJob?: boolean;
  canWrite?: boolean;
  estimateId: string;
  back: () => void;
  openJob: (jobId: string, step?: JobPreparationStep) => void;
}) {
  const communication = useRecordCommunications();
  const [legacyInvoice, setLegacyInvoice] = useState<{ jobId?: string; paidCents: number; balanceDueCents: number; totalCents: number } | null>(null);
  const [document, setDocument] = useState<EstimateDocument | null>(null);
  const workItem = document ? { ...document, estimateNumber: 0, createdAt: document.createdAt ?? "", jobId: document.jobId ?? undefined } : null;
  const workFacts = useEstimateWorkFacts(workItem ? [workItem] : [], true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [deliveryRevision, setDeliveryRevision] = useState(0);
  const [pdfOption, setPdfOption] = useState<{ id?: string; label: string } | null>(null);
  const [emailJobTypes, setEmailJobTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [fulfilling, setFulfilling] = useState(false);
  const [approvals, setApprovals] = useState<EstimateApproval[]>([]);
  const [approvalsLoaded, setApprovalsLoaded] = useState(false);
  const openedJob = useRef<string | null>(null);
  const [booking, setBooking] = useState(false);
  const [remoteApproval, setRemoteApproval] = useState(false);
  useEffect(() => {
    if (!emailing) return;
    let cancelled = false;
    void data<{ items: Array<{ id: string; name: string; active: boolean }> }>("/api/v1/workspace/dispatch/job-types").then(result => { if (!cancelled) setEmailJobTypes(result.items.filter(type => type.active)); }).catch(() => { if (!cancelled) setError("Job types could not be loaded. Reopen email to retry."); });
    return () => { cancelled = true; };
  }, [emailing]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    void data<{ document: EstimateDocument }>(
      `/api/v1/workspace/estimates/${estimateId}/document`,
    )
      .then((payload) => {
        if (!cancelled) setDocument(payload.document);
      })
      .catch((cause) => {
        if (!cancelled)
          setError(
            cause instanceof Error
              ? cause.message
              : "Estimate not found.",
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [estimateId]);
  useEffect(() => {
    setLegacyInvoice(null);
    if (!document?.convertedInvoiceId || document.optionsManaged) return;
    let cancelled = false;
    void data<{ document: { jobId?: string; balanceDueCents: number; totalCents: number; payments: Array<{ kind?: string; amountCents: number }> } }>(`/api/v1/workspace/invoices/${document.convertedInvoiceId}/document`).then(({ document: invoice }) => {
      if (!cancelled) setLegacyInvoice({ jobId: invoice.jobId, balanceDueCents: invoice.balanceDueCents, totalCents: invoice.totalCents, paidCents: (invoice.payments ?? []).reduce((sum, payment) => sum + (payment.kind === "refund" ? -1 : 1) * Math.abs(payment.amountCents), 0) });
    }).catch(() => { if (!cancelled) setError("Invoice payment details could not be loaded."); });
    return () => { cancelled = true; };
  }, [document?.convertedInvoiceId, document?.optionsManaged]);
  const loadApprovals = useCallback(() => {
    void data<{ items: EstimateApproval[] }>(`/api/v1/workspace/estimates/${estimateId}/approvals`)
      .then((payload) => { setApprovals(payload.items); setApprovalsLoaded(true); })
      .catch(() => { setApprovals([]); setError("Approval history could not be loaded. Reopen the estimate to retry."); });
  }, [estimateId]);
  useEffect(loadApprovals, [loadApprovals]);
  useEffect(() => {
    if (!openInJob || loading || !document || !approvalsLoaded) return;
    const jobs = [...new Set(approvals.flatMap(approval => approval.jobId ? [approval.jobId] : []))];
    // Keep unallocated and split sold work visible so the owner can choose its destination.
    if (approvals.some(approval => !approval.jobId) || jobs.length > 1) return;
    if (!document.optionsManaged && document.convertedInvoiceId && !legacyInvoice) return;
    const jobId = jobs[0] || legacyInvoice?.jobId || document.jobId;
    if (jobId && openedJob.current !== jobId) { openedJob.current = jobId; openJob(jobId); }
  }, [openInJob, loading, document, approvalsLoaded, approvals, legacyInvoice, openJob]);
  const pendingApprovals = approvals.filter((approval) => !approval.jobId);
  const hasRemainingOptions = document?.options?.some(
    (option) => option.status !== "accepted" && option.status !== "declined",
  ) ?? false;
  const printPdf = (optionId?: string) => {
    if (!document) return;
    setError("");
    void openPrintablePdf(
      `/api/v1/workspace/estimates/${encodeURIComponent(document.id)}/pdf${optionId ? `?optionId=${encodeURIComponent(optionId)}` : ""}`,
      "Estimate",
    ).catch((cause) =>
      setError(cause instanceof Error ? cause.message : "We couldn’t open the printable estimate."),
    );
  };
  if (loading)
    return <p className="text-sm text-[var(--ink-soft)]">Loading estimate…</p>;
  if (!document)
    return (
      <div>
        <button onClick={back} className="text-sm font-semibold text-[var(--ink)]">
          ← Back
        </button>
        <p className="mt-4 text-sm text-[var(--accent)]">
          {error || "Estimate documents are not on this API yet."}
        </p>
      </div>
    );
  if (openInJob) {
    if (document.optionsManaged && !approvalsLoaded) return <section><QuietButton type="button" onClick={back}>← Back</QuietButton><p role={error ? "alert" : "status"}>{error || "Finding the estimate’s job…"}</p></section>;
    if (pendingApprovals.length) return <EstimateFulfillmentDialog document={document} approvals={pendingApprovals} onClose={back} onDone={(jobId, step) => { if (jobId) openJob(jobId, step); else loadApprovals(); }} />;
    const jobs = [...new Set(approvals.flatMap(approval => approval.jobId ? [approval.jobId] : []))];
    if (jobs.length > 1) return <section className="space-y-4"><QuietButton type="button" onClick={back}>← Back</QuietButton><h1 className="text-xl font-semibold">Jobs for {document.displayNumber || document.title || "this estimate"}</h1>{jobs.map(jobId => <div key={jobId}><p>{approvals.filter(approval => approval.jobId === jobId).map(approval => document.options?.find(option => option.id === approval.optionId)?.label).filter(Boolean).join(" · ")}</p><QuietButton type="button" onClick={() => openJob(jobId)}>{workFacts.jobs[jobId]?.job.jobNumber ? `Open job #${workFacts.jobs[jobId].job.jobNumber}` : "Open job"}</QuietButton></div>)}</section>;
    if (jobs.length || document.jobId) return <section><QuietButton type="button" onClick={back}>← Back</QuietButton><p role="status">Opening job…</p></section>;
    return <section className="space-y-4"><QuietButton type="button" onClick={back}>← Back</QuietButton><h1 className="text-xl font-semibold">{document.title || "Estimate"}</h1><p>This estimate has no linked job or approved work ready to book.</p></section>;
  }
  const jobs = [...new Set([...(document.jobId ? [document.jobId] : []), ...approvals.flatMap(approval => approval.jobId ? [approval.jobId] : [])])];
  const options = document.optionsManaged && document.options?.length ? document.options : [{ id: "", label: document.optionLabel || "Estimate PDF", description: document.summary, status: document.status, totalCents: document.totalCents, taxCents: document.taxCents, lines: document.lines }];
  return <Dialog title={document.displayNumber ? `Estimate #${document.displayNumber}` : "Estimate"} onClose={back} width="max-w-6xl" className="estimate-record-dialog" overlayClassName="estimate-dialog-overlay"><article className="space-y-5">
    {error && <p role="alert" className="rounded-lg border border-[var(--line)] p-3 text-sm">{error}</p>}
    <header className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5"><div className="flex flex-wrap items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-soft)]">Estimate</p><h1 className="mt-1 text-2xl font-semibold">{document.displayNumber ? `#${document.displayNumber}` : "Estimate number unavailable"}</h1><p className="mt-1 font-semibold">{document.customerName}</p><p className="mt-1 text-sm text-[var(--ink-soft)]">{address([document.serviceAddressLine1, document.serviceAddressLine2, document.serviceCity, document.serviceState, document.servicePostalCode])}</p></div><span className="rounded-full bg-[var(--wash)] px-3 py-1 text-sm font-semibold">{document.status === "accepted" ? hasRemainingOptions ? "Partially sold" : "Sold" : (document.officeStatus || document.status).replaceAll("_", " ")}</span></div>
    <div className="flex flex-wrap items-center justify-between gap-3"><div className="flex flex-wrap gap-2">
      {canWrite && !["void", "cancelled", "canceled"].includes(document.status) && <PrimaryButton type="button" onClick={() => setEmailing(true)}>Email estimate</PrimaryButton>}
      {canWrite && Boolean(document.optionsManaged) && hasRemainingOptions && <QuietButton type="button" onClick={() => setRemoteApproval(true)}>Text estimate</QuietButton>}
      {canWrite && (!document.optionsManaged || !hasRemainingOptions) && document.customerId && document.customerPhone && communication.openText && <QuietButton type="button" onClick={() => communication.openText?.({ customerId: document.customerId!, phone: document.customerPhone!, name: document.customerName })}>Text customer</QuietButton>}
      {canWrite && Boolean(document.optionsManaged) && document.status !== "accepted" && <QuietButton type="button" onClick={() => setBooking(true)}>Schedule appointment</QuietButton>}
    </div></div>
      {workItem && <EstimateWorkStatusSummary item={workItem} facts={workFacts} onOpenJob={openJob} />}
      <EstimateDeliverySummary estimateId={document.id} revision={deliveryRevision} />
      {(document.title || document.summary) && <section aria-label="Estimate details" className="mt-4 border-t border-[var(--line)] pt-4"><h2 className="font-semibold">{estimateDescription(document.title || document.optionLabel) || "Estimate details"}</h2>{document.summary && <p className="mt-2 whitespace-pre-wrap text-sm leading-6">{estimateDescription(document.summary)}</p>}</section>}
    </header>
    {canWrite && pendingApprovals.length > 0 && <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950"><span><b>{pendingApprovals.length} sold {pendingApprovals.length === 1 ? "option" : "options"}</b> ready to schedule.</span><QuietButton type="button" onClick={() => setFulfilling(true)}>Schedule job</QuietButton></div>}
    <section aria-label="Estimate options" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{options.map(option => {
      const sales: EstimateApproval[] = !document.optionsManaged && document.convertedInvoiceId && legacyInvoice ? [{ id: "legacy-invoice", optionId: "", customerName: document.customerName, method: "onsite", approvedAmountCents: document.totalCents, jobId: legacyInvoice.jobId || document.jobId, invoiceId: document.convertedInvoiceId, ...legacyInvoice }] : approvals.filter(approval => approval.optionId === option.id);
      const sold = option.status === "accepted" || sales.length > 0;
      const status = sold ? "Sold" : option.status === "declined" ? "Declined" : option.status === "void" ? "Void" : document.status === "sent" ? "Sent" : "Open";
      const optionJobs = [...new Set(sales.flatMap(sale => sale.jobId ? [sale.jobId] : []))];
      return <section key={option.id || "legacy"} aria-label={`Option ${option.label}`} className="flex flex-col overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
        <button type="button" aria-label={`Preview PDF for ${option.label}`} onClick={() => setPdfOption({ id: option.id || undefined, label: option.label })} className="flex min-h-36 items-center justify-center gap-3 border-b border-[var(--line)] bg-[var(--wash)] p-5 text-[var(--ink-soft)] hover:text-[var(--ink)]"><svg width="42" height="52" viewBox="0 0 42 52" fill="none" aria-hidden="true"><path d="M6 1h20l15 15v35H1V6a5 5 0 0 1 5-5Z" stroke="currentColor"/><path d="M26 1v15h15M9 27h23M9 34h23M9 41h14" stroke="currentColor"/></svg><span className="text-sm font-semibold">View PDF</span></button>
        <div className="flex flex-1 flex-col gap-3 p-4"><div className="flex items-start justify-between gap-3"><h2 className="font-semibold">{option.label}</h2><strong className="whitespace-nowrap text-lg tabular-nums">{money(option.totalCents)}</strong></div>
          <div><span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${sold ? "bg-emerald-100 text-emerald-900" : status === "Declined" ? "bg-slate-100 text-slate-600" : "bg-blue-50 text-blue-900"}`}>{status}</span></div>
          {option.description && <p className="line-clamp-3 whitespace-pre-wrap text-sm text-[var(--ink-soft)]">{estimateDescription(option.description)}</p>}
          {sold && (sales.length ? sales.map(sale => <div key={sale.id} className="rounded-lg bg-[var(--wash)] p-3 text-sm"><p className="font-semibold">{!sale.invoiceId ? "Awaiting invoice" : sale.paidCents === undefined ? "Payment status unavailable" : sale.invoiceTotalCents === 0 ? "No deposit required" : (sale.paidCents ?? 0) <= 0 ? "No deposit received" : (sale.balanceDueCents ?? 1) <= 0 ? "Paid in full" : "Deposit received"}</p>{sale.invoiceId && sale.paidCents !== undefined && <p className="mt-1 text-[var(--ink-soft)]">Invoice payments: {money(sale.paidCents)}</p>}<p className="mt-1 text-xs text-[var(--ink-soft)]">{sale.jobId ? `${workFacts.jobs[sale.jobId]?.job.jobNumber ? `Job #${workFacts.jobs[sale.jobId].job.jobNumber}` : "Assigned to job"} · ${sale.jobStatus === "completed" ? "Completed" : sale.fulfillment === "later" && sale.jobStatus === "new" ? "Unscheduled" : sale.jobStatus || "Status unavailable"}` : "Ready to schedule"}</p></div>) : <p className="text-sm text-[var(--ink-soft)]">Payment details are available in the job invoice.</p>)}
          <div className="mt-auto flex flex-wrap gap-2 pt-2"><QuietButton type="button" onClick={() => printPdf(option.id || undefined)}>Open PDF</QuietButton>{optionJobs.map(jobId => <QuietButton key={jobId} type="button" onClick={() => openJob(jobId)}>{workFacts.jobs[jobId]?.job.jobNumber ? `Open job #${workFacts.jobs[jobId].job.jobNumber}` : "Open job"}</QuietButton>)}</div>
          <details className="border-t border-[var(--line)] pt-2 text-sm"><summary className="cursor-pointer text-[var(--ink-soft)]">Line items</summary><div className="mt-2 space-y-2">{option.lines.map(line => <p key={line.id}>{estimateDescription(line.description)} <span className="text-[var(--ink-soft)]">· {line.quantity} × {money(line.unitPriceCents)}</span></p>)}</div></details>
        </div>
      </section>;
    })}</section>
    {jobs.filter(jobId => !approvals.some(approval => approval.jobId === jobId) && !(!document.optionsManaged && legacyInvoice && document.jobId === jobId)).map(jobId => <QuietButton key={jobId} type="button" onClick={() => openJob(jobId)}>{workFacts.jobs[jobId]?.job.jobNumber ? `Open job #${workFacts.jobs[jobId].job.jobNumber}` : "Open job"}</QuietButton>)}
    {document.terms && <p className="text-sm text-[var(--ink-soft)]">Terms: {document.terms}</p>}
    {emailing && <EstimateBatchEmail jobTypes={emailJobTypes} estimates={[{ id: document.id, estimateNumber: 0, displayNumber: document.displayNumber, title: document.title, status: document.status, officeStatus: document.officeStatus, optionsManaged: document.optionsManaged, totalCents: document.totalCents, createdAt: document.createdAt || "" }]} customerEmail={document.customerEmail || ""} emailRecipients={document.customerEmail ? [{ email: document.customerEmail, name: document.customerName }] : []} onClose={() => { setEmailing(false); setDeliveryRevision(value => value + 1); loadApprovals(); }} />}
    {pdfOption && <EstimatePdfPreview estimateId={document.id} initialOptionId={pdfOption.id} label={pdfOption.label} onClose={() => setPdfOption(null)} />}
    {fulfilling && <EstimateFulfillmentDialog document={document} approvals={pendingApprovals} onClose={() => setFulfilling(false)} onDone={(jobId, step) => { if (jobId) openJob(jobId, step); else loadApprovals(); }} />}
    {booking && <OwnerAppointmentDialog owner={{ type: "estimate", id: document.id }} onClose={() => setBooking(false)} onDone={() => { setBooking(false); window.location.reload(); }} />}
    {remoteApproval && <RemoteApprovalDialog estimateId={document.id} initialChannel="sms" onClose={() => setRemoteApproval(false)} onDone={() => { setDeliveryRevision(value => value + 1); loadApprovals(); }} />}
  </article></Dialog>;
}

function OwnerAppointmentDialog({ owner, events = [], onClose, onDone }: { owner: { type: "estimate" | "membership"; id: string }; events?: Array<{ id: string; status: string; dueAt: string | null }>; onClose: () => void; onDone: () => void }) {
  const initialStart = new Date(Date.now() + 60 * 60 * 1000); initialStart.setMinutes(0, 0, 0);
  const [startsAt, setStartsAt] = useState(new Date(initialStart.getTime() - initialStart.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  const [endsAt, setEndsAt] = useState(new Date(initialStart.getTime() + 60 * 60 * 1000 - initialStart.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  const [eventId, setEventId] = useState(events.length === 1 ? events[0].id : ""), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const eligibleEvents = events.filter((event) => ["planned", "scheduled"].includes(event.status));
  const submit = async () => { setBusy(true); setError(""); try { const response = await apiFetch("/api/v1/workspace/dispatch/appointments", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ owner, startsAt: new Date(startsAt).toISOString(), endsAt: new Date(endsAt).toISOString(), ...(eventId ? { membershipServiceEventId: eventId } : {}) }) }); if (!response.ok) { const body = await response.json().catch(() => null) as { error?: { message?: string } } | null; throw new Error(body?.error?.message || "Appointment could not be scheduled."); } onDone(); } catch (cause) { setError(cause instanceof Error ? cause.message : "Appointment could not be scheduled."); } finally { setBusy(false); } };
  return <Dialog title="Schedule appointment" onClose={onClose}><div className="space-y-3"><label className="block text-sm">Start<input aria-label="Appointment start" type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2" /></label><label className="block text-sm">End<input aria-label="Appointment end" type="datetime-local" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2" /></label>{owner.type === "membership" && <label className="block text-sm">Included service event (optional)<select aria-label="Included service event" value={eventId} onChange={(event) => setEventId(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2"><option value="">No included-service event</option>{eligibleEvents.map((event) => <option key={event.id} value={event.id}>{event.status}{event.dueAt ? ` · due ${date(event.dueAt)}` : ""}</option>)}</select></label>}{error && <p role="alert" className="text-sm text-[var(--accent)]">{error}</p>}<div className="flex gap-2"><QuietButton type="button" onClick={onClose}>Cancel</QuietButton><PrimaryButton type="button" disabled={busy} onClick={() => void submit()}>{busy ? "Scheduling…" : "Schedule appointment"}</PrimaryButton></div></div></Dialog>;
}

function RemoteApprovalDialog({ estimateId, onClose, onDone, initialChannel = "email" }: { estimateId: string; onClose: () => void; onDone: () => void; initialChannel?: "email" | "sms" | "link" }) {
  const [items, setItems] = useState<Array<{ id: string; channel: "email" | "sms" | "link"; recipientEmail: string | null; recipientPhone: string | null; jobTypeId: string; sentAt: string; viewedAt: string | null; acceptedAt: string | null; revokedAt: string | null; expiresAt: string; deliveryError: string | null }>>([]), [jobTypes, setJobTypes] = useState<Array<{ id: string; name: string; active: boolean }>>([]);
  const [channel, setChannel] = useState<"email" | "sms" | "link">(initialChannel), [recipientEmail, setRecipientEmail] = useState(""), [recipientPhone, setRecipientPhone] = useState(""), [jobTypeId, setJobTypeId] = useState(""), [link, setLink] = useState(""), [notice, setNotice] = useState(""), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const load = () => void Promise.all([data<{ items: typeof items }>(`/api/v1/workspace/estimates/${estimateId}/remote-approval`), data<{ items: Array<{ id: string; name: string; active: boolean }> }>("/api/v1/workspace/dispatch/job-types")]).then(([history, types]) => { setItems(history.items); setJobTypes(types.items.filter((type) => type.active)); }).catch((cause) => setError(cause instanceof Error ? cause.message : "Unable to load approval delivery."));
  useEffect(load, [estimateId]);
  const copyLink = async (value: string) => {
    if (!navigator.clipboard) { setNotice("Approval link created. Copy it from the field below."); return; }
    try { await navigator.clipboard.writeText(value); setNotice("Secure approval link copied."); }
    catch { setNotice("Approval link created. Copy it from the field below."); }
  };
  const send = async (resend = false) => { if (!jobTypeId) { setError("Choose a future job type before creating the approval link."); return; } if (channel === "sms" && !recipientPhone.trim()) { setError("Enter a customer mobile number before sending."); return; } setBusy(true); setError(""); setNotice(""); try { const response = await apiFetch(`/api/v1/workspace/estimates/${estimateId}/remote-approval${resend ? "/resend" : ""}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ channel, jobTypeId, ...(channel === "email" && recipientEmail.trim() ? { recipientEmail: recipientEmail.trim() } : {}), ...(channel === "sms" ? { recipientPhone: recipientPhone.trim() } : {}) }) }); const body = await response.json().catch(() => null) as { data?: { item?: { link?: string; deliveryError?: string | null } }; error?: { message?: string } } | null; if (!response.ok) throw new Error(body?.error?.message || "Approval link could not be sent."); const nextLink = body?.data?.item?.link ?? ""; setLink(nextLink); if (channel === "link" && nextLink) await copyLink(nextLink); if (body?.data?.item?.deliveryError) setError("Approval link could not be delivered. Copy the link below to share it securely."); load(); onDone(); } catch (cause) { setError(cause instanceof Error ? cause.message : "Approval link could not be sent."); } finally { setBusy(false); } };
  const revoke = async (id: string) => { setBusy(true); setError(""); try { const response = await apiFetch(`/api/v1/workspace/estimates/${estimateId}/remote-approval/${id}/revoke`, { method: "POST" }); if (!response.ok) throw new Error("Approval link could not be revoked."); load(); } catch (cause) { setError(cause instanceof Error ? cause.message : "Approval link could not be revoked."); } finally { setBusy(false); } };
  return <Dialog title="Send estimate for approval" onClose={onClose}>
    <p className="text-sm text-[var(--ink-soft)]">The customer can review the estimate and sign online using a secure link. Their acceptance comes straight back to your team, ready for scheduling.</p>
    <label className="mt-4 block text-sm">Delivery method<select aria-label="Approval delivery method" value={channel} onChange={(event) => setChannel(event.target.value as "email" | "sms" | "link")} className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2"><option value="email">Email</option><option value="sms">Text message</option><option value="link">Copy a secure link</option></select></label>
    {channel === "email" ? <label className="mt-3 block text-sm">Customer email (defaults to the customer record)<input type="email" value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2" /></label> : channel === "sms" ? <label className="mt-3 block text-sm">Customer mobile number<input aria-label="Customer mobile number" type="tel" value={recipientPhone} onChange={(event) => setRecipientPhone(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2" /></label> : <p className="mt-3 text-sm text-[var(--ink-soft)]">Creates a unique, revocable link that expires in seven days. It will not send an email or text.</p>}
    <label className="mt-3 block text-sm">Future job type<select value={jobTypeId} onChange={(event) => setJobTypeId(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2"><option value="">Choose job type</option>{jobTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>
    {channel === "sms" && <p className="mt-2 text-xs text-[var(--ink-soft)]">Text delivery is available in Production. If it is unavailable here, copy the secure link instead.</p>}
    <div className="mt-4 flex gap-2"><QuietButton type="button" onClick={onClose}>Close</QuietButton><PrimaryButton type="button" disabled={busy} onClick={() => void send(items.length > 0)}>{busy ? "Creating…" : channel === "link" ? "Get approval link" : items.length ? "Resend approval link" : channel === "sms" ? "Text approval link" : "Email approval link"}</PrimaryButton></div>
    {notice && <p role="status" className="mt-3 text-sm text-[var(--good)]">{notice}</p>}
    {link && <div className="mt-4 rounded-lg border border-[var(--line)] p-3 text-sm"><p className="font-semibold">Secure approval link</p><input readOnly value={link} className="mt-2 w-full rounded border px-2 py-1" /><QuietButton type="button" onClick={() => void copyLink(link)}>Copy link</QuietButton></div>}
    {items.length > 0 && <div className="mt-5 border-t pt-4 text-sm"><p className="font-semibold">Delivery history</p>{items.slice(0, 5).map((item) => <div key={item.id} className="mt-2 flex flex-wrap items-center justify-between gap-2"><span>{item.channel === "sms" ? "Text" : item.channel === "link" ? "Copied link" : "Email"} · {item.recipientPhone ?? item.recipientEmail ?? "not delivered"} · {item.acceptedAt ? "approved" : item.revokedAt ? "revoked" : item.viewedAt ? "viewed" : "created"}{item.deliveryError ? " · delivery failed" : ""}</span>{!item.acceptedAt && !item.revokedAt && <QuietButton type="button" disabled={busy} onClick={() => void revoke(item.id)}>Revoke</QuietButton>}</div>)}</div>}
    {error && <p role="alert" className="mt-3 text-sm text-[var(--accent)]">{error}</p>}
  </Dialog>;
}

export function PendingEstimateWork({ openEstimate, onSchedule, children, soldCount = 0, revision = 0, onCountChange, showEmpty = false }: { openEstimate?: (id: string) => void; onSchedule?: (id: string) => void; children?: import("react").ReactNode; soldCount?: number; revision?: number; onCountChange?: (count: number) => void; showEmpty?: boolean }) {
  const [items, setItems] = useState<Array<{ approvalId: string; estimateId: string; optionId?: string; title?: string; displayNumber?: string; customerName: string; optionLabel?: string; serviceAddress?: string; serviceCity?: string }>>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!openEstimate && !onSchedule) return;
    let cancelled = false;
    setError("");
    void data<{ items: typeof items }>("/api/v1/workspace/estimate-fulfillment/pending")
      .then(async (result) => {
        if (cancelled) return;
        setItems(result.items);
        // Older preview APIs return approval IDs without the display details.
        // Read each missing estimate once, even when multiple options were sold.
        const ids = [...new Set(result.items.filter((item) => !item.displayNumber || !item.optionLabel || !item.title).map((item) => item.estimateId))];
        const documents = new Map(await Promise.all(ids.map(async (id) => {
          const payload = await data<{ document: EstimateDocument }>(`/api/v1/workspace/estimates/${encodeURIComponent(id)}/document`).catch(() => null);
          return [id, payload?.document] as const;
        })));
        if (cancelled) return;
        setItems(result.items.map((item) => {
          const document = documents.get(item.estimateId);
          if (!document) return item;
          return { ...item, title: document.title || item.title, displayNumber: document.displayNumber || item.displayNumber,
            optionLabel: document.options?.find((option) => option.id === item.optionId)?.label || item.optionLabel,
            serviceAddress: document.serviceAddressLine1 || item.serviceAddress,
            serviceCity: document.serviceCity || item.serviceCity };
        }));
      })
      .catch(() => { if (!cancelled) setError("Sold estimates could not be loaded."); });
    return () => { cancelled = true; };
  }, [Boolean(openEstimate || onSchedule), revision]);
  const estimates = [...new Map(items.map((item) => [item.estimateId, item])).values()].map((item) => ({ ...item,
    optionLabel: [...new Set(items.filter((option) => option.estimateId === item.estimateId).map((option) => option.optionLabel).filter(Boolean))].join(" · ") }));
  useEffect(() => { onCountChange?.(estimates.length); }, [estimates.length, onCountChange]);
  if (!items.length && !soldCount && !showEmpty) return error ? <p role="alert">{error}</p> : null;
  return <section aria-label="Sold / Unscheduled" className="dispatch-work-queue">
    <div className="dispatch-work-heading"><h2>Sold / Unscheduled</h2><span>{estimates.length + soldCount} to schedule</span></div>
    {error && <p role="alert">{error}</p>}
    {!items.length && !soldCount && <p className="job-muted">No sold work waiting to schedule.</p>}
    <div className="dispatch-work-cards" tabIndex={0} aria-label="Unscheduled estimates">
      {estimates.map((item) => <article key={item.estimateId} className="dispatch-work-card cursor-pointer" onClick={() => (openEstimate ?? onSchedule)?.(item.estimateId)}>
        <button type="button" className="dispatch-work-number">{item.displayNumber ? `Estimate #${item.displayNumber}` : "Open estimate"}</button>
        <h3 title={item.customerName}>{item.customerName}</h3>
        <p className="dispatch-work-description font-medium text-[var(--ink)]" title={item.title}>{item.title || "Estimate name not set"}</p>
        {item.optionLabel && <p className="text-xs text-[var(--ink-soft)]">{item.optionLabel}</p>}
        <p className="dispatch-work-type">Choose job type</p>
        <p className="dispatch-work-address" title={[item.serviceAddress, item.serviceCity].filter(Boolean).join(", ")}>{[item.serviceAddress, item.serviceCity].filter(Boolean).join(", ")}</p>
        {onSchedule && <button type="button" className="dispatch-work-schedule" onClick={(event) => { event.stopPropagation(); onSchedule(item.estimateId); }}>Book appointment</button>}
      </article>)}
      {children}
    </div>
  </section>;
}

type JobPreparationStep = "summary" | "deposit" | "purchase-orders";
export function EstimateFulfillmentDialog({ document, approvals, onClose, onDone, startBooking = false }: { document: EstimateDocument; approvals: EstimateApproval[]; onClose: () => void; onDone: (jobId?: string, step?: JobPreparationStep) => void; startBooking?: boolean }) {
  const [fulfillment, setFulfillment] = useState<"now" | "later">("later");
  const canAddToJob = Boolean(document.jobId) && approvals.length > 0 && approvals.every(approval => approval.method === "onsite");
  const [booking, setBooking] = useState(startBooking);
  const [approvalIds] = useState(() => approvals.map((approval) => approval.id));
  const [jobTypes, setJobTypes] = useState<Array<{ id: string; name: string; active: boolean }>>([]);
  const [jobTypeId, setJobTypeId] = useState("");
  const [busy, setBusy] = useState(false);
  const [createdJobId, setCreatedJobId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    void data<{ items: typeof jobTypes }>("/api/v1/workspace/dispatch/job-types")
      .then(({ items }) => setJobTypes(items.filter((type) => type.active)))
      .catch(() => setError("Unable to load job types."));
  }, []);
  const prepareJob = async () => {
    if (createdJobId) return createdJobId;
    if (!jobTypeId || !approvalIds.length) throw new Error("Choose a job type and sold work.");
    const response = await apiFetch("/api/v1/workspace/estimate-fulfillment", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ approvalIds, fulfillment: "later", jobTypeId, idempotencyKey }) });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error?.message || "The sold job could not be created.");
    const jobId = body?.data?.jobId as string | undefined;
    if (!jobId) throw new Error("The job could not be confirmed. Retry to recover the saved job.");
    setCreatedJobId(jobId);
    return jobId;
  };
  const openPreparedJob = async (step: JobPreparationStep) => {
    if (busy) return;
    setBusy(true); setError("");
    try { const id = await prepareJob(); onDone(id, step); onClose(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to open job."); }
    finally { setBusy(false); }
  };
  const close = () => { if (!busy) { if (createdJobId) onDone(); onClose(); } };
  return <Dialog title={booking ? "Book appointment" : "Approved job"} width="max-w-3xl" onClose={close}>
    <QuietButton type="button" disabled={busy} onClick={() => booking && !startBooking ? setBooking(false) : close()}>← Back</QuietButton>
    {!booking && <section className="my-4 rounded-xl border border-[var(--line)] p-4"><p className="font-semibold text-emerald-700">Estimate approved</p><h2 className="mt-2 text-xl font-semibold">{document.customerName}</h2></section>}
    <details className="rounded-xl border border-[var(--line)]">
      <summary className="cursor-pointer rounded-xl p-3 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
        {document.title || "Estimate"}{document.displayNumber ? ` · #${document.displayNumber}` : ""}
        <span className="mt-1 block text-xs font-normal text-[var(--ink-soft)]">View estimate details</span>
      </summary>
      <div className="space-y-4 border-t border-[var(--line)] p-3 text-sm">
        <p className="font-medium">{document.customerName}</p>
        {document.summary && <p className="whitespace-pre-wrap">{estimateDescription(document.summary)}</p>}
        {approvals.map((approval) => {
          const option = document.options?.find((item) => item.id === approval.optionId);
          return <section key={approval.id} className="space-y-2">
            <p className="flex justify-between gap-3 font-semibold"><span>{option?.label || "Sold work"}</span><span>{money(approval.approvedAmountCents)}</span></p>
            {option?.description && <p className="whitespace-pre-wrap">{estimateDescription(option.description)}</p>}
            {option?.lines?.map((line, index) => <div key={line.id || index} className="flex justify-between gap-3 border-t border-[var(--line)] py-2"><div><p className="whitespace-pre-wrap">{estimateDescription(line.description)}</p><p className="text-xs text-[var(--ink-soft)]">{line.quantity} × {money(line.unitPriceCents)}</p></div><span className="shrink-0">{money(Math.round(line.quantity * line.unitPriceCents))}</span></div>)}
            {option && <p className="flex justify-between"><span>Sales tax</span><span>{money(option.taxCents)}</span></p>}
          </section>;
        })}
        {document.terms && <p className="whitespace-pre-wrap text-[var(--ink-soft)]">{document.terms}</p>}
      </div>
    </details>
    <p className="mt-2 text-sm">{[document.serviceAddressLine1, document.serviceCity, document.serviceState].filter(Boolean).join(", ")}</p>
    <div className="mt-3 space-y-1 text-sm">{approvals.map((approval) => <p key={approval.id}><strong>{document.options?.find((option) => option.id === approval.optionId)?.label ?? "Sold work"}</strong> · {money(approval.approvedAmountCents)}</p>)}</div>
    {canAddToJob && !booking && <label className="mt-4 block text-sm">Sold work<select aria-label="Sold work" disabled={busy || Boolean(createdJobId)} value={fulfillment} onChange={event => setFulfillment(event.target.value as "now" | "later")} className="mt-1 w-full rounded border p-2"><option value="later">Book into a new job</option><option value="now">Add to this job</option></select></label>}
    <p className="mt-2 text-sm">{fulfillment === "later" ? "Future work gets a separate job and invoice. Its estimate and deposit stay linked to the original visit." : "Adds these sold items to the current job invoice for completion on this visit."}</p>
    {fulfillment === "later" && <label className="mt-4 block text-sm">Job type<select disabled={busy || Boolean(createdJobId)} aria-label="Job type" value={jobTypeId} onChange={(event) => setJobTypeId(event.target.value)} className="mt-1 w-full rounded border p-2"><option value="">Choose job type</option>{jobTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>}
    {error && <p role="alert">{error}</p>}
    {fulfillment === "now" ? <PrimaryButton type="button" disabled={busy} onClick={async () => {
      setBusy(true); setError("");
      try {
        const response = await apiFetch("/api/v1/workspace/estimate-fulfillment", {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({approvalIds,fulfillment:"now",jobId:document.jobId,idempotencyKey})});
        const body = await response.json();
        if (!response.ok) throw new Error(body.error?.message || "Unable to add sold work.");
        onDone(body.data.jobId); onClose();
      } catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to add sold work."); } finally { setBusy(false); }
    }}>Add to this job</PrimaryButton> : booking ? <InstallScheduler allowDoubleBooking jobTypeId={jobTypeId} prepareJob={prepareJob} onBusyChange={setBusy} onDone={() => { onDone(createdJobId ?? undefined); onClose(); }} /> : <>
      <div className="job-preparation-actions">
        {([{ step: "deposit", title: "Deposit" }, { step: "purchase-orders", title: "Equipment" }] as const).map(action => <section key={action.step} className="rounded-xl border border-[var(--line)] p-4"><h3 className="font-semibold">{action.title}</h3><PrimaryButton type="button" disabled={busy} onClick={() => void openPreparedJob(action.step)}>{action.step === "deposit" ? "Collect deposit" : "Create purchase order"}</PrimaryButton></section>)}
      </div>
      <div className="job-preparation-footer"><PrimaryButton type="button" disabled={busy} onClick={() => void openPreparedJob("summary")}>{busy ? "Opening job…" : "Open job"}</PrimaryButton><QuietButton type="button" disabled={busy} onClick={() => setBooking(true)}>Book appointment</QuietButton></div>
    </>}
    {createdJobId && <p className="mt-3 text-sm">Job saved.</p>}
  </Dialog>;
}

function JobEstimateAllocationAction({ estimateId, optionId, onClose, onDone, startBooking = false }: { estimateId: string; optionId?: string; onClose: () => void; onDone: (jobId?: string, step?: JobPreparationStep) => void; startBooking?: boolean }) {
  const [document, setDocument] = useState<EstimateDocument | null>(null);
  const [approvals, setApprovals] = useState<EstimateApproval[]>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      data<{ document: EstimateDocument }>(`/api/v1/workspace/estimates/${estimateId}/document`),
      data<{ items: EstimateApproval[] }>(`/api/v1/workspace/estimates/${estimateId}/approvals`),
    ])
      .then(([documentPayload, approvalsPayload]) => {
        if (cancelled) return;
        setDocument(documentPayload.document);
        setApprovals(approvalsPayload.items.filter((approval) => !approval.jobId && (!optionId || approval.optionId === optionId)));
      })
      .catch((cause) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "Unable to load approved work.");
      });
    return () => { cancelled = true; };
  }, [estimateId]);
  if (error) return <Dialog title="Approved job" onClose={onClose}><p role="alert" className="text-sm text-[var(--accent)]">{error}</p></Dialog>;
  if (!document) return <Dialog title="Approved job" onClose={onClose}><p className="text-sm text-[var(--ink-soft)]">Loading approved work…</p></Dialog>;
  if (!approvals.length) return <Dialog title="Approved job" onClose={onClose}><p className="text-sm text-[var(--ink-soft)]">All approved options are linked to a job.</p></Dialog>;
  return <EstimateFulfillmentDialog startBooking={startBooking} document={document} approvals={approvals} onClose={onClose} onDone={onDone} />;
}

function OfficeEstimateApproval({ estimateId, onClose, onDone, onSendLink }: { estimateId: string; onClose: () => void; onDone: () => void; onSendLink: () => void }) {
  const [method, setMethod] = useState<"phone" | "onsite" | "remote">("phone");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [drawn, setDrawn] = useState(false);
  const clearSignature = () => { const canvas = canvasRef.current; canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height); drawing.current = false; setDrawn(false); };
  const point = (event: PointerEvent<HTMLCanvasElement>) => { const canvas = canvasRef.current!; const bounds = canvas.getBoundingClientRect(); return { x: (event.clientX - bounds.left) * canvas.width / bounds.width, y: (event.clientY - bounds.top) * canvas.height / bounds.height }; };
  const [document, setDocument] = useState<EstimateDocument | null>(null);
  const [jobTypes, setJobTypes] = useState<Array<{ id: string; name: string; active: boolean }>>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [customerName, setCustomerName] = useState("");
  const [jobTypeId, setJobTypeId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const idempotencyKey = useRef(crypto.randomUUID());
  useEffect(() => {
    let active = true;
    void Promise.all([
      data<{ document: EstimateDocument }>(`/api/v1/workspace/estimates/${encodeURIComponent(estimateId)}/document`),
      data<{ items: Array<{ id: string; name: string; active: boolean }> }>("/api/v1/workspace/dispatch/job-types"),
    ]).then(([payload, types]) => {
      if (!active) return;
      const options = payload.document.options?.filter(option => !["accepted", "declined", "void"].includes(option.status ?? "")) ?? [];
      setDocument(payload.document); setCustomerName(payload.document.customerName || "");
      setSelected(options.length === 1 ? [options[0].id] : []);
      setJobTypes(types.items.filter(type => type.active));
    }).catch(() => { if (active) setError("Approval details could not load. Close and try again."); });
    return () => { active = false; };
  }, [estimateId]);
  const submit = async () => {
    if (method === "remote") { onSendLink(); return; }
    if (!document || !selected.length || !customerName.trim() || (method === "phone" && !jobTypeId)) { setError("Choose the approved options, customer name, and future job type."); return; }
    if (method === "onsite" && (!drawn || !canvasRef.current)) { setError("Capture the customer signature before approving."); return; }
    setBusy(true); setError("");
    try {
      const response = await apiFetch(`/api/v1/workspace/estimates/${encodeURIComponent(estimateId)}/approve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ revision: document.revision, optionIds: selected, method, customerName: customerName.trim(), ...(method === "phone" ? { jobTypeId } : { signatureDataUrl: opaqueSignatureJpeg(canvasRef.current!) }), idempotencyKey: idempotencyKey.current }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.data?.approvalIds?.length) throw new Error(payload.error?.message || "Approval could not be saved.");
      onDone();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Approval could not be saved."); } finally { setBusy(false); }
  };
  return <section aria-label="Customer approval" className="space-y-3 text-sm">
    <div className="flex items-center justify-between gap-3"><h3 className="font-semibold">Customer approval</h3><button type="button" aria-label="Close approval" disabled={busy} onClick={onClose} className="grid h-8 w-8 place-items-center rounded-md hover:bg-[var(--paper)]"><X size={18} aria-hidden="true" /></button></div>
    <label className="block font-semibold">Approval method<select disabled={busy} value={method} onChange={event => { clearSignature(); setError(""); setMethod(event.target.value as typeof method); }} className="mt-1 w-full rounded-md border border-[var(--line)] bg-[var(--paper)] px-3 py-2 font-normal"><option value="phone">Phone authorization</option><option value="onsite">Customer signature</option><option value="remote">Send customer approval link</option></select></label>
    <p className="text-[var(--ink-soft)]">{method === "phone" ? "Record the customer’s phone authorization. Confirming creates an unscheduled job and invoice for the selected work." : method === "onsite" ? "Have the customer review the selected work and sign below." : "Send the customer a secure link by email or text, or copy the link, so they can choose options and sign online."}</p>
    {!document && !error && <p role="status">Loading approval details…</p>}
    {document && method !== "remote" && <>
      <fieldset disabled={busy} className="space-y-2"><legend className="mb-2 font-semibold">Approved options</legend>{document.options?.filter(option => !["accepted", "declined", "void"].includes(option.status ?? "")).map(option => <label key={option.id} className="flex items-center gap-2"><input type={document.selectionMode === "single" ? "radio" : "checkbox"} name={`office-approval-${estimateId}`} checked={selected.includes(option.id)} onChange={() => { clearSignature(); setSelected(current => document.selectionMode === "single" ? [option.id] : current.includes(option.id) ? current.filter(id => id !== option.id) : [...current, option.id]); }} />{option.label} · {money(option.totalCents)}</label>)}</fieldset>
      <div className="grid gap-3 sm:grid-cols-2"><label className="font-semibold">Customer name<input disabled={busy} value={customerName} maxLength={160} onChange={event => { clearSignature(); setCustomerName(event.target.value); }} className="mt-1 w-full rounded-md border border-[var(--line)] bg-[var(--paper)] px-3 py-2 font-normal" /></label>{method === "phone" && <label className="font-semibold">Future job type<select disabled={busy} value={jobTypeId} onChange={event => setJobTypeId(event.target.value)} className="mt-1 w-full rounded-md border border-[var(--line)] bg-[var(--paper)] px-3 py-2 font-normal"><option value="">Choose job type</option>{jobTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>}</div>
      {method === "onsite" && <div><div className="flex items-center justify-between"><span className="font-semibold">Customer signature</span><QuietButton type="button" disabled={busy} onClick={clearSignature}>Clear signature</QuietButton></div><canvas ref={canvasRef} width={600} height={160} aria-label="Customer signature pad" className="mt-2 h-32 w-full rounded-md border border-dashed border-[var(--line)] bg-[var(--paper)]" style={{ touchAction: "none" }} onPointerDown={event => { const canvas = canvasRef.current; const context = canvas?.getContext("2d"); if (!canvas || !context || busy) return; drawing.current = true; canvas.setPointerCapture(event.pointerId); const start = point(event); context.beginPath(); context.moveTo(start.x, start.y); }} onPointerMove={event => { if (!drawing.current || busy) return; const context = canvasRef.current?.getContext("2d"); if (!context) return; const next = point(event); context.lineTo(next.x, next.y); context.strokeStyle = "#111"; context.lineWidth = 3; context.lineCap = "round"; context.stroke(); setDrawn(true); }} onPointerUp={() => { drawing.current = false; }} onPointerCancel={() => { drawing.current = false; }} /></div>}
    </>}
    <div className="flex justify-end"><PrimaryButton type="button" disabled={busy || !document} onClick={() => void submit()}>{busy ? "Saving approval…" : method === "remote" ? "Continue to approval link" : method === "phone" ? "Confirm phone approval" : "Approve estimate"}</PrimaryButton></div>
    {error && <p role="alert" className="text-[var(--accent)]">{error}</p>}
  </section>;
}

function EstimateCard({
  estimate,
  priceBookItems,
  priceBookCategories = [],
  canWrite,
  reload,
  openEstimate,
  openRelatedJob,
  openRelatedInvoice,
  editManagedEstimate,
  addApprovedWorkToInvoice,
  editingOptionId,
  stopEditing,
  job,
}: {
  estimate: JobDetail["estimates"][number];
  job: JobDetail["job"]; editingOptionId?: string; stopEditing: () => void;
  priceBookItems: PriceBookItem[];
  priceBookCategories?: PriceBookCategory[];
  canWrite: boolean;
  reload: () => void;
  openEstimate?: (estimateId: string) => void;
  openRelatedJob?: (jobId: string, focus?: string) => void;
  openRelatedInvoice?: (invoiceId: string, jobId: string) => void;
  editManagedEstimate?: (estimateId: string, optionId?: string) => void;
  addApprovedWorkToInvoice?: (estimateId: string, optionId?: string) => void;
}) {
  const [approving, setApproving] = useState(false);
  const [approvalLink, setApprovalLink] = useState(false);
  const [editing, setEditing] = useState<DocumentLineItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const editable = canWrite && !estimate.optionsManaged && ["draft", "sent"].includes(estimate.status);
  const [presentationDocument, setPresentationDocument] = useState<EstimateDocument | null>(null);
  const [presentationError, setPresentationError] = useState("");
  useEffect(() => {
    let active = true;
    setPresentationDocument(null);
    setPresentationError("");
    if (estimate.optionsManaged) void data<{ document: EstimateDocument }>(`/api/v1/workspace/estimates/${encodeURIComponent(estimate.id)}/document`)
      .then(({ document }) => { if (active) setPresentationDocument(document); })
      .catch(() => { if (active) setPresentationError("Additional estimate details could not load. The saved job details are shown."); });
    return () => { active = false; };
  }, [estimate.id, estimate.optionsManaged, estimate.options, estimate.lineItems]);
  const catalog = useMemo(() => new Map(priceBookItems.map(item => [item.id, item])), [priceBookItems]);
  const lines = estimate.lineItems ?? [];
  const options = estimate.options ?? [];
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [inspectedOptionId, setInspectedOptionId] = useState<string | null>(null);
  useEffect(() => {
    setSelectedOptionId((current) => options.some((option) => option.id === current) ? current : options[0]?.id ?? "");
  }, [estimate.id, estimate.options]);
  useEffect(() => {
    setInspectedOptionId((current) => current && options.some((option) => option.id === current) ? current : null);
  }, [estimate.id, estimate.options]);
  const activeOptionId = selectedOptionId || options[0]?.id || "";
  const activeOption = options.find((option) => option.id === activeOptionId);
  const approvedOptions = options.filter((option) => option.status === "accepted");
  const hasApprovedOptions = approvedOptions.length > 0;
  const showPresentation = Boolean(estimate.optionsManaged && (options.length || presentationDocument?.options?.length));
  const presentationOptions = presentationDocument?.options?.length ? presentationDocument.options : options.map(option => ({
    ...option,
    description: null,
    taxCents: option.taxCents ?? 0,
    lines: lines.filter(line => line.optionId === option.id),
  }));
  const approvedOptionIds = new Set(approvedOptions.map((option) => option.id));
  const approvedTotalCents = estimate.approvedTotalCents ?? approvedOptions.reduce((total, option) => total + option.totalCents, 0);
  const approvedTaxCents = approvedOptions.reduce((total, option) => total + (option.taxCents ?? 0), 0);
  const displayedTotalCents = hasApprovedOptions ? approvedTotalCents : activeOption?.totalCents ?? estimate.totalCents;
  const displayedTaxCents = hasApprovedOptions ? approvedTaxCents : activeOption?.taxCents ?? 0;
  const inspectedOption = options.find((option) => option.id === inspectedOptionId);
  const detailsTotalCents = inspectedOption?.totalCents ?? displayedTotalCents;
  const detailsTaxCents = inspectedOption?.taxCents ?? displayedTaxCents;
  const visibleLines = hasApprovedOptions
    ? inspectedOption
      ? lines.filter((line) => line.optionId === inspectedOption.id)
      : lines.filter((line) => line.optionId != null && approvedOptionIds.has(line.optionId))
    : activeOptionId ? lines.filter((line) => line.optionId === activeOptionId) : lines;
  const orderedLines = groupInvoiceLines(visibleLines, priceBookItems, priceBookCategories).flatMap(([, rows]) => rows);
  const printOptionId = hasApprovedOptions && approvedOptions.length === 1
    ? approvedOptions[0].id
    : activeOptionId || undefined;
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await mutate(
        `/api/v1/workspace/estimates/${estimate.id}/line-items/${editing.id}`,
        "PATCH",
        {
          description: String(form.get("description") || "").trim(),
          quantity: Number(form.get("quantity")),
          unitPriceCents: Math.round(Number(form.get("unitPrice")) * 100),
        },
      );
      setEditing(null);
      reload();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "We couldn’t update that line.",
      );
    } finally {
      setBusy(false);
    }
  };
  const remove = async (line: DocumentLineItem) => {
    if (
      !window.confirm(
        `Remove “${cleanDescription(line.description, "this line", 80)}” from this estimate?`,
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      const response = await apiFetch(
        `/api/v1/workspace/estimates/${estimate.id}/line-items/${line.id}`,
        { method: "DELETE" },
      );
      if (!response.ok) throw new Error("We couldn’t remove that line.");
      reload();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "We couldn’t remove that line.",
      );
    } finally {
      setBusy(false);
    }
  };
  const deleteEstimate = async () => {
    const deleteOption = Boolean(estimate.optionsManaged && activeOptionId && options.length > 1);
    if (!window.confirm(deleteOption ? `Delete option “${activeOption?.label ?? "this option"}”? This cannot be undone.` : `Delete ${customerDocumentLabel("Estimate", estimate)}? This only deletes the draft and cannot be undone.`)) return;
    setBusy(true); setError("");
    try {
      const response = await apiFetch(deleteOption ? `/api/v1/workspace/estimates/${estimate.id}/options/${activeOptionId}` : `/api/v1/workspace/estimates/${estimate.id}`, { method: "DELETE" });
      if (!response.ok) { const body = await response.json().catch(() => null); throw new Error(body?.error?.message || `We couldn’t delete that ${deleteOption ? "option" : "estimate"}.`); }
      reload();
    } catch (err) { setError(err instanceof Error ? err.message : `We couldn’t delete that ${deleteOption ? "option" : "estimate"}.`); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg bg-[var(--wash)] px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {editingOptionId === undefined && <div className="flex flex-wrap gap-2">
          {estimate.originatedFromSoldWork === 1 && openRelatedJob && <QuietButton type="button" disabled={busy} onClick={() => {
            setBusy(true); setError("");
            void data<{ document: EstimateDocument }>(`/api/v1/workspace/estimates/${encodeURIComponent(estimate.id)}/document`).then(({ document }) => {
              if (!document.jobId) throw new Error("This estimate has no original job linked to it.");
              openRelatedJob(document.jobId);
            }).catch(cause => setError(cause instanceof Error ? cause.message : "Unable to open the original estimate job.")).finally(() => setBusy(false));
          }}>Original visit, notes & timesheets</QuietButton>}
          {canWrite && estimate.optionsManaged && estimate.status === "draft" && editManagedEstimate ? (
            <QuietButton type="button" disabled={editingOptionId !== undefined} onClick={() => { setApproving(false); editManagedEstimate(estimate.id); }}>
              Edit
            </QuietButton>
          ) : openEstimate && (
            <QuietButton type="button" onClick={() => openEstimate(estimate.id)}>
              View
            </QuietButton>
          )}
          {canWrite && estimate.optionsManaged && (estimate.pendingApprovalCount ?? 0) > 0 && addApprovedWorkToInvoice && (
            <PrimaryButton type="button" onClick={() => addApprovedWorkToInvoice(estimate.id, activeOptionId)}>
              Approved job
            </PrimaryButton>
          )}
          {editingOptionId === undefined && canWrite && estimate.status === "draft" && <QuietButton type="button" disabled={busy} onClick={() => void deleteEstimate()}>{estimate.optionsManaged && options.length > 1 ? "Delete option" : "Delete"}</QuietButton>}
          <QuietButton
            type="button"
            disabled={busy || editingOptionId !== undefined}
            onClick={() => {
              setBusy(true);
              setError("");
              void openEstimatePdf(estimate, printOptionId)
                .catch((cause) => setError(cause instanceof Error ? cause.message : "We couldn’t open the printable estimate."))
                .finally(() => setBusy(false));
            }}
          >
            Print
          </QuietButton>
        </div>}
      </div>
      {editingOptionId !== undefined ? <OfficeEstimateComposer
        key={`${estimate.id}:${editingOptionId}`} presentation owner={{ customerId: job.customerId, propertyId: job.propertyId, jobId: job.id }} estimateId={estimate.id} focusOptionId={editingOptionId || undefined}
        priceBookItems={priceBookItems} priceBookCategories={priceBookCategories} onClose={stopEditing}
        onSaved={() => { stopEditing(); reload(); }}
      /> : <>
      {((estimate.title && !(showPresentation && presentationOptions.length === 1 && presentationOptions[0].label === estimate.title)) || estimate.summary) && <div className="mt-3 space-y-1 text-sm text-[var(--ink)]">{estimate.title && !(showPresentation && presentationOptions.length === 1 && presentationOptions[0].label === estimate.title) && <h3 className="font-semibold">{estimate.title}</h3>}{estimate.summary && <p className="whitespace-pre-wrap break-words">{estimateDescription(estimate.summary)}</p>}</div>}
      {showPresentation && <>
        {presentationError && <p role="status" className="mt-2 text-xs text-[var(--ink-soft)]">{presentationError}</p>}
        <EstimatePresentation office options={presentationOptions} selected={[...approvedOptionIds]} onSelect={() => undefined}
          catalog={priceBookItems.length ? { items: priceBookItems, categories: priceBookCategories } : undefined}
          onEditOption={canWrite && estimate.status === "draft" && editManagedEstimate && presentationOptions.length > 1 ? id => { setApproving(false); editManagedEstimate(estimate.id, id); } : undefined} />
        {canWrite && !["void", "cancelled", "canceled"].includes(estimate.status) && presentationOptions.some(option => !["accepted", "declined", "void"].includes(option.status ?? "")) && <div className="mt-4 border-t border-[var(--line)] pt-4">
          {approving ? <OfficeEstimateApproval estimateId={estimate.id} onClose={() => setApproving(false)} onDone={() => { setApproving(false); reload(); }} onSendLink={() => { setApproving(false); setApprovalLink(true); }} /> : <PrimaryButton type="button" onClick={() => setApproving(true)}>Approve</PrimaryButton>}
        </div>}
        {approvalLink && <RemoteApprovalDialog estimateId={estimate.id} onClose={() => setApprovalLink(false)} onDone={reload} />}
      </>}
      {(estimate.soldWork ?? []).length > 0 && (
        <section className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950" aria-label="Approved work created from this estimate">
          <p className="font-semibold">Approved work created</p>
          <p className="mt-1 text-xs text-emerald-900">Remote and phone approvals create separate unscheduled work, so the invoice belongs to that new job.</p>
          <div className="mt-3 space-y-2">
            {estimate.soldWork!.map((work) => (
              <div key={`${work.optionId}-${work.invoiceId}`} className="flex flex-wrap items-center justify-between gap-2 rounded border border-emerald-200 bg-white px-3 py-2">
                <span><b>{work.optionLabel}</b> · {work.fulfillment === "later" ? "Approved · Unscheduled" : "Approved · Completed now"}<small className="ml-2 text-emerald-800">Invoice {work.invoiceDisplayNumber} · {work.invoiceStatus.replaceAll("_", " ")} · {money(work.invoiceBalanceDueCents)} due</small></span>
                <span className="flex flex-wrap gap-2">
                  {openRelatedJob && <QuietButton type="button" onClick={() => openRelatedJob(work.jobId)}>Open job #{work.jobNumber}</QuietButton>}
                  {openRelatedInvoice && <QuietButton type="button" onClick={() => openRelatedInvoice(work.invoiceId, work.jobId)}>Open invoice #{work.invoiceDisplayNumber}</QuietButton>}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      {!showPresentation && (hasApprovedOptions ? <section className="mt-3" aria-label="Customer choices">
        <p className="text-sm font-semibold text-[var(--ink)]">Customer choices</p>
        <div className="mt-2 space-y-2">
          {approvedOptions.map((option) => <div key={option.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-950"><span><b>{option.label}</b><small className="ml-2 text-emerald-800">Approved and signed</small></span><b>{money(option.totalCents)}</b></div>)}
          {options.filter((option) => option.status !== "accepted").map((option) => <div key={option.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm text-[var(--ink-soft)]"><span><b className="text-[var(--ink)]">{option.label}</b><small className="ml-2">Not selected</small></span><span className="flex items-center gap-3"><span>{money(option.totalCents)}</span><QuietButton type="button" onClick={() => setInspectedOptionId((current) => current === option.id ? null : option.id)}>{inspectedOptionId === option.id ? "Hide details" : "View details"}</QuietButton></span></div>)}
        </div>
      </section> : options.length > 0 && <label className="mt-3 block text-sm font-semibold text-[var(--ink)]">Option<select aria-label="Estimate option" value={selectedOptionId} onChange={(event) => setSelectedOptionId(event.target.value)} className="ml-2 rounded border border-[var(--line)] bg-[var(--paper)] px-2 py-1 text-sm font-normal">{options.map((option) => <option key={option.id} value={option.id}>{option.label} · {money(option.totalCents)}</option>)}</select></label>)}
      {!showPresentation && visibleLines.length > 0 && (
        <div className="mt-3 divide-y divide-[var(--line)] border-t border-[var(--line)]">
          {hasApprovedOptions && <p className="py-2 text-xs font-semibold uppercase tracking-wide text-[var(--ink-soft)]">{inspectedOption ? `${inspectedOption.label} details · Not selected` : "Approved work"}</p>}
          {orderedLines.map((line) => {
            const catalogItem = line.priceBookItemId ? catalog.get(line.priceBookItemId) : undefined;
            const item = commercialLinePresentation(
              estimateDescription(line.catalogName || line.itemName || catalogItem?.name),
              estimateDescription(line.description),
              estimateDescription(line.catalogDescription || line.itemDescription || catalogItem?.description),
            );
            return <div key={line.id} role="group" aria-label={`Estimate item ${item.name || item.description || "Line item"}`} className="grid items-start gap-3 py-3 text-sm sm:grid-cols-[minmax(0,1fr)_auto]">
              <div className="min-w-0 space-y-1">
                <h4 className="whitespace-pre-wrap break-words font-semibold">{item.name || item.description || "Line item"}</h4>
                {item.name && item.description && <p className="whitespace-pre-wrap break-words leading-6 text-[var(--ink-soft)]">{item.description}</p>}
              </div>
              <div className="flex flex-wrap items-start gap-4">
                <dl className="grid flex-1 grid-cols-3 gap-4 sm:grid-cols-[60px_90px_100px]">
                  <div><dt className="text-xs text-[var(--ink-soft)]">Quantity</dt><dd className="mt-1 tabular-nums">{line.quantity}</dd></div>
                  <div className="text-right"><dt className="text-xs text-[var(--ink-soft)]">Price</dt><dd className="mt-1 whitespace-nowrap tabular-nums">{money(line.unitPriceCents)}</dd></div>
                  <div className="text-right"><dt className="text-xs text-[var(--ink-soft)]">Total</dt><dd className="mt-1 whitespace-nowrap font-semibold tabular-nums">{money(line.totalCents ?? Math.round(line.quantity * line.unitPriceCents))}</dd></div>
                </dl>
                {editable && <span className="flex gap-2">
                  <button type="button" onClick={() => setEditing(line)} className="text-xs font-semibold text-[var(--accent)]">Edit</button>
                  <button type="button" disabled={busy} onClick={() => void remove(line)} className="text-xs font-semibold text-[var(--accent)]">Delete</button>
                </span>}
              </div>
            </div>;
          })}
        </div>
      )}
      {!showPresentation && <DocumentTaxSummary
        kind="estimates"
        id={estimate.id}
        totalCents={detailsTotalCents}
        totalsOverride={hasApprovedOptions || activeOption ? {
          subtotalCents: detailsTotalCents - detailsTaxCents,
          taxCents: detailsTaxCents,
        } : undefined}
      />}
      {!showPresentation && <div className="mt-2 flex justify-between gap-3 border-t border-[var(--line)] pt-2 text-sm font-semibold"><span>{inspectedOption ? `${inspectedOption.label} total` : hasApprovedOptions ? "Approved total" : "Total"}</span><span className="tabular-nums">{money(detailsTotalCents)}</span></div>}
      {canWrite && !editable && (
        <p className="mt-3 text-xs text-[var(--ink-soft)]">
          Accepted and historical estimates are retained as immutable records.
        </p>
      )}
      {error && <p className="mt-2 text-xs text-[var(--accent)]">{error}</p>}
      {editing && (
        <section aria-label="Edit estimate line" className="mt-3 border-t border-[var(--line)] pt-3">
          <h3 className="mb-3 text-sm font-semibold">Edit estimate line</h3>
          <form onSubmit={(event) => void save(event)} className="space-y-3">
            <TextInput
              required
              label="Description"
              name="description"
              defaultValue={editing.description}
            />
            <TextInput
              required
              label="Quantity"
              name="quantity"
              type="number"
              min="0.01"
              step="0.01"
              defaultValue={editing.quantity}
            />
            <TextInput
              required
              label="Unit price"
              name="unitPrice"
              type="number"
              min="0"
              step="0.01"
              defaultValue={editing.unitPriceCents / 100}
            />
            <div className="flex gap-2">
              <PrimaryButton disabled={busy}>
                {busy ? "Saving…" : "Save line"}
              </PrimaryButton>
              <QuietButton type="button" onClick={() => setEditing(null)}>
                Cancel
              </QuietButton>
            </div>
          </form>
        </section>
      )}
      </>}
    </div>
  );
}

/**
 * The job type as it came off the booking, and a way to correct it. Clicking it
 * turns the chip into a list; picking from the list saves. There is no second
 * "save" to press, because there is nothing else on the chip to save.
 */
function JobTypeChip({
  current,
  options,
  canEdit,
  save,
}: {
  current: Array<{ id: string; name: string }>;
  options: Array<{ id: string; name: string }>;
  canEdit: boolean;
  save: (jobTypeId: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const label = current.map((item) => item.name).join(", ");
  if (editing)
    return (
      <select
        autoFocus
        disabled={saving}
        aria-label="Job type"
        defaultValue={current[0]?.id ?? ""}
        onBlur={() => setEditing(false)}
        onChange={(event) => {
          const next = event.target.value;
          if (!next) {
            setEditing(false);
            return;
          }
          setSaving(true);
          void save(next).finally(() => {
            setSaving(false);
            setEditing(false);
          });
        }}
        className="rounded-full border border-[var(--accent)] bg-[var(--paper)] px-3 py-1 text-xs font-semibold text-[var(--ink)]"
      >
        <option value="">{label || "Choose a job type"}</option>
        {options.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </select>
    );
  return (
    <button
      type="button"
      disabled={!canEdit}
      onClick={() => setEditing(true)}
      title={canEdit ? "Booked as this — click to correct it" : undefined}
      className="job-type-chip"
    >
      {label || "No job type"}
    </button>
  );
}

/**
 * Who is going, and the roster if nobody is. Reads as a line of text until it
 * is clicked, because most of the time the answer is just a name.
 */
function AssignTechnician({
  appointmentId,
  assigned,
  assignedTechnicianIds = [],
  canWrite,
  onSaved,
  hideNames = false,
}: {
  hideNames?: boolean;
  appointmentId: string;
  assigned: string | null;
  assignedTechnicianIds?: string[];
  canWrite: boolean;
  onSaved: () => void;
}) {
  const { save: saveScheduling, conflictDialog } = useOfficeSchedulingSave();
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(false);
  const [roster, setRoster] = useState<Technician[]>([]);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState("");
  useEffect(() => {
    if (!editing || roster.length) return;
    void data<{ items: Technician[] }>("/api/v1/workspace/dispatch/technicians")
      .then((result) =>
        setRoster(
          result.items.filter((technician) => technician.active !== false),
        ),
      )
      .catch(() => setFailed("We couldn’t load the roster."));
  }, [editing, roster.length]);
  const assign = async (technicianId: string) => {
    if (!technicianId) return;
    setSaving(true);
    setFailed("");
    try {
      const result = await saveScheduling(
        `/api/v1/workspace/dispatch/appointments/${appointmentId}/assignments`,
        "PUT",
        { technicianIds: [...new Set([...assignedTechnicianIds, technicianId])] },
      );
      setNotice(result.warning || "");
      setEditing(false);
      onSaved();
    } catch (error) {
      setFailed(
        error instanceof Error
          ? error.message
          : "That technician could not be assigned.",
      );
    } finally {
      setSaving(false);
    }
  };
  if (!editing)
    return (
      <div className="job-assigned-technicians">
        {notice && <p role="status" className="text-xs text-amber-800">{notice}</p>}
        {!hideNames && <div className="job-technician-list" aria-label="Assigned technicians">
          {assigned ? assigned.split(",").map((name) => name.trim()).filter(Boolean).map((name, index) => (
            <span className="job-assigned-technician" key={`${name}-${index}`}>{name}</span>
          )) : <span className="font-semibold text-[var(--st-conflict)]">Nobody assigned</span>}
        </div>}
        {canWrite && (
          <button type="button" onClick={() => setEditing(true)} className="text-xs font-semibold text-[var(--accent)]">
            {assigned ? "Add technician" : "Assign somebody"}
          </button>
        )}
      </div>
    );
  return (
    <><div className="mt-1.5">
      <select
        autoFocus
        disabled={saving}
        aria-label="Assign a technician"
        defaultValue=""
        onChange={(event) => void assign(event.target.value)}
        className="w-full max-w-xs rounded-lg border border-[var(--accent)] bg-[var(--paper)] px-2.5 py-1.5 text-sm"
      >
        <option value="">
          {roster.length ? "Choose a technician" : "Loading the roster…"}
        </option>
        {roster.map((technician) => (
          <option key={technician.id} value={technician.id}>
            {technician.name} — {tradeLabel(technician.skills)}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={saving}
        onClick={() => setEditing(false)}
        className="ml-2 text-xs font-semibold text-[var(--ink-soft)]"
      >
        Cancel
      </button>
      {failed && (
        <p className="mt-1 text-xs text-[var(--st-conflict)]">{failed}</p>
      )}
    </div>{conflictDialog}</>
  );
}

export function JobWorkspace({
  detail,
  reloadJob = () => {},
  priceBookItems,
  priceBookCategories = [],
  timezone,
  openHistoricalTextThread,
  back,
  backLabel,
  openCustomer,
  inventoryPermissions = { read: false, write: false, approve: false, adjust: false },
  openInvoice,
  openRelatedJob,
  openRelatedInvoice,
  openEstimate,
  canSchedule = false,
  canCreateCommercial,
  canEditCommercial = false,
  focusDocumentId = "",
  collectPaymentInvoiceId = "",
  customerContacts = [],
  canEditContacts = false,
  saveContact,
  deleteContact,
  saveJobNotes,
  saveAfterHoursPricing,
  assignJobType,
  saveJobRequiredSkills,
  resetJobRequiredSkills,
  createCommercial,
  acceptEstimate,
  recordPayment,
}: {
  detail: JobDetail;
  reloadJob?: () => void | Promise<void>;
  priceBookItems: PriceBookItem[];
  priceBookCategories?: PriceBookCategory[];
  timezone: string;
  openHistoricalTextThread?: (threadId: string) => void;
  back: () => void;
  backLabel: string;
  openCustomer: (id: string) => void;
  inventoryPermissions?: JobInventoryPermissions;
  openInvoice?: (invoiceId: string) => void;
  openRelatedJob?: (jobId: string, focus?: string) => void;
  openRelatedInvoice?: (invoiceId: string, jobId: string) => void;
  openEstimate?: (estimateId: string) => void;
  canSchedule?: boolean;
  canCreateCommercial: boolean;
  canEditCommercial?: boolean;
  focusDocumentId?: string;
  collectPaymentInvoiceId?: string;
  customerContacts?: Array<{
    channel: string;
    value: string;
    isPrimary: number;
  }>;
  canEditContacts?: boolean;
  saveContact?: (
    jobId: string,
    owner: "customer" | "property",
    ownerId: string,
    contact: Omit<WorkspaceContact, "id">,
    contactId?: string,
  ) => Promise<void>;
  deleteContact?: (
    jobId: string,
    owner: "customer" | "property",
    ownerId: string,
    contactId: string,
  ) => Promise<void>;
  saveJobNotes?: (jobId: string, description: string) => Promise<void>;
  saveAfterHoursPricing: (
    jobId: string,
    body: {
      mode: "flat_fee" | "labor_multiplier" | null;
      laborMultiplier: number | null;
    },
  ) => Promise<void>;
  assignJobType: (jobId: string, jobTypeId: string) => Promise<void>;
  saveJobRequiredSkills?: (jobId: string, skillIds: string[]) => Promise<void>;
  resetJobRequiredSkills?: (jobId: string) => Promise<void>;
  createCommercial: (
    jobId: string,
    kind: "estimate" | "invoice",
    title: string,
    lineItems: Array<{ priceBookItemId?: string; description?: string; quantity: number; unitPriceCents?: number; taxable?: boolean; stripeTaxCode?: string | null }>,
    appointmentId: string | undefined,
    jobTypeId: string,
  ) => Promise<void>;
  acceptEstimate: (
    jobId: string,
    estimateId: string,
    signature: { fulfillment: "now" | "later"; jobTypeId?: string; signerName?: string; signatureDataUrl?: string },
  ) => Promise<{ invoiceId: string }>;
  recordPayment: (
    invoiceId: string,
    body: { amountCents: number; method: string; reference: string | null },
  ) => Promise<void>;
}) {
  const { textsOpen: companionTextsOpen = false } = useRecordCommunications();
  const estimateWorkFacts = useEstimateWorkFacts(detail.estimates, true);
  const [jobMessagesOpen, setJobMessagesOpen] = useState(false);
  const { job } = detail;
  const visibleAppointments = detail.appointments.filter((appointment) =>
    !["cancelled", "canceled", "no_show"].includes(appointment.status),
  );
  const visitTime = groupTimeByVisit(visibleAppointments, detail.timesheets ?? []);
  const emailAddress =
    job.customerEmail ||
    [
      ...(detail.propertyContacts ?? []),
      ...(detail.billingContacts ?? []),
    ].find((contact) => contact.email)?.email ||
    customerContacts.find((method) => method.channel !== "phone")?.value ||
    "";
  const billingContacts = detail.billingContacts?.length ? detail.billingContacts : customerContacts.map((method, index) => ({
    id: `fallback-${index}`, name: job.customerName,
    phone: method.channel === "phone" ? method.value : null,
    email: method.channel === "phone" ? null : method.value,
    phoneType: null, memo: null, doNotAlert: false, isPrimary: Boolean(method.isPrimary),
  }));
  const accountEmailOnFile = [...billingContacts, ...(detail.propertyContacts ?? [])].some((contact) => contact.email?.trim().toLowerCase() === job.customerEmail?.trim().toLowerCase());
  const displayedBillingContacts = job.customerEmail && !accountEmailOnFile ? [...billingContacts, {
    id: "fallback-account-email", name: job.customerName, phone: null, email: job.customerEmail,
    phoneType: null, memo: null, doNotAlert: false, isPrimary: false,
  }] : billingContacts;
  const [addingAppointment, setAddingAppointment] = useState(false);
  const [reschedulingAppointment, setReschedulingAppointment] = useState<JobDetail["appointments"][number] | null>(null);
  const [bookingAppointment, setBookingAppointment] = useState(false);
  const [bookingAppointmentBusy, setBookingAppointmentBusy] = useState(false);
  const [bookingJobTypeId, setBookingJobTypeId] = useState("");
  const [appointmentNotice, setAppointmentNotice] = useState("");
  const [invoicePdf, setInvoicePdf] = useState<{ id: string; label: string } | null>(null);
  useEffect(() => { setInvoicePdf(null); }, [job.id]);
  const [creating, setCreating] = useState<"estimate" | "invoice" | null>(null);
  const [optionComposer, setOptionComposer] = useState<{ estimateId?: string; optionId?: string } | null>(null);
  const [addingApprovedWorkFromEstimate, setAddingApprovedWorkFromEstimate] = useState<{ estimateId: string; optionId?: string } | null>(null);
  const [documentItems, setDocumentItems] = useState<
    Array<{ priceBookItemId?: string; description?: string; quantity: number; unitPriceCents?: number; taxable?: boolean; stripeTaxCode?: string | null }>
  >([]);
  const [invoiceSummary, setInvoiceSummary] = useState("");
  const [invoiceDraftRows, setInvoiceDraftRows] = useState<Array<{ id: number; kind: InvoiceItemKind }>>([]);
  const invoiceDraftSequence = useRef(0);
	const [pricingAppointmentId, setPricingAppointmentId] = useState("");
	const [pricingJobTypeId, setPricingJobTypeId] = useState("");
	const [pricingPreview, setPricingPreview] = useState<{ subtotalCents: number; lineItems: Array<{ priceBookItemId: string; unitPriceCents: number; totalCents: number }> } | null>(null);
	const [commercialTaxCents, setCommercialTaxCents] = useState(0);
	const [commercialTaxPending, setCommercialTaxPending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableJobTypes, setAvailableJobTypes] = useState<
    Array<{ id: string; name: string; active: boolean }>
  >([]);
  const [requiredSkillIds, setRequiredSkillIds] = useState<string[]>(() =>
    (detail.requiredSkills || []).map((skill) => skill.id),
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<
    JobDetail["estimates"][number] | null
  >(null);
  // ServiceTitan descriptions are rich HTML. Keep the source value intact in
  // storage, but never make office staff clean up markup in a text editor.
  const [noteDraft, setNoteDraft] = useState(() => plainTextDescription(job.description));
  const [savedNotes, setSavedNotes] = useState(() => plainTextDescription(job.description));
  const [notesError, setNotesError] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
	const [jobPanel, setJobPanel] = useState<"summary" | "appointments" | "estimates" | "invoices" | "photos" | "purchase-orders" | null>(focusDocumentId === "job-step:purchase-orders" ? "purchase-orders" : focusDocumentId === "job-step:summary" ? "summary" : focusDocumentId ? detail.estimates.some(estimate => estimate.id === focusDocumentId) ? "estimates" : "invoices" : null);
	useEffect(() => setJobPanel(focusDocumentId === "job-step:purchase-orders" ? "purchase-orders" : focusDocumentId === "job-step:summary" ? "summary" : focusDocumentId ? detail.estimates.some(estimate => estimate.id === focusDocumentId) ? "estimates" : "invoices" : null), [job.id, focusDocumentId]);
  const hasJobFlyout = jobPanel !== null && jobPanel !== "appointments" && jobPanel !== "summary";
  useEffect(() => {
    if (jobPanel === "appointments" || jobPanel === "summary") {
      document.getElementById(`job-${job.id}-${jobPanel}-tile`)?.scrollIntoView?.({ block: "start" });
    }
  }, [job.id, jobPanel]);
  const detailsPaneRef = useRef<HTMLElement>(null);
  useEffect(() => { if (detailsPaneRef.current) detailsPaneRef.current.scrollTop = 0; }, [jobPanel, invoicePdf]);
  const closeJobDetails = () => {
    document.getElementById(`job-${job.id}-${jobPanel}-tile`)?.focus();
    setJobPanel(null);
  };



  const act = async (id: string, action: () => Promise<void>) => {
    setBusy(id);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };
  const selectableAppointments = detail.appointments.filter(
    (appointment) => appointment.status !== "cancelled",
  );
  const selectableJobTypes = detail.jobTypes ?? [];
  useEffect(() => {
    let cancelled = false;
    void data<{ items: Array<{ id: string; name: string; active: boolean }> }>(
      "/api/v1/workspace/dispatch/job-types",
    )
      .then((result) => {
        if (!cancelled)
          setAvailableJobTypes(result.items.filter((item) => item.active));
      })
      .catch(() => {
        if (!cancelled) setAvailableJobTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canCreateCommercial]);
  const submitCommercial = async () => {
    if (!creating) return;
		const appointmentId = pricingAppointmentId || undefined;
		const jobTypeId = pricingJobTypeId;
    if (!jobTypeId) return;
    setSaving(true);
    try {
      await createCommercial(
        job.id,
        creating,
        invoiceSummary.trim() || "Service invoice",
        documentItems,
        appointmentId,
        jobTypeId,
      );
      setCreating(null);
      setDocumentItems([]);
    } finally {
      setSaving(false);
    }
  };
	useEffect(() => {
		const pricedItems = documentItems.filter((line): line is { priceBookItemId: string; quantity: number } => Boolean(line.priceBookItemId) && line.unitPriceCents === undefined);
		if (!creating || !documentItems.length || !pricingJobTypeId) { setPricingPreview(null); return; }
		if (!pricedItems.length) { setPricingPreview({ subtotalCents: documentItems.reduce((sum, line) => sum + Math.round(line.quantity * (line.unitPriceCents ?? 0)), 0), lineItems: [] }); return; }
		let cancelled = false;
		void apiFetch(`/api/v1/workspace/jobs/${job.id}/commercial/pricing-preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...(pricingAppointmentId ? { appointmentId: pricingAppointmentId } : {}), jobTypeId: pricingJobTypeId, lineItems: pricedItems }) }).then(async (response) => { const payload = await response.json() as { data?: { subtotalCents: number; lineItems: Array<{ priceBookItemId: string; unitPriceCents: number; totalCents: number }> } }; if (!response.ok || !payload.data) throw new Error("Unable to preview prices"); if (!cancelled) setPricingPreview({ ...payload.data, subtotalCents: payload.data.subtotalCents + documentItems.filter((line) => !line.priceBookItemId || line.unitPriceCents !== undefined).reduce((sum, line) => sum + Math.round(line.quantity * (line.unitPriceCents ?? 0)), 0) }); }).catch(() => { if (!cancelled) setPricingPreview(null); });
		return () => { cancelled = true; };
	}, [creating, documentItems, pricingAppointmentId, pricingJobTypeId, job.id]);
	useEffect(() => {
		if (!creating || !pricingPreview || !documentItems.length) { setCommercialTaxCents(0); setCommercialTaxPending(false); return; }
		let cancelled = false;
		setCommercialTaxPending(true);
		const lines = documentItems.map((line) => ({
			...(line.priceBookItemId ? { priceBookItemId: line.priceBookItemId } : {}),
			description: line.description || "Pricebook item",
			quantity: line.quantity,
			unitPriceCents: line.unitPriceCents ?? (line.priceBookItemId ? pricingPreview.lineItems.find((priced) => priced.priceBookItemId === line.priceBookItemId)?.unitPriceCents ?? 0 : 0),
			taxable: line.taxable,
			stripeTaxCode: line.stripeTaxCode,
		}));
		void apiFetch("/api/v1/workspace/estimates/tax-preview", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ propertyId: job.propertyId, options: [{ lines }] }) })
			.then(async (response) => { const payload = await response.json(); if (!response.ok) throw new Error(); return payload.data as { taxCents: number[] }; })
			.then((preview) => { if (!cancelled) setCommercialTaxCents(preview.taxCents[0] ?? 0); })
			.catch(() => { if (!cancelled) setCommercialTaxCents(0); })
			.finally(() => { if (!cancelled) setCommercialTaxPending(false); });
		return () => { cancelled = true; };
	}, [creating, documentItems, job.propertyId, pricingPreview]);
  const saveRequiredSkills = () => {
    if (saveJobRequiredSkills)
      void saveJobRequiredSkills(job.id, requiredSkillIds);
  };
	const selectedTotal = pricingPreview?.subtotalCents ?? documentItems.reduce(
    (total, lineItem) =>
      total +
      Math.round(
        (lineItem.priceBookItemId
          ? priceBookItems.find((item) => item.id === lineItem.priceBookItemId)?.effectivePriceCents
            ?? priceBookItems.find((item) => item.id === lineItem.priceBookItemId)?.priceCents
            ?? 0
          : lineItem.unitPriceCents ?? 0) * lineItem.quantity,
      ),
    0,
	  );
	const selectedDocumentTotal = selectedTotal + commercialTaxCents;
	const invoicePriceBookItems = priceBookItems.filter((item) => item.active !== false);
  const orderedJobInvoices = orderJobInvoices(detail.invoices);
  const priority = (job.priority || "").toLowerCase();
  const operationalJobStatus = job.status === "new" && detail.invoices.some((invoice) => invoice.fulfillment === "later")
    ? "Sold / Unscheduled"
    : job.status === "new" ? "Unscheduled" : statusChip(job.status).label;
  const orderedAppointments = [...visibleAppointments].sort((a, b) => Date.parse(a.scheduledStart) - Date.parse(b.scheduledStart));
  const primaryAppointment = orderedAppointments.find((visit) => ["dispatched", "en_route", "arrived", "in_progress"].includes(visit.status))
    ?? orderedAppointments.find((visit) => !["completed", "cancelled", "canceled"].includes(visit.status))
    ?? (job.waitingOn || job.status === "new" ? undefined : orderedAppointments.at(-1));
  const additionalAppointments = detail.appointments.filter((visit) => visit.id !== primaryAppointment?.id).sort((a, b) => Date.parse(a.scheduledStart) - Date.parse(b.scheduledStart));
  const todayLabel = formatDispatchDate(new Date().toISOString(), timezone, { year: "numeric" });
  const todayUnmatchedTime = visitTime.unmatched.filter((entry) => formatDispatchDate(entry.clockIn, timezone, { year: "numeric" }) === todayLabel);
  const historicalUnmatchedTime = visitTime.unmatched.filter((entry) => formatDispatchDate(entry.clockIn, timezone, { year: "numeric" }) !== todayLabel);
  const jobClosed = ["completed", "cancelled", "canceled", "no_show"].includes(job.status);
  const schedulingBlockReason = ["cancelled", "canceled", "no_show"].includes(job.status)
    ? "This job is closed. Additional appointments cannot be added."
    : job.waitingOn ? "Mark the hold resolved above before adding an appointment." : "";
  const addAppointmentButton = canSchedule && !addingAppointment && <QuietButton type="button" disabled={Boolean(schedulingBlockReason)} aria-describedby={schedulingBlockReason ? `job-${job.id}-scheduling-block` : undefined} onClick={() => { setAppointmentNotice(""); setReschedulingAppointment(null); setAddingAppointment(true); }}>+ Add appointment</QuietButton>;
  const renderVisit = (appointment: JobDetail["appointments"][number], showSchedule = true) => (
                <div
                  key={appointment.id}
                  className="job-visit-row"
                >
                  <div>
                    {showSchedule && <p className="text-sm font-semibold text-[var(--ink)]">
                      {formatDispatchDate(
                        appointment.scheduledStart,
                        timezone,
                        { weekday: "short", year: "numeric" },
                      )}{" "}
                      ·{" "}
                      {formatDispatchTime(appointment.scheduledStart, timezone)}
                      –{formatDispatchTime(appointment.scheduledEnd, timezone)}
                    </p>}
                    {!appointment.technicians?.length && !appointment.technicianNames?.trim() && <p className="job-muted">Crew: Unassigned</p>}
                    {Boolean(appointment.technicians?.length || appointment.technicianNames) && <JobTechnicianActions timeEntries={visitTime.byVisit.get(appointment.id) ?? []} appointmentId={appointment.id} technicians={appointment.technicians?.length ? appointment.technicians.map((technician) => appointment.technicians?.length === 1 ? { ...technician, dispatchedAt: technician.dispatchedAt ?? appointment.dispatchedAt ?? appointment.enRouteAt, arrivedAt: technician.arrivedAt ?? appointment.arrivedAt, completedAt: technician.completedAt ?? appointment.departedAt } : technician) : (appointment.technicianNames ?? "").split(",").map((name) => ({ id: "", name: name.trim(), status: appointment.status, dispatchedAt: appointment.dispatchedAt ?? appointment.enRouteAt, arrivedAt: appointment.arrivedAt, completedAt: appointment.departedAt })).filter((technician) => technician.name)} canWrite={canSchedule} canCorrectTimes={!["cancelled", "canceled", "no_show"].includes(job.status) && !["cancelled", "canceled", "no_show"].includes(appointment.status)} closed={["completed", "cancelled", "canceled", "no_show"].includes(job.status) || ["completed", "cancelled", "canceled", "no_show"].includes(appointment.status)} timezone={timezone} onSaved={reloadJob} />}
                    {!appointment.technicians?.length && !appointment.technicianNames && <JobTimesheets canEdit={canSchedule} closed={["completed", "cancelled", "canceled"].includes(job.status)} onSaved={reloadJob} entries={visitTime.byVisit.get(appointment.id) ?? []} timezone={timezone} assignedNames={appointment.technicianNames} hideHeading />}
                    <div className="job-visit-assignment-actions">
                      {canSchedule && !["cancelled", "canceled", "no_show"].includes(job.status) && !["completed", "cancelled", "canceled", "no_show"].includes(appointment.status) && <QuietButton type="button" onClick={() => { setAddingAppointment(false); setAppointmentNotice(""); setReschedulingAppointment(appointment); }}>Reschedule</QuietButton>}
                    {(appointment.technicianNames || !["completed", "cancelled", "canceled"].includes(job.status) && !["completed", "cancelled", "canceled"].includes(appointment.status)) && <AssignTechnician
                      appointmentId={appointment.id}
                      assigned={appointment.technicianNames}
                      assignedTechnicianIds={appointment.technicians?.map((technician) => technician.id).filter(Boolean) ?? []}
                      hideNames
                      canWrite={canSchedule && !["cancelled", "canceled", "no_show"].includes(job.status) && !["completed", "cancelled", "canceled", "no_show"].includes(appointment.status)}
                      onSaved={reloadJob}
                    />}
                      {canSchedule && !["cancelled", "canceled", "no_show"].includes(job.status) && ["pending", "scheduled", "confirmed"].includes(appointment.status) && !appointment.dispatchedAt && !appointment.enRouteAt && !appointment.arrivedAt && !appointment.departedAt && !visitTime.byVisit.get(appointment.id)?.length && (appointment.technicians ?? []).every(technician => ["pending", "scheduled", "confirmed"].includes(technician.status) && !technician.dispatchedAt && !technician.arrivedAt && !technician.completedAt) && <JobCancellation appointmentId={appointment.id} appointmentLabel={`${formatDispatchDate(appointment.scheduledStart, timezone, { weekday: "short", year: "numeric" })} · ${formatDispatchTime(appointment.scheduledStart, timezone)}–${formatDispatchTime(appointment.scheduledEnd, timezone)}`} onSaved={reloadJob} />}
                      <AppointmentUnassign appointmentId={appointment.id} technicians={(appointment.technicians ?? []).map(technician => appointment.technicians?.length === 1 ? { ...technician, dispatchedAt: technician.dispatchedAt ?? appointment.dispatchedAt ?? appointment.enRouteAt } : technician)} canWrite={canSchedule} closed={["cancelled", "canceled", "no_show"].includes(job.status) || ["completed", "cancelled", "canceled", "no_show"].includes(appointment.status)} onSaved={reloadJob} />
                    </div>
                  </div>
                  {showSchedule && <span
                    className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${statusChip(appointment.status).className}`}
                  >
                    {statusChip(appointment.status).label}
                  </span>}
                </div>
  );
  const headerStatus = jobClosed ? operationalJobStatus : primaryAppointment && !["cancelled", "canceled", "no_show"].includes(primaryAppointment.status)
    ? ({ pending: "Scheduled", scheduled: "Scheduled", confirmed: "Confirmed", dispatched: "Dispatched", en_route: "Dispatched", arrived: "Arrived", in_progress: "Arrived", completed: "Completed" } as Record<string, string>)[primaryAppointment.status] ?? operationalJobStatus
    : operationalJobStatus;
  const jobNotes = <section className="job-notes-section" aria-label="Job notes"><h2>Dispatch notes</h2>
    {saveJobNotes ? <form onSubmit={(event) => {
      event.preventDefault();
      if (notesSaving || noteDraft === savedNotes) return;
      setNotesSaving(true); setNotesError("");
      void saveJobNotes(job.id, noteDraft).then(() => setSavedNotes(noteDraft)).catch(() => setNotesError("Notes could not be saved. Your changes are still here; try again.")).finally(() => setNotesSaving(false));
    }}>
      <DispatchNotesInput aria-label="Job notes" placeholder="Type dispatch notes…" value={noteDraft} maxLength={5000} disabled={notesSaving} onChange={(event) => setNoteDraft(event.target.value)} className="job-dispatch-notes-input" rows={1} />
      {noteDraft !== savedNotes && <div className="mt-2 flex flex-wrap gap-2"><PrimaryButton type="submit" className="w-auto" disabled={notesSaving}>{notesSaving ? "Saving…" : "Save notes"}</PrimaryButton><QuietButton type="button" disabled={notesSaving} onClick={() => { setNoteDraft(savedNotes); setNotesError(""); }}>Discard changes</QuietButton></div>}
      {notesError && <p role="alert" className="job-error">{notesError}</p>}
    </form> : <div>{job.description ? <p className="job-widget-notes">{cleanDescription(job.description, "", 5000)}</p> : <p className="text-sm text-[var(--ink-faint)]">No job notes on file.</p>}</div>}
  </section>;
  const jobHeader = (
    <div className="job-glance-banner" aria-label="Job summary">
          <div className="job-glance-overview">
            <div className="job-glance-identity">
              <button type="button" className="job-glance-customer text-left hover:underline focus-visible:outline-2 focus-visible:outline-offset-2" aria-label={`Open customer ${job.customerName}`} onClick={() => openCustomer(job.customerId)}>{job.customerName}</button>
              <div className="job-glance-reference">
                <h1>Job #{job.jobNumber}</h1><span aria-hidden="true">·</span>
                <JobTypeChip current={selectableJobTypes} options={availableJobTypes} canEdit={canCreateCommercial} save={(id) => assignJobType(job.id, id)} />
              </div>
              <p className="job-technician-schedule" aria-label="Appointment time">
                {primaryAppointment ? <>
                  <span className="font-semibold">Scheduled</span>{" · "}
                  {formatDispatchDate(primaryAppointment.scheduledStart, timezone, { weekday: "short", year: "numeric" })}
                  <span aria-hidden="true"> · </span>
                  {formatDispatchTime(primaryAppointment.scheduledStart, timezone)}–{formatDispatchTime(primaryAppointment.scheduledEnd, timezone)}
                  {formatDispatchDate(primaryAppointment.scheduledStart, timezone) !== formatDispatchDate(primaryAppointment.scheduledEnd, timezone) && <> · ends {formatDispatchDate(primaryAppointment.scheduledEnd, timezone)}</>}
                </> : job.waitingOn ? "Next appointment unscheduled" : ["completed", "cancelled", "canceled"].includes(job.status) ? "No appointment recorded" : "Not scheduled"}
              </p>
              <p className="job-header-technicians">Technician: {primaryAppointment?.technicians?.length ? primaryAppointment.technicians.map(technician => technician.name).join(", ") : primaryAppointment?.technicianNames || "Unassigned"}</p>
            </div>
            <div className="job-glance-waiting">
          <JobWaitingStatus jobId={job.id} value={job.waitingOn ?? null} note={job.waitingNote} statusLabel={headerStatus} hideStatus canEdit={canCreateCommercial && canSchedule && !["cancelled", "canceled"].includes(job.status)} onSaved={reloadJob} />
            </div>
            <div className="job-glance-state">
          {canSchedule && !jobClosed && primaryAppointment && <JobCancellation appointmentId={primaryAppointment.id} appointmentLabel={`Job #${job.jobNumber}`} cancelJob onSaved={reloadJob} />}
              <button type="button" className="job-widget-close" onClick={back} aria-label="Close job" title={backLabel}><X size={16} /></button>
              {priority && !["normal", "standard", "low"].includes(priority) && <span className="job-glance-priority">{job.priority}</span>}
            </div>
            {jobNotes}
          </div>
          </div>
  );
  const inlineJobWidgets = {
    appointments: (
          <JobRecordWidget id={`job-${job.id}-appointments-panel`} label="Appointments" regionLabel="Job appointments" open={jobPanel === "appointments"} onToggle={closeJobDetails}>
          <section className="job-primary-appointment" aria-label="Scheduled appointment">

            <div className="job-appointment-heading">{addAppointmentButton}</div>
            <div id={`job-${job.id}-appointments`}>
            {canSchedule && schedulingBlockReason && <p id={`job-${job.id}-scheduling-block`} className="job-muted">{schedulingBlockReason}</p>}
            {primaryAppointment ? renderVisit(primaryAppointment) : <Empty text={jobClosed ? "No visits recorded." : "Not scheduled yet."} />}
          {additionalAppointments.length > 0 && <details key={`${job.id}-additional`} aria-label="Additional appointments" className="job-additional-appointments job-appointment-dropdown">
            <summary>Additional appointments <span className="job-appointment-count">{additionalAppointments.length}</span></summary>
            {additionalAppointments.map((appointment) => renderVisit(appointment))}
          </details>}
          {canSchedule && <section className="job-add-appointment" aria-label="Schedule another appointment">
            {addingAppointment && job.status === "completed" && <p className="job-muted">Adding an appointment will reopen this job as scheduled. Completed visits and invoices will stay unchanged.</p>}
            {addingAppointment ? <JobAppointmentForm jobId={job.id} jobTypeIds={selectableJobTypes.map(type => type.id)} visit={primaryAppointment} timezone={timezone} onClose={() => setAddingAppointment(false)} onSaved={(warning) => { setAddingAppointment(false); setAppointmentNotice(warning || "Appointment added."); reloadJob(); }} /> : null}
            {reschedulingAppointment && <JobAppointmentForm key={reschedulingAppointment.id} jobId={job.id} jobTypeIds={selectableJobTypes.map(type => type.id)} reschedule={reschedulingAppointment} timezone={timezone} onClose={() => { setReschedulingAppointment(null); reloadJob(); }} onSaved={(warning) => { setReschedulingAppointment(null); setAppointmentNotice(warning || "Appointment rescheduled."); reloadJob(); }} />}
            {appointmentNotice && <p role="status" className="job-muted">{appointmentNotice}</p>}
          </section>}
            </div>
          </section>
          </JobRecordWidget>
    ),
    summary: (
          <JobRecordWidget id={`job-${job.id}-summary-panel`} label="Customer details" regionLabel="Customer details" open={jobPanel === "summary"} onToggle={closeJobDetails}>
        <header className="job-widget-header">
          {!!detail.jobTags?.length && <div className="job-assigned-tags" aria-label="Job tags">{detail.jobTags.map((tag) => <span key={tag.id} style={{ borderColor: tag.color || undefined }}>{tag.name}</span>)}</div>}
        </header>
          <section aria-label="Job overview">
        <div className="job-work-sections">
          <section aria-label="Visits and timesheets">



          {todayUnmatchedTime.length > 0 && <div className="job-unmatched-time"><JobTimesheets canEdit={canSchedule} closed={["completed", "cancelled", "canceled"].includes(job.status)} onSaved={reloadJob} entries={todayUnmatchedTime} timezone={timezone} title="Today’s other recorded time" /><p className="job-muted">These entries could not be matched to one visit using its time and assigned technician.</p></div>}
          {historicalUnmatchedTime.length > 0 && <details key={`${job.id}-other-time`} className="job-unmatched-time"><summary className="cursor-pointer text-sm font-semibold">Earlier time records · {historicalUnmatchedTime.length}</summary><JobTimesheets canEdit={canSchedule} closed={jobClosed} onSaved={reloadJob} entries={historicalUnmatchedTime} timezone={timezone} title="Other recorded time" /><p className="job-muted">These entries could not be matched to one visit using its time and assigned technician.</p></details>}
          </section>



        </div>
          <div className="job-widget-contacts">
            <JobContacts
              key={job.id}
              jobId={job.id}
              propertyId={job.propertyId}
              customerId={job.customerId}
              propertyContacts={detail.propertyContacts ?? []}
              billingContacts={displayedBillingContacts}
              customerName={job.customerName}
              address={streetAddress(job) || "No service address on file"}
              showBilling={false}
              locationIdentity={<><button type="button" onClick={() => void openCustomer(job.customerId)} className="job-record-customer">{job.customerName}</button>{!!detail.locationTags?.length && <div className="job-assigned-tags" aria-label="Location tags">{detail.locationTags.map((tag) => <span key={tag.id} style={{ borderColor: tag.color || undefined }}>{tag.name}</span>)}</div>}</>}

              canEdit={canEditContacts}
              saveContact={saveContact}
              deleteContact={deleteContact}
            />
          </div>
          </section>
          <details className="job-customer-activity"><summary>Calls</summary>
          <CallHistory key={`calls:${job.id}`} jobId={job.id} timezone={timezone} />
          <section aria-label="Historical calls">
			  <JobSection title="Historical calls" right={<span className="job-muted">ServiceTitan archive</span>}>
				  {!detail.historicalCalls?.length ? <Empty text="No historical calls are linked to this job." /> : <div className="space-y-3">
					  {detail.historicalCalls.map((call) => <article key={call.id} className="rounded-lg border border-[var(--line)] bg-[var(--paper)] p-3" aria-label={`Historical call ${callDuration(call.durationSeconds)}`}>
						  <div className="flex flex-wrap items-start justify-between gap-2">
							  <div><p className="font-semibold text-[var(--ink)]">{call.direction || call.callType || "Call"} <span className="font-normal text-[var(--ink-soft)]">· {callDuration(call.durationSeconds)}</span></p>
							  <p className="mt-0.5 text-xs text-[var(--ink-faint)]">{call.occurredAt ? `${date(call.occurredAt)} · ${time(call.occurredAt)}` : "Date unavailable"}{call.fromPhone || call.toPhone ? ` · ${call.fromPhone || "Unknown"} → ${call.toPhone || "Unknown"}` : ""}</p></div>
							  <span className={`rounded px-2 py-0.5 text-xs font-medium ${call.recordingStatus === "available" ? "bg-[var(--st-complete-bg)] text-[var(--st-complete)]" : "bg-[var(--paper-muted)] text-[var(--ink-soft)]"}`}>{call.recordingStatus === "available" ? "Recording available" : call.recordingStatus === "unavailable" ? "Recording unavailable" : "Not recorded"}</span>
						  </div>
						  {call.reason && <p className="mt-2 text-sm text-[var(--ink-soft)]">{call.reason}</p>}
						  {call.recordingUrl ? <CallRecordingPlayer durationSeconds={call.durationSeconds} src={`${API_URL}${call.recordingUrl}`} /> : <p className="mt-3 text-sm text-[var(--ink-faint)]">{call.recordingStatus === "unavailable" ? "ServiceTitan no longer provides this recording." : "This call did not include a recording."}</p>}
					  </article>)}
				  </div>}
			  </JobSection>
          </section></details>
          <details className="job-customer-activity" onToggle={event => setJobMessagesOpen(event.currentTarget.open)}><summary>Messages</summary>
            {jobMessagesOpen && <CustomerMessages
              key={job.id}
              endpoint={`/api/v1/workspace/jobs/${encodeURIComponent(job.id)}/historical-threads`}
              inlineJobId={job.id}
              timezone={timezone}
            />}
          </details>
          </JobRecordWidget>
    ),
  };
  return (
    <>
      <Dialog title={`Job #${job.jobNumber}`} modal={!companionTextsOpen} overlayClassName={`job-dialog-overlay ${hasJobFlyout || invoicePdf ? "job-dialog-overlay--details" : ""}`} className={`job-detail-dialog job-page-reference job-detail-dialog--tiles ${hasJobFlyout || invoicePdf ? "job-detail-dialog--details" : ""} ${invoicePdf ? "job-detail-dialog--pdf" : ""}`} width="max-w-6xl" onClose={back} header={<></>}>
      <div className="job-document-layout"><div className="job-record-pane">
      {jobHeader}
      <article className="job-record job-record-widget">
        <div className="job-record-content">
          <div className="job-summary-tiles" role="group" aria-label="Job sections">
            {([
              { id: "appointments", label: "Appointments", icon: CalendarDays, count: visibleAppointments.length, summary: primaryAppointment ? statusChip(primaryAppointment.status).label : "Not scheduled" },
              { id: "summary", label: "Customer details", icon: UserRound, summary: streetAddress(job) || "Contacts and service address" },
              { id: "estimates", label: "Estimates", icon: FilePlus2, count: detail.estimates.length, summary: detail.estimates.length ? `${detail.estimates.filter(item => item.status === "accepted" || item.officeStatus === "sold").length} approved` : "No estimates yet" },
              { id: "invoices", label: "Invoices", icon: FilePlus2, count: detail.invoices.length, summary: detail.invoices.length ? `${money(detail.invoices.reduce((sum, item) => sum + (item.balanceDueCents ?? 0), 0))} outstanding` : "Not billed yet" },
              { id: "photos", label: "Photos & files", icon: Images, summary: "Photos and documents" },
              ...(inventoryPermissions.read ? [{ id: "purchase-orders" as const, label: "Purchase orders and returns", icon: FolderOpen, summary: "Equipment and materials" }] : []),
            ] as const).map(tile => <div key={tile.id} className={`job-summary-tile-item ${jobPanel === tile.id && (tile.id === "appointments" || tile.id === "summary") ? "job-summary-tile-item--expanded" : ""}`}><button id={`job-${job.id}-${tile.id}-tile`} type="button" className="job-summary-tile" aria-label={`${tile.label}${"count" in tile ? ` (${tile.count})` : ""}`} aria-expanded={jobPanel === tile.id && !invoicePdf} aria-controls={`job-${job.id}-${tile.id}-panel`} onClick={() => { if (detail.detailPending && !["appointments", "summary"].includes(tile.id)) return; setInvoicePdf(null); setJobPanel(current => current === tile.id && !invoicePdf ? null : tile.id); }}>
              <span className="job-summary-tile-top"><tile.icon size={18} aria-hidden />{"count" in tile && <strong>{tile.count}</strong>}<ArrowRight size={15} aria-hidden className="job-summary-tile-arrow" /></span>
              <span className="job-summary-tile-title">{tile.label}</span><span className="job-summary-tile-description">{detail.detailPending && !["appointments", "summary"].includes(tile.id) ? "Loading details…" : tile.summary}</span>
            </button>
            {(tile.id === "appointments" || tile.id === "summary") && <div className="job-inline-widgets" hidden={jobPanel !== tile.id || Boolean(invoicePdf)} onKeyDown={event => { if (event.key === "Escape") { event.stopPropagation(); closeJobDetails(); } }}>{inlineJobWidgets[tile.id]}</div>}
            </div>)}
          </div>

          {!["completed", "cancelled", "canceled"].includes(job.status) && detail.estimates.some(estimate => estimate.status === "accepted" || estimate.officeStatus === "sold") && <section className="job-approved-actions" aria-label="Sold estimate next steps">
            <h2 className="font-semibold">Approved estimate</h2>

            <div className="job-actions">
              <QuietButton type="button" onClick={() => setJobPanel("invoices")}>Deposit & payments</QuietButton>
              {inventoryPermissions.read && <QuietButton type="button" onClick={() => setJobPanel("purchase-orders")}>Order equipment</QuietButton>}
              <QuietButton type="button" onClick={() => setJobPanel("summary")}>Visits, notes & timesheets</QuietButton>
              {canSchedule && <QuietButton type="button" onClick={() => setBookingAppointment(true)}>Book appointment</QuietButton>}
            </div>
          </section>}
        </div>

      </article>
      </div>
      <aside ref={detailsPaneRef} className="job-details-pane" aria-label="Job section details" hidden={!hasJobFlyout || Boolean(invoicePdf)} onKeyDown={event => { if (event.key === "Escape") { event.stopPropagation(); closeJobDetails(); } }}>
          <JobRecordWidget id={`job-${job.id}-estimates-panel`} label="Estimates" regionLabel="Job estimates" count={detail.estimates.length} open={jobPanel === "estimates"} onToggle={closeJobDetails}>
          <JobSection title="Estimates" hideTitle>
            <div className="space-y-2">
              <JobEstimates
                jobTypes={availableJobTypes}
                workStates={Object.fromEntries(detail.estimates.map(estimate => [estimate.id, estimateWorkState(estimate, estimateWorkFacts)]))}
                renderTextStatus={id => <EstimateDeliverySummary estimateId={id} onlyText />}
                renderStatus={id => { const estimate = detail.estimates.find(item => item.id === id); return estimate ? <EstimateWorkStatusSummary item={estimate} facts={estimateWorkFacts} onOpenJob={openRelatedJob} /> : null; }}
                key={job.id}
                initialExpandedId={focusDocumentId}
                estimates={detail.estimates}
                timezone={timezone}
                canWrite={canEditCommercial}
                customerEmail={emailAddress}
                emailRecipients={[...displayedBillingContacts, ...(detail.propertyContacts ?? [])].filter(contact => contact.email).map(contact => ({ email: contact.email!, name: contact.name }))}
                createEstimate={canCreateCommercial ? () => setOptionComposer({}) : undefined}
                onDeleted={reloadJob}
                renderDetails={(estimateId) => {
                  const estimate = detail.estimates.find((item) => item.id === estimateId)!;
                  return <div key={estimate.id} className="space-y-2">
                  <EstimateCard
                    job={job}
                    editingOptionId={optionComposer?.estimateId === estimate.id ? optionComposer.optionId ?? "" : undefined}
                    stopEditing={() => setOptionComposer(null)}
                    estimate={estimate}
                    priceBookItems={priceBookItems}
                    priceBookCategories={priceBookCategories}
                    canWrite={canEditCommercial}
                    reload={reloadJob}
                    openEstimate={openEstimate}
                    openRelatedJob={openRelatedJob}
                    openRelatedInvoice={openRelatedInvoice}
                    editManagedEstimate={(estimateId, optionId) => setOptionComposer({ estimateId, optionId })}
                    addApprovedWorkToInvoice={(estimateId, optionId) => setAddingApprovedWorkFromEstimate({ estimateId, optionId })}
                  />
                  {canCreateCommercial && (
                    <div className="flex gap-2">
                      {!estimate.optionsManaged && ["draft", "sent"].includes(estimate.status) && (
                        <button
                          disabled={busy === estimate.id}
                          onClick={() => setAccepting(estimate)}
                          className="rounded-full border border-[var(--line)] bg-[var(--wash)] px-3 py-1.5 text-xs font-semibold text-[var(--ink)] disabled:opacity-60"
                        >
                          Customer approved it
                        </button>
                      )}
                    </div>
                  )}
                </div>;
                }}
              />
              {optionComposer && !optionComposer.estimateId && (
                <OfficeEstimateComposer
                  owner={{ customerId: job.customerId, propertyId: job.propertyId, jobId: job.id }}
                  priceBookItems={priceBookItems}
                  priceBookCategories={priceBookCategories}
                  onClose={() => setOptionComposer(null)}
                  onSaved={() => { reloadJob(); }}
                />
              )}
            </div>
          </JobSection>
          </JobRecordWidget>
          <JobRecordWidget id={`job-${job.id}-invoices-panel`} label="Invoices" regionLabel="Job invoices" count={detail.invoices.length} open={jobPanel === "invoices"} onToggle={closeJobDetails}>
          <JobSection title="Invoices" hideTitle>
          {canCreateCommercial && detail.invoices.length === 0 && (
            <div>
              {canCreateCommercial && (
                <div className="mt-4">
                  <button
                    onClick={() => {
                      setCreating("invoice");
                      setDocumentItems([]);
						setInvoiceDraftRows([]); setInvoiceSummary("");
						setPricingAppointmentId(selectableAppointments[0]?.id ?? "");
						setPricingJobTypeId(selectableJobTypes[0]?.id ?? "");
                    }}
                    className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)]"
                  >
                    New invoice
                  </button>
                </div>
              )}
              {creating && <section className="job-invoice-editor mt-4 border-t border-[var(--line)] pt-4" aria-label="Create invoice in job">
                <div className="job-invoice-details-form"><TextArea label="Invoice summary" value={invoiceSummary} maxLength={200} onChange={event => setInvoiceSummary(event.target.value)} /></div>
                <InvoiceItemActions disabled={saving || documentItems.length + invoiceDraftRows.length >= 100} onAdd={kind => setInvoiceDraftRows(rows => [...rows, { id: ++invoiceDraftSequence.current, kind }])} />
                <div className="invoice-sheet-columns" aria-hidden="true"><span>Description</span><span>Quantity</span><span>Unit price</span><span>Line total</span></div>
                {documentItems.map((line, index) => {
                  const item = invoicePriceBookItems.find(item => item.id === line.priceBookItemId);
                  const cents = line.unitPriceCents ?? pricingPreview?.lineItems.find(item => item.priceBookItemId === line.priceBookItemId)?.unitPriceCents ?? item?.effectivePriceCents ?? item?.priceCents ?? 0;
                  const change = (patch: Partial<typeof line>) => setDocumentItems(lines => lines.map((current, position) => position === index ? { ...current, ...patch } : current));
                  return <div className="invoice-sheet-row" key={index}><fieldset disabled={saving}>
                    <TextArea label="Description" value={line.description ?? item?.name ?? ""} onChange={event => change({ description: event.target.value })} />
                    <input aria-label="Quantity" type="number" min="0.01" step="0.01" value={line.quantity} onChange={event => change({ quantity: Number(event.target.value) })} />
                    <input aria-label="Unit price" type="number" step="0.01" value={(cents / 100).toFixed(2)} onChange={event => change({ unitPriceCents: Math.round(Number(event.target.value) * 100) })} />
                    <strong>{money(Math.round(line.quantity * cents))}</strong>
                    <button type="button" className="job-icon-button" aria-label={`Remove ${line.description ?? item?.name ?? "item"}`} onClick={() => setDocumentItems(lines => lines.filter((_, position) => position !== index))}><Trash2 size={16} /></button>
                  </fieldset></div>;
                })}
                {invoiceDraftRows.map(row => <InvoiceAddLine key={row.id} filterKind={row.kind} registerSave={() => () => {}} onAdded={() => setInvoiceDraftRows(rows => rows.filter(current => current.id !== row.id))} priceBookItems={invoicePriceBookItems} categories={priceBookCategories} busy={saving} baseCents={selectedTotal} add={async body => { setDocumentItems(lines => [...lines, body as typeof documentItems[number]]); return true; }} />)}
                {commercialTaxPending && <p role="status" className="job-muted">Calculating tax…</p>}
                <dl className="job-invoice-edit-totals"><div><dt>Subtotal</dt><dd>{money(selectedTotal)}</dd></div><div><dt>Sales tax</dt><dd>{money(commercialTaxCents)}</dd></div><div><dt>Invoice total</dt><dd>{money(selectedDocumentTotal)}</dd></div><div><dt>Payments applied</dt><dd>{money(0)}</dd></div><div><dt>Balance due</dt><dd>{money(selectedDocumentTotal)}</dd></div></dl>
                {!selectableJobTypes.length && <p className="job-muted">Assign a job type to this job before creating an invoice.</p>}
                <div className="job-actions invoice-sheet-save"><PrimaryButton type="button" disabled={saving || commercialTaxPending || !selectableJobTypes.length || !documentItems.length || invoiceDraftRows.length > 0 || documentItems.some(line => !Number.isFinite(line.quantity) || line.quantity <= 0 || line.quantity > 100000 || (line.unitPriceCents !== undefined && (!Number.isFinite(line.unitPriceCents) || Math.abs(line.unitPriceCents) > 100000000)) || (!line.priceBookItemId && !line.description?.trim())) || !pricingPreview || selectedDocumentTotal <= 0} onClick={() => void submitCommercial()}>{saving ? "Saving…" : "Save"}</PrimaryButton><QuietButton type="button" disabled={saving} onClick={() => { setCreating(null); setDocumentItems([]); setInvoiceDraftRows([]); }}>Cancel</QuietButton></div>
              </section>}

            </div>
          )}
            <div className="space-y-2">
              {orderedJobInvoices.map((invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  previewPdf={(id, label) => setInvoicePdf({ id, label })}
                  payments={(detail.payments ?? []).filter((payment) => payment.invoiceId === invoice.id)}
                  onSaved={reloadJob}
                  priceBookItems={priceBookItems}
                  priceBookCategories={priceBookCategories}
                  canWrite={canEditCommercial}
                  recordPayment={recordPayment}
                  openInvoice={openInvoice}
                  customerEmail={emailAddress}
                  startCollectPayment={invoice.id === collectPaymentInvoiceId}
                  startOpen={invoice.id === focusDocumentId || (jobPanel === "invoices" && invoice.fulfillment === "later" && job.status === "new")}
                />
              ))}
              {!detail.invoices.length && <Empty text={job.status === "completed" ? "No invoice is available for this completed job in the current view." : "Not billed yet."} />}
            </div>
          </JobSection>
          </JobRecordWidget>
          <JobRecordWidget id={`job-${job.id}-photos-panel`} label="Photos & files" regionLabel="Job photos" open={jobPanel === "photos"} onToggle={closeJobDetails}>
          <JobSection title="Photos & files" hideTitle>
            <PhotoGallery
              title="Job photos"
              path={`/api/v1/workspace/jobs/${job.id}/photos`}
              canWrite={canCreateCommercial}
            />
            <DocumentsPanel title="Files" path={`/api/v1/workspace/jobs/${job.id}/attachments`} canWrite={canCreateCommercial} />
          </JobSection>

          </JobRecordWidget>
          {inventoryPermissions.read && <JobRecordWidget id={`job-${job.id}-purchase-orders-panel`} label="Purchase orders and returns" regionLabel="Job purchase orders" open={jobPanel === "purchase-orders"} onToggle={closeJobDetails}>
            <JobPurchaseOrders startCreating={focusDocumentId === "job-step:purchase-orders"} estimates={detail.estimates} priceBookItems={priceBookItems} canInvoice={canCreateCommercial} canEditPricebook={canEditCommercial} onInvoiceSaved={reloadJob} jobId={job.id} jobNumber={String(job.jobNumber)} permissions={inventoryPermissions} />
          </JobRecordWidget>}

      </aside>
      {invoicePdf && <InvoicePdfPane invoiceId={invoicePdf.id} label={invoicePdf.label} onClose={() => setInvoicePdf(null)} />}
      </div>
      </Dialog>
      {bookingAppointment && <Dialog title="Book appointment" width="max-w-3xl" onClose={() => { if (!bookingAppointmentBusy) setBookingAppointment(false); }}>
        {!detail.jobTypes.length && <label className="mb-4 block text-sm">Job type<select aria-label="Appointment job type" value={bookingJobTypeId} disabled={bookingAppointmentBusy} onChange={event => setBookingJobTypeId(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2"><option value="">Choose job type</option>{availableJobTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>}
        <InstallScheduler allowDoubleBooking jobTypeId={detail.jobTypes[0]?.id || bookingJobTypeId} prepareJob={async () => {
          if (!detail.jobTypes.length) {
            if (!bookingJobTypeId) throw new Error("Choose a job type before scheduling.");
            await assignJobType(job.id, bookingJobTypeId);
          }
          return job.id;
        }} onBusyChange={setBookingAppointmentBusy} onDone={() => { setBookingAppointment(false); reloadJob(); }} />
      </Dialog>}
      {accepting && (
        <EstimateAcceptDialog
          estimate={accepting}
          busy={busy === accepting.id}
          onClose={() => setAccepting(null)}
          onAccept={(signature) =>
            act(accepting.id, () =>
              acceptEstimate(job.id, accepting.id, signature).then(() => {
                setAccepting(null);
              }),
            )
          }
        />
      )}
      {addingApprovedWorkFromEstimate && <JobEstimateAllocationAction estimateId={addingApprovedWorkFromEstimate.estimateId} optionId={addingApprovedWorkFromEstimate.optionId} onClose={() => setAddingApprovedWorkFromEstimate(null)} onDone={(jobId, step) => { if (jobId && openRelatedJob) openRelatedJob(jobId, step ? `job-step:${step}` : undefined); else void reloadJob(); }} />}
    </>
  );
}
const priceBookKinds: PriceBookCategory["kind"][] = [
  "service",
  "material",
  "equipment",
  "discount",
  "fee",
  "membership_plan",
];
const currencyNumber = (value: FormDataEntryValue | null) => {
  const normalized = String(value ?? "").replace(/[,$\s]/g, "");
  if (!normalized) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};
const centsFromInput = (value: FormDataEntryValue | null) =>
  Math.round((currencyNumber(value) ?? 0) * 100);
/**
 * Markup, not margin. The office prices off cost — "what did it cost us and
 * what do we charge" — so the number under a line is (price − cost) / cost.
 * No cost on the record means no number rather than a misleading zero.
 */
const markupPercent = (
  priceCents: number,
  costCents: number | null | undefined,
) =>
  costCents && costCents > 0
    ? Math.round(((priceCents - costCents) / costCents) * 100)
    : null;
const inputFromCents = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? "" : String(cents / 100);
const currencyInputFromCents = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? "" : money(cents);

/**
 * A pricebook picture, or the initial of what it was meant to show.
 *
 * The ServiceTitan import carries a URL for most items and categories, and a
 * good number of those URLs do not load in a browser — they point at assets
 * behind ServiceTitan's own auth. An empty grey frame reads as a broken
 * screen; a letter reads as a record without a photo, which is the truth.
 */
const pbControl =
  "mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm text-[var(--ink)] outline-none focus:border-[var(--line-strong)]";

function PriceBookField({
  label,
  hint,
  wide = false,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={wide ? "block md:col-span-2" : "block"}>
      <span className="block text-sm font-medium text-[var(--ink-soft)]">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-[11px] text-[var(--ink-soft)]">
          {hint}
        </span>
      )}
    </label>
  );
}

function PriceBookGroup({ title }: { title: string }) {
  return (
    <p className="mt-1 border-t border-[var(--line)] pt-3 text-sm font-semibold text-[var(--ink)] first:mt-0 first:border-t-0 first:pt-0 md:col-span-2">
      {title}
    </p>
  );
}

export function Pricebook({
  categories,
  items,
  accountingAccounts = [],
  vendorOptions,
  supportsItemDetails = Boolean(vendorOptions),
  loading = false,
  archived,
  canWrite,
  setArchived,
  save,
  uploadImage,
  settings = null,
  saveSettings,
  onOpenEstimate,
}: {
  settings?: { form: SettingsForm; jobTypes: CompanyData["jobTypes"] } | null;
  saveSettings?: (next: SettingsForm) => Promise<void>;
  onOpenEstimate?: (id: string) => void;
  categories: PriceBookCategory[];
  vendorOptions?: Array<{ id: string; name: string }>;
  supportsItemDetails?: boolean;
  items: PriceBookItem[];
  accountingAccounts?: Array<{ accountKind: "income" | "expense"; name: string; itemCount: number }>;
  readiness?: PriceBookReadiness;
  loading?: boolean;
  archived: boolean;
  canWrite: boolean;
  setArchived: (value: boolean) => void;
  save: (
    path: string,
    method: "POST" | "PATCH" | "PUT" | "DELETE",
    body: unknown,
  ) => Promise<void>;
  uploadImage?: (path: string, image: File) => Promise<void>;
}) {
  const blankCategory = (): PriceBookCategory => ({
    id: "",
    name: "",
    kind: "service",
    parentCategoryId: null,
    position: 0,
    description: null,
    imageUrl: null,
    active: true,
  });
  const blankItem = (): PriceBookItem => ({
    id: "",
    categoryId: "",
    categoryIds: [],
    name: "",
    description: null,
    priceCents: 0,
    unitType: "flat",
    costCents: null,
    sku: null,
    taxable: true,
    taxClass: "labor",
    itemType: null,
    itemTypeSource: null,
    sourceIncomeAccountName: null,
    sourceExpenseAccountName: null,
    afterHoursPriceCents: null,
    memberPriceCents: null,
    addOnPriceCents: null,
    imageUrl: null,
    vendorName: null,
    modifierType: null,
    modifierAmountType: null,
    modifierBasisPoints: null,
    active: true,
  });
  const [categoryEdit, setCategoryEdit] = useState<PriceBookCategory | null>(
    null,
  );
  const [itemEdit, setItemEdit] = useState<PriceBookItem | null>(null);
  const [newItemChoice, setNewItemChoice] = useState<"types" | "adjustment" | null>(null);
  const [itemTypeEdited, setItemTypeEdited] = useState(false);
  const [itemPriceInput, setItemPriceInput] = useState("");
  const [itemCostInput, setItemCostInput] = useState("");
  const [selectedImage, setSelectedImage] = useState<{
    name: string;
    imageUrl: string;
  } | null>(null);
  const [afterHours, setAfterHours] = useState(false);
  const [taxClass, setTaxClass] = useState<"labor" | "material">("labor");
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const [searchScope, setSearchScope] = useState("category");
  const [showAllItems, setShowAllItems] = useState(false);
  const [directItemsOnly, setDirectItemsOnly] = useState(false);
  const [catalogSection, setCatalogSection] = useState<"categories" | "equipment" | "discounts" | "templates">("categories");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesBusy, setRulesBusy] = useState(false);
  const [rulesError, setRulesError] = useState("");
  const [imageBusy, setImageBusy] = useState(false);
  const [itemSaving, setItemSaving] = useState(false);
  const [itemPanel, setItemPanel] = useState<"categories" | "photo" | null>(null);
  const [itemSaveError, setItemSaveError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState<"all" | "service" | "material" | "equipment" | "adjustment">("all");
  const [trade, setTrade] = useState<"all" | "hvac" | "plumbing">("all");
  const [browseAllCategories, setBrowseAllCategories] = useState(false);
  const openCategory = (id: string | null) => {
    setOpenCategoryId(id);
    setDirectItemsOnly(false);
    setSearch("");
    setFilter("all");
    setShowAllItems(false);
    setSearchScope("category");
  };
  const clearResults = () => {
    setSearch("");
    setFilter("all");
    setShowAllItems(false);
  };
  const chooseTrade = (next: typeof trade) => {
    setTrade(next);
    setBrowseAllCategories(next === "all");
    setCatalogSection("categories");
    openCategory(null);
  };
  const showTrades = () => {
    setTrade("all");
    setBrowseAllCategories(false);
    openCategory(null);
  };
  const ordered = [...categories].sort(
    (a, b) => a.position - b.position || a.name.localeCompare(b.name),
  );
  const categoriesById = useMemo(() => new Map(categories.map(category => [category.id, category])), [categories]);
  const categoryPaths = useMemo(() => new Map(categories.map(category => {
    const names: string[] = [];
    const seen = new Set<string>();
    let node: PriceBookCategory | undefined = category;
    while (node && !seen.has(node.id)) {
      seen.add(node.id);
      names.unshift(node.name);
      node = node.parentCategoryId ? categoriesById.get(node.parentCategoryId) : undefined;
    }
    return [category.id, names.join(" / ")];
  })), [categories, categoriesById]);
  // Trade follows saved category branches, including secondary placements.
  // Unassigned/shared categories remain available under All trades.
  const tradeCategoryIds = useMemo(() => new Set([...categoryPaths].filter(([, path]) => trade === "all" ||
    path.split(" / ").some(name => trade === "hvac" ? /^hvac\b/i.test(name) : /^plumbing\b/i.test(name))).map(([id]) => id)), [categoryPaths, trade]);
  const categoryMatchesTrade = (id: string) => trade === "all" || tradeCategoryIds.has(id);
  const tradeItems = useMemo(() => trade === "all" ? items : items.filter(item =>
    (item.categoryIds?.length ? item.categoryIds : [item.categoryId]).some(id => tradeCategoryIds.has(id))), [items, trade, tradeCategoryIds]);
  const adjustmentIds = new Set(discountFeeItems(items, categories).map(item => item.id));
  const itemCatalogType = (item: PriceBookItem): "service" | "material" | "equipment" | "adjustment" => {
    if (item.modifierType === "discount" || item.modifierType === "fee") return "adjustment";
    if (item.itemType) return item.itemType;
    if (adjustmentIds.has(item.id)) return "adjustment";
    if (equipmentDetails(item)) return "equipment";
    const kinds = (item.categoryIds || [item.categoryId]).map(id => categoriesById.get(id)?.kind);
    if (kinds.includes("equipment")) return "equipment";
    if (kinds.includes("material")) return "material";
    return "service";
  };
  const TYPE_TABS = [
    { id: "all", label: "All items" }, { id: "service", label: "Services" },
    { id: "material", label: "Materials" }, { id: "equipment", label: "Equipment" },
    { id: "adjustment", label: "Discounts & fees" },
  ] as const;
  const typeCategoryLabel = { all: "All categories", service: "Service categories", material: "Material categories", equipment: "Equipment categories", adjustment: "Discount and fee categories" }[typeFilter];
  const typeItems = typeFilter === "all" ? tradeItems : tradeItems.filter(item => itemCatalogType(item) === typeFilter);
  const consolidatedCategoryIds = consolidatedDiscountFeeCategoryIds(categories);
  const children = (parentId: string | null) =>
    ordered.filter((category) => category.parentCategoryId === parentId);
  const roots = ordered.filter(
    (category) =>
      !category.parentCategoryId ||
      !categories.some((parent) => parent.id === category.parentCategoryId),
  );
  const categoryItems = (categoryId: string) =>
    typeItems.filter((item) =>
      (item.categoryIds || [item.categoryId]).includes(categoryId),
    );
  const descendantIds = (categoryId: string) => {
    const ids = new Set<string>();
    const pending = [categoryId];
    while (pending.length) {
      const id = pending.pop()!;
      if (ids.has(id)) continue;
      ids.add(id);
      pending.push(...children(id).map((child) => child.id));
    }
    return ids;
  };
  const itemsInTree = (categoryId: string) => {
    const ids = descendantIds(categoryId);
    return typeItems.filter((item) =>
      (item.categoryIds || [item.categoryId]).some((id) => ids.has(id)),
    );
  };
  // Parent assignments often repeat child assignments. Only items without a
  // visible child placement need a General items tile.
  const directOnlyItems = (categoryId: string) => {
    const childItemIds = new Set(children(categoryId)
      .filter((category) => (typeFilter !== "all" || !consolidatedCategoryIds.has(category.id)))
      .flatMap((category) => itemsInTree(category.id).map((item) => item.id)));
    return categoryItems(categoryId).filter((item) =>
      !childItemIds.has(item.id) &&
      (typeFilter !== "all" || !(item.categoryIds || [item.categoryId]).some((id) => consolidatedCategoryIds.has(id))),
    );
  };
  const scopedItems = openCategoryId && searchScope === "category"
    ? directItemsOnly ? directOnlyItems(openCategoryId) : itemsInTree(openCategoryId)
    : typeItems;
  const categoryPath = (categoryId: string) => categoryPaths.get(categoryId) || "Category unavailable";
  const configuredMaterialMarkup = settings?.form.materialMarkupPercent.trim();
  const materialMultiplier =
    configuredMaterialMarkup === undefined || configuredMaterialMarkup === ""
      ? null
      : 1 + Number(configuredMaterialMarkup) / 100;
  const pricedFromCost = (item: PriceBookItem) =>
    item.taxClass === "material" &&
    item.costCents !== null &&
    materialMultiplier !== null
      ? Math.round(item.costCents * materialMultiplier)
      : null;
  const updateItemCost = (value: string) => {
    setItemCostInput(value);
    const amount = currencyNumber(value);
    const costCents = amount === null ? null : Math.round(amount * 100);
    const priceCents = costCents !== null && materialMultiplier !== null ? Math.round(costCents * materialMultiplier) : null;
    if (priceCents !== null) setItemPriceInput(currencyInputFromCents(priceCents));
    setItemEdit(current => current ? { ...current, costCents, ...(priceCents !== null ? { priceCents } : {}),
      ...(costCents !== null ? { vendorCosts: current.vendorCosts?.map(cost => cost.isPrimary ? { ...cost, costCents } : cost) } : {}),
    } : null);
  };
  const effectivePriceCents = (item: PriceBookItem) =>
    pricedFromCost(item) ?? item.priceCents;
  const categoryOptions = (
    nodes: PriceBookCategory[],
    excludedIds = new Set<string>(),
    depth = 0,
  ): ReactNode[] =>
    nodes.flatMap((category) => {
      // A category cannot become its own parent or live beneath one of its
      // descendants. Keep those impossible moves out of the editor instead
      // of making the dispatcher discover them only after saving.
      if (excludedIds.has(category.id)) return [];
      return [
        <option key={category.id} value={category.id}>
          {"  ".repeat(depth)}
          {category.name}
        </option>,
        ...categoryOptions(children(category.id), excludedIds, depth + 1),
      ];
    });
  const imageSourceLabel = (source?: PriceBookCategory["imageSource"]) =>
    source === "workview_upload"
      ? "WorkView upload"
      : source === "servicetitan_recovery"
        ? "Recovered from ServiceTitan"
        : source === "servicetitan_backup"
          ? "ServiceTitan backup"
          : source === "external_url"
            ? "External image URL"
            : "No image";
  const uploadPriceBookImage = async (
    kind: "items" | "categories",
    record: PriceBookItem | PriceBookCategory,
    image: File,
  ) => {
    if (!uploadImage) return;
    setImageBusy(true);
    try {
      await uploadImage(`/api/v1/workspace/pricebook/${kind}/${record.id}/image`, image);
      const updated = {
        ...record,
        imageContentUrl: `/api/v1/workspace/pricebook/${kind}/${record.id}/image?v=${Date.now()}`,
        imageSource: "workview_upload" as const,
      };
      if (kind === "items") setItemEdit(updated as PriceBookItem);
      else setCategoryEdit(updated as PriceBookCategory);
    } finally {
      setImageBusy(false);
    }
  };
  const removePriceBookImage = async (
    kind: "items" | "categories",
    record: PriceBookItem | PriceBookCategory,
  ) => {
    setImageBusy(true);
    try {
      await save(`/api/v1/workspace/pricebook/${kind}/${record.id}/image`, "DELETE", {});
      if (kind === "items") setItemEdit(null);
      else setCategoryEdit(null);
    } finally {
      setImageBusy(false);
    }
  };
  const submitCategory = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const parentCategoryId = String(form.get("parentCategoryId") || "") || null;
    const body = {
      name: String(form.get("name")),
      kind: String(form.get("kind")),
      description: String(form.get("description") || "") || null,
      imageUrl: categoryEdit?.imageContentUrl?.startsWith("/api/")
        ? categoryEdit.imageUrl
        : String(form.get("imageUrl") || "") || null,
    };
    const existing = Boolean(categoryEdit?.id);
    await save(
      existing
        ? `/api/v1/workspace/pricebook/categories/${categoryEdit!.id}`
        : "/api/v1/workspace/pricebook/categories",
      existing ? "PATCH" : "POST",
      existing ? body : { ...body, parentCategoryId },
    );
    if (existing && parentCategoryId !== categoryEdit!.parentCategoryId)
      await save(
        `/api/v1/workspace/pricebook/categories/${categoryEdit!.id}/move`,
        "POST",
        { parentCategoryId, position: categoryEdit!.position },
      );
    setCategoryEdit(null);
  };
  const submitItem = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const categoryIds = [
      ...new Set(form.getAll("categoryIds").map(String).filter(Boolean)),
    ];
    if (!categoryIds.length) { setItemSaveError("Choose at least one category before saving."); return; }
    const optionalCents = (name: string) =>
      String(form.get(name) || "") ? centsFromInput(form.get(name)) : null;
    const kind = String(form.get("taxClass"));
    const modifierType = (String(form.get("modifierType") || "") || null) as PriceBookItem["modifierType"];
    const modifierAmountType = modifierType
      ? (String(form.get("modifierAmountType") || "") || null) as PriceBookItem["modifierAmountType"]
      : null;
    const modifierBasisPoints = modifierAmountType === "percentage"
      ? Math.round(Number(form.get("modifierPercentage")) * 100)
      : null;
    if (!supportsItemDetails && (itemEdit?.equipmentDetails || itemEdit?.vendorCosts?.length)) { setItemSaveError("Equipment and vendor detail saving requires the API update. Your changes are still open here."); return; }
    const cost = optionalCents("cost");
    // A material's customer price is calculated at use time. Keep the stored
    // number as source history so an import is never silently rewritten.
    const price = modifierAmountType === "percentage"
      ? 0
      // A fixed fee or discount is always priced directly, even when an
      // imported record retained the material tax class from its legacy
      // ServiceTitan catalog row.
      : modifierType
        ? centsFromInput(form.get("price"))
      : kind === "material"
        ? itemEdit?.taxClass === "material"
          ? itemEdit.priceCents
          : 0
        : centsFromInput(form.get("price"));
    const body = {
      categoryId: categoryIds[0],
      categoryIds,
      name: String(form.get("name")),
      description: String(form.get("description") || "") || null,
      priceCents: price,
      unitType: String(form.get("unitType")),
      costCents: cost,
      sku: String(form.get("sku") || "") || null,
      taxable: form.get("taxable") === "on",
      taxClass: kind,
      sourceIncomeAccountName: String(form.get("sourceIncomeAccountName") || "").trim() || null,
      sourceExpenseAccountName: String(form.get("sourceExpenseAccountName") || "").trim() || null,
      stripeTaxCode: itemEdit?.stripeTaxCode ?? null,
      afterHoursPriceCents: itemEdit?.afterHoursPriceCents ?? null,
      memberPriceCents: optionalCents("memberPrice"),
      addOnPriceCents: optionalCents("addOnPrice"),
      imageUrl: itemEdit?.imageContentUrl?.startsWith("/api/")
        ? itemEdit.imageUrl
        : String(form.get("imageUrl") || "") || null,
      vendorName: String(form.get("vendorName") || "") || null,
      ...(supportsItemDetails ? { vendorCosts: itemEdit?.vendorCosts ?? [],
        ...((itemEdit?.itemType === "equipment" || (itemEdit && equipmentDetails(itemEdit))) ? { equipmentDetails: equipmentFormDetails(itemEdit!) } : {}),
      } : {}),
      modifierType,
      modifierAmountType,
      modifierBasisPoints,
      // Do not turn an unrelated Pricebook edit into a manual override.
      ...(modifierType
        ? { itemType: null }
        : itemEdit?.id && !itemTypeEdited
        ? {}
        : { itemType: String(form.get("itemType") || "") || null }),
    };
    const existing = Boolean(itemEdit?.id);
    setItemSaving(true);
    setItemSaveError("");
    try {
      await save(
        existing ? `/api/v1/workspace/pricebook/items/${itemEdit!.id}` : "/api/v1/workspace/pricebook/items",
        existing ? "PATCH" : "POST",
        body,
      );
      setItemEdit(null);
      setAfterHours(false);
    } catch (error) {
      setItemSaveError(error instanceof Error ? error.message : "Unable to save this item. Please try again.");
    } finally {
      setItemSaving(false);
    }
  };

  const move = async (category: PriceBookCategory, direction: -1 | 1) => {
    const siblings = children(category.parentCategoryId);
    const index = siblings.findIndex((item) => item.id === category.id);
    const other = siblings[index + direction];
    if (!other) return;
    await save(
      `/api/v1/workspace/pricebook/categories/${other.id}/move`,
      "POST",
      { parentCategoryId: other.parentCategoryId, position: category.position },
    );
    await save(
      `/api/v1/workspace/pricebook/categories/${category.id}/move`,
      "POST",
      { parentCategoryId: category.parentCategoryId, position: other.position },
    );
  };
  const startNewPricebookItem = (type: "service" | "material" | "equipment" | "fee" | "discount") => {
    const adjustment = type === "fee" || type === "discount";
    const blank = { ...blankItem(), itemType: adjustment ? null : type,
      modifierType: adjustment ? type : null, modifierAmountType: adjustment ? "fixed" as const : null,
      taxClass: type === "material" || type === "equipment" ? "material" as const : "labor" as const,
      taxable: !adjustment,
    };
    setNewItemChoice(null); setItemPanel(null); setItemSaveError(""); setItemEdit(blank);
    setItemPriceInput(currencyInputFromCents(blank.priceCents)); setItemCostInput("");
    setItemTypeEdited(true); setAfterHours(false); setTaxClass(blank.taxClass); setCategoryEdit(null);
  };
  const openPricebookItem = (item: PriceBookItem) => {
    setItemPanel(null);
    setItemSaveError("");
    setItemEdit(item);
    setItemPriceInput(currencyInputFromCents(item.priceCents));
    setItemCostInput(currencyInputFromCents(item.costCents));
    setItemTypeEdited(false);
    setAfterHours(item.afterHoursPriceCents !== null);
    setTaxClass(item.taxClass);
    setCategoryEdit(null);
  };
  const modifierLabel = (item: PriceBookItem) => item.modifierType === "discount"
    ? "Discount"
    : item.modifierType === "fee"
      ? "Fee"
      : null;
  const modifierAmountLabel = (item: PriceBookItem) =>
    item.modifierAmountType === "percentage" && item.modifierBasisPoints !== null && item.modifierBasisPoints !== undefined
      ? `${item.modifierBasisPoints / 100}%`
      : item.modifierAmountType === "fixed"
        ? money(item.priceCents)
        : "Amount to confirm";
  const itemTypeLabel = (item: PriceBookItem) => modifierLabel(item) ?? (item.itemType
    ? `${item.itemType.slice(0, 1).toUpperCase()}${item.itemType.slice(1)}`
    : "Unassigned");
  const itemIssues = (item: PriceBookItem) => [
    !item.modifierType && item.taxClass === "material" && item.costCents === null ? "Missing cost" : null,
    !item.modifierType && effectivePriceCents(item) === 0 ? "Missing price" : null,
    !item.imageContentUrl && !item.imageUrl ? "Missing photo" : null,
  ].filter((issue): issue is string => issue !== null);
  const needsAttention = (item: PriceBookItem) => itemIssues(item).length > 0;
  const attentionBadge = (label: string, candidates: PriceBookItem[], onClick: () => void) => {
    const affected = candidates.filter(needsAttention);
    if (archived || !affected.length) return null;
    const details = ["Missing cost", "Missing price", "Missing photo"].map(issue => {
      const count = affected.filter(item => itemIssues(item).includes(issue)).length;
      return count ? `${issue}: ${count}` : null;
    }).filter(Boolean).join(" · ");
    return <button type="button" aria-label={`Review missing details in ${label}: ${affected.length} ${affected.length === 1 ? "item" : "items"}`} title={details} onClick={onClick}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-[9px] font-semibold leading-none text-white shadow-sm ring-2 ring-[var(--paper)] hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700">
      {affected.length > 99 ? "99+" : affected.length}
    </button>;
  };
  // Compact category cards keep names and counts visible while browsing.
  const itemRow = (category: { id: string }, item: PriceBookItem, showCategories = true) => (
    <div
      key={`${category.id}-${item.id}`}
      className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${canWrite && !archived ? "cursor-pointer hover:bg-[var(--wash)]" : ""}`}
      onClick={(event) => {
        if (canWrite && !archived && !(event.target as HTMLElement).closest("button, a, input, textarea, select")) openPricebookItem(item);
      }}
    >
      <div className="flex min-w-0 items-center gap-3">
        <PriceBookThumb
          src={item.imageContentUrl ?? item.imageUrl}
          name={item.name}
          fit="contain"
          className="h-16 w-16 shrink-0 bg-white text-sm"
          onOpen={(imageUrl) =>
            setSelectedImage({ name: item.name, imageUrl })
          }
        />
        <div className="min-w-0">
          <p className="font-semibold text-[var(--ink)]">
            {canWrite && !archived ? <button type="button" aria-label={`Open ${item.name}`} onClick={() => openPricebookItem(item)} className="text-left font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]">{equipmentDisplayName(item)}</button> : equipmentDisplayName(item)}{" "}
            {pricebookPartNumber(item) && <span className="ml-2 inline-flex rounded border border-[var(--line)] bg-[var(--wash)] px-1.5 py-0.5 align-middle text-[11px] font-semibold text-[var(--ink-soft)]">Code {pricebookPartNumber(item)}</span>}
            <span className="ml-2 text-xs font-normal text-[var(--ink-faint)]">
                {modifierLabel(item) ? itemTypeLabel(item) : `${item.unitType} · ${itemTypeLabel(item)}`}
            </span>
            {!archived && itemIssues(item).length > 0 && (canWrite ? <button type="button" aria-label={`Fix missing details for ${item.name}`} title={itemIssues(item).join(" · ")} onClick={() => openPricebookItem(item)}
              className="ml-2 inline-flex min-h-7 min-w-7 items-center justify-center rounded-full bg-red-50 align-middle text-red-700 hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-700"><span aria-hidden="true" className="h-2 w-2 rounded-full bg-red-600" /></button>
              : <span className="ml-2 inline-block h-2 w-2 rounded-full bg-red-600" role="img" aria-label={itemIssues(item).join(" · ")} />)}
          </p>
          <p className="mt-1 whitespace-pre-wrap text-xs text-[var(--ink-soft)]">
            {equipmentDetails(item)?.model && <span className="font-medium">Model: {equipmentDetails(item)?.model} · </span>}
            {estimateDescription(item.description) || "Catalog item"}
            {item.afterHoursPriceCents !== null
              ? ` · After-hours ${money(item.afterHoursPriceCents)}`
              : ""}
          </p>
          {showCategories && <p className="mt-1 text-xs text-[var(--ink-soft)]">
            Categories: {[...new Set(item.categoryIds || [item.categoryId])].map(categoryPath).join(" · ") || "Unassigned"}
          </p>}
          <p className="mt-1 text-xs text-[var(--ink-faint)]">
            {[
              item.memberPriceCents
                ? `Member ${money(item.memberPriceCents)}`
                : "",
              item.taxable ? "Taxable" : "Non-taxable",
              item.taxable && item.stripeTaxCode
                ? `Tax code ${item.stripeTaxCode}`
                : "",
              item.vendorName || "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          {modifierLabel(item) ? (
            <p className="flex items-baseline justify-end gap-2">
              <span className="text-xs font-medium text-[var(--ink-soft)]">{item.modifierAmountType === "percentage" ? "Percentage" : "Fixed amount"}</span>
              <span className="font-semibold text-[var(--ink)]">{modifierAmountLabel(item)}</span>
            </p>
          ) : effectivePriceCents(item) > 0 ? (
            <p className="flex items-baseline justify-end gap-2">
              <span className="text-xs font-medium text-[var(--ink-soft)]">
                Unit price
              </span>
              <span className="font-semibold text-[var(--ink)]">
                {money(effectivePriceCents(item))}
              </span>
            </p>
          ) : (
            <p className="flex items-baseline justify-end gap-2">
              <span className="text-xs font-medium text-[var(--ink-soft)]">
                Unit price
              </span>
              <span className="font-semibold text-[var(--ink-faint)]">No price</span>
            </p>
          )}
          {item.costCents !== null && (
            <p className="mt-0.5 text-xs text-[var(--ink-soft)]">
              Cost {money(item.costCents)}
              {markupPercent(effectivePriceCents(item), item.costCents) !== null
                ? ` + ${markupPercent(effectivePriceCents(item), item.costCents)}% markup`
                : ""}
            </p>
          )}
        </div>
        {canWrite && archived && (
          <button
            type="button"
            onClick={() =>
              void save(
                `/api/v1/workspace/pricebook/items/${item.id}/restore`,
                "POST",
                {},
              )
            }
            className="text-sm font-semibold text-[var(--accent)]"
          >
            Restore
          </button>
        )}
      </div>
    </div>
  );
  const FILTERS = [
    { id: "all", match: (_item: PriceBookItem) => true },
    { id: "attention", match: needsAttention },
  ];
  /**
   * HOW MGP PRICES. A service carries the price it is sold at. A material
   * carries what it cost us, and the customer price is that cost plus the
   * company markup — one setting, not a number typed onto two thousand
   * rows. After hours is a job type with its own rate, so it is not asked
   * about here at all; it would only be the same rule written twice.
   */
  const vendors = [
    ...new Set(
      items
        .map((item) => item.vendorName)
        .filter((name): name is string => Boolean(name)),
    ),
  ].sort((a, b) => a.localeCompare(b));
  const term = search.trim().toLowerCase();
  const searching = showAllItems || term.length > 0 || filter !== "all";
  const matcher = FILTERS.find((entry) => entry.id === filter) ?? FILTERS[0];
  const found = searching
    ? scopedItems.filter(
        (item) =>
          matcher.match(item) &&
          (!term ||
            `${item.name} ${item.description ?? ""} ${pricebookPartSearchText(item)} ${item.vendorName ?? ""} ${(item.categoryIds?.length ? item.categoryIds : [item.categoryId]).map(categoryPath).join(" ")}`
              .toLowerCase()
              .includes(term)),
      )
    : [];

  const path: PriceBookCategory[] = [];
  for (
    let node = categories.find((category) => category.id === openCategoryId);
    node;
    node = categories.find((category) => category.id === node!.parentCategoryId)
  )
    path.unshift(node);
  const open = path[path.length - 1] ?? null;
  const hasTradeBranches = [...categoryPaths.values()].some(path => /^(hvac|plumbing)\b/i.test(path));
  const atTradePicker = hasTradeBranches && trade === "all" && !browseAllCategories && !open && !searching;
  const tradeLabel = trade === "hvac" ? "HVAC" : "Plumbing";
  const tradeCards = [
    { id: "hvac", label: "HVAC", description: "Heating, cooling & ventilation", Icon: Wind },
    { id: "plumbing", label: "Plumbing", description: "Water, piping & fixtures", Icon: Droplets },
    { id: "all", label: "All categories", description: "Shared categories & the full catalog", Icon: LayoutGrid },
  ] as const;
  const visibleCategory = (category: PriceBookCategory) => categoryMatchesTrade(category.id) &&
    (typeFilter !== "all" || !consolidatedCategoryIds.has(category.id)) &&
    (typeFilter === "all" || itemsInTree(category.id).length > 0);
  const tiles = directItemsOnly ? [] : (open ? children(open.id) : roots).filter(visibleCategory);
  const foundCategories = term && filter === "all" ? ordered.filter(category => visibleCategory(category) &&
    `${categoryPath(category.id)} ${category.description ?? ""}`.toLowerCase().includes(term)) : [];
  const generalItems = open ? directOnlyItems(open.id) : [];
  const openItems = open ? directItemsOnly ? generalItems : categoryItems(open.id) : [];
  const uncategorizedItems = typeItems.filter(item => !(item.categoryIds || [item.categoryId]).some(id => categoriesById.has(id)));
  const countIn = (category: PriceBookCategory): number =>
    new Set(itemsInTree(category.id).map((item) => item.id)).size;
  const tile = (category: PriceBookCategory, showPath = false) => {
    const count = countIn(category);
    return (
      <div key={category.id} className="relative">
        <div className="absolute -top-2 right-3 z-10">{attentionBadge(categoryPath(category.id), itemsInTree(category.id), () => {
          openCategory(category.id); setFilter("attention"); setShowAllItems(true);
        })}</div>
        {/* Restore rides on the tile rather than inside it — a button cannot hold
        another button, and an archived category has to be reachable without
        opening it. */}
        {archived && canWrite && (
          <button
            type="button"
            onClick={() =>
              void save(
                `/api/v1/workspace/pricebook/categories/${category.id}/restore`,
                "POST",
                {},
              )
            }
            className="absolute right-2 top-2 z-10 rounded-full bg-[var(--paper)] px-2.5 py-1 text-xs font-semibold text-[var(--accent)] shadow"
          >
            Restore
          </button>
        )}
        <button
          type="button"
          onClick={() => openCategory(category.id)}
          className="group flex h-full w-full items-center gap-3 rounded-xl border border-[var(--line)] bg-[var(--paper)] p-3 text-left transition hover:border-[var(--line-strong)] hover:bg-[var(--wash)]"
        >
          <span className="block h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-[var(--wash)]">
            <PriceBookThumb
              src={category.imageContentUrl ?? category.imageUrl}
              name={category.name}
              className="h-full w-full text-lg"
            />
          </span>
          <span className="block min-w-0 flex-1">
            <span className="block break-words text-sm font-semibold text-[var(--ink)]">
              {showPath ? categoryPath(category.id) : category.name}
            </span>
            <span className="mt-0.5 block text-xs text-[var(--ink-soft)]">
              {count} {count === 1 ? "catalog item" : "catalog items"}
              {children(category.id).filter(child => typeFilter === "all" || itemsInTree(child.id).length > 0).length > 0 ? ` · ${children(category.id).filter(child => typeFilter === "all" || itemsInTree(child.id).length > 0).length} sections` : ""}
            </span>
          </span>
          <ArrowRight size={16} aria-hidden="true" className="shrink-0 text-[var(--ink-faint)]" />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {selectedImage && (
        <Dialog
          title={selectedImage.name}
          onClose={() => setSelectedImage(null)}
          width="max-w-3xl"
        >
          <img
            src={selectedImage.imageUrl}
            alt={selectedImage.name}
            className="max-h-[72vh] w-full object-contain"
          />
        </Dialog>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-semibold tracking-tight text-[var(--ink)]">
          Pricebook
        </h1>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {!loading && catalogSection !== "templates" && (<div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setCategoryEdit(blankCategory());
                setItemEdit(null);
              }}
              disabled={!canWrite || archived}
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--accent)] disabled:opacity-50"
            >
              Add category
            </button>
            <button
              type="button"
              onClick={() => {
                setNewItemChoice("types");
              }}
              disabled={!canWrite || archived || !categories.length}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-50"
            >
              <Plus size={18} aria-hidden="true" /> Add item
            </button>
          </div>)}
        <button
          type="button"
          aria-label="Pricebook settings"
          title="Pricebook settings"
          disabled={loading}
          onClick={() => {
            setRulesError("");
            setRulesOpen(true);
          }}
          className="rounded-lg border border-[var(--line)] bg-[var(--paper)] p-2 text-[var(--ink)] hover:bg-[var(--wash)] disabled:opacity-50"
        >
          <Settings size={20} aria-hidden="true" />
        </button>
        </div>
      </div>
      {!loading && catalogSection !== "templates" && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            disabled={catalogSection === "categories" && !open && !searching && trade === "all" && !browseAllCategories}
            onClick={() => catalogSection !== "categories" ? setCatalogSection("categories") : searching ? clearResults() : directItemsOnly ? setDirectItemsOnly(false) : open ? openCategory(open.parentCategoryId ?? null) : showTrades()}
            className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink)] disabled:opacity-40"
          >
            <ArrowLeft size={16} aria-hidden="true" /> Back
          </button>
        </div>
      )}
      {loading ? (
        <div
          role="status"
          aria-live="polite"
          className="space-y-4"
          aria-label="Loading Pricebook"
        >
          <div className="h-20 animate-pulse rounded-lg border border-[var(--line)] bg-[var(--wash)]" />
          <div className="h-12 w-52 animate-pulse rounded-lg bg-[var(--wash)]" />
          <div className="space-y-3">
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                className="h-24 animate-pulse rounded-xl border border-[var(--line)] bg-[var(--paper)]"
              />
            ))}
          </div>
          <p className="text-sm text-[var(--ink-soft)]">Loading Pricebook…</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 pt-2" role="tablist" aria-label="Pricebook sections">
            {TYPE_TABS.map(tab => <div key={tab.id} className="relative">
              <button type="button" role="tab" aria-selected={catalogSection !== "templates" && typeFilter === tab.id}
              onClick={() => { setTypeFilter(tab.id); setCatalogSection("categories"); openCategory(null); }}
              className={`min-h-10 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] ${catalogSection !== "templates" && typeFilter === tab.id ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)] shadow-sm" : "border-[var(--line-strong)] bg-[var(--wash)] text-[var(--ink)] hover:border-[var(--ink)] hover:bg-[var(--paper)]"}`}>
              {tab.label} <span className="ml-1 text-xs font-medium opacity-65">{tab.id === "all" ? tradeItems.length : tradeItems.filter(item => itemCatalogType(item) === tab.id).length}</span>
              </button>
              <span className="absolute -right-1 -top-2">{attentionBadge(tab.label, tab.id === "all" ? tradeItems : tradeItems.filter(item => itemCatalogType(item) === tab.id), () => {
                setTypeFilter(tab.id); setCatalogSection("categories"); openCategory(null); setFilter("attention"); setShowAllItems(true);
              })}</span>
            </div>)}
            <button type="button" role="tab" aria-selected={catalogSection === "templates"} onClick={() => setCatalogSection("templates")} className={`min-h-10 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] ${catalogSection === "templates" ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)] shadow-sm" : "border-[var(--line-strong)] bg-[var(--wash)] text-[var(--ink)] hover:border-[var(--ink)] hover:bg-[var(--paper)]"}`}>Templates</button>
          </div>
          {catalogSection !== "templates" && <>
            {typeFilter === "equipment" && <button type="button" onClick={() => { setCatalogSection(catalogSection === "equipment" ? "categories" : "equipment"); }} className="text-sm font-semibold text-[var(--accent)]">{catalogSection === "equipment" ? "Browse equipment categories" : "Equipment by brand"}</button>}
            {typeFilter === "adjustment" && <button type="button" onClick={() => setCatalogSection(catalogSection === "discounts" ? "categories" : "discounts")} className="text-sm font-semibold text-[var(--accent)]">{catalogSection === "discounts" ? "Browse adjustment categories" : "Discount and fee breakdown"}</button>}

          </>}
          {newItemChoice && <Dialog title={newItemChoice === "adjustment" ? "Discount or fee" : "Add item"} onClose={() => setNewItemChoice(null)} width="max-w-xl">
            <div className="space-y-4 p-5">
              <h2 className="text-lg font-semibold">{newItemChoice === "adjustment" ? "Choose an adjustment" : "What would you like to add?"}</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {(newItemChoice === "adjustment" ? [
                  { id: "discount", label: "Discount", description: "Reduce an invoice or estimate by an amount or percentage." },
                  { id: "fee", label: "Fee", description: "Add a charge by an amount or percentage." },
                ] : [
                  { id: "adjustment", label: "Discounts & fees", description: "A fixed amount or percentage adjustment." },
                  { id: "equipment", label: "Equipment", description: "Equipment, specifications, warranties, and vendors." },
                  { id: "material", label: "Material", description: "Parts, vendor costs, and material pricing." },
                  { id: "service", label: "Service", description: "Work you perform and its selling price." },
                ]).map(choice => <button key={choice.id} type="button" aria-label={choice.label} className="min-h-24 rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4 text-left hover:border-[var(--accent)] hover:bg-[var(--wash)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]" onClick={() => choice.id === "adjustment" ? setNewItemChoice("adjustment") : startNewPricebookItem(choice.id as "service" | "material" | "equipment" | "fee" | "discount")}><strong className="block text-sm">{choice.label}</strong><span className="mt-1 block text-xs text-[var(--ink-soft)]">{choice.description}</span></button>)}
              </div>
              {newItemChoice === "adjustment" && <QuietButton type="button" onClick={() => setNewItemChoice("types")}>Back to item types</QuietButton>}
            </div>
          </Dialog>}
          {categoryEdit && (
            <Dialog
              title={categoryEdit.id ? "Edit category" : "New category"}
              onClose={() => setCategoryEdit(null)}
              width="max-w-2xl"
            >
              <form
                key={categoryEdit.id || "new-category"}
                onSubmit={(event) => void submitCategory(event)}
                className="grid gap-2 overflow-y-auto p-4 md:grid-cols-2"
              >
                <input
                  required
                  name="name"
                  defaultValue={categoryEdit.name}
                  placeholder="Category or section name"
                  className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
                />
                <select
                  name="kind"
                  defaultValue={categoryEdit.kind}
                  className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
                >
                  {priceBookKinds.map((kind) => (
                    <option key={kind} value={kind}>
                      {kind.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Parent category"
                  name="parentCategoryId"
                  defaultValue={categoryEdit.parentCategoryId || ""}
                  className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
                >
                  <option value="">Top-level category</option>
                  {categoryOptions(
                    roots,
                    categoryEdit.id ? descendantIds(categoryEdit.id) : new Set<string>(),
                  )}
                </select>
                <input
                  name="description"
                  defaultValue={categoryEdit.description || ""}
                  placeholder="Description (optional)"
                  className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
                />
                <input
                  name="imageUrl"
                  defaultValue={categoryEdit.imageUrl || ""}
                  placeholder="Image URL (optional)"
                  className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
                />
                {categoryEdit.id && (
                  <div className="flex flex-wrap items-center gap-2 md:col-span-2">
                    <span className="text-xs text-[var(--ink-soft)]">
                      {imageSourceLabel(categoryEdit.imageSource)}
                    </span>
                    <label className="cursor-pointer rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink)]">
                      {categoryEdit.imageContentUrl ? "Replace image" : "Upload image"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="sr-only"
                        disabled={imageBusy}
                        onChange={(event) => {
                          const image = event.currentTarget.files?.[0];
                          if (image) void uploadPriceBookImage("categories", categoryEdit, image);
                          event.currentTarget.value = "";
                        }}
                      />
                    </label>
                    {categoryEdit.imageSource === "workview_upload" && (
                      <button type="button" disabled={imageBusy} onClick={() => void removePriceBookImage("categories", categoryEdit)} className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)]">
                        Remove WorkView override
                      </button>
                    )}
                  </div>
                )}
                {(categoryEdit.imageContentUrl ?? categoryEdit.imageUrl) && (
                  <PriceBookThumb
                    src={categoryEdit.imageContentUrl ?? categoryEdit.imageUrl}
                    name={categoryEdit.name}
                    className="h-20 w-20 rounded-lg border border-[var(--line)] text-lg"
                    onOpen={(imageUrl) =>
                      setSelectedImage({ name: categoryEdit.name, imageUrl })
                    }
                  />
                )}
                <div className="flex gap-2">
                  <button className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)]">
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setCategoryEdit(null)}
                    className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)]"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </Dialog>
          )}
          {rulesOpen && (
            <Dialog
              title="Pricebook settings"
              onClose={() => setRulesOpen(false)}
              width="max-w-2xl"
            >
              <div className="border-b border-[var(--line)] p-5">
                <QuietButton
                  type="button"
                  disabled={rulesBusy}
                  onClick={() => {
                    setRulesOpen(false);
                    setArchived(!archived);
                  }}
                >
                  {archived ? "Show active" : "View archived"}
                </QuietButton>
              </div>
              <section aria-label="Cash/check pricing" className="border-b border-[var(--line)] p-5">
                <h3 className="mb-4 text-base font-semibold">Cash/check pricing</h3>
                <CashCheckPricingSettings />
              </section>
              {/*
               * The rules the whole book is priced by, on the screen the book is on.
               * They are company settings underneath, so this sends the whole company
               * record back — a partial save would blank the address.
               */}
              {settings && saveSettings && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const data = new FormData(event.currentTarget);
                  setRulesBusy(true);
                  setRulesError("");
                  void saveSettings({
                    ...settings.form,
                    materialMarkupPercent: String(
                      data.get("materialMarkup") || "",
                    ).trim(),
                    afterHoursStartsAt: String(
                      data.get("afterHoursStartsAt") ||
                        settings.form.afterHoursStartsAt,
                    ),
                    afterHoursEndsAt: String(
                      data.get("afterHoursEndsAt") ||
                        settings.form.afterHoursEndsAt,
                    ),
                    jobTypeMultipliers: settings.form.jobTypeMultipliers.map(
                      (jobType) => ({
                        ...jobType,
                        multiplier: String(
                          data.get(`multiplier-${jobType.id}`) || "",
                        ).trim(),
                      }),
                    ),
                  })
                    .then(() => setRulesOpen(false))
                    .catch((error) =>
                      setRulesError(
                        error instanceof Error
                          ? error.message
                          : "We couldn’t save the pricebook settings.",
                      ),
                    )
                    .finally(() => setRulesBusy(false));
                }}
                className="grid gap-x-5 gap-y-4 overflow-y-auto p-5 md:grid-cols-2"
              >
                <PriceBookGroup title="Materials" />
                <PriceBookField
                  label="Markup on cost"
                  hint="A percentage. 55 sells a $10 part for $15.50."
                  wide
                >
                  <input
                    name="materialMarkup"
                    type="number"
                    min="0"
                    step="1"
                    defaultValue={settings.form.materialMarkupPercent}
                    className={pbControl}
                  />
                </PriceBookField>

                <PriceBookGroup title="After hours" />
                <PriceBookField label="Starts at">
                  <input
                    name="afterHoursStartsAt"
                    type="time"
                    defaultValue={settings.form.afterHoursStartsAt}
                    className={pbControl}
                  />
                </PriceBookField>
                <PriceBookField label="Back to normal at">
                  <input
                    name="afterHoursEndsAt"
                    type="time"
                    defaultValue={settings.form.afterHoursEndsAt}
                    className={pbControl}
                  />
                </PriceBookField>
                {settings.form.jobTypeMultipliers.map((jobType) => (
                  <PriceBookField
                    key={jobType.id}
                    label={jobType.name}
                    hint="Times the labor rate. Blank leaves it alone."
                  >
                    <input
                      name={`multiplier-${jobType.id}`}
                      type="number"
                      min="0"
                      step="0.05"
                      defaultValue={jobType.multiplier}
                      className={pbControl}
                    />
                  </PriceBookField>
                ))}
                {!settings.form.jobTypeMultipliers.length && (
                  <p className="text-sm text-[var(--ink-soft)] md:col-span-2">
                    No job types yet, so there is nothing to charge more for.
                  </p>
                )}

                {rulesError && (
                  <p
                    role="alert"
                    className="text-[13px] font-semibold text-[var(--st-alert)] md:col-span-2"
                  >
                    {rulesError}
                  </p>
                )}
                <div className="flex gap-2 md:col-span-2">
                  <button
                    disabled={rulesBusy}
                    className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-60"
                  >
                    {rulesBusy ? "Saving…" : "Save settings"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRulesOpen(false)}
                    className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)]"
                  >
                    Cancel
                  </button>
                </div>
              </form>
              )}
            </Dialog>
          )}
          {itemEdit && (
            <div className="pb-item-widget">
            <Dialog
              title={itemEdit.id ? "Edit item" : "New item"}
              onClose={() => {
                setItemEdit(null);
                setAfterHours(false);
              }}
              width="max-w-3xl"
            >
              <form
                key={itemEdit.id || "new-item"}
                onSubmit={(event) => void submitItem(event)}
                className="pb-item-widget-form"
              >
                <div className="pb-item-widget-product">
                  <div className="pb-item-widget-image">
                    <PriceBookThumb
                      src={itemEdit.imageContentUrl ?? itemEdit.imageUrl}
                      name={itemEdit.name || "Item"}
                      fit="contain"
                      className="h-48 w-28 bg-white text-3xl"
                      onOpen={(imageUrl) => setSelectedImage({ name: itemEdit.name, imageUrl })}
                    />
                    <button type="button" className="pb-item-photo-edit" aria-expanded={itemPanel === "photo"} onClick={() => setItemPanel(itemPanel === "photo" ? null : "photo")}>Edit photo</button>
                  </div>
                  <div className="pb-item-widget-information">
                    <div className="pb-item-widget-summary">
                    {equipmentDetails(itemEdit) && equipmentDisplayName(itemEdit) !== itemEdit.name ? <><h3 className="text-xl font-semibold">{equipmentDisplayName(itemEdit)}</h3><input type="hidden" name="name" value={itemEdit.name} /></> : (
                    <textarea aria-label="Name" placeholder="Item name" required name="name" rows={1} defaultValue={itemEdit.name} className={pbControl}
                      ref={(element) => { if (element) { element.style.height = "auto"; element.style.height = `${element.scrollHeight}px`; } }}
                      onInput={(event) => { event.currentTarget.style.height = "auto"; event.currentTarget.style.height = `${event.currentTarget.scrollHeight}px`; }} />
                    )}
                    <textarea aria-label="Description" name="description" rows={1} defaultValue={itemEdit.description || ""} placeholder="Add a description" className="pb-item-description"
                      ref={(element) => { if (element) { element.style.height = "auto"; element.style.height = `${element.scrollHeight}px`; } }}
                      onInput={(event) => { event.currentTarget.style.height = "auto"; event.currentTarget.style.height = `${event.currentTarget.scrollHeight}px`; }} />
                    <div className="pb-item-category-buttons">
                      {(itemEdit.categoryIds || [itemEdit.categoryId]).map((id) => <button key={id} type="button" title={categoryPath(id)} aria-expanded={itemPanel === "categories"} onClick={() => setItemPanel(itemPanel === "categories" ? null : "categories")}><Badge variant="default" className="pb-category-badge">{equipmentDetails(itemEdit) ? `${equipmentDetails(itemEdit)?.manufacturer || "Equipment"} · ${equipmentDetails(itemEdit)?.type || "Equipment"}` : categories.find((category) => category.id === id)?.name ?? "Category"}</Badge></button>)}
                      {!(itemEdit.categoryIds || [itemEdit.categoryId]).length && <button type="button" onClick={() => setItemPanel("categories")}><Badge variant="default" className="pb-category-badge">Choose category</Badge></button>}
                    </div>
                    </div>
                    <div className="pb-item-widget-prices">
                <PriceBookField
                  label="Item type"
                >
                  <select
                    aria-label="Item type"
                    value={itemEdit.modifierType ?? itemEdit.itemType ?? ""}
                    onChange={(event) => {
                      const value = event.target.value as "" | "service" | "material" | "equipment" | "fee" | "discount";
                      setItemTypeEdited(true);
                      if (value === "material" || value === "equipment") setTaxClass("material");
                      else if (value === "service") setTaxClass("labor");
                      setItemEdit((current) => {
                        if (!current) return current;
                        if (value === "fee" || value === "discount") return {
                          ...current,
                          modifierType: value,
                          modifierAmountType: current.modifierAmountType ?? "fixed",
                          modifierBasisPoints: current.modifierAmountType === "percentage" ? current.modifierBasisPoints : null,
                          itemType: null,
                          unitType: "flat",
                          costCents: null,
                          sourceExpenseAccountName: null,
                          taxable: false,
                        };
                        return { ...current, modifierType: null, modifierAmountType: null, modifierBasisPoints: null, itemType: value || null };
                      });
                    }}
                    className={pbControl}
                  >
                    <option value="">Unassigned</option>
                    <option value="service">Service</option>
                    <option value="material">Material</option>
                    <option value="equipment">Equipment</option>
                    <option value="fee">Fee</option>
                    <option value="discount">Discount</option>
                  </select>
                </PriceBookField>
                {modifierLabel(itemEdit) ? <>
                  <input type="hidden" name="itemType" value="" />
                  <input type="hidden" name="modifierType" value={itemEdit.modifierType ?? ""} />
                  <PriceBookField label="Calculation">
                    <select
                      aria-label="Calculation"
                      name="modifierAmountType"
                      value={itemEdit.modifierAmountType ?? "fixed"}
                      onChange={(event) => setItemEdit((current) => current ? {
                        ...current,
                        modifierAmountType: event.target.value as "fixed" | "percentage",
                        modifierBasisPoints: event.target.value === "percentage" ? current.modifierBasisPoints ?? 0 : null,
                      } : null)}
                      className={pbControl}
                    >
                      <option value="fixed">Fixed amount</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </PriceBookField>
                  {itemEdit.modifierAmountType === "percentage" ? <PriceBookField key="percentage-adjustment" label="Percentage" hint="A percentage of the current invoice subtotal.">
                    <input aria-label="Percentage" name="modifierPercentage" required type="number" min="0" max="100" step="0.01" value={String((itemEdit.modifierBasisPoints ?? 0) / 100)} onChange={(event) => setItemEdit((current) => current ? { ...current, modifierBasisPoints: Math.round((Number(event.target.value) || 0) * 100) } : null)} className={pbControl} />
                  </PriceBookField> : <PriceBookField key="fixed-adjustment" label="Fixed amount">
                    <input aria-label="Fixed amount" name="price" required type="number" min="0" step="0.01" defaultValue={inputFromCents(itemEdit.priceCents)} className={pbControl} />
                  </PriceBookField>}
                  <input type="hidden" name="unitType" value="flat" />
                  <input type="hidden" name="cost" value="" />
                </> : <>
                  <input type="hidden" name="modifierType" value="" />
                  <input type="hidden" name="modifierAmountType" value="" />
                  <input type="hidden" name="modifierPercentage" value="" />
                  <input type="hidden" name="itemType" value={itemEdit.itemType ?? ""} />
                  {taxClass === "labor" ? (
                  <PriceBookField label="Unit price">
                    <input
                      name="price"
                      required
                      type="text"
                      inputMode="decimal"
                      min="0"
                      value={itemPriceInput}
                      onChange={(event) => { setItemPriceInput(event.target.value); setItemEdit((current) => current ? { ...current, priceCents: centsFromInput(event.target.value) } : null); }}
                      className={pbControl}
                    />
                  </PriceBookField>
                ) : (
                  <PriceBookField
                    label="Unit price"
                    hint={
                      configuredMaterialMarkup
                        ? `Calculated from cost + ${configuredMaterialMarkup}% markup.`
                        : "Enter a material cost and set a markup to calculate this price."
                    }
                  >
                    <input
                      aria-label="Unit price"
                      readOnly
                      value={
                        pricedFromCost({ ...itemEdit, taxClass }) !== null
                          ? money(pricedFromCost({ ...itemEdit, taxClass }))
                          : "—"
                      }
                      className={`${pbControl} bg-[var(--wash)] text-[var(--ink-soft)]`}
                    />
                  </PriceBookField>
                )}
                <input type="hidden" name="unitType" value={itemEdit.unitType} />
                <PriceBookField label="Cost">
                  <input
                    name="cost"
                    type="text"
                    inputMode="decimal"
                    min="0"
                    value={itemCostInput}
                    onChange={(event) => {
                      updateItemCost(event.currentTarget.value);
                    }}
                    className={pbControl}
                  />
                </PriceBookField>
                <p className="text-sm text-[var(--ink-soft)]">Markup: {markupPercent(pricedFromCost({ ...itemEdit, taxClass }) ?? itemEdit.priceCents, itemEdit.costCents) === null ? "—" : `${markupPercent(pricedFromCost({ ...itemEdit, taxClass }) ?? itemEdit.priceCents, itemEdit.costCents)}%`}</p>
                </>}
                    </div>
                {modifierLabel(itemEdit) && <>
                  <input type="hidden" name="sku" value={itemEdit.sku ?? ""} />
                  <input type="hidden" name="vendorName" value={itemEdit.vendorName ?? ""} />
                </>}
                  </div>
                </div>
                {!supportsItemDetails && <p className="rounded-lg bg-[var(--wash)] p-3 text-xs text-[var(--ink-soft)]">The layout is ready to review. Saving new vendor and equipment details requires the API update.</p>}
                {!modifierLabel(itemEdit) && (vendorOptions ? <PriceBookVendorCosts item={itemEdit} vendors={vendorOptions} costs={itemEdit.vendorCosts ?? []} defaultCostCents={itemEdit.costCents} preferredVendor={itemEdit.vendorName ? { name: itemEdit.vendorName, partNumber: pricebookPartReference(itemEdit)?.vendors.find(vendor => vendor.name.trim().toLowerCase() === itemEdit.vendorName?.trim().toLowerCase())?.partNumber } : undefined} onChange={(vendorCosts) => {
                  const primary = vendorCosts.find(cost => cost.isPrimary && cost.vendorId && cost.active !== false);
                  const previous = itemEdit.vendorCosts?.find(cost => cost.isPrimary && cost.active !== false);
                  const primaryName = vendorOptions.find(vendor => vendor.id === primary?.vendorId)?.name;
                  const primaryChanged = previous
                    ? primary?.vendorId !== previous.vendorId || primary?.costCents !== previous.costCents
                    : primaryName?.trim().toLowerCase() !== itemEdit.vendorName?.trim().toLowerCase() || primary?.costCents !== itemEdit.costCents;
                  if (primary && primaryChanged) updateItemCost(currencyInputFromCents(primary.costCents));
                  setItemEdit(current => current ? { ...current, vendorCosts, vendorName: primary ? vendorOptions.find(vendor => vendor.id === primary.vendorId)?.name ?? current.vendorName : null } : null);
                }} /> : <section className="pb-vendor-section"><h4>Vendors</h4><PriceBookPartFields item={itemEdit} vendors={vendors} /><p className="text-xs text-[var(--ink-soft)]">Vendor-specific costs will be available after the API update.</p></section>)}
                <PricebookEquipmentDetails item={itemEdit} onChange={(details) => setItemEdit(current => current ? { ...current, equipmentDetails: details } : null)} />
                {itemEdit.modifierType === "discount" ? <div className="rounded-lg bg-[var(--wash)] p-3 text-sm text-[var(--ink-soft)]">
                  <strong className="text-[var(--ink)]">Accounting</strong><br />This discount uses your company-wide QuickBooks discount account. It does not need an income or expense account on the item.
                  <input type="hidden" name="sourceIncomeAccountName" value="" />
                  <input type="hidden" name="sourceExpenseAccountName" value="" />
                </div> : <div className="grid gap-3 md:grid-cols-2">
                  <PriceBookField label="Income account" hint={itemEdit.modifierType === "fee" ? "Required: this fee increases invoice revenue." : "The accounting account that receives revenue from this item."}>
                    <select required={itemEdit.modifierType === "fee"} name="sourceIncomeAccountName" value={itemEdit.sourceIncomeAccountName ?? ""} onChange={(event) => setItemEdit((current) => current ? { ...current, sourceIncomeAccountName: event.target.value || null } : null)} className={pbControl}>
                      <option value="">Choose an income account</option>
                      {accountingAccounts.filter((account) => account.accountKind === "income").map((account) => <option key={`income:${account.name}`} value={account.name}>{account.name}</option>)}
                    </select>
                  </PriceBookField>
                  {itemEdit.modifierType === "fee" ? <input type="hidden" name="sourceExpenseAccountName" value="" /> : <PriceBookField label="Expense account" hint="The expense or COGS account for purchasing this item.">
                    <select name="sourceExpenseAccountName" value={itemEdit.sourceExpenseAccountName ?? ""} onChange={(event) => setItemEdit((current) => current ? { ...current, sourceExpenseAccountName: event.target.value || null } : null)} className={pbControl}>
                      <option value="">Choose an expense account</option>
                      {accountingAccounts.filter((account) => account.accountKind === "expense").map((account) => <option key={`expense:${account.name}`} value={account.name}>{account.name}</option>)}
                    </select>
                  </PriceBookField>}
                </div>}
                <label className="pricebook-tax-toggle">
                  <input
                    type="checkbox"
                    role="switch"
                    name="taxable"
                    checked={itemEdit.taxable}
                    onChange={(event) => setItemEdit((current) => current ? { ...current, taxable: event.target.checked } : null)}
                  />
                  <span className="pricebook-tax-toggle-control" aria-hidden="true" />
                  <span>
                    <strong>Taxable</strong>
                    <small>Apply sales tax when this item is used on an invoice.</small>
                  </span>
                </label>
                <input type="hidden" name="memberPrice" value={inputFromCents(itemEdit.memberPriceCents)} />
                <input type="hidden" name="addOnPrice" value={inputFromCents(itemEdit.addOnPriceCents)} />
                <input type="hidden" name="taxClass" value={taxClass} />
                <div hidden={itemPanel !== "categories"} className="pb-item-widget-section">
                  <PriceBookCategoryPicker expanded categories={categories} selectedIds={itemEdit.categoryIds || [itemEdit.categoryId]} path={categoryPath} onChange={(categoryIds) => setItemEdit((current) => current ? { ...current, categoryIds } : null)} />
                </div>
                <details open hidden={itemPanel !== "photo"} className="pb-item-widget-section">
                  <summary className="cursor-pointer text-sm font-semibold">Photo</summary>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                <PriceBookField
                  label="Photo"
                  hint="Paste an image link or upload a photo below."
                >
                  <input
                    name="imageUrl"
                    defaultValue={itemEdit.imageUrl || ""}
                    className={pbControl}
                  />
                </PriceBookField>
                {itemEdit.id && (
                  <div className="flex flex-wrap items-center gap-2 md:col-span-2">
                    <span className="text-xs text-[var(--ink-soft)]">
                      {imageSourceLabel(itemEdit.imageSource)}
                    </span>
                    <label className="cursor-pointer rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink)]">
                      {itemEdit.imageContentUrl ? "Replace image" : "Upload image"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="sr-only"
                        disabled={imageBusy}
                        onChange={(event) => {
                          const image = event.currentTarget.files?.[0];
                          if (image) void uploadPriceBookImage("items", itemEdit, image);
                          event.currentTarget.value = "";
                        }}
                      />
                    </label>
                    {itemEdit.imageSource === "workview_upload" && (
                      <button type="button" disabled={imageBusy} onClick={() => void removePriceBookImage("items", itemEdit)} className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)]">
                        Remove WorkView override
                      </button>
                    )}
                  </div>
                )}
                {(itemEdit.imageContentUrl ?? itemEdit.imageUrl) && (
                  <PriceBookThumb
                    src={itemEdit.imageContentUrl ?? itemEdit.imageUrl}
                    name={itemEdit.name}
                    fit="contain"
                    className="h-24 w-24 bg-white text-lg"
                    onOpen={(imageUrl) =>
                      setSelectedImage({ name: itemEdit.name, imageUrl })
                    }
                  />
                )}

                  </div>
                </details>
                {itemSaveError && <p role="alert" className="text-sm text-red-700 md:col-span-2">{itemSaveError}</p>}
                <div className="pb-item-widget-footer">
                  {canWrite && itemEdit.id && <button type="button" disabled={itemSaving} className="order-last mr-auto text-sm font-semibold text-red-700 disabled:opacity-50" onClick={async () => {
                    setItemSaving(true); setItemSaveError("");
                    try {
                      await save(`/api/v1/workspace/pricebook/items/${itemEdit.id}/archive`, "POST", {});
                      setItemEdit(null); setAfterHours(false);
                    } catch (cause) { setItemSaveError(cause instanceof Error ? cause.message : "Unable to archive item."); }
                    finally { setItemSaving(false); }
                  }}>Archive item</button>}

                  <button disabled={itemSaving} className="rounded-lg bg-[var(--ink)] px-5 py-2.5 text-sm font-semibold text-[var(--paper)] disabled:opacity-50">
                    {itemSaving ? "Saving…" : "Save item"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setItemEdit(null);
                      setAfterHours(false);
                    }}
                    className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)]"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </Dialog>
            </div>
          )}
          {catalogSection === "templates" ? (<PricebookTemplates canWrite={canWrite} items={items} categories={categories} onOpenEstimate={onOpenEstimate} />) : catalogSection === "discounts" ? (
            <DiscountFeeCatalog key={String(archived)} items={typeItems} categories={categories} renderItem={(item) => itemRow({ id: item.categoryId }, item, false)} onClose={() => setCatalogSection("categories")} />
          ) : catalogSection === "equipment" ? (
            <EquipmentCatalog
              key={String(archived)}
              items={typeItems}
              categories={categories}
              renderItem={(item) => itemRow({ id: item.categoryId }, item, false)}
              onClose={() => setCatalogSection("categories")}
            />
          ) : (
          <div className="space-y-5">
                {(open || searching || trade !== "all" || browseAllCategories) && (
                  <nav aria-label="Pricebook category path" className="flex flex-wrap items-center gap-2 text-sm">
                    {hasTradeBranches && <>
                      <button type="button" onClick={showTrades} className="font-semibold text-[var(--ink-soft)] hover:text-[var(--ink)]">All trades</button>
                      <span aria-hidden="true" className="text-[var(--ink-faint)]">/</span>
                    </>}
                    <button
                      type="button"
                      onClick={() => openCategory(null)}
                      className="font-semibold text-[var(--ink-soft)] transition hover:text-[var(--ink)]"
                    >
                      {trade !== "all" ? tradeLabel : "All categories"}
                    </button>
                    {path.map((node, index) => (
                      <span
                        key={node.id}
                        className="flex items-center gap-2 text-[var(--ink-soft)]"
                      >
                        <span className="text-[var(--ink-faint)]">/</span>
                        <button
                          type="button"
                          onClick={() => openCategory(node.id)}
                          className={
                            index === path.length - 1
                              ? "font-semibold text-[var(--ink)]"
                              : "font-semibold transition hover:text-[var(--ink)]"
                          }
                        >
                          {node.name}
                        </button>
                      </span>
                    ))}
                    {directItemsOnly && <span aria-current="page">/ General items</span>}
                    {open && canWrite && !archived && (
                      <span className="ml-auto flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryEdit({
                              ...blankCategory(),
                              parentCategoryId: open.id,
                              kind: open.kind,
                            });
                            setItemEdit(null);
                          }}
                          className="text-sm font-semibold text-[var(--accent)]"
                        >
                          Add section
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryEdit(open);
                            setItemEdit(null);
                          }}
                          className="text-sm font-semibold text-[var(--accent)]"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            void save(
                              `/api/v1/workspace/pricebook/categories/${open.id}/archive`,
                              "POST",
                              {},
                            )
                          }
                          className="text-sm font-semibold text-[var(--accent)]"
                        >
                          Archive
                        </button>
                      </span>
                    )}
                  </nav>
                )}
            <div className="w-full max-w-sm">
              <input value={search} onChange={event => { setSearch(event.target.value); setSearchScope("all"); setFilter("all"); setShowAllItems(false); }}
                placeholder="Search items or categories" aria-label="Search the pricebook"
                className="min-h-10 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm outline-none focus:border-[var(--line-strong)]" />
            </div>
            {!atTradePicker && <h2 className="text-xl font-semibold text-[var(--ink)]">{directItemsOnly ? `General items in ${open?.name}` : open?.name ?? (trade !== "all" ? `${tradeLabel} categories` : typeCategoryLabel)}</h2>}
            {atTradePicker && <section aria-label="Browse by trade" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {tradeCards.map(({ id, label, description, Icon }) => {
                const cardItems = items.filter(item => (typeFilter === "all" || itemCatalogType(item) === typeFilter) && (id === "all" ||
                  (item.categoryIds?.length ? item.categoryIds : [item.categoryId]).some(categoryId =>
                    (categoryPaths.get(categoryId) ?? "").split(" / ").some(name => id === "hvac" ? /^hvac\b/i.test(name) : /^plumbing\b/i.test(name)))));
              return <div key={id} className="relative"><button type="button" aria-label={id === "all" ? "Browse all categories" : `Browse ${label}`} onClick={() => chooseTrade(id)}
                  className="group flex min-h-40 w-full flex-col items-start rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-5 text-left transition-colors hover:border-[var(--line-strong)] hover:bg-[var(--wash)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]">
                  <span className="mb-4 grid h-12 w-12 place-items-center rounded-xl bg-[var(--wash)] text-[var(--accent)]"><Icon size={25} strokeWidth={1.5} aria-hidden="true" /></span>
                  <span className="flex w-full items-center justify-between gap-3"><span className="text-xl font-semibold text-[var(--ink)]">{label}</span><ArrowRight size={18} aria-hidden="true" className="text-[var(--ink-soft)]" /></span>
                  <span className="mt-1 text-sm text-[var(--ink-soft)]">{description}</span>
                  <span className="mt-3 text-xs font-medium text-[var(--ink-faint)]">{cardItems.length} {cardItems.length === 1 ? "item" : "items"}</span>
                </button>
                  <div className="absolute -top-2 right-3">{attentionBadge(label, cardItems, () => { chooseTrade(id); setFilter("attention"); setShowAllItems(true); })}</div>
                </div>;
              })}
            </section>}
            {open && /estimate.*template/i.test(open.name) && (
              <p className="rounded-lg border border-[var(--line)] bg-[var(--wash)] p-3 text-sm text-[var(--ink-soft)]">
                This folder contains catalog items, not reusable estimates. The folder name does not indicate that estimate templates are available.
              </p>
            )}
            {searching && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-[var(--ink)]">
                  {filter === "attention" && "Missing details · "}{found.length} {found.length === 1 ? "item" : "items"} in {open && searchScope === "category" ? `${open.name} and its sections` : trade !== "all" ? `the ${trade === "hvac" ? "HVAC" : "Plumbing"} pricebook` : typeFilter === "all" ? "the entire Pricebook" : `all ${typeCategoryLabel.toLowerCase()}`}
                </p>
                <button
                  type="button"
                  onClick={clearResults}
                  className="text-sm font-semibold text-[var(--accent)]"
                >
                  {filter === "attention" ? "Back to categories" : "Clear"}
                </button>
              </div>
            )}
            {searching ? (
              <div className="space-y-5">
                {foundCategories.length > 0 && (
                  <section aria-label="Matching categories" className="space-y-3">
                    <h3 className="text-sm font-semibold text-[var(--ink)]">Categories · {foundCategories.length}</h3>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{foundCategories.map(category => tile(category, true))}</div>
                  </section>
                )}
              {found.length ? (
                <section aria-label="Matching items" className="space-y-3">
                {term && <h3 className="text-sm font-semibold text-[var(--ink)]">Items · {found.length}</h3>}
                <div className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
                  <div className="divide-y divide-[var(--line)]">
                    {found.map((item) =>
                      itemRow({ id: item.categoryId }, item),
                    )}
                  </div>
                </div>
                </section>
              ) : !foundCategories.length ? (
                <p className="rounded-lg border border-dashed border-[var(--line)] px-6 py-10 text-center text-sm text-[var(--ink-soft)]">
                  No items match your search.
                </p>
              ) : null}
              </div>
            ) : (
              <>
                {!atTradePicker && tiles.length > 0 && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {tiles.map(category => tile(category))}
                    {open && generalItems.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setDirectItemsOnly(true)}
                        className="flex items-center justify-between gap-3 rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4 text-left hover:bg-[var(--wash)]"
                      >
                        <span>
                          <span className="block text-sm font-semibold">General items</span>{" "}
                          <span className="mt-1 block text-xs text-[var(--ink-soft)]">{generalItems.length} catalog items · No subcategory</span>
                        </span>
                        <ArrowRight size={16} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                )}
                {open && !tiles.length && openItems.length > 0 && (
                  <div className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
                    <div className="divide-y divide-[var(--line)]">
                      {openItems.map((item) => itemRow(open, item))}
                    </div>
                  </div>
                )}
                {open && !openItems.length && !tiles.length && (
                  <p className="rounded-lg border border-dashed border-[var(--line)] px-6 py-10 text-center text-sm text-[var(--ink-soft)]">
                    No {archived ? "archived" : "active"} items in this section.
                  </p>
                )}
                {!atTradePicker && !open && uncategorizedItems.length > 0 && <section aria-label="Items without a category" className="space-y-2"><h3 className="text-sm font-semibold">Uncategorized items</h3><div className="divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">{uncategorizedItems.map(item => itemRow({ id: item.categoryId }, item))}</div></section>}
                {typeFilter !== "all" && !open && !typeItems.length && <p className="rounded-lg border border-dashed border-[var(--line)] p-6 text-sm text-[var(--ink-soft)]">No {TYPE_TABS.find(tab => tab.id === typeFilter)?.label.toLowerCase()} in this catalog.</p>}
                {!categories.length && (
                  <div className="rounded-lg border border-dashed border-[var(--line)] bg-[var(--paper)] px-6 py-14 text-center text-sm text-[var(--ink-soft)]">
                    {archived
                      ? "No archived pricebook records."
                      : "Create a category to start your pricebook."}
                  </div>
                )}
              </>
            )}
          </div>
          )}
        </>
      )}
    </div>
  );
}

export function Commercial({
  items,
  summary,
  type,
  filter,
  total,
  page = 1,
  pageSize,
  search = "",
  setSearch,
  setPage,
  setType,
  setFilter,
  openJob,
  openInvoice = (invoiceId, jobId) => openJob(jobId, invoiceId),
  openEstimate,
  canRetryQuickBooks = false,
  openPurchaseOrder,
  activeSection,
  onSectionChange,
}: {
  items: CommercialItem[];
  summary: CommercialSummary | null;
  type: "invoices" | "estimates";
  filter: CommercialFilter;
  total?: number;
  page?: number;
  pageSize?: number;
  search?: string;
  setSearch?: (value: string) => void;
  setPage?: (page: number) => void;
  setType: (type: "invoices" | "estimates") => void;
  setFilter: (filter: CommercialFilter) => void;
  openJob: (jobId: string, documentId?: string) => void;
  openInvoice?: (invoiceId: string, jobId: string) => void;
  openEstimate?: (estimateId: string, jobId?: string) => void;
  canRetryQuickBooks?: boolean;
  openPurchaseOrder?: (purchaseOrderId: string) => void;
  activeSection?: AccountingSection;
  onSectionChange?: (section: AccountingSection) => void;
}) {
  const [section, setSectionState] = useState<AccountingSection>(activeSection ?? "documents");
  useEffect(() => { setSectionState(activeSection ?? "documents"); }, [activeSection]);
  const setSection = (next: AccountingSection) => { setSectionState(next); onSectionChange?.(next); };
  const [pendingSoldWork, setPendingSoldWork] = useState<Array<{ approvalId: string; estimateId: string; jobId: string | null; title: string | null; customerName: string; approvedAmountCents: number; approvedAt: string; method: "onsite" | "phone" | "remote" }>>([]);
  useEffect(() => {
    if (type !== "estimates") return;
    let cancelled = false;
    void data<{ items: typeof pendingSoldWork }>("/api/v1/workspace/estimate-fulfillment/pending")
      .then(({ items }) => { if (!cancelled) setPendingSoldWork(items); })
      .catch(() => { if (!cancelled) setPendingSoldWork([]); });
    return () => { cancelled = true; };
  }, [type]);
  const invoice = type === "invoices";
  const showDocuments = (next: "invoices" | "estimates") => { setSection("documents"); if (type !== next) { setType(next); setFilter(next === "invoices" ? "attention" : "open"); } };
  const quickBooks = !["documents", "payments"].includes(section);
  const header = <header className="accounting-page-header"><div className="accounting-page-title"><h1>Accounting</h1></div><nav aria-label="Accounting sections" className="accounting-tabs">{[
    { label: "Invoices", active: section === "documents" && invoice, click: () => showDocuments("invoices") },
    { label: "Estimates", active: section === "documents" && !invoice, click: () => showDocuments("estimates") },
    { label: "Payments", active: section === "payments", click: () => setSection("payments") },
    { label: "QuickBooks", active: quickBooks, click: () => setSection("invoice_reconciliation") },
  ].map(tab => <button key={tab.label} type="button" aria-current={tab.active ? "page" : undefined} onClick={tab.click} className="accounting-tab">{tab.label}</button>)}</nav></header>;
  const syncTabs = <nav aria-label="QuickBooks sync sections" className="mt-4 flex flex-wrap gap-2"><QuietButton type="button" onClick={() => setSection("invoice_reconciliation")}>Invoices & payments</QuietButton><QuietButton type="button" onClick={() => setSection("purchase_order_reconciliation")}>Purchase order bills</QuietButton></nav>;
  if (section === "payments") return <section aria-label="Accounting workspace" className="accounting-workspace">{header}<div className="mt-5"><AccountingPayments embedded backToDocuments={() => setSection("documents")} openInvoice={openInvoice} /></div></section>;
  if (section === "invoice_reconciliation") return <section aria-label="Accounting workspace" className="accounting-workspace">{header}{syncTabs}<ReconciliationWorkspace embedded canRetryQuickBooks={canRetryQuickBooks} backToDocuments={() => setSection("documents")} openPurchaseOrders={() => setSection("purchase_order_reconciliation")} openInvoice={openInvoice} /></section>;
  if (section === "purchase_orders" || section === "purchase_order_reconciliation") return <section aria-label="Accounting workspace" className="accounting-workspace">{header}{syncTabs}<PurchaseOrdersReconciliationWorkspace embedded canRetryQuickBooks={canRetryQuickBooks} backToDocuments={() => setSection("documents")} backToPurchaseOrders={() => setSection("purchase_order_reconciliation")} openPurchaseOrder={openPurchaseOrder} /></section>;
  const isPlaceholder = (item: CommercialItem) =>
    invoice && item.totalCents === 0;
  const isOverdue = (item: CommercialItem) =>
    invoice &&
    !!item.dueDate &&
    item.balanceDueCents! > 0 &&
    new Date(item.dueDate).getTime() < Date.now();
  const serverPaged = typeof total === "number";
  const tileFiltered = serverPaged
    ? items
    : items.filter((item) => {
        if (invoice) {
          if (filter === "review") return isPlaceholder(item);
          if (filter === "paid")
            return !isPlaceholder(item) && (item.balanceDueCents ?? 0) === 0;
          if (filter === "overdue") return !isPlaceholder(item) && isOverdue(item);
          return !isPlaceholder(item) && (item.balanceDueCents ?? 0) > 0;
        }
        if (filter === "approved") return item.isApprovedWork === true;
        if (filter === "lost") return item.isLost === true;
        if (filter === "history")
          return !["draft", "sent", "accepted"].includes(item.status);
        return item.isFollowUp === true;
      });
  const needle = search.trim().toLowerCase();
  const shown =
    needle && !serverPaged
      ? tileFiltered.filter((item) =>
          [
            item.customerName,
            String(item.jobNumber),
            item.displayNumber ?? "",
            item.title ?? "",
            item.address ?? "",
          ]
            .join(" ")
            .toLowerCase()
            .includes(needle),
        )
      : tileFiltered;
  const pageCount =
    serverPaged && pageSize
      ? Math.max(1, Math.ceil(total / pageSize))
      : 1;
  const visibleStatus = (item: CommercialItem) =>
    isPlaceholder(item)
      ? "import review"
      : isOverdue(item)
        ? "overdue"
        : invoice
          ? invoicePresentation({
              status: item.status,
              totalCents: item.totalCents,
              balanceDueCents: item.balanceDueCents ?? item.totalCents,
              paidCents: item.paidCents,
              hasInvoiceSignature: item.hasInvoiceSignature,
              jobStatus: item.jobStatus,
              fulfillment: item.fulfillment,
              estimateDisplayNumber: item.estimateDisplayNumber,
            }).label
          : estimatePresentation({
              status: item.status,
              optionsManaged: item.pendingApprovalCount !== undefined || item.isApprovedWork,
              pendingApprovalCount: item.pendingApprovalCount,
              approvalCount: item.isApprovedWork ? 1 : 0,
            });
  const select = (
    nextType: "invoices" | "estimates",
    nextFilter: CommercialFilter,
  ) => {
    setType(nextType);
    setFilter(nextFilter);
  };
  return (
    <section aria-label="Accounting workspace" className="accounting-workspace">
      {header}
      {invoice && <div className="mt-4"><QuietButton type="button" onClick={() => select("invoices", "review")}>$0 Invoices Needing Review</QuietButton></div>}
      <div className={`mt-5 grid gap-3 sm:grid-cols-2 ${invoice ? "" : "lg:grid-cols-3"}`}>
        {invoice ? <>
        <WorkTile
          active={invoice && filter === "attention"}
          label="Open invoices"
          value={money(summary?.unpaidInvoiceValueCents)}
          note=""
          tone="amber"
          onClick={() => select("invoices", "attention")}
        />
        <WorkTile
          active={invoice && filter === "overdue"}
          label="Overdue invoices"
          value={money(summary?.overdueValueCents)}
          note=""
          tone="rose"
          onClick={() => select("invoices", "overdue")}
        />
        </> : <>
        <WorkTile
          active={!invoice && filter === "open"}
          label="Estimates to follow up"
          value={money(summary?.openEstimateValueCents)}
          note={`${summary?.openEstimateCount ?? 0} jobs`}
          tone="blue"
          onClick={() => select("estimates", "open")}
        />
        <WorkTile
          active={!invoice && filter === "approved"}
          label="Approved estimates"
          value={money(summary?.acceptedEstimateValueCents)}
          note=""
          tone="emerald"
          onClick={() => select("estimates", "approved")}
        />
        <WorkTile
          active={!invoice && filter === "lost"}
          label="Lost estimates"
          value={money(summary?.lostEstimateValueCents)}
          note={`${summary?.lostEstimateCount ?? 0} jobs`}
          tone="slate"
          onClick={() => select("estimates", "lost")}
        />
        </>}
      </div>
      {!invoice && filter === "approved" && pendingSoldWork.length > 0 && <section className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-5"><div className="flex items-center justify-between gap-3"><div><h2 className="font-semibold text-emerald-950">Approved estimates</h2><p className="mt-1 text-sm text-emerald-900">{pendingSoldWork.length} approved {pendingSoldWork.length === 1 ? "option needs" : "options need"} a current or future job. </p></div></div><div className="mt-4 divide-y divide-emerald-200 rounded-lg border border-emerald-200 bg-white">{pendingSoldWork.map((item) => <div key={item.approvalId} className="flex items-center justify-between gap-4 px-4 py-3 text-sm"><span><b>{item.customerName}</b>{item.title ? ` · ${item.title}` : ""}<small className="ml-2 text-[var(--ink-soft)]">{item.method} · {date(item.approvedAt)}</small></span><div className="flex shrink-0 gap-2"><button type="button" onClick={() => openEstimate?.(item.estimateId, item.jobId ?? undefined)} className="rounded-lg bg-[var(--ink)] px-3 py-2 text-xs font-semibold text-[var(--paper)]">Open job</button>{item.jobId && <QuietButton type="button" onClick={() => openJob(item.jobId!)}>Open job</QuietButton>}</div></div>)}</div></section>}
      <section className="accounting-widget mt-5">
        <AccountingWidgetHeading>{invoice ? "Invoices" : "Estimates"}</AccountingWidgetHeading>
        <div className="border-b border-[var(--line)] px-5 py-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <input
              value={search}
              onChange={(event) => setSearch?.(event.target.value)}
              placeholder={
                invoice
                  ? "Customer, job, or invoice number"
                  : "Customer, job, or estimate"
              }
              aria-label="Search accounting records"
              className="w-full max-w-sm rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-1.5 text-sm outline-none focus:border-[var(--line-strong)]"
            />
            <div className="flex justify-end gap-2">
            {invoice && filter === "paid" && (
              <QuietButton
                type="button"
                onClick={() => select("invoices", "attention")}
              >
                ← Back to open balances
              </QuietButton>
            )}
            {invoice && filter !== "paid" && filter !== "review" && (
              <QuietButton
                type="button"
                onClick={() => select("invoices", "paid")}
              >
                Paid history
              </QuietButton>
            )}
            {!invoice && (
              <QuietButton
                type="button"
                onClick={() => select("estimates", "history")}
              >
                Estimate history
              </QuietButton>
            )}
            </div>
          </div>
        </div>
        <div className="divide-y divide-[var(--line)]">
          {shown.map((item) => {
            const openDocument = () =>
              invoice
                ? openInvoice(item.id, item.jobId)
                : openEstimate
                  ? openEstimate(item.id, item.jobId)
                  : openJob(item.jobId, item.id);
            return (
              <article
                key={item.id}
                className="flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-4 transition hover:bg-[var(--wash)]"
              >
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-[var(--wash)] text-sm font-semibold text-[var(--ink)]">
                  {invoice ? "$" : "↗"}
                </div>
                <button
                  onClick={openDocument}
                  className="min-w-[190px] flex-1 text-left"
                >
                  <p className="font-semibold text-[var(--ink)]">
                    {item.customerName}
                  </p>
                  <p className="mt-1 text-xs text-[var(--ink-soft)]">
                    Job #{item.jobNumber} ·{" "}
                    {invoice
                      ? customerDocumentLabel("Invoice", item)
                      : `${customerDocumentLabel("Estimate", item)}${item.title ? ` · ${item.title}` : ""}`}
                  </p>
                </button>
                <div className="min-w-[118px]">
                  <span
                    className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${statusStyle(visibleStatus(item))}`}
                  >
                    {visibleStatus(item)}
                  </span>
                  <p className="mt-2 text-xs text-[var(--ink-faint)]">
                    {invoice && item.dueDate
                      ? `Due ${date(item.dueDate)}`
                      : `${invoice ? "Issued" : "Created"} ${date(item.createdAt)}`}
                  </p>
                </div>
                <div className="min-w-[130px] text-right">
                  <p className="text-sm font-semibold text-[var(--ink)]">
                    {invoice
                      ? money(item.balanceDueCents)
                      : money(item.totalCents)}
                  </p>
                  <p className="mt-1 text-xs text-[var(--ink-soft)]">
                    {invoice
                      ? `of ${money(item.totalCents)} invoice`
                      : "proposed total"}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={openDocument}
                    className="rounded-lg bg-[var(--ink)] px-3 py-2 text-xs font-semibold text-[var(--paper)]"
                  >
                    {invoice ? "Open invoice" : "Open estimate"}
                  </button>
                  <QuietButton type="button" onClick={() => openJob(item.jobId)}>
                    Open job
                  </QuietButton>
                  {item.projectId && <Link to={`/dashboard?view=projects&projectId=${encodeURIComponent(item.projectId)}`} className="rounded-lg border border-[var(--line)] px-3 py-2 text-xs font-semibold text-[var(--accent)]">{item.projectName || "Open project"}</Link>}
                </div>
              </article>
            );
          })}
          {!shown.length && (
            <div className="px-5 py-14 text-center text-sm text-[var(--ink-soft)]">
              Nothing to review in this section.
            </div>
          )}
        </div>
        {serverPaged && pageSize && total > pageSize && (
          <div className="flex items-center justify-between gap-3 border-t border-[var(--line)] px-5 py-3">
            <p className="text-xs text-[var(--ink-soft)]">
              {(page - 1) * pageSize + 1}&ndash;
              {Math.min(page * pageSize, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Previous accounting page"
                disabled={page <= 1}
                onClick={() => setPage?.(page - 1)}
                className="rounded-lg border border-[var(--line)] px-2.5 py-1 text-sm font-semibold text-[var(--ink)] disabled:opacity-40"
              >
                &larr;
              </button>
              <button
                type="button"
                aria-label="Next accounting page"
                disabled={page >= pageCount}
                onClick={() => setPage?.(page + 1)}
                className="rounded-lg border border-[var(--line)] px-2.5 py-1 text-sm font-semibold text-[var(--ink)] disabled:opacity-40"
              >
                &rarr;
              </button>
            </div>
          </div>
        )}
      </section>
    </section>
  );
}
type AccountingPurchaseOrder = {
  id: string;
  displayNumber: string | null;
  status: string;
  sourceImported: number;
  sourceStatus: string | null;
  sentAt: string | null;
  accountingDate: string;
  amountCents: number;
  freightCents: number;
  taxCents: number;
  vendorName: string;
  jobId: string | null;
  jobNumber: string | null;
  quickBooksBillStatus: string | null;
  quickBooksBillError: string | null;
  quickBooksBillId: string | null;
};
type QuickBooksBillComparisonResult = {
  purchaseOrderId: string;
  purchaseOrderDisplayNumber: string;
  quickBooksBillId: string | null;
  quickBooksBillUrl: string | null;
  status: "verified" | "needs_manual_fix" | "unable_to_compare";
  reasons: string[];
};

function PurchaseOrdersWorkspace({ backToDocuments, openReconciliation, openPurchaseOrder }: { backToDocuments: () => void; openReconciliation: () => void; openPurchaseOrder?: (id: string) => void }) {
  const [items, setItems] = useState<AccountingPurchaseOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [purchaseOrderStatus, setPurchaseOrderStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadSequence = useRef(0);
  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: "25", status: "all", purchaseOrderStatus, source, search });
      const result = await data<{ items: AccountingPurchaseOrder[]; total: number }>(`/api/v1/workspace/accounting/purchase-orders?${params}`);
      if (sequence === loadSequence.current) { setItems(result.items); setTotal(result.total); setError(""); }
    } catch { if (sequence === loadSequence.current) setError("We couldn’t load purchase orders."); }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [page, purchaseOrderStatus, search, source]);
  useEffect(() => { void load(); }, [load]);
  const pageCount = Math.max(1, Math.ceil(total / 25));
  const statusLabel = (status: string) => status.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
  return <>
    <header className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
      <div className="flex flex-wrap items-start justify-between gap-4 px-5 pt-5"><div><h1 className="text-3xl font-semibold tracking-tight text-[var(--ink)]">Accounting</h1><p className="mt-1 text-sm text-[var(--ink-soft)]">Invoices, estimates, and purchase orders.</p></div><QuietButton type="button" onClick={openReconciliation}>Audit QuickBooks</QuietButton></div>
      <nav aria-label="Accounting sections" className="mt-5 flex gap-1 border-t border-[var(--line)] px-3 pt-2"><button type="button" onClick={backToDocuments} className="rounded-t-lg px-4 py-2 text-sm font-semibold text-[var(--ink-soft)] hover:bg-[var(--wash)] hover:text-[var(--ink)]">Invoices & estimates</button><button type="button" aria-current="page" className="rounded-t-lg border border-b-0 border-[var(--line)] bg-[var(--wash)] px-4 py-2 text-sm font-semibold text-[var(--ink)]">Purchase orders</button></nav>
    </header>
    <section className="mt-5 overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--line)] p-4"><input aria-label="Search purchase orders" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="PO number, vendor, or job" className="w-full max-w-sm rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm" /><select aria-label="Purchase order source" value={source} onChange={(event) => { setSource(event.target.value); setPage(1); }} className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"><option value="workview">WorkView purchase orders</option><option value="imported">Imported ServiceTitan purchase orders</option><option value="all">All sources</option></select><select aria-label="Purchase order status" value={purchaseOrderStatus} onChange={(event) => { setPurchaseOrderStatus(event.target.value); setPage(1); }} className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"><option value="all">All purchase orders</option><option value="sent">Sent</option><option value="partially_received">Partially received</option><option value="received">Received</option><option value="cancelled">Cancelled</option></select></div>
      {error && <p role="alert" className="px-4 pt-4 text-sm text-[var(--accent)]">{error}</p>}
      {loading ? <p className="px-5 py-14 text-center text-sm text-[var(--ink-soft)]">Loading purchase orders…</p> : !items.length ? <p className="px-5 py-14 text-center text-sm text-[var(--ink-soft)]">No purchase orders match this search.</p> : <div className="divide-y divide-[var(--line)]">{items.map((item) => <a key={item.id} href={`/purchase-orders/${encodeURIComponent(item.id)}`} onClick={(event) => { event.preventDefault(); openPurchaseOrder?.(item.id); }} className="flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-4 transition hover:bg-[var(--wash)]"><div className="grid h-10 w-10 place-items-center rounded-lg bg-[var(--wash)] text-sm font-semibold text-[var(--ink)]">PO</div><div className="min-w-[190px] flex-1"><p className="font-semibold text-[var(--accent)]">{item.displayNumber ?? "Purchase order"}</p><p className="mt-1 text-xs text-[var(--ink-soft)]">{item.vendorName}{item.jobNumber ? ` · Job #${item.jobNumber}` : " · No job"}</p></div><div className="min-w-[168px]"><div className="flex flex-wrap gap-1.5"><span className="rounded-md bg-[var(--wash)] px-2 py-0.5 text-[11px] font-medium text-[var(--ink-soft)]">{item.sourceImported ? "Imported from ServiceTitan" : "WorkView"}</span><span className="rounded-md bg-[var(--wash)] px-2 py-0.5 text-[11px] font-medium text-[var(--ink-soft)]">{statusLabel(item.status)}</span></div><p className="mt-2 text-xs text-[var(--ink-faint)]">{item.sentAt ? `Sent ${date(item.sentAt)}` : `Updated ${date(item.accountingDate)}`}</p></div><div className="min-w-[130px] text-right"><p className="text-sm font-semibold tabular-nums text-[var(--ink)]">{money(item.amountCents)}</p><p className="mt-1 text-xs text-[var(--ink-soft)]">purchase order total</p></div><div className="flex shrink-0"><span className="rounded-lg bg-[var(--ink)] px-3 py-2 text-xs font-semibold text-[var(--paper)]">Open purchase order</span></div></a>)}</div>}
      <div className="flex items-center justify-between border-t border-[var(--line)] px-4 py-3 text-sm"><span>{total} purchase orders</span><div className="flex gap-2"><QuietButton type="button" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</QuietButton><span className="py-2">{page} / {pageCount}</span><QuietButton type="button" disabled={page === pageCount} onClick={() => setPage(page + 1)}>Next</QuietButton></div></div>
    </section>
  </>;
}

function PurchaseOrdersReconciliationWorkspace({
  embedded = false,
  canRetryQuickBooks,
  backToDocuments,
  backToPurchaseOrders,
  openPurchaseOrder,
}: {
  embedded?: boolean;
  canRetryQuickBooks: boolean;
  backToDocuments: () => void;
  backToPurchaseOrders: () => void;
  openPurchaseOrder?: (id: string) => void;
}) {
  const [items, setItems] = useState<AccountingPurchaseOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadSequence = useRef(0);
  const [selected, setSelected] = useState<AccountingPurchaseOrder | null>(
    null,
  );
  const [retrying, setRetrying] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [results, setResults] = useState<
    QuickBooksBillComparisonResult[] | null
  >(null);
  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: "25",
        status,
        source: "workview",
        search,
      });
      const result = await data<{
        items: AccountingPurchaseOrder[];
        total: number;
      }>(`/api/v1/workspace/accounting/purchase-orders?${params}`);
      if (sequence === loadSequence.current) {
        setItems(result.items);
        setTotal(result.total);
        setError("");
      }
    } catch {
      if (sequence === loadSequence.current) setError("We couldn’t load purchase orders.");
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [page, search, status]);
  useEffect(() => {
    void load();
  }, [load]);
  const retry = async (item: AccountingPurchaseOrder) => {
    setRetrying(item.id);
    try {
      const response = await apiFetch(
        `/api/v1/integrations/quickbooks/purchase-orders/${item.id}/retry`,
        { method: "POST" },
      );
      const body = (await response.json().catch(() => null)) as {
        data?: { status?: string };
        error?: { message?: string };
      } | null;
      if (!response.ok)
        throw new Error(body?.error?.message || "Unable to retry QuickBooks.");
      setSelected(null);
      await load();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Unable to retry QuickBooks.",
      );
    } finally {
      setRetrying(null);
    }
  };
  const compare = async () => {
    setComparing(true);
    try {
      const response = await apiFetch(
        "/api/v1/workspace/accounting/purchase-orders/quickbooks-compare",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            purchaseOrderIds: items.map((item) => item.id),
          }),
        },
      );
      const body = (await response.json().catch(() => null)) as {
        data?: { results?: QuickBooksBillComparisonResult[] };
        error?: { message?: string };
      } | null;
      if (!response.ok || !body?.data?.results)
        throw new Error(
          body?.error?.message ||
            "Unable to compare these bills with QuickBooks.",
        );
      setResults(body.data.results);
      setConfirming(false);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Unable to compare these bills with QuickBooks.",
      );
    } finally {
      setComparing(false);
    }
  };
  const billStatus = (value: string | null) =>
    ({
      synced: "Synced",
      pending: "Queued",
      processing: "Processing",
      retrying: "Retrying",
      needs_attention: "Needs attention",
      blocked: "Blocked",
    })[value ?? ""] ?? "No Bill queued";
  const statusTone = (value: string | null) =>
    value === "synced"
      ? "bg-emerald-100 text-emerald-800"
      : value === "needs_attention" || value === "blocked"
        ? "bg-amber-100 text-amber-900"
        : value
          ? "bg-sky-100 text-sky-900"
          : "bg-[var(--wash)] text-[var(--ink-soft)]";
  const pageCount = Math.max(1, Math.ceil(total / 25));
  const visibleIds = items.map((item) => item.id);
  return (
    <>
      {!embedded && <header className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 pt-5"><div><h1 className="text-3xl font-semibold tracking-tight text-[var(--ink)]">Accounting</h1><p className="mt-1 text-sm text-[var(--ink-soft)]">QuickBooks audit for purchase orders and Bills.</p></div><QuietButton type="button" onClick={backToPurchaseOrders}>← Back to purchase orders</QuietButton></div>
        <nav aria-label="Accounting sections" className="mt-5 flex gap-1 border-t border-[var(--line)] px-3 pt-2"><button type="button" onClick={backToDocuments} className="rounded-t-lg px-4 py-2 text-sm font-semibold text-[var(--ink-soft)] hover:bg-[var(--wash)] hover:text-[var(--ink)]">Invoices & estimates</button><button type="button" aria-current="page" className="rounded-t-lg border border-b-0 border-[var(--line)] bg-[var(--wash)] px-4 py-2 text-sm font-semibold text-[var(--ink)]">Purchase orders</button></nav>
      </header>}
      <section className="accounting-widget mt-5">
      <AccountingWidgetHeading kind="sync">Purchase order bill sync</AccountingWidgetHeading><p className="border-b border-[var(--line)] px-4 py-3 text-sm text-[var(--ink-soft)]">Review Bill sync status and compare purchase orders with QuickBooks.</p>
        <div className="flex flex-wrap items-center gap-3 border-b border-[var(--line)] p-4">
          <input
            aria-label="Search purchase orders"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="PO number, vendor, or job"
            className="w-full max-w-sm rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
          />
          <select
            aria-label="Purchase order status filter"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            <option value="attention">Needs attention</option>
            <option value="processing">Queued / processing</option>
            <option value="synced">Synced</option>
            <option value="blocked">Blocked</option>
            <option value="not_queued">No Bill queued</option>
          </select>
          <QuietButton
            type="button"
            disabled={loading || !visibleIds.length || comparing}
            onClick={() => setConfirming(true)}
          >
            Run live verification
          </QuietButton>
        </div>
        {error && (
          <p role="alert" className="px-4 pt-4 text-sm text-[var(--accent)]">
            {error}
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-[var(--line)] bg-[var(--wash)] text-xs uppercase tracking-wide text-[var(--ink-soft)]">
              <tr>
                <th className="px-4 py-3">Purchase order</th>
                <th className="px-4 py-3">Vendor</th>
                <th className="px-4 py-3">Job</th>
                <th className="px-4 py-3">PO status</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">QuickBooks Bill</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="cursor-pointer hover:bg-[var(--wash)]"
                  onClick={() => setSelected(item)}
                >
                  <td className="px-4 py-3 font-medium">
                    {item.displayNumber ?? "Purchase order"}
                  </td>
                  <td className="px-4 py-3">{item.vendorName}</td>
                  <td className="px-4 py-3">{item.jobNumber ?? "—"}</td>
                  <td className="px-4 py-3">
                    {item.status.replaceAll("_", " ")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {money(item.amountCents)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-md px-2 py-0.5 text-xs font-semibold ${statusTone(item.quickBooksBillStatus)}`}
                    >
                      {billStatus(item.quickBooksBillStatus)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading ? (
          <p className="px-5 py-14 text-center text-sm text-[var(--ink-soft)]">
            Loading purchase orders…
          </p>
        ) : !items.length ? (
          <p className="px-5 py-14 text-center text-sm text-[var(--ink-soft)]">
            No accounting purchase orders match these filters.
          </p>
        ) : (
          <div className="flex items-center justify-between border-t border-[var(--line)] px-4 py-3 text-sm">
            <span>{total} purchase orders</span>
            <div className="flex gap-2">
              <QuietButton
                type="button"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </QuietButton>
              <span className="py-2">
                {page} / {pageCount}
              </span>
              <QuietButton
                type="button"
                disabled={page === pageCount}
                onClick={() => setPage(page + 1)}
              >
                Next
              </QuietButton>
            </div>
          </div>
        )}
      </section>
      {selected && (
        <Dialog
          title="Purchase order accounting"
          onClose={() => setSelected(null)}
        >
          <div className="space-y-3 text-sm">
            <p>
              <b>{selected.displayNumber}</b> · {selected.vendorName}
            </p>
            <p>Purchase order status: {selected.status.replaceAll("_", " ")}</p>
            <p>
              Total: {money(selected.amountCents)}
              {selected.freightCents
                ? ` · Freight ${money(selected.freightCents)}`
                : ""}
              {selected.taxCents ? ` · Tax ${money(selected.taxCents)}` : ""}
            </p>
            <p>
              QuickBooks Bill:{" "}
              <code>{selected.quickBooksBillId ?? "Not queued"}</code>
            </p>
            {selected.quickBooksBillError && (
              <p
                role="alert"
                className="rounded-lg bg-amber-50 p-3 text-amber-900"
              >
                {selected.quickBooksBillError}
              </p>
            )}
          </div>
          <div className="mt-5 flex flex-wrap justify-between gap-2">
            <QuietButton
              type="button"
              onClick={() => openPurchaseOrder?.(selected.id)}
            >
              Open purchase order
            </QuietButton>
            <div className="flex gap-2">
              {selected.quickBooksBillId && (
                <a
                  href={`https://qbo.intuit.com/app/bill?txnId=${encodeURIComponent(selected.quickBooksBillId)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold"
                >
                  Open in QuickBooks
                </a>
              )}
              {canRetryQuickBooks &&
                selected.quickBooksBillStatus !== "synced" &&
                selected.quickBooksBillStatus !== "blocked" && (
                  <PrimaryButton
                    type="button"
                    disabled={retrying === selected.id}
                    onClick={() => void retry(selected)}
                  >
                    {retrying === selected.id
                      ? "Retrying…"
                      : "Retry QuickBooks"}
                  </PrimaryButton>
                )}
            </div>
          </div>
        </Dialog>
      )}
      {confirming && (
        <Dialog
          title="Run live verification"
          onClose={() => {
            if (!comparing) setConfirming(false);
          }}
        >
          <div className="space-y-4 text-sm">
            <p>
              Verify {visibleIds.length} visible purchase order
              {visibleIds.length === 1 ? "" : "s"} against the current QuickBooks Bill records? This can
              take a moment.
            </p>
            <p className="rounded-lg bg-[var(--wash)] p-3 text-[var(--ink-soft)]">
              This check compares PO number, vendor, total, and line count. It
              does not change purchase orders, Bills, payments, or sync status.
            </p>
            <div className="flex justify-end gap-2">
              <QuietButton
                type="button"
                disabled={comparing}
                onClick={() => setConfirming(false)}
              >
                Close
              </QuietButton>
              <PrimaryButton
                type="button"
                disabled={comparing}
                onClick={() => void compare()}
              >
                {comparing ? "Verifying…" : "Run verification"}
              </PrimaryButton>
            </div>
          </div>
        </Dialog>
      )}
      {results && (
        <Dialog
          title="QuickBooks Bill comparison"
          width="max-w-3xl"
          onClose={() => setResults(null)}
        >
          <div className="space-y-4 text-sm">
            {results.map((result) => (
              <div
                key={result.purchaseOrderId}
                className={`rounded-lg border p-3 ${result.status === "verified" ? "border-emerald-200 bg-emerald-50" : result.status === "needs_manual_fix" ? "border-amber-200 bg-amber-50" : "border-[var(--line)]"}`}
              >
                <div className="flex justify-between gap-2">
                  <b>
                    {result.purchaseOrderDisplayNumber} ·{" "}
                    {result.status === "verified"
                      ? "Passed"
                      : result.status === "needs_manual_fix"
                        ? "Needs manual review"
                        : "Could not compare"}
                  </b>
                  {result.quickBooksBillUrl && (
                    <a
                      href={result.quickBooksBillUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-[var(--accent)] hover:underline"
                    >
                      Open in QuickBooks
                    </a>
                  )}
                </div>
                {result.reasons.map((reason) => (
                  <p key={reason} className="mt-1">
                    {reason}
                  </p>
                ))}
              </div>
            ))}
            <div className="flex justify-end">
              <PrimaryButton type="button" onClick={() => setResults(null)}>
                Close
              </PrimaryButton>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

function ReconciliationWorkspace({ embedded = false, canRetryQuickBooks, backToDocuments, openPurchaseOrders, openInvoice }: { embedded?: boolean; canRetryQuickBooks: boolean; backToDocuments: () => void; openPurchaseOrders: () => void; openInvoice?: (invoiceId: string, jobId: string) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const initialFrom = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [search, setSearch] = useState("");
  const query = useDebouncedValue(search.trim());
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<ReconciliationItem[]>([]);
  const [type, setType] = useState<"all" | "invoice" | "payment" | "fee">("all");
  const [status, setStatus] = useState<"all" | "attention" | "without_quickbooks" | "historical">("all");
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(today);
  const [selected, setSelected] = useState<ReconciliationItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState<string | null>(null);
  const [comparisonConfirming, setComparisonConfirming] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<QuickBooksComparisonResult[] | null>(null);
  const loadSequence = useRef(0);
  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ type, status, from, to, q: query, page: String(page), pageSize: "50" });
      const result = await data<{ items: ReconciliationItem[]; total?: number }>(`/api/v1/workspace/accounting/reconciliation?${params}`);
      if (sequence === loadSequence.current) { setItems(result.items); setTotal(result.total ?? result.items.length); }
    } catch { setError("We couldn’t load reconciliation records."); }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [type, status, from, to, query, page]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!items.some((item) => item.kind !== "fee" && (item.quickBooksStatus === "pending" || item.quickBooksStatus === "retrying"))) return;
    const timer = window.setTimeout(() => void load(), 2_000);
    return () => window.clearTimeout(timer);
  }, [items, load]);
  const retry = async (item: ReconciliationItem) => {
    setRetrying(item.id); setError("");
    try {
      const path = item.kind === "invoice"
        ? `/api/v1/integrations/quickbooks/invoices/${item.invoiceId}/retry`
        : `/api/v1/integrations/quickbooks/payments/${item.paymentId}/retry`;
      const response = await apiFetch(path, { method: "POST" });
      const body = await response.json().catch(() => null) as { data?: { status?: string }; error?: { message?: string } } | null;
      if (!response.ok) throw new Error(body?.error?.message || "Unable to retry QuickBooks.");
      // The queue performs the actual accounting write asynchronously. Reflect
      // its durable pending state immediately, then keep refreshing until it settles.
      const queuedStatus = body?.data?.status ?? "pending";
      setItems((current) => current.map((currentItem) => currentItem.id === item.id && currentItem.kind === item.kind
        ? { ...currentItem, quickBooksStatus: queuedStatus, overallStatus: "pending", lastError: null }
        : currentItem));
      setSelected(null);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to retry QuickBooks."); }
    finally { setRetrying(null); }
  };
  const visibleInvoiceIds = [...new Set(items.filter((item) => item.kind !== "fee").map((item) => item.invoiceId))];
  const compareWithQuickBooks = async () => {
    if (!visibleInvoiceIds.length) return;
    setComparing(true); setError("");
    try {
      const response = await apiFetch("/api/v1/workspace/accounting/reconciliation/quickbooks-compare", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invoiceIds: visibleInvoiceIds }),
      });
      const body = await response.json().catch(() => null) as { data?: { results?: QuickBooksComparisonResult[] }; error?: { message?: string } } | null;
      if (!response.ok || !body?.data?.results) throw new Error(body?.error?.message || "Unable to compare these invoices with QuickBooks.");
      setComparisonResults(body.data.results);
      setComparisonConfirming(false);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to compare these invoices with QuickBooks."); }
    finally { setComparing(false); }
  };
  const paymentMethodLabel = (value: string | null) => value ? value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "—";
  const quickBooksLabel = (value: string) => ({ synced: "Applied", not_queued: "Not queued", pending: "Queued", retrying: "Retrying", needs_attention: "Needs attention", waiting_invoice: "Waiting for invoice", not_connected: "Not connected", historical: "Previous connection", not_applicable: "Not applicable" }[value] ?? value.replaceAll("_", " "));
  const overallLabel = (value: string) => ({ reconciled: "Ready", recorded: "Ready", pending: "Processing", retrying: "Processing", review: "Review", historical: "Historical" }[value] ?? value.replaceAll("_", " "));
  const statusClass = (value: string) => value === "reconciled" || value === "synced" || value === "recorded" ? "bg-emerald-100 text-emerald-800" : value === "review" || value === "needs_attention" ? "bg-amber-100 text-amber-900" : value === "pending" || value === "retrying" ? "bg-sky-100 text-sky-900" : "bg-[var(--wash)] text-[var(--ink-soft)]";
  return <>
    {!embedded && <header className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
      <div className="flex flex-wrap items-start justify-between gap-4 px-5 pt-5"><div><h1 className="text-3xl font-semibold tracking-tight text-[var(--ink)]">Accounting</h1><p className="mt-1 text-sm text-[var(--ink-soft)]">QuickBooks audit for invoices and payments.</p></div><QuietButton type="button" onClick={backToDocuments}>← Back to invoices & estimates</QuietButton></div>
      <nav aria-label="Accounting sections" className="mt-5 flex gap-1 border-t border-[var(--line)] px-3 pt-2"><button type="button" aria-current="page" className="rounded-t-lg border border-b-0 border-[var(--line)] bg-[var(--wash)] px-4 py-2 text-sm font-semibold text-[var(--ink)]">Invoices & estimates</button><button type="button" onClick={openPurchaseOrders} className="rounded-t-lg px-4 py-2 text-sm font-semibold text-[var(--ink-soft)] hover:bg-[var(--wash)] hover:text-[var(--ink)]">Purchase orders</button></nav>
    </header>}
    <section className="accounting-widget mt-5">
      <AccountingWidgetHeading kind="sync">QuickBooks sync</AccountingWidgetHeading><p className="border-b border-[var(--line)] px-4 py-3 text-sm text-[var(--ink-soft)]">Review invoice and payment sync status, then compare with QuickBooks.</p>
      <div className="flex flex-wrap items-end gap-3 border-b border-[var(--line)] p-4">
        <label className="text-xs text-[var(--ink-soft)]">Search<input aria-label="Search invoices or customers" placeholder="Invoice number or customer" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} className="ml-2 rounded-lg border border-[var(--line)] px-3 py-2 text-sm text-[var(--ink)]" /></label>
        <select aria-label="Reconciliation type" value={type} onChange={(event) => { setType(event.target.value as typeof type); setPage(1); }} className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"><option value="all">All activity</option><option value="invoice">Invoices</option><option value="payment">Payments</option><option value="fee">Fees</option></select>
        <select aria-label="Reconciliation status" value={status} onChange={(event) => { setStatus(event.target.value as typeof status); setPage(1); }} className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"><option value="all">All statuses</option><option value="attention">Requires attention</option><option value="historical">Historical</option><option value="without_quickbooks">Without QuickBooks</option></select>
        <label className="text-xs text-[var(--ink-soft)]">From<input aria-label="Reconciliation from" type="date" disabled={Boolean(search.trim())} value={from} max={to} onChange={(event) => { setFrom(event.target.value); setPage(1); }} className="ml-2 rounded-lg border border-[var(--line)] px-2 py-1.5 text-sm text-[var(--ink)]" /></label>
        <label className="text-xs text-[var(--ink-soft)]">To<input aria-label="Reconciliation to" type="date" disabled={Boolean(search.trim())} value={to} min={from} max={today} onChange={(event) => { setTo(event.target.value); setPage(1); }} className="ml-2 rounded-lg border border-[var(--line)] px-2 py-1.5 text-sm text-[var(--ink)]" /></label>
        <QuietButton type="button" disabled={loading || !visibleInvoiceIds.length || comparing} onClick={() => setComparisonConfirming(true)}>Run live verification</QuietButton>
      </div>
      {search.trim() && <p role="status" className="border-b border-[var(--line)] px-4 py-2 text-xs text-[var(--ink-soft)]">Searching all dates, including invoices not yet queued for QuickBooks.</p>}
      <div aria-label="Reconciliation status legend" className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[var(--line)] bg-[var(--wash)] px-4 py-2.5 text-xs text-[var(--ink-soft)]">
        <span className="font-semibold text-[var(--ink)]">Status:</span>
        <span><b className="text-emerald-800">Ready</b> — Invoice or payment is in QuickBooks</span>
        <span><b className="text-sky-900">Processing</b> — QuickBooks is queued or retrying</span>
        <span><b className="text-amber-900">Review</b> — An invoice is missing or needs attention</span>
        <span><b>Historical</b> — Created under a previous QuickBooks connection; kept for records</span>
      </div>
      {error && <p role="alert" className="px-4 pt-4 text-sm text-[var(--accent)]">{error}</p>}
      <div className="overflow-x-auto"><table className="min-w-full text-left text-sm"><thead className="border-b border-[var(--line)] bg-[var(--wash)] text-xs uppercase tracking-wide text-[var(--ink-soft)]"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Customer / invoice</th><th className="px-4 py-3 text-right">Gross</th><th className="px-4 py-3 text-right">Fee</th><th className="px-4 py-3 text-right">Net</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">QuickBooks</th><th className="px-4 py-3">Status</th></tr></thead><tbody className="divide-y divide-[var(--line)]">{items.map((item) => <tr key={`${item.kind}-${item.id}`} className="cursor-pointer hover:bg-[var(--wash)]" onClick={() => setSelected(item)}><td className="whitespace-nowrap px-4 py-3">{date(item.occurredAt)}</td><td className="px-4 py-3 font-medium">{item.kind === "invoice" ? "Invoice" : item.kind === "payment" ? "Payment" : "Fee"}</td><td className="px-4 py-3"><p className="font-medium">{item.customerName}</p><p className="text-xs text-[var(--ink-soft)]">{item.invoiceDisplayNumber}</p></td><td className="px-4 py-3 text-right">{item.kind === "fee" ? "—" : money(item.grossCents)}</td><td className="px-4 py-3 text-right">{item.feeCents ? money(item.feeCents) : "—"}</td><td className="px-4 py-3 text-right font-medium">{item.kind === "fee" ? money(item.netCents) : "—"}</td><td className="px-4 py-3">{item.paymentSource}{item.paymentMethod ? ` · ${paymentMethodLabel(item.paymentMethod)}` : ""}</td><td className="px-4 py-3">{quickBooksLabel(item.quickBooksStatus)}</td><td className="px-4 py-3"><span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${statusClass(item.overallStatus)}`}>{overallLabel(item.overallStatus)}</span></td></tr>)}</tbody></table></div>
      {!loading && !items.length && <p className="px-5 py-14 text-center text-sm text-[var(--ink-soft)]">No reconciliation movements match these filters.</p>}
      {total > 50 && <div className="flex items-center justify-between border-t border-[var(--line)] px-4 py-3 text-sm"><QuietButton type="button" disabled={loading || page === 1} onClick={() => setPage((value) => value - 1)}>Previous</QuietButton><span>Page {page} of {Math.ceil(total / 50)}</span><QuietButton type="button" disabled={loading || page * 50 >= total} onClick={() => setPage((value) => value + 1)}>Next</QuietButton></div>}
      {loading && <p className="px-5 py-14 text-center text-sm text-[var(--ink-soft)]">Loading reconciliation…</p>}
    </section>
    {selected && (
      <Dialog title={selected.kind === "invoice" ? "Invoice sync" : selected.kind === "payment" ? "Payment reconciliation" : "Stripe fee"} onClose={() => setSelected(null)}>
        <div className="grid gap-3 text-sm">
          <p><b>{selected.customerName}</b> · Invoice {selected.invoiceDisplayNumber}</p>
          {selected.kind !== "invoice" && <><p>Gross: {money(selected.grossCents)} · Fee: {selected.feeCents ? money(selected.feeCents) : "—"} · Net: {money(selected.netCents)}</p><p>Source: {selected.paymentSource}{selected.paymentMethod ? ` · ${paymentMethodLabel(selected.paymentMethod)}` : ""}</p></>}
          {selected.kind === "invoice" && <p>QuickBooks invoice: <code>{selected.quickBooksInvoiceId ?? "Not synced"}</code></p>}
          {selected.stripeTransactionId && <p>Stripe ID: <code>{selected.stripeTransactionId}</code></p>}
          <p>QuickBooks payment: <code>{selected.quickBooksPaymentId ?? "Not synced"}</code></p>
          {selected.kind === "payment" && <>
            <p>QuickBooks invoice: <code>{selected.quickBooksInvoiceId ?? "Waiting for invoice"}</code></p>
            <p>Undeposited Funds: <code>{selected.undepositedFundsAccountId ?? "Not mapped"}</code></p>
            {selected.quickBooksStatus === "waiting_invoice" && <p className="rounded-lg bg-amber-50 p-3 text-amber-900">Sync this invoice to QuickBooks first. WorkView will then automatically queue this payment.</p>}
          </>}
          {selected.overallStatus === "historical" && <p className="rounded-lg bg-[var(--wash)] p-3 text-[var(--ink-soft)]">This payment was created under a previous QuickBooks connection. It is retained for history and is not reconciled to the current QuickBooks company.</p>}
        </div>
        {selected.lastError && <p role="alert" className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{selected.lastError}</p>}
        <div className="mt-5 flex justify-between gap-2">
          <QuietButton type="button" onClick={() => openInvoice?.(selected.invoiceId, selected.jobId)}>Open invoice</QuietButton>
          {selected.kind !== "fee" && selected.quickBooksStatus !== "synced" && selected.quickBooksStatus !== "historical" && selected.quickBooksStatus !== "waiting_invoice" && canRetryQuickBooks && <PrimaryButton type="button" disabled={retrying === selected.id} onClick={() => void retry(selected)}>{retrying === selected.id ? "Retrying…" : "Retry QuickBooks"}</PrimaryButton>}
        </div>
      </Dialog>
    )}
    {comparisonConfirming && (
      <Dialog title="Run live verification" onClose={() => { if (!comparing) setComparisonConfirming(false); }}>
        <div className="space-y-4 text-sm">
          <p>Verify {visibleInvoiceIds.length} visible invoice{visibleInvoiceIds.length === 1 ? "" : "s"} against the current QuickBooks records? This can take a moment.</p>
          <p className="rounded-lg bg-[var(--wash)] p-3 text-[var(--ink-soft)]">This check compares invoice totals, balances, customer links, and line counts. It does not change the reconciliation table, invoice records, payments, or QuickBooks.</p>
          <div className="flex justify-end gap-2"><QuietButton type="button" disabled={comparing} onClick={() => setComparisonConfirming(false)}>Close</QuietButton><PrimaryButton type="button" disabled={comparing} onClick={() => void compareWithQuickBooks()}>{comparing ? "Verifying…" : "Run verification"}</PrimaryButton></div>
        </div>
      </Dialog>
    )}
    {comparisonResults && (() => {
      const needsReview = comparisonResults.filter((result) => result.status === "needs_manual_fix");
      const unable = comparisonResults.filter((result) => result.status === "unable_to_compare");
      const verified = comparisonResults.filter((result) => result.status === "verified");
      return <Dialog title="Live verification results" width="max-w-3xl" onClose={() => setComparisonResults(null)}>
        <div className="space-y-4 text-sm">
          <p><b>{needsReview.length}</b> need review · <b>{verified.length}</b> passed these checks · <b>{unable.length}</b> could not be compared</p>
          {needsReview.length > 0 && <section><h3 className="font-semibold text-amber-900">Needs manual review</h3><div className="mt-2 divide-y rounded-lg border border-amber-200 bg-amber-50">{needsReview.map((result) => <div key={result.invoiceId} className="p-3"><div className="flex flex-wrap items-center justify-between gap-2"><b>Invoice {result.invoiceDisplayNumber}</b>{result.quickBooksInvoiceUrl && <a href={result.quickBooksInvoiceUrl} target="_blank" rel="noreferrer" className="font-semibold text-[var(--accent)] hover:underline">Open in QuickBooks</a>}</div><ul className="mt-1 list-disc space-y-1 pl-5 text-amber-950">{result.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></div>)}</div></section>}
          {unable.length > 0 && <section><h3 className="font-semibold">Could not compare</h3><div className="mt-2 divide-y rounded-lg border border-[var(--line)]">{unable.map((result) => <div key={result.invoiceId} className="p-3"><b>Invoice {result.invoiceDisplayNumber}</b><p className="mt-1 text-[var(--ink-soft)]">{result.reasons.join(" ")}</p></div>)}</div></section>}
          {!needsReview.length && !unable.length && <p className="rounded-lg bg-emerald-50 p-3 text-emerald-900">No differences found in the checked totals, balances, customer links, and line counts.</p>}
          <div className="flex justify-end"><PrimaryButton type="button" onClick={() => setComparisonResults(null)}>Close</PrimaryButton></div>
        </div>
      </Dialog>;
    })()}
  </>;
}

function WorkTile({
  active,
  label,
  value,
  note,
  tone,
  onClick,
}: {
  active: boolean;
  label: string;
  value: string;
  note: string;
  tone: "blue" | "emerald" | "amber" | "rose" | "slate";
  onClick: () => void;
}) {
  const Icon = tone === "rose" ? Clock3 : tone === "emerald" ? CheckCircle2 : tone === "slate" ? Archive : FileText;
  return <button type="button" onClick={onClick} aria-pressed={active} className="job-summary-tile accounting-summary-widget">
    <span className="job-summary-tile-top"><Icon size={18} aria-hidden="true" /><strong>{value}</strong><ArrowRight size={15} className="job-summary-tile-arrow" aria-hidden="true" /></span>
    <span className="job-summary-tile-title">{label}</span>
    <span className="job-summary-tile-description">{note || "View records"}</span>
  </button>;
}

